// src/components/IntakeTab.jsx — the "Data Desk"
// Paste a client message → AI decides Client/Property/Meeting/Candidate, pre-fills the
// right form, and highlights the matching button. If the AI can't decide, all four buttons
// glow so a human picks. Every button also opens its modal on a plain manual click.
//
// Follows core/INTAKE_CONTRACT.md: AI emits a decision envelope (never writes), the human
// reviews on screen, and only an explicit commit executes it through the gateway. Editing a
// field by hand before committing is the human-confirmation signal — stored on the proposal
// as `human_edited` (see viewToCmd/handleAcceptClient below) so the history list can tell
// "AI-only" apart from "human-curated" without any schema change.
//
// Ambiguity resolution (2026-07-02): the AI now also returns `clarifications[]` (short
// Yes/No/Don't-know/Skip or free-text questions, asked one at a time, chat-style — never
// all at once) and `candidates_considered[]` (existing demands it compared this message
// against). A confident "amend" glows both the Client button AND the matching row in
// Recent Entries the same amber as an auto-picked category; an unsure match glows the row
// in a calmer blue as a hint only — clicking it switches the draft to "amend this one"
// instead of creating new. Nothing is ever silently merged; the human always clicks.
//
// Undo (core migration 0039): nothing is ever deleted — a wrongly-created requirement is
// *closed* (close_requirement), never removed, so it's fully reversible and the mistake
// stays visible in the audit trail (core's append-only philosophy). Per-field revert of an
// already-committed amend isn't built yet (would need a "read this requirement" endpoint);
// only "undo this create" is wired for now.
import { MyWorkerSwitch } from './MyWorkerSwitch.jsx'
import { createContext, Fragment, memo, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { logEvent, getCurrentAdmin } from '../lib/agentLog.js'
import { t, tOr, tLog, getLanguage } from '../lib/i18n.js'
import { ClientSharePanel } from './desk/ClientSharePanel.jsx'
import { mergeReshareStatus } from './desk/reshareStatus.js'
import { SellerFacet, ContactFacet, sellerChips } from './desk/SellerFacets.jsx'
import { ClientLeadAlert } from './desk/LeadAlertToggle.jsx'
import { summaryOf, ClientSummaryStrip, ClientFeatureChips, ClientTeamChip, MaskedClientBanner, ClientTabs } from './desk/ClientRequestParts.jsx'
import { AgentPicker } from './desk/AgentPicker.jsx'
import { ShareMenu } from './desk/ShareMenu.jsx'
import { fetchPrioritized } from '../lib/thumbPriority.js'
// Shared with the public fill form so the Desk renders a client's stated nationality /
// profession / sex with the exact labels they picked (src/lib/householdVocab.js).
import { nationalityLabel, professionLabel, sexLabel } from '../lib/householdVocab.js'
import { intentLabel } from '../lib/labels.js'
import { placeIcon } from '../lib/placeIcon.js'
// Monochrome SVG icons (inline Lucide paths — see Icon.jsx for why not npm). Card-face
// chips use these instead of emoji so icons inherit the chip's text color and stay crisp.
import { Icon, IconText } from './Icon.jsx'
// The add-menu's 'candidate' entry is capability-gated (DESK_BUTTONS below) — hiring is
// management's door, and an agent must not be shown one that leads nowhere for them.
import { can } from '../lib/permissions.js'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
// Groups nearby pins into a single "N" bubble that splits apart on zoom — EntriesMapView (2026-07-06)
// can have hundreds of listings in a small area (e.g. central Tbilisi), which without clustering
// renders as an unreadable wall of overlapping pins.
import 'leaflet.markercluster'
import 'leaflet.markercluster/dist/MarkerCluster.css'
import 'leaflet.markercluster/dist/MarkerCluster.Default.css'
// Leaflet's default marker icon resolves its image URLs relative to the stylesheet, which
// breaks under Vite bundling — L.marker renders an INVISIBLE marker (map centers on it,
// nothing shows). Import the images so Vite emits real URLs and wire them in explicitly.
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'
L.Icon.Default.mergeOptions({ iconRetinaUrl: markerIcon2x, iconUrl: markerIcon, shadowUrl: markerShadow })
import {
  getDistricts, getFeatures, searchComplexes, getKindFeatureCapability, getConditionLadder, getRestrictionExpansion, getRestrictionVocab, getRequirement, getParty, findPartyCandidates, getPartyDuplicates, recordPartyVerdict,
  addPartyContact, updatePartyContact, revealContactBook,
  parseIntakeEnvelope, commitIntakeEnvelope, getRecentProposals, logCandidateProposal,
  saveListingEntry, parseListingText, dismissProposal, getAgentListings, getStreetCatalog, getAgentListingPins, getAgentListingStatusCounts, getListingThumbs, getPropertyThumbs, getListingDescriptions, getAgentCounts, getOfficeScope, getListingsLineageBatch, geocode, geocodeSuggest, getProposalMatchCounts,
  readDeskCache, writeDeskCache,
  searchCoreListings,
  getPropertyShares, getReshareStatus,
  listCooperations, listCoopOffers, listMyCooperations, getCooperation, openCooperation, amendCooperation, setCooperationStatus, updateCooperationParticipants,
  getPropertyChips,
  getPropertyDistinctiveness,
  getDevelopmentCards,
  getSellersByProperty,
  getMetroStations,
  getAgentDeals, getActivityRanking,
  getPropertyContacts, addPropertyContact, removePropertyContact, setContactViewerAgentId,
  getPropertyEvents,
  uploadListingImage, listListingImages, removeListingImage, setListingImageKind, reorderListingImages,
  getListingTerms, amendListingTerms, listListingPolicies, setListingPolicy, setPropertyFeature, getListingTermsBatch,
  getFavorites, addFavorite, removeFavorite,
  getArchived, addArchived, removeArchived, getAuthorRoleMarks,
  getRequirementMatches, getPropertyMatchingClients, setCoreViewer, setMatchVerdict, confirmRequirementActive,
  getPropertyDuplicates, recordPropertyVerdict,
  getMyPropertyVersion, addPropertyNote, deletePropertyNote, setPropertyInventory,
  getAgentProperties,
  getLeads, claimLead,
  revealContact, recordAttemptOutcome, getOpenTouches, getComplexesByDeveloper, getComplexesByIds, getDormancy, getStreetsByIds, getMarketReportCounts, recordMarketReport, setPropertyAvailability,
  fileAccessRequest, fileRequirementAccessRequest, getAccessRequests, decideAccessRequest, getShareActivity,
  getNotificationRead, setNotificationRead, undoNotificationRead,
  logSearchQuery, getRecentSearches, recordRecentSearchServer,
  getSavedSearches, saveSearchServer, deleteSavedSearchServer, renameSavedSearchServer,
  searchSuggest, searchListingsByImage, resolveStreetQuery, trackDeskEvent,
  getDedupHealthAlerts, getSystemAlerts,
  logListingView, getListingViews,
  createFillLink, listFillLinks,
  voteDistrict,
  getPropertyViewings,
  getMeetingsFeed,
  getMeetingNudges,
  getClientWaiting,
  getBinderChanges,
  looksLikeShareLink, resolvePropertyShareLink,
  getRequirementRepresentatives, shareRequirement, leaveRequirement, unshareRequirement, sharePropertyWith, getPropertyRepresentatives,
  getPriceSense,
} from '../db/core.js'
import { queryKey } from '../lib/queryKey.js'
import { readQuery, writeQuery, dedupeFetch, metrics as cacheMetrics } from '../db/queryCache.js'
import { freshnessTier, deskDirtyAt, whenIdle, isFreshWithin, DESK_COUNTS_FRESH_MS, DESK_FRESH_DEFAULT_MS, DESK_STALE_MAX_MS } from '../lib/deskFreshness.js'
// B1214 rule 11 — real UX latency, readable from the console: window.__sntvPerf and performance marks.
// F-045 trace (rule 11): an append-only log of the events that change the feed's height, readable from the
// console as window.__sntvPerf.trace — the marks above are first-wins and cannot describe a second search.
const perfTrace = (what, extra) => {
  try {
    const w = globalThis.__sntvPerf || (globalThis.__sntvPerf = { marks: {}, cache: cacheMetrics })
    ;(w.trace || (w.trace = [])).push({ t: Math.round(performance.now()), what, ...(extra || {}) })
    if (w.trace.length > 400) w.trace.splice(0, 100)
  } catch { /* never */ }
}
const perfMark = (name, extra) => {
  try {
    if (typeof performance === 'undefined') return
    performance.mark(name)
    const w = globalThis.__sntvPerf || (globalThis.__sntvPerf = { marks: {}, cache: cacheMetrics })
    if (w.marks[name] == null) w.marks[name] = Math.round(performance.now())
    // first-wins for the facts that describe the FIRST paint (measured 2026-09-05: the fresh
    // paint overwrote firstCardsSource with "network" after the cache had painted first)
    for (const [k, v] of Object.entries(extra || {})) if (!(k === 'firstCardsSource' || k === 'cachedAgeMs') || w[k] == null) w[k] = v
  } catch { /* measurement never breaks the feed */ }
}
// LOCATION card (2026-07-16): nearby amenities + complex-membership question.
// locations/complexes live in the SCHEDULER Supabase (not core) — direct client reads.
import { nearbyLocations, nearbyComplexes, getLocations, getPropertyQuestionVotes, castPropertyQuestionVote } from '../db/locations.js'
// ⚠️ toGel was USED here and never imported — a live ReferenceError on the commission sort
// and the deal-figure path, on main since 2026-09-07 (41d530c, a restore that dropped the
// specifier and not the calls). Found by `npm run gate:undef`, which had been red on it for two
// days. One conversion rule per feed: the same function the Stats tab and the price sort use.
import { rankParts, capScaleForDays, meetingsByAgent, officeDaysByAgent, toGel } from '../lib/rankingScore.js'
// The Desk board's closes and commission come from THE SAME LEDGER as the Stats tab (2026-09-06).
import { AgentScorecardPanel } from './desk/AgentScorecardPanel.jsx'
import { buildLedger } from '../lib/statsModel.js'
import { mergeDealSources } from '../lib/performance.js'
import { compileQuery, suggestDistricts, suggestRestrictions, restrictionPhrase, restrictionGlyph, NEAR_TYPE_EMOJI } from '../lib/searchCompiler.js'
import { whoIsItForRows } from '../lib/whoIsItFor.js'
// The quarter/microdistrict short renderer — display only, canonical name stays on hover.
import { shortPlaceLabel } from '../lib/districtLabel.js'
import { pickFacetCounts } from '../lib/facetCountSource.js'
import { feedEmptyKind } from '../lib/emptyState.js'
import { faceForCluster } from '../lib/displayAddress.js'
// B37: the browse-worthy feature vocabulary. ONE list drives this panel and the search box
// (see searchVocab.js) — the panel can therefore never be asked to represent a chip the
// compiler invented, which is the trap that kept the amenity filter unbuilt.
import { FEATURE_GROUPS, CONDITION_LADDERS, conditionLaddersFrom, featureGroupsFrom, capabilityFilter, FURNISH_DEGREE } from '../lib/searchVocab.js'
import { defaults as registryDefaults, arrayKeys as registryArrayKeys, ringsToParam, ringsFromParam, invalidNumericBounds } from '../lib/deskFilters.js'
import { searchPreviewState, ownRun, releaseRun, loadStreetCatalog } from '../lib/deskAnswerState.js'   // 2026-10-23 audit
// 0756: the four-state feature filter. The cycle is a tested pure module — no surface
// re-derives the order, because getting it wrong searches for the opposite of what was meant.
import { activeCount as featureActiveCount } from '../lib/featureStates.js'
import { stateOf as facetStateOf, cycle as facetCycle, pressName as facetPressName, excludeKeyOf, excludePairs, isFlagFacet } from '../lib/facetTriState.js'
import { districtShown, districtShownOwn, districtPress, districtPressOwn, districtNamePress, districtNamePressOwn } from '../lib/districtTree.js'
import { districtTreeIndex, cityScopedDistrictFilters, pruneDistrictsToCity, pruneLocationToCity, DISTRICT_KEYS, districtFilterActive } from '../lib/districtFilter.js'
import { cityTotalsFrom, districtSeedAllowed, agentCutClientOnly, serverVettedSpec, serverReadout, baselineKey } from '../lib/deskCounts.js'   // 2026-10-22
import { cityValue } from '../lib/districtModel.js'   // core 3836: the dropdown label → the city_counts key
import FeatureStateRow from './desk/FeatureStateRow.jsx'
import ComplexFacet from './desk/ComplexFacet.jsx'
import ComplexHeaderCard from './desk/ComplexHeaderCard.jsx'
import ProjectFacet from './desk/ProjectFacet.jsx'
import FurnishingFacet from './desk/FurnishingFacet.jsx'
import LadderRow from './desk/LadderRow.jsx'
import { axisOf as featureAxisOf } from '../lib/featureAxes.js'
// The Recent-Entries filter/tally/sort chain + the pure predicates it needs. Extracted from
// this file 2026-07-27 (DESK_RENDER_PERF_HANDOFF §5 Phase 0 / B122) so `node --test` can gate
// it against committed fixtures before Phases 1–3 restructure it for speed.
import { feedPipeline, mergeProposalFeeds, advFiltersActive, normalizeAdvFilters, reconcileRows, reconcileGroups, LOCAL_ONLY_ADV_KEYS, parseRestrictions, serverOnlyFiltersActive, localPassCanAnswer, SERVER_ONLY_ADV_KEYS, CLIENT_INTENT_OF } from '../lib/deskFeed.js'
import { committedTextFor, streetToAcceptOnEnter, applyTokenOverlay as applyTokenOverlayPure, boxTextAfterFold } from '../lib/searchCommit.js'
import { useStickyControl } from '../lib/useStickyControl.js'
// The identity taxonomy + the three capability systems (SEARCH_GRAMMAR.md §5b) — ONE
// declaration, read by the tree, the panel and the verification gate alike.
import { KIND_SUBTYPES, CATEGORIES, CATEGORY_KINDS, APARTMENT_STATUS, APARTMENT_CONTEXT, APARTMENT_CONTEXT_CODES, CATEGORY_CONTEXT_NODES, INTENT_ICON, CATEGORY_ICON, KIND_ICON, SUBTYPE_ICON, deriveIdentityPath, identityPatch, interviewCapability, applies, applicableFilters, inapplicableFilterKeys } from '../lib/taxonomy.js'
import { bestVisibleDealValue } from '../lib/coopMath.js'
import { makeCommitted, isEmptyCommitted, sameCommitted, isNewContext, committedToAdvFilters, requirementToCommitted, canonicalFilters, agentCountsDescribeSearch } from '../lib/deskSearchState.js'
import { facetSignature, resolveFacet, resolveFacetDurable, resolveRanges } from '../lib/facetOwnership.js'
import { serializeSearch, parseHash, serializeCards, withCards, withPage, withSort, DEFAULT_SORT, defaultSortFor } from '../lib/deskUrl.js'
import { FeedPager } from './desk/FeedPager.jsx'
import DatesFacet from './desk/DatesFacet.jsx'
import { checkPhone, splitDial } from '../lib/phoneRules.js'
import { georgianStem } from '../../core/georgian-stem.mjs'
import { normalizeText, UNNORMALIZABLE } from '../../core/normalization.js'
// The agent facet and its two drawing primitives now live in one module, shared with the
// contracts tab (owner 2026-09-11: "import it exactly"). Moved, not copied — see that file.
import { TriGlyph, FacetCheck, AgentFacetList } from './desk/AgentFacetList.jsx'
// Same platform noting/coloring as the Listings tab (korter=orange, ss=blue, myhome=green)
// — one source of truth so the two tabs can't drift apart.
import { PLATFORM_LABEL, PLATFORM_COLOR, PIN_COLOR, platformKey } from './ListingsTab.jsx'
import { getAttendance, getAiAlerts, supabase } from '../db/index.js'
import { useCandidates } from '../hooks/useCandidates.js'
import { CandidateModal } from './CandidatesTab.jsx'
import BusinessSection, { BusinessFeed } from './BusinessSection.jsx'
import { ViewingForm } from './DealsTab.jsx'
import { ViewToggle, SingleSelect, MultiSelect, AgentMultiSelect } from './AgentsTab.jsx'
import { useViewPreference } from '../hooks/useViewPreference.js'
import { PriceFilter, AreaFilter, PpmFilter } from './RangeFilters.jsx'
import { formatMoney, comparableAmount, CONVERTIBLE } from '../lib/money.js'
import { keepInView } from '../lib/keepInView.js'

// Money / MoneyCtx / useMoney / priceTitle — moved to ./desk/primitives.jsx (plan §3 decomposition).

// Shared Desk primitives — extracted to ./desk/primitives.jsx (plan §3 decomposition,
// behavior-preserving; bodies verbatim). One definition, imported everywhere.
import { card, input, inputCompact, btn, chip, mobilePill, Badge, COUNTER, pill, PriceHistoryBadge, MoneyCtx, AdTextCtx, ViewerCtx, useMoney, Money, priceTitle, ObservedPriceHistoryBadge, BtnLabel, glowAmber, glowBlue, timeAgo, Modal, SkeletonCard, SearchElapsedNote, popoverPos } from './desk/primitives.jsx'
import { DiscussWithTeamButton } from './desk/ChatInbox.jsx'
import { ParseConflictPanel } from './desk/ParseConflictPanel.jsx'
import { RunningNote } from './desk/RunningNote.jsx'
import { splitClientParse, acceptedClientPatch } from '../lib/clientParseFields.js'
// Match-suggestions cluster — extracted to ./desk/MatchSuggestions.jsx (plan §3 decomposition,
// behavior-preserving; bodies verbatim). Shared by the client edit modal and the lineage modal.
// Manual "Add client" modal + shared criteria form — extracted to ./desk/ManualClientModal.jsx
// (plan §3 decomposition, behavior-preserving; bodies verbatim).
import { APT_CLASS, CriteriaFieldsForm, FeatureDefPanel, FillLinkSection, InfoDot, ManualClientModal, PROJECT_TYPES, PROPERTY_CATEGORY_KEYS, PROP_STATE, PROP_TYPE_TAGS, amendRequirementCmd, extractRequirementCmd, featureDefOf, isNationalityFeature } from './desk/ManualClientModal.jsx'
// Cooperation modal + person view — extracted to ./desk/CooperationModal.jsx / ./desk/PersonModal.jsx
// (plan §3 decomposition, behavior-preserving; bodies verbatim).
import { CooperationModal } from './desk/CooperationModal.jsx'
// Failed-save retry — extracted so the resolve-before-create rule lives in one place.
import { FailedEntryModal } from './desk/FailedEntryModal.jsx'
import { DealEditor } from './desk/DealEditor.jsx'
import AdvertQualityFacet, { advertQualityBadge } from './desk/AdvertQualityFacet.jsx'
import { PersonModal, PhonePersonHeader } from './desk/PersonModal.jsx'
// Manual property workspace — extracted to ./desk/ManualPropertyModal.jsx (plan §3
// decomposition, behavior-preserving; bodies verbatim).
import { AvailabilityFacet, AvailabilityLine, LISTING_INTENT_OPTIONS, ManualPropertyModal, PROPERTY_KIND_OPTIONS, PrivateNotesPanel, RentalTermsByAgent, RentalTermsModal, cityKey, districtInCity, districtReach, termsModeFor } from './desk/ManualPropertyModal.jsx'
// Reshare lineage modal — extracted to ./desk/LineageModal.jsx (plan §3 decomposition,
// behavior-preserving; bodies verbatim).
import { ListingLineageModal, NEAR_SEARCH_RADIUS_M, lineageFieldRow } from './desk/LineageModal.jsx'
import { AccessRequestPanel, AccessRequestsNotice, AgentPostingsGroup, BetterToKnow, CLIENT_COMPLETENESS_TOTAL, ContactReveal, CopyListingLink, MatchSuggestions, OpenTouchNudge, STANCE_CYCLE, STANCE_ICON, STANCE_STYLE, StanceChip, TIER_ICON, TIER_STYLE, clientGaps, diffCriteriaOps, listingCompleteness, propertyGaps } from './desk/MatchSuggestions.jsx'
import { ListingGallery, splitCityFromAddress, osmTileUrl, getApproxLocation } from './desk/ListingGallery.jsx'
import { NewApplicantsBanner } from './desk/NewApplicantsBanner.jsx'
import { MeetingsFeed, MeetingCard, MeetingNudgesBanner, meetingNudgeIndex, meetingNudgeOf, MEETING_STATUS_KEYS, MEETING_ACTIVE, MEETING_ACTIVE_SELECTABLE, MEETING_FINISHED } from './desk/MeetingsFeed.jsx'
import { treeTotalFor } from '../lib/treeTotal.js'   // F-089: a landed zero is an answer
import { LeadsFeed, prefetchLeads, leadsCachedTotal, leadsCachedSearchMeta, leadsCachedRows, warmLeadSearchMeta } from './desk/LeadsFeed.jsx'
import { mergeFacetMaps } from '../lib/rangeFacets.js'
// Client leads (0462/0463) — the DEMAND half of the Leads pool. Separate feed for the
// same reason BusinessFeed is: a requirement has no address, photo or coordinates.
import { ClientLeadsFeed } from './desk/ClientLeadsFeed.jsx'
import { PossibleMatchesShelf, possibleClusterGroups, SHELF_PAGE } from './desk/PossibleMatches.jsx'
import { HighlightedSearchInput, KEYWORD_CLASS } from './desk/HighlightedSearchInput.jsx'
import { KeywordEditor, isEditableKeyword } from './desk/keywordEditors.jsx'
import { NotificationsPanel } from './desk/NotificationsPanel.jsx'
import { CoreMeetingDetail, MeetingModal } from './desk/MeetingModal.jsx'
// THE one resolver for "where does a meeting keep its time" (its own header: "Exported so no
// surface re-derives it"). This file had two private re-derivations, one of which dropped any
// row whose time lives in `scheduled_at` without an embedded event.
import { meetingScheduledAt as meetingSchedAt } from './desk/MeetingCountdown.jsx'
import { fmtTbilisi } from '../lib/tbilisiTime.js'
import { ShareModal } from './desk/ShareModal.jsx'
import { NegotiationsPanel } from './desk/NegotiationModal.jsx'
import { ContactPill } from './desk/ContactPill.jsx'
import { COOP_INTENT_KEYS, COOP_STATUS_KEYS, CooperationChips, CooperationList, VIEWING_STATUS_STYLE, coopIntentOf, coopLockLabel, coopMeetingSeed, representingSeg, viewingSchedAt, viewingSchedText } from './desk/CooperationChips.jsx'
import { ResultCount } from './desk/ResultCount.jsx'
import { AddressDiffLine, AreaPreview, ENTRY_TYPE_FILTER_LABEL_KEYS, EntriesMapView, FIELD_LABEL_KEYS, FactsDiffLine, HistoryGroup, HistoryItem, OBJECT_ICON, OBJECT_TYPE, PlatformPill, STATUS_LABEL_KEYS, availableFromBadge, formatListingSummary, groupHistory, kindOf, listingAgeBadge, marketReportBadge, objectTypeOf, relTime, summarize } from './desk/HistoryCards.jsx'
import { DateInput } from './SegmentedInput.jsx'
import { fmtPhone } from '../lib/phone.js'

// How many projected filter keywords the search field shows before collapsing to "+N"
// (design §1: "~3, exact N by width"). The cap is 3; the actual number comes from the field's
// MEASURED width, because the same pill is ~340px in the strip with the tools panel open and
// ~940px on a wide screen — a fixed 3 turned three keywords into unreadable 7px slivers at
// the narrow end (measured 2026-07-24). Measured on resize only, never per keystroke.
// Stable empty read-state (0447) — a fresh object here would make every consumer
// memo that depends on it recompute on each render.
const NO_READ_STATE = { read: new Set(), dismissed: new Set() }

const KW_INLINE_MAX = 3
// Everything in the pill that is NOT keywords: magnifier + a typable input floor + 📷 +
// category select. Whatever is left over is the keyword budget, ~110px per keyword (a
// Georgian district label + gap).
const KW_PILL_RESERVED_PX = 276
const KW_SLOT_PX = 110
const kwCapacityFor = w => Math.max(0, Math.min(KW_INLINE_MAX, Math.floor((w - KW_PILL_RESERVED_PX) / KW_SLOT_PX)))

// ─── Desk buttons ───────────────────────────────────────────────────────────
// 'candidate' intentionally removed from the Desk (2026-07-03) — not relevant to the paste-intake
// flow; candidates are created from the Candidates tab. 'meeting' removed 2026-07-06 — a listing
// card already has its own "Add meeting" (onAddMeeting), so a second entry point here was
// redundant. The two left double as the disambiguation picker when the AI is unsure (client/property).
// icon = Icon.jsx registry name (rendered via <Icon/>), not an emoji.
// EXPORTED (2026-08-04): App.jsx's mobile "+" menu had its own hardcoded copy of these three
// ids, so adding 'meeting' here reached the desktop dropdown and not the phone — the owner
// found it missing on mobile the same day. Two lists for one menu is one list too many.
export const DESK_BUTTONS = [
  { id: 'client',    labelKey: 'desk_add_client',    icon: 'user-check' },
  { id: 'property',  labelKey: 'desk_add_property',  icon: 'house' },
  // 'business' (2026-07-17, user-directed third slot): still PROPERTY-FIRST under the hood
  // (BUSINESS_ARCHITECTURE.md §7 — a business always starts from a property) — the modal's
  // first step is a property picker, then the same BusinessSection add flow the property
  // card uses. This is a front door to that flow, not a standalone create path.
  { id: 'business',  labelKey: 'desk_add_business',  icon: 'store' },
  // 'meeting' restored 2026-08-03 (owner): it was removed in 2026-07-06 because every card
  // face carries its own "Add meeting" — but that only books a meeting about a property you
  // already have on screen. This is the door for the other case (a client rang, no card in
  // front of you). It routes to the SAME core MeetingModal every other entry point uses
  // (MEETINGS_MODEL §3 — one destination), through a property pick so the seed is real.
  { id: 'meeting',   labelKey: 'desk_add_meeting',   icon: 'calendar' },
  // 'candidate' is BACK (owner 2026-09-09), and it is not the 2026-07-03 entry the
  // comment above says was removed: that one tried to make a candidate out of a pasted
  // message, which is why it did not belong here. This one is a DOOR — it routes to the
  // Candidates tab's own modal, the one creation path (CandidatesTab → useCandidates.create,
  // which is where authorship and office are stamped). Nothing is created on the Desk.
  // ⚠️ `cap` is the first entry with one, and it is why both menus filter rather than
  // render blindly: hiring is management's, and an agent must not see the door at all
  // (owner: "agents shouldn't see it"). tab.candidates is the SAME capability that shows
  // the tab, so a role that gains hiring later needs no edit here.
  { id: 'candidate', labelKey: 'desk_add_candidate', icon: 'briefcase', cap: 'tab.candidates' },
]
// Below this the AI's stance (about) is treated as unsure → the human picks instead of a modal
// auto-opening. 0.85 per the intake-box UX design (2026-07-03).
const CONFIDENCE_THRESHOLD = 0.85

// The Desk-level paste box is retired from the UI (2026-07-04) — parsing now lives INSIDE the
// Add-client / Add-property modals, next to the fields it fills. The whole parse flow (handleParse,
// envelope routing, glow matching) is kept intact behind this flag, not deleted: it may come back
// as an "AI search bar" once there's a reason. Flip to true to restore the old behavior.
const SHOW_MAIN_PARSER = false
// glowAmber / glowBlue — moved to ./desk/primitives.jsx (plan §3 decomposition).

// Moved out of the right sidebar and into the entries grid/list itself (2026-07-06): always the
// very first slot, split in half — Add client | Add property — sized to match whichever view
// (list/card/gallery) it's sitting in, so it reads as "one of the cards", not a floating toolbar.
// fixedHeight/feedColumns (Desk home, 2026-07-17): on the home page the tile spans the
// full content width with NO sibling card to pin to — unpinned, its 24-row feed ran down
// the whole page (user: "notifications run through whole screen"). fixedHeight caps it
// into a short strip with internal scroll; feedColumns flows the rows into a responsive
// multi-column grid so the width is spent on columns, not one long line per row.
// Add-business front door (2026-07-17): property-first per BUSINESS_ARCHITECTURE.md §7 —
// step 1 picks a property from the agent's own LOADED listings (cheap, no new endpoint;
// a property not in the current feed window is reachable via its card's own facet), step 2
// embeds the exact same BusinessSection add flow the property card uses. No parallel
// create path exists — this modal only routes to the canonical one.
function AddBusinessModal({ listings, onClose }) {
  const [q, setQ] = useState('')
  const [picked, setPicked] = useState(null)   // { property_id, label }
  const options = useMemo(() => {
    const seen = new Set(), out = []
    for (const l of listings || []) {
      if (!l.property_id || seen.has(l.property_id)) continue
      seen.add(l.property_id)
      out.push({
        property_id: l.property_id,
        label: [l.address || l.district_code, l.kind, l.price != null ? `${l.price} ${l.currency || ''}`.trim() : null]
          .filter(Boolean).join(' · ') || l.property_id.slice(0, 8),
      })
    }
    return out
  }, [listings])
  const ql = q.trim().toLowerCase()
  const shown = ql ? options.filter(o => o.label.toLowerCase().includes(ql)) : options
  return (
    <Modal title={t('desk_add_business')} onClose={onClose}>
      {!picked ? (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">{t('desk_add_business_pick_property')}</p>
          <input className={input} autoFocus value={q} onChange={e => setQ(e.target.value)}
            placeholder={t('desk_add_business_search_ph')} />
          <div className="max-h-72 overflow-y-auto space-y-1">
            {shown.slice(0, 30).map(o => (
              <button key={o.property_id} onClick={() => setPicked(o)}
                className="w-full text-left px-3 py-2 rounded-lg border border-border hover:bg-accent text-sm">
                <Icon name="house" /> {o.label}
              </button>
            ))}
            {shown.length === 0 && <p className="text-xs text-muted-foreground">{t('desk_add_business_none')}</p>}
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <button className="text-xs text-primary hover:underline" onClick={() => setPicked(null)}>← 🏠 {picked.label}</button>
          <BusinessSection propertyId={picked.property_id} />
        </div>
      )}
    </Modal>
  )
}
// Add-meeting front door (2026-08-03, owner: "add meeting in the add dropdown"). Like
// AddBusinessModal this creates NOTHING itself — it only picks the property so the canonical
// MeetingModal opens with a real seed (the card-face "Add meeting" already arrives seeded).
// "No property yet" is a first-class choice, not a fallback: MEETINGS_MODEL §3's client-side
// entry is a meeting that legitimately has no property, and the modal's client picker still
// works (it just can't offer property-matched clients).
//
// ⚠️ THE OWNER'S NUMBER IS PER-ROW, NEVER PRINTED FOR THE WHOLE LIST (owner asked for
// "name and number"; this is the half that can be honoured and the half that cannot).
// Feed rows carry NO phone at all — shapeListingRow returns author_name and stops there,
// because contacts are click-to-reveal (C1) and each reveal is a deliberate act against one
// property. Rendering 30 numbers would mean 30 getPropertyContacts calls AND a bulk reveal
// of exactly what that design exists to prevent. So: the NAME rides on every row (it is
// already on the card faces), and the NUMBER is one click on the row you actually mean —
// resolved by the same ranked resolver the card chips use, viewer-masked server-side.
// (Reach-back hits are the exception and NOT an inconsistency: /listings/search already ran
// that same ranked resolver server-side for the ≤25 rows it returns, so their contact is
// already in hand — asking for it again would be a second call for an answer we have.)
//
// ⚠️ TWO ROW SHAPES MEET HERE. The loaded feed speaks shapeListingRow (rooms/floor/area_sqm/
// currency/author_name), /listings/search speaks the MEETING-SEED shape (rooms_count/
// apt_floor/area_sq_m/price_unit/contact_name). Normalizing at the door — every pick leaves
// as one seed shape — is what lets the modal and the seed mapping stop caring which door the
// row came through. Adding a field means adding it to `normRow`, not to the render.
const ADDMEETING_DEAL = { sale: 'trade', rent: 'lease' }
// The property line every meeting-seed door can render (owner 2026-08-03: the modal's
// ობიექტი slot showed a street and nothing else, so two flats on the same street were
// indistinguishable at the moment of booking). Reads off the SEED shape, so the card faces
// and the lineage modal get it too — they already carry these fields.
function meetingPropertyFacts(s) {
  if (!s) return null
  return [
    s.rooms_count != null ? tLog('desk_entry_rooms_abbrev', { rooms: s.rooms_count }) : null,
    s.area_sq_m != null ? tLog('desk_entry_area_abbrev', { area: s.area_sq_m }) : null,
    s.apt_floor != null ? (s.total_floors != null
      ? tLog('desk_entry_floor_position', { floor: s.apt_floor, total: s.total_floors })
      : tLog('desk_entry_floor_only', { floor: s.apt_floor })) : null,
    s.price != null ? `${s.price} ${s.price_unit || ''}`.trim() : null,
  ].filter(Boolean).join(' · ') || null
}
function normRow(l, remote) {
  return remote ? {
    __source: 'core', id: l.id || null, property_id: l.property_id || null, address: l.address || null,
    rooms_count: l.rooms_count ?? null, apt_floor: l.apt_floor ?? null, total_floors: l.total_floors ?? null,
    area_sq_m: l.area_sq_m ?? null, deal_type: l.deal_type || null, price: l.price ?? null,
    price_unit: l.price_unit || null, source_url: l.source_url || null,
    contact_name: l.contact_name || null, contact_phone: l.contact_phone || null,
    contacts_ranked: l.contacts_ranked?.length ? l.contacts_ranked : null,
    default_contact_party_id: l.default_contact_party_id || null,
    cooperation: l.cooperation || null,
  } : {
    __source: 'core', id: l.listing_id || null, property_id: l.property_id || null, address: l.address || null,
    rooms_count: l.rooms ?? null, apt_floor: l.floor ?? null, total_floors: l.floors_total ?? null,
    area_sq_m: l.area_sqm ?? null, deal_type: ADDMEETING_DEAL[l.intent] || l.intent || null,
    price: l.price ?? null, price_unit: l.currency || null, source_url: l.source_url || null,
    // On a reshare the row's author is OUR OWN agent, not the owner — same rule the card
    // meeting seed follows (blank beats wrong). The phone is not on the row at all.
    contact_name: l.is_reshare ? null : (l.author_name || null), contact_phone: null,
    contacts_ranked: null, default_contact_party_id: null, cooperation: null,
  }
}
function AddMeetingModal({ listings, onPick, onClose, agentId, scopeAll, initialQuery = '' }) {
  // initialQuery: the property the pasted message named (envelope.meeting.property_hint) — typed
  // into the search for the agent, who still picks the real row (2026-09-23).
  const [q, setQ] = useState(() => String(initialQuery || '').slice(0, 120))
  // Phone / listing-id reach-back (B260 'identity'): the loaded feed window is a few hundred
  // rows, so "find it by the owner's number" cannot be a local filter — it is the same
  // DB-wide search the Desk box runs, and it answers WITHOUT ever printing the number.
  const [remote, setRemote] = useState([])
  const [searching, setSearching] = useState(false)
  const [revealed, setRevealed] = useState({})   // property_id → 'loading' | {name, phone} | 'none'
  useEffect(() => {
    const digits = q.replace(/\D/g, '')
    // Digits only: this endpoint takes a phone or a portal listing id, never a name or an
    // address (those are the local filter below). 6 is short enough for a pasted id, long
    // enough that a house number in an address query never fires a request.
    if (digits.length < 6 || digits.length !== q.trim().length) { setRemote([]); setSearching(false); return }
    let gone = false
    setSearching(true)
    const timer = setTimeout(() => {
      searchCoreListings(q.trim(), { agentId: scopeAll ? undefined : agentId, widen: 'identity' })
        .then(rows => { if (!gone) setRemote(rows || []) })
        .catch(() => { if (!gone) setRemote([]) })
        .finally(() => { if (!gone) setSearching(false) })
    }, 400)
    return () => { gone = true; clearTimeout(timer) }
  }, [q, agentId, scopeAll])
  const options = useMemo(() => {
    const seen = new Set(), out = []
    // Reach-back hits FIRST: the agent typed a number to find one specific property, and
    // burying it under the loaded page would make the search look like it did nothing.
    for (const [src, isRemote] of [[remote, true], [listings || [], false]]) {
      for (const raw of src) {
        if (!raw.property_id || seen.has(raw.property_id)) continue
        seen.add(raw.property_id)
        const row = normRow(raw, isRemote)
        out.push({
          row, remote: isRemote,
          address: row.address || raw.district_code || row.property_id.slice(0, 8),
          // Property details, not just a street (owner 2026-08-03: "show more info like
          // property details") — the same facts the card face leads with, in the same
          // order, so a row here reads like the card it stands for.
          facts: [
            row.rooms_count != null ? tLog('desk_entry_rooms_abbrev', { rooms: row.rooms_count }) : null,
            row.area_sq_m != null ? tLog('desk_entry_area_abbrev', { area: row.area_sq_m }) : null,
            row.apt_floor != null ? (row.total_floors != null
              ? tLog('desk_entry_floor_position', { floor: row.apt_floor, total: row.total_floors })
              : tLog('desk_entry_floor_only', { floor: row.apt_floor })) : null,
            row.price != null ? `${row.price} ${row.price_unit || ''}`.trim() : null,
          ].filter(Boolean).join(' · '),
          owner: row.contact_name,
          phone: row.contact_phone,
        })
      }
    }
    return out
  }, [listings, remote])
  const ql = q.trim().toLowerCase()
  // ⚠️ The text filter applies to LOADED rows ONLY. A reach-back hit matched on the owner's
  // phone, which appears nowhere in its address line — filtering it by the same string threw
  // away every server result the moment it arrived, and the picker reported "not found" for a
  // property the agent could see on their own Desk (owner-caught 2026-08-03, number
  // 579066633 → გიგა, two authored listings).
  const shown = ql ? options.filter(o => o.remote || o.address.toLowerCase().includes(ql) || (o.owner || '').toLowerCase().includes(ql)) : options
  async function reveal(propertyId) {
    if (!propertyId || revealed[propertyId]) return
    setRevealed(r => ({ ...r, [propertyId]: 'loading' }))
    const pc = await getPropertyContacts(propertyId).catch(() => null)
    const dflt = pc?.contacts?.find(c => c.party_id === pc.default_party_id) || null
    setRevealed(r => ({ ...r, [propertyId]: dflt && dflt.phone ? { name: dflt.name, phone: dflt.phone } : 'none' }))
  }
  return (
    <Modal title={t('desk_add_meeting')} onClose={onClose}>
      <div className="space-y-2">
        <p className="text-xs text-muted-foreground">{t('desk_add_meeting_pick_property')}</p>
        <input className={input} autoFocus value={q} onChange={e => setQ(e.target.value)}
          placeholder={t('desk_add_meeting_search_ph')} />
        {searching && <p className="text-xs text-muted-foreground">{t('desk_add_meeting_searching')}</p>}
        <div className="max-h-72 overflow-y-auto space-y-1">
          {shown.slice(0, 30).map(o => {
            const rv = revealed[o.row.property_id]
            const name = (rv && rv !== 'loading' && rv !== 'none' && rv.name) || o.owner
            const phone = o.phone || (rv && rv !== 'loading' && rv !== 'none' ? rv.phone : null)
            return (
              <div key={o.row.property_id}
                className="w-full px-3 py-2 rounded-lg border border-border hover:bg-accent text-sm cursor-pointer"
                onClick={() => onPick(o.row)}>
                <div className="truncate"><Icon name="house" /> {o.address}</div>
                {o.facts && <div className="text-xs text-muted-foreground truncate">{o.facts}</div>}
                <div className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5">
                  <span className="truncate">
                    <Icon name="user" size={12} /> {name || t('desk_add_meeting_owner_unknown')}
                  </span>
                  {/* Reveal stops the click from bubbling into the pick — asking who to call
                      is not the same act as booking the meeting. */}
                  {phone ? <span className="text-foreground shrink-0">{fmtPhone(phone)}</span>
                    : rv === 'loading' ? <span>…</span>
                    : rv === 'none' ? <span>{t('desk_add_meeting_no_contact')}</span>
                    : <button type="button" className="text-primary hover:underline shrink-0"
                        onClick={e => { e.stopPropagation(); reveal(o.row.property_id) }}>{t('desk_add_meeting_reveal')}</button>}
                </div>
              </div>
            )
          })}
          {/* Nothing matched — and the honest reason depends on what was asked. A number
              searched the WHOLE database (the reach-back above), so "not in the loaded
              list" would be a lie there; a name or address only filtered what is loaded. */}
          {shown.length === 0 && !searching && (
            <p className="text-xs text-muted-foreground">
              {q.trim() && /^\d{6,}$/.test(q.replace(/\D/g, '')) && q.replace(/\D/g, '').length === q.trim().length
                ? t('desk_add_meeting_none_anywhere') : t('desk_add_business_none')}
            </p>
          )}
        </div>
        {/* A dead end is not an answer (owner 2026-08-03). The agent has a client on the
            phone NOW; the property they are describing may not be in our data at all yet.
            Booking with what is known — the number, the name, whatever was typed — and
            completing it later beats losing the meeting, so the typed fragment travels into
            the meeting as an OPEN QUESTION on its notes (and, when it is a number, into the
            client quick-add) instead of being thrown away with the search box.
            ⚠️ Offered even when rows DID come back (owner 2026-08-03: "what if things were
            found but not correct"). A match is a candidate, not a verdict — one phone can
            author several ads and none of them be the flat being discussed — so "it isn't in
            this list" has to stay reachable at the end of every list, not only empty ones.
            Filled only when the list is empty (then it is the sole way forward); quiet
            underneath results, where it is the second-choice answer. */}
        {q.trim() && !searching && (
          <button className={shown.length === 0 ? btn('primary') + ' w-full' : 'text-xs text-primary hover:underline text-left'}
            onClick={() => onPick({ unresolved: q.trim() })}>
            {t('desk_add_meeting_anyway')}
          </button>
        )}
        <button className="text-xs text-primary hover:underline" onClick={() => onPick({})}>
          {t('desk_add_meeting_no_property')}
        </button>
      </div>
    </Modal>
  )
}
// AddEntryTile — DELETED 2026-08-03 (owner). The big Add-client | Add-property | Add-business
// bar was the last surface duplicating what the + დამატება button's menu already offers; home
// was its only remaining caller (browse lost it with the §6 CommandStrip on 2026-07-24). The
// menu is now the single door on both, so the tile had no reason left to exist.

// Modal — moved to ./desk/primitives.jsx (plan §3 decomposition).

// listingDeepLink / CopyListingLink / WhatsAppShare — moved to ./desk/MatchSuggestions.jsx (plan §3 decomposition).

// ─── dot-path patch helper (for clarification answers → draft command) ──────
// Supports "a.b.c" and "a.b[0].c"; creates missing objects/arrays as needed.
// Used only for a handful of AI-suggested paths, so kept intentionally simple.
function patchByPath(obj, path, value) {
  if (!path) return obj
  const segs = String(path).replace(/\[(\d+)\]/g, '.$1').split('.').filter(Boolean)
  const clone = JSON.parse(JSON.stringify(obj || {}))
  let cur = clone
  for (let i = 0; i < segs.length - 1; i++) {
    const k = segs[i]
    if (cur[k] == null || typeof cur[k] !== 'object') cur[k] = /^\d+$/.test(segs[i + 1]) ? [] : {}
    cur = cur[k]
  }
  cur[segs[segs.length - 1]] = value
  return clone
}

// ─── Envelope ⇄ review-shape adapters ──────────────────────────────────────
// extractRequirementCmd / amendRequirementCmd — moved to ./desk/ManualClientModal.jsx (plan §3 decomposition).
function viewToCmd(view, originalCmd) {
  return {
    ...originalCmd,
    type: 'extract_requirement',
    person: {
      ...(originalCmd.person || {}),
      display_name: view.party?.name ?? originalCmd.person?.display_name ?? null,
      phone:        view.party?.phone ?? originalCmd.person?.phone ?? null,
      email:        view.party?.email ?? originalCmd.person?.email ?? null,
      kind:         view.party?.kind ?? originalCmd.person?.kind ?? null,
    },
    requirement: view.requirement || {},
    criteria: view.criteria || {},
  }
}
// Scalar → 'set', array → 'replace'; only fields this message actually revealed (absence
// is never interpreted — core/INTAKE_CONTRACT.md amend_requirement rule). Used only when a
// human overrides the AI's "new" call and picks an existing entry themselves — when the AI
// itself decides "amend", it already emits real ops directly (see buildEnvelopePrompt).
const ARRAY_CRITERIA_FIELDS = ['district_codes', 'excluded_district_codes', 'excluded_complex_codes', 'complex_codes',
  'must_have_codes', 'nice_to_have_codes', 'must_not_have_codes', 'condition_codes', 'condition_exclude_codes']
function opsFromCriteria(criteria) {
  const ops = []
  const c = criteria || {}
  for (const [field, value] of Object.entries(c)) {
    if (value === undefined || value === null) continue
    // normalizeEnvelope defaults currency to USD even when the message named no price —
    // sending that would silently flip a GEL demand. Only carry currency alongside a price.
    if (field === 'currency' && c.price_min == null && c.price_max == null) continue
    if (Array.isArray(value)) { if (value.length) ops.push({ field, op: 'replace', value }) }
    else ops.push({ field, op: 'set', value })
  }
  return ops
}

// The AI's clarification target_field paths use the COMMAND shape (person.display_name);
// the review view uses party.name/phone/email. Without this mapping, person.* answers
// patched a stray key the commit adapter never read (bug found 2026-07-02).
function mapTargetField(path) {
  const p = String(path || '')
  if (p.startsWith('person.contacts')) return 'party.phone'   // contacts questions are about the lifted primary
  return p
    .replace(/^person\.display_name$/, 'party.name')
    .replace(/^person\./, 'party.')
}

// ─── Clarify — chat-style, one question at a time, capped at 3 ─────────────
const IMPORTANCE_RANK = { high: 3, medium: 2, low: 1 }
// ⚠️ A QUESTION MUST HAVE SOMEWHERE TO LAND (form audit I38; owner 2026-10-12: "let's do it"). The model may return any
// question it likes; the agent is only asked one that (a) names a field this review actually holds — the person, the
// request or its criteria — so the answer changes something, and (b) is the only question about that field. A question
// with no target, or a second one about the same field, is questionnaire expansion and is dropped here, whatever the
// prompt allowed. Still capped at three, still skippable, still logged.
const CLARIFY_ROOTS = /^(party|requirement|criteria)\./
export function usableClarifications(clarifications) {
  const seen = new Set()
  return [...(clarifications || [])]
    .filter(q => q && q.question && CLARIFY_ROOTS.test(mapTargetField(q.target_field)))
    .sort((a, b) => (IMPORTANCE_RANK[b.importance] || 0) - (IMPORTANCE_RANK[a.importance] || 0))
    .filter(q => { const k = mapTargetField(q.target_field); if (seen.has(k)) return false; seen.add(k); return true })
    .slice(0, 3)
}
function readByPath(obj, path) {
  let cur = obj
  for (const k of String(path || '').replace(/\[(\d+)\]/g, '.$1').split('.').filter(Boolean)) { if (cur == null) return undefined; cur = cur[k] }
  return cur
}
function ClarifyChat({ clarifications, onAnswer, onDone, view }) {
  const queue = useMemo(() => usableClarifications(clarifications), [clarifications])
  // what the AI read for the field the question is about — shown beside the question, so "yes / no" is answered
  // against the value itself and not from memory of the pasted text
  const readingOf = q => { const v = readByPath(view, mapTargetField(q.target_field))
    return v == null || v === '' || (Array.isArray(v) && !v.length) || typeof v === 'object' && !Array.isArray(v) ? null : Array.isArray(v) ? v.join(', ') : String(v) }
  const [idx, setIdx] = useState(0)
  const [log, setLog] = useState([])
  const [textVal, setTextVal] = useState('')

  if (queue.length === 0) return null
  const current = idx < queue.length ? queue[idx] : null

  function respond(value, label) {
    onAnswer(current, value)
    setLog(l => [...l, { question: current.question, label }])
    setTextVal('')
    const next = idx + 1
    setIdx(next)
    if (next >= queue.length) onDone()
  }

  return (
    <div className="space-y-2 mb-4">
      {log.map((l, i) => (
        <div key={i} className="text-xs bg-muted/40 rounded-lg px-3 py-2">
          <span className="text-muted-foreground">{l.question}</span> → <span className="font-medium">{l.label}</span>
        </div>
      ))}
      {current && (
        <div className="bg-muted/60 rounded-lg px-3 py-2.5 space-y-2">
          <p className="text-sm"><Icon name="bot" /> {current.question}</p>
          {readingOf(current) && <p className="text-xs text-muted-foreground">→ {readingOf(current)}</p>}
          {current.type === 'confirm' ? (
            <div className="flex flex-wrap gap-2">
              <button className={chip} onClick={() => respond(true, t('desk_clarify_yes'))}>{t('desk_clarify_yes')}</button>
              <button className={chip} onClick={() => respond(false, t('desk_clarify_no'))}>{t('desk_clarify_no')}</button>
              <button className={chip} onClick={() => respond(null, t('desk_clarify_dont_know'))}>{t('desk_clarify_dont_know')}</button>
              <button className={chip + ' opacity-60'} onClick={() => respond(undefined, 'Skipped')}>{t('desk_clarify_skip')}</button>
            </div>
          ) : (
            <div className="flex gap-2">
              <input className={input + ' text-sm'} value={textVal} onChange={e => setTextVal(e.target.value)}
                placeholder={t('desk_clarify_type_answer')}
                onKeyDown={e => { if (e.key === 'Enter' && textVal.trim()) respond(textVal.trim(), textVal.trim()) }} />
              <button className={btn('ghost')} disabled={!textVal.trim()} onClick={() => respond(textVal.trim(), textVal.trim())}>↵</button>
              <button className={btn('ghost')} onClick={() => respond(undefined, 'Skipped')}>{t('desk_clarify_skip')}</button>
            </div>
          )}
        </div>
      )}
      {!current && <p className="text-xs text-muted-foreground"><IconText>{t('desk_clarify_got_it')}</IconText></p>}
    </div>
  )
}

// ─── Client review (AI draft → clarify → human review → commit) ────────────
// mode 'create' shows the full requirement; mode 'amend' shows the ops that will change on
// the existing demand named by `targetLabel` — no need to fetch/diff the old values (the ops
// already say exactly what's changing; core's own amend rule: absent field = untouched).
function ClientReview({ mode, view, ops, targetLabel, decision, confidence, clarifications, districts, features, onAnswerClarify, onAccept, onReject, loading }) {
  const [edited, setEdited] = useState(null)
  const [editedOps, setEditedOps] = useState(null)
  const [jsonMode, setJsonMode] = useState(false)
  const [jsonText, setJsonText] = useState('')
  const [jsonErr, setJsonErr] = useState(null)
  // Amend mode skips the chat: its answers would have nowhere to land (the ops list already
  // reflects the message; wiring answers into ops is deferred until real usage needs it).
  const [clarifyDone, setClarifyDone] = useState(mode === 'amend' || usableClarifications(clarifications).length === 0)
  const [clarifyLog, setClarifyLog] = useState([])

  const current = edited || view
  const currentOps = editedOps || ops

  const districtName = code => { const d = districts.find(d => d.code === code); return d ? (d.name_en || d.name_ka || code) : code }
  const featureName  = code => { const f = features.find(f => f.code === code); return f ? (f.name_en || f.name_ka || code) : code }

  function handleClarifyAnswer(q, value) {
    // Every answer (incl. skips) is recorded and lands on the committed proposal as
    // clarification_answers — the audit trail of what the human was asked and said.
    setClarifyLog(l => [...l, { id: q.id, question: q.question, answer: value === undefined ? 'skipped' : value }])
    if (value === undefined) return           // Skip → the AI's guess stands, unmarked
    const path = mapTargetField(q.target_field)
    if (q.type === 'confirm') {
      if (value === true) return              // Yes → confirmed, keep the AI's value
      // No / Don't know → the AI's value for that field is unreliable → clear it (the human
      // fixes it on the review card right after; "don't know" honestly stays unknown).
      setEdited(v => patchByPath(v || view, path, null))
    } else {
      let v = value
      if (typeof v === 'string' && v.trim() !== '' && !isNaN(Number(v)) &&
          /price|area|rooms|floor|occupants|months|minutes|sqm|bedrooms/.test(path)) v = Number(v)
      setEdited(prev => patchByPath(prev || view, path, v))
    }
  }

  const handleJsonSave = () => {
    try {
      if (mode === 'amend') { setEditedOps(JSON.parse(jsonText)) } else { setEdited(JSON.parse(jsonText)) }
      setJsonMode(false); setJsonErr(null)
    } catch (e) { setJsonErr(e.message) }
  }

  if (!clarifyDone) {
    return (
      <div className="space-y-3">
        <div className="flex flex-wrap gap-2 items-center">
          {decision && <Badge label={tLog('desk_review_decision_badge', { decision })} variant="blue" />}
          {confidence != null && <Badge label={tLog('desk_review_confidence_badge', { pct: Math.round(confidence * 100) })} />}
        </div>
        <ClarifyChat clarifications={clarifications} view={view} onAnswer={handleClarifyAnswer} onDone={() => setClarifyDone(true)} />
      </div>
    )
  }

  if (mode === 'amend') {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex flex-wrap gap-2 items-center">
            <Badge label={t('desk_review_continuing_demand')} variant="green" />
            {targetLabel && <span className="text-sm text-muted-foreground">{targetLabel}</span>}
          </div>
          <button className={btn('ghost')} onClick={() => {
            setJsonText(JSON.stringify(currentOps, null, 2)); setJsonMode(!jsonMode); setJsonErr(null)
          }}>{jsonMode ? t('desk_review_preview') : t('desk_review_edit_json')}</button>
        </div>
        {jsonMode ? (
          <div className="space-y-2">
            <textarea className={`${input} font-mono text-xs`} rows={14} value={jsonText} onChange={e => setJsonText(e.target.value)} />
            {jsonErr && <p className="text-xs text-destructive">{jsonErr}</p>}
            <button className={btn('ghost')} onClick={handleJsonSave}>{t('desk_review_apply_edits')}</button>
          </div>
        ) : (
          <div className={card}>
            <p className="text-xs font-semibold text-muted-foreground mb-2">{t('desk_review_fields_will_change')}</p>
            {(currentOps || []).length === 0 && <p className="text-sm text-muted-foreground">{t('desk_review_nothing_to_change')}</p>}
            <div className="space-y-1 text-sm">
              {(currentOps || []).map((op, i) => (
                <div key={i} className="flex gap-2">
                  <span className="text-muted-foreground w-40 shrink-0">{op.field}</span>
                  <span className="text-muted-foreground">{op.op}</span>
                  <span className="font-medium">{Array.isArray(op.value) ? op.value.join(', ') : String(op.value)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="flex gap-2 pt-2">
          <button className={btn('primary')} disabled={loading} onClick={() => onAccept({ ops: currentOps }, !!editedOps, clarifyLog)}>
            {loading ? t('desk_review_saving') : editedOps ? t('desk_review_accept_edited_commit') : t('desk_review_accept_commit')}
          </button>
          <button className={btn('destructive')} disabled={loading} onClick={onReject}><IconText>{t('desk_review_reject')}</IconText></button>
        </div>
      </div>
    )
  }

  const req  = current.requirement || {}
  const crit = current.criteria    || {}
  const party = current.party      || {}
  const distCodes = crit.district_codes || [], exclDist = crit.excluded_district_codes || []
  const mustHave  = crit.must_have_codes || [], niceHave = crit.nice_to_have_codes || []
  const mustNot   = crit.must_not_have_codes || []
  const condCodes = crit.condition_codes || [], condExcl = crit.condition_exclude_codes || []

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex flex-wrap gap-2 items-center">
          {decision && <Badge label={`decision: ${decision}`} variant="blue" />}
          {confidence != null && <Badge label={`confidence: ${Math.round(confidence * 100)}%`} />}
        </div>
        <button className={btn('ghost')} onClick={() => {
          setJsonText(JSON.stringify(current, null, 2)); setJsonMode(!jsonMode); setJsonErr(null)
        }}>
          {jsonMode ? t('desk_review_preview') : t('desk_review_edit_json')}
        </button>
      </div>

      {jsonMode ? (
        <div className="space-y-2">
          <textarea className={`${input} font-mono text-xs`} rows={18} value={jsonText} onChange={e => setJsonText(e.target.value)} />
          {jsonErr && <p className="text-xs text-destructive">{jsonErr}</p>}
          <button className={btn('ghost')} onClick={handleJsonSave}>{t('desk_review_apply_edits')}</button>
        </div>
      ) : (
        <div className="grid gap-3">
          {(party.name || party.phone || party.email) && (
            <div className={card}>
              <p className="text-xs font-semibold text-muted-foreground mb-1">{t('desk_review_section_party')}</p>
              <div className="flex flex-wrap gap-3 text-sm">
                {party.name  && <span><Icon name="user" /> {party.name}</span>}
                {party.phone && <span><Icon name="phone" /> {party.phone}</span>}
                {party.email && <span><Icon name="mail" /> {party.email}</span>}
                {party.kind  && <Badge label={party.kind} />}
              </div>
            </div>
          )}

          <div className={card}>
            <p className="text-xs font-semibold text-muted-foreground mb-1">{t('desk_review_section_intent')}</p>
            <div className="flex flex-wrap gap-2 text-sm">
              <Badge label={req.intent || '?'} variant="blue" />
              <Badge label={req.property_category || '?'} />
              {req.urgency && <Badge label={`urgency: ${req.urgency}`} variant="yellow" />}
            </div>
            {req.notes && <p className="mt-2 text-sm text-muted-foreground">{req.notes}</p>}
          </div>

          <div className={card}>
            <p className="text-xs font-semibold text-muted-foreground mb-2">{t('desk_review_section_criteria')}</p>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
              {(crit.price_min != null || crit.price_max != null) && (
                <Row label={t('desk_review_field_price')}>{crit.price_min ?? '?'} – {crit.price_max ?? '?'} {crit.currency || ''}</Row>
              )}
              {(crit.area_sqm_min != null || crit.area_sqm_max != null) && (
                <Row label={t('desk_review_field_area_sqm')}>{crit.area_sqm_min ?? '?'} – {crit.area_sqm_max ?? '?'}</Row>
              )}
              {(crit.land_sqm_min != null || crit.land_sqm_max != null) && (
                <Row label={t('desk_review_field_land_sqm')}>{crit.land_sqm_min ?? '?'} – {crit.land_sqm_max ?? '?'}</Row>
              )}
              {(crit.rooms_min != null || crit.rooms_max != null) && (
                <Row label={t('desk_review_field_rooms')}>{crit.rooms_min ?? '?'} – {crit.rooms_max ?? '?'}</Row>
              )}
              {(crit.bedrooms_min != null || crit.bedrooms_max != null) && (
                <Row label={t('desk_review_field_bedrooms')}>{crit.bedrooms_min ?? '?'} – {crit.bedrooms_max ?? '?'}</Row>
              )}
              {crit.max_drive_minutes != null && <span className="col-span-2">{tLog('desk_review_drive_max', { n: crit.max_drive_minutes })}</span>}
              {crit.mortgage_ready && <Row label={t('desk_review_label_mortgage')}>{t('desk_tristate_yes')}</Row>}
              {crit.needs_deeded_parking && <Row label={t('desk_crit_parking_deeded')}>{t('desk_tristate_yes')}</Row>}
              {crit.has_pets       && <Row label={t('desk_review_label_pets')}>{t('desk_tristate_yes')}</Row>}
              {crit.has_children   && <Row label={t('desk_review_label_children')}>{t('desk_tristate_yes')}</Row>}
            </div>
          </div>

          {(distCodes.length > 0 || exclDist.length > 0) && (
            <div className={card}>
              <p className="text-xs font-semibold text-muted-foreground mb-2">{t('desk_review_section_districts')}</p>
              {distCodes.length > 0 && <div className="flex flex-wrap gap-1 mb-1">{distCodes.map(c => <Badge key={c} label={districtName(c)} variant="green" />)}</div>}
              {exclDist.length > 0 && <div className="flex flex-wrap gap-1"><span className="text-xs text-muted-foreground mr-1">{t('desk_review_exclude_prefix')}</span>{exclDist.map(c => <Badge key={c} label={districtName(c)} variant="red" />)}</div>}
            </div>
          )}

          {(mustHave.length > 0 || niceHave.length > 0 || mustNot.length > 0 || condCodes.length > 0) && (
            <div className={card}>
              <p className="text-xs font-semibold text-muted-foreground mb-2">{t('desk_review_section_features')}</p>
              {condCodes.length > 0 && <FeatureGroup label={t('desk_review_feature_condition')} codes={condCodes} getName={featureName} variant="blue" />}
              {condExcl.length > 0 && <FeatureGroup label={t('desk_review_feature_cond_exclude')} codes={condExcl} getName={featureName} variant="red" />}
              {mustHave.length > 0 && <FeatureGroup label={t('desk_review_feature_must_have')} codes={mustHave} getName={featureName} variant="green" />}
              {niceHave.length > 0 && <FeatureGroup label={t('desk_review_feature_nice_have')} codes={niceHave} getName={featureName} variant="blue" />}
              {mustNot.length > 0 && <FeatureGroup label={t('desk_review_feature_must_not_have')} codes={mustNot} getName={featureName} variant="red" />}
            </div>
          )}

          {current.raw_notes && (
            <div className={card}>
              <p className="text-xs font-semibold text-muted-foreground mb-1">{t('desk_review_section_ai_explanation')}</p>
              <p className="text-sm">{current.raw_notes}</p>
            </div>
          )}
        </div>
      )}

      <div className="flex gap-2 pt-2">
        <button className={btn('primary')} disabled={loading} onClick={() => onAccept(current, !!edited, clarifyLog)}>
          {loading ? t('desk_review_saving') : edited ? t('desk_review_accept_edited_commit') : t('desk_review_accept_commit')}
        </button>
        <button className={btn('destructive')} disabled={loading} onClick={onReject}><IconText>{t('desk_review_reject')}</IconText></button>
      </div>
    </div>
  )
}

function Row({ label, children }) {
  return <><span className="text-muted-foreground">{label}</span><span>{children}</span></>
}
function FeatureGroup({ label, codes, getName, variant }) {
  return (
    <div className="flex flex-wrap gap-1 mb-1 items-center">
      <span className="text-xs text-muted-foreground w-24 shrink-0">{label}:</span>
      {codes.map(c => <Badge key={c} label={getName(c)} variant={variant} />)}
    </div>
  )
}

// PolygonMapPicker — moved to ./desk/ManualClientModal.jsx (plan §3 decomposition).

// PointMapPicker — moved to ./desk/ManualPropertyModal.jsx (plan §3 decomposition).

// TierTagPicker, criteria vocab (APT_CLASS/PROP_STATE/…), featureDefOf/InfoDot/FeatureDefPanel, CriteriaFieldsForm — moved to ./desk/ManualClientModal.jsx (plan §3 decomposition).

// ─── Client edit — the CURRENT saved demand, not an AI draft ───────────────────
// Distinct from ClientReview above: that one reviews an AI-parsed draft before its FIRST
// commit; this one edits an already-saved requirement's live state (fetched fresh via
// getRequirement — never trusted from the event log, which only proves what changed, not
// what IS). Only requirement_criteria fields are amendable (server v_allowed, migration
// 0039) — intent/property_category/urgency/title/party are shown read-only; changing those
// needs a fresh pasted message, which is the AI's job, not a manual edit's.
// EDITABLE_CRITERIA_FIELDS / ARRAY_EDIT_FIELDS / diffCriteriaOps — moved to ./desk/MatchSuggestions.jsx (plan §3 decomposition).

// Read-only criteria facts for the client modal's view stage — same row style as the
// property modal's "THE AD (as scraped)" table (lineageFieldRow). Null fields just don't
// render; a fully-empty criteria set gets an honest one-liner instead of a blank card.
export function ClientCriteriaSummary({ criteria, districts, features, masked, grouped = false, sourceHint = null }) {
  const c = criteria || {}
  const lang = getLanguage()
  // In the viewer's language (owner 2026-09-17: the Georgian card listed Delisi, Vazha-Pshavela Avenue in English).
  const districtName = code => { const d = districts.find(d => d.code === code); return d ? ((lang === 'ka' ? d.name_ka : d.name_en) || d.name_en || d.name_ka || code) : code }
  const featName = code => { const f = features?.find(x => x.code === code); return f ? (f.name_ka || f.name_en || code) : code }
  const names = (codes, fn) => (codes || []).length ? (grouped ? { chips: codes.map(fn) } : codes.map(fn).join(', ')) : null
  const range = (min, max, unit = '') => {
    if (min == null && max == null) return null
    const u = unit ? ` ${unit}` : ''
    if (min != null && max != null) return min === max ? `${min}${u}` : `${min} – ${max}${u}`
    return min != null ? `${min}+${u}` : `≤ ${max}${u}`
  }
  const check = v => v === true ? <Icon name="check" /> : null
  // ── The people behind the counters (2026-08-02, owner-caught) ────────────────
  // The fill form REQUIRES sex + nationality for every adult on a long-term rent, and
  // core_fill.js stores the whole profile in criteria.extra.household_members. Until now
  // this card rendered only the derived `household_codes` tags, so an agent read
  // "russians, foreigners" where the client had said "42, female, Russian, working" —
  // captured, stored, and invisible. `household_codes` STAYS (it is the vocabulary the
  // scorer compares against owner restrictions); these rows are the evidence under it,
  // and a disagreement between the two is itself worth seeing.
  // Adult vs child is the same split deriveHousehold() uses server-side — a child row
  // carries only an age, so classifying by age keeps the two ends consistent.
  const members = Array.isArray(c.extra?.household_members) ? c.extra.household_members : []
  const pets = Array.isArray(c.extra?.pets) ? c.extra.pets : []
  const isChild = m => m && m.age !== undefined && m.age !== null && m.age < 18
  const personRows = () => {
    if (!members.length) return []
    // Cross-agent view: this card already masks the client's phone/email because the
    // demand belongs to another agent's client. Age + sex + nationality + profession of
    // a named household is identifying in the same way, so it rides the same gate —
    // announced, never silently dropped, and it unmasks with the same access grant.
    if (masked) return [[t('desk_crit_people'), t('desk_crit_people_masked')]]
    let adults = 0, kids = 0
    // B300: the declared relationship leads the list — it is the one household fact the
    // member rows cannot carry, and the reason an exclude-couple rule can now hard-fail.
    const comp = c.extra?.household_composition
    const compRow = (comp === 'couple' || comp === 'group')
      ? [[t('desk_crit_together'), t(`desk_crit_together_${comp}`)]] : []
    if (grouped) {
      // One line per distinct household profile instead of near-identical person rows.
      // Ages stay listed; nothing about any person is dropped.
      const byProfile = new Map()
      for (const m of members) {
        const kid = isChild(m)
        const bits = [
          sexLabel(m.sex, lang),
          m.nationality_note ? `${nationalityLabel(m.nationality, lang)} (${m.nationality_note})` : nationalityLabel(m.nationality, lang),
          m.profession_note ? `${professionLabel(m.profession, lang)} (${m.profession_note})` : professionLabel(m.profession, lang),
          m.working === true ? t('desk_crit_working') : m.working === false ? t('desk_crit_not_working') : null,
        ].filter(Boolean).join(' · ')
        const key = (kid ? 'k|' : 'a|') + bits
        if (!byProfile.has(key)) byProfile.set(key, { kid, bits, ages: [] })
        byProfile.get(key).ages.push(m.age != null ? m.age : '?')
      }
      return [...compRow, ...[...byProfile.values()].map(g => [
        tLog(g.kid ? 'client_sum_children' : 'client_sum_adults', { n: String(g.ages.length) }),
        [g.ages.join(', '), g.bits].filter(Boolean).join(' · '),
      ])]
    }
    return [...compRow, ...members.map(m => {
      const kid = isChild(m)
      const label = kid ? `${t('desk_crit_child')} ${++kids}` : `${t('desk_crit_person')} ${++adults}`
      const bits = [
        m.age != null ? `${m.age}` : null,
        sexLabel(m.sex, lang),
        // B305: "Other" plus the client's own words beats the bare constant that used to
        // destroy the answer ("სხვა (ინდონეზიელი)" — the note only exists beside Other).
        m.nationality_note ? `${nationalityLabel(m.nationality, lang)} (${m.nationality_note})` : nationalityLabel(m.nationality, lang),
        m.profession_note ? `${professionLabel(m.profession, lang)} (${m.profession_note})` : professionLabel(m.profession, lang),
        m.working === true ? t('desk_crit_working') : m.working === false ? t('desk_crit_not_working') : null,
      ].filter(Boolean)
      return [label, bits.length ? bits.join(' · ') : null]
    })]
  }
  // Pets: `has_pets ✓` said one existed but never what it was, and the client's own
  // description ("small, well-behaved, all vaccinations") is exactly what an owner with a
  // no-animals stance asks for. Not masked — a cat identifies nobody.
  const petRows = () => pets.map((p, i) => [
    `${t('desk_crit_pet')} ${i + 1}`,
    [t(`desk_crit_pet_${p.type}`), p.size ? t(`desk_crit_pet_size_${p.size}`) : null, p.description].filter(Boolean).join(' · ') || null,
  ])
  // Group markers (client modal redesign 1b, 2026-09-17): the same rows, filed under five headings
  // so a 45-row table reads as money & size · where · what · who · terms. A marker is not a row.
  const G = id => ({ group: id })
  const rows = [
    G('money'),
    [t('desk_lineage_field_price'), range(c.price_min, c.price_max, c.currency || 'USD')],
    [t('desk_lineage_field_area'), range(c.area_sqm_min, c.area_sqm_max, 'm²')],
    [t('desk_crit_land'), range(c.land_sqm_min, c.land_sqm_max, 'm²')],
    [t('desk_lineage_field_rooms'), range(c.rooms_min, c.rooms_max)],
    [t('desk_crit_bedrooms'), range(c.bedrooms_min, c.bedrooms_max)],
    G('where'),
    [t('desk_crit_max_drive'), c.max_drive_minutes],
    [t('desk_crit_districts'), names(c.district_codes, districtName)],
    // near_locations (fill wizard, 2026-07-16): [{id,name,type}] captured from the
    // locations dictionary. Lives in criteria.extra (0096 jsonb passthrough — real
    // requirement_criteria columns are whitelisted). Display-only; matching doesn't
    // consume it yet.
    [t('desk_crit_near_locations'), (() => {
      // Complexes are excluded here since 0381 — they have their own row below, and a
      // pick that means "IN this building" reading as "near these places" is the exact
      // confusion that row exists to end.
      const near = (c.near_locations || c.extra?.near_locations || []).filter(l => l.type !== 'complex')
      return near.length ? near.map(l => l.name).join(', ') : null
    })()],
    // Location step v2 (2026-08-02): the wizard's explicit whole-city answer and the
    // free-text place description. Both display-only evidence — never filters. Their
    // absence on older rows means "form predates the question", not "client said no".
    [t('desk_crit_any_district'), c.extra?.location_any_city || null],
    [t('desk_crit_location_note'), (c.extra?.location_note || '').trim() || null],
    [t('desk_crit_districts_must'), names(c.district_must_codes, districtName)],
    [t('desk_crit_districts_excluded'), names(c.excluded_district_codes, districtName)],
    [t('desk_crit_complexes_excluded'), names(c.excluded_complex_codes, String)],
    // complex_codes (0381): "an apartment IN this development" — a real criterion the
    // scorer answers from the complex linker's evidence, not a radius. Names come from
    // the near_locations snapshot the fill form stores alongside it; a pick whose name
    // found no core complex is NOT in complex_codes, so showing the snapshot when the
    // counts disagree would claim a constraint that is not being matched. Ids are the
    // honest fallback there — same weakness the excluded row above already has.
    [t('desk_crit_complexes'), (() => {
      const ids = c.complex_codes || []
      if (!ids.length) return null
      const snap = (c.near_locations || c.extra?.near_locations || []).filter(l => l.type === 'complex')
      return snap.length === ids.length ? snap.map(l => l.name).join(' or ') : ids.join(', ')
    })()],
    [t('desk_crit_search_area'), c.search_polygon ? t('desk_crit_search_area_drawn') : null],
    // near_points (0267, B220) — the MATCHABLE proximity constraint, distinct from the
    // display-only near_locations above: each point carries its own radius and walk/bus
    // tier, and the scorer measures pin-to-point distance against them. It was editable in
    // the form and invisible here, so an agent could not see a constraint that was
    // actively collapsing their client's match list.
    [t('desk_crit_near_points'), (Array.isArray(c.near_points) ? c.near_points : []).map(p => {
      const tier = p.proximity_kind === 'bus' ? t('desk_crit_near_bus') : t('desk_crit_near_walk')
      return `${p.label || '?'} (${tier}${p.radius_m ? `, ${p.radius_m} m` : ''})`
    }).join(', ') || null],
    G('features'),
    [t('desk_crit_must_have'), names(c.must_have_codes, featName)],
    [t('desk_crit_nice_to_have'), names(c.nice_to_have_codes, featName)],
    [t('desk_crit_must_not'), names(c.must_not_have_codes, featName)],
    [t('desk_lineage_field_condition'), names(c.condition_codes, featName)],
    // A code in BOTH lists is not a preference, it is a contradiction — and an expensive
    // one: the scorer hard-fails an excluded condition it observes, so "renovated" sitting
    // in both silently deletes every renovated flat from a client who asked for renovated.
    // Found by rendering this row for the first time (2026-08-02): 2 of the 6 live rows
    // carrying an exclusion were self-contradictory. The fill form can no longer produce
    // it; existing rows are flagged here rather than auto-corrected, because which of the
    // two taps the client meant is a question for their agent, not for us to guess.
    [t('desk_crit_condition_exclude'), (() => {
      const excl = c.condition_exclude_codes || []
      if (!excl.length) return null
      const clash = excl.filter(code => (c.condition_codes || []).includes(code))
      const text = excl.map(featName).join(', ')
      if (!clash.length) return text
      return <span className="text-amber-600 dark:text-amber-400" title={t('desk_crit_condition_clash_hint')}>
        {text} <IconText>{t('desk_crit_condition_clash')}</IconText>
      </span>
    })()],
    // Floor: the wizard asks "lowest / highest floor you'd accept" and two separate
    // never-first / never-last booleans. All four were stored and none were rendered.
    G('money'),
    [t('desk_crit_floor'), range(c.floor_min, c.floor_max)],
    [t('desk_crit_floor_not_first'), check(c.floor_not_first)],
    [t('desk_crit_floor_not_last'), check(c.floor_not_last)],
    G('features'),
    [t('desk_crit_land_purpose'), c.land_purpose ? tOr(`desk_crit_land_purpose_${c.land_purpose}`, c.land_purpose) : null],
    [t('desk_crit_with_structure_ok'), check(c.with_structure_ok)],
    G('household'),
    [t('desk_crit_household'), names(c.household_codes, featName)],
    ...personRows(),
    [t('desk_crit_occupants'), c.occupants],
    [t('desk_crit_has_children'), check(c.has_children)],
    [t('desk_crit_children_ages'), names(c.children_ages, String)],
    [t('desk_crit_has_pets'), check(c.has_pets)],
    ...petRows(),
    G('terms'),
    [t('desk_crit_move_in'), c.move_in_date],
    [t('desk_crit_check_out'), c.check_out_date],
    [t('desk_crit_min_lease'), c.min_lease_months],
    // Seasonal lease window (0017): months 1..12 the lease may start / end. Rendered as
    // raw month numbers — nothing writes it today, so inventing a friendlier format would
    // be guessing at a shape no real row has ever had.
    [t('desk_crit_lease_window'), range(c.lease_start_month, c.lease_end_month)],
    [t('desk_crit_registered_lease'), check(c.needs_registered_lease)],
    [t('desk_crit_mortgage_ready'), check(c.mortgage_ready)],
    [t('desk_crit_parking_deeded'), check(c.needs_deeded_parking)],
    // The move-in money ceilings (B202 part 2). `filled` drops null/'' but keeps 0, which
    // is right: "can prepay 0 months" is a stated fact about the client, not a blank.
    [t('desk_crit_max_deposit_months'), c.max_deposit_months],
    [t('desk_crit_prepayment_months'), c.prepayment_months],
    // Distinct from `currency` (which units the budget above): payment_currency is the
    // currency the client wants to PAY in, and the scorer treats a mismatch as negotiable
    // rather than a fail — so the agent needs to see that there is something to negotiate.
    [t('desk_crit_payment_currency'), c.payment_currency],
    [t('desk_crit_power'), c.required_power_kw],
  ]
  const filled = rows.filter(r => !r.group && r[1] != null && r[1] !== '')
  if (grouped) {
    const order = ['money', 'where', 'features', 'household', 'terms']
    const byGroup = Object.fromEntries(order.map(g => [g, []]))
    let cur = 'money'
    for (const r of rows) {
      if (r.group) { cur = r.group; continue }
      if (r[1] != null && r[1] !== '') byGroup[cur].push(r)
    }
    return (
      <div className="space-y-3" data-criteria-grouped>
        {sourceHint && <p className="text-xs text-muted-foreground">{sourceHint}</p>}
        {filled.length ? (
          <div className="grid gap-x-6 gap-y-4 lg:grid-cols-3">
            {order.filter(g => byGroup[g].length).map(g => (
              <div key={g} className="min-w-0" data-criteria-group={g}>
                <p className="text-[13px] font-medium text-muted-foreground mb-1">{t('client_grp_' + g)}</p>
                {byGroup[g].map(([label, value], i) => value && value.chips ? (
                  <div key={`${g}:${i}:${label}`} className="py-1.5 border-b border-border/40 last:border-0">
                    <p className="text-[13px] text-muted-foreground mb-1">{label}</p>
                    <div className="flex flex-wrap gap-1">
                      {value.chips.map((chip, j) => <span key={j} className="inline-flex items-center min-h-6 px-2 py-0.5 rounded-full border border-border text-xs leading-snug">{chip}</span>)}
                    </div>
                  </div>
                ) : (
                  <div key={`${g}:${i}:${label}`} className="flex items-baseline justify-between gap-3 py-1.5 border-b border-border/40 last:border-0">
                    <span className="text-[13px] text-muted-foreground min-w-0">{label}</span>
                    <span className="text-[13px] font-medium text-right tabular-nums min-w-0 break-words">{value}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        ) : <p className="text-sm text-muted-foreground">{t('desk_crit_none')}</p>}
      </div>
    )
  }
  return (
    <div className={card}>
      <p className="text-xs font-semibold text-muted-foreground mb-1">{t('desk_client_criteria_section')}</p>
      {filled.length
        /* Keyed since 2026-08-02: the list used to be a fixed set of columns, so React's
           key warning was cosmetic. It now includes one row PER household member and pet,
           i.e. a genuinely variable-length list — index reuse across a re-fetch would
           reconcile the wrong row into place. */
        ? filled.map(([label, value], i) => <Fragment key={`${i}:${label}`}>{lineageFieldRow(label, value)}</Fragment>)
        : <p className="text-sm text-muted-foreground">{t('desk_crit_none')}</p>}
    </div>
  )
}

// onLeaveToSearch defaults to onClose only so a caller that does not care keeps the old
// behavior; the card-stack call site passes closeCards, because taking the leads hatch has
// to clear every card, not just reveal the one under this.
function ClientEditModal({ requirementId, districts, features, onClose, onSaved, onClosedOrReopened, onOpenPerson, onOpenListing, actingReqId, agentId, agents = [], onLeaveToSearch = null, leadSince = undefined }) {
  const [loading, setLoading] = useState(true)
  const [loadErr, setLoadErr] = useState(null)
  const [original, setOriginal] = useState(null)   // as fetched — the diff baseline
  const [form, setForm] = useState(null)            // editable copy
  const [saving, setSaving] = useState(false)
  const [saveErr, setSaveErr] = useState(null)
  // Opens READ-ONLY like the property modal (owner request 2026-07-13): facts first,
  // ✎ Edit switches to the form; Cancel reverts the form copy and drops back to view.
  const [editing, setEditing] = useState(false)
  // ── Paste more details onto a SAVED demand (owner, 2026-09-04) ──────────────────────────────
  // "paste text, parse, save, come back, paste another text for more details, parse, enrich —
  // if different values ask user which to keep." Until this, the second half of that sentence had
  // nowhere to happen on the demand side: the paste box lived only on the ADD modal, so once a
  // client was saved the only way to add what they told you on the phone was to retype it.
  // ⚠️ Criteria ONLY. Person and requirement (intent, category, urgency) are not editable on this
  // surface, and splitClientParse is told so — it skips those fields rather than applying them
  // somewhere the agent cannot see or undo them.
  const [pasteText, setPasteText] = useState('')
  const [parsing, setParsing] = useState(false)
  const [parseErr, setParseErr] = useState(null)
  const [parseConflicts, setParseConflicts] = useState([])
  const [parseAnswers, setParseAnswers] = useState({})
  const [parseRaw, setParseRaw] = useState(null)
  // Seed 2 (MEETINGS_MODEL §3) — client-only meeting. The server derives the
  // client party from the requirement, so the seed needs nothing else.
  const [meetingSeed, setMeetingSeed] = useState(false)
  // Bumped by an inline gap fill in the completeness meter — re-fetches the requirement so
  // the meter ticks up and the filled criterion drops off, and forces MatchSuggestions to
  // re-score with the new value (a new budget/room count changes who matches).
  const [reloadKey, setReloadKey] = useState(0)

  // ── who works this client (owner 2026-09-02) ───────────────────────────────────────────
  // SHARE adds a colleague and keeps you; LEAVE removes only you, and if nobody is left the
  // client returns to the pool. Both verbs already existed in set_requirement_representative
  // and had no door — see lib/core_leads.js for why this is not a new permission model.
  // ⚠️ An empty list is a REAL answer (nobody represents them = they are in the pool), so
  // `null` is the loading state and `[]` is a fact. Collapsing the two would draw "in the
  // pool" over a client whose representatives simply had not arrived yet.
  const [reps, setReps] = useState(null)
  const [repsBusy, setRepsBusy] = useState(false)
  const [repsErr, setRepsErr] = useState(null)
  const [repsKey, setRepsKey] = useState(0)
  useEffect(() => {
    if (!requirementId) return
    let gone = false
    getRequirementRepresentatives(requirementId)
      .then(r => { if (!gone) setReps(r || []) })
      .catch(() => { if (!gone) setReps([]) })
    return () => { gone = true }
  }, [requirementId, repsKey])

  // The footer ↗ pill's door into the share picker (owner 2026-09-08: "on client modal just
  // add share pill, no link pill"). A counter, not a boolean — see ClientSharePanel's prop.
  const [sharePick, setSharePick] = useState(0)
  const myParty = (reps || []).find(r => r.agent_id && r.agent_id === agentId)?.party_id || null
  const runRep = async (fn) => {
    setRepsBusy(true); setRepsErr(null)
    try { await fn(); setRepsKey(k => k + 1) }
    catch (e) { setRepsErr(e.message) }
    finally { setRepsBusy(false) }
  }

  useEffect(() => {
    let cancelled = false
    getRequirement(requirementId).then(data => {
      if (cancelled) return
      setOriginal(data); setForm(data.criteria || {}); setLoading(false)
    }).catch(e => { if (!cancelled) { setLoadErr(e.message); setLoading(false) } })
    return () => { cancelled = true }
  }, [requirementId, reloadKey])

  const set = (field, value) => setForm(f => ({ ...f, [field]: value }))

  const busy = actingReqId === requirementId
  const isClosed = original?.status === 'closed'
  // search_polygon is compared separately — it's outside diffCriteriaOps' EDITABLE_CRITERIA_FIELDS
  // (a top-level command key, not an ops[] entry; see migration 0041).
  const polygonChanged = original && JSON.stringify(original.criteria?.search_polygon || null) !== JSON.stringify(form?.search_polygon || null)
  const dirty = original && (diffCriteriaOps(original.criteria, form).length > 0 || polygonChanged)

  async function handlePasteEnrich() {
    if (!pasteText.trim() || !form) return
    setParsing(true); setParseErr(null)
    try {
      const { envelope: env } = await parseIntakeEnvelope({ raw_text: pasteText, channel: 'scheduler' })
      const cmds = env?.commands || []
      // Either shape carries the same criteria. The AI decides "new" or "amend" from its own
      // threading; here the agent has already told us which demand this is by having it open,
      // so refusing an amend envelope would be refusing the exact message this box is for.
      const cmd = cmds.find(c => c.type === 'extract_requirement') || cmds.find(c => c.type === 'amend_requirement')
      if (!cmd) {
        setParseErr(env?.about === 'supply' ? t('desk_parse_reads_as_property') : t('desk_parse_no_demand_found'))
        return
      }
      const { patch, conflicts, raw } = splitClientParse(cmd, { criteria: form })
      if (patch.criteria) setForm(f => ({ ...f, ...patch.criteria }))
      setParseConflicts(conflicts)
      setParseAnswers({})
      setParseRaw(raw)
      setPasteText('')
      // Nothing is written yet: this fills the FORM, and Save still amends through
      // diffCriteriaOps exactly as a hand edit does — one path to the database, one audit trail.
    } catch (e) { setParseErr(e.message) }
    finally { setParsing(false) }
  }

  async function handleSave() {
    const ops = diffCriteriaOps(original.criteria, form)
    if (!ops.length && !polygonChanged) { onClose(); return }
    setSaving(true); setSaveErr(null)
    try {
      const cmd = { type: 'amend_requirement', target_requirement_id: requirementId, person_party_id: original.person?.party_id || null, ops, human_edited: true }
      if (polygonChanged) cmd.search_polygon = form.search_polygon ? JSON.stringify(form.search_polygon) : null
      const resp = await commitIntakeEnvelope({
        raw_text: `[manual edit via Data Desk] requirement ${requirementId}`,
        channel: 'scheduler', envelope: { decision: 'noop', confidence: 1, commands: [cmd] }, anchors: null, intake_id: null,
        agent_id: agentId,
      })
      if ((resp.results || []).some(r => !r.executed)) {
        setSaveErr(resp.results[0]?.result?.message || 'gateway refused')
      } else {
        onSaved(); onClose()
      }
    } catch (e) { setSaveErr(e.message) }
    finally { setSaving(false) }
  }

  const masked = !!original?.contact_masked
  const summary = original ? summaryOf(original.criteria, original.requirement, { districts, features, masked }) : null
  const sourceHint = original ? t(original.source === 'client_form' ? 'desk_review_intent_readonly_hint_form'
    : original.source === 'manual' ? 'desk_review_intent_readonly_hint_manual'
    : original.source === 'ai' ? 'desk_review_intent_readonly_hint'
    : 'desk_review_intent_readonly_hint_unknown') : null
  const sharePanel = signal => (
            <ClientSharePanel
              representatives={reps}
              agents={agents}
              myPartyId={myParty}
              openSignal={signal}
              busy={repsBusy}
              error={repsErr}
              // The panel hands back the SET the agent picked (owner 2026-09-08: one, many
              // or all). There is one share endpoint and it takes one agent, so this loops
              // — and it must not stop at the first failure: with nine selected, aborting
              // on the third leaves a half-shared client behind an error naming only the
              // one that broke, which reads as "nothing worked". Every share is attempted,
              // and the message says exactly which ones did not land.
              onShare={async list => runRep(async () => {
                const failed = []
                for (const a of (Array.isArray(list) ? list : [list])) {
                  try { await shareRequirement(requirementId, { toAgentId: a.id, agentId }) }
                  catch { failed.push(a.name || a.id) }
                }
                if (failed.length) {
                  const total = Array.isArray(list) ? list.length : 1
                  throw new Error(tLog('desk_client_share_partial',
                    { ok: total - failed.length, bad: failed.length, names: failed.join(', ') }))
                }
              })}
              onLeave={() => runRep(() => leaveRequirement(requirementId, agentId))}
              // Stop a share. runRep refetches the representative list, so the chip
              // disappears because the SERVER says it is gone, not because the client
              // optimistically removed it — the two disagree the moment a call fails.
              onUnshare={rep => runRep(() => unshareRequirement(requirementId,
                { toPartyId: rep.party_id, toAgentId: rep.agent_id, agentId }))}
            />
  )

  // ── Redesign 1b (owner-approved 2026-09-17, Claude Design round 1) ───────────────────────────────
  // View: identity line → summary strip → ✓/★ chips → MATCHES → tabs (criteria · history · negotiations).
  // `wide`: a large modal on desktop, a full-screen sheet below lg (the owner's "modal on desktop, full
  // page on phone"). Edit mode keeps the proven form for now — round 2 redesigns it.
  return (
    <Modal wide title={editing ? t('desk_client_edit_title') : t('desk_client_view_title')} onClose={onClose}>
      {loading ? (
        <p className="text-sm text-muted-foreground py-6 text-center">{t('desk_loading')}</p>
      ) : loadErr ? (
        <p className="text-sm text-destructive py-6 text-center">{tLog('desk_couldnt_load', { err: loadErr })}</p>
      ) : (
        <div className="space-y-3.5" data-client-modal={masked ? 'masked' : 'own'}>
          {/* ── identity line ───────────────────────────────────────────────────────────── */}
          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
            <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 min-w-0">
              <Badge label={isClosed ? t('desk_client_status_closed') : t('desk_client_status_active')} variant={isClosed ? 'red' : 'green'} />
              {/* Name → person view: this demand is one of possibly several things this human is doing. */}
              {original.person?.party_id ? (
                <button className="text-base font-semibold hover:text-primary hover:underline decoration-dotted text-left"
                  title={t('desk_client_see_profile')} onClick={() => onOpenPerson(original.person.party_id)}>
                  {original.person?.name || t('desk_client_fallback_person')}
                </button>
              ) : <span className="text-base font-semibold">{original.person?.name || t('desk_client_fallback_person')}</span>}
              {summary.intentLine && <span className="text-[13px] text-muted-foreground">{summary.intentLine}</span>}
              {masked && <span className="inline-flex items-center h-6 px-2.5 rounded-full border border-dashed border-border text-[11.5px] text-muted-foreground">{t('client_masked_badge')}</span>}
            </div>
            {!masked && (
              <div className="flex flex-wrap items-center gap-2">
                {original.agent?.name && <span className="text-xs text-muted-foreground">{tLog('client_owner_agent', { name: original.agent.name })}</span>}
                {reps !== null && (
                  <ClientTeamChip representatives={reps} myAgentId={agentId} openSignal={sharePick}>
                    {sharePanel}
                  </ClientTeamChip>
                )}
                <ClientLeadAlert compact requirementId={requirementId} clientName={original.person?.name || null}
                  criteria={original.criteria || null} intent={original.requirement?.intent || null}
                  propertyCategory={original.requirement?.property_category || null}
                  onFill={isClosed ? null : () => setEditing(true)} />
              </div>
            )}
          </div>

          {/* Another agent's client: say who represents them and what is hidden, and offer the access ask. */}
          {masked && (
            <MaskedClientBanner agent={original.agent}>
              <AccessRequestPanel requirementId={requirementId} />
            </MaskedClientBanner>
          )}

          {!editing && <>
            <ClientSummaryStrip summary={summary} />
            <ClientFeatureChips summary={summary} />
          </>}

          {editing && <>
            <div className={card}>
              <p className="text-xs font-semibold text-muted-foreground mb-1">{sourceHint}</p>
              <p className="text-sm">{summary.intentLine || '—'}</p>
            </div>
            <div className={card} data-client-enrich>
              <p className="text-xs font-semibold text-muted-foreground mb-2">{t('desk_enrich_paste_heading')}</p>
              <textarea className={`${input} font-mono text-sm`} rows={3} value={pasteText}
                placeholder={t('desk_enrich_paste_ph')}
                onChange={e => setPasteText(e.target.value)} />
              <button className={`${btn('ghost')} mt-2`} disabled={parsing || !pasteText.trim()} onClick={handlePasteEnrich}>
                {parsing ? t('desk_modal_parsing') : t('desk_enrich_parse')}
              </button>
              {parseErr && <p className="text-xs text-destructive mt-2"><Icon name="alert-triangle" /> {parseErr}</p>}
            </div>
            {/* The same panel the property modal uses: one row per field, unanswered means keep. */}
            <ParseConflictPanel
              conflicts={parseConflicts}
              answers={parseAnswers}
              cardClass={card}
              onToggle={(key, next) => setParseAnswers(a => ({ ...a, [key]: next }))}
              onApply={() => {
                const accepted = acceptedClientPatch(parseConflicts, parseAnswers, parseRaw, { criteria: form })
                if (accepted.criteria) setForm(f => ({ ...f, ...accepted.criteria }))
                setParseConflicts([]); setParseAnswers({})
              }}
              onKeepAll={() => { setParseConflicts([]); setParseAnswers({}) }}
            />
            <CriteriaFieldsForm form={form} set={set} districts={districts} features={features} intent={original.requirement?.intent} propertyCategory={original.requirement?.property_category} />
          </>}

          {/* ── matches, promoted directly under the summary (the block agents act on most) ── */}
          {!editing && (
            <MatchSuggestions kind="requirement" id={requirementId} onOpenListing={onOpenListing} refreshKey={reloadKey}
              criteria={masked ? null : (original.criteria || null)}
              intent={original.requirement?.intent || null}
              propertyCategory={original.requirement?.property_category || null}
              clientName={original.person?.name || null}
              onLeaveToSearch={onLeaveToSearch || onClose}
              leadSince={leadSince}
              // The over-broad line's `დააზუსტე მოთხოვნა` opens THIS modal's own editor; a masked client is read-only.
              onRefineCriteria={masked ? null : () => setEditing(true)}
              completeness={{
                label: t('desk_completeness_client'),
                // clientGaps only asks "is a phone known" — feed it the presence flag (C1: the value never ships).
                missing: clientGaps({ phone: (original.person?.phone_present ?? !!original.person?.phone) ? 'present' : '' }, { urgency: original.requirement?.urgency }, original.criteria || {}),
                total: CLIENT_COMPLETENESS_TOTAL,
                fill: masked ? null : {
                  requirementId,
                  personPartyId: original.person?.party_id || null,
                  criteria: original.criteria || {},
                  agentId,
                  onSaved: () => { setReloadKey(k => k + 1); onSaved?.() },
                },
              }} />
          )}

          {/* ── criteria · history · negotiations ──────────────────────────────────────── */}
          {!editing && (
            <ClientTabs
              criteria={<ClientCriteriaSummary grouped sourceHint={sourceHint} criteria={original.criteria} districts={districts} features={features} masked={masked} />}
              history={<RunningNote kind="requirement" id={requirementId} refreshKey={reloadKey} cardClass={card} />}
              negotiations={original.person?.party_id ? <NegotiationsPanel clientPartyId={original.person.party_id} /> : null}
            />
          )}

          {saveErr && <p className="text-sm text-destructive"><IconText>{tLog('desk_client_not_saved_err', { err: saveErr })}</IconText></p>}

          {/* Footer matches the listing modal exactly (owner rule 2026-07-20: contact
              placement is consistent across modals): sticky to the bottom of the scroll
              area, CONTACT bottom-left, actions right. Negative margins cancel the modal's
              own px-5 so it spans edge to edge like the sticky title bar. This modal is long
              (criteria + match list), so an inline footer scrolled away exactly as it did on
              the listing side. */}
          {/* flex-wrap + whitespace-nowrap on every control (mobile, owner-caught 2026-07-20):
              the close-REQUEST label is long in Georgian, and on a phone the two groups
              collided while the label wrapped INSIDE its button (✕ painting over the text).
              Buttons stay intact and wrap as whole units onto a second row instead. */}
          <div className="sticky bottom-0 -mx-5 -mb-5 px-5 py-2 mt-2 bg-card border-t border-border flex flex-wrap items-center justify-between gap-2 z-10">
            <span className="flex items-center gap-2 min-w-0">
              {/* SAME pill as the listing modal (owner rule: same exact design). Party mode —
                  opening it is the one logged whole-book reveal; the payload itself carries
                  only phone_present (C1). `?? !!…phone` tolerates a cached pre-strip payload. */}
              {(original.person?.phone_present ?? !!original.person?.phone) && original.person?.party_id && (
                // `iconOnly` (owner 2026-09-08): "client modal also has long contact pill, no
                // need of — leave only phone emoji". The word sat in a footer that already
                // carries four labelled buttons, and the 📞 says it on its own; the label
                // moves into title= rather than being deleted, so it is still readable to a
                // screen reader and on hover.
                <ContactPill partyId={original.person.party_id} partyName={original.person?.name}
                  iconOnly requirementId={requirementId} label={t('desk_coop_chip_default')} />
              )}
              {/* ↗ SHARE PILL — the same door the property card has, and deliberately WITHOUT
                  the 🔗 links pill beside it (owner 2026-09-08): a client has no ad to publish
                  and no client-link to mint from here, so a links pill would be a door onto
                  nothing. It opens the picker that already lives in the card body rather than
                  owning a second one — one picker, two ways in. */}
              {/* The ONE share door (redesign 1b): it opens the team popover's picker. Not offered on a
                  masked client — the share endpoint would refuse a non-representative anyway. */}
              {reps !== null && !original.contact_masked && (
                <ShareMenu onShareOthers={() => setSharePick(n => n + 1)} />
              )}
              {/* archive, NOT x: this closes the REQUEST (filed away, reopenable) and sits
                  in the same footer as the modal's own x — two ✕ side by side was ambiguous
                  (owner-caught). Reopen gets the counter-clockwise arrow. */}
              <button
                className={`${btn('ghost')} whitespace-nowrap`}
                disabled={busy}
                title={isClosed ? t('desk_client_reopen_tooltip') : t('desk_client_close_tooltip')}
                onClick={() => onClosedOrReopened(requirementId, isClosed)}>
                {busy ? '…' : <BtnLabel icon={isClosed ? 'rotate-ccw' : 'archive'}
                  label={isClosed ? t('desk_client_reopen_button') : t('desk_client_close_button')} />}
              </button>
            </span>
            {/* Close-the-modal is ALWAYS the rightmost control (owner rule 2026-07-20) —
                the exit lives in one place regardless of edit state. */}
            {editing ? (
              <div className="flex gap-2 ml-auto">
                <button className={`${btn('primary')} whitespace-nowrap`} disabled={saving || !dirty} onClick={handleSave}
                  title={t('desk_client_save_changes')}>
                  {saving ? t('desk_review_saving') : dirty ? <BtnLabel icon="check" label={t('desk_client_save_changes')} /> : t('desk_client_no_changes')}
                </button>
                {/* Cancel drops back to the read-only view with the form copy reverted —
                    NOT closing the modal (that's the view stage's Close). */}
                <button className={`${btn('ghost')} whitespace-nowrap`} onClick={() => { setForm(original.criteria || {}); setSaveErr(null); setEditing(false) }}>{t('desk_client_cancel')}</button>
              </div>
            ) : (
              <div className="flex gap-2 ml-auto">
                {/* Seed 2 (MEETINGS_MODEL §3): a meeting with only the CLIENT side —
                    "I'm seeing this client", property not chosen yet. Booking a
                    meeting against a SPECIFIC matched property is already one press
                    on each row of the match panel above (seed 3); this covers the
                    case where the property isn't decided. */}
                {/* 💬 the team's thread about THIS client (messaging phase 2): the client's agents and management
                    only — anyone else gets a plain "only this client's agents" line, never a silent failure. */}
                <DiscussWithTeamButton requirementId={requirementId} className={`${btn('ghost')} whitespace-nowrap inline-flex items-center gap-1`} />
                <button className={`${btn('ghost')} whitespace-nowrap`} onClick={() => setMeetingSeed(true)}
                  title={t('meeting_create_btn')}><BtnLabel icon="calendar" label={t('meeting_create_btn')} /></button>
                <button className={`${btn('primary')} whitespace-nowrap`} onClick={() => setEditing(true)}
                  title={t('desk_client_edit_button')}><BtnLabel icon="pencil" label={t('desk_client_edit_button')} /></button>
                <button className={`${btn('ghost')} whitespace-nowrap`} onClick={onClose}
                  title={t('desk_lineage_close_button')}><BtnLabel icon="x" label={t('desk_lineage_close_button')} /></button>
              </div>
            )}
          </div>
        </div>
      )}
      {meetingSeed && (
        <MeetingModal
          seed={{
            requirementId,
            clientLabel: original?.person?.name || null,
            // Property side deliberately empty — this is the "seeing the client,
            // property not decided" case (§3, entry point 2).
          }}
          onClose={() => setMeetingSeed(false)}
          onCreated={() => onSaved?.()} />
      )}
    </Modal>
  )
}

// INTENT_OPTIONS / PROPERTY_CATEGORY_OPTIONS / URGENCY_OPTIONS (+ *_KEYS) — moved to ./desk/ManualClientModal.jsx (plan §3 decomposition).

// BetterToKnow…MatchSuggestions block (gaps/completeness, match-explain satellites, ContactReveal, access requests, MatchSuggestions) — moved to ./desk/MatchSuggestions.jsx (plan §3 decomposition).
// ManualClientModal + FillLinkSection — moved to ./desk/ManualClientModal.jsx (plan §3 decomposition).

// Manual property workspace (PROPERTY_KIND_OPTIONS…ManualPropertyModal: Facet, DistrictOptions, rental/sale terms, timeline, notes, availability, inventory, duplicates) — moved to ./desk/ManualPropertyModal.jsx (plan §3 decomposition).

// ─── Activity log — the side column beside the intake box ────────────────────
// Replaces the old "AI property drafts" panel. Property drafts are no longer a
// separate surface — they live in Recent entries now (pending, pinned, searchable),
// so the side column is freed up for what it does better: a flat, newest-first feed
// of WHAT JUST HAPPENED / who did it. Rows are clickable and route through the same
// handleCardClick as the ledger (one item wrapped as a group), so clicking a pending
// property here still opens its review modal. Read-only otherwise — the searchable
// Recent entries below is the place to act at length.
// Recent-entries time-period filter → an ISO `since` bound for the server fetch (undefined =
// 'all', no date floor — still hits the server's own row cap). 'Today' means since LOCAL
// midnight (calendar day, what a human means by "today") — a rolling last-24h window kept
// showing yesterday afternoon's scraping burst under "Today". The multi-day periods stay
// rolling (last N×24h) — "7 days ago" has no midnight intuition to violate.
const ENTRY_PERIOD_DAYS = { '7d': 7, '30d': 30, '90d': 90 }
function periodSinceISO(period) {
  if (period === '1d') { const d = new Date(); d.setHours(0, 0, 0, 0); return d.toISOString() }
  const days = ENTRY_PERIOD_DAYS[period]
  return days ? new Date(Date.now() - days * 86400000).toISOString() : undefined
}
// timeAgo — moved to ./desk/primitives.jsx (plan §3 decomposition).
// activityLabel — moved to ./desk/NotificationsPanel.jsx with the notifications feed (2026-07-24).

// ─── Agent ranking (v2, 2026-07-12) — the motivation leaderboard ───────────────
// Self-contained: own Daily/Weekly/Monthly period (weekly default, independent of
// the feed's entryPeriod) and its own fetches. Counts come from the 0087
// activity_ranking RPC (FULL window, not the loaded feed page — the v1 tally
// undercounted anything past the first page), meetings from the viewings prop,
// attendance from the scheduler DB.
//
// CLOSES AND COMMISSION COME FROM THE LEDGER (2026-09-06), i.e. /agent-deals run through
// lib/statsModel.buildLedger — the same object the Stats tab reads. They used to come from
// /deal-closures, which (a) is keyed by core party id and could only be joined to an agent
// through the activity rows, so an agent who closed a deal but posted nothing in the window
// scored ZERO closes, and (b) is built from deal.closed events that fire once per closing
// agent, so a co-broked deal's whole commission was credited to each co-broker in full.
// The ledger keys by scheduler agent id and pays each agent their own share. The two boards
// now literally read one number, which is what "same engine" was always meant to mean.
// Scoring is config-driven (config.desk_ranking_points, editable in Admin →
// General): deals dominate, spammable actions are capped, attendance is a small
// positive-only BONUS — present days add points, lateness/absence never shows
// on the board (that detail stays in the Stats tab, manager-only by design).
// Cap scaling per view: weekly caps are the stored base (capScaleForDays).
const RANK_PERIOD_DAYS = { daily: 1, weekly: 7, monthly: 30 }
function rankWindows(period) {
  const now = Date.now()
  if (period === 'daily') {
    const mid = new Date(); mid.setHours(0, 0, 0, 0)
    return { since: mid.toISOString(), until: null, prevSince: new Date(mid.getTime() - 86400000).toISOString(), prevUntil: mid.toISOString() }
  }
  const days = period === 'monthly' ? 30 : 7
  return {
    since: new Date(now - days * 86400000).toISOString(), until: null,
    prevSince: new Date(now - 2 * days * 86400000).toISOString(), prevUntil: new Date(now - days * 86400000).toISOString(),
  }
}
// One period's scored rows. Rows are keyed by core party id; scheduler-side data
// (meetings, attendance) joins via the scheduler_agent_id the RPC carries along.
// Scoring itself lives in lib/rankingScore.js — the SAME engine the Stats tab
// uses, so the two surfaces can never rank the same people differently.
function scoreRanking({ activity, deals, roster, meetings, attendance, points, capScale, isWeekly, fx }) {
  const byParty = new Map()
  const bySched = new Map()
  for (const r of activity || []) {
    const e = { id: r.party_id, name: r.display_name, schedId: r.scheduler_agent_id || null,
      reshares: r.reshares || 0, scraped: r.scraped || 0, closes: 0, meetings: 0, commission: 0 }
    byParty.set(r.party_id, e)
    if (e.schedId) bySched.set(e.schedId, e)
  }
  // The ledger. The server already range-filtered the deals by closed_at, so no from/to here —
  // re-filtering by DAY would clip a daily window that starts at local midnight. Legacy
  // scheduler viewings are not merged in: that table is frozen (newest close 2026-06-22) and
  // this board never looks back more than 30 days, so it would contribute nothing.
  const ledger = buildLedger(mergeDealSources([], deals || [], roster), null, null, fx, roster)
  for (const [schedId, m] of ledger.byAgent) {
    let e = bySched.get(schedId)
    if (!e) {
      // A close with no listing activity in the window still puts the agent on the board —
      // the exact case the party↔agent bridge used to drop.
      e = { id: `sched:${schedId}`, name: null, schedId, reshares: 0, scraped: 0, closes: 0, meetings: 0, commission: 0 }
      byParty.set(e.id, e); bySched.set(schedId, e)
    }
    e.closes = m.participations
    e.commission = m.commission   // the agent's OWN share, already in GEL
  }
  const rows = []
  for (const e of byParty.values()) {
    if (e.schedId && meetings) e.meetings = meetings.get(e.schedId) || 0
    const att = e.schedId && attendance ? attendance.get(e.schedId) : null
    const perfect = isWeekly && att && !att.hasAbsent && att.days >= 5
    if (e.reshares + e.scraped + e.closes + e.meetings === 0) continue
    e.score = rankParts({ ...e, officeDays: att?.days || 0 }, points, capScale, { perfect }).score
    rows.push(e)
  }
  rows.sort((a, b) => b.score - a.score || (a.name || '').localeCompare(b.name || ''))
  return rows
}
function AgentRankingPanel({ myAgentId, allAgents = [], viewings = [], config = {} }) {
  const [period, setPeriod] = useState('weekly')
  const [data, setData] = useState(null)   // { activity, prevActivity, deals, prevDeals, att, prevAtt, windows }
  useEffect(() => {
    let dead = false
    const w = rankWindows(period)
    const cacheKey = `rank3:${period}`   // rank2 held /deal-closures rows; the shape changed
    const cached = readDeskCache(cacheKey)
    if (cached) setData(cached)
    else setData(null)
    // ── A WARM CACHE NOW SUPPRESSES THE REFETCH (2026-09-02) ────────────────────────────
    // This panel already cached at `rank2:<period>`, but only to paint immediately — it then
    // re-fired all SIX requests unconditionally, so the cache prevented a flicker and nothing
    // else. Two of those six are `activity_ranking`, the Desk's heaviest per-load pair (current
    // window + previous window, for the movement arrows).
    //
    // WHY IT MATTERS BEYOND ONE PANEL: a pm2 restart drops every open Desk at once, and they
    // all re-fire their heaviest initial loads together — a reconnect thundering herd. Measured
    // by the outage session after the 13:07:52 restart: `activity_ranking` 24 calls, max 10.5 s
    // (temp_blks_written = 0 — slow under the herd, not spilling; the 38 MB spill that day was
    // `feature_usage_counts`, a different statement that merely landed alongside it). An agent
    // reloading within the window now costs zero calls instead of six.
    //
    // ⚠️ THE TTL LIVES HERE, NOT IN THE TRANSPORT. `coalesce.js` already dedupes concurrent
    // identical GETs and its header forbids exactly this ("A TTL here would trade the rule for
    // milliseconds; don't") — because the platform's standing rule is that an agent sees truth,
    // never a stale copy. A leaderboard is the one place a few minutes of staleness is
    // defensible: it ranks a week or a month of work, so it cannot meaningfully change between
    // two reloads a minute apart. That judgement belongs to the component that knows what its
    // number means, which is why it is written here and not applied to every GET.
    //
    // ⚠️ Not a substitute for the DB-side fix. This reduces how OFTEN the query runs; it does
    // nothing about a single call taking 10.5 s under load.
    const RANK_TTL_MS = 5 * 60 * 1000
    if (cached && Date.now() - (cached.__at || 0) < RANK_TTL_MS) return () => { dead = true }
    const day = iso => iso.slice(0, 10)
    Promise.all([
      getActivityRanking(w.since, w.until),
      getActivityRanking(w.prevSince, w.prevUntil),
      getAgentDeals(w.since, w.until),
      getAgentDeals(w.prevSince, w.prevUntil),
      getAttendance({ dateFrom: day(w.since), dateTo: day(new Date().toISOString()) }).catch(() => []),
      getAttendance({ dateFrom: day(w.prevSince), dateTo: day(w.prevUntil) }).catch(() => []),
    ]).then(([a, pa, d, pd, att, prevAtt]) => {
      if (dead) return
      const next = { activity: a.ranking || [], prevActivity: pa.ranking || [],
        deals: d.deals || [], prevDeals: pd.deals || [], att, prevAtt, windows: w }
      // ⚠️ `__at` is what makes the TTL above real — `readDeskCache` returns the stored object
      // with no timestamp of its own (deskCache's own stamp is a write SEQUENCE for eviction
      // order, not a time), so freshness has to ride inside the payload. Written only on a
      // SUCCESSFUL fetch: a failed load must not stamp a cache entry and thereby suppress the
      // next attempt for five minutes.
      setData(next); writeDeskCache(cacheKey, { ...next, __at: Date.now() })
    }).catch(e => { console.error('[agent-ranking]', e.message); if (!dead && !cached) setData({ activity: [], prevActivity: [], deals: [], prevDeals: [], att: [], prevAtt: [] }) })
    return () => { dead = true }
  }, [period])

  const points = config.desk_ranking_points
  const fx = config.fx_to_gel
  const { rows, prevRank } = useMemo(() => {
    if (!data) return { rows: null, prevRank: new Map() }
    const w = data.windows || rankWindows(period)
    const capScale = capScaleForDays(RANK_PERIOD_DAYS[period] ?? 7)
    const isWeekly = period === 'weekly'
    // Active roster agents only — resigned agents and admin/email-keyed parties
    // (e.g. gigi) don't belong on a motivation board. Ranks and movement are
    // computed on the same filtered cohort so arrows never point at ghosts.
    const activeIds = new Set(allAgents.filter(a => a.is_active !== false).map(a => a.id))
    const onBoard = r => r.schedId && activeIds.has(r.schedId)
    const attMap = officeDaysByAgent(data.att)
    const rows = scoreRanking({
      activity: data.activity, deals: data.deals, roster: allAgents,
      meetings: meetingsByAgent(viewings, w.since, w.until),
      attendance: attMap, points, capScale, isWeekly, fx,
    }).filter(onBoard)
    // Every ACTIVE roster agent appears, zero-activity included — an empty row at
    // the bottom is the nudge; silently vanishing from the board is just invisible.
    // They still earn their attendance bonus (being in the office counts even
    // before the first posting lands).
    const seen = new Set(rows.map(r => r.schedId))
    const zeros = []
    for (const a of allAgents) {
      if (a.is_active === false || seen.has(a.id)) continue
      const att = attMap.get(a.id)
      const perfect = isWeekly && att && !att.hasAbsent && att.days >= 5
      zeros.push({ id: `roster:${a.id}`, schedId: a.id, name: a.name, reshares: 0, scraped: 0, closes: 0, meetings: 0,
        score: rankParts({ officeDays: att?.days || 0 }, points, capScale, { perfect }).score })
    }
    zeros.sort((a, b) => b.score - a.score || a.name.localeCompare(b.name))
    rows.push(...zeros)
    rows.sort((a, b) => b.score - a.score)
    const prev = scoreRanking({
      activity: data.prevActivity, deals: data.prevDeals, roster: allAgents,
      meetings: meetingsByAgent(viewings, w.prevSince, w.prevUntil),
      attendance: officeDaysByAgent(data.prevAtt), points, capScale, isWeekly, fx,
    }).filter(onBoard)
    const prevRank = new Map(prev.map((r, i) => [r.id, i + 1]))
    return { rows, prevRank }
  }, [data, viewings, points, fx, period, allAgents])

  const nameOf = e => e.name || allAgents.find(a => a.id === e.schedId)?.name || t('desk_ranking_unknown')
  const tabBtn = p => `flex-1 px-1 py-1 rounded-md text-xs font-medium transition-colors ${period === p ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`
  return (
    <div className={`${card} flex flex-col h-full`}>
      <p className="text-xs font-semibold text-muted-foreground mb-1.5 shrink-0"><IconText>{t('desk_ranking_title')}</IconText></p>
      <div className="flex gap-0.5 mb-2 shrink-0 rounded-lg bg-muted/50 p-0.5" role="tablist">
        <button className={tabBtn('daily')} onClick={() => setPeriod('daily')}>{t('desk_ranking_daily')}</button>
        <button className={tabBtn('weekly')} onClick={() => setPeriod('weekly')}>{t('desk_ranking_weekly')}</button>
        <button className={tabBtn('monthly')} onClick={() => setPeriod('monthly')}>{t('desk_ranking_monthly')}</button>
      </div>
      <p className="text-xs text-muted-foreground/70 mb-1.5 shrink-0 leading-[1.45]"><IconText>{t('desk_ranking_legend')}</IconText></p>
      {rows === null ? (
        <p className="text-xs text-muted-foreground">…</p>
      ) : rows.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t('desk_ranking_empty')}</p>
      ) : (
        <div className="space-y-0.5 overflow-y-auto min-h-0 flex-1 max-h-[280px] lg:max-h-none" title={t('desk_ranking_legend')}>
          {rows.map((a, i) => {
            const mine = myAgentId && a.schedId === myAgentId
            const was = prevRank.get(a.id)
            const move = was == null ? null : was - (i + 1)   // + = climbed
            const gap = i > 0 ? rows[i - 1].score - a.score : 0
            return (
              <div key={a.id} className={`flex items-center gap-2 px-2 py-1.5 rounded-lg ${mine ? 'bg-primary/10 border border-primary/40' : ''}`}>
                <span className="text-sm font-bold text-muted-foreground/40 w-5 shrink-0">{i + 1}</span>
                <span className="min-w-0 flex-1">
                  {/* Score sits on the NAME line (owner 2026-08-01: "it overlays over another
                      number — move that score up across the agent's name"). It used to be a
                      sibling column beside this one, but the badge row below is
                      whitespace-nowrap and a busy agent's row (↑9 · 🏁1 · 🤝1 · 📦94 · ↻232)
                      simply ran underneath it. Up here it costs no vertical space and gives
                      the badges the card's full width. */}
                  <span className="flex items-baseline gap-2">
                    <span className="text-xs font-medium truncate">{nameOf(a)}</span>
                    <span className="ml-auto text-sm font-bold tabular-nums shrink-0">{a.score}</span>
                  </span>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {move != null && move !== 0 && <span className={`font-semibold ${move > 0 ? 'text-emerald-500' : 'text-red-400'}`}>{move > 0 ? `↑${move}` : `↓${-move}`} · </span>}
                    {was == null && (a.reshares + a.scraped + a.closes + a.meetings) > 0 && <span className="font-semibold text-sky-400">{t('desk_ranking_new')} · </span>}
                    {a.closes > 0 && <span className="text-emerald-500 font-semibold">🏁{a.closes} · </span>}
                    {a.meetings > 0 && <>🤝{a.meetings} · </>}
                    <>📥{a.scraped} · ↻{a.reshares}</>
                  </span>
                  {mine && i > 0 && (
                    <span className="text-xs font-semibold text-primary block">{tLog('desk_ranking_to_next', { n: gap + 1, rank: i })}</span>
                  )}
                </span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// Person view cluster (INTENT_LABEL_KEYS, demandLine, listingLine, PhonePersonHeader, PartyDuplicates, PersonModal) — moved to ./desk/PersonModal.jsx (plan §3 decomposition).

// Cooperation chips renderers (coopStatusColor…CooperationList, CooperationChips) — moved to ./desk/CooperationChips.jsx (plan §3 decomposition).

// CooperationModal — moved to ./desk/CooperationModal.jsx (plan §3 decomposition).

// Reshare lineage modal cluster (CapturedByToken, lineageFieldRow, FillFactPill, editUrlFor, QuestionBox, loc helpers, NearbyLocationsCard, ListingLineageModal) — moved to ./desk/LineageModal.jsx (plan §3 decomposition).

// Recent-entries ledger + history cards (OBJECT_TYPE…relTime, kindOf, summarize, AreaPreview, HistoryItem, groupHistory, HistoryGroup, EntriesMapView) — moved to ./desk/HistoryCards.jsx (plan §3 decomposition).
// Cheap per-group summary for filtering/sorting/list-row display — deliberately separate from
// HistoryItem's render logic (titleOverride etc.) since filters need this for EVERY group up
// front, not just the one being rendered. Same source facts (kindOf/objectTypeOf/summarize).
// `money` is only needed by RENDER callers — the filter/sort/recent-view callers never read
// .price, so they keep the inert default rather than re-running every memo on a currency flip.
function compactSummary(group, districts, features, money = { display: null, rate: null }) {
  const head = group.items[0]
  const anchor = group.items.length > 1
    ? (group.items.find(i => kindOf(i) === 'extract_requirement') || group.items[group.items.length - 1])
    : null
  const objType = objectTypeOf(head)
  // Anchor governs the TITLE (thread identity), but the subtitle (deal type/rooms/floor/etc for
  // a listing) should reflect the HEAD — the freshest actual state — same split HistoryItem uses.
  const headSummary = summarize(head, districts, features, money)
  const title = anchor ? summarize(anchor, districts, features).title : headSummary.title
  const subtitle = headSummary.subtitle
  const titleMuted = anchor ? false : !!headSummary.titleMuted
  const isAuditOnly = objType === 'meeting' || objType === 'candidate'
  const displayStatus = isAuditOnly && head.status === 'accepted' ? 'executed' : head.status
  const reqId = group.items.map(i => i.result?.requirement_id).find(Boolean)
  const kOfHead = kindOf(head)
  const isHeadListing = kOfHead === 'listing_readonly' || kOfHead === 'scraped_listing_readonly'
  // ⚠️ CLOSED IS NOT A CLIENT-ONLY WORD — owner report 2026-08-09, "this never loads any
  // results". `isClosed` was `kindOf(head) === 'close_requirement'` alone, so the Status row's
  // "დახურული" could only ever match a CLIENT. With the client count at 0 (the usual case in a
  // property-heavy scope) the button lit up and the feed emptied, while the facet counts beside
  // it still read 3,185 — a facet number that does not equal what its own click delivers, which
  // is the one rule B273 exists to enforce.
  //
  // The defect is sharper than a missing branch: the CARD FACE ALREADY SAYS IT. HistoryCards
  // renders `live = listing.status === 'active'` and labels everything else `desk_state_closed`
  // — the same Georgian word. So an agent read "დახურული" on a card, clicked "დახურული" in the
  // filter, and got nothing. Measured on the live corpus: 880 withdrawn listings across 557
  // properties are authored by our own agents, and NOT ONE was reachable through this control.
  //
  // 📌 So this must stay the SAME TEST the face uses, not a second opinion about the same word.
  // If the face's rule ever moves, move this with it. (`status` is `active` | `withdrawn` in
  // core — see the note on the dead 'inactive' comparison in HistoryCards.)
  const isClosed = kOfHead === 'close_requirement'
    || (isHeadListing && !!head.listing?.status && head.listing.status !== 'active')
  // Archived = a dismissed draft (proposal status 'dismissed', migration 0045) — a different
  // lifecycle from a *closed client* (close_requirement), so the status filter treats it as a
  // sub-state under Closed rather than conflating the two.
  const isArchived = head.status === 'dismissed'
  // City, for the listing-only City filter — null for every other entry type (client/meeting/
  // property draft), which the filter treats as "can't be filtered out, always shown".
  const city = isHeadListing ? splitCityFromAddress(head.listing?.address).city : null
  // districtCode — second geography signal for the City/District filters. Listings rarely
  // carry it (0/168 live, see splitCityFromAddress note) so address-city does their work;
  // property DRAFTS are the opposite: no reliable city-prefixed address, but the modal/AI
  // sets district_code. Without this, drafts passed every geo filter ("City filter does
  // nothing", 2026-07-07) — the desk is mostly drafts, so the filter looked dead.
  const districtCode = isHeadListing ? (head.listing?.district_code || null)
    : kOfHead === 'extract_listing' ? (head.proposed?.input?.property?.district_code || null) : null
  const ageBadge = isHeadListing ? listingAgeBadge(head.listing) : null
  return { objType, title, titleMuted, price: headSummary.price || null, subtitle, displayStatus, ageBadge, reqId, isClosed, isArchived, city, districtCode, count: group.items.length }
}

// Per-interaction the feed used to re-summarize EVERY loaded group — a currency toggle or a
// sort change paid compactSummary × the whole corpus for rows that had not changed
// (DESK_RENDER_PERF_HANDOFF §5 Phase 1, 2026-07-27). compactSummary is pure in
// (group, districts, features), so the result is cached on the GROUP OBJECT: groups are rebuilt
// by their own memos whenever their rows change, so a stale entry is impossible unless someone
// mutates a group in place — don't (build a new group object instead, as every producer does).
// districts/features are identity-compared; a new list drops the whole cache.
function groupsWithMeta(cache, groups, districts, features) {
  if (cache.districts !== districts || cache.features !== features) {
    cache.districts = districts; cache.features = features; cache.map = new WeakMap()
  }
  let misses = 0
  const out = groups.map(g => {
    let meta = cache.map.get(g)
    if (meta === undefined) { misses++; meta = compactSummary(g, districts, features); cache.map.set(g, meta) }
    return { group: g, meta }
  })
  groupsWithMeta.lastMisses = misses   // read by the DEV perf probe only
  return out
}

// Latest-ref callback stabilizer (B122 Phase 2 step 2): identity-stable wrapper that always
// calls the CURRENT closure. The feed's card components are React.memo'd, so every callback
// prop must keep identity across renders — but handleCardClick/toggleFavorite read live state
// (favRows, districts, …), so a plain useCallback([]) would freeze a stale closure. This is
// the standard latest-ref pattern: stable identity outside, fresh closure inside.
function useStableCallback(fn) {
  const ref = useRef(fn)
  ref.current = fn
  return useCallback((...args) => ref.current(...args), [])
}
// Stable no-op for memo'd cards that take an onToggle but never expand (possible-matches
// shelf) — an inline () => {} would defeat the memo on every parent render.
const NOOP = () => {}

// One line, reused for every row list view ever renders — a lone item, the front of a collapsed
// stack, or one member of an expanded group. `c` lets the caller override the summary (the
// collapsed stack's front row needs the GROUP's anchor-derived title, e.g. the client's identity,
// not just this one item's) — left off, it falls back to summarizing the item on its own, which
// is what an expanded member row wants (each shows its own title, same as the card grid does).
// availableFrom prop: was a FREE IDENTIFIER here since before the decomposition (latent —
// this row is the removed ☰ list view's renderer, so it never mounted); default keeps it
// crash-proof if resurrected. Found by the 2026-07-24 no-undef sweep.
// ⚠️ `reportedGone` MUST be a prop here. It was read on the 0258 market-report badge below
// while only HistoryListRow (a SIBLING top-level function, not a parent closure) declared it
// — a free identifier, so every list-view render threw ReferenceError. Found 2026-07-31 by
// the no-undef sweep while fixing the same class of bug one scope up; pre-existing since
// 6204932, invisible to `vite build` and the test suite alike.
function SingleListRow({ item, c, districts, features, glow, onClick, thread, availableFrom = null, reportedGone = 0 }) {
  const money = useMoney()
  const summary = c || compactSummary({ items: [item] }, districts, features, money)
  const statusColor = { pending: 'yellow', accepted: 'red', executed: 'green', rejected: 'red', edited: 'blue', failed: 'red', dismissed: 'default', active: 'green', inactive: 'default' }[summary.displayStatus] || 'default'
  return (
    <div
      className={`bg-card border rounded-lg flex items-center gap-2 px-3 py-2 cursor-pointer hover:border-primary/40 transition-all ${glow === 'amber' ? glowAmber : glow === 'blue' ? glowBlue : 'border-border'}`}
      onClick={onClick}>
      {summary.objType && <Icon name={OBJECT_ICON[summary.objType]} size={15} className="shrink-0 text-muted-foreground" />}
      <span className="flex-1 min-w-0">
        <span className={`font-medium text-sm truncate block ${summary.titleMuted ? 'text-muted-foreground/70' : ''}`}
          title={summary.titleMuted ? t('desk_entry_address_inherited') : undefined}>{summary.title}</span>
        {/* Deal type/rooms/area/floor/etc for a listing — list view was title+status only,
            nothing about the apartment itself. */}
        {summary.subtitle && <span className="text-xs text-muted-foreground truncate block">{summary.subtitle}</span>}
      </span>
      {/* Price left the subtitle for its own bold slot (2026-07-17 card redesign) — the list
          row keeps it visible too, right-aligned before the status badge. */}
      {summary.price && (
        <span className={`text-sm font-bold shrink-0 tabular-nums ${summary.price.muted ? 'text-muted-foreground/60' : ''}`}
          title={priceTitle(summary.price)}>{summary.price.text}<ObservedPriceHistoryBadge history={summary.price.history} /></span>
      )}
      {/* price per m² beside it, as on the card face (summarize, owner 2026-10-24) */}
      {summary.price?.ppm && <span className="text-xs text-muted-foreground shrink-0 tabular-nums hidden sm:inline">{summary.price.ppm}</span>}
      {summary.ageBadge
        ? <Badge label={summary.ageBadge.label} variant={summary.ageBadge.variant} title={summary.ageBadge.title} />
        : <Badge label={t(STATUS_LABEL_KEYS[summary.displayStatus]) || summary.displayStatus} variant={statusColor} />}
      {(() => { const b = availableFromBadge(availableFrom); return b ? <Badge label={b.label} variant={b.variant} title={b.title} /> : null })()}
      {/* 0258: list view badges it too — the sink applies to BOTH views, so a card that
          silently moved down in list view without the badge would be the same
          unexplained behaviour the card view avoids. */}
      {(() => { const b = marketReportBadge(reportedGone); return b ? <Badge label={b.label} variant={b.variant} title={b.title} /> : null })()}
      {thread && (
        <button
          className="text-xs text-muted-foreground hover:text-foreground shrink-0 underline decoration-dotted"
          onClick={e => { e.stopPropagation(); thread.onToggle() }}>
          🧵{thread.count} {thread.expanded ? '▲' : '▼'}
        </button>
      )}
      <span className="text-xs text-muted-foreground shrink-0 hidden sm:inline tabular-nums">
        {/* A scraped listing with no recorded listed_at (rare, e.g. a bare-created source ad)
            has created_at: null — guard so it renders blank instead of the 1970 epoch time. */}
        {item.created_at && new Date(item.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
      </span>
    </div>
  )
}

// List view: one line per client/meeting/candidate. Clicking anywhere on a row opens it for
// editing — same router as card view (onCardClick).
// memo'd like HistoryGroup (B122 step 2) — same contract: every prop identity-stable.
const HistoryListRow = memo(function HistoryListRow({ group, availableFrom = null, reportedGone = 0, districts, features, glow, onCardClick, expanded, onToggle, shade = 0 }) {
  const money = useMoney()
  const [head, ...rest] = group.items
  const n = group.items.length

  const kHead = kindOf(head)
  const isListingCascade = kHead === 'listing_readonly' || kHead === 'scraped_listing_readonly'
  // CLIENT thread = ONE row, final state only, NO expand — same reasoning as the card grid
  // (HistoryGroup): the events are versions of one demand, folded to final state here; the
  // per-event history lives in the Activity side panel, not on this search surface. The
  // stack/expand below is reserved for listing cascades, whose members are distinct listings.
  if (rest.length > 0 && !isListingCascade) {
    return (
      <SingleListRow item={head} c={compactSummary(group, districts, features, money)} districts={districts} features={features} glow={glow}
        reportedGone={reportedGone} onClick={() => onCardClick(group)} />
    )
  }

  if (rest.length > 0 && expanded) {
    // LISTING cascade = every member is its own full row: the origin ad and each reshare are
    // genuinely distinct listings (own natural keys/contacts), not versions of one thing.
    const ringClass = shade === 1 ? 'ring-yellow-800/60' : 'ring-amber-300/60'
    return (
      <div className="space-y-1.5">
        {group.items.map(item => (
          <div key={item.id} className={`ring-2 ${ringClass} rounded-lg`}>
            <SingleListRow item={item} districts={districts} features={features}
              reportedGone={reportedGone}
              onClick={() => onCardClick({ key: group.key, items: [item] })}
              thread={{ count: n, expanded, onToggle }} />
          </div>
        ))}
      </div>
    )
  }

  // COLLAPSED (default) = the same corner-sliver stack the card grid uses: the front row is a
  // normal, full row with real content (the group's anchor-derived title via `c`); sheets behind
  // it are bare — same size, translated diagonally — so only a thin top-left line peeks out per
  // extra item.
  const STEP = 4
  // Same 4-sheet cap + fade as the card grid — the sheets are a cue, not a count.
  const SHEETS = Math.min(rest.length, 4)
  return (
    <div className="relative" style={SHEETS ? { paddingTop: SHEETS * STEP, paddingLeft: SHEETS * STEP } : undefined}>
      {Array.from({ length: SHEETS }, (_, j) => (
        <div key={j} className="absolute rounded-lg border border-border bg-card shadow-sm"
          style={{ top: j * STEP, left: j * STEP, right: (SHEETS - j) * STEP, bottom: (SHEETS - j) * STEP, zIndex: j + 1, opacity: (j + 1) / (SHEETS + 1) }} />
      ))}
      <div className="relative" style={{ zIndex: SHEETS + 1 }}>
        <SingleListRow item={head} c={compactSummary(group, districts, features, money)} districts={districts} features={features} glow={glow}
          reportedGone={reportedGone}
          onClick={() => onCardClick(group)}
          thread={rest.length ? { count: n, expanded, onToggle } : null} />
      </div>
    </div>
  )
})

// ─── Advanced search — lives where the retired paste box used to be ───────────
// Structured filters over the Recent-Entries grid (price/rooms/area/deal/type), applied to
// LISTING rows; client/meeting/property-draft entries always pass through (same "null passes"
// rule as the City filter — these filters describe apartments, not people). Price compares raw
// listing price within ONE selected currency — never converts across currencies silently.
// q / near (v5, 2026-07-16): the search box's residual text and the active near-location
// token ride advFilters so the whole filtered-fetch pipeline (debounce, value-signature,
// load-more gating, local fast path) treats them like any panel filter — that's what makes
// text/geo search SERVER-side (RPC v5, migration 0104). They render no constraint chip of
// their own (the text sits in the box; the near token already chips via its sig).
// Checkbox facets (2026-07-17, eBay-style sidebar): intents/kinds/roomsSet/bedroomsSet are
// ARRAYS (multi-select OR within the facet, AND across facets — same composition rule as
// districtCodes). roomsSet/bedroomsSet members are 1..5 where 5 is the "5+" box (≥5); see
// facetSetCheck/facetSetEnvelope. bathrooms/floor/floorsTotal stay min/max ranges (Details
// group) — real from–to use, no natural checkbox vocabulary.
// topFloor (engine v15 / RPC v11, 2026-07-18): boolean — the unit's floor equals the
// building's floors_total ("ბოლო სართული"). Cross-column fact, not a range.
// The four view-status modes (0249 + 0253). ONE list, shared by the panel, the chip row and
// the fetch layer — a mode present in one and missing from another is the half-existing-key
// trap that has bitten COMMITTED_FILTER_KEYS twice.
const SEEN_MODES = ['unseen_by_me', 'seen_by_me', 'unseen', 'seen']
// Axis-B market-spread bands (B213, 0263). Band tokens the server maps to
// p_spread_min/max; '' = any = never committed. The slider that ordered these was replaced
// by checkboxes (2026-07-30), so the list lives at the render site — the chip row resolves
// its label straight off the token, and nothing else needed the ordering.
// 0294 seller-type band tokens. The SLIDER was cut 2026-07-30 (the portal filter does
// the same job with complete coverage — see the CUT note in the Lead status facet), so
// the ordered stop list died with it. sellerType is still accepted, sent, chipped and
// badged: a saved search carrying it must keep meaning.
// Our footprint on the cluster (0294), replacing the 0262 ordinal ladder. A SET, not a
// threshold: "we reposted it" is not MORE exposure than "someone worked it", they differ
// in kind. Ordering here is display order only and implies nothing.
const FOOTPRINT_STATES = ['none', 'viewed', 'reposted']
// Which definition of "age" the day-range means (0294). first_posted (myhome) survives
// renewal = true time-on-market; current_run (ss/korter) RESETS on every renewal. A myhome
// ad renewed weekly since 2024 reads 600 days while an identical ss ad reads 3, so these
// must never be silently averaged into one number.
const AGE_KINDS = ['first_posted', 'current_run']
// Time on market is picked the way people think about it, not as a numeric range (owner
// 2026-07-31: "if agents repeatedly hunt for this week / this month / older than a month,
// then arbitrary numeric ranges are the wrong PRIMARY control"). The from/to boxes are not
// deleted — they move behind "Custom…", so precision stays available to whoever wants it
// without being the default burden on everyone.
//
// These are THRESHOLDS, not a partition: "this week" contains "today", "this month"
// contains both. That nesting is how the words already work in speech, and it is safe here
// because only one option is ever active — nothing is double-counted. Only the last one
// points the other way, and it is the complement of "this month", so between them the whole
// corpus is reachable.
//
// ⚠️ Bounds are DAYS as a float server-side: extract(epoch from now() - posted_at)/86400.0
// (0294 ov_age). Integer-abutting bands (…7 / 8–30) would strand a 7.4-day-old listing in
// no band at all, so the boundary value is shared rather than skipped.
const AGE_PRESETS = [
  { key: 'today', lo: '',   hi: '1' },
  { key: 'week',  lo: '',   hi: '7' },
  { key: 'month', lo: '',   hi: '30' },
  { key: 'older', lo: '30', hi: '' },
]
// ⚠️ THE SUBTYPE MAP LIVES IN `lib/taxonomy.js` (SEARCH_GRAMMAR.md §5b), not here. B265
// declared it locally; the 2026-08-01 ruling made the taxonomy a first-class pure module the
// tree, the panel and the gate all read, so this file consumes it like everyone else.
// A new token is a GRAMMAR change first (§5), then subtype_map.js, then the server whitelist,
// then taxonomy.js, then the box vocabulary.
const SUBTYPES_BY_KIND = KIND_SUBTYPES
// Subtypes belong to their kind: EVERY writer of `kinds` prunes chips whose kind is no
// longer selected. A dangling subtype (land unchecked, `agricultural` still set) would
// shelve the entire result set — unknown ≠ no, but it is also not what the agent still
// means. Three writers exist today (Type facet, category pill, removeChip); all go through this.
const pruneSubtypes = (kinds, subtypes) => {
  if (!(subtypes || []).length) return subtypes || []
  const allowed = new Set((kinds || []).flatMap(k => SUBTYPES_BY_KIND[k] || []))
  return (subtypes || []).filter(s => allowed.has(s))
}
// One frozen empty count map (2026-10-22): a fresh `{}` per render would re-run every count memo.
const NO_COUNTS = Object.freeze({})
// ⚠️ REGISTRY FIRST, LITERAL SECOND — and the order is the safety property. A key declared
// in BOTH takes the literal's value, so this spread cannot change any default that exists
// today; it can only supply one for a filter that is registered and not yet written here.
// That is what makes deskFilters.js a mechanism rather than documentation: before this, a
// filter added to the registry alone had no initial value and read as undefined everywhere.
const ADV_DEFAULTS = { ...registryDefaults(), intents: [], kinds: [], subtypes: [], sources: [], agentIds: [], currency: 'USD', districtCodes: [], districtOwnCodes: [], streetIds: [], complexIds: [], streetNearbyM: 0, priceMin: '', priceMax: '', roomsSet: [], bedroomsSet: [], bathroomsMin: '', bathroomsMax: '', balconyCountMin: '', balconyCountMax: '', floorMin: '', floorMax: '', floorsTotalMin: '', floorsTotalMax: '', topFloor: false, areaMin: '', areaMax: '', landAreaMin: '', landAreaMax: '', restrictions: [], featureCodes: [], featureStates: [], conditionCodes: [], advanceMaxMonths: '', moveInBy: '', moveInHard: false,
  // 0249. dateField defaults to 'added' rather than '' because it is a DIMENSION, not a
  // constraint — the panel always needs a selected option to show, and canonicalFilters
  // drops it from committed state whenever no range accompanies it, so the default never
  // makes an empty search look non-empty.
  dateField: 'added', dateFrom: '', dateTo: '', claimsLt: '', claimsGt: '', seen: '',
  // B1188. FOUR date dimensions, each with its own range, all combinable -- {dim: {from, to}}.
  // Hand-written because registryDefaults() gives '' for a value kind and this one's empty
  // value is {}. It SUPERSEDES dateField/dateFrom/dateTo rather than replacing them: old URLs
  // and saved searches still carry the trio, and deskUrl migrates them on read.
  dateRanges: {},
  // B215 (0262): exposure ladder threshold, ''|'0'|'1'|'2' as a string like its claims
  // siblings. '' = ყველა = off; there is no '3' (at-most-top-rung ≡ off, see the facet).
  exposureMax: '',
  // 0431 — "…except the ones I work". A MODIFIER on the team-activity rows, not a filter:
  // alone it selects nothing (the RPC ignores it unless a footprint/exposure value is set),
  // which is why it is absent from advFiltersActive below. Same shape as moveInHard.
  keepMine: false,
  // B213 (0263): market-spread band, ''|'1'|'2_3'|'4p'. '' = ყველა = off.
  spread: '',
  // 0294 (v22) — the hunter lenses. sellerType is a band token like spread; footprint and
  // ageKinds are multi-select arrays; the rest are strings so the '' = off convention and
  // the meaningful-zero guards match their claims/exposure siblings above.
  sellerType: '', ageMinDays: '', ageMaxDays: '', ageKinds: [],
  reposts: '', ownerPlatforms: '',
  viewsMin: '', promoted: '', footprint: [], ownerEngaged: '',
  // Advert quality (0702-0704, RPC v55, 2026-08-22). `photos` is a band token
  // (''|'none'|'thin'|'ok') and NOT two booleans: "3 or fewer" strictly contains "none",
  // so a pair of checkboxes could both be ticked and mean nothing. noDescription and
  // missingFacts are true-or-off flags — a `false` would read as "adverts that DO have a
  // description", which is not a question this section asks.
  // ⚠️ All three are judged on the OWNER's advert only (owner ruling: an agent may repost
  //    a bare advert with photos of their own). That rule lives in SQL, not here.
  photos: '', noDescription: false, missingFacts: false,
  q: '', near: null }
// Desk sort option → v13 server sort key (0186). newest = server default ordering
// (activity, omitted); oldest has no server equivalent and stays client-side over
// loaded rows. The client's own re-sort (visibleResult) keeps running on the same
// key — cosmetic tiebreak over what the server already ordered, plus mixed-type
// feeds (clients/meetings) the server never sees.
// 0299 adds `posted` → the RPC's 'freshest': the cluster's EARLIEST posted_at, descending
// = newest on the market. Both hunter reviews asked for a freshness sort ("filter the
// garbage out, then sort the gold by freshness"; "for fresh leads I need to sort ascending,
// minutes matter") and none existed.
// ⚠️ NOT wired to the existing `fresh` option, which sorts by last_seen_at — "most recently
// confirmed still alive by our crawler". That is a different question, and quietly
// repointing it would change what a saved sort means. Two options, two labels.
// 0310 (owner ask 2026-07-30: "all these three dates sorting up and down") pushes the
// ascending halves down to the RPC, and adds `added`/`added_asc` — min(listed_at), the
// date WE captured it, corpus-wide in both directions.
// ⚠️ `added` is a NEW key, NOT the legacy `newest` renamed. `newest`/`oldest` order by the
// ENTRY's created_at (deskFeed's comparator tail) and are pinned by golden fixtures, with
// `newest` as deskFeed's own default — repointing them would have silently changed the
// default feed order. They stay accepted for saved searches; the dropdown offers `added`
// instead (the B206 rule: a control can leave the screen while its key keeps working).
// `fresh`/`fresh_asc` ("last checked") got a real server key 2026-08-03: migration 0380
// added max(last_seen_at) as p_sort 'checked'/'checked_asc' to the cluster RPC, and
// server.js's SORT_OPTS accepts both. The owner's three dates now all sort corpus-wide:
// market age (posted_at), added-to-Sentavus (listed_at), last checked (last_seen_at).
// ⚠️ The rule stands for any FUTURE key: it must exist in the RPC *and* in server.js's
// SORT_OPTS, or it silently degrades to the default — which reads as "sort does nothing".
const SERVER_SORT_OF = {
  // Best Match (core 1197–1203): the champion's frozen rank — the Leads default.
  best_match: 'best_match',
  updated: 'updated', updated_asc: 'updated_asc',
  cheapest: 'price_asc', priciest: 'price_desc', largest: 'area_desc',
  posted: 'freshest', posted_asc: 'posted_asc',
  added: 'added', added_asc: 'added_asc',
  fresh: 'checked', fresh_asc: 'checked_asc',
}
// Fix plan 9: Commission is server-sorted on Leads ONLY. The book feed keeps ranking it in the browser (deskFeed.js)
// over its loaded rows; mapping it in SERVER_SORT_OF would also send it from the book, where the route reads it as
// the default order and the client comparator would be ranking a page chosen by something else.
const LEADS_SERVER_SORT_OF = { ...SERVER_SORT_OF, commission: 'commission' }
// ONE sort menu for both surfaces (owner 2026-07-31: "I just don't get what sorts what").
// Desktop and mobile used to declare the same 14 options twice, in two literals a comment
// begged future sessions to keep in step; they are built here once instead.
//
// Two things were wrong, and the second one is the interesting one.
//
// (1) The same date wore two names. `updated` desc read "განახლებული" while `updated_asc`
//     read "ბოლოს შეიცვალა"; `posted`/`fresh` desc wore idioms while their ascending halves
//     wore the date-facet name. One date, two names, read as two unrelated sorts.
// (2) Grouping by TIMESTAMP is still the schema in a disguise (owner's correction to this
//     file's first fix). Nobody thinks in posted_at / listed_at / updated_at / last_seen_at.
//     They think: how old is the AD, how long have WE had it, is it still alive. So the
//     groups are the SUBJECT a date is about — 📰 the advertisement, 📥 our own copy,
//     🤖 our monitoring — and each option is named for the answer it gives.
//
// Structure that falls out of that:
//   · ONE row per sort, not one per direction. A reversible row carries `altValue`; clicking
//     it while active flips (Gmail/Linear/Airtable). That halves the menu and, more
//     importantly, stops one date looking like two sorts.
//     Price stays as explicit rows: "cheapest" and "largest" are different QUESTIONS about
//     different columns, not two ends of one — collapsing them would invent a pair.
//   · A reversible row is a NOUN + a direction line, and the noun NEVER changes. Two earlier
//     passes had the whole row relabel on flip ("Newest advertisement" → "Oldest
//     advertisement" → "Longest unchanged"); the menu appeared to morph under the cursor,
//     and the reversed names drifted into phrasing nobody actually says. Identity constant,
//     direction variable — and no ▲▼ anywhere, because words carry what an arrow can't.
//   · Crawler-check time ("last checked") was `advanced: true` until 2026-08-03; it is
//     now a visible row — the owner ruled it one of the three first-class dates and it
//     sorts server-side (0380).
//   · Values are UNCHANGED, both halves of every pair included: saved searches, shared
//     URLs and SERVER_SORT_OF (B206) keep working exactly as before. Only labels moved.
// ppm2 has no server key by design — it is ordered client-side over the rows
// already loaded (B128; pushdown waits for the RPC family's next coordinated bump). The
// PROPERTY feed does that ordering in deskFeed's comparator. The LEADS feed does not: its
// only ordering is a stable re-sort by reported_unavailable_count, so on Leads these two
// sorted nothing at all — a menu entry that silently did nothing (owner-caught 2026-07-31,
// alongside the server-gate bug that broke every OTHER sort on that feed).
// Offering them there is the failure this whole pass exists to remove, so they are withheld
// on the feeds that cannot honour them. The VALUES stay accepted everywhere (B206): a saved
// search carrying ppm2 keeps working on Properties and simply is not offered on Leads.
const CLIENT_ONLY_SORTS = new Set(['ppm2'])
// WHICH SORTS EACH FEED CAN ACTUALLY EXECUTE (2026-07-31).
// The sort and period pills are toolbar chrome: they render for every category, with no
// gate. Four of the five special feeds honoured neither — MeetingsFeed takes no `sort`
// prop, PoolFeed is ({agentId, query}), BusinessFeed is ({query}), and Favorites rendered
// [...favRows.values()] in Map insertion order. So the same control the owner caught doing
// nothing on Leads was doing nothing on four more surfaces at once.
// The fix is NOT to wire a sort into every feed — most of this menu is meaningless against
// a meeting, which has no price and no m². It is to stop offering what cannot be honoured:
//   undefined  → the full set (the property feed)
//   a Set      → only these (the feed executes exactly this much)
//   null       → this feed sorts nothing; the control is HIDDEN, not disabled
const FEED_SORTS = {
  // Server-executed only: LeadsFeed runs no client comparator (see CLIENT_ONLY_SORTS).
  // 'commission' (withdrawn 2026-10-22, back 2026-10-24, fix plan 9): Leads sends it through LEADS_SERVER_SORT_OF
  // and the server ranks by the viewer's VISIBLE deal values (core_cooperation.js liveDealRank → p_comm_rank).
  Leads: new Set(['best_match', 'activity', 'commission', 'cheapest', 'priciest', 'largest',
    'posted', 'posted_asc', 'updated', 'updated_asc', 'added', 'added_asc',
    'fresh', 'fresh_asc']),
  // Favorites is a local snapshot: whatever the saved card itself carries can be ordered
  // honestly and for free. It carries price/currency/area — and nothing else the sort menu
  // asks about, so the date sorts and commission are absent rather than silently inert.
  // 'activity' keeps its slot as the default and means what it can mean here: most
  // recently favourited, which is the order the feed already arrived in.
  Favorites: new Set(['activity', 'cheapest', 'priciest', 'largest', 'ppm2']),
  Meeting: null, Business: null,
}
// Same question for the period pill. MeetingsFeed does take `since`; Business/Favorites
// do not, and inventing one for Favorites would have to pick a meaning (favourited-on vs
// listed-on) that nobody has chosen.
const FEED_PERIOD = { Business: false, Favorites: false }
// `feed` (F-098, 2026-09-09): the SAME key means different things on different feeds, so the row
// that key fills must be able to say so. The owner asked for leads to default to "date added to
// senTavus" — and it already did: on leads every member is authored, so both the materialised path
// (`lead_cluster_page_v6`) and the filtered body order by max(listed_at), which IS that date. What
// was wrong was the WORD: ⚡ "Active", hinted "Recently changed, added, or re-listed", describing a
// smart ranking that exists nowhere in the SQL.
// ⚠️ The KEY stays 'activity' and must. The leads fast path is gated on `p_sort is null or p_sort =
// 'activity'` and serves ~88% of leads traffic from the prebuilt layer; defaulting to 'added' would
// take every default leads view off it and onto the 137 KB body. This is a rename, and renaming a
// LABEL is exactly how it stays one.
// The BOOK feed (the property feed, the one with the full sort set) is named "Started working" since
// F-098 part 2: the server publishes `book_at` (min of agent_cluster_member.mine_at per cluster) and
// deskFeed orders by it, so the word is finally true of the order on screen. Favorites keeps ⚡:
// there 'activity' means most-recently-favourited, the order that feed already arrives in.
const deskSortOptions = ({ allow = null, feed = null } = {}) => {
  const leads = feed === 'Leads'
  const book = !leads && !(feed && feed in FEED_SORTS)
  // One pair of direction strings for every reversible row: `dir` is the line under the
  // active row, `dirShort` the tail on the closed pill (where the row's context is gone).
  const dir = { desc: t('desk_sort_dir_newest'), asc: t('desk_sort_dir_oldest') }
  const dirShort = { desc: t('desk_sort_dir_newest_short'), asc: t('desk_sort_dir_oldest_short') }
  return [
  // Best Match (owner 2026-09-16) — Leads only, and first: the leads most worth an agent's attention
  // right now, ranked by the registered, frozen Best Match model. It carries the group heading on Leads.
  ...(leads ? [{ value: 'best_match', group: t('desk_sort_group_smart'),
    label: `🎯 ${t('desk_sort_opt_best_match')}`, hint: t('desk_sort_hint_best_match'),
    pillLabel: `🎯 ${t('desk_sort_opt_best_match')}` }] : []),
  { value: 'activity', group: leads ? undefined : t('desk_sort_group_smart'),
    label: leads ? `📥 ${t('desk_sort_id_added_sentavus')}` : book ? `📥 ${t('desk_sort_id_started_working')}` : `⚡ ${t('desk_search_sort_activity')}`,
    hint: leads ? t('desk_sort_hint_added_sentavus') : book ? t('desk_sort_hint_started_working') : t('desk_sort_hint_activity'),
    pillLabel: leads ? `📥 ${t('desk_sort_id_added_sentavus')}` : book ? `📥 ${t('desk_sort_id_started_working')}` : `⚡ ${t('desk_search_sort_activity')}` },
  { value: 'commission', label: `🤝 ${t('desk_sort_opt_commission')}`, hint: t('desk_sort_hint_commission'),
    pillLabel: `🤝 ${t('desk_search_sort_commission')}` },
  // No longer `advanced: true` (2026-08-03): the owner named "last checked" as one of the
  // three first-class dates (market / added / checked), and it now sorts server-side (0380).
  // Under ⭐ Recommended since 2026-10-01 (owner: "move last check in recommended category");
  // it was alone under 🤖 Monitoring, which therefore no longer renders.
  { value: 'fresh', altValue: 'fresh_asc', dir, dirShort,
    label: t('desk_sort_id_verified'), hint: t('desk_sort_hint_fresh'),
    pillLabel: `🤖 ${t('desk_sort_id_verified')}` },

  { value: 'cheapest', group: t('desk_sort_group_money'), label: t('desk_search_sort_cheapest'),
    pillLabel: t('desk_search_sort_cheapest') },
  { value: 'priciest', label: t('desk_search_sort_priciest'), pillLabel: t('desk_search_sort_priciest') },
  { value: 'ppm2',     label: t('desk_sort_opt_ppm2'), pillLabel: t('desk_sort_opt_ppm2') },   // client-only
  { value: 'largest',  label: t('desk_search_sort_largest'), pillLabel: t('desk_search_sort_largest') },

  { value: 'posted', altValue: 'posted_asc', dir, dirShort, group: t('desk_sort_group_ad'),
    label: t('desk_sort_id_ad_age'), hint: t('desk_sort_hint_posted'),
    pillLabel: `📰 ${t('desk_sort_id_ad_age')}` },
  { value: 'updated', altValue: 'updated_asc', dir, dirShort,
    label: t('desk_sort_id_updated'), hint: t('desk_sort_hint_updated'),
    pillLabel: `📰 ${t('desk_sort_id_updated')}` },

  { value: 'added', altValue: 'added_asc', dir, dirShort, group: t('desk_sort_group_ours'),
    label: t('desk_sort_id_imported'), hint: t('desk_sort_hint_added'),
    pillLabel: `📥 ${t('desk_sort_id_imported')}` },
  ].filter(o => !allow || allow.has(o.value))
}
// Every array-valued filter field — token add/remove and chip removal treat these as SETS
// (union on add, subtract own values on remove), never plain assignment (which would
// clobber manual picks; the districtCodes lesson of 2026-07-16 generalized).
// Filter-mode options for the restriction chips — the Desk's own vocabulary, deliberately
// NOT the four capture stances (RESTRICT_MODES in deskFeed.js explains why). A function,
// not a constant: the labels are translated, and a module constant would freeze them in
// whichever language loaded first.
// TWO states, ✓ and ✗ (owner, 2026-07-28: "I was wrong about 3 states — we need two
// states of these chips: it either is accepted and has a tick, or is not and has x").
// The chip mirrors the question the ad answers about a person — accepted or not — and
// nothing else:
//   ✓ accepted     — I want places that take this client. Recorded refusals drop out;
//                    unrecorded PASSES (only 2.6% of listings state anything, so treating
//                    silence as refusal would empty the feed — see B206).
//   ✗ not accepted — show me the ads that DO refuse them.
// ⚠️ `allow` (only where explicitly recorded as accepted) stays in RESTRICT_MODES and in
// the classifier, but is no longer OFFERED: dropping it from the parser would silently
// change the meaning of any saved search or shared URL that already carries it.
const restrictModeOptions = () => [
  { value: 'hide',       icon: STANCE_ICON.allowed,     style: STANCE_STYLE.allowed,     label: t('desk_filter_mode_hide') },
  { value: 'restricted', icon: STANCE_ICON.not_allowed, style: STANCE_STYLE.not_allowed, label: t('desk_filter_mode_restricted') },
]
// Union with the registry (see ADV_DEFAULTS above): a registered 'set' filter gets array
// semantics — token UNION rather than replace — without being named here as well.
const ADV_ARRAY_KEYS = [...new Set(['districtCodes', 'districtOwnCodes', 'streetIds', 'complexIds', 'metroIds', 'intents', 'kinds', 'subtypes', 'sources', 'roomsSet', 'bedroomsSet', 'restrictions', 'featureCodes', 'featureStates', 'conditionCodes', ...registryArrayKeys()])]
// facetSetCheck moved to ../lib/deskFeed.js (Phase 0 extraction) — imported above.
// Server translation: the RPC (0048/0105) speaks min/max only, so the set ships as its
// [lo,hi] envelope (5+ → open top) and the exact set is re-enforced client-side by
// facetSetCheck — a non-contiguous pick (1,3) over-fetches 2s server-side, never misses.
const facetSetEnvelope = set => {
  if (!set?.length) return [null, null]
  return [Math.min(...set), set.includes(5) ? null : Math.max(...set)]
}
// normalizeAdvFilters + its VACUOUS_ZERO_MIN_KEYS moved to ../lib/deskFeed.js (Phase 0
// extraction) — imported above. The vacuous-0-min rule they carry is unchanged.
// Draft token overlay (DESK_MODE_REWRITE_PLAN §2.3) — PURE merge of the live compiled
// tokens' filter patches over the COMMITTED advFilters. This replaced the prevTokensRef
// diff effect (which patched advFilters itself, per keystroke): committed state must be
// written only by commit acts, so token compilation now feeds a derived draft instead.
// Merge semantics preserved from the diff effect:
//   • array fields (ADV_ARRAY_KEYS) are SETS — token values UNION into the committed set,
//     never clobber manual picks (the districtCodes lesson of 2026-07-16);
//   • scalar fields — the token value applies only where the committed side is unset
//     (equals its default), so panel edits made while a token is live keep winning
//     (the diff effect got this by never re-applying unchanged tokens);
//   • currency rides the token (it only means anything next to the token's own price).
// Removal bookkeeping is GONE by construction: a token that leaves activeTokens (deleted
// text, dismissed sig) simply stops being merged — nothing to un-patch, no invisible
// leftover filter. Also the fold applied by commitSearch (draft → committed, §2.3).
// (2026-10-22: the merge itself lives in searchCommit.js, tested there. A commit passes { fold: true }: the typed scalar
// replaces the committed one, price and currency as a pair — after "≤ 200,000 USD", "1000 ლარი" kept 200,000 as GEL.)
function applyTokenOverlay(f, tokens, opts = {}) {
  return applyTokenOverlayPure(f, tokens, { defaults: ADV_DEFAULTS, arrayKeys: ADV_ARRAY_KEYS, ...opts })
}
// advFiltersActive moved to ../lib/deskFeed.js (Phase 0 extraction) — imported above.
// 🗑 B705 — `streetCoreToken` / `STREET_TYPE_WORDS` were DELETED here, not sharpened.
//
// They implemented a best-effort "is this address on the street" test from the 2026-07-24
// geo-radius era, when a property had no street link and a street search was a ~700m
// proximity filter: reduce the street name to its most-identifying token (the surname) and
// substring-match it against the address. For `შ. ნუცუბიძის ქ.` that token is `ნუცუბიძის`,
// which is ALSO inside `ნუცუბიძის პლატო` and `ნუცუბიძის ფერდობი` — so plateau listings were
// counted as exactly ON the street. Owner: *ნუციბიძის ქუჩა და პლატო სხვადასხვაა.*
//
// ⚠️ The links were never wrong. `property_street` is the system of record and every one of
// its rows across the Nutsubidze streets names the street properly; none name the plateau.
// The CLIENT was guessing beside a record that already knew the answer. B705's ruling was to
// RETIRE the guesser, not improve it: two implementations of "on this street" that can
// disagree IS the defect, and a better heuristic is still a second implementation.
//
// "On this street" now has exactly one asker — `p_street_ids` — used by both the search
// box's commit path and the sidebar facet.
// One labeled from–to pair of narrow numeric boxes — rooms/bedrooms/floor are single digits,
// they never need full-width inputs.
function MiniRange({ label, lo, hi, onLo, onHi }) {
  return (
    <label className="flex items-center gap-2 text-xs text-muted-foreground">
      <span className="w-20 shrink-0">{label}</span>
      <div className="flex items-center gap-1 flex-1">
        <input className={inputCompact} type="number" min="0" placeholder={t('desk_range_from_ph')} value={lo} onChange={e => onLo(e.target.value)} />
        <input className={inputCompact} type="number" min="0" placeholder={t('desk_range_to_ph')} value={hi} onChange={e => onHi(e.target.value)} />
      </div>
    </label>
  )
}
// Data-driven slider ceiling, rounded UP to a "nice" 1/2/5×10ⁿ number, so the histogram fits the
// actual market segment on screen (a fixed $1M ceiling is right for sales but squashes rent into
// the first bar; a fixed 500m² buries a wall of 40-70m² flats when a few land plots are mixed in).
//
// The cutoff is the Tukey UPPER FENCE (Q3 + 1.5·IQR): the axis hugs the main CLUSTER and a thin
// long tail of far-out listings (a lone 5000m² lot, one luxury penthouse) collapses into the "+"
// bucket instead of stretching the slider flat with dead space. p95 (the old rule) failed here —
// a real ~10% tail of large properties pulled it up to 2000m² while 88% sat under 105m². We floor
// the cutoff at p90 (never clip the real cluster) and cap it at the true max (no empty space past
// the largest value). Typing a bigger max by hand still works — the thumb just parks at the end.
function niceCeiling(values, fallback) {
  let nums = values.filter(v => Number.isFinite(v) && v > 0).sort((a, b) => a - b)
  if (nums.length < 5) return fallback
  // Median-anchored trim FIRST: with Deal=Any the values are bimodal (rents ~300-1500 mixed
  // with sales ~37k+), and quartiles/p90 straddle the gap — the axis stretched to $50K while
  // 200+ rentals sat in bucket 0. Anything >20× the median is a different market segment, not
  // an outlier of this one; drop it before the fence math so the axis hugs the DOMINANT
  // cluster (the minority segment collapses into the "+" bucket). ×20 spans any real
  // single-segment spread (600→12k rent, 100k→2M sale) so unimodal data is untouched.
  const med = nums[Math.floor(nums.length / 2)]
  nums = nums.filter(v => v <= med * 20)
  const at = p => nums[Math.min(nums.length - 1, Math.floor(nums.length * p))]
  const q1 = at(0.25), q3 = at(0.75)
  const fence = q3 + 1.5 * (q3 - q1)
  const target = Math.min(Math.max(fence, at(0.90)), nums[nums.length - 1])
  const mag = 10 ** Math.floor(Math.log10(target))
  for (const m of [1, 2, 5, 10]) if (m * mag >= target) return m * mag
  return fallback
}
// "Smart sticky" wrapper for the search-tools sidebar. The panel is taller than the
// viewport, so a single static CSS sticky anchor can't reveal both edges: top-sticky hides
// the bottom until the page end, bottom-sticky hides the top until the page start. Instead
// the pinning itself stays pure CSS `position: sticky` (compositor-smooth — a per-frame JS
// translate was visibly shaky, and sticky is naturally bounded by its container so it can
// never slide over the footer), and JS only flips the sticky anchor when the SCROLL
// DIRECTION changes: scrolling down → top = viewport − height (bottom edge pinned),
// scrolling up → top = below the app header (which is sticky itself and would otherwise
// cover the panel's first rows). At each flip, margin-top re-anchors the panel's flow
// position to exactly where it currently sits, so the hand-off is seamless.
function StickyAside({ children, className = '' }) {
  const asideRef = useRef(null)
  const innerRef = useRef(null)
  useEffect(() => {
    const aside = asideRef.current
    const inner = innerRef.current
    if (!aside || !inner) return
    aside.style.position = 'relative'   // absolute-mode offset parent
    let mode = null      // 'abs' | 'fixedTop' | 'fixedBottom'
    let lastY = window.scrollY
    const isLg = () => window.matchMedia('(min-width: 768px)').matches   // matches the md: row layout below
    const maxTop = () => (document.querySelector('header')?.offsetHeight || 0) + 8
    const minTop = h => Math.min(maxTop(), window.innerHeight - h - 8)
    // Absolute = scrolls naturally with the page, anchored inside the aside's flex slot
    // (so it can never leave the column / overlap the footer). Fixed = pinned, and the
    // browser holds it with ZERO per-scroll style writes — that's the stability fix: the
    // previous translate-compensation approach re-corrected the position on every scroll
    // event, which read as shake. Here JS only writes styles at mode TRANSITIONS.
    const setAbs = top => {
      mode = 'abs'
      const h = inner.offsetHeight
      const clamped = Math.max(0, Math.min(top, (aside.parentElement.offsetHeight || h) - h))
      inner.style.position = 'absolute'
      inner.style.top = `${clamped}px`
      inner.style.left = '0'
      inner.style.width = '100%'
    }
    const setFixed = (m, top) => {
      mode = m
      inner.style.position = 'fixed'
      inner.style.top = `${top}px`
      inner.style.left = `${aside.getBoundingClientRect().left}px`
      inner.style.width = `${aside.clientWidth}px`
    }
    const currentAbsTop = () => inner.getBoundingClientRect().top - aside.getBoundingClientRect().top
    const onScroll = () => {
      if (!isLg()) return
      const y = window.scrollY
      const dir = y > lastY ? 'down' : y < lastY ? 'up' : null
      lastY = y
      if (!dir) return
      const h = inner.offsetHeight
      const rect = inner.getBoundingClientRect()
      const parentRect = aside.parentElement.getBoundingClientRect()
      if (mode === 'fixedBottom') {
        if (dir === 'up') setAbs(currentAbsTop())                       // release: scroll up reveals top immediately
        else if (parentRect.bottom < rect.bottom) setAbs(parentRect.height - h)  // container end: park at column bottom
      } else if (mode === 'fixedTop') {
        if (dir === 'down') setAbs(currentAbsTop())                     // release: scroll down reveals bottom immediately
        else if (parentRect.top > maxTop()) setAbs(0)                   // container start: park at column top
      } else {
        if (dir === 'down' && rect.top <= minTop(h) && parentRect.bottom >= window.innerHeight - 8) setFixed('fixedBottom', minTop(h))
        else if (dir === 'up' && rect.top >= maxTop() && parentRect.top <= maxTop()) setFixed('fixedTop', maxTop())
      }
    }
    const reset = () => {
      if (!isLg()) {
        mode = null
        inner.style.position = ''; inner.style.top = ''; inner.style.left = ''; inner.style.width = ''
        return
      }
      // Re-anchor in place (handles resize + panel-height changes), next scroll re-pins.
      setAbs(mode ? currentAbsTop() : 0)
    }
    reset()
    const ro = new ResizeObserver(reset)
    ro.observe(inner)
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', reset)
    return () => {
      ro.disconnect()
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', reset)
    }
  }, [])
  return (
    <aside ref={asideRef} className={className}>
      <div ref={innerRef}>{children}</div>
    </aside>
  )
}

// The left search-tools sidebar (restructured 2026-07-06 from the old full-width drawer):
// one vertical column — Deal, Type, Price, Area, Rooms/Bedrooms/Floor. City/District moved UP
// to the command strip (they're primary geography, not fine-tuning), so this panel is purely
// the numeric/type refinement tools. Same filter state as before — only the layout changed.
// ─── eBay-style facet groups (2026-07-17, user-directed restyle) ──────────────
// One collapsible group per facet; open/closed persists per group in localStorage so the
// sidebar keeps each agent's own working shape. Collapse state is UI memory, not filter
// state — a closed group's filters stay ACTIVE (the constraint-chip row is the single
// truthful display of what's filtering; a badge on the closed header echoes it here).
const FACETS_OPEN_KEY = 'desk_facets_open'
const readFacetsOpen = () => { try { return JSON.parse(localStorage.getItem(FACETS_OPEN_KEY)) || {} } catch { return {} } }
function FacetGroup({ id, title, defaultOpen = true, badge, children }) {
  const [open, setOpen] = useState(() => { const m = readFacetsOpen(); return typeof m[id] === 'boolean' ? m[id] : defaultOpen })
  const toggle = () => setOpen(o => {
    const n = !o
    try { const m = readFacetsOpen(); m[id] = n; localStorage.setItem(FACETS_OPEN_KEY, JSON.stringify(m)) } catch { /* UI memory only */ }
    return n
  })
  return (
    /* Design turn 8b (§8b, items 3 and 4). The complaint was "which one is category, where
       does it start, where does it end?" — and the cause was that all sixteen group headings
       were the SAME visual class as the identity rows above them, so nothing said which
       answer changes what the others mean.
       3. Headings step DOWN — 12px semibold foreground/90 → 11px/16px weight 500 at
          muted-foreground, letter-spacing .02em, the ± at 12px and dimmer. They stop
          competing with the identity card for the same rank.
       4. Each body sits behind an INDENT RAIL — 9px padding-left + a 1px left border — so a
          group's contents visibly belong to its label, which a flat left-aligned column
          never showed.
       ⚠️ TOKENS, NEVER `hsl(220 9% NN%)` LITERALS (owner, 2026-08-13: "this color is not
       good"). §8b and turn 9 shipped fourteen of them across this panel, every one a value
       read off the DARK palette — so in light mode the identity card rendered as a dark grey
       box on a white page and every rail, dash and badge was invisible or wrong. The panel
       has no dark-only guarantee: `--foreground` is 10% light / 95% dark, so a literal can
       only ever be right in one of them. Anything with a fixed lightness here is a bug. */
    <div className="border-t border-border/60 pt-2 first:border-t-0 first:pt-0">
      <button type="button" onClick={toggle}
        className="flex w-full items-center justify-between gap-2 hover:text-foreground"
        style={{ fontSize: '11px', lineHeight: '16px', fontWeight: 500, letterSpacing: '.02em', color: 'hsl(var(--muted-foreground))' }}>
        <span className="truncate">{title}
          {badge ? <span className="ml-1.5 font-normal text-primary">{badge}</span> : null}</span>
        <span className="leading-none shrink-0" style={{ fontSize: '12px', color: 'hsl(var(--muted-foreground) / 0.75)' }}>{open ? '−' : '+'}</span>
      </button>
      {open && (
        <div className="mt-2 space-y-1.5"
          style={{ paddingLeft: '9px', borderLeft: '1px solid hsl(var(--border) / 0.6)' }}>{children}</div>
      )}
    </div>
  )
}
// One checkbox row. `count` renders eBay-style "(n)"; disabled = provably vain (0 reachable
// rows) — but a CHECKED box is never disabled, or the agent couldn't uncheck it.
// B1213 (2026-09-06): `excluded` draws the ✕ state — the same row, the same click, a third answer.
//
// ⚠️ F-050 (owner, 2026-09-05, on the phone): "pressing the box itself doesn't give neutral; pressing the
// name gives the previous state and then neutral". The control used to be an <input type=checkbox> for
// ☐/✓ and a <button> for ✕, swapped by React inside ONE <label>. A label's activation behaviour runs
// as the click's DEFAULT ACTION, after the handlers — by then React had already replaced the element
// the click landed on, so the browser dispatched a second click at the NEW labelled control and the
// row advanced twice (✕ → neutral → ✓ in one press). The fix is structural, not a handler guard: the
// control is ONE element in every state — a <button role="checkbox"> that draws ☐ / ✓ / − / ✕ itself —
// so nothing is ever swapped under a click, and a press on the name or on the box is one activation
// of one element. scripts/render-tri-box.mjs pins it (every state renders the same tag, no <input>).
// F-050b: the row for a TriBox facet (sources, footprint, age kinds, floor flags) — the box cycles, the name deselects.
// Replaces the <label> wrappers those facets used: a label re-targets a click at its control, which is exactly the
// single-affordance the owner ruled out.
function TriRow({ state, onCycle, onName, label, count, nameFirst = false, className = '' }) {
  const name = (
    <button type="button" data-facet-name="" onClick={onName} title={state ? t('desk_facet_name_deselect_hint') : undefined}
      className={`${nameFirst ? 'shrink-0 whitespace-nowrap' : 'flex-1 min-w-0 truncate'} text-left cursor-pointer`}>{label}</button>
  )
  return (
    <div className={`flex items-center gap-2 text-muted-foreground hover:text-foreground py-0.5 ${className}`} data-facet-row="">
      {nameFirst && name}
      <TriBox state={state} onCycle={onCycle} />
      {!nameFirst && name}
      {count != null && count > 0 && <span className="text-xs text-muted-foreground shrink-0">({count})</span>}
    </div>
  )
}
// B1213: the three-state box for a plain categorical facet — ☐ neutral, ✓ include, ✕ exclude. The cycle
// itself lives in src/lib/facetTriState.js. Same one-element rule as FacetCheck (F-050): its callers wrap
// it in a <label> with the name, and a label activates its labelled control exactly once only if that
// control is never swapped.
function TriBox({ state, onCycle }) {
  return (
    <button type="button" role="checkbox" aria-checked={state === 'include' ? 'true' : 'false'} data-state={state || 'neutral'}
      onClick={onCycle} className="shrink-0 flex items-center"
      title={state === 'exclude' ? t('desk_facet_exclude_hint') : state === 'include' ? t('desk_facet_exclude_next') : undefined}>
      <TriGlyph state={state} />
    </button>
  )
}
// District facet: checkbox list ordered by confirmed count (most stock first — the eBay
// "top facets" rule), truncated with a "see all" toggle. Counts + gray-out are the same
// 0056 data the old add-select showed; selected codes stay visible even outside the top-N.
// The flat ordering trades the select's raion/ubani optgroup hierarchy for scanability —
// the search box + completion hints remain the fast path for a specific known district.
const DISTRICT_FACET_TOP_N = 8
// Agent facet body (2026-07-28). Deliberately NOT AgentMultiSelect: that is a toolbar PILL
// that opens its own dropdown, and nesting a dropdown inside an accordion facet made one
// choice cost two disclosures. Every other facet here puts its options inline as FacetCheck
// rows, so this does too — one idiom, one interaction.
//
// ACTIVE BY DEFAULT, inactive behind an explicit option (owner 2026-07-28: "in general
// search is performed for active agents and no need to load inactive always but if needed
// there must be option"). The roster carries ~76 people and only ~20 are current, so
// resigned agents are not just noise — they push the person you actually want off the first
// screen. They stay one click away, never gone: their past work is exactly why the panel
// gets allAgents rather than the role-filtered roster.
//
// SEARCH SPANS BOTH REGARDLESS. Typing a name is an explicit request for that person, and
// answering "no agents match" because they resigned would be a lie the UI cannot explain.
// Matches from the hidden set render with a muted "·" marker instead.
//
// Matching uses normalizeText — the SAME normalizer the rest of Desk search uses (0106/0107
// Georgian↔Latin folding), so "gvantsa" finds გვანცა and vice versa. A plain lowercase
// includes() was the first cut and would have failed every cross-script search in a bilingual
// roster. UNNORMALIZABLE input (digits, symbols) falls back to raw contains rather than
// matching nothing.
// One district row, memoised on PRIMITIVES only. This is what makes expanding a raion cheap:
// without it, toggling one disclosure re-rendered every row in the rail — 235 of them with
// "see all" on — because the parent re-renders and every child element is rebuilt. With it,
// React skips any row whose own props are unchanged, so an expand costs the toggled row and
// its children and nothing else.
// ⚠️ Props must stay primitive. Passing the district object, the `selected` array, or an
// inline arrow would defeat the comparison and silently restore the old cost — the memo would
// still be there, doing nothing, which is worse than not having it at all.
const DistrictRow = memo(function DistrictRow({ code, text, count, checked, disabled, depth, hasKids, open, onToggle, onName, onDisclose, ownBucket = false, indeterminate = false, excluded = false, inherited = false }) {
  return (
    <div className="flex items-center gap-1" style={{ paddingLeft: depth ? 14 : 0 }}>
      {/* The disclosure is its own control, never the checkbox: opening a raion to look
          inside is not the same act as searching the whole raion, and one click doing both
          is how an agent ends up with a filter they did not ask for. */}
      {hasKids ? (
        <button type="button" aria-expanded={open} aria-label={text}
          className="w-3 shrink-0 text-[10px] leading-none text-muted-foreground hover:text-foreground"
          onClick={() => onDisclose(code)}>{open ? '▾' : '▸'}</button>
      ) : <span className="w-3 shrink-0" />}
      <div className={`min-w-0 flex-1 ${ownBucket ? 'opacity-80' : ''}`}>
        {/* The own bucket sits UNDER its raion among that raion's children and carries the same
            name, so on its own it reads as a duplicate. The title says which of the two it is;
            the muting keeps the raion above it the primary target, since picking the whole raion
            is the commoner intent. Same checkbox otherwise — it is a real filter now, not a
            caption. */}
        {/* `shortPlaceLabel` renders; `title` keeps the canonical name (owner, 2026-08-19 —
            *"on hover it still shows full canonical name"*). FacetCheck self-titles a string
            label, so the explicit `title` is what stops it titling the SHORT form back at
            itself and losing the very thing the hover exists for. */}
        <FacetCheck checked={checked} disabled={disabled} onChange={() => onToggle(code)} onName={onName ? () => onName(code) : undefined} excluded={excluded} inherited={inherited}
          title={text} indeterminate={indeterminate}
          label={ownBucket
            ? <span title={t('desk_district_direct_hint')}>{shortPlaceLabel(text)}</span>
            : shortPlaceLabel(text)} count={count} />
      </div>
    </div>
  )
})

// ── CASCADE (owner, 2026-08-22: "if I select parent saburtalo it should already select all
// its child … then if i disselect any of child it must show partially selected saburtalo") ──
// Ticking a raion ALREADY returns everything under it — `districtCheck` in lib/deskFeed.js
// passes a row when any ANCESTOR of its code is selected (B792). So the behaviour was right
// and only the boxes lied: children rendered unticked under a ticked parent.
//
// Two halves, and only the first is cosmetic:
//   1. a node is CHECKED when it is selected or any ancestor is  → tick the children
//   2. unticking one child means "Saburtalo EXCEPT this one", which the filter has no
//      exclusion for. It is expressed with the pieces that already exist (0363/0372):
//      drop the parent, add the parent's OWN bucket (rows coded to the raion itself, which
//      would otherwise be lost), and add every sibling along the path down to the unticked
//      node. The result is an equivalent union, so the RPC needs no change.
// The parent then has some-but-not-all descendants selected → indeterminate.
// ── B1216 (owner, 2026-09-05): THE TREE UNDER ✓ / ✕ IS INHERITANCE ──────────────────────────────────────
// A child inherits the nearest EXPLICITLY decided ancestor's state; a press on it is an EXCEPTION against that
// ancestor. Under a ✓ parent a child shows inherited ✓ and a press makes it ✕ (press again: back to inherited);
// under a ✕ parent it shows inherited ✕ and a press makes it ✓. Under a neutral parent it cycles
// neutral → ✓ → ✕ → neutral. Every explicit press on a node CLEARS every explicit state beneath it (no memory).
// Neutral never means excluded. The − state is display only: a neutral parent with something decided beneath.
// The own bucket ("Saburtalo under Saburtalo") is a child like any other. The 0370 cascade — unticking a child
// expanded the raion into its siblings — is retired from the click path; its SQL include expansion stays, and
// 0886 gives the search the matching carve-out ("✕ parent + ✓ child"). The pure state machine lives in
// src/lib/districtTree.js so it is tested without a DOM; this component only draws it.

function DistrictFacetList({ geo, selected, selectedOwn = [], excluded = [], excludedOwn = [], onCommit }) {
  // B1216: four explicit arrays (include / exclude, and the own-bucket halves of each) are the WHOLE state; every
  // displayed state derives from them plus the hierarchy (districtTree.js). One press → one commit of all four.
  const excludedSet = useMemo(() => new Set(excluded), [excluded])
  const excludedOwnSet = useMemo(() => new Set(excludedOwn), [excludedOwn])
  const prefer = getLanguage()
  const [showAll, setShowAll] = useState(false)
  const [openRaions, setOpenRaions] = useState(() => new Set())
  const label = d => (prefer === 'ka' ? (d.name_ka || d.name_en) : (d.name_en || d.name_ka)) || d.code
  const graying = geo.hasDistrictCounts
  const exact = c => (graying ? geo.distExactOf.get(c) || 0 : 0)
  const reachable = c => (graying ? geo.distReachableOf.get(c) || 0 : 1)
  // ── THE TREE (owner, 2026-08-02) ────────────────────────────────────────────────────
  // Tbilisi's geography NESTS — a raion holds neighbourhoods — and the search already knows
  // it: picking Saburtalo returns flats in Vedzisi inside it, and the reverse lands on the
  // may-match shelf rather than being dropped. The LIST did not know it. Sorted flat by
  // count, it opened on "11th Microdistrict, 12th Microdistrict…" and buried the names
  // anyone actually searches, while forcing an agent to already know which neighbourhood
  // sits inside which raion.
  //
  // ⚠️ Falls back to the flat list when no row carries `parent_code` — the same guard
  // ManualPropertyModal uses. A tree built over a hierarchy that has not loaded renders
  // every district as a root, which looks identical to the bug being fixed.
  const inCity = useMemo(() => geo.districts.filter(d => districtInCity(d, geo.city, geo.districts)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [geo.districts, geo.city])
  const hierarchical = inCity.some(d => d.parent_code)
  const byCount = (a, b) => (exact(b.code) - exact(a.code)) || label(a).localeCompare(label(b))
  // ⚠️ `selected` arrives as an ARRAY and was `.includes()`-ed once per row and again per
  // child in the visibility test — O(rows × selections) on every expand and every keystroke.
  // A Set turns each of those into a hash lookup. This was the cheaper half of the slowness
  // and the easier half to miss.
  const selectedSet = useMemo(() => new Set(selected), [selected])
  const ownSet = useMemo(() => new Set(selectedOwn), [selectedOwn])
  // The hierarchy is SANITISED ONCE here rather than guarded per node at render time.
  // `district_boundary` is a six-level gazetteer built from imported OSM data
  // (region→municipality→raion→quarter→neighbourhood→city_block) and nothing validates
  // `parent_code` on the way in, so a cycle — or a row that is its own parent — would make
  // rendering infinite. Walking each node's ancestry once here costs O(n); the previous guard
  // allocated a Set per node per paint.
  const { childrenOf, roots } = useMemo(() => {
    const kids = new Map()
    if (!hierarchical) return { childrenOf: kids, roots: inCity.slice().sort(byCount) }
    const byCode = new Map(inCity.map(d => [d.code, d]))
    const rootList = []
    for (const d of inCity) {
      // Climb to the top refusing to loop. A node whose chain revisits itself is treated as a
      // root: visible and searchable, just not nested — dropping a district from the list
      // would be a worse failure than dropping its indentation.
      let p = d.parent_code, seen = new Set([d.code]), ok = true
      while (p) {
        if (seen.has(p) || !byCode.has(p)) { ok = false; break }
        seen.add(p); p = byCode.get(p).parent_code
      }
      if (d.parent_code && ok) {
        if (!kids.has(d.parent_code)) kids.set(d.parent_code, [])
        kids.get(d.parent_code).push(d)
      } else rootList.push(d)
    }
    for (const list of kids.values()) list.sort(byCount)
    return { childrenOf: kids, roots: rootList.sort(byCount) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inCity, hierarchical, geo.distExactOf, prefer])
  // A parent's own number counts the WHOLE raion, children included — that is what the filter
  // delivers when you tick it (Rule 1), and it is why a child is never summed into it twice.
  const visible = useMemo(() => {
    if (showAll) return roots
    const anyChildSelected = code => (childrenOf.get(code) || []).some(c => selectedSet.has(c.code))
    return roots.filter((d, i) => i < DISTRICT_FACET_TOP_N || selectedSet.has(d.code) || anyChildSelected(d.code))
  }, [showAll, roots, childrenOf, selectedSet])
  // ⚠️ `onToggle` arrives as a FRESH ARROW on every parent render
  // (`onToggle={code => toggleIn('districtCodes', code)}`), which would change on every row
  // every time and defeat the memo entirely — leaving the optimisation visible in the code
  // and absent in the profile. Ref-and-stable-wrapper is the pattern this file already uses
  // for the feed's memoised cards: the identity never changes, the ref always holds the
  // current closure, so no call is ever made against a stale one.
  // Cascade helpers. `parentOf` is built from the SANITISED tree (childrenOf), never from raw
  // parent_code, so a cycle the tree build already rejected cannot reappear here.
  const parentOf = useMemo(() => {
    const m = new Map()
    for (const [parent, kids] of childrenOf) for (const k of kids) m.set(k.code, parent)
    return m
  }, [childrenOf])
  const descendantsOf = useCallback(code => {
    const out = []
    const stack = [code]
    const seen = new Set([code])
    while (stack.length) {
      for (const k of (childrenOf.get(stack.pop()) || [])) {
        if (seen.has(k.code)) continue
        seen.add(k.code); out.push(k.code); stack.push(k.code)
      }
    }
    return out
  }, [childrenOf])
  const treeState = useMemo(() => ({ include: selectedSet, exclude: excludedSet, own: ownSet, ownNot: excludedOwnSet }), [selectedSet, excludedSet, ownSet, excludedOwnSet])
  const shown = useCallback(code => districtShown(treeState, code, parentOf, descendantsOf), [treeState, parentOf, descendantsOf])
  const shownOwn = useCallback(code => districtShownOwn(treeState, code, parentOf), [treeState, parentOf])
  const isChecked = useCallback(code => shown(code).state === 'include', [shown])
  const isPartial = useCallback(code => shown(code).state === 'mixed', [shown])

  // One press → one commit of all four arrays (two sequential setFilters would render an intermediate state).
  // Refs keep the handlers stable so DistrictRow's memo holds.
  const pressRef = useRef(null)
  pressRef.current = { treeState, parentOf, descendantsOf, onCommit }
  const toggleStable = useCallback(code => { const p = pressRef.current; p.onCommit(districtPress(p.treeState, code, p.parentOf, p.descendantsOf)) }, [])
  const toggleOwnStable = useCallback(code => { const p = pressRef.current; p.onCommit(districtPressOwn(p.treeState, code, p.parentOf)) }, [])
  // F-050b: the NAME is select/deselect (districtTree.js districtNamePress) — same commit path, same stability.
  const nameStable = useCallback(code => { const p = pressRef.current; p.onCommit(districtNamePress(p.treeState, code, p.parentOf, p.descendantsOf)) }, [])
  const nameOwnStable = useCallback(code => { const p = pressRef.current; p.onCommit(districtNamePressOwn(p.treeState, code, p.parentOf)) }, [])
  // Stable identity, or the memo above is defeated by a fresh arrow on every render.
  const disclose = useCallback(code => setOpenRaions(prev => {
    const next = new Set(prev)
    if (next.has(code)) next.delete(code); else next.add(code)
    return next
  }), [])
  const renderRow = (d, depth) => {
    const kids = childrenOf.get(d.code)
    const hasKids = !!(kids && kids.length)
    // A raion auto-opens when one of its children is selected, so a restored search can never
    // hide its own state.
    // ownSet: a SELECTED own-bucket lives inside this group, so if the group is shut the
    // agent's own active filter is invisible and cannot be unticked from the tree.
    const open = hasKids && (openRaions.has(d.code) || kids.some(k => selectedSet.has(k.code) || excludedSet.has(k.code)) || ownSet.has(d.code) || excludedOwnSet.has(d.code))
    return (
      <div key={d.code}>
        <DistrictRow code={d.code} text={label(d)} count={graying ? exact(d.code) : null}
          checked={isChecked(d.code)} indeterminate={isPartial(d.code)} excluded={shown(d.code).state === 'exclude'} inherited={shown(d.code).inherited}
          disabled={reachable(d.code) === 0}
          depth={depth} hasKids={hasKids} open={open}
          onToggle={toggleStable} onName={nameStable} onDisclose={disclose} />
        {open && (
          <div className="ml-1.5 border-l border-border/50">
            {kids.map(k => renderRow(k, 1))}
            {/* ── THE PARENT UNDER ITSELF (owner, 2026-08-02: "Didube says 1149, its only sub
                   says 605"; "like Saburtalo and Vake we can have Saburtalo under Saburtalo").
                A raion's number ALREADY contains its neighbourhoods — `districtReach` computes
                `exactOf = own + children's own` — so Didube's 1,149 includes Dighmis Masivi's
                605. Nothing was being lost. But with only the child shown, the arithmetic was
                invisible and the parent looked like it had mislaid 544 listings.
                This row IS that remainder: the listings coded to the raion itself, with no
                sub-district. Now the children add up to their parent in front of the agent.
                ✅ NOW CLICKABLE (2026-08-02), and the history matters. The old text here said it
                must never be, because ticking it would mean "coded exactly here, excluding
                sub-districts" and the filter could not express that — `district_codes` on a raion
                deliberately returns its children (0055). 0363 made that false by adding
                `p_district_own_codes`; the row still stayed inert for a day, because it had no
                honest number to promise: after 0370 the count it printed was the EXPANDED total,
                i.e. the whole raion, not the remainder. 0372 gave it its own number, verified
                equal to what ticking it delivers (Saburtalo own 4,943 = own-click 4,943).
                It now filters as it reads, which is Rule 1 for this row. */}
            {/* ⚠️ THE ROW'S EXISTENCE MUST NOT DEPEND ON ITS COUNT ARRIVING. It used to be
                `(distOwnOf[code] || 0) > 0`, and district_own_counts is null under any active
                district filter — so ticking Vake made the "Vake under Vake" row vanish, taking
                a filter the agent was mid-way through using with it. Three cases now:
                  counts known + positive → render with the number
                  counts unknown (null)   → render with NO number (never 0 — same rule as the
                                            corpus total: an unarrived number is not a zero)
                  currently selected      → always render, or the only way to untick it is gone
                Counts known and zero is still the one case that hides the row, which is the
                only case where hiding it is true. */}
            {(() => {
              const ownKnown = !!geo.distOwnOf
              const ownN = ownKnown ? (geo.distOwnOf[d.code] || 0) : null
              if (!(ownN === null || ownN > 0 || ownSet.has(d.code) || excludedOwnSet.has(d.code))) return null
              const so = shownOwn(d.code)
              return (
                <DistrictRow code={d.code} text={label(d)}
                  count={graying && ownN ? ownN : null}
                  checked={so.state === 'include'} excluded={so.state === 'exclude'} inherited={so.inherited} depth={1} hasKids={false} open={false}
                  onToggle={toggleOwnStable} onName={nameOwnStable} onDisclose={disclose} ownBucket />
              )
            })()}
          </div>
        )}
      </div>
    )
  }
  return (
    <div className="space-y-1.5">
      {/* content-visibility lets the browser skip layout and paint for rows scrolled out of
          the box — "see all" is 235 rows and only about a dozen are ever on screen. */}
      {/* ⚠️ The height cap applies in BOTH states (owner-reported 2026-08-18): with the cap
          only on showAll, an opened raion's subtree made the compact state LOOK fully
          extended under a "see all (79)" label, and expanding then SHRANK the list into a
          scrollbox under "show less" — the labels read as inverted while both were true.
          A stable container makes the button change the content, never the shape. */}
      <div className="max-h-72 overflow-y-auto space-y-1.5 pr-1 [content-visibility:auto]">
        {visible.map(d => renderRow(d, 0))}
      </div>
      {roots.length > DISTRICT_FACET_TOP_N && (
        <button type="button" className="text-xs text-primary hover:underline"
          onClick={() => setShowAll(v => !v)}>
          {showAll ? t('desk_facet_show_less') : tLog('desk_facet_see_all', { n: roots.length })}
        </button>
      )}
    </div>
  )
}

// ── The street facet (0537/0538/0539) ────────────────────────────────────────────────
// A street used to be a ~700m circle around a single point, because `property` had no
// street link and `street.geo` is ST_Point on every row. 0537 stores the link, so a street
// is now a selection like a district code, and this is its control.
//
// ⚠️ IT APPEARS ONLY UNDER A CHOSEN DISTRICT, and that is a decision, not a limitation.
// Tbilisi alone holds 2,256 streets and names repeat across raions — "26 მაისის ქუჩა"
// exists in Gldani-Nadzaladevi, Sapichkhia, Batumi, Telavi and Kobuleti — so an unscoped
// list would ask the agent to disambiguate something the district has already answered.
// The RPC applies the same gate to `street_counts`, so the two can never disagree about
// when this surface has an answer.
//
// ⚠️ THE NUMBERS AND THE NAMES HAVE DIFFERENT LIFETIMES, and conflating them was the trap
// here. `street_counts` is null the moment a street IS selected (the RPC declines to count
// a facet the query has already narrowed), which would leave the selected row unlabelled —
// `street` lives in core and the browser cannot read it. So labels are remembered by the
// caller and passed in, while counts are only ever the CURRENT answer. A row whose number
// has not arrived renders with no number, never with 0 and never with a stale one — the
// same rule the corpus total follows.
// Exported for render-verification only — nothing in the app imports it. The 2026-08-19
// self-erasing-search bug was diagnosed by mounting THIS component in a scratch harness and
// typing into it, which is the only way to catch a defect that needs three keystrokes to
// appear. Keeping the export means the next person can do the same in one step.
// 0688/v48 — the metro facet. Deliberately NOT StreetFacetList: that one carries a catalogue,
// a search box, a "see more" ladder and a coverage sentence because there are 3,390 streets.
// There are 22 metro stations, so the whole list IS the answer and none of that machinery
// earns its place.
//
// The number beside a station is how many properties' ADS NAME it (property_metro_v1) — which
// is exactly what clicking delivers as the matched set, the standing rule for a facet count.
// It is NOT "properties within N metres": nothing has measured that, and a count you cannot
// reproduce by clicking is the disease B273 exists to prevent.
//
// ⚠️ AND FOR A YEAR IT WAS THAT DISEASE (B914, fixed 0694). The catalogue's `property_count` is
// CORPUS-WIDE. This component took `selected` and `onToggle` and nothing else, so the numbers
// never moved when a district was picked — and the list is SORTED by them. Measured with
// საბურთალო selected: ახმეტელის თეატრი showed 452 and delivered 1, ვარკეთილი 361 → 4,
// ისანი 232 → 2, while the three stations that ARE in Saburtalo sorted below them. The comment
// above was true only of an otherwise-unfiltered search, in a panel whose entire purpose is
// other filters.
//
// `counts` is now the scoped answer from the search RPC (`search_meta.metro_counts`, keyed by
// poi id) — the same shape and the same rule the street facet has always followed:
//   counts known    → render the scoped number
//   counts null/absent → render NO number, never the stale global one and never 0
// ⚠️ Do NOT fall back to `property_count` when `counts` is missing. An unscoped number under a
// filtered search is the exact defect this replaced; absence is the honest answer.
//
// Fetched once per page load and cached in module scope — 22 rows is smaller than the request
// that asks for it, so re-fetching per accordion open would be pure waste. The CATALOGUE is
// still global (the names and the id set do not depend on a filter); only the numbers are scoped.
let _metroStationsCache = null
export function MetroFacetList({ selected, onToggle, counts = null }) {
  const [stations, setStations] = useState(_metroStationsCache)
  useEffect(() => {
    if (_metroStationsCache) return
    let gone = false
    getMetroStations()
      .then(rows => { _metroStationsCache = rows; if (!gone) setStations(rows) })
      .catch(() => { if (!gone) setStations([]) })
    return () => { gone = true }
  }, [])
  const sel = new Set(selected || [])
  if (stations == null) return <div className="text-xs text-muted-foreground py-1">…</div>
  // B914: the number for THIS search, or null when the RPC declined to count (a station is
  // already selected, or this is page 2). `counts` is keyed by poi id as text.
  const scoped = s => (counts && counts[s.id] != null ? Number(counts[s.id]) : null)
  const haveCounts = !!counts
  // Busiest first, but a SELECTED station never sorts away from where the agent can see it.
  // ⚠️ Sort on the SCOPED number when it exists. Sorting by the corpus-wide one is half the
  // defect: it put five stations with 1–4 matching properties above the three with 188–264.
  // Without counts the order is alphabetical, because there is no honest "busiest" to offer.
  const rows = [...stations].sort((a, b) =>
    (sel.has(b.id) - sel.has(a.id)) ||
    (haveCounts
      ? ((scoped(b) ?? -1) - (scoped(a) ?? -1))
      : String(a.name_ka || a.name_en || '').localeCompare(String(b.name_ka || b.name_en || ''))))
  return (
    <div className="flex flex-col gap-0.5">
      {rows.map(s => {
        const on = sel.has(s.id)
        return (
          <button key={s.id} type="button" onClick={() => onToggle(s.id)}
            className={`flex items-center justify-between gap-2 text-left text-xs rounded px-1.5 py-1 ${
              on ? 'bg-primary/10 text-foreground font-medium' : 'hover:bg-accent/50 text-muted-foreground'}`}>
            <span className="min-w-0 truncate">{on ? '✓ ' : ''}{getLanguage() === 'ka' ? (s.name_ka || s.name_en) : (s.name_en || s.name_ka)}</span>
            {/* 0 is a real answer here (nothing in THIS search names the station) and is shown
                as such — a dash would read as "not counted", a different statement. But an
                UNARRIVED number renders as nothing at all, never as 0 and never as the stale
                corpus-wide figure: same rule the street facet and the corpus total follow. */}
            <span className="tabular-nums shrink-0 text-muted-foreground/70">
              {haveCounts ? (scoped(s) ?? 0) : ''}
            </span>
          </button>
        )
      })}
    </div>
  )
}
export function StreetFacetList({ counts, labels, catalog, selected, onToggle, districtsPicked, coverage, districtName, nearbyM, onNearbyM }) {
  const [q, setQ] = useState('')
  const TOP = 8
  const STEP = 20          // one "see more" — a screenful, not a jump to 443
  const [limit, setLimit] = useState(TOP)
  const { rows, total } = useMemo(() => {
    const out = new Map()
    // Selected streets come first and unconditionally: an agent must always be able to see
    // and undo what they picked, even when this query returned no counts at all.
    for (const id of (selected || [])) out.set(id, { id, label: labels?.[id] || id, n: null, on: true })
    // 0567 CATALOGUE: the district's street LIST, from its own 53ms query instead of the
    // ~2,000ms search RPC. Seeded FIRST and always with `n: null` — a catalogue is scoped by
    // district only, so it cannot answer "how many match your search" and must not appear to.
    // `null` is already this list's word for "not counted for this query" (it renders the
    // dash), so the fast path fills the NAMES and the search's own counts overwrite them
    // below with the real numbers. Order is preserved from the server (largest first).
    for (const c of (catalog || [])) {
      if (!c || !c.id || out.has(c.id)) continue
      out.set(c.id, { id: c.id, label: c.label || labels?.[c.id] || c.id, n: null, on: false })
    }
    for (const [id, v] of Object.entries(counts || {})) {
      const n = v && typeof v === 'object' ? v.n : v
      const label = (v && typeof v === 'object' && v.label) || labels?.[id] || id
      if (out.has(id)) out.set(id, { ...out.get(id), label, n })
      else out.set(id, { id, label, n, on: false })
    }
    // Q7 grain: read off the name, because that is where the registry states it — these are
    // the words the gazetteer itself uses. A row with no such word is an ordinary street and
    // carries no tag, which is the common case and must stay unmarked.
    const GRAIN = [['მიკრორაიონი', 'მიკრორაიონი'], ['კვარტალი', 'კვარტალი'], ['დასახლება', 'დასახლება'], ['მასივი', 'მასივი']]
    for (const r of out.values()) {
      const hit = GRAIN.find(([needle]) => String(r.label).includes(needle))
      r.grain = hit ? hit[1] : null
    }
    const list = [...out.values()]
    const needle = q.trim().toLowerCase()
    const hit = needle ? list.filter(r => r.on || String(r.label).toLowerCase().includes(needle)) : list
    // Selected on top, then by volume — the same ordering the district list uses, and for
    // the same reason: an agent scans for the big ones and needs their own picks in reach.
    hit.sort((a, b) => (b.on - a.on) || ((b.n ?? -1) - (a.n ?? -1)) || String(a.label).localeCompare(String(b.label)))
    // `total` is the UNFILTERED size and it is returned alongside, because the search box's
    // own visibility depends on it — see the input below. Derived here rather than
    // recomputed at the call site: two counts of "how many streets are there" would be one
    // refactor away from disagreeing, and the disagreement is invisible.
    return { rows: hit, total: list.length }
  }, [counts, labels, catalog, selected, q])

  if (!districtsPicked) {
    return <p className="text-[11px] text-muted-foreground leading-snug">{t('desk_street_pick_district_first')}</p>
  }
  // 🚨 "see all" used to be a ONE-WAY DOOR from 8 rows to every row, and on a real district
  // pair that is 443 checkboxes — a list the owner described as running forever, with no way
  // back short of collapsing the whole facet. Three controls now, because the agent is doing
  // three different things: STEP (+20, "show me a bit more"), ALL (they know the street is
  // far down and would rather scroll than page), and COMPACT (they are done looking and want
  // the panel navigable again). `limit` is a count rather than a boolean so the first two
  // share one mechanism instead of being two states that can disagree.
  const shown = rows.slice(0, limit)
  const more = rows.length - shown.length
  return (
    <div className="space-y-1.5">
      {/* 🚨 THE BOX USED TO DELETE ITSELF WHILE YOU WERE TYPING IN IT (owner, 2026-08-19:
          *"i wrote a text in street search and search box gone and results are shown"*).
          The gate read `rows.length > TOP` — and `rows` is the FILTERED list. So the search
          input rendered only while its own query still matched more than 8 streets: type
          enough to narrow to 8 and the field UNMOUNTED mid-keystroke, taking the caret and
          the visible query with it.
          What made it a trap rather than a glitch is that `q` is state and SURVIVED the
          unmount. The list stayed filtered by a needle the agent could no longer see or
          edit, with no control anywhere to clear it — the only way back to the full street
          list was to collapse and reopen the whole facet. Same disease as the "see all"
          one-way door this component already fixed once, and the same shape as the fold-latch
          in PillOverflow: a control that measures the consequence of its own decision.
          `total` is the UNFILTERED count, so the box's existence no longer depends on what
          the box currently contains. `|| q` keeps it once typing has begun, so a query that
          narrows a short list to nothing still has a field to clear it in. */}
      {(total > TOP || q) && (
        <div className="relative">
          <input className={`${inputCompact} ${q ? 'pr-7' : ''}`} value={q} onChange={e => setQ(e.target.value)}
            placeholder={t('desk_street_search_ph')} />
          {/* The way back, on the control that caused it. Only while there is something to
              clear — an always-visible ✕ over an empty field is a button that does nothing. */}
          {q && (
            <button type="button" aria-label={t('desk_search_tools_clear')} title={t('desk_search_tools_clear')}
              className="absolute right-1 top-1/2 -translate-y-1/2 px-1.5 text-xs leading-none text-muted-foreground hover:text-foreground"
              onClick={() => setQ('')}>✕</button>
          )}
        </div>
      )}
      {/* catalog === null means the 0567 fetch is still IN FLIGHT — say nothing rather than
          assert "none recorded" over a district that has plenty (2026-08-16 audit: Vake's 87
          streets rendered as this copy for the duration of every load). The copy is reserved
          for a LOADED empty answer. */}
      {/* ⚠️ TWO DIFFERENT EMPTIES, and only one of them is about the district. With a query
          typed, "no street is recorded for this district yet" is a claim about the CORPUS
          that an agent ACTS on — they go and capture a street that is already in the
          registry. `total` tells the two apart: rows empty while total > 0 means the SEARCH
          found nothing, not that the district is bare. */}
      {/* catalog === false: the street list did not load (2026-10-23 audit). Not "none recorded":
          nothing was learned about the district, so it gets its own sentence. */}
      {!rows.length && catalog !== null && (
        <p className="text-[11px] text-muted-foreground">
          {t(q && total > 0 ? 'desk_street_no_match' : catalog === false ? 'desk_street_catalog_failed' : 'desk_street_none_here')}
        </p>
      )}
      {/* Same capped scrollbox as the district facet, for the same reason and on the same
          owner request (2026-08-18: "let streets also become scrollable — it's an even
          bigger list"): "see all" here is up to 443 rows, and without a cap they run the
          panel off the screen. The cap applies in BOTH states — the stable-container rule
          from the district fix, where capping only the expanded state made the see-all /
          show-less labels read as inverted. Buttons and search stay OUTSIDE the box so the
          exits never scroll out of reach. */}
      <div className="max-h-72 overflow-y-auto space-y-1.5 pr-1 [content-visibility:auto]">
      {shown.map(r => {
        // Q2 (design turn 9): once ANY street is picked the RPC stops counting the facet, so
        // every other row dims and its count becomes a 9×1px DASH holding the number's slot —
        // nothing reflows, and "not applicable now" reads differently from "none". The
        // sentence below stays: a dash alone does not say the numbers come back.
        const anyPicked = (selected || []).length > 0
        const dim = anyPicked && !r.on
        return (
          <label key={r.id} className="flex items-center gap-2 text-xs cursor-pointer rounded px-1 -mx-1"
            style={{ background: r.on ? 'hsl(var(--foreground) / 0.12)' : undefined,
                     color: dim ? 'hsl(var(--muted-foreground) / 0.85)' : undefined }}>
            <input type="checkbox" checked={r.on} onChange={() => onToggle(r.id)}
              style={dim ? { borderColor: 'hsl(var(--border))' } : undefined} />
            {/* SHORT ON THE ROW, CANONICAL ON HOVER (owner, 2026-08-19). `title` was already
                the full label from the 2026-08-18 hover fix and stays exactly that — only
                the rendered text changes. See shortPlaceLabel. */}
            <span className="flex-1 truncate" title={r.label}>{shortPlaceLabel(r.label)}</span>
            {/* Q7: the registry holds microdistricts and quarters beside streets. Mark the
                GRAIN, do not split the list — an agent looking for an address does not know
                which kind it is, and a separate section would make them guess first.
                ⚠️ Suppressed once the label itself carries the abbreviation: "ვაჟას I კვ."
                followed by a `კვარტალი` badge says the same word twice in 40px. The badge
                still fires for the grains the shortener leaves alone (მასივი / დასახლება on
                a name with no numbered unit), which is where it still earns its space. */}
            {r.grain && shortPlaceLabel(r.label) === r.label && (
              <span className="shrink-0 rounded border px-1"
                style={{ fontSize: '10px', lineHeight: '14px', borderColor: 'hsl(var(--border))', color: 'hsl(var(--muted-foreground))' }}>
                {r.grain}
              </span>
            )}
            {/* No number is the honest rendering of "not counted for this query" — a 0 here
                would claim the street is empty, which is the disease B273 was about. */}
            {r.n != null
              ? <span className="text-muted-foreground tabular-nums">({r.n})</span>
              : anyPicked
                ? <span aria-hidden="true" className="shrink-0"
                    style={{ display: 'inline-block', width: '9px', height: '1px', background: 'hsl(var(--border))' }} />
                : null}
          </label>
        )
      })}
      </div>
      {/* tLog, not t — t() takes no vars and would print a literal "{n}". Same family as
          the vocabulary keys that rendered raw on 15 surfaces (MEETING_NUDGES_REVIEW). */}
      {(more > 0 || limit > TOP) && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pt-0.5">
          {more > 0 && (
            <button type="button" className="text-[11px] text-primary underline"
              onClick={() => setLimit(l => l + STEP)}>
              {tLog('desk_street_see_more', { n: Math.min(STEP, more) })}
            </button>
          )}
          {/* "all" only when a step would not already finish the list — offering both when
              they do the same thing is two buttons for one outcome. */}
          {more > STEP && (
            <button type="button" className="text-[11px] text-primary underline"
              onClick={() => setLimit(rows.length)}>{tLog('desk_street_see_all', { n: rows.length })}</button>
          )}
          {/* The way BACK. Its absence is the actual complaint: 443 checkboxes with no exit. */}
          {limit > TOP && (
            <button type="button" className="text-[11px] text-muted-foreground underline"
              onClick={() => setLimit(TOP)}>{t('desk_facet_show_less')}</button>
          )}
        </div>
      )}
      {(selected || []).length > 0 && !counts && (
        <p className="text-[11px] text-muted-foreground">{t('desk_street_counts_pending')}</p>
      )}
      {/* Q5 (designer turn 9): coverage, ONCE, at the foot, above a rule — not a per-row
          badge, and scoped to THIS district rather than the corpus-wide 26.6%. A district
          figure is what tells the agent whether this particular search is trustworthy;
          "26.6% of everything" tells them nothing about Vake. Both numbers come from the
          same `counted` CTE the results come from, so the facet cannot drift from the feed.
          Absent (null) when the RPC declined to compute it — then we say nothing at all,
          because a coverage claim with no number behind it is worse than silence. */}
      {coverage && coverage.total > 0 && districtName && (
        <p className="text-[11px] text-muted-foreground leading-snug pt-1.5 border-t border-border/60">
          {tLog('desk_street_coverage', {
            district: districtName || '',
            total: coverage.total.toLocaleString(),
            n: (coverage.with_street || 0).toLocaleString(),
          })}
        </p>
      )}
      <p className="text-[11px] text-muted-foreground leading-snug">{t('desk_street_shelf_note')}</p>
      {/* Q4 opt-in. Appears ONLY with a street picked, because the radius is measured from
          the picked street's centre — with none, the control would be a switch wired to
          nothing. OFF by default and staying that way: this is the ~700m centroid proximity
          that answered "Politkovskaya" with Kavtaradze, allowed back only as an explicit
          question with its own answer group. Making it default-on would restore the bug for
          everyone who never asked. */}
      {(selected || []).length > 0 && (
        <label className="flex items-start gap-2 text-[11px] text-muted-foreground leading-snug pt-1.5 border-t border-border/60 cursor-pointer">
          <input type="checkbox" className="mt-0.5" checked={!!nearbyM}
            onChange={e => onNearbyM?.(e.target.checked ? NEARBY_DEFAULT_M : 0)} />
          <span>{tLog('desk_street_nearby_optin', { m: NEARBY_DEFAULT_M })}</span>
        </label>
      )}
    </div>
  )
}
// The one radius the opt-in offers. A single value, not a slider: every extra metre widens
// a CENTROID circle, and the failure it reproduces is gradual rather than obvious — 400m
// keeps it to "the same block or the next one". The server caps anything sent at 1000m.
const NEARBY_DEFAULT_M = 400

// Static eval (same contract as IntakeTab's isMobile): a phone doesn't become a desktop
// mid-session, and a static const keeps the two-presentation split below hook-safe.
const PANEL_IS_MOBILE = typeof window !== 'undefined' && !window.matchMedia('(min-width: 768px)').matches
// Mobile pill + bottom-sheet select (pill slider, 2026-07-18): the eBay per-control
// pattern — a uniform slider pill opens a bottom sheet of single-choice rows; tapping
// an option applies it and closes (no confirm step: these are one-tap toggles).
function PillSheetSelect({ title, value, onChange, options, icon, active = false }) {
  const [open, setOpen] = useState(false)
  const [showAdvanced, setShowAdvanced] = useState(false)
  // group / groupHint / hint / altValue / advanced mirror SingleSelect's (AgentsTab.jsx) —
  // the two surfaces share ONE option list (deskSortOptions), so they must honour the same
  // fields or a sort behaves differently depending on the screen you opened it from.
  const cur = options.find(o => o.value === value || o.altValue === value)
  const curIsAdvanced = !!cur?.advanced
  const hasAdvanced = options.some(o => o.advanced)
  const visible = options.filter(o => !o.advanced || showAdvanced || curIsAdvanced)
  return (<>
    <button type="button" className={mobilePill(active)} onClick={() => setOpen(true)}>
      {icon}{cur?.pillLabel ?? cur?.label}{cur?.dirShort
        ? ` · ${cur.altValue === value ? cur.dirShort.asc : cur.dirShort.desc}` : ''} ▾
    </button>
    {open && (
      <div className="fixed inset-0 z-[60]" onClick={() => setOpen(false)}>
        <div className="absolute inset-0 bg-black/50" />
        <div className="absolute inset-x-0 bottom-0 rounded-t-2xl bg-background border-t border-border max-h-[70vh] flex flex-col"
          onClick={e => e.stopPropagation()}>
          <div className="flex items-center px-4 pt-3 pb-2 shrink-0">
            <span className="flex-1 text-base font-semibold">{title}</span>
            <button className="px-1 text-lg leading-none text-muted-foreground" aria-label={t('desk_search_collapse_tools_tooltip')}
              onClick={() => setOpen(false)}>✕</button>
          </div>
          {/* Fields absent on the period sheet, which renders exactly as before. */}
          <div className="flex-1 overflow-y-auto pb-3">
            {visible.map((o, i) => {
              const on = o.value === value || o.altValue === value
              const flips = on && !!o.altValue
              return (
              <div key={o.value}>
                {o.group && (
                  <div className={`px-4 pb-1 ${i === 0 ? 'pt-1' : 'pt-3 mt-1 border-t border-border/50'}`}>
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{o.group}</div>
                    {o.groupHint && <div className="text-[11px] leading-snug text-muted-foreground/80 mt-0.5">{o.groupHint}</div>}
                  </div>
                )}
                <button type="button"
                  className="w-full text-left px-4 py-2.5 text-sm flex items-start gap-2 hover:bg-accent"
                  onClick={() => {
                    if (flips) { onChange(o.altValue === value ? o.value : o.altValue); return }
                    onChange(o.value); setOpen(false)
                  }}>
                  <span className="w-4 shrink-0 text-primary">{on ? <Icon name="check" /> : ''}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block">{o.label}</span>
                    {flips && o.dir && (
                      <span className="block text-[11px] leading-snug text-primary mt-0.5">
                        {o.altValue === value ? o.dir.asc : o.dir.desc} ⇄ {t('desk_sort_flip_hint')}
                      </span>
                    )}
                    {o.hint && <span className="block text-[11px] leading-snug text-muted-foreground mt-0.5">{o.hint}</span>}
                  </span>
                </button>
              </div>
            )})}
            {hasAdvanced && !curIsAdvanced && (
              <button type="button" onClick={() => setShowAdvanced(s => !s)}
                className="w-full text-left px-4 py-2.5 mt-1 border-t border-border/50 text-xs text-muted-foreground hover:bg-accent">
                {showAdvanced ? t('desk_sort_less') : t('desk_sort_more')} {showAdvanced ? '▲' : '▾'}
              </button>
            )}
          </div>
        </div>
      </div>
    )}
  </>)
}
// statusFilter/onStatusChange: the entries-status dropdown (Active/Closed/Archived) — separate
// state from `filters` (it filters entry GROUPS, not listing facts), moved in from the command
// strip 2026-07-06; the panel's own "clear" resets it along with the fact filters.
// sheetOnly (mobile pill slider, 2026-07-18): a facet id — render ONLY that facet's body
// as an eBay bottom sheet (backdrop + rounded top + its own Show-N button). The strip's
// facet pills mount a second panel instance in this mode; the registry keeps the body
// identical to the full page/sidebar, so a pill can never drift from the ⚙ page.
// ONE portal vocabulary for the Source facet AND saved-search labels — the facet used to
// inline this list, and a second copy in the labelizer would drift the moment a portal is
// added (bog.ge joined after PLATFORM_LABEL froze, which is why that map wasn't reused).
// Identity labels — MODULE level, because the tree (in the panel) and the breadcrumb (in
// the toolbar) both name the same node and must use the same words. Two copies is how
// "Residential" and "Homes" end up on one screen.
const IDENTITY_CAT_LABEL = { residential: 'desk_cat_residential', commercial: 'desk_cat_commercial', land: 'desk_cat_land' }
const IDENTITY_STATUS_LABEL = { cls_new_built: 'desk_status_new', cls_old_built: 'desk_status_old', cls_under_constr: 'desk_status_constr' }
const identityIntentText = v => LISTING_INTENT_OPTIONS.find(o => o.value === v)?.label || v
// ── THE FILTER READOUT — one row, every active filter (design turn 5) ─────────────────
// Replaces the identity LINEAGE that held this slot from 2026-08-02 to 2026-08-06 (owner:
// "we can remove that lineage part and use it to full filter description"). The lineage
// answered a third of the question — identity — while district, price, rooms, features and
// restrictions were only visible inside a panel that can be collapsed. This row answers all
// of it, in one place, with every item removable.
//
// It renders `constraintChips`, which is ALREADY declared to be the single display of the
// whole active filter state — identity chips included. Nothing about what counts as an
// active filter is decided here.
//
// ⚠️ ONE LINE, because of where it sits: on desktop this row is absolutely positioned under
// the search pill and takes ZERO height by design (the toolbar reserves the space it floats
// into — see the savedSearchBar comment, which records two failed attempts at putting it in
// normal flow). A wrapping row would overlay the feed. So: as many as fit, then a `+N` that
// opens the rest as a popover — also absolutely positioned, so it overlays instead of
// shifting anything, and every hidden filter stays one click from being read AND removed.
// A bare "+2" that could only be counted, never inspected, is the invisible-filter state
// this row exists to end.
// Exported for src/harness.jsx ONLY, same rule as AdvancedSearchPanel below: this row's
// risk is geometry (truncation, the popover's wrap, the +N boundary), and only a render can
// see geometry. Nothing in the app imports this name.
// `stranded` (B347) = filters the current identity cannot apply. They render AFTER the active
// chips and are never collapsed behind the `+N`: the whole point is that the agent can see the
// value that stopped applying, and a stranded chip hidden behind a counter is the silent drop
// this row was opened to end. They are also deliberately NOT counted anywhere — see the
// partition in `chipModel`.
export function FilterReadout({ chips, stranded = [], capacity, onRemove }) {
  const [pos, setPos] = useState(null)   // null = closed; otherwise the portal's fixed coords
  const btnRef = useRef(null)
  if (!chips.length && !stranded.length) return null
  // Same guardrail the in-field keywords use: collapse only ever triggers at 2+, because a
  // lone "+1" costs a word and says less than the word it hides.
  const shown = chips.length === 1 ? 1 : Math.max(1, capacity || 3)
  const hidden = chips.slice(shown)
  const chipClass = 'inline-flex items-center gap-1 rounded-full border border-border bg-background px-1.5 py-0.5 max-w-[12rem]'
  const one = c => (
    <span key={c.key} className={chipClass}>
      <span className="truncate">{c.label}</span>
      <button type="button" title={t('desk_search_remove_filter')} aria-label={t('desk_search_remove_filter')}
        className="shrink-0 leading-none text-muted-foreground hover:text-foreground"
        onClick={() => onRemove(c)}>×</button>
    </span>
  )
  // Inactive, and it must READ as inactive without relying on colour alone: struck-through
  // label + muted + a dashed border, so the state survives a greyscale screen and a glance.
  // The × is the ruling's one-tap მოხსნა and is the ONLY control — a stranded chip is not
  // editable, because the facet that would edit it is not being asked on this identity.
  const strandedOne = c => (
    <span key={`x:${c.key}`} title={t('desk_search_stranded_hint')}
      className="inline-flex items-center gap-1 rounded-full border border-dashed border-border bg-muted/40 px-1.5 py-0.5 max-w-[12rem] text-muted-foreground">
      <span className="truncate line-through">{c.label}</span>
      <button type="button" title={t('desk_search_remove_filter')} aria-label={t('desk_search_remove_filter')}
        className="shrink-0 leading-none hover:text-foreground"
        onClick={() => onRemove(c)}>×</button>
    </span>
  )
  return (
    <span className="flex items-center gap-1 min-w-0 overflow-hidden">
      {chips.slice(0, shown).map(one)}
      {stranded.map(strandedOne)}
      {hidden.length > 0 && (
        <span className="relative shrink-0">
          {/* 🚨 THE PANEL OPENED AND NOT ONE PIXEL OF IT WAS VISIBLE (owner, 2026-08-19:
              *"when we press +2 should it not expand?"*). It did expand. This row is
              `overflow-hidden` — deliberately, so the chips never spill out of the search bar —
              and `overflow` on an ancestor CLIPS absolutely-positioned descendants. The panel
              is `top-full`, i.e. it opens BELOW the row, which is precisely the clipped side.
              Measured live before the fix: panel 217×36 at y=210, clipping ancestor ending at
              y=209 → **0 visible pixels of 36**. A control that toggles invisible state reads
              as a dead button, and it is the second one of these this week (the ⋯ row menu ran
              off the left edge for the same family of reason).
              So it PORTALS to <body> and is positioned from the button's own rect, the pattern
              PillOverflow already uses. `popoverPos` also clamps to the viewport, which the
              old `left-0` never did — on a narrow window a right-hand chip's panel would have
              hung off the edge even without the clipping. */}
          <button ref={btnRef} type="button" className="hover:text-foreground underline decoration-dotted"
            title={hidden.map(c => c.label).join(' · ')}
            onClick={() => setPos(pos ? null : popoverPos(btnRef.current, 264, 'down'))}>+{hidden.length}</button>
          {pos && createPortal(
            <>
              {/* Click-away closes it. A filter readout must never become a thing you have
                  to hunt for a close button on. */}
              <span className="fixed inset-0 z-[199]" onClick={() => setPos(null)} />
              <span style={{ ...pos, width: 'max-content', maxWidth: 264 }}
                className="fixed z-[200] flex flex-wrap gap-1 rounded-md border border-border bg-popover p-1.5 shadow-lg overflow-y-auto">
                {hidden.map(one)}
              </span>
            </>, document.body)}
        </span>
      )}
    </span>
  )
}
// წყარო = WHERE THE AD IS LISTED. The filter matches `listing.source` across the cluster
// (p_sources in the RPC), so every option here must be a value that column actually holds.
//
// 🔴 'facebook_group' REMOVED 2026-08-28 (owner: "we need one facebook here as a source").
// Facebook appeared TWICE and the second one could never return anything. Measured:
//     listing.source     = 'facebook'        1 row
//     raw_listing.source = 'facebook_group'  5 rows
// They are two codes for one thing at two different LAYERS — raw_listing is the capture record,
// listing is the ad. This facet reads the listing, so the capture-layer code matched zero rows
// and rendered as an option with no count beside a real one. A control that cannot return
// anything is worse than a missing control: it reads as "we have none of those" rather than
// "this question is not asked here".
// ⚠️ Do NOT "fix" it by adding facebook_group to listing.source — that would mint a second
// portal for one platform. If FB-group capture should be findable, it is the ADDED-FROM
// question (how it entered the book), not the LISTED-ON one, and that is a different filter.
const SOURCE_FACET_OPTIONS = [['myhome.ge', 'MyHome'], ['home.ge', 'Home'], ['korter.ge', 'Korter'], ['ss.ge', 'SS.ge'], ['bog.ge', 'Bog'], ['facebook', 'Facebook']]
// F-099 (owner, 2026-09-10): a property with NO portal is a source too. Every hand-added and AI-intake property is
// written with listing.source = 'manual' (core_prepare) and the Desk-draft repost path writes 'sentavus'; the RPC
// already tallied both (22 + 1 across the company) and the list above simply never offered them, so the count was
// computed, sent and thrown away. ONE row, not three: hand / AI / owner-link is HOW it was added — that is the
// როგორ დაემატა facet's question — while this facet asks WHERE it is listed, and the answer is "nowhere but ours".
// The code is 'manual'; the server registry expands it to manual+sentavus on the wire, and the count here sums both.
// The LABEL depends on whose book you are looking at: on your own it is honestly "added by me"; on every book it is
// "senTavus" — added by someone, not necessarily you.
const SOURCE_FACET_OURS = 'manual'
const sourceFacetLabel = (code, bookScope) => code === SOURCE_FACET_OURS
  ? (bookScope === 'mine' ? t('desk_source_added_by_me') : t('desk_source_sentavus'))
  : (SOURCE_FACET_OPTIONS.find(([c]) => c === code) || [code, code])[1]
const sourceFacetCount = (counts, code) => code === SOURCE_FACET_OURS
  ? ((counts?.manual ?? counts?.sentavus) == null ? undefined : (Number(counts?.manual) || 0) + (Number(counts?.sentavus) || 0))
  : counts?.[code]
// 0779/0780 — the OTHER half of the owner's ask: "filter for where it's listed AND filter for
// how and where it was added from". Two values, so it is a radio and not a checklist: asking for
// both is asking for no filter, and a checklist would invite exactly that.
// 'evaluation' (core 1921, owner 2026-09-22): an Evaluate-your-property request from sentavus.com — a Leads row
const ADDED_HOW_OPTIONS = [['crawler', 'desk_added_how_crawler'], ['agent', 'desk_added_how_agent'], ['evaluation', 'desk_added_how_evaluation']]

// „რას ნიშნავს კარკასი?" — the frame ladder explained inside the facet (designer handoff
// 2026-08-06). Georgian agents know these words; a first-week hire does not, and neither does
// anyone reading the English locale. A tap-to-open block rather than a hover tooltip, for the
// reason the ⓘ dot exists at all (0162, owner: "on mobile hover is impossible").
//
// ⚠️ The text comes from the VOCABULARY ROW, never from a string table here. `description_ka`
// is the owner's own wording (0432) and the same field the ⓘ dot reads elsewhere, so the two
// surfaces cannot drift and a rewording is a data change rather than a deploy. A rung with no
// description simply contributes no line — coverage grows with the dictionary and a missing
// definition never blocks the ladder.
function ConditionHelp({ features, t }) {
  const [open, setOpen] = useState(false)
  const ka = getLanguage() === 'ka'
  const rows = (CONDITION_LADDERS.find(([l]) => l === 'frame')?.[1] || [])
    .map(c => (features || []).find(x => x.code === c))
    .filter(f => f && (ka ? (f.description_ka || f.description_en) : (f.description_en || f.description_ka)))
  if (!rows.length) return null
  return (
    <div className="pt-0.5">
      <button type="button" onClick={() => setOpen(o => !o)}
        className="text-[11px] text-muted-foreground hover:text-foreground underline decoration-dotted underline-offset-2">
        {t('desk_facet_condition_help')}
      </button>
      {open && (
        <div className="mt-1 space-y-1 rounded-md border border-border bg-muted/40 p-2">
          {rows.map(f => (
            <p key={f.code} className="text-[11px] leading-snug text-muted-foreground">
              <span className="font-medium text-foreground">{ka ? (f.name_ka || f.name_en) : (f.name_en || f.name_ka)}</span>
              {' — '}
              {ka ? (f.description_ka || f.description_en) : (f.description_en || f.description_ka)}
            </p>
          ))}
        </div>
      )}
    </div>
  )
}
// Exported for src/harness.jsx ONLY (the B308 render-harness rule: verify panel work by
// RENDERING it — vite build cannot see geometry). Nothing in the app imports this name.
// ── SNAPSHOT LENSES ──────────────────────────────────────────────────────────
// ♥ Favorites and 🗄 Archive do not FILTER the entries pipeline — they REPLACE it, rendering
// bespoke cards straight from the `favRows`/`archRows` snapshots with zero core reads (0191 /
// 0375). Everything the pipeline owns is therefore inert while one of them is on.
//
// ⚠️ That is the whole of owner report 2026-08-09, "selecting favorites and then status don't
// change anything". Nothing was broken in the status code; the axis simply had no pipeline left
// to act on, while its buttons went on lighting up as though it did. The panel's own comment
// above the state row already spells this failure out — for ClientLeads, where the same lens
// problem was found first and fixed with `cur !== 'ClientLeads'`. It was never extended to the
// two lenses that came later, so the diagnosis existed in the file and the fix did not.
//
// 📌 The honest UI for an unanswerable question is ABSENCE, not a disabled control: a greyed
// row reads as a permission error, and a live-looking one reads as a broken filter.
// 📌 This does not make favourites filterable — that is a real piece of work (the snapshot
// would have to become a query) and is filed as a backlog row. This only stops the Desk from
// claiming otherwise.
const SNAPSHOT_LENS = new Set(['Favorites', 'Archived'])

// ── B1188: the four date sections ────────────────────────────────────────────────────────────
// One section per DIMENSION, each with its own range, all combinable — the engine has taken
// them together since 0863 (p_dates) and until now nothing on screen could produce one.
//
// `posted` appears ONCE even though it has two dimension keys: posted and posted_or_added are
// the same question at two strictnesses, and the checkbox inside that section chooses which.
// Rendering them as two sections would invite an agent to set both, which asks one question
// twice and lets the stricter answer hide inside the looser one.
const DATE_SECTIONS = [{ dim: 'added' }, { dim: 'posted' }, { dim: 'last_seen' }, { dim: 'updated' }]

// Which lead_facet_count bucket (0830) carries the counts for each dimension. Keyed by the
// DIMENSION rather than the section, so the posted section reads the bucket matching whichever
// strictness is currently chosen — otherwise ticking 'substitute' would leave the pills showing
// the counts for the question no longer being asked.
const POOL_KEY_BY_DIM = {
  added: 'date_added', posted: 'date_posted', posted_or_added: 'date_posted_sub',
  last_seen: 'date_checked', updated: 'date_updated',
}

export function AdvancedSearchPanel({ filters, setFilters, listings, statusFilter, onStatusChange, onCollapse, geo, entryType, resultsCount, sheetOnly = null, bookScope = null, category = null, features = [], facetCounts = null, agentFilter = null, readout = null, poolSwitch = null, onUndoPoolSwitch = null, clientLens = null, clientFill = null, sort = null, rangeFacets = null, kindFeatureCaps = [], conditionLadder = null, poolCounts = null }) {
  // Per-category sidebar (§12 phase one, 2026-07-17): the Client view's criteria-overlap
  // filter deliberately ignores bathrooms/floor/total-floors ("clients don't carry these"
  // — see the classify comment in visibleResult), so showing those controls there was a
  // silent no-op: an agent narrowing by floor saw zero effect and no explanation. Hide
  // what can't apply instead of letting it lie. Everything else (geo/deal/kind/status/
  // price/area/rooms/bedrooms) genuinely filters clients via range overlap — kept.
  const clientView = entryType === 'Client'
  // B1188. A UI PREFERENCE, not a filter: it only decides which posted strictness a FUTURE
  // range will be written under. Whenever either key already holds a range the data wins (see
  // postedDim below), so this never contradicts what is on screen. Default true is the owner's
  // 2026-09-02 ruling -- substitute, and let them untick it.
  // ⚠️ DECLARED HERE, ABOVE postedDim, AND THAT IS THE WHOLE POINT. It first sat with the other
  // UI state ~40 lines below, which is AFTER the code that reads it -- a const is in its
  // temporal dead zone until its own line runs, so every AdvancedSearchPanel on the page threw
  // 'Cannot access postedSubstitutePref before initialization' and the panel rendered NOTHING.
  // vite build passed, 1,234 tests passed, gate:undef passed. Only rendering it found this.
  // FACET 07B (owner design, 2026-09-06): the substitution is a SWITCH, OFF by default — our date standing in
  // for the portal's is a different question, not a refinement. Reverses the 2026-09-02 default ON.
  const [postedSubstitutePref, setPostedSubstitutePref] = useState(false)

  // B1196 — keep the control the agent just clicked under the pointer. Ticking the first
  // district grew the readout above the tree by 82px (while the source list shrank 29), so the
  // checkbox slid out from under the cursor. Anchors the ELEMENT rather than compensating any
  // one block's height, because two things move in opposite directions in the same commit.
  const panelScrollRef = useRef(null)
  useStickyControl(panelScrollRef)
  const set = (k, v) => setFilters(f => ({ ...f, [k]: v }))

  // ── B1188: reading and writing one date dimension ──────────────────────────────────────────
  const dateRanges = filters.dateRanges || {}

  // Which of the two posted strictnesses this section is currently asking. Derived from the
  // DATA where the data says, and only falls back to the local preference when neither key
  // holds a range — so a shared link or a saved search arrives showing the question it actually
  // encodes, rather than whatever this browser last toggled.
  const postedDim = dateRanges.posted ? 'posted'
    : dateRanges.posted_or_added ? 'posted_or_added'
      : (postedSubstitutePref ? 'posted_or_added' : 'posted')

  // A dimension with neither bound is DELETED, never stored empty: an empty range is not a
  // filter, and leaving one behind would make an untouched search compare as changed.
  const setDateRange = (dim, patch) => setFilters(f => {
    const cur = { ...(f.dateRanges || {}) }
    const next = { ...(cur[dim] || { from: '', to: '' }), ...patch }
    if (!next.from && !next.to) delete cur[dim]
    else cur[dim] = next
    return { ...f, dateRanges: cur }
  })

  // Ticking 'substitute' MOVES the range between the two keys rather than setting a flag, so
  // only one of them ever holds a range. Two keys holding ranges at once would ask one question
  // twice, and the RPC would AND them — silently answering something narrower than the panel
  // shows.
  const togglePostedSubstitute = (on) => {
    setPostedSubstitutePref(on)
    const to = on ? 'posted_or_added' : 'posted'
    const from = on ? 'posted' : 'posted_or_added'
    setFilters(f => {
      const cur = { ...(f.dateRanges || {}) }
      if (!cur[from]) return f
      cur[to] = cur[from]
      delete cur[from]
      return { ...f, dateRanges: cur }
    })
  }
  // Time-on-market "Custom…". Open is a UI intent, but it must ALSO be forced open whenever
  // a live range matches none of the presets — a saved search or a shared URL can arrive
  // carrying 45–60 days, and a control that hid it would show four unticked boxes while a
  // range filter was quietly running. Derived, not stored, so it cannot drift from state.
  const [ageCustom, setAgeCustom] = useState(false)
  const ageRangeSet = (filters.ageMinDays ?? '') !== '' || (filters.ageMaxDays ?? '') !== ''
  const ageCustomOpen = ageCustom || (ageRangeSet && !AGE_PRESETS.some(p =>
    String(filters.ageMinDays ?? '') === p.lo && String(filters.ageMaxDays ?? '') === p.hi))
  // Checkbox facets toggle a value in/out of the array field (OR within the facet).
  // B1213 (2026-09-06): the three-state cycle for a categorical facet — neutral → ✓ → ✕ → neutral.
  // Which facets have a ✕ half, and the state key it lives in, come from the single filter definition.
  const cycleFacet = (k, v) => setFilters(f => ({ ...f, ...facetCycle(f, k, v) }))
  // F-050b (owner, 2026-09-06): the NAME deselects — a decided value goes straight to neutral, a neutral one to ✓.
  const nameFacet = (k, v) => setFilters(f => ({ ...f, ...facetPressName(f, k, v) }))
  const toggleIn = (k, v) => setFilters(f => {
    const cur = f[k] || []
    return { ...f, [k]: cur.includes(v) ? cur.filter(x => x !== v) : [...cur, v] }
  })
  // Which restriction chip has its menu open (one at a time) — mirrors openPolicy in the
  // property terms modal, same component, same rule.
  // ── "Who is it for" (design handoff desk-search, turn 6a) ──────────────────────────
  // The restriction pills grouped into AXIS rows: axis name + its answer (or `not asked`),
  // pills only inside the axis you opened. The grouping is a query over the vocabulary
  // (whoIsItFor.js reads restriction_code_projection off /restriction-vocab) — never a
  // second taxonomy. One axis open at a time. (The per-pill stance menu that the
  // `openRestrict` state used to drive is gone — one tap now says everything.)
  const [openWhoAxis, setOpenWhoAxis] = useState(null)
  const [whoVocab, setWhoVocab] = useState(null)
  // Module-level promise cache in core.js — this costs one fetch per session, shared with
  // the capture panel. A failed fetch leaves null and the rows degrade to a visible
  // standard/other listing (whoIsItFor.test.mjs pins that), never a blank facet.
  useEffect(() => { getRestrictionVocab().then(setWhoVocab).catch(() => {}) }, [])
  const whoRows = useMemo(() => whoIsItForRows(
    (features || []).filter(x => x.category === 'tenant_terms' && x.active !== false), whoVocab),
    [features, whoVocab])
  // code → {code, mode} for the committed restriction lenses — the rows' answer lookup.
  const whoSelected = useMemo(() =>
    new Map(parseRestrictions(filters.restrictions).map(r => [r.code, r])),
    [filters.restrictions])
  // ⚠️ `'active'` IS THE NEUTRAL STATUS, not `'all'` (fixed 2026-08-18). B576 retired `all`
  // and `closed` from the two-button row on 2026-08-16 and made `active` the default the rest
  // of this file already treats as "nothing chosen" (entryFiltersActive, clientOnlyCut, the
  // too-broad banner, the useState seed). This test was left comparing against the retired
  // word, so it read TRUE on every ordinary view: the mobile ⚙ page's Clear control was lit
  // permanently, with nothing to clear — and pressing it ran `resetAll`, which SET status to
  // `all`. Measured live: the server treats `all` as no status at all, so "clear" widened the
  // feed from 309 to 311 (both piles), lit neither status pill, and flipped the panel into
  // filtered-looking state. A clear button that adds a filter is the worst version of this.
  const active = advFiltersActive(filters) || statusFilter !== 'active'
  const num = v => (v === '' || v == null ? null : +v)
  const intentsKey = (filters.intents || []).join(','), kindsKey = (filters.kinds || []).join(',')
  // Histogram fuel: the real loaded listings, narrowed by the panel's own deal/type/currency
  // picks so the distribution shows what the OTHER filters would still let through.
  // ── 🚨 A PRICE-SORTED PAGE IS NOT A SAMPLE OF PRICES ─────────────────────────────────
  // The quick-select chips cut at the TERCILES of these arrays (lib/rangePresets.js), which is
  // only sound if the array is an unbiased sample of the segment. It is not, under two of the
  // sort options: `cheapest`/`priciest` are SERVER page selectors (SERVER_SORT_OF → price_asc /
  // price_desc) and `FILTERED_PAGE` is 90 (was 30 until B884), so under a filter the panel
  // holds the 90 CHEAPEST
  // rows of the whole result — terciles of which sit far below the market and would quietly
  // relabel every chip. `largest` does the same to area, and `ppm2` orders by price per m²,
  // which biases both.
  // So the values are WITHDRAWN, not corrected: rangePresets falls back to the axis, which is
  // sort-independent. A slightly coarser bucket beats a confidently wrong one, and there is no
  // honest way to de-bias a top-N slice from inside the client.
  // ⚠️ This is the same exposure `niceCeiling` has always had for the ceiling itself — it reads
  // the same page. Fixing that properly is B839 item 3 (distribution from the server, per
  // intent × kind × geography), which retires this guard along with the sampling problem.
  const priceBiasedSort = sort === 'cheapest' || sort === 'priciest' || sort === 'ppm2'
  const areaBiasedSort = sort === 'largest' || sort === 'ppm2'
  const priceValues = useMemo(() =>
    listings.filter(l => l.currency === filters.currency
      && (!filters.intents?.length || filters.intents.includes(l.intent))
      && (!filters.kinds?.length || filters.kinds.includes(l.kind)))
      .map(l => +l.price).filter(Number.isFinite),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [listings, filters.currency, intentsKey, kindsKey])
  const areaValues = useMemo(() =>
    listings.filter(l => (!filters.intents?.length || filters.intents.includes(l.intent))
      && (!filters.kinds?.length || filters.kinds.includes(l.kind)))
      .map(l => +l.area_sqm).filter(Number.isFinite),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [listings, intentsKey, kindsKey])
  const priceCeiling = useMemo(() => niceCeiling(priceValues, 1_000_000), [priceValues])
  // Same data-driven, outlier-tolerant ceiling for area (was hardcoded 500) — the axis hugs the
  // real cluster via p95, so a lone 900m² penthouse doesn't stretch the slider past a wall of
  // 40-70m² flats. Falls back to 500 only when there are too few areas to trust (niceCeiling).
  const areaCeiling = useMemo(() => niceCeiling(areaValues, 500), [areaValues])
  // ── Land area (0226 fact, filterable since v29/0345) ──────────────────────────────────
  // Offered only when the current kind selection can HAVE a plot. That mirrors the DB's
  // capability map (kind_attribute_capability): apartment → land_area_sqm is not merely
  // unknown, the concept does not exist, and the RPC folds 'na' as EXCLUDED. So a land
  // filter set while looking at apartments would empty the grid and read as a broken
  // screen. No kind selected → offered, because the mixed feed does contain plots.
  // ── STAGE 3 (SEARCH_GRAMMAR §5b): the panel is an INTERVIEW, driven by capability ──────
  // The interview is the INTERSECTION of three orthogonal profiles — Identity (what it is) ×
  // Intent (what transaction) × Object (what we're searching) — computed once here and asked
  // via applies(). This replaces hand-rolled per-facet conditionals like the `showLandArea`
  // line that used to live here: those are the "dozens of pairwise conditions" the ruling
  // names, and each one drifts on its own.
  // ⚠️ Identity comes from deriveIdentityPath, NOT from a stored node — the taxonomy projects
  // onto state, never becomes it. No kind chosen → no identity constraint → everything
  // applies, which is the unknown-≠-no rule holding at the UI layer too.
  const identityPath = deriveIdentityPath(filters)
  const cap = interviewCapability({
    kind: identityPath.kind,
    subtype: identityPath.subtype,
    intents: filters.intents || [],
    objectType: entryType || 'Property',
  })
  const showLandArea = applies(cap, 'landArea')
  const landAreaValues = useMemo(() =>
    listings.filter(l => (!filters.intents?.length || filters.intents.includes(l.intent))
      && (!filters.kinds?.length || filters.kinds.includes(l.kind)))
      .map(l => +l.land_area_sqm).filter(Number.isFinite),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [listings, intentsKey, kindsKey])
  // Plots run far larger than flats and the spread is wider (600 m² next to 9,000), so the
  // fallback is its own, not area's 500. niceCeiling still hugs the real cluster via p95.
  const landAreaCeiling = useMemo(() => niceCeiling(landAreaValues, 2000), [landAreaValues])
  // Buy↔Rent is not a filter change, it's a price-scale swap: a $100k min carried from sale
  // into rent silently guarantees an empty grid. When the deal type changes and the kept price
  // bounds sit above the NEW segment's ceiling (priceCeiling already recomputed from the new
  // segment's real listings), the bounds are semantically dead — clear price ONLY (rooms/floor
  // survive a deal-type switch fine) and say so, rather than reinterpreting silently.
  // ── THE DEAL-CHANGE CARRY-OVER (design 4b — ONE mechanism, not two) ─────────────────
  // This was an amber sentence with no undo, which is the weaker half of what the pool
  // switch already does: keep what survives, name what did not, and let the agent put it
  // back. The brief is explicit that there is ONE carry-over component — so this now
  // carries the same shape and renders through the same banner.
  // ⚠️ DEVIATION from the mock, stated rather than hidden: it offers **Undo**, not the
  // mock's forward "Set a rent budget" repair link. That link has to OPEN the Price facet,
  // and FacetGroup reads its open state from localStorage in a mount-time initializer —
  // forcing it open from outside means remounting the group by key, which trades a real
  // mechanism for a cosmetic one. Undo restores the deal AND the bounds it cleared, which
  // is the same promise the pool banner makes and needs no new plumbing.
  const [dealChange, setDealChange] = useState(null)
  const prevIntent = useRef(intentsKey)
  useEffect(() => {
    if (prevIntent.current === intentsKey) return
    const from = prevIntent.current
    prevIntent.current = intentsKey
    const pMin = num(filters.priceMin), pMax = num(filters.priceMax)
    if ((pMin != null && pMin > priceCeiling) || (pMax != null && pMax > priceCeiling)) {
      // Never REINTERPRET a bound across a deal change: $200,000 as a monthly rent is not a
      // smaller number, it is a different question, and silently keeping it guarantees an
      // empty grid the agent reads as "no supply".
      setFilters(f => ({ ...f, priceMin: '', priceMax: '' }))
      setDealChange({ reason: t('desk_search_price_range_cleared'), prev: { intents: from.split(',').filter(Boolean), priceMin: filters.priceMin, priceMax: filters.priceMax } })
    } else {
      setDealChange(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intentsKey])
  const undoDealChange = () => {
    if (!dealChange) return
    const p = dealChange.prev
    // prevIntent is set BEFORE setFilters so the effect above sees the restore as "no
    // change" and cannot fire a second banner about undoing the first one.
    prevIntent.current = (p.intents || []).join(',')
    setFilters(f => ({ ...f, intents: p.intents, priceMin: p.priceMin, priceMax: p.priceMax }))
    setDealChange(null)
  }
  // Facet registry (2026-07-18): ONE definition of every facet's title/badge/body,
  // rendered TWO ways below — desktop keeps the 2026-07-17 accordion sidebar; mobile
  // renders the eBay full-screen Filter page (rows → per-facet bottom sheets). Same
  // rule as CooperationChips/suggestRows: one renderer per concept, containers differ.
  const facets = [
    // Category FIRST (2026-07-22 user ruling — the true eBay model: categories live in
    // the LEFT RAIL with counts, not as horizontal tab-chips above the feed; the chips
    // rail shipped earlier the same day is superseded by this). Radio semantics: one
    // category at a time, same values the search-box select sets — two surfaces, ONE
    // state. Counts come from the visibleResult tail tally (loaded rows); the special
    // feeds (Pool/Business/Leads) have their own data so no count is claimed for them.
    // ⚠️ An IIFE returning the whole facet ENTRY, not just its body (2026-08-05, design 2c).
    // The pool box has to render in two places — inside this facet on desktop, pinned in the
    // mobile filter page's header — and the alternative was a second copy of the segmented
    // control. This surface already has the rule (CooperationChips): ONE renderer per
    // concept, containers differ. So the closure hands the element out as `poolBox` and the
    // two containers place it; there is still exactly one of it, built once, per render.
    category && (() => {
        // TREE, not a flat list (2026-07-22 user ruling #3 the same day): "All types" is
        // the ROOT; every category is its child, indented; a category's own sub-filters
        // nest one level deeper — Meeting's status tree is the first (statuses come from
        // the lifted meetingStatusFilter, the SAME state the feed's pills set: two
        // surfaces, one state, same rule as the search-box select).
        // ⚠️ A selected SUB-row must not look like more of its selected parent (owner-caught
        // 2026-07-25: "highlighted categories stack on each other and seem like one"). Two
        // causes, both fixed here: the fills were the identical `bg-primary/10`, AND the
        // `space-y-0.5` that separates the top-level rows lives on the OUTER container only —
        // a category row and its own subtree sit in the same wrapper with no gap at all, so
        // two equal fills met edge to edge and read as one block. A sub-row now carries a
        // left accent bar + a stronger tint; the 2px is taken back out of its padding so the
        // label stays on the tree's indent grid instead of jogging right when selected.
        // `icon` (2026-08-10) is a Material Symbols name, ALWAYS supplied from taxonomy.js's
        // four node maps and never written literally here, so the tree and the Type facet
        // cannot drift onto different glyphs for the same token. Omitted = no icon, which is
        // the honest render for a node with no entry (taxonomy.js states why).
        // ⚠️ `shrink-0` on the glyph, `truncate` on the TEXT — not on the pair. A long Georgian
        // subtype in a narrow rail must eat its own label, never the icon. This is the same
        // inversion StateRow shipped backwards on the card face (2026-08-04), where truncating
        // the value instead of the label clipped platform dots off the column and a
        // three-portal flat read as one.
        const row = (label, n, isActive, onClick, depth = 0, key, onHover, icon) => (
          <button key={key || label} type="button" onMouseEnter={onHover}
            style={{ paddingLeft: `${8 + depth * 14 - (isActive && depth >= 2 ? 2 : 0)}px` }}
            className={`w-full text-left rounded pr-2 py-1 text-sm flex items-center justify-between ${isActive
              ? (depth >= 2
                ? 'bg-primary/20 border-l-2 border-primary text-foreground font-medium'
                : 'bg-primary/10 text-foreground font-medium')
              : 'text-muted-foreground hover:text-foreground hover:bg-accent'}`}
            onClick={onClick}>
            <span className="min-w-0 flex items-center gap-1.5">
              {icon && <Icon name={icon} size={14} className={isActive ? '' : 'opacity-70'} />}
              <span className="truncate" title={typeof label === 'string' ? label : undefined}>{label}</span>
            </span>
            {/* ⚠️ A PROVISIONAL NUMBER MUST LOOK PROVISIONAL (owner-reported 2026-08-02:
                "numbers grow slowly"). Until page 0 reports, these counts are tallied over the
                rows LOADED SO FAR, so they climb with every appended page — an agent watching
                "2,877" become "3,004" cannot tell a still-counting number from a changing one.
                Same law as the district question shipped the same day: the system must never
                present something it is unsure of as settled. Pulsing + muted says "still
                counting" without moving the layout or hiding the running estimate, which is
                genuinely useful while it lands. */}
            {n != null && (
              <span title={category.countsProvisional ? t('desk_counts_loading_hint') : undefined}
                className={`text-xs shrink-0 ml-2 ${isActive ? 'text-primary' : ''} ${category.countsProvisional ? 'opacity-50 animate-pulse' : ''}`}>
                {n}
              </span>
            )}
          </button>
        )
        const pick = (set, n) => () => { category.set(set); trackDeskEvent('category_switched', { to: set[0] || 'all', count: n, surface: 'facet' }) }
        const cur = category.value[0] || ''
        const m = category.meeting   // { value, set, counts } | undefined — lifted meetings status state
        // ⚠️ CORPUS count, not a loaded-rows tally — this row must share a universe with the
        // kind children beneath it (see propertyTotalForTree for the why and the Δ=0 check).
        // `undefined` = an older caller never passed the prop → keep the previous tally rather
        // than render a blank row. `null` is a REAL value meaning "page 0 hasn't reported",
        // which row() renders as no number at all.
        const propertyRowN = category.propertyTotal !== undefined
          ? category.propertyTotal
          : (category.counts.Property || 0) + (category.counts.Listings || 0)
        // The Meeting row renders m.counts.all whenever the lifted meeting state exists, NOT
        // category.counts.Meeting — so allN below must read THIS, or the root silently omits
        // every meeting (owner's screenshot: 119 + 56 = 175, the 67 meetings nowhere in it).
        // null while the lazy meetings fetch has not landed — see the subtree's own note
        // below. A fake `0` here is not merely a wrong row: it is summed into "All types",
        // so the root reads as a finished total with the whole meeting book missing from it.
        const meetingRowN = m ? (m.settled ? m.counts.all : null) : (category.counts.Meeting || 0)
        const kids = [
          { label: t('desk_type_properties'), set: ['Property', 'Listings'], n: propertyRowN },
          { label: t('desk_type_client'), set: ['Client'], n: category.counts.Client || 0 },
          { label: t('desk_type_meeting'), set: ['Meeting'], n: meetingRowN },
          // Pool (ბირჟა) was hidden here 2026-07-24 and REMOVED ENTIRELY 2026-08-01
          // (owner: "remove pool completely") — the object type, its feed, its client API
          // and its strings are all gone. See B268.
          { label: t('desk_type_business'), set: ['Business'], n: null },
        ]
        // ── THE REORDER (owner ruling 2026-08-02) ────────────────────────────────────
        // Leads and Favorites LEFT this list. They were never object types, and rendering
        // them as siblings of Properties/Client/Meeting made `All types` look like their
        // parent — which is how the panel came to show `All types 2,937` sitting above its
        // own supposed child `Leads 22,666`. `allN` already excluded them (see below), so
        // the number was never wrong; the TREE was, and no relabelling could fix it.
        //
        // What each of the three groups asks (core/DOMAIN_VOCABULARY.md §0):
        //   INVENTORY  Leads          — the company's discovery pool. Identical for every
        //                               viewer. "Am I hunting new business?"
        //   OBJECTS    All types →…   — our work: authored ∪ captured ∪ worked (B236).
        //                               Per-viewer. "…or working what we already have?"
        //   LENSES     ♥ Favorites    — a view over either, not a thing you can own.
        //
        // A pool, a personal scope and a lens are three different kinds of question, so
        // they get three groups. Nothing here is new state or new routing — the same
        // `category.set()` calls, in different places. See SEARCH_INFRASTRUCTURE_AUDIT §9f.
        //
        // Leads DOES claim a count (owner 2026-07-24) — but only its own feed's total for
        // the SAME query, never a second count that could disagree with what clicking the
        // row shows. null until that query has been fetched (mount warm / hover / entering
        // the category), because a total from other filters would read as "N leads match
        // your search" and be wrong. Business still has no count: its feed carries no total.
        // (The former `inventory` array lived here. It carried a vestigial label/count that
        // nothing rendered — it existed only to hang identitySub() off Leads. The Leads
        // tree below now states its own rows explicitly, so the indirection is gone.)
        // "All types" = the SUM OF THE ROWS SHOWN BENEATH IT, so the root can never read
        // smaller than one of its own children (which is exactly what happened once Properties
        // switched to the corpus count while this kept summing loaded tallies). Leads and
        // Favorites stay excluded, as before: separate destinations with their own feeds, not
        // a slice of this corpus.
        // ⚠️ Still MIXED until the proposals fetch is uncapped — Properties is whole-corpus,
        // Client/Meeting are loaded-window. Honest as "the sum of what you see"; it becomes a
        // true total only when those two report real totals.
        // ⚠️ NULL-PROPAGATE, never ?? 0 (owner 2026-08-05: "it goes 92 → 162 → 3141").
        // propertyRowN is null precisely while the corpus total is unreported, and the old
        // `?? 0` summed the two SMALL components into something that rendered exactly like
        // the answer — a partial sum wearing the total's clothes, counting up as fetches
        // land. The row/pill machinery already renders null as no number (with the
        // provisional pulse), which is the honest face of "not arrived yet" — the same
        // rule CarryOverBanner states as "a number that has not arrived is never drawn
        // as 0". With the tc: seed below, the null window is a cold first visit only.
        // ── 🚨 `allN` COUNTS OUR WORK, SO IT MUST NOT ADD THE LEADS TOTAL (owner, 2026-08-22) ──
        // Owner's screenshots showed the our-work pool box reading 9121, 2908, 9087 and 4612 for
        // the same label across four switches — each time roughly whichever pool total had just
        // been computed, plus ~50. Read off the live props: `lead.count` 30,179, `counts.Client`
        // 35, `meetingRowN` 50 → **30,264**, exactly the number on screen, while `workCounts`
        // said our work was **327**. A subset box reporting more than the whole leads pool.
        //
        // The cause is that `propertyRowN` serves TWO consumers that live in DIFFERENT universes:
        //   · the `ობიექტები` row inside the Leads subtree, which must be the LEADS total — that
        //     is what `treePropertyTotal` switches for, and what B899 fixed;
        //   · this sum, which is the OUR-WORK pool box and must be the our-work total.
        // One value cannot be both. `51b5162` (2026-08-19) taught `treePropertyTotal` to switch on
        // the Leads pool for the first consumer, and this second one silently inherited the switch.
        // ⚠️ NOT a B899 regression, and I checked rather than assumed: at `805b38d^` this line
        // already read `propertyRowN`, and `treePropertyTotal` already switched.
        //
        // So this reads `workPropertyTotal` — `propertyTotalForTree`, the entries-pipeline total,
        // which is our work whatever pool is on screen. `counts.Client` and `meetingRowN` were
        // ALWAYS our-work-scoped, so all three terms now share one universe.
        // ⚠️ Keep the `!== undefined` fallback shape: `null` is a REAL value here meaning "page 0
        // has not reported", and the null-propagation below depends on telling that apart from
        // "an older caller never passed the prop".
        const workPropertyRowN = category.workPropertyTotal !== undefined
          ? category.workPropertyTotal
          : propertyRowN
        const allN = (workPropertyRowN == null || meetingRowN == null) ? null
          : workPropertyRowN + (category.counts.Client || 0) + meetingRowN
        // Meeting status subtree — mirrors the feed pills' two tiers exactly: branch row
        // (all/active/finished), then the CHOSEN branch's granular statuses one deeper.
        const meetingSub = () => {
          if (!m) return null
          // Granular statuses nest DIRECTLY under their own branch row (owner report
          // 2026-07-24: appending them after all three branches made Active's statuses
          // look like Finished's children). Only the chosen branch reveals its children.
          const branchOf = s => MEETING_FINISHED.includes(s) ? 'finished' : MEETING_ACTIVE.includes(s) ? 'active' : s
          const openBranch = branchOf(m.value) === 'finished' ? 'finished' : m.value === 'all' ? null : 'active'
          // SELECTABLE, not the whole partition — see MEETING_ACTIVE_SELECTABLE: the
          // Desk cannot produce a `rescheduled` row, so offering the bucket was offering
          // a filter that can only ever answer "nothing".
          const kidsOf = { active: MEETING_ACTIVE_SELECTABLE, finished: MEETING_FINISHED }
          // 🏆 on closed (owner): a successfully closed deal is the row worth celebrating.
          const statusLabel = s => (s === 'closed' ? '🏆 ' : '') + t(MEETING_STATUS_KEYS[s])
          // ⚠️ NULL-PROPAGATE, never `?? 0` — the same rule stated 25 lines above for the
          // property row, and not applied here until 2026-08-15. `viewings` is LAZY: it is
          // fetched when the agent first enters the Meeting category, so on a cold Desk
          // every one of these was a confident `0`, indistinguishable from a genuinely
          // empty book — and the fake zeros were folded into the "All types" total, which
          // then jumped when the fetch landed. That is the "92 → 162 → 3141" complaint,
          // one branch over. The row machinery already draws null as no number.
          const n = k => (m.settled ? (m.counts[k] ?? 0) : null)
          return (<>
            {['all', 'active', 'finished'].map(s => (<div key={`ms-${s}`}>
              {row(t(s === 'all' ? 'desk_meeting_filter_all' : s === 'active' ? 'desk_meeting_filter_active' : 'desk_meeting_filter_finished'),
                n(s), m.value === s, () => m.set(s), 2)}
              {openBranch === s && kidsOf[s].map(g =>
                row(statusLabel(g), n(g), m.value === g, () => m.set(g), 3, `msg-${g}`))}
            </div>))}
          </>)
        }
        // Properties kind subtree (design skin §2): ბინა/სახლი/კომერციული/მიწა one level
        // under Properties. Radio semantics like the rest of the tree — one kind or none;
        // it writes the SAME filters.kinds the checkbox facet below edits (two surfaces,
        // one state), so radio-picking here replaces any multi-select. Counts: v13
        // authoritative kind_counts once a committed page-0 reported them, loaded-rows
        // tally as the instant fallback — the tree never blocks on the server (spec rule).
        // ── STAGE 2 (owner ruling, SEARCH_GRAMMAR §5b): the tree owns the WHOLE identity
        // grammar — Intent → Category → Kind → Subtype (apartment: → Status). It replaced
        // kindSub (a flat 4-kind radio) 2026-08-01. Load-bearing properties:
        //  · the tree DERIVES its lit path from the shared filter state (deriveIdentityPath)
        //    and writes back only explicit predicate fields — it never stores a node.
        //  · switching Intent is ONE CLICK and preserves the whole interview (…f spread):
        //    the owner's hypothesis is that the old high toggle-rate was agents rebuilding
        //    searches the UI forced them to lose. This makes the cheap switch real.
        //  · siblings stay visible as context; only the chosen branch expands.
        //  · a level with ONE child is not a question — commercial/land skip Kind.
        //  · counts: intent/kind rows spend the SAME server counts the facets spent
        //    (one universe); category sums its kinds; subtype/status rows carry none yet
        //    (no subtype_counts in the RPC — a number from loaded rows would lie).
        const CAT_LABEL = IDENTITY_CAT_LABEL, STATUS_LABEL = IDENTITY_STATUS_LABEL, intentText = identityIntentText
        const identitySub = () => {
          const path = deriveIdentityPath(filters)
          const setPath = next => setFilters(f => {
            const p = identityPatch(next)
            // status and the apart-hotel context are ORTHOGONAL predicates riding featureCodes —
            // strip only OUR axes, never the agent's other feature chips.
            const otherCodes = (f.featureCodes || [])
              .filter(c => !APARTMENT_STATUS.includes(c) && !APARTMENT_CONTEXT_CODES.includes(c))
            return {
              ...f,   // ← the whole interview survives an identity change, by design
              intents: next.intent ? [next.intent] : [],
              kinds: p.kinds || [],
              subtypes: p.subtypes || [],
              featureCodes: [...otherCodes, ...(p.featureCodes || [])],
            }
          })
          const kindN = k => category.kindCounts ? (category.kindCounts[k] ?? 0) : null
          const catN = c => category.kindCounts
            ? (CATEGORY_KINDS[c] || []).reduce((a, k) => a + (category.kindCounts[k] ?? 0), 0) : null
          // Third level of a kind: subtype rows (toggle), or apartment's status axis.
          // ⚠️ No explicit "unknown" status child yet — featureCodes can only assert
          // presence, and a control that can never match reads as broken (§12). The 11%
          // stay REACHABLE by simply not picking a status; the dedicated child is blocked
          // on a feature-absent server verdict (B270).
          const kindChildren = (k, depth) => k === 'apartment'
            ? APARTMENT_STATUS.map(sv => row(t(STATUS_LABEL[sv]), null, path.status === sv,
                () => setPath({ ...path, kind: k, subtype: null, status: path.status === sv ? null : sv }),
                depth, `st-${sv}`))
            : (KIND_SUBTYPES[k] || []).map(sv => row(t('desk_subtype_' + sv), null, path.subtype === sv,
                () => setPath({ ...path, kind: k, status: null, subtype: path.subtype === sv ? null : sv }),
                depth, `sub-${k}-${sv}`, undefined, SUBTYPE_ICON[sv]))
          return (<>
            {['sale', 'rent', 'daily_rent', 'girao'].map(iv => {
              const iOn = path.intent === iv
              return (<div key={`it-${iv}`}>
                {/* 🔴 THE SELECTED INTENT CARRIES NO NUMBER, and the reason is semantic,
                    not cosmetic (owner's screenshot 2026-08-02: `For rent 10718` sitting
                    above `Residential 20015` — a child larger than its parent, the same
                    shape as the `All types` bug this whole restructure began with).
                    Two count systems meet on this row and they do not mean the same thing:
                      · INTENT counts are an overlapping PREVIEW. 0229: a cluster holding
                        both a rent and a sale posting is counted under BOTH, so "buckets
                        may overlap — the sum can exceed total_clusters … the client must
                        never sum them into a total."
                      · KIND counts PARTITION. Each cluster is attributed to exactly one
                        kind via min(prop_kind), which is why a category row may legitimately
                        sum its kinds.
                    Preview semantics are right for CHOOSING an option and wrong for being a
                    parent: nesting asserts "Residential ⊂ For rent", which an overlapping
                    preview never promised. On top of that, the moment an intent is picked
                    the server nulls its own counts by design (the own-filter rule) and the
                    sticky cache — correctly, per the 2026-07-28 ruling — keeps showing the
                    PREVIOUS response's numbers, measured over the wider corpus.
                    So the number stays exactly where it is honest: on the intents you have
                    NOT chosen, where it answers "what would I get if I ticked this" and has
                    no children beneath it to contain. The chosen intent is the only row that
                    makes a containment claim, and it is the only one that drops its count.
                    ⚠️ Do not "fix" this by summing the kinds instead: that number would be
                    right for one branch and silently wrong the moment a cluster carries two
                    intents, which is the case the preview semantics exist to handle. */}
                {row(intentText(iv), iOn ? null : (facetCounts?.intents?.[iv] ?? null), iOn,
                  () => setPath(iOn ? { ...path, intent: null } : { ...path, intent: iv }), 2, `it-${iv}`,
                  undefined, INTENT_ICON[iv])}
                {iOn && CATEGORIES.map(cv => {
                  const cOn = path.category === cv
                  const kinds = CATEGORY_KINDS[cv] || []
                  const single = kinds.length === 1 ? kinds[0] : null
                  return (<div key={`cat-${cv}`}>
                    {row(t(CAT_LABEL[cv]), catN(cv), cOn,
                      () => setPath(cOn ? { intent: path.intent } : { intent: path.intent, category: cv }), 3, `cat-${cv}`,
                      undefined, CATEGORY_ICON[cv])}
                    {cOn && (single
                      ? kindChildren(single, 4)
                      : kinds.map(kv => {
                          // A kind row is lit only when no context node (apart-hotel) has taken
                          // the apartment — otherwise both rows would claim the selection.
                          const kOn = path.kind === kv && !path.context
                          return (<div key={`kn-${kv}`}>
                          {row(t(PROPERTY_CATEGORY_KEYS[kv]), kindN(kv), kOn,
                            () => setPath(kOn
                              ? { intent: path.intent, category: cv }
                              : { intent: path.intent, category: cv, kind: kv }), 4, `kn-${kv}`,
                            undefined, KIND_ICON[kv])}
                          {kOn && kindChildren(kv, 5)}
                          {/* Apart-hotel (owner 2026-10-02): drawn as apartment's sibling, but it
                              writes kind=apartment + type_aparthotel_unit (taxonomy.js
                              APARTMENT_CONTEXT). No count: the search reports kind counts, not
                              feature counts, and a number from loaded rows would lie — the same
                              rule the status rows follow. Its children are apartment's status
                              axis, because it IS an apartment. */}
                          {kv === 'apartment' && (CATEGORY_CONTEXT_NODES[cv] || []).map(nv => {
                            const nOn = path.context === nv
                            return (<div key={`cx-${nv}`}>
                              {row(t('desk_context_' + nv), null, nOn,
                                () => setPath(nOn
                                  ? { intent: path.intent, category: cv }
                                  : { intent: path.intent, category: cv, kind: 'apartment', context: nv }), 4, `cx-${nv}`,
                                undefined, KIND_ICON[nv])}
                              {nOn && kindChildren('apartment', 5)}
                            </div>)
                          })}
                        </div>)
                        }))}
                  </div>)
                })}
              </div>)
            })}
          </>)
        }
        // ── POOL SELECTOR (design 2a §2) ────────────────────────────────────────────
        // The first question, asked once: which inventory am I standing in? Replaces the
        // interim three-group list — a segmented control says "pick one of two" in a way a
        // list of rows never can, which was the whole failure being fixed (a pool and a
        // personal scope rendered as siblings).
        //
        // The sub-line is REQUIRED, not decoration (brief Rule 5 / Q3): Leads is identical
        // for every viewer and Our work is not, so the segment states whose universe it is.
        // An unstated asymmetry gets discovered as a bug — two agents compare screens, see
        // different numbers under the same word, and file it.
        //
        // Colours use semantic tokens, never the mock's literal hsl() values, per the
        // handoff's own instruction.
        // ⚠️ BOTH lead types light the Leads segment. `cur === 'Leads'` alone would leave
        // the pool switch showing "our work" as active while a client-lead feed is on
        // screen — the control would be lying about which inventory you are in, which is
        // the one thing the 2026-08-02 label ruling exists to prevent.
        const leadsOn = cur === 'Leads' || cur === 'ClientLeads'
        // ⚠️ `Archived` joined this test 2026-08-09. It is the ♥ block's twin in every other
        // respect (same snapshot render, same optimistic un-toggle) and was simply missed here,
        // so opening 🗄 left the "our work" segment lit while a snapshot feed was on screen —
        // the same lie about which inventory you are in that the leadsOn comment below forbids.
        const workOn = !leadsOn && !SNAPSHOT_LENS.has(cur)
        // ── THE POOL SEGMENT — restored 2026-08-17 (owner: "design is ruined. ეძებ part
        // shouldn't change at all and bottom one should resemble that").
        // The 2026-08-16 "one similar UI for that pills" request was read BACKWARDS: it meant
        // the STATUS row should resemble THIS control, not that this control should become
        // pills. The pill pass shipped inverted for one day; this restores the segment
        // verbatim and gives the status row a smaller sibling of it (`viewBtn` below).
        //
        // A segment is three facts: WHAT pool, HOW MANY, WHOSE it is. Three attempts
        // at two lines each clipped one of them — scope+count clipped the number
        // ("everyone's · 2…"), name+count clipped the name ("Our …"), and count-with-scope
        // clipped the scope ("everyo… 22659"). At ~125px per segment there is simply not
        // room for two of these side by side at a readable size.
        //
        // So each fact gets its own line. Vertical space in this rail is cheap; horizontal
        // space is the scarce thing, and stacking is what stops the control from having to
        // choose which of its three facts to destroy.
        const poolSeg = (k, label, sub, n, on, onClick, onHover) => (
          <button key={k} type="button" onClick={onClick} onMouseEnter={onHover}
            className={`flex-1 min-w-0 text-left px-2.5 py-2 transition-colors ${k === 'pool-work' ? 'border-l border-border' : ''} ${
              on ? 'bg-foreground' : 'hover:bg-accent'}`}>
            <span className={`block truncate text-[13px] leading-[16px] ${on ? 'font-semibold text-background' : 'text-foreground/80'}`}>{label}</span>
            {n != null && <span className={`block truncate text-[14px] leading-[19px] tabular-nums ${on ? 'font-semibold text-background' : 'text-foreground'}`}>{n}</span>}
            <span className={`block truncate text-[10px] leading-[14px] ${on ? 'text-background/60' : 'text-muted-foreground'}`}>{sub}</span>
          </button>
        )
        // Views of the CHOSEN pool — never object types. Active/Archive keep rendering a
        // literal 0 (owner ruling): an empty archive is a fact, a missing one is a mystery.
        // ── SMALLER SIBLING OF THE POOL SEGMENT (owner, 2026-08-16/17) ──────────────
        // Same anatomy as poolSeg — joined segments in a bordered box, label over count,
        // selected = inverted — at reduced scale and without the third (WHOSE) line, which
        // belongs to the pool alone. The status row's container supplies the box; `first:`
        // handles the divider so any number of segments compose. If this needs a tweak,
        // keep it a scaled poolSeg — the resemblance IS the request.
        const viewBtn = (k, label, n, on, onClick, onHover) => (
          <button key={k} type="button" onClick={onClick} onMouseEnter={onHover}
            className={`flex-1 min-w-0 text-left px-2.5 py-1.5 transition-colors border-l border-border first:border-l-0 ${
              on ? 'bg-foreground' : 'hover:bg-accent'}`}>
            <span className={`block truncate text-[12px] leading-[15px] ${on ? 'font-semibold text-background' : 'text-foreground/80'}`}>{label}</span>
            {n != null && <span className={`block truncate text-[13px] leading-[17px] tabular-nums ${on ? 'font-semibold text-background' : 'text-foreground'}`}>{n}</span>}
          </button>
        )
        const ldv = category.lead
        // { active, archived } | null. NULL means "not arrived / failed", and the pill then
        // renders NO number rather than 0 — same rule the corpus total follows (B321): a
        // count that shows 0 because a request failed is a lie the agent cannot detect.
        const workCounts = category.workCounts || null
        // ── THE STATE AXIS IS BACK IN THE POOL BOX (owner, 2026-08-18) ───────────────────
        // Earlier the same day the designer's Call B moved it down into a `status` facet
        // group, to shrink a 309px pinned block on the phone (the pool box was 207px of it).
        // The owner used it and ruled the other way: the state row belongs up top, beside the
        // pool it describes — it was "moved below" and had to be found. So it renders inside
        // poolBox again, and the `status` facet entry is gone (it existed only to carry this
        // box; a heading over nothing must not render — see the splice below the facets array).
        // The designer's px measurement stands; if the pinned height hurts again on mobile,
        // the answer has to keep the row reachable without scrolling, not re-bury it.
        // ⚠️ BUILT HERE, PLACED BY WHOEVER RENDERS IT — the same contract poolBox already has,
        // and for the same reason: viewBtn / ldv / leadsOn / workCounts all live in this closure,
        // and a second copy at panel scope would be a second definition of the state axis.
        // ⚠️ Its guard travels WITH it. ClientLeads has no such lifecycle, and a SNAPSHOT_LENS
        // replaces the pipeline rather than filtering it, so in both the buttons were lit,
        // clickable and incapable of changing a row. Null here means the row is absent from
        // the pool box entirely — not an empty labelled row.
        // ── B832: THE ROW DISAPPEARED, AND NOBODY SAID WHY (owner, 2026-08-19) ──────────
        // Reported as a MOBILE bug — *"move active and archive right after leads and our work
        // as it was before and as it is now on desktop"* — and it is not one. Verified in the
        // owner's own browser at 1908px: switching to კლიენტები makes the pair vanish on the
        // DESKTOP too. Nothing in this path is device-gated (the only `PANEL_IS_MOBILE` use
        // near it is `{!PANEL_IS_MOBILE && poolBox}`, which merely stops the desktop drawing a
        // box the phone pins instead), so both surfaces render this same element in the same
        // place. The absence is POOL-dependent and it was already deliberate.
        // What was wrong is that it was SILENT. The note below rightly rejected "an eyebrow
        // over two dead buttons" and "an empty labelled row" — but there is a third option it
        // predates: name the absence, which is exactly what the facets started doing the same
        // day (UnavailableFacet). One idiom for "this question is not asked here", used in
        // both places, so a vanished control is never left to be discovered.
        const stateNoneReason = cur === 'ClientLeads' ? t('desk_pool_state_none_clientlead')
          : SNAPSHOT_LENS.has(cur) ? t('desk_pool_state_none_lens') : null
        const stateBox = stateNoneReason ? (
              <div className="border-t border-border/50 pt-2">
                <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground/70">
                  {t('desk_pool_state')}
                </div>
                {/* Dashed and uninteractive, like UnavailableFacet: it states that the axis
                    does not exist here without offering a control that would filter the wrong
                    table (property_lead.status is a vocabulary a requirement does not have). */}
                <div className="rounded-lg border border-dashed border-border/70 px-2 py-1.5 text-[11px] leading-4 text-muted-foreground/70">
                  {stateNoneReason}
                </div>
              </div>
        ) : (
              <div className="border-t border-border/50 pt-2">
                <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground/70">
                  {t('desk_pool_state')}
                </div>
                <div className="flex rounded-lg border border-border overflow-hidden">
                  {/* Same question, two stores: `property_lead.status` in Leads, the entry
                      `statusFilter` in our work. BOTH are now a true binary — a lead is
                      archived or it is not, and since B576 so is a property.
                      📌 This comment used to argue the opposite ("work carries four values…
                      `all` is explicit"), and it was right when four states existed: a
                      two-value row would then have silently deleted two reachable ones.
                      The owner retired `ყველა`/`დახურული` on 2026-08-16 after the cost was
                      MEASURED rather than guessed — see the note on the work row below for
                      the numbers and for the gap that retirement leaves open. */}
                  {/* `desk_lead_active` reads "Active leads" — inside the Leads pool, under a
                      STATUS eyebrow, that says "leads" three times over (owner: "under leads
                      category active is enough"). The pool names the inventory; this row
                      names the state. Both pools now use the same neutral word. */}
                  {leadsOn && ldv && viewBtn('v-active', t('desk_entry_filter_active'), ldv.count ?? null,
                    !ldv.value, () => ldv.set(false), ldv.value ? ldv.prefetchOther : undefined)}
                  {leadsOn && ldv && viewBtn('v-archive', t('desk_lead_archive'), ldv.archiveCount ?? null,
                    ldv.value, () => ldv.set(true), ldv.value ? undefined : ldv.prefetchOther)}
                  {/* ── TWO STATES, WITH NUMBERS (owner, 2026-08-16) ────────────────────
                      Was four unlabelled buttons — ყველა · აქტიური · დახურული · არქივი —
                      and NONE of them carried a count, because this row filtered
                      already-loaded rows client-side and a client-side predicate can only
                      count the page it was handed. Both halves are fixed together: the axis
                      is now server-side (0600) and the numbers come from the same function
                      the feed and the map read, so each number equals what its click
                      delivers (B273).
                      ⚠️ `ყველა` and `დახურული` are RETIRED on the owner's explicit ruling.
                      Measured before retiring them, so the cost was known and not guessed:
                      `დახურული` meant closed CLIENTS (not closed properties — that surprised
                      both of us), 5 exist, and the filter could only ever see ONE of them
                      because it keyed on a proposal kind written 3 times ever, last on
                      2026-07-02. It was a control that showed 1 of 5. `ყველა` was the only
                      way to see both piles at once and has no meaning once there are two.
                      🔴 KNOWN GAP, filed not hidden: those 5 closed clients and the 9
                      dismissed drafts are no longer reachable FROM THIS ROW. They were not
                      folded into არქივი on purpose — the archive count is properties, and a
                      count that says 20 while the list shows 34 rebuilds the exact defect
                      this change removes. See B576. */}
                  {!leadsOn && [['active', 'desk_entry_filter_active', workCounts?.active],
                                ['archived', 'desk_lead_archive', workCounts?.archived]]
                    .map(([v, key, n]) => viewBtn(`w-${v}`, t(key), n ?? null, statusFilter === v,
                      () => onStatusChange(v),
                      // 2026-09-24: hovering the OTHER status warms its page 0 (warmFeedPage in IntakeTab).
                      statusFilter === v ? undefined : category?.warmStatus ? () => category.warmStatus(v) : undefined))}
                </div>
              </div>
        )
        // ── THE POOL: which inventory am I searching? ─────────────────────────────
        // Built here, placed by whoever renders it (see the entry's `poolBox` below).
        const poolBox = (
          <div className="-mx-0.5 mb-2 rounded-[10px] border border-border bg-muted/30 p-2.5 space-y-2">
              <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                {t('desk_pool_searching_in')}
              </div>
              <div className="flex rounded-lg border border-border overflow-hidden">
                {poolSeg('pool-leads', t('desk_type_leads'), t('desk_pool_everyones'),
                  category.lead?.count ?? null, leadsOn,
                  // Hover-prefetch: warm the Leads page-0 snapshot before the click lands
                  // (desktop only by nature — touch has no hover; the SWR revalidate on
                  // mount keeps this safe to fire with any pending state).
                  pick(['Leads'], category.lead?.count ?? null), category.lead?.prefetch)}
                {/* ⚠️ THE LABEL FOLLOWS THE SCOPE, NOT THE JOB TITLE (owner, 2026-08-02:
                    "when agent is logged in they see only theirs and it's called MINE;
                    when admin or manager is logged in they see our work and it's
                    everyone's"). An agent is always scoped to themselves, so "Our work"
                    told them they were looking at the agency's inventory when they were
                    looking at their own — the same word covering two universes, which is
                    the exact error the sub-line exists to prevent.
                    Keying on scope rather than on role also fixes the case role-keying
                    would miss: a manager who unticks "Everyone's work" is scoped to
                    themselves too, and must read "Mine" like anyone else. The label
                    always describes what is on screen. */}
                {poolSeg('pool-work',
                  category.scope?.value === 'all' ? t('desk_facet_ourwork') : t('desk_pool_mine'),
                  category.scope?.value === 'all'
                    /* ⚠️ B1297 — "everyone's" is FALSE for a manager: their 'all' is one office,
                       capped server-side. The segment's own header says it "states whose
                       universe it is", so it has to name the office rather than the agency.
                       Falls back to "everyone's" for admin/GM, whose 'all' really is everyone. */
                    ? (category.scope?.allLabel || t('desk_pool_everyones'))
                    : t('desk_pool_yours'),
                  allN, workOn,
                  // Leaving Leads lands on "all types". Already inside our work → no-op, so
                  // clicking the segment you are on never discards the object you chose.
                  () => { if (!workOn) pick([], allN)() })}
              </div>
              {/* ── ONE ROW PER AXIS, EACH NAMED ──────────────────────────────────
                  This box had grown four different questions stacked without labels —
                  which pool, which lifecycle state, marked-by-me, and whose work — and
                  the owner's verdict was the right one: it made no sense. Collapsing
                  unrelated axes into one undifferentiated control is the exact failure
                  this whole restructure exists to undo; it had simply reappeared one
                  level down, inside the fix.
                  So: a hairline and a name for every axis. The eyebrow is what makes it
                  readable — without it, "All Active Closed Archived ♥ Favorites" is one
                  run-on row of six unrelated words. */}
              {/* ⚠️ HIDDEN ENTIRELY for client leads, not merely emptied. `leadsOn` now
                  covers both lead types, and active/archive here reads `property_lead.status`
                  — a vocabulary a requirement does not have. Left as-is it would render an
                  eyebrow over two dead buttons that silently filter the wrong table, or an
                  empty labelled row, which reads as a broken control rather than an absent
                  question. A client lead's only lifecycle IS being unclaimed: the moment it
                  is picked up it leaves this pool by definition. */}
              {/* ⚠️ SNAPSHOT_LENS joined this test 2026-08-09 (owner: "selecting favorites and
                  then status don't change anything"). ♥/🗄 replace the pipeline rather than
                  filter it, so these four buttons were pure decoration there — clickable, lit,
                  and incapable of changing a single row. Exactly the case the comment above
                  describes for ClientLeads. See the SNAPSHOT_LENS declaration. */}
              {stateBox}
              {/* ♥ is not a lifecycle state and must not sit in that row: it is a mark I
                  put on a thing, not something the thing is doing. Its own axis, its own
                  line. */}
              {/* ── ♥ / 🗄 LEFT THIS PANEL, 2026-08-09 (owner) ────────────────────────────
                  *"Don't you think we need to place favorites and hidden somewhere else? I guess
                  it's something different than a filter."* Correct, and it is the root of B625:
                  every other control here asks a question about the INVENTORY — its status, its
                  city, its district, its price — while these two are facts about the VIEWER.
                  Nothing in the corpus changes when you favourite something, which is exactly
                  why every axis went inert the moment one was clicked: the panel had no
                  inventory question left to answer.
                  They now live in the app header beside the bell, where per-agent state already
                  lives. ♥ is a shortlist and a destination is the right shape for it; 🗄 also
                  got the inline "N hidden here" line on the feed, because a suppression's job is
                  to change what the MAIN feed shows and it should say so there.
                  ⚠️ `category.favCount` / `category.archCount` are still passed by both call
                  sites and are still read — `allN` excludes them from "all types". Do not delete
                  the props along with the buttons. */}
              {/* Whose work — a MANAGER-ONLY row (design 2b §1). For everyone else it is
                  not rendered at all: no disabled control, no explanation. A greyed row an
                  agent can never use reads as a permission error, and this is not one —
                  see the scope comment at the listingsScope declaration.
                  ⚠️ Also hidden when the account cannot be scoped to ITSELF (an admin with
                  no roster row). Offering the toggle there produced a "Mine" that returned
                  zero properties because the question was unaskable, not because the answer
                  was zero — a control whose off-state is a false empty is worse than no
                  control. */}
              {/* ── WHOSE, in the Leads pool. The same axis the manager checkbox answers for
                     our work: "everything, or only mine?" It sits on its own line for the
                     same reason every other axis does.
                     It may finally be called **Working**. Until 0362 this filtered
                     `lead_claim` — an INTENTION, 3 rows, whose write gesture 0205 had already
                     deleted — while wearing the word this system uses for work that actually
                     happened. It now filters the `represents_property` edge the five work
                     triggers write (cooperation · agent restriction · meeting · repost ·
                     contact add), so the label is earned rather than borrowed.
                     Measured before wiring: 4,517 edges over 28 agents; the lens returns
                     115 / 74 / 98 / 44 / 82 for the five busiest, and 0 for an agent with
                     none. A real set, unlike the one it replaces.
                     ⚠️ Only meaningful across the ACTIVE statuses. Against `open` alone it
                     returns ~0 for everyone — working a lead moves it out of `open` — which
                     would read as a broken filter. Active is open/working/engaged, so this is
                     right by construction; do not narrow the status set under it. */}
              {/* ── REMOVED 2026-08-09 (owner): "this is totally useless, we already have my
                     work — I'm working on it right beside Leads."
                     MEASURED before removing, because the comment above argued it was a real
                     set: of the 1,457 rows the lens returns, **1,451 (99.6%) are already in the
                     agent's own "our work" pool** (`agent_cluster_member`). That is not a
                     coincidence — "our work" is *authored ∪ captured ∪ worked*, and this lens
                     filters the `represents_property` edge, i.e. the same word in both
                     definitions. The pool switch two rows up already asks "leads, or our work?";
                     this checkbox re-asked the pool question INSIDE one of its own answers,
                     which is the exact disease this panel keeps being restructured to cure.
                     ⚠️ The 6 rows that did NOT overlap are not a justification for keeping it —
                     they are a small hole in "our work" (a property an agent demonstrably works
                     that their own pool omits), filed separately. Fix the pool, do not keep a
                     second control whose only remaining value is accidentally revealing the bug.
                     The `mine`/`workedByMe` plumbing stays on LeadsFeed's props exactly as the
                     note there already intends, for the day a `Claimed` control is built. */}
              {category.scope?.canAll && workOn && (
                <div className="border-t border-border/50 pt-2">
                  <label className="flex items-center gap-2 text-[11px] leading-[15px] text-muted-foreground hover:text-foreground cursor-pointer">
                    <input type="checkbox" checked={category.scope.value === 'all'}
                      onChange={e => category.scope.set(e.target.checked ? 'all' : 'mine')} />
                    <span>{t('desk_scope_everyones')}</span>
                  </label>
                </div>
              )}
          </div>
        )
        return {
          id: 'category', title: t('desk_facet_category'), badge: category.value.length ? 1 : null,
          poolBox,
          body: (
          <div className="space-y-0.5">
            {/* On mobile this same element is pinned in the filter page's header instead
                (design 2c: the pool must be reachable without scrolling the filter list),
                so rendering it here too would put two copies of one control on one screen. */}
            {!PANEL_IS_MOBILE && poolBox}
            {/* ── WHAT KIND OF THING, INSIDE LEADS (owner ruling 2026-08-06) ──────
                The SAME shape our work uses below: a row per type, each with its own
                count, children nested under the selected one. Not a separate control
                grammar — an agent should not learn two layouts for "what I own" and
                "what I could own".

                ⚠️ First built as a horizontal button row under a "ტიპი" eyebrow. Owner
                rejected it on sight: it looked nothing like the tree three inches
                below it. Same information, wrong dialect.

                Objects carries the pool total (the same number the Leads segment
                shows, deliberately — here it is a PARENT of the intent rows beneath
                it, which is a different claim than the segment's). Clients carries
                its own, because nothing else on screen states it.

                🔴 identitySub() UNDER Objects closes a live regression, not just a
                design wish. Stage 2 (2026-08-01) moved Deal and Type OUT of the
                desktop accordion on the rule that "identity is chosen once, in the
                tree" — but the tree was attached to `cur === 'Property'` only. Since
                that day an agent on Leads had no way to say "for sale" or "apartment"
                at all: the tree did not offer it and the facets had been taken away.
                Nothing server-side had to change (0357: the leads fork was absorbed
                into the main search function as `p_scope='leads'`, so leads always
                accepted the same intent/kind arguments — only the client declined to
                ask). Same tree, same sentence, whichever inventory you stand in.

                ⚠️ Its counts come from the LEAD universe, not the property one —
                `facetCounts.kinds` switches to the leads feed's own page-0 snapshot
                when Leads is selected. Rendering property tallies here would be the
                denominator bug this restructure exists to remove. */}
            {leadsOn && (<>
              {/* ── 🚨 THIS ROW MUST FOLLOW THE ARCHIVE TOGGLE (owner-reported, 2026-08-20) ──
                  It rendered `category.lead.count` — which is `leadCountActive`, the ACTIVE
                  total and ONLY the active total, whatever the agent had selected. Standing on
                  არქივი the panel read: არქივი **9,411** selected, feed header **9,411 ლიდი**,
                  kind children summing to exactly 9,411, intent children 4,287 / 5,096 / 93
                  (the archive answer, verified against the RPC) — and this row, their parent,
                  saying **30,423**. Every number on the panel was right except the one above
                  them, so the tree accused itself of being broken.
                  ⚠️ It is NOT the counts pipeline, and I chased that first: reading the live
                  React props showed `propertyTotal` already correct at 9,411 and `lead.value`
                  already `true`. The data arrived right and this row declined to read it —
                  `category.propertyTotal` IS `leadArchive ? leadCountArchive : leadCountActive`
                  (see treePropertyTotal), computed for exactly this purpose by the 2026-08-19
                  fix, which taught the OTHER properties row (the `kids` list) to follow the
                  children's chooser and left this one behind.
                  So it now reads the same number as its sibling. Third instance of one shape:
                  a parent in this tree quoting a different question than its children. */}
              {row(t('desk_type_properties'), category.propertyTotal ?? null,
                   cur === 'Leads', pick(['Leads'], category.propertyTotal ?? null), 1)}
              {cur === 'Leads' && identitySub()}
              {/* null while unfetched — the tree renders null as no number, which is the
                  honest face of "not arrived yet". "N+" once the RPC's 300-row count
                  saturates: a capped number drawn as a total is the 2026-08-05
                  `92 → 162 → 3141` failure in slow motion. */}
              {row(t('desk_type_client_leads'),
                   category.clientLead?.count == null ? null
                     : `${category.clientLead.count}${category.clientLead.capped ? '+' : ''}`,
                   cur === 'ClientLeads', pick(['ClientLeads'], null), 1)}
            </>)}
            {/* ── WHAT KIND OF THING — only inside our work. Flats, people, events and
                   business are four different KINDS OF RECORD, not four slices of one
                   inventory (57 is fifty-seven people, not fifty-seven flats), so they are
                   their own question and they never sit beside a pool count. Leads are all
                   properties, so this question does not arise there. */}
            {workOn && (<>
              {row(t('desk_search_all_types'), allN, cur === '', pick([], allN), 0)}
              {kids.map(c => (<div key={c.label} className="space-y-0.5">
                {row(c.label, c.n, cur === c.set[0], pick(c.set, c.n), 1)}
                {c.set[0] === 'Property' && cur === 'Property' && identitySub()}
                {c.set[0] === 'Meeting' && cur === 'Meeting' && meetingSub()}
              </div>))}
            </>)}
          </div>
          ),
        }
      })(),
    // ── Date facet (0249, 2026-07-29) ─────────────────────────────────────────────────
    // ONE range over ONE chosen dimension, answered whole-corpus by RPC v18.
    //
    // Placement is an owner ruling (2026-07-29): directly after Category, ahead of Agent
    // and every content facet. Same reasoning the Agent facet's own comment gives — these
    // are a different KIND of question. Category asks what kind of thing, Date asks WHEN,
    // and only then do Location/Deal/Type narrow which ones.
    //
    // ⚠️ The owner asked for "date added / scraped date" as if they were two things. They
    // are not: listing.listed_at and listing.created_at are EQUAL on 100% of 38,031 rows,
    // so a "scraped date" option would be a second name for the same number — and this
    // panel already has scars from numbers that describe something other than what they
    // sit next to. The four options below are four genuinely different questions, and the
    // list came from counting columns, not from guessing.
    //
    // ⚠️ "posted" is UNKNOWN on 14.2% of the corpus — MEASURED 2026-09-02 (40,312 of 46,969
    // active listings carry it). This comment said 87% until that day, which is the figure
    // INVERTED, and the error did not stay here: a designer read it and built a four-block
    // date panel around a middle column for the missing majority that does not exist. A
    // wrong number in a comment is a wrong number in the next design. Re-measure with
    // count(posted_at) before quoting it again.
    //
    // It is not a copy of listed_at either, which is the obvious way 85.8% could be fake:
    // 13,104 rows carry a posted_at more than a day BEFORE we first saw the ad, so the
    // column holds real independent observation. (23,981 land on the same day we captured
    // them, which is what catching a fresh ad looks like, not a defaulted value.)
    //
    // The unknowns go to the may-consider shelf rather than being dropped — the system says
    // "we don't know", which is true, instead of silently answering a narrower question.
    !clientView && {
      id: 'dates', title: t('desk_facet_dates'), defaultOpen: false,
      // The badge counts DIMENSIONS in play rather than showing a bare dot: with four of them
      // combinable, the useful question at a glance is 'how many date rules am I running',
      // which a dot cannot answer.
      badge: (() => {
        const n = Object.values(dateRanges).filter(r => r && (r.from || r.to)).length
        return n ? String(n) : null
      })(),
      // FACET 07B (owner design, 2026-09-06): "Four dates, one open" — ONE component draws the four dimensions
      // (src/components/desk/DatesFacet.jsx; scripts/render-dates-facet.mjs draws it in the gate). Counts came off
      // the buttons; the state (dateRanges / postedDim / setDateRange) is exactly what the old inline body wrote.
      body: (<DatesFacet dateRanges={dateRanges} setDateRange={setDateRange} postedDim={postedDim}
        onSubstitute={togglePostedSubstitute} t={tLog}
        postedEmptyPct={(() => {
          const u = poolCounts?.date_posted?.__unknown__, tot = poolCounts?.date_added?.all ?? poolCounts?.date_added?.['90'] ?? null
          return u != null && tot ? Math.round(100 * u / tot) : null
        })()} />),
    },
    // ── Lead exposure + read-state (B215, 2026-07-29) — LEADS ONLY ────────────────────
    // The six leadwork controls (two claim-count inputs + four view checkboxes, 0249/0253)
    // collapse to ONE stepped slider over the 0262 exposure ladder: 0 untouched / 1 viewed
    // / 2 worked / (3 = our reshare). AT-MOST semantics — a track with a handle makes the
    // threshold visible where pills would read as "exactly this rung" (owner steer).
    // Detents send 0/1/2; the far-right rest position is ყველა = no filter. There is
    // deliberately NO ≤3 detent: at-most-our-reshare IS the whole ladder, so it would be a
    // second "all" that routes the cheap bare-leads page through the search RPC for an
    // identical answer. The rung-3 word surfaces as the hint under the ≤2 stop instead —
    // that boundary (476 clusters, measured at deploy) is what the own-detent ruling bought.
    // Read-state stays its own toggle (ruling 3: it is about the VIEWER, not the market),
    // reduced to the _by_me pair — the company-wide seen/unseen question folded into
    // rung 1. ⚠️ The legacy claimsLt/claimsGt/seen VALUES stay committed, chipped and sent:
    // a saved search or shared URL carrying them must keep meaning (the B206 rule). Only
    // the controls are gone. Rendered only in the Leads category, same §12 reasoning.
    category && category.value[0] === 'Leads' && {
      // ── ჩვენი მუშაობა / OUR WORK (restructured 2026-07-30) ───────────────
      // Grouped by the DECISION, not by whose behaviour the data describes. An agent about
      // to dial does not separate "our actions" from "the owner's" — they ask two questions
      // in sequence: "am I about to duplicate a colleague's work?" and "is this worth my
      // time?". These are the first question. (Reviewed externally; the alternative split —
      // ours-vs-theirs — is clean for engineers and wrong for the person holding the phone.)
      // ORDER = strength of exclusion, strongest first: an existing cooperation stops most
      // agencies outright; repeated reposting means someone is actively handling it; a mere
      // "someone opened the card" means least of all.
      id: 'leadwork', title: t('desk_facet_ourwork'), defaultOpen: false,
      // Legacy keys still count toward the badge though their controls are gone (spread,
      // exposureMax, claims, seen): a saved search carrying one is still filtering, and a
      // facet showing no badge while silently filtering is the lie the badge exists to stop.
      badge: (filters.ownerEngaged ? 1 : 0)
        + (filters.reposts ? 1 : 0)
        + ((filters.footprint || []).length ? 1 : 0)
        + (filters.spread ? 1 : 0)
        + ((filters.exposureMax ?? '') !== '' ? 1 : 0)
        + ((filters.claimsLt ?? '') !== '' ? 1 : 0) + ((filters.claimsGt ?? '') !== '' ? 1 : 0)
        + (filters.seen ? 1 : 0) || null,
      body: (<>
        {/* 1. Owner relationship — the strongest exclusion (333 clusters have one). */}
        <p className="text-xs text-muted-foreground mb-1">{t('desk_engaged_label')}</p>
        {/* no_wa (core 4201, owner 2026-11-03): owners our WhatsApp lines cannot reach — the list worked by phone. */}
        {['no', 'yes', 'no_wa'].map(v => (
          <label key={v} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground cursor-pointer py-0.5">
            <input type="checkbox" checked={filters.ownerEngaged === v}
              onChange={() => set('ownerEngaged', filters.ownerEngaged === v ? '' : v)} />
            <span className="flex-1 truncate">{t('desk_engaged_' + v)}</span>
          </label>
        ))}
        {/* 2. Our reposts — none / 1-2 / 3+ (18,987 / 121 / 356 on the live leads pool). */}
        <p className="text-xs text-muted-foreground mt-2 mb-1">{t('desk_reposts_label')}</p>
        {['0', '1_2', '3p'].map(v => (
          <label key={v} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground cursor-pointer py-0.5">
            <input type="checkbox" checked={filters.reposts === v}
              onChange={() => set('reposts', filters.reposts === v ? '' : v)} />
            <span className="flex-1 truncate">{t('desk_reposts_' + v)}</span>
          </label>
        ))}
        {/* 3. Has anyone here opened it. ⚠️ The 'reposted' state is GONE from this control:
            it selected exactly the same 477 clusters as "our reposts >= 1" (verified, zero
            difference either way), and the repost control says the same thing plus HOW MANY.
            A count of zero already means "not reposted", so nothing is lost. What remains is
            conceptually clean — these are EXPLORATION states, the control above is
            PUBLICATION states. The value stays accepted server-side for saved searches. */}
        <p className="text-xs text-muted-foreground mt-2 mb-1">{t('desk_footprint_label')}</p>
        {['none', 'viewed'].map(v => (
          <TriRow key={v} className="text-sm" state={facetStateOf(filters, 'footprint', v)} onCycle={() => cycleFacet('footprint', v)}
            onName={() => nameFacet('footprint', v)} label={t('desk_footprint_' + v)} />
        ))}
        {/* 0431 — "…except the ones I work" (owner ruling 2026-08-06, on outside advice).
            The rows above are AGENCY-WIDE and stay that way: the moment "me" enters a
            NUMBER, the thing measured stops being a fact about the property and becomes a
            fact about a relationship, and one number for both is the district-count failure
            again. So "me" is a checkbox BESIDE them, never a second set of states.
            Without it, an agent hunting untouched leads loses their own worked ones the
            moment they touch them — measured at 82 per agent on average, 122 at worst.
            ⚠️ A MODIFIER: on its own it selects nothing (the RPC ignores it unless a
            footprint value is set), which is why it is absent from advFiltersActive and
            why it renders indented under the rows it qualifies rather than beside them. */}
        <label className={`flex items-center gap-2 text-sm cursor-pointer py-0.5 ml-5 ${(filters.footprint || []).length ? 'text-muted-foreground hover:text-foreground' : 'text-muted-foreground/40'}`}>
          <input type="checkbox" checked={!!filters.keepMine}
            disabled={!(filters.footprint || []).length}
            onChange={e => set('keepMine', e.target.checked)} />
          <span className="flex-1 truncate">{t('desk_footprint_keep_mine')}</span>
        </label>
      </>),
    },
    // ── ლიდის მდგომარეობა / LEAD STATUS ───────────────────────────
    // The second question: "how promising is this opportunity?" DEFAULT OPEN, alone among
    // the lead facets — after price/district/type have narrowed the inventory the next thing
    // an agent wants is fresh opportunities, not internal workflow. "Our work" only becomes
    // useful once a shortlist exists.
    // ⚠️ NOT called "motivation" any more: none of these MEASURE motivation. Days listed,
    // VIP and portal count are proxies for it, and naming a section after the hidden state
    // would claim knowledge we do not have.
    // B921: was `category.value[0] === 'Leads' && {…}`. Every control in this section works
    // on the our-work pool — measured against the live RPC on `p_scope => 'agent'`:
    // baseline 704 clusters, `age_max_days=30` → 260, `promoted='vip'` → 483,
    // `owner_platforms_min=2` → 10, `reposts_min=1` → 608. Nothing here reads a server
    // tally (the badge counts `filters.*`), so unlike the portal facet it needed no
    // count plumbing to become honest.
    category && {
      id: 'leadseller', title: t('desk_facet_leadstate'), defaultOpen: true,
      // viewsMin/sellerType still badge though their controls are gone — same rule as above.
      badge: ((filters.ageMinDays ?? '') !== '' || (filters.ageMaxDays ?? '') !== '' ? 1 : 0)
        + (filters.ownerPlatforms ? 1 : 0)
        + (filters.promoted ? 1 : 0)
        + (filters.sellerType ? 1 : 0)
        + ((filters.viewsMin ?? '') !== '' ? 1 : 0) || null,
      body: (<>
        {/* 1. Freshness dominates outbound hunting. From listing.posted_at — NOT listed_at,
            which is our own first-sight date and reads the whole corpus as under a month
            old. 1,413 clusters have no posting date and shelve rather than hide. */}
        <p className="text-xs text-muted-foreground mt-1 mb-1">{t('desk_range_age_days')}</p>
        {AGE_PRESETS.map(p => {
          const on = String(filters.ageMinDays ?? '') === p.lo && String(filters.ageMaxDays ?? '') === p.hi
          return (
            <label key={p.key} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground cursor-pointer py-0.5">
              <input type="checkbox" checked={on}
                onChange={() => {
                  // Clicking the active band clears it — same toggle-off as every other
                  // single-select row in this panel (portals, VIP), so the idiom is one.
                  set('ageMinDays', on ? '' : p.lo)
                  set('ageMaxDays', on ? '' : p.hi)
                  if (!on) setAgeCustom(false)
                }} />
              <span className="flex-1 truncate">{t('desk_age_preset_' + p.key)}</span>
            </label>
          )
        })}
        {/* Custom stays visible once a hand-typed range is in force, or the control would
            hide the filter that is actually running — the same rule the sort menu's advanced
            fold follows. */}
        <label className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground cursor-pointer py-0.5">
          <input type="checkbox" checked={ageCustomOpen}
            onChange={() => {
              // Clears the bounds in BOTH directions. Opening Custom on top of an active
              // preset would otherwise leave two boxes ticked for one filter — and inheriting
              // the preset's numbers is not a kindness, since the reason to open Custom is
              // that none of them was the answer.
              set('ageMinDays', ''); set('ageMaxDays', '')
              setAgeCustom(!ageCustomOpen)
            }} />
          <span className="flex-1 truncate">{t('desk_age_preset_custom')}</span>
        </label>
        {ageCustomOpen && (
          <div className="mt-1">
            <MiniRange label={t('desk_age_unit')} lo={filters.ageMinDays} hi={filters.ageMaxDays}
              onLo={v => set('ageMinDays', v)} onHi={v => set('ageMaxDays', v)} />
          </div>
        )}
        {/* ⚠️ The kind is only meaningful beside a range, so it is only shown beside one —
            alone it is a modifier with nothing to modify (the B177 move_in_hard rule). */}
        {((filters.ageMinDays ?? '') !== '' || (filters.ageMaxDays ?? '') !== '') && (
          <div className="mt-1">
            <p className="text-xs text-muted-foreground mb-0.5">{t('desk_age_kind_label')}</p>
            {AGE_KINDS.map(v => (
              <TriRow key={v} className="text-sm" state={facetStateOf(filters, 'ageKinds', v)} onCycle={() => cycleFacet('ageKinds', v)}
                onName={() => nameFacet('ageKinds', v)} label={t('desk_age_kind_' + v)} />
            ))}
            <p className="text-xs text-muted-foreground mt-0.5">{t('desk_age_kind_hint')}</p>
          </div>
        )}
        {/* 2. How hard the OWNER is pushing — moved here from Exposure, where it never
            belonged: this describes the owner's effort, not our footprint. Ours excluded, or
            our own reposting would inflate "the owner's" reach. 17,160 / 2,083 / 232. */}
        <p className="text-xs text-muted-foreground mt-2">{t('desk_owner_platforms_label')}</p>
        {/* The label is the question an agent actually asks; this line carries the one thing
            the question omits. Burying "excluding ours" in the label would make it scan like
            a column name again — which is the whole failure mode this pass is fixing. */}
        <p className="text-[11px] leading-snug text-muted-foreground/70 mb-1">{t('desk_owner_platforms_note')}</p>
        {['1', '2', '3p'].map(v => (
          <label key={v} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground cursor-pointer py-0.5">
            <input type="checkbox" checked={filters.ownerPlatforms === v}
              onChange={() => set('ownerPlatforms', filters.ownerPlatforms === v ? '' : v)} />
            <span className="flex-1 truncate">{t('desk_owner_platforms_' + v)}</span>
          </label>
        ))}
        {/* 3. Weakest of the three, deliberately last: paying for VIP is a hint, not a
            reliable signal — some owners buy it on day one, most never do. 0228's column
            contract makes NULL "not captured", never "not promoted", so 9,454 uncaptured
            clusters shelve rather than counting as "not promoted". */}
        <p className="text-xs text-muted-foreground mt-2 mb-1">{t('desk_promoted_label')}</p>
        {['yes', 'no'].map(v => (
          <label key={v} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground cursor-pointer py-0.5">
            <input type="checkbox" checked={filters.promoted === v}
              onChange={() => set('promoted', filters.promoted === v ? '' : v)} />
            <span className="flex-1 truncate">{t('desk_promoted_' + v)}</span>
          </label>
        ))}
        {/* ── CUT 2026-07-30: "seller holds N apartments" ────────────────────────
            It failed the test every control must pass — "would I ever choose this over a
            filter I already have?". To avoid professionals an agent unticks korter and bog
            in the PORTAL facet and removes almost the entire professional inventory with
            complete coverage; this control's coverage is derived and partial. Measured:
            myhome 4.9% / ss.ge 2.5% professional at capture (discovery already fetches
            private individuals only), so the residue it could catch is a few percent.
            A control holding permanent panel space for an edge case is what makes a filter
            panel feel heavy for reasons users cannot name. sellerType stays accepted and
            badged so saved searches keep meaning. */}
      </>),
    },
    {
      // ── განცხადების ხარისხი / ADVERT QUALITY (owner-asked 2026-08-22) ──────
      // Its OWN section rather than a row inside ლიდის მდგომარეობა, because it answers a
      // different question. That facet asks "how hot is this lead" — freshness, the owner's
      // reach, VIP — which is market pressure. This one asks "is this advert workable",
      // which is completeness. And it is the only facet judged on the OWNER's advert alone:
      // an agent may repost a bare advert with photos of their own, and the owner ruled that
      // must not clear the flag, so the footnote below is load-bearing, not decoration.
      //
      // ORDER = what an agent notices first. A card with no photograph is unusable at a
      // glance; a thin gallery is workable but weak; a missing bedroom count is only visible
      // once you open it.
      //
      // ⚠️ "Bad images" is DELIBERATELY ABSENT. The vocabulary exists (media_mark:
      //    portal_logo / phone_number / agency_mark / camera_stamp) but covers 15,301 of
      //    696,358 photographs — 2.2%. A control on it would answer confidently over 2% of
      //    the corpus and pass the rest in silence, which is the "a number you pay for must
      //    have something behind it" rule. It ships when coverage is real, not before.
      // Body and badge both live in AdvertQualityFacet.jsx — the Desk is login-gated, so
      // the only way to SEE this drawn is harness.html, and a harness rendering a copy of
      // the JSX would drift from what agents get. One definition, two mount points.
      id: 'advert_quality', title: t('desk_facet_advert_quality'), defaultOpen: false,
      badge: advertQualityBadge(filters),
      body: <AdvertQualityFacet filters={filters} set={set} t={t} />,
    },
    // Agent — ADMIN/GM ONLY (owner 2026-07-28: "only admin uses filter by agent … move
    // that filter inside DETAILED SEARCH at best place possible"). It sits directly after
    // Category and before every content facet because it is a different KIND of question:
    // Category asks what kind of thing, Agent asks whose work, and only then do Location/
    // Deal/Type narrow which ones. It was a toolbar pill, competing for width with sort
    // and period on every screen while being useful to two people in the agency.
    // `agentFilter` is null for everyone else, so the facet simply does not exist for them.
    agentFilter && {
      id: 'agent', title: t('desk_facet_agent'), badge: agentFilter.selected.length || null,
      body: (
        <AgentFacetList agents={agentFilter.agents} selected={agentFilter.selected} counts={agentFilter.counts}
          excluded={filters.agentIdsNot || []}
          onName={id => nameFacet('agentIds', id)}   // F-050b: the name deselects
          onToggle={id => id !== null && facetStateOf(filters, 'agentIds', id) !== null
            ? cycleFacet('agentIds', id)                                   // B1213: ✓ → ✕ → neutral through the spec's cycle
            : agentFilter.onChange(
            id === null ? []
              : agentFilter.selected.includes(id)
                ? agentFilter.selected.filter(x => x !== id)
                : [...agentFilter.selected, id])} />
      ),
    },
    // Portal source — LEADS ONLY (owner 2026-07-23: "in leads search we also need a
    // source filter, in დეტალური ძიება after category"). Sits right after Category and
    // only renders while the Leads category is active — the main scopes' RPCs don't
    // accept p_sources yet (0190 forked the leads family only; B128 folds it in).
    // Values are the CANONICAL listing.source strings; labels are the platform names.
    // ⚠️ The entry is ALWAYS produced now (design 2b §3, 2026-08-05). Until today it was
    // `category.value[0] === 'Leads' && {…}`, i.e. a falsy hole in the array outside the
    // Leads pool — the control did not become unavailable, it ceased to exist. That is the
    // one thing the brief forbids: "Never remove a control silently." A hole in the array is
    // reachable by nothing downstream — not the panel, not the sheet, not any explanation.
    // Portal is not capability-blocked (no identity makes a portal meaningless) — it is
    // POOL-blocked. Since 2026-08-19 both absences render the same way, as a dashed
    // `UnavailableFacet` row in the facet's own position; only the sentence differs. This
    // entry supplies its own (`desk_facet_source_unavailable`, "leads only"), and
    // `refusalReason` supplies the identity one — hence the `f.unavailable ||` in facetList,
    // which lets a registry entry keep its more specific wording.
    category && {
      id: 'lead_sources', title: t('desk_facet_source'),
      // B921: NO LONGER POOL-BLOCKED. B915 (2026-08-22) fixed both halves this facet was
      // waiting on — `src/db/core.js` now sends `sources` on every scope, and the server's
      // `search_filters.js` emits `p_sources` on every scope — so the filter works on
      // our-work exactly as on leads. VERIFIED END TO END on the live Desk before removing
      // the gate, with a PAIRED control on one observable: `?type=Property&sources=facebook`
      // (whitelisted, this agent owns none) renders the empty state, `&sources=ss.ge`
      // renders 43 rows. A filtered-vs-unfiltered comparison would NOT have proved this —
      // see the note on the drop below for the control that fooled me first.
      badge: (filters.sources || []).length || null,
      unavailable: null,
      body: (
        <div className="space-y-0.5">
          {/* 0229 exact-preview count: distinct clusters this portal would return.
              Buckets overlap (a cluster can live on two portals) — never sum them. */}
          {[...SOURCE_FACET_OPTIONS.map(([code]) => code), SOURCE_FACET_OURS].map(code => (
            <TriRow key={code} className="text-sm" state={facetStateOf(filters, 'sources', code)} onCycle={() => cycleFacet('sources', code)}
              onName={() => nameFacet('sources', code)} label={sourceFacetLabel(code, bookScope)} count={sourceFacetCount(facetCounts?.sources, code)}
              title={code === SOURCE_FACET_OURS ? t('desk_source_sentavus_hint') : undefined} />
          ))}
        </div>
      ),
    },
    // 1685 (owner 2026-09-21) — SELLER and CONTACT: whose property, and who handles it. Two questions, two facets,
    // right after Sources (where it is listed) — the third question. Include-only; see desk/SellerFacets.jsx.
    category && {
      id: 'seller', title: t('desk_facet_seller_whose'), defaultOpen: false,
      badge: ((filters.sellerKinds || []).length + (filters.sellerIds || []).length) || null,
      body: <SellerFacet filters={filters} setFilters={setFilters} t={t} />,
    },
    category && {
      id: 'contact', title: t('desk_facet_contact'), defaultOpen: false,
      badge: ((filters.contactRoles || []).length + (filters.agentRepresents || []).length) || null,
      body: <ContactFacet filters={filters} setFilters={setFilters} t={t} />,
    },
    // 0779/0780 — "როგორ დაემატა", the second half of the owner's ask and a DIFFERENT question
    // from წყარო directly above it. They sit adjacent on purpose: one asks where the advert is
    // listed, the other how it reached us, and seeing them together is what makes the difference
    // legible. Separating them is how Facebook came to appear in the portal list twice.
    // ⚠️ RADIO, NOT CHECKBOXES: there are two values, so selecting both is selecting nothing —
    // a checklist would invite an agent to tick both and get the unfiltered corpus back while
    // believing they had filtered.
    category && {
      id: 'added_how', title: t('desk_facet_added_how'), defaultOpen: false,
      badge: filters.addedHow ? '•' : null,
      body: (
        <div className="space-y-0.5">
          {ADDED_HOW_OPTIONS.map(([code, labelKey]) => {
            const on = filters.addedHow === code
            return (
              <button key={code} type="button" aria-pressed={on}
                onClick={() => setFilters(f => ({ ...f, addedHow: f.addedHow === code ? '' : code }))}
                className={`w-full flex items-center gap-2 text-sm py-0.5 text-left max-md:min-h-11 ${on ? 'text-foreground font-medium' : 'text-muted-foreground hover:text-foreground'}`}>
                <span className={`h-4 w-4 shrink-0 rounded-full border flex items-center justify-center ${on ? 'border-primary bg-primary/15' : 'border-input'}`}>
                  {on && <span className="h-1.5 w-1.5 rounded-full bg-primary" />}
                </span>
                <span className="flex-1 truncate">{t(labelKey)}</span>
              </button>
            )
          })}
          {/* The distinction is not self-evident from the two labels alone — an agent reading
              "captured by an agent" beside a portal list can reasonably think it means the ad
              is ON that portal. One line, stated where the choice is made. */}
          <p className="text-[11px] leading-snug text-muted-foreground pt-1">{t('desk_added_how_note')}</p>
        </div>
      ),
    },
    // Location leads (2026-07-12 ruling stands: geography is what an agent narrows FIRST).
    // Counts + gray-out (0056) ride along unchanged, on checkbox rows.
    geo && {
      id: 'location', title: t('desk_facet_location'), badge: DISTRICT_KEYS.reduce((s, k) => s + (filters[k] || []).length, 0) || null,
      body: (<>
        <select className={input} value={geo.city} onChange={e => geo.onCity(e.target.value)}>
          <option value="all">{t('desk_search_all_cities')}</option>
          {geo.availableCities.map(c => {
            const n = geo.cityTotals.get(c)
            return <option key={c} value={c} disabled={geo.hasDistrictCounts && n === 0 && c !== geo.city}>{geo.cityDisplayLabel(c)}{geo.hasDistrictCounts && n > 0 ? ` (${n})` : ''}</option>
          })}
        </select>
        {/* Multi-district OR semantics end to end (migration 0100 / server district_codes /
            districtCheck below) — selections ALSO render as removable 📍 chips in the
            GLOBAL constraint row; unchecking here and removing there are the same action. */}
        {/* B1216: the four explicit arrays are the whole state; one press commits all four (districtTree.js). */}
        <DistrictFacetList geo={geo} selected={filters.districtCodes || []} excluded={filters.districtCodesNot || []}
          selectedOwn={filters.districtOwnCodes || []} excludedOwn={filters.districtOwnCodesNot || []}
          onCommit={next => setFilters(f => ({ ...f, districtCodes: next.include, districtCodesNot: next.exclude, districtOwnCodes: next.own, districtOwnCodesNot: next.ownNot }))} />
      </>),
    },
    // Metro sits beside streets for the same reason both sit under location: it is the next
    // step of one narrowing, not a separate concept. Unlike the street facet it needs no
    // district picked first — a station is a city-wide landmark, and "near ვარკეთილი" is a
    // complete thought on its own.
    {
      id: 'metro', title: t('desk_facet_metro'), badge: (filters.metroIds || []).length || null,
      body: <MetroFacetList selected={filters.metroIds || []} onToggle={id => toggleIn('metroIds', id)}
              counts={geo?.metroCounts || null} />,
    },
    // Streets sit UNDER location, in the same accordion, because they are the next step of
    // the same narrowing an agent already started — not a separate concept. They are also
    // the reason the location facet is no longer the end of the geography story.
    geo && {
      id: 'street', title: t('desk_facet_street'), badge: (filters.streetIds || []).length || null,
      body: (
        <StreetFacetList
          counts={geo.streetCounts}
          labels={geo.streetLabels}
          catalog={geo.streetCatalog}
          selected={filters.streetIds || []}
          onToggle={id => toggleIn('streetIds', id)}
          coverage={geo.streetCoverage}
          /* The sentence names ONE district, so it is only honest with exactly one picked;
             with two, "of Vake's 1,556" would be a figure drawn from both. */
          /* ⚠️ The label rule is INLINED, not borrowed: `districtDisplayLabel` is declared
             inside IntakeTab, and calling it from this component threw a ReferenceError that
             took the whole Desk to its error boundary. Only rendering can catch that.
             ⚠️ And this comment carries NO braces — a brace-wrapped comment between JSX
             attributes is a syntax error, which is how the first hotfix attempt failed. */
          districtName={(() => {
            const picked = [...(filters.districtCodes || []), ...(filters.districtOwnCodes || [])]
            if (picked.length !== 1) return null
            const d = (geo.districts || []).find(x => x.code === picked[0])
            if (!d) return null
            return (getLanguage() === 'ka' ? (d.name_ka || d.name_en) : (d.name_en || d.name_ka)) || null
          })()}
          districtsPicked={((filters.districtCodes || []).length + (filters.districtOwnCodes || []).length) > 0}
          nearbyM={filters.streetNearbyM || 0}
          onNearbyM={m => set('streetNearbyM', m)} />
      ),
    },
    // 0729/B1145 — KOMPLEKSI. The filter has been complete end to end since 0729 and had no
    // control: committed, in the URL, pushed to SQL, and reachable only by typing its name in
    // the search box. 332 developments, 1,874 properties. Sits in the location run after street,
    // because "which development" is the same question as "where exactly", one grain coarser.
    !clientView && {
      id: 'complex', title: t('desk_facet_complex'), defaultOpen: false,
      badge: (filters.complexIds || []).length || null,
      body: (
        <ComplexFacet
          selected={filters.complexIds || []}
          onToggle={id => toggleIn('complexIds', id)}
          search={searchComplexes} resolveByIds={getComplexesByIds} t={t} />
      ),
    },
    // 📍 HOW FAR IS "NEAR" — the third filter found complete end to end with no control, after
    // the development facet above and პროექტი below. The proximity search has shipped its point
    // to the RPC (`near_lat/near_lng/near_radius_m`), round-tripped it through the URL
    // (`near=lat,lng,radiusM,id`) and counted as an active filter since B762b. The RADIUS was
    // never askable: it is a per-type constant in NEAR_SEARCH_RADIUS_M — metro 300 m, park and
    // school 1,000, mall and market 2,000, airport 3,000 — chosen when the place is clicked and
    // fixed forever after.
    //
    // 🔑 THAT CONSTANT IS A GOOD DEFAULT AND A BAD ANSWER. It encodes the 2026-07-24 two-tier
    // ruling — the WALK tier, what a filter means, as against the bus tier a card displays — and
    // it is right about the typical client. It cannot be right about a particular one: a family
    // that will walk 800 m to the metro and a buyer who wants the station in sight at 150 m are
    // both asking a question this filter could already answer, and neither could say it.
    //
    // ⚠️ RENDERED ONLY WHEN A POINT IS COMMITTED, and that is not a hedge. "Near" is not a
    // standalone concept here — the point comes from clicking a place in the search box, and a
    // radius with nothing to measure from is a control that cannot be used. The facet appears
    // the moment a place is picked, in the location run, next to the thing it modifies.
    // ⚠️ The default is LABELLED rather than silently pre-selected, so an agent can see which
    // distance the system chose for them and tell it apart from one they chose themselves.
    !clientView && filters.near && {
      id: 'near_radius', title: t('desk_facet_near_radius'), defaultOpen: true,
      badge: filters.near.radiusM >= 1000
        ? `${(filters.near.radiusM / 1000).toFixed(filters.near.radiusM % 1000 ? 1 : 0)}კმ`
        : `${filters.near.radiusM}მ`,
      body: (() => {
        const nearLoc = filters.near.id ? (geo?.locations || []).find(l => l.id === filters.near.id) : null
        const dflt = nearLoc ? (NEAR_SEARCH_RADIUS_M[nearLoc.type] || 1000) : null
        const placeName = filters.near.label
          || (nearLoc ? (nearLoc.name_ka || nearLoc.name) : null)
        return (
          <div className="space-y-1">
            {placeName && (
              <p className="text-[11px] leading-snug text-muted-foreground">
                {t('desk_near_radius_hint').replace('{place}', placeName)}
              </p>
            )}
            {/* 🔴 THE LADDER MUST CONTAIN THE COMMITTED VALUE AND THE DEFAULT, or the control
                lies about the state it is showing. Caught by RENDERING, not by any gate: the
                per-type defaults are 300 (metro, complex), 1000, 1500 (hospital), 2000 and 3000
                (airport), and a fixed 250/500/1k/2k/5k ladder contains NEITHER 300 nor 1500 nor
                3000. So the commonest case of all — clicking a metro station — drew five options
                with NONE selected, and every one of them moved the agent off the default with no
                way back to it. A radius the filter is actively using has to be on the list.
                ⚠️ Union, then sort, then dedupe — in that order. Sorting numerically matters:
                these are numbers rendered as text, and 1000 sorts before 300 as a string. */}
            <div className="flex flex-wrap gap-1">
              {[...new Set([250, 500, 1000, 2000, 5000, filters.near.radiusM || 1000,
                            ...(dflt ? [dflt] : [])])].sort((a, b) => a - b).map(m => {
                const on = (filters.near.radiusM || 1000) === m
                return (
                  <button key={m} type="button"
                    onClick={() => setFilters(f => ({ ...f, near: { ...f.near, radiusM: m } }))}
                    aria-pressed={on}
                    className={`px-2 py-1 rounded border text-xs tabular-nums max-md:min-h-11 ${
                      on ? 'border-primary bg-primary/15 text-primary font-medium'
                         : 'border-input text-muted-foreground hover:text-foreground'}`}>
                    {m >= 1000 ? `${m / 1000}კმ` : `${m}მ`}
                    {m === dflt && <span className="ml-1 opacity-60">· {t('desk_near_default')}</span>}
                  </button>
                )
              })}
            </div>
          </div>
        )
      })(),
    },
    // პროექტი (owner ruling 2026-08-28). 41,872 property-codes that were filterable all along and
    // had no control — see ProjectFacet.jsx for why the panel correctly hid them and why the
    // grouping is historical rather than cosmetic.
    // ⚠️ SINGLE-SELECT written into featureCodes: two proj_ codes would AND to nothing.
    !clientView && {
      id: 'project', title: t('desk_facet_project'), defaultOpen: false,
      badge: (filters.featureCodes || []).some(c => c.startsWith('proj_')) ? '•' : null,
      body: (
        <ProjectFacet features={features} t={t}
          selected={(filters.featureCodes || []).find(c => c.startsWith('proj_')) || null}
          onSelect={code => setFilters(f => {
            const rest = (f.featureCodes || []).filter(c => !c.startsWith('proj_'))
            return { ...f, featureCodes: code ? [...rest, code] : rest }
          })} />
      ),
    },
    // ავეჯი — the furnishing DEGREE ladder (0764). Held out of the derived panel since it was
    // minted, because three mutually-exclusive codes as AND-ed checkboxes return zero rows and
    // read as "no such flats"; the vision promotion landed the rows, so it gets its control.
    // The plain "furnished" checkbox stays where it is — see FurnishingFacet.jsx for why.
    !clientView && {
      id: 'furnishing', title: t('desk_facet_furnishing'), defaultOpen: false,
      badge: (filters.featureCodes || []).some(c => FURNISH_DEGREE.includes(c)) ? '•' : null,
      body: (
        <FurnishingFacet features={features}
          selected={(filters.featureCodes || []).find(c => FURNISH_DEGREE.includes(c)) || null}
          onSelect={code => setFilters(f => {
            const rest = (f.featureCodes || []).filter(c => !FURNISH_DEGREE.includes(c))
            return { ...f, featureCodes: code ? [...rest, code] : rest }
          })} />
      ),
    },
    {
      // 🔒 STAGE 2 (SEARCH_GRAMMAR §5b): Deal and Type are IDENTITY, and identity is chosen
      // once — in the tree. `identityFacet: true` removes them from the DESKTOP accordion
      // (facetList below) while the mobile pill sheets keep finding them by id, because
      // mobile has no tree yet and stripping its only identity surface would leave phones
      // with no way to say "apartment". The flag is the transition marker: it comes off
      // when mobile gets its identity navigation (B270).
      identityFacet: true,
      id: 'deal', title: t('desk_search_deal_label'), badge: (filters.intents || []).length || null,
      // 0229 counts: FacetCheck already renders `count` district-style ((N), hidden at 0);
      // intent buckets are exact previews and may overlap across a cluster — never summed.
      body: LISTING_INTENT_OPTIONS.map(o => (
        <FacetCheck key={o.value} checked={(filters.intents || []).includes(o.value)}
          onChange={() => toggleIn('intents', o.value)} label={o.label}
          count={facetCounts?.intents?.[o.value]} />
      )),
    },
    {
      identityFacet: true,   // Stage 2 — see the deal facet's note; one flag, one rule.
      id: 'type', title: t('desk_search_type_label'),
      badge: ((filters.kinds || []).length + (filters.subtypes || []).length) || null,
      // Type's numbers are the SAME kind_counts the category tree spends (one source) —
      // they were computed all along and only this facet never read them (B205).
      // B265: subtype chips render UNDER a selected kind, only for kinds with a subtype
      // slot (SEARCH_GRAMMAR.md §5 — apartment has none). No counts: the RPC emits no
      // subtype_counts today, and a number from loaded rows beside a server-only predicate
      // would be the mixed-denominator lie this panel already scarred over.
      body: PROPERTY_KIND_OPTIONS.map(o => {
        const on = (filters.kinds || []).includes(o)
        const subs = SUBTYPES_BY_KIND[o]
        return (
          <div key={o}>
            <FacetCheck checked={on}
              onChange={() => setFilters(f => {
                const kinds = (f.kinds || []).includes(o)
                  ? (f.kinds || []).filter(k => k !== o) : [...(f.kinds || []), o]
                // Unticking apartment takes the apart-hotel narrowing with it — the code only
                // means "this kind of apartment", so it must not outlive the apartment tick.
                const featureCodes = kinds.includes('apartment') ? f.featureCodes
                  : (f.featureCodes || []).filter(c => !APARTMENT_CONTEXT_CODES.includes(c))
                return { ...f, kinds, subtypes: pruneSubtypes(kinds, f.subtypes), featureCodes }
              })}
              label={t(PROPERTY_CATEGORY_KEYS[o])} count={facetCounts?.kinds?.[o]} />
            {/* Apart-hotel (owner 2026-10-02) narrows a ticked apartment: kind stays apartment,
                the chip writes type_aparthotel_unit — the same predicate the tree row writes.
                Include-only on purpose: "not apart-hotel" would have to mean "no TRUE row", and
                featureStates' `no`/`unknown` mean "recorded false" / "no row" — neither is it.
                The exclusion the owner ruled on lives on the CLIENT (must_not_have_codes). */}
            {on && o === 'apartment' && (
              <div className="ml-5 mt-1 mb-1.5 flex flex-wrap gap-1">
                {Object.entries(APARTMENT_CONTEXT).map(([node, code]) => {
                  const cOn = (filters.featureCodes || []).includes(code)
                  return (
                    <button key={node} type="button"
                      onClick={() => setFilters(f => ({ ...f, featureCodes: cOn
                        ? (f.featureCodes || []).filter(c => c !== code)
                        : [...(f.featureCodes || []), code] }))}
                      className={`rounded-full border px-2 py-0.5 text-[11px] transition-colors ${cOn
                        ? 'border-primary bg-primary/15 text-foreground font-medium'
                        : 'border-border bg-background text-muted-foreground hover:text-foreground hover:border-primary/40'}`}>
                      {t('desk_context_' + node)}
                    </button>
                  )
                })}
              </div>
            )}
            {on && subs && (
              <div className="ml-5 mt-1 mb-1.5 flex flex-wrap gap-1">
                {subs.map(s => {
                  const sState = facetStateOf(filters, 'subtypes', s)
                  return (
                    <button key={s} type="button" onClick={() => cycleFacet('subtypes', s)} title={sState === 'include' ? t('desk_facet_exclude_next') : sState === 'exclude' ? t('desk_facet_exclude_hint') : undefined}
                      className={`rounded-full border px-2 py-0.5 text-[11px] transition-colors ${sState === 'include'
                        ? 'border-primary bg-primary/15 text-foreground font-medium'
                        : sState === 'exclude'
                          ? 'border-rose-500 bg-rose-500/10 text-rose-600 dark:text-rose-400 font-medium line-through'
                          : 'border-border bg-background text-muted-foreground hover:text-foreground hover:border-primary/40'}`}>
                      {sState === 'exclude' ? '✕ ' : ''}{t('desk_subtype_' + s)}
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        )
      }),
    },
    {
      // One-tap BUTTONS (designer board 2026-07-23), not checkboxes — same roomsSet
      // state and multi-select toggle semantics, only the control form changed.
      // Stage 3: land has no rooms; a warehouse has none either. Capability decides, not a
      // kind list — a hotel SUBTYPE revives them under commercial, which no hand-written
      // `kinds.includes(...)` here would ever have known.
      capability: 'rooms',
      id: 'rooms', title: t('desk_range_rooms'), badge: (filters.roomsSet || []).length || null,
      body: (
        <div className="flex gap-1.5">
          {[1, 2, 3, 4, 5].map(n => {
            const on = (filters.roomsSet || []).includes(n)
            return (
              <button key={n} type="button" onClick={() => toggleIn('roomsSet', n)}
                className={`flex-1 rounded-md border px-0 py-1.5 text-sm transition-colors ${on
                  ? 'border-primary bg-primary/15 text-foreground font-semibold'
                  : 'border-border bg-background text-muted-foreground hover:text-foreground hover:border-primary/40'}`}>
                {n === 5 ? '5+' : n}
              </button>
            )
          })}
        </div>
      ),
    },
    {
      // Same one-tap buttons as Rooms above (owner 2026-07-23: "bedrooms look like rooms").
      capability: 'bedrooms',
      id: 'bedrooms', title: t('desk_range_bedrooms'), defaultOpen: false, badge: (filters.bedroomsSet || []).length || null,
      body: (
        <div className="flex gap-1.5">
          {[1, 2, 3, 4, 5].map(n => {
            const on = (filters.bedroomsSet || []).includes(n)
            return (
              <button key={n} type="button" onClick={() => toggleIn('bedroomsSet', n)}
                className={`flex-1 rounded-md border px-0 py-1.5 text-sm transition-colors ${on
                  ? 'border-primary bg-primary/15 text-foreground font-semibold'
                  : 'border-border bg-background text-muted-foreground hover:text-foreground hover:border-primary/40'}`}>
                {n === 5 ? '5+' : n}
              </button>
            )
          })}
        </div>
      ),
    },
    // B838 (owner, 2026-08-19): სველი წერტილი gets the same one-tap pills as rooms/bedrooms,
    // immediately after bedrooms — it was a MiniRange buried in the details facet, the wrong
    // CONTROL in the wrong PLACE. A pill writes the EXISTING bathroomsMin/Max range (min=max=n;
    // 5+ leaves max open), so the wire, URL and caches are untouched; exact-set semantics arrive
    // with B831's array param. ⚠️ The vacuous-zero rule (bathroomsMin ∈ VACUOUS_ZERO_MIN_KEYS)
    // is why there is no "0" pill: bathrooms is null on most listings, and "from 0" once
    // returned zero results (2026-07-11, user-caught). Unknowns reach the shelf, never drop.
    !clientView && {
      capability: c => applies(c, 'bathrooms'),
      id: 'wet_points', title: t('desk_facet_wet_points'), defaultOpen: false,
      badge: filters.bathroomsMin !== '' || filters.bathroomsMax !== '' ? '•' : null,
      body: (
        <div className="flex gap-1.5">
          {[1, 2, 3, 4, 5].map(n => {
            const on = String(filters.bathroomsMin) === String(n)
              && (n === 5 ? filters.bathroomsMax === '' : String(filters.bathroomsMax) === String(n))
            return (
              <button key={n} type="button"
                onClick={() => {
                  if (on) { set('bathroomsMin', ''); set('bathroomsMax', '') }
                  else { set('bathroomsMin', String(n)); set('bathroomsMax', n === 5 ? '' : String(n)) }
                }}
                className={`flex-1 rounded-md border px-0 py-1.5 text-sm transition-colors ${on
                  ? 'border-primary bg-primary/15 text-foreground font-semibold'
                  : 'border-border bg-background text-muted-foreground hover:text-foreground hover:border-primary/40'}`}>
                {n === 5 ? '5+' : n}
              </button>
            )
          })}
        </div>
      ),
    },
    // 0757 — BALCONY COUNT, pills not a slider, and the control choice IS the sanity bound.
    // 28,359 properties carry the count (Idmon reads it from the advert prose); median 1,
    // p99 4, and a tail running to 58 that is extraction error rather than architecture
    // (73 claims over 6, 0.25%). A slider would take its ceiling from that 58 and hand the
    // agent an axis where every real answer is squashed into the first 7% of the track.
    // Pills make the bound STRUCTURAL: 4+ contains the 58 honestly, nothing is clamped, and
    // no observation is altered — the rule a projection must obey.
    // ⚠️ No "0" pill, for bathrooms' reason: the fact is null on most listings, so "from 0"
    // reads as a filter and shelves the corpus. Absent counts reach the shelf, never a drop.
    !clientView && {
      capability: c => applies(c, 'balcony_count'),
      id: 'balcony_count', title: t('desk_facet_balcony_count'), defaultOpen: false,
      badge: (filters.balconyCountMin !== '' || filters.balconyCountMax !== ''
              || (filters.featureStates || []).includes('balcony:no')) ? '•' : null,
      body: (
        <div className="flex gap-1.5">
          {/* 🔴 "0" IS NOT A COUNT — owner: "you have not updated balcony selector from 0".
              balcony_count NEVER STORES ZERO: a count is only extracted when a balcony exists,
              so there is no row saying "0 balconies" and a min/max of 0 would match nothing.
              Zero balconies is the FEATURE'S ABSENT STATE — `balcony` recorded present=false,
              8,222 properties — which is a different key and a different question.
              So this pill writes featureStates 'balcony:no' and NOT a count, and picking it
              clears any count (asking for "no balcony" AND "2 balconies" is asking for nothing).
              ⚠️ It is the same rule 0777 leans on from the other side: a COUNT of 1 or more is
              evidence a balcony EXISTS, which is why a counted property can no longer answer
              "no balcony". The two halves now agree. */}
          {(() => {
            const noBalcony = (filters.featureStates || []).includes('balcony:no')
            return (
              <button type="button" aria-pressed={noBalcony}
                onClick={() => setFilters(f => {
                  const rest = (f.featureStates || []).filter(x => !String(x).startsWith('balcony:'))
                  return { ...f, featureStates: noBalcony ? rest : [...rest, 'balcony:no'],
                           balconyCountMin: '', balconyCountMax: '' }
                })}
                className={`flex-1 rounded-md border px-0 py-1.5 text-sm transition-colors ${noBalcony
                  ? 'border-primary bg-primary/15 text-foreground font-semibold'
                  : 'border-border bg-background text-muted-foreground hover:text-foreground hover:border-primary/40'}`}>
                0
              </button>
            )
          })()}
          {[1, 2, 3, 4].map(n => {
            const on = String(filters.balconyCountMin) === String(n)
              && (n === 4 ? filters.balconyCountMax === '' : String(filters.balconyCountMax) === String(n))
            return (
              <button key={n} type="button"
                onClick={() => {
                  if (on) { set('balconyCountMin', ''); set('balconyCountMax', '') }
                  else {
                    set('balconyCountMin', String(n)); set('balconyCountMax', n === 4 ? '' : String(n))
                    // ⚠️ and drop "no balcony" — the two are a contradiction, and the RPC would
                    // answer it by demoting the corpus to the shelf rather than by erroring.
                    setFilters(f => ({ ...f, featureStates: (f.featureStates || []).filter(x => !String(x).startsWith('balcony:')) }))
                  }
                }}
                className={`flex-1 rounded-md border px-0 py-1.5 text-sm transition-colors ${on
                  ? 'border-primary bg-primary/15 text-foreground font-semibold'
                  : 'border-border bg-background text-muted-foreground hover:text-foreground hover:border-primary/40'}`}>
                {n === 4 ? '4+' : n}
              </button>
            )
          })}
        </div>
      ),
    },
    {
      id: 'price', title: t('desk_range_price_header'), badge: [filters.priceMin, filters.priceMax, filters.ppmMin, filters.ppmMax].some(v => v !== '' && v != null) ? '•' : null,
      body: (
        <>
        {/* This picker belongs to the SEARCH question: changing USD → GEL keeps the entered
            numbers and intentionally reinterprets them. The top-bar preference is different:
            it converts both bounds so the economic band, and therefore the search, stays put. */}
        <div className="flex gap-1 mb-2">
          {['USD', 'GEL'].map(c => (
            <button key={c} type="button" onClick={() => setFilters(f => ({ ...f, currency: c }))}
              aria-pressed={filters.currency === c}
              className={`flex-1 rounded-md border px-2 py-1 text-sm max-md:min-h-11 ${
                filters.currency === c
                  ? 'border-primary bg-primary/15 text-foreground font-medium'
                  : 'border-input text-muted-foreground hover:text-foreground'}`}>
              {c === 'USD' ? '$ USD' : '₾ GEL'}
            </button>
          ))}
        </div>
        <PriceFilter min={num(filters.priceMin)} max={num(filters.priceMax)} currency={filters.currency}
          label={null}
          values={priceValues} presetValues={priceBiasedSort ? null : priceValues}
          ceiling={priceCeiling} gap={Math.max(1, Math.round(priceCeiling / 100))}
          facet={rangeFacets?.price?.[filters.currency] ?? null} countUnit={entryType === 'Leads' ? 'leads' : 'properties'}
          onChange={(a, b) => setFilters(f => ({ ...f, priceMin: a ?? '', priceMax: b ?? '' }))} />
        {/* core 3872 (owner 2026-10-24): price per m², in the currency picked above. Not in Client view (filters audit
            2026-10-27 F07): it is a listing-side, server-only lens, and applied there it removed every client */}
        {!clientView && <PpmFilter min={num(filters.ppmMin)} max={num(filters.ppmMax)} currency={filters.currency}
          labels={{ title: t('desk_facet_ppm'), min: t('desk_ppm_min'), max: t('desk_ppm_max') }}
          onChange={(a, b) => setFilters(f => ({ ...f, ppmMin: a ?? '', ppmMax: b ?? '' }))} />}
        </>
      ),
    },
    {
      // Two measurements, two capabilities: the facet survives if EITHER applies, and each
      // control is gated on its own. Land has no building area; an apartment has no plot.
      capability: c => applies(c, 'area') || applies(c, 'landArea'),
      id: 'area', title: t('desk_range_area_header'),
      badge: [filters.areaMin, filters.areaMax, filters.landAreaMin, filters.landAreaMax].some(v => v !== '') ? '•' : null,
      body: (<>
        {/* Two DIFFERENT measurements, deliberately shown as two controls rather than one
            "size" slider. area_sqm is the BUILDING, land_area_sqm (0226) is the PLOT; a
            house has both and they disagree, and 0345's capability map says the building
            figure does not apply to land at all. One merged control would have to lie about
            which number it was filtering. Labelled only when land is offered, so the common
            apartment case keeps its original single unlabelled slider. */}
        {applies(cap, 'area') && (
          <AreaFilter min={num(filters.areaMin)} max={num(filters.areaMax)}
            label={showLandArea ? t('desk_range_area_building') : null}
            values={areaValues} presetValues={areaBiasedSort ? null : areaValues}
            ceiling={areaCeiling} gap={Math.max(1, Math.round(areaCeiling / 100))}
            facet={rangeFacets?.area?._ ?? null} countUnit={entryType === 'Leads' ? 'leads' : 'properties'}
            onChange={(a, b) => setFilters(f => ({ ...f, areaMin: a ?? '', areaMax: b ?? '' }))} />
        )}
        {showLandArea && (
          <AreaFilter min={num(filters.landAreaMin)} max={num(filters.landAreaMax)}
            label={t('desk_range_land_area')}
            values={landAreaValues} presetValues={areaBiasedSort ? null : landAreaValues}
            ceiling={landAreaCeiling} gap={Math.max(1, Math.round(landAreaCeiling / 100))}
            facet={rangeFacets?.land_area?._ ?? null} countUnit={entryType === 'Leads' ? 'leads' : 'properties'}
            onChange={(a, b) => setFilters(f => ({ ...f, landAreaMin: a ?? '', landAreaMax: b ?? '' }))} />
        )}
      </>),
    },
    // Listing-only ranges — hidden in Client view (they no-op there, see header note);
    // real from–to ranges, so MiniRange stays, not checkboxes.
    !clientView && {
      // Four controls, four capabilities. A house has floors_total but no `floor` and no top
      // floor; land has none of them. The facet disappears only when ALL four are meaningless.
      capability: c => ['bathrooms', 'floor', 'floorsTotal', 'topFloor'].some(k => applies(c, k)),
      id: 'details', title: t('desk_facet_details'), defaultOpen: false,
      // room_isolated_kitchen dropped from this badge with the control it belonged to — a dot
      // here for a filter the section no longer offers is exactly the "chip lit, control gone"
      // shape the Desk keeps paying for.
      badge: [filters.floorMin, filters.floorMax, filters.floorsTotalMin, filters.floorsTotalMax].some(v => v !== '') || filters.topFloor || filters.topFloorNot || filters.firstFloor || filters.firstFloorNot ? '•' : null,
      body: (<>
        {/* bathrooms moved to its own wet_points pill facet after bedrooms (B838); the range
            control here was both the wrong control and the wrong place. */}
        {applies(cap, 'floor') && (
          <MiniRange label={t('desk_range_floor')} lo={filters.floorMin} hi={filters.floorMax}
            onLo={v => set('floorMin', v)} onHi={v => set('floorMax', v)} />
        )}
        {applies(cap, 'floorsTotal') && (
          <MiniRange label={t('desk_range_total_floors')} lo={filters.floorsTotalMin} hi={filters.floorsTotalMax}
            onLo={v => set('floorsTotalMin', v)} onHi={v => set('floorsTotalMax', v)} />
        )}
        {/* Top floor (RPC v11): floor == floors_total — a fact, not a range.
            ⚠️ NO `w-20` (owner, 2026-08-19: *"we need to make ბოლო სართული on one line like
            იზოლირებული სამზ"*). It carried the same fixed 80px as MiniRange's labels, which
            aligned it with სართული / სველი სართ. above — but ბოლო სართული is wider than 80px, so
            the alignment was bought by wrapping the label onto two lines. The two CHECKBOX rows
            are their own group (label + tick, no input column to line up with), and the
            isolated-kitchen row directly below already sizes to its content; this now matches it.
            `whitespace-nowrap` so a longer translation cannot quietly reintroduce the wrap. */}
        {/* Owner 2026-09-05: first floor beside top floor, and ✕ on both — a flag with an exclude half in the
            spec (topFloorNot / firstFloorNot), cycled by the same TriBox as the categorical facets:
            neutral → ✓ must be → ✕ must NOT be → neutral. The verdicts live in the body (0888) and are
            mirrored in deskFeed.js for the local re-filter. `firstFloor` shares topFloor's capability: a
            kind with no floor has neither. */}
        {applies(cap, 'topFloor') && [['topFloor', 'desk_top_floor'], ['firstFloor', 'desk_first_floor']].map(([k, lk]) => (
          <TriRow key={k} nameFirst className="text-xs" state={facetStateOf(filters, k, true)} onCycle={() => cycleFacet(k, true)}
            onName={() => nameFacet(k, true)} label={t(lk)} />
        ))}
        {/* 🔴 THE ISOLATED-KITCHEN TICK WAS HERE AND IS NOW IN დაგეგმარება (owner, 2026-08-27:
            „shoudl we not move იზოლირებული სამზარეულო from here?"). It rendered TWICE: this
            hand-placed checkbox among the FLOOR NUMBERS, and again in the derived feature panel,
            because `room_isolated_kitchen` is category `planning` / subcategory `room` in the
            registry with 1,676 rows — far above the offer bar. Same duplicate the owner reported
            for კომუნიკაციები, and the same cause: a code hand-listed on one surface while the
            derived panel offers it from the registry on another.
            ⚠️ It also carried a second defect that only deletion fixes: it was gated on
            `applies(cap, 'bathrooms')` — a BATHROOM capability deciding whether a KITCHEN control
            appears — so any kind whose capability profile splits those two showed the wrong answer.
            Nothing replaces it here: the filter still exists, still uses featureCodes, and is
            reached from the section its own registry row names. */}
      </>),
    },
    // ── მდგომარეობა — the state of the flat (0433 / v37, designer handoff 2026-08-06) ─────
    // ONE facet, TWO subheaded rows (რემონტი · კარკასი) — not one merged eight-option list,
    // which would read შავი კარკასი as a worse grade of renovation, and not two sibling
    // accordions, because the agent is asking one question. Designer's Q1.
    //
    // ⚠️ OR, NOT AND — the opposite of the Features facet directly below, and the reason this
    // has its own state field and its own RPC parameter. Every tick here is OR'd, including
    // across the two rows; the facet as a whole still ANDs against every other facet. Ticking
    // two amenities means "both"; ticking two states means "either", because no flat is two
    // states at once. Folding these into featureCodes would have silently meant "both" and
    // emptied the result list — see the registry entry on the server for the same warning.
    //
    // ⚠️ THE VERDICT RULE IS NOT THE AMENITY RULE. There too it is three-valued, but here a
    // property carrying ANY rung of a ladder is KNOWN for that ladder: a flat recorded
    // ძველი რემონტი is a MISMATCH for ახალი რემონტი, not an unknown. That is only sound
    // because `present = false` is recorded for ZERO of these codes (measured 2026-08-06) —
    // carrying one rung is the only evidence that another is false. Only a property with no
    // rung at all (6,045 of 34,363, 17.6%) reaches the possible-matches shelf. The server
    // decides; nothing here re-implements it.
    //
    // ⚠️ NO COUNTS, same rule as Features below: the RPC emits facet counts for
    // kinds/intents/sources only. The designer's mock carries corpus-wide numbers, but those
    // describe the whole database rather than the pool the agent is standing in, and "a number
    // beside an option must be the number the click delivers". No number beats a wrong one.
    //
    // ⚠️ Options are the INTERSECTION of CONDITION_LADDERS and the live vocabulary, and the
    // nine zero-supply codes are absent from that list on purpose (see searchVocab.js).
    // Hidden in Client view for the reason Features and Terms are: a client card carries no
    // property, so every box would be a silent no-op.
    !clientView && {
      // 🚨 THE RULE EXISTED AND THE FACET NEVER READ IT (owner, 2026-08-19: *"we didn't need
      // these when we are in land category"*). `IDENTITY_BASE.land.condition` has been 'M' —
      // meaningless for this identity — since the map was written, and `applicableFilters`
      // already lists `condition: ['conditionCodes']`, so a land search STRIPS conditionCodes
      // at the query edge and from the chip row. But this entry declared no `capability`, and
      // an entry without one is allowed unconditionally — so the whole რემონტი / კარკასი
      // ladder rendered on a land search and every tick was a silent no-op: the agent narrows
      // by "new renovation", the query never carries it, and nothing on screen says why.
      // That is precisely the dead-control defect Stage 3 exists to prevent, hiding in the one
      // facet that never opted in. Declaring the capability is the whole fix; the strip
      // behaviour it pairs with was already correct.
      capability: 'condition',
      id: 'condition', title: t('desk_facet_condition'), defaultOpen: false,
      badge: ((filters.conditionCodes || []).length + (filters.conditionExcludeCodes || []).length) || null,
      body: (
        <div className="space-y-2">
          {/* 0788-0790: THE LADDER COMES FROM THE REGISTRY NOW, not from a constant. The constant
              had drifted materially from the owner's ruling — 9 codes in 2 groups against 5
              stages and 13 rungs, missing მშენებარე (1,410 properties), the კარკასი own bucket,
              გარემონტებული (2,647), კოსმეტიკური and უცხოვრებელი outright, and still grouping
              სარემონტო with the shells after the owner made it a stage of its own.
              ⚠️ Falls back to the constant when the server sends no ladder, which is the
              pre-0788 panel byte for byte — a condition facet with no rungs would be worse than
              a stale one. */}
          {conditionLaddersFrom(conditionLadder).map(([ladder, codes, stage]) => {
            // 🔴 ZERO-SUPPLY RUNGS ARE DROPPED, and re-adding them is the regression the owner
            // caught within an hour: "the condition that we just added don't filter anything at
            // all." Deriving the ladder from the registry brought back exactly what the old
            // hard-coded list had excluded ON PURPOSE — its comment said "the nine zero-supply
            // codes are absent from that list on purpose". Measured: frame (the კარკასი generic
            // minted in 0788), reno_cosmetic and reno_unfinished each carry 0 properties, so the
            // three NEWLY VISIBLE rungs were the three dead ones. Clicking any of them returned
            // nothing, which reads as a broken filter rather than as an empty corpus.
            // 📌 The ladder is still COMPLETE in the database — the owner ruled that it is defined
            // whether or not rows exist ("I don't care what carries data"). What the DATABASE
            // knows and what the PANEL OFFERS are different questions, and this is the second
            // one: a control that cannot return anything is worse than a missing control.
            // They appear by themselves the day a producer fills them; nobody has to remember.
            const rows = codes.map(c => (features || []).find(x => x.code === c))
              .filter(x => x && (Number(x.usage) || 0) > 0)
            if (!rows.length) return null
            return (
              <div key={ladder}>
                {/* 🔴 THE STAGE IS SELECTABLE, AND A ONE-RUNG STAGE IS NOT REPEATED.
                    Two owner reports, one cause: "there is not კარკასი under კარკასი" and
                    "მშენებარე has no sub".
                    The district idiom the owner asked for has TWO parts and I had built one.
                    დიდუბე holds 360 and is ITSELF CLICKABLE — it returns everything beneath it;
                    the 201 under it are the remainder with no finer detail. I rendered the
                    heading as inert text, so კარკასი could not be asked for at all, and the
                    own-bucket rung that WOULD have carried it (`frame`) has 0 rows and was
                    correctly dropped by the supply filter. The parent had to become the answer.
                    ⚠️ AND A STAGE WITH ONE RUNG IS DRAWN ONCE. მშენებარე / მშენებარე,
                    მიმდინარე რემონტი / მიმდინარე რემონტი, სარემონტო / სარემონტო each rendered
                    the same word twice — the idiom is only meaningful when there is something
                    beneath to distinguish from the whole.
                    ⚠️ NO COUNT ON THE PARENT, deliberately: the rungs are buckets of one ladder
                    and summing them is the thing 0229 forbids outright. A parent number would be
                    a sum wearing the clothes of a measurement. */}
                {rows.length > 1 && (() => {
                  const codesHere = rows.map(x => x.code)
                  // 🔴 THE STAGE CYCLES LIKE ITS RUNGS — owner, twice: "when i press კარკასი second
                  // time it dont x it instead disselect" and "still cant make კარკასი x".
                  // I made the stage selectable and stopped there, so it was a two-state toggle
                  // sitting in a column of three-state controls. A parent that cannot say what its
                  // children can say is a worse control than no parent: „not a shell of any kind"
                  // is exactly the question a parent exists to make askable in one click.
                  // unset -> ✓ every rung wanted -> ✕ every rung struck -> unset.
                  const allOn  = codesHere.every(c => (filters.conditionCodes || []).includes(c))
                  const allOut = codesHere.every(c => (filters.conditionExcludeCodes || []).includes(c))
                  // 🔴 A PARENT MUST LOOK LIKE A PARENT IN GEORGIAN TOO — owner: "no subs".
                  // The ladder WAS nested; nothing was visible. This row carried its rank
                  // with `uppercase tracking-wide` at `text-xs`, and in a UNICASE script
                  // both devices do nothing: Georgian has no capitals, so `uppercase` is a
                  // no-op and the only surviving signal was a SMALLER type size — which
                  // reads as a lesser item, not a heading. კარკასი sat above its four
                  // shells looking like a fifth one.
                  // ⚠️ The general rule, and it is not only about this facet: hierarchy
                  // expressed through CASE is invisible in Georgian, Armenian, Hebrew,
                  // Arabic, and every CJK script. Indentation, weight and size survive
                  // translation; letterforms do not.
                  return (
                    <button type="button" aria-pressed={allOn || allOut}
                      title={t(allOn ? 'desk_cond_state_yes' : allOut ? 'desk_cond_state_no' : 'desk_cond_state_unset')}
                      onClick={() => setFilters(f => {
                        // Both lists are stripped of this stage's codes first, so a rung left in
                        // the opposite state by an earlier click cannot survive into the next one
                        // and make the parent disagree with its own children.
                        const inc = (f.conditionCodes || []).filter(c => !codesHere.includes(c))
                        const exc = (f.conditionExcludeCodes || []).filter(c => !codesHere.includes(c))
                        if (allOn)  return { ...f, conditionCodes: inc, conditionExcludeCodes: [...exc, ...codesHere] }
                        if (allOut) return { ...f, conditionCodes: inc, conditionExcludeCodes: exc }
                        return { ...f, conditionCodes: [...inc, ...codesHere], conditionExcludeCodes: exc }
                      })}
                      className={`w-full flex items-center gap-2 text-sm font-medium mt-2 mb-1 text-left max-md:min-h-11 ${
                        allOn || allOut ? 'text-foreground font-semibold' : 'text-foreground/80 hover:text-foreground'}`}>
                      {/* GLYPH AND COLOUR, never colour alone — the same rule the rungs follow. */}
                      <span className={`h-4 w-4 shrink-0 rounded border text-[11px] leading-none flex items-center justify-center ${
                        allOn ? 'border-primary bg-primary/15 text-primary'
                        : allOut ? 'border-destructive bg-destructive/15 text-destructive' : 'border-input'}`}>
                        {allOn ? '✓' : allOut ? '✕' : ''}
                      </span>
                      <span className="flex-1 truncate">{stage?.name_ka || t('desk_facet_condition_' + ladder)}</span>
                    </button>
                  )
                })()}
                {/* THE INDENT IS THE STATEMENT. A left spine and a step in are what say "these
                    four are inside კარკასი"; they work in every script and at every type size,
                    which the case change did not. Only when a parent was actually drawn — a
                    one-rung stage has nothing to be inside of. */}
                <div className={`space-y-0.5 ${rows.length > 1 ? 'ml-2 pl-3 border-l border-border' : ''}`}>
                  {/* 0778: the rung CYCLES — unset -> ✓ wanted -> ✕ struck -> unset. The owner asked
                      for "NOT renovated" and this is the door to it. Deliberately the same idiom
                      as the amenity rows, so a struck rung reads the way a struck feature does.
                      ⚠️ A CODE MUST NEVER BE IN BOTH LISTS: "renovated AND not renovated" is a
                      contradiction the RPC would answer with zero rows, and zero rows read as
                      "no such flats" rather than "you asked for nothing". Each step removes the
                      code from the other list, so the state machine cannot express it. */}
                  {rows.map(x => {
                    const on  = (filters.conditionCodes || []).includes(x.code)
                    const out = (filters.conditionExcludeCodes || []).includes(x.code)
                    const next = () => setFilters(f => {
                      const inc = (f.conditionCodes || []).filter(c => c !== x.code)
                      const exc = (f.conditionExcludeCodes || []).filter(c => c !== x.code)
                      if (on)  return { ...f, conditionCodes: inc, conditionExcludeCodes: [...exc, x.code] }
                      if (out) return { ...f, conditionCodes: inc, conditionExcludeCodes: exc }
                      return { ...f, conditionCodes: [...inc, x.code], conditionExcludeCodes: exc }
                    })
                    const look = on ? 'border-primary bg-primary/15 text-primary'
                              : out ? 'border-destructive bg-destructive/15 text-destructive'
                                    : 'border-input'
                    return (
                      <button key={x.code} type="button" onClick={next}
                        aria-pressed={on || out}
                        title={t(on ? 'desk_cond_state_yes' : out ? 'desk_cond_state_no' : 'desk_cond_state_unset')}
                        className={`w-full flex items-center gap-2 text-sm py-0.5 text-left max-md:min-h-11 ${on || out ? 'text-foreground font-medium' : 'text-muted-foreground hover:text-foreground'}`}>
                        {/* GLYPH AND COLOUR, never colour alone — ✓ / ✕ carry the meaning for a
                            reader who cannot separate the two hues. */}
                        <span className={`h-4 w-4 shrink-0 rounded border text-[11px] leading-none flex items-center justify-center ${look}`}>
                          {on ? '✓' : out ? '✕' : ''}
                        </span>
                        <span className="flex-1 truncate" title={x.name_ka || x.name_en || x.code}>{x.name_ka || x.name_en || x.code}</span>
                        {/* F-088 (owner, 2026-09-09): the corpus-wide usage figure is no longer printed
                            here. It sat beside a FILTERED view and read as the view's answer (second
                            agent's audit: "39,662 … beside a much smaller inventory"); the 08-06 ruling
                            gives the condition facet no counts. `usage` still decides the supply floor
                            above — a rung nothing carries stays hidden — only the digit is gone. */}
                      </button>
                    )
                  })}
                </div>
                {/* 0790/0791 — WHEN IT COMPLETES, offered only under მშენებარე. Owner: "that's
                    not subs, that's just time of completion — a filter under that category", and
                    then "just wire it till show later".
                    ⚠️ THE LABELS SLIDE AND THE CODES DO NOT, which is why the mapping is by YEAR
                    rather than by position. completion_buckets_v1() regenerates its labels from
                    the calendar (owner: "year must move itself, next year must say +1 on all"),
                    while cls_eta_2027 means 2027 forever. Matching bucket.year_from to the code
                    keeps them agreeing in January; matching by list position would silently
                    relabel every property.
                    ⚠️ SUPPLY FLOOR, same rule as the rungs above: a bucket appears the day
                    something carries its code and not before. Today that is NONE of them — 350
                    completion years are extracted and none reaches property_feature, because
                    `completion_stated` has no promotion row. So this is wired and invisible, by
                    design: it lights up on its own when the promotion lands, and nobody has to
                    remember to come back. */}
                {/* ⚠️ ALWAYS INDENTED, even though მშენებარე is a one-rung stage and draws no
                    parent heading: these buckets ARE beneath it whether or not a heading was
                    rendered. The owner's second report — "მშენებარე has no sub" — is answered by
                    the step in, not just by the rows existing. */}
                <div className={ladder === 'under_constr' ? 'ml-2 pl-3 border-l border-border space-y-0.5' : ''}>
                {ladder === 'under_constr' && (conditionLadder?.completion_buckets || []).map(b => {
                  // 🔑 A BUCKET IS RELATIVE; THE CODE IT ASKS FOR IS ABSOLUTE (0794/0795).
                  // „სრულდება წელს" maps to cls_eta_2026, not cls_eta_this_year — that code is
                  // permanently INACTIVE, because a stored relative code goes wrong on 1 January
                  // with the row keeping its tag, the tag keeping its name, and nothing erroring.
                  // The sliding LABEL stays here, recomputed from the calendar on every render by
                  // completion_buckets_v1; only the absolute year is ever written down.
                  // ⚠️ cls_eta_2029_plus is the one code still carrying a year in its name. In
                  // 2027 the plus_3 bucket becomes 2030+ and will want its own; the supply floor
                  // two lines down hides it until one is minted, so the failure mode is a missing
                  // option rather than a wrong one.
                  const code = b.key === 'overdue' ? 'cls_eta_overdue'
                    : b.year_to == null ? 'cls_eta_2029_plus' : `cls_eta_${b.year_from}`
                  const feat = (features || []).find(x => x.code === code)
                  // 🔴 THE SUPPLY FLOOR DOES NOT APPLY TO A RANGE — owner: "we need 2029+ too",
                  // then "or it's hidden because there are 0". It was, and the zero is the
                  // defect rather than the answer.
                  // Hiding an empty rung is right for a LIST of independent options. These
                  // buckets are a CONTIGUOUS RANGE, and dropping the ends silently MOVES the
                  // range's edges: truncated at 2028 the control states that nothing completes
                  // later and that no deadline has passed. Measured against the descriptions,
                  // both are false — 9 active listings state 2029 and 7 state 2025, and NONE of
                  // either is extracted. So the ends were empty because of an extraction gap,
                  // and hiding them turned that gap into a confident claim about the market.
                  // ⚠️ An empty bucket renders DISABLED WITH ITS ZERO, never clickable: the
                  // range stays honest without offering a control that returns nothing, which
                  // is the regression this floor exists to prevent. See LadderRow.
                  // 📌 A code the vocabulary has not minted yet is still skipped — there is
                  // nothing to name, and that is a different absence from "minted, unused".
                  if (!feat) return null
                  const nHere = Number(feat.usage) || 0
                  const on = (filters.featureCodes || []).includes(code)
                  return (
                    <LadderRow key={b.key} code={code} label={b.name_ka} count={nHere}
                      selected={on} disabled={nHere === 0}
                      onSelect={c => setFilters(f => {
                        // Single-select: a building completes once. Two eta codes AND together and
                        // demote the corpus to the shelf rather than erroring — the furnishing lesson.
                        const rest = (f.featureCodes || []).filter(x => !String(x).startsWith('cls_eta_'))
                        return { ...f, featureCodes: c ? [...rest, c] : rest }
                      })} />
                  )
                })}
                </div>
              </div>
            )
          })}
          {/* The definitions are the OWNER'S OWN Georgian (0432), served on the vocabulary row
              itself — so this help text can never drift from what the ⓘ dot shows elsewhere,
              and a definition edit is a data change rather than a deploy. Rendered only for
              rungs that actually carry one. */}
          <ConditionHelp features={features} t={t} />
          {/* Q6: the 17.6% with no state recorded. Stated in the facet as well as above the
              results, because the agent decides whether to tick a box HERE — learning only
              afterwards that a sixth of the corpus stepped aside is learning too late. */}
          <p className="text-[11px] leading-snug text-muted-foreground pt-1">{t('desk_facet_condition_unknown_note')}</p>
        </div>
      ),
    },
    // 0786 — APARTMENT CLASS, the second axis, and the control it never had. The ladder has been
    // in the database since yesterday (apartment_class_rung, 8 rungs) and reached nobody: the
    // owner asked "where are area classes?" and the honest answer was that I built the data and
    // no door to it.
    // ⚠️ SINGLE-SELECT, for the reason the furnishing degree is: a flat is ONE class, and two
    // AND-ed class codes do not return zero — they demote the whole corpus to the may-consider
    // shelf, which reads as "my filter stopped confirming anything" rather than as an error.
    // ⚠️ Declares the same `condition` capability as the ladder above it: a plot of land has no
    // finish to grade, and an entry without a capability renders unconditionally (the defect
    // that left the რემონტი ladder live on land searches).
    !clientView && {
      capability: 'condition',
      id: 'apartment_class', title: t('desk_facet_apt_class'), defaultOpen: false,
      badge: (filters.featureCodes || []).some(c => /^class_[1-8]_/.test(c)) ? '•' : null,
      body: (
        <div className="space-y-0.5">
          {(conditionLadder?.classes || []).map(cl => {
            const feat = (features || []).find(x => x.code === cl.code)
            if (!feat) return null
            const on = (filters.featureCodes || []).includes(cl.code)
            return (
              <LadderRow key={cl.code} code={cl.code} count={feat.usage}
                label={feat.name_ka || feat.name_en || cl.code} selected={on}
                onSelect={code => setFilters(f => {
                  const rest = (f.featureCodes || []).filter(c => !/^class_[1-8]_/.test(c))
                  return { ...f, featureCodes: code ? [...rest, code] : rest }
                })} />
            )
          })}
          {/* The two axes are confusable by name alone — „სუფთა ბინა" on this ladder and
              „სუფთა / საცხოვრებელი" on the other one nearly collided for exactly that reason. */}
          <p className="text-[11px] leading-snug text-muted-foreground pt-1">{t('desk_facet_apt_class_note')}</p>
        </div>
      ),
    },
    // ── Features facet (B37, 2026-07-29): the amenity filter. ─────────────────────────
    // `p_feature_codes` has been live in the search RPC since 0241 with nothing to drive it.
    // This is the control. Checked = the property MUST have it; multiple checks AND together
    // (worst verdict wins server-side), matching how every other facet composes.
    //
    // THREE verdicts, not two, and the third is the whole reason this reads honestly:
    // property_feature stores "someone looked and there is no elevator" as `present=false`,
    // distinct from "nobody ever recorded one". Only the first is a mismatch; the second is
    // the possible-matches shelf. The split is sharp and per-feature — elevator carries
    // ~2,950 explicit falses, parking essentially none (2,711 yes / 16 no) — so collapsing
    // absence into "no" would silently reject most of the corpus for parking on evidence
    // that does not exist. The server does the deciding; nothing here re-implements it.
    //
    // ⚠️ NO COUNTS on these rows, deliberately. The RPC emits facet counts for kinds/intents/
    // sources only, and a count computed from loaded rows would describe a set nobody asked
    // for — the rule that generated most of 2026-07-29's bugs ("a number on screen may only
    // describe the result set it sits next to"). No number beats a plausible wrong one.
    //
    // ⚠️ Options come from the INTERSECTION of FEATURE_GROUPS and the live vocabulary, so a
    // code retired from the registry disappears from the panel instead of being sent and
    // shelving the entire corpus as unknown (0241's measured whiteout). Hidden in Client
    // view for the §12 reason the Terms facet is: client cards carry no property, so every
    // box would be a silent no-op.
    !clientView && {
      id: 'features', title: t('desk_facet_features'), defaultOpen: false,
      badge: featureActiveCount(filters) || null,
      body: (
        <div className="space-y-2">
          {/* 2026-08-27: derived from the registry (searchVocab.featureGroupsFrom), not a
              hand-typed array. The intersection rule below is unchanged and still guards
              the whiteout; what changed is that the candidate set is now every populated,
              non-ladder, non-tenant_terms code the backend knows — 26 reachable of 422
              was a UI limit, not a data one. */}
          {/* 0759: gated on the SELECTED kind. Land could answer 10 of the 91 controls this
              offered, so 81 boxes returned nothing and read as "no such plots". Fails open —
              no kind selected, or no capability rows, means every group. */}
          {featureGroupsFrom(features, featureAxisOf, capabilityFilter(filters.kinds, kindFeatureCaps)).map(([group, codes]) => {
            const rows = codes.map(c => (features || []).find(x => x.code === c)).filter(Boolean)
            if (!rows.length) return null
            return (
              <div key={group}>
                <p className="text-xs uppercase tracking-wide text-muted-foreground mb-0.5">{tOr('desk_facet_features_' + group, group.replace(/_/g, ' '))}</p>
                <div className="space-y-0.5">
                  {rows.map(x => (
                    /* 0756 — the owner's four-state control. Its own component so it can be
                       RENDERED outside the login gate (src/harness.jsx cases FS1–FS4); the cycle
                       itself lives in src/lib/featureStates.js and is tested there. */
                    <FeatureStateRow key={x.code} feature={x} filters={filters} t={t}
                      onChange={patch => setFilters(f => ({ ...f, ...patch }))} />
                  ))}
                </div>
                {/* The defects group is the one whose meaning an agent cannot guess from the
                    checkbox: every other row asks for a flat that HAS something, this one asks
                    for a flat where something WAS RECORDED. Since 0756 it is no longer presence-only —
                    the ✕ state asks for a recorded absence — so the note still has to say out loud both
                    what a tick does and what an empty answer means. Rendered per group rather
                    than appended to the shared hint below, which is about the possible-matches
                    shelf and is true of every group including this one. */}
                {group === 'defects' && (
                  <p className="text-xs text-muted-foreground mt-1">{t('desk_facet_features_defects_note')}</p>
                )}
              </div>
            )
          })}
          <p className="text-xs text-muted-foreground">{t('desk_facet_features_hint')}</p>
        </div>
      ),
    },
    // ── Terms facet (B177, 2026-07-27): exclude-known-bad over overlay facts. ──────────
    // The chips are the CLIENT's profile ("my client has pets / is a foreigner…") and hide
    // listings with a RECORDED not_allowed stance on that subject; the two payment controls
    // hide recorded over-budget deposits / recorded last-month-up-front demands. Unrecorded
    // facts always pass — coverage is ~2% of the feed (B177 measurements), so treating
    // absence as a miss would blank the feed and absence-as-unknown would shelf it. Same
    // vocabulary as the client form's household chips (features.category === 'tenant_terms')
    // — one subject vocab across capture and filtering, never a parallel list.
    // Hidden in Client view (§12: these exclude LISTINGS; client cards pass classify
    // untouched, so the controls would be silent no-ops there).
    // The 2026-07-27 Leads hide is RETIRED (2026-08-05): it dated from when this trio was
    // client-only over loaded rows the leads feed never loads. All three are SERVER
    // predicates now (restrictions 0244/0245; advance + move-in with B177's close), every
    // scope's filtered path lands on the ONE search RPC (v34), and restriction RULES reach
    // it through cluster_restriction_evidence_v1 (0409) — so the same chips answer over the
    // leads inventory too. The gate lives in the Object profile (taxonomy.js): Client 'M'
    // (§12: these exclude LISTINGS; a client card passes classify untouched), Leads 'O'.
    // Stage 3: `!clientView && entryType !== 'Leads'` became `capability: 'terms'` — the SAME
    // rule, stated once in the Object profile instead of inline.
    // That is the whole point of the intersection: this gate was correct and invisible.
    {
      capability: 'terms',
      // ── THE RENAME (design turn 4, 2026-08-06) ────────────────────────────────────
      // "Terms & restrictions" was two questions wearing one title, and 6a rebuilt only
      // one of them. Advance and move-in have moved to their own facet directly below,
      // so this one now asks exactly what it is: WHO IS IT FOR.
      // ⚠️ The facet ID stays `terms` on purpose. It is the key `FacetGroup` persists
      // open/closed state under and the key `sheetOnly` resolves by; renaming it would
      // silently reset every agent's panel and buy nothing — the id is a storage key,
      // the title is the question.
      id: 'terms', title: t('desk_facet_who'), defaultOpen: false,
      badge: (filters.restrictions || []).length || null,
      body: (<>
        {/* 6b — only when these lenses came FROM a client. The marker is pruned in the state
            layer, so by the time it is here every code it names is really in this search. */}
        {/* 6a's door — always available, so "this search is for Nino" is one click from
            inside the panel and not only from her card. */}
        {clientFill && <FillFromClient options={clientFill.options} note={clientFill.note} onPick={clientFill.onPick} />}
        {clientLens && clientLens.derived?.size > 0 && (
          <ClientLensCard name={clientLens.name} derived={clientLens.derived}
            rows={whoRows} selected={whoSelected}
            onAnswer={key => setOpenWhoAxis(key)}
            onOpenClient={clientLens.onOpenClient} />
        )}
        {/* 6a's intro states the honesty rule ON the surface: silence is `not asked`,
            never "no restriction" — the distinction the whole restrictions rework exists
            for, and the one the flat wall could not express. */}
        <p className="text-xs text-muted-foreground mb-1">{t('desk_who_intro')}</p>
        {/* Same StanceChip menu as the property terms modal (owner ruling 2026-07-27) — one
            mechanism for every stance chip in the app. The OPTIONS differ on purpose: this
            surface filters, it does not record, so its values are the agent's two intents
            (hide / only-allowed) rather than the owner's four stances. See RESTRICT_MODES
            in deskFeed.js for why offering the raw stances here would invert the common
            case. Icons/colours are borrowed from the stance vocabulary so the two chips
            still read as the same family. */}
        {/* ⚠️ ACTIVE ONLY — a filter may never offer a retired code (2026-08-05). 11 of the 48
            tenant_terms codes are retired and this list rendered all 48: the three language
            codes (0400 — language turned out to BE nationality), pol_residency and
            pol_local_salary (0421 — measured at zero demand), pol_pet_deposit (0416 — it became
            a rental TERM, a number, not a restriction), three payment methods, and the two
            internal carriers `x_child_teen` / `x_smoker_balcony` which 0405 marks never-show
            because they exist only to hang an expansion edge on.
            Capture surfaces deliberately do NOT filter this way: a stance recorded against a
            code before it was retired is a real answer and has to stay visible and clearable
            (ManualPropertyModal's grid, ManualClientModal's household chips). A filter has no
            such case — nothing here is "already recorded", so a retired code is only a way to
            ask a question the vocabulary no longer answers. `active` is undefined against a
            server older than this deploy, so `!== false` keeps the whole list rather than
            emptying the facet. */}
        {/* ── AXIS ROWS, not a wall (design handoff 6a/6d, 2026-08-05). Each 0398 axis is one
            collapsed row carrying its answer or `not asked`; the pills render only inside the
            axis you opened, broad umbrella codes first. State, wire format (`code:mode`) and
            the ✓/✗ two-answer semantics are UNCHANGED — this is a reshape of the same facet,
            deliberately not a tenth control (adding one costs ~9 wiring sites). */}
        <div className="flex flex-col gap-1 mb-2">
          {whoRows.map(row => {
            const rowLabel = row.label
              ?? t(row.key === 'standard' ? 'desk_who_row_standard' : 'desk_who_row_other')
            const chosen = row.codes.filter(c => whoSelected.has(c.code))
            const open = openWhoAxis === row.key
            return (
              <div key={row.key}>
                <button type="button"
                  onClick={() => setOpenWhoAxis(o => (o === row.key ? null : row.key))}
                  className={`w-full flex items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-xs ${open ? 'bg-muted/60' : 'odd:bg-muted/30 hover:bg-muted/50'}`}>
                  <span className={chosen.length ? 'font-medium' : ''}>{rowLabel}</span>
                  {/* The row's right side IS the answer — the subject, or `not asked` in
                      words. Never an empty cell: silence rendered as blankness is the failure
                      mode 6a exists to fix.
                      ⚠️ The ✓ glyph is gone from the normal case (2026-08-06). With one
                      authorable answer — "my client has this" — a mark that never varies is
                      noise reading as information. It still renders for a LEGACY `restricted`
                      entry, where it is the only thing distinguishing "has a cat" from an old
                      search's "refuses cats". A glyph that appears only when it means
                      something is worth more than one that is always there. */}
                  <span className={`flex items-center gap-1 min-w-0 ${chosen.length ? '' : 'text-muted-foreground'}`}>
                    <span className="truncate">
                      {chosen.length
                        ? chosen.slice(0, 2).map(c => whoSelected.get(c.code).mode === 'hide'
                            ? c.label : `${restrictionGlyph(whoSelected.get(c.code).mode)} ${c.label}`).join(' · ')
                          + (chosen.length > 2 ? ` +${chosen.length - 2}` : '')
                        : t('desk_who_not_asked')}
                    </span>
                    <span aria-hidden>{open ? '▾' : '▸'}</span>
                  </span>
                </button>
                {open && (
                  <div className="mt-1 rounded-lg border border-border p-2 flex flex-wrap gap-1">
                    {/* ── ONE TAP: "my client has this" (owner ruling 2026-08-06) ────────
                        This grid used to open a ✓/✗ menu per pill — "ჩემი კლიენტისთვის:
                        კატა · დაიშვება / არ დაიშვება". The owner's ruling retires the ✗:
                        THIS FACET IS WHERE AN AGENT DESCRIBES THEIR CLIENT, and no agent
                        describing a client needs to say "not allowed". They say what the
                        client has. One gesture, no menu, no polarity question.
                        It also removes a sentence that could not be read at all on the
                        `standard` row, where the subjects are the AD's posture rather than
                        a person: "for my client: without restrictions ✓/✗" parses to
                        nothing (owner-reported, same day).
                        ⚠️ The WIRE FORMAT is untouched — a tap still writes `<code>:hide`,
                        which is the same entry the menu wrote for ✓. Nothing downstream
                        knows this changed.
                        ⚠️ `restricted` is NOT removed from the vocabulary (deskFeed's
                        RESTRICT_MODES, the parser, the server predicate). A saved search or
                        a shared URL from before today may carry one, and it still applies
                        and still shows as selected here — it simply cannot be authored from
                        this panel any more. Dropping the mode instead would silently change
                        what an old saved search means. */}
                    {row.codes.map(x => {
                      const cur = whoSelected.get(x.code) || null
                      const toggle = () => setFilters(f => {
                        const kept = (f.restrictions || []).filter(s => parseRestrictions([s])[0]?.code !== x.code)
                        return { ...f, restrictions: cur ? kept : [...kept, `${x.code}:hide`] }
                      })
                      return (
                        <button key={x.code} type="button" onClick={toggle}
                          aria-pressed={!!cur}
                          className={pill('none', { door: true, extra: cur
                            ? 'border border-foreground bg-foreground/15 font-medium text-foreground'
                            : 'border border-border text-muted-foreground hover:bg-muted' })}>
                          {x.label}
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </div>
        {/* 6a's honesty footer: what is being asked, how much is NOT, and where the
            unanswerable land. The silent-axis count is stated in words because "not asked"
            as an absence is exactly what the old wall rendered — i.e. nothing. */}
        {(() => {
          const asking = whoRows.flatMap(r => r.codes.filter(c => whoSelected.has(c.code)))
          const silent = whoRows.filter(r => r.key !== 'standard' && r.key !== 'other'
            && !r.codes.some(c => whoSelected.has(c.code))).length
          return (
            <div className="text-[11px] text-muted-foreground mb-2 space-y-0.5">
              {asking.length > 0 && (
                <p>
                  {t('desk_who_asking_for')}{' '}
                  <b className="text-foreground">
                    {/* Same glyph rule as the rows and the 6b chips: a mark that never
                        varies is noise. It renders only for a legacy `restricted` entry. */}
                    {asking.map(c => whoSelected.get(c.code).mode === 'hide'
                      ? c.label : `${restrictionGlyph(whoSelected.get(c.code).mode)} ${c.label}`).join(', ')}
                  </b>
                  {silent > 0 && <> · {silent} {t('desk_who_axes_silent')}</>}
                </p>
              )}
              <p>{t('desk_who_shelf_note')}</p>
            </div>
          )
        })()}
        {/* No honesty note here: `desk_who_shelf_note` in the footer above already states
            the same rule in this facet's own words, and 6a designed that footer to be the
            one that speaks for the axis rows. Two sentences saying "unrecorded still
            shows" on one panel is how a screen stops being read. */}
      </>),
    },
    // ── TERMS, now its own question (design turn 4, 2026-08-06) ──────────────────────
    // Advance and move-in were living inside the restrictions facet for one historical
    // reason — they arrived together — and the 6a rebuild made the mismatch loud: a facet
    // of axis rows asking who the owner will accept, with a money dropdown and a date
    // picker stapled underneath. They are the Intent overlay's own terms
    // (SEARCH_GRAMMAR: Rent → Deposit · Advance · Move-in), so they get their own row.
    // ⚠️ Same `capability: 'terms'` gate as the facet above, deliberately: that key names
    // the OBJECT profile (Client 'M', Leads 'O'), not the word in either title. The
    // intent gate is the inline `applies(cap, 'advance')` — one question, one gate, both
    // where they already were. Nothing about what these two filters DO changed here.
    {
      capability: 'terms',
      id: 'rental_terms', title: t('desk_facet_rental_terms'), defaultOpen: false,
      badge: ((filters.advanceMaxMonths !== '' ? 1 : 0) + ((filters.minLeaseStay ?? '') !== '' ? 1 : 0) + (filters.moveInBy !== '' ? 1 : 0)) || null,
      body: (<>
        {/* Move-in ceiling in MONTHS (owner ruling 2026-07-27, replacing the deposit-amount
            + no-last-month pair): "1 month" = the owner accepts move-in with just one
            month's payment and demands no more. Months, because that is how the market
            speaks — first+last IS the deposit (RESTRICTIONS_MARKET_KNOWLEDGE §4). */}
        {/* Stage 3 — the ONE term measurement confirms is rent-only (B269). A sale search no
            longer offers it. Restrictions above are deliberately NOT gated on intent: policy
            rows exist on sale and girao, so hiding them would hide real data. */}
        {applies(cap, 'advance') && (
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="w-28 shrink-0">{t('desk_filter_advance_max')}</span>
            <select className={input} value={filters.advanceMaxMonths ?? ''}
              onChange={e => set('advanceMaxMonths', e.target.value)}>
              <option value="">—</option>
              {[1, 2, 3].map(n => <option key={n} value={n}>{n} {t('desk_filter_advance_month_unit')}</option>)}
            </select>
          </label>
        )}
        {/* core 4238 (owner 2026-11-04): how long the client will rent. A flat whose stated minimum lease is longer is
            hidden; one that states no minimum goes to possible matches — never hidden for what it did not say. */}
        {applies(cap, 'advance') && (
          <label className="flex items-center gap-2 text-xs text-muted-foreground mt-1.5">
            <span className="w-28 shrink-0">{t('desk_filter_minlease_stay')}</span>
            <select className={input} value={filters.minLeaseStay ?? ''}
              onChange={e => set('minLeaseStay', e.target.value)}>
              <option value="">—</option>
              {[1, 3, 6, 12].map(n => <option key={n} value={n}>{n} {t('desk_filter_advance_month_unit')}</option>)}
            </select>
          </label>
        )}
        {/* The OTHER move-in question (0220 step 4): when does the client need to be IN.
            A date, matching the owner-stated date it is compared against. By default a gap
            SHELVES onto possible-matches rather than hiding — a date is the most negotiable
            thing on a match, and the agent decides who waits. The checkbox is the exception
            the owner named: "if someone says I absolutely need it today, then yes, filter." */}
        <label className="flex items-center gap-2 text-xs text-muted-foreground mt-1.5">
          <span className="w-28 shrink-0">{t('desk_filter_movein_by')}</span>
          {/* min-w-0: a flex child defaults to min-width:auto, and a native date input's
              intrinsic width (mm/dd/yyyy + picker button) is wider than this rail — w-full
              alone cannot shrink it, so it overflowed the sidebar. */}
          <DateInput className={`flex items-center gap-0.5 ${input} min-w-0`} value={filters.moveInBy ?? ''}
            onChange={v => set('moveInBy', v)} />
        </label>
        {(filters.moveInBy ?? '') !== '' && (
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1 ml-[7.5rem]">
            <input type="checkbox" checked={!!filters.moveInHard}
              onChange={e => set('moveInHard', e.target.checked)} />
            {t('desk_filter_movein_hard')}
          </label>
        )}
        <p className="text-xs text-muted-foreground mt-1">{t('desk_filter_terms_honesty_note')}</p>
      </>),
    },
    // ── NO `status` FACET ENTRY (owner, 2026-08-18) ─────────────────────────────────────
    // The state axis lived here for part of one day (designer Call B moved it down from the
    // pool box that morning; the owner moved it back the same day — "it was moved below…
    // move it up there"). It renders inside `poolBox` again, built as `stateBox` in the
    // category entry's closure. Nothing opens a 'status' sheet (checked: no qp.sheet or
    // setFacetSheet caller uses it), and 'status' is removed from GRAMMAR_ORDER too —
    // facet-order-check fails the gate on a ranked id no facet declares. The entry is gone
    // rather than kept as a vestige — the last vestige here carried a dead four-option
    // <select> full of retired words.
  ].filter(Boolean)
  // Meeting view (2026-07-22 — REVERSES the 2026-07-17 "special feeds keep their own
  // controls, no sidebar" ruling, owner-confirmed): the sidebar now renders there, but
  // ONLY the Category facet — price/area/rooms don't wire into the meetings feed, and
  // per the §12 rule ("hide what can't apply instead of letting it lie") they must not
  // render as silent no-ops. Pool/Business/Leads keep their own controls for now.
  // ── STAGE 3: the interview ───────────────────────────────────────────────────────────
  // THE GRAMMAR'S ORDER, declared once (SEARCH_GRAMMAR §1). The panel used to render 17
  // accordions in the order they were historically added — Operational above Location and
  // Budget. The grammar is an interview: each answer narrows the next question, so the
  // sections follow the sentence. `category` (the identity tree) stays first because it is
  // navigation, not a question; identity is already established by the time the rest runs.
  const GRAMMAR_ORDER = [
    'category',                                     // ← navigation (the tree)
    'lead_sources',                                 // Which portal? — see below
    // 0779/0780 — "how it was added" renders IMMEDIATELY after "which portal", because the two
    // are only legible as a pair: one asks where the advert is listed, the other how it reached
    // us. Read apart they sound like the same question, which is precisely how Facebook ended up
    // offered twice in the portal list. (Sixth facet caught by the test below before shipping —
    // an id missing from this list sorts to 999 and lands under Date, wherever it was declared.)
    'added_how',                                    // …and how did it reach us?
    'seller',                                       // 1685 whose property? (owner 2026-09-21: its own question)
    'contact',                                      // …and who handles it (agent → whom they act for)
    'location',                                     // Where?
    // …and then WHERE EXACTLY. The street facet is the second half of the same question,
    // which is why it is declared next to `location` in the facet array and why its own
    // empty state is "pick a district first — its streets appear here".
    // 🚨 IT WAS MISSING FROM THIS LIST and the sort below does exactly what its comment
    // says an unlisted id does: sorts it to 999, i.e. dead last. So the facet rendered
    // after Date, at the very bottom of the sidebar — a street question asked ten
    // questions after the district it depends on. Declaration order in the `facets` array
    // has NO effect here; this list is the only thing that orders the panel.
    'street',                                       // Where exactly?
    'complex',                                      // …or which development? (B1145)
    'project',                                      // what kind of building? (typology)
    // 0688: metro closes the Location run. It is a where-question, so it belongs to this
    // layer and not to Characteristics — but it comes AFTER street because it is a coarser
    // answer: a station names a walk, a street names an address. (Third facet to be told
    // the 🚨 above: an id missing from this list sorts to 999 and lands under Date.)
    'metro',                                        // …or near which station?
    // …and HOW FAR from it. Closes the Location run because it is the only facet in it that
    // does not narrow WHERE — it narrows how much of "there" counts, so it can only be asked
    // once a point exists, and it renders only when one does.
    // (Fourth facet told the 🚨 above, and the order test caught it before shipping rather
    // than an owner finding a distance control under Date.)
    'near_radius',                                  // …and how far from it?
    'price',                                        // Budget?
    // B838: სველი წერტილი sits RIGHT AFTER bedrooms (owner, twice — the second time because
    // this list was missed and the facet sorted to 999, the street facet's exact 🚨 above).
    // B838's full order (owner): rooms · bedrooms · სველი წერტილი · details (floors + isolated
    // kitchen) · area — the details group follows the wet points it used to bury.
    // 0757: balcony_count sits in Size beside wet_points — both are 'how many of X does
    // it have', both are one-tap pill rows, and the grammar layer is the same question.
    'rooms', 'bedrooms', 'wet_points', 'balcony_count', 'details', 'area', // Size?
    // Characteristics? — what it is like, who it is for, and on what terms. `terms` is the
    // Who-is-it-for facet (its id is a storage key, see the rename note at the entry) and
    // `rental_terms` is advance + move-in, split out of it in turn 4. Order is a judgment
    // call, not a ruling: people before paperwork, both after the flat itself. It is one
    // line to change if the owner reads it the other way round.
    // `condition` leads the Characteristics run (designer's Q3): after size, before amenities.
    // The interview stays linear — where → how much → how big → what state → nice-to-haves —
    // and state eliminates far more listings than any amenity does, so it earns the earlier
    // slot. The alternative considered was placing it adjacent to the size facets, on the
    // argument that frame state changes what a square-metre figure MEANS; rejected because
    // ფართობი is a slider block and this is a list, and the two adjacent read as one crowded
    // control.
    // 0764: ავეჯი sits between condition and features, and the position is an argument.
    // It is the same KIND of question as condition — what state is it in, what comes with it —
    // and it must land immediately before the amenity checklist because the plain "ავეჯით" box
    // lives THERE. The degree ladder and the yes/no it refines would read as unrelated controls
    // if a facet came between them. (Fifth facet to be told the 🚨 above: an id missing from
    // this list sorts to 999 and lands under Date, whatever position it was declared in — and
    // the test that guards it caught this one before it shipped.)
    // 0786: კლასი sits immediately after მდგომარეობა because they are the two axes of the same
    // subject — how FAR ALONG the work is, and how EXPENSIVE it was. Read apart they get confused
    // for each other, which is exactly what happened to reno_clean / reno_premium before 0788
    // moved them onto the class ladder where they belonged.
    'condition', 'apartment_class', 'furnishing', 'features', 'terms', 'rental_terms',
    'agent', 'dates', 'leadwork',                   // Operational? (status moved back to the
                                                    // pool box, owner 2026-08-18 — see the
                                                    // note where its facet entry used to be)
    'leadseller',                                   // Intelligence?
    // …and advert quality CLOSES the Intelligence run. It belongs to this layer because it
    // describes what WE know about the advert, not what the property is — but it comes
    // last, after leadseller, because it is the question an agent asks about the answers
    // they already have ("of these, which are actually workable?") rather than one that
    // narrows the market. Fourth facet to be told the 🚨 above: an id missing from this
    // list sorts to 999 and lands under Date, whatever position it was declared in.
    'advert_quality',
  ]
  // ⚠️ `lead_sources` (Portal) moved OUT of Operational and up behind the tree
  // (design handoff 2a §5, 2026-08-02). It looks operational — provenance, "where did
  // this come from" — but it is not: it only exists in the Leads pool, and it asks
  // which slice of THAT POOL you are standing in. It belongs with the pool question it
  // qualifies, not with status/agent/dates, which ask about our relationship to a
  // property and apply in both pools.
  //
  // ⚠️ It is also NOT a breakdown of the pool. The filter predicates on the LISTING's
  // source and the results then cluster by property (0190), so a flat advertised on both
  // myhome and ss.ge appears under BOTH. Any counts added here must over-sum, and nothing
  // may imply a partition — no proportions, no "of 22,666". Today it carries no counts at
  // all, which is honest and is what ships.
  // 🔒 Identity is chosen once (§5b): the desktop panel never asks Deal/Type again — the tree
  // owns them. sheetOnly (mobile) bypasses this list on purpose, above.
  // Capability then removes what cannot apply, rather than rendering a dead control (§12):
  // `capability` is a key, or a predicate for facets whose controls span several keys.
  const capabilityAllows = f => !f.capability
    || (typeof f.capability === 'function' ? f.capability(cap) : applies(cap, f.capability))
  // ── GHOST CHIPS: name what is NOT being asked (design 4b, Q7) ──────────────────────
  // Stage 3 removes a facet that cannot apply rather than rendering a dead control, and
  // that was the right call — a control that can never match reads as broken. But removal
  // is SILENT, and silence has its own failure: an agent who used "rooms" yesterday on an
  // apartment and cannot find it today on a plot of land has no way to learn that the
  // question was dropped on purpose. They look for a bug that is not there.
  //
  // Removing beats a dead control; NAMING beats both. These are the questions the current
  // identity does not answer, listed and never clickable. Same rule the brief states for
  // "unavailable": the agent must never wonder where a filter went.
  //
  // ⚠️ Derived from the SAME predicate that does the removing — never a second hand-kept
  // list. A parallel list is how the two drift apart, which is the exact disease Stage 3
  // cured by folding showLandArea and the Terms gate into one capability question.
  // ⚠️ `poolViewFacet` is excluded for the same reason `identityFacet` is: those questions
  // ARE asked, just somewhere else on this panel (the tree, the pool box). Listing them as
  // "not asked here" would be a ghost chip that lies — the row exists to explain a genuine
  // absence, and a question relocated is not a question dropped.
  // ── THE REASON A FACET CANNOT APPLY, in one place ────────────────────────────────────
  // Lifted out of the mobile sheet (2026-08-19) so both surfaces say the same sentence. It
  // names the CAUSE, not the rule — the reason is a property of the THING, so the line reads
  // `მიწას ოთახი არ აქვს`, land has no rooms.
  // ⚠️ `tOr`, never `t`: `t()` returns the KEY when a string is missing, so an unrecognised
  // kind would render `desk_facet_not_for_kind_garage` at an agent. Falls back to the
  // identity-free line when the kind is absent or multi — a wrong subject is worse than a
  // general one.
  const refusalReason = facetTitle => {
    const tpl = identityPath.kind ? tOr(`desk_facet_not_for_kind_${identityPath.kind}`, '') : ''
    return tpl ? tpl.replaceAll('{facet}', facetTitle) : t('desk_facet_not_for_identity')
  }
  // ── 🚨 A FACET THAT CANNOT APPLY CLOSES; IT DOES NOT MOVE (owner, 2026-08-19) ─────────
  // *"we didn't need these when we are in land category, so these don't go anywhere but get
  // closed when not needed."* This REVERSES Stage 3's removal rule, deliberately and on the
  // owner's word. Removing a facet and naming it in a ghost-chip line at the BOTTOM of the
  // panel is honest but it reshuffles the panel every time the identity changes: the row you
  // were reaching for moves, and the explanation is nowhere near the gap it explains.
  // Now the facet keeps its position in GRAMMAR_ORDER and renders as the same dashed
  // `UnavailableFacet` row the mobile sheet already used — closed, uninteractive, with the
  // reason on the row itself. Spatial memory survives an identity change; nothing lies.
  // ⚠️ It is NOT a collapsed FacetGroup. A group can be opened, and opening one whose controls
  // cannot filter is the dead-control defect Stage 3 removed them to avoid — the agent would
  // set a value that changes nothing (`applicableFilters` drops it at the query edge AND the
  // chip row). A static dashed row cannot be opened, so it states the absence without
  // offering to fill it. That distinction is the whole reason this is safe to reverse.
  const facetList = (entryType === 'Meeting' ? facets.filter(f => f.id === 'category') : facets)
    .filter(f => !f.identityFacet && !f.poolViewFacet)
    .map(f => (capabilityAllows(f) ? f : { ...f, unavailable: f.unavailable || refusalReason(f.title) }))
    .slice()
    .sort((a, b) => {
      // An unlisted facet sorts to the END, never to index -1 (which would silently make it
      // first — the exact drift the deskUrl PARAM_ORDER comment records).
      const ia = GRAMMAR_ORDER.indexOf(a.id), ib = GRAMMAR_ORDER.indexOf(b.id)
      return (ia < 0 ? 999 : ia) - (ib < 0 ? 999 : ib)
    })
  // Clearing returns status to the NEUTRAL word, `active` — not the retired `all`, which the
  // server reads as no status and which lights neither pill. See the `const active` note.
  const resetAll = () => { setFilters({ ...ADV_DEFAULTS }); onStatusChange('active') }
  // §8b: the identity facet leaves the accordion and becomes the card above it. Everything
  // else keeps its order and its component — only its rank changes.
  const identityFacet = facetList.find(f => f && f.id === 'category' && !f.unavailable)
  const narrowFacets = facetList.filter(f => !(f && f.id === 'category' && !f.unavailable))

  // ── Single-facet bottom sheet (strip facet pills) ──
  if (sheetOnly) {
    const f = facets.find(x => x.id === sheetOnly)
    if (!f) return null
    // ⚠️ CAPABILITY APPLIES HERE TOO (2026-08-18). This lookup goes to the raw `facets` list,
    // deliberately — a pill must resolve by id whatever the identity is — but it then rendered
    // `f.body` unconditionally, so the phone was the one surface that still handed an agent a
    // control the chosen identity cannot answer. Pick land, tap რიცხვები/ოთახები, set 2 rooms:
    // `applicableFilters` drops it at both the query edge and the chip row (higher hierarchy
    // wins, 2026-08-01), so the sheet accepted an answer that changed nothing. Desktop has been
    // safe since Stage 3 removes the facet and the ghost row names it; mobile never got either
    // half. Say the same thing the desktop panel says instead of taking the input — the words
    // come from the registry's own `unavailable`, so there is still no second list.
    const sheetRefused = !f.unavailable && !capabilityAllows(f)
    // ── NAME THE CAUSE, not the rule (designer, 2026-08-18) ──────────────────────────────
    // Shipped as "not asked for this type" and overruled: the reason is a property of the
    // THING, not of the control, so the line says `მიწას ოთახი არ აქვს` — land has no rooms.
    // The register stays "not asked"; it is the subject that changes.
    // ⚠️ ONE TEMPLATE PER KIND with the facet title dropped in, never a phrase composed from
    // parts. Georgian inflects the possessor (მიწა → მიწას, ბინა → ბინას) and this repo has
    // already shipped a generator that built ungrammatical Georgian while reporting success
    // (RESTRICTIONS_GALLERY_AND_GRAMMAR_2026-08-04) — a whole template per kind is four
    // strings a native speaker can read, instead of a rule that has to be trusted.
    // The facet titles interpolate in the nominative (ოთახი / სართული / ფართობი), which is
    // what these templates are written around.
    // Falls back to the identity-free line whenever the kind is absent or unrecognised — a
    // multi-kind selection has no single subject to name, and a wrong subject is worse than
    // a general one.
    // ⚠️ `tOr`, never `t`, for the dynamic key: `t()` returns the KEY ITSELF when a string is
    // missing, so a `t(...) || fallback` here is structurally dead code and an unrecognised
    // kind would render `desk_facet_not_for_kind_garage` at an agent. That exact bug shipped
    // once already (see tOr's own header) — this is what the helper exists for.
    return (
      <div className="fixed inset-0 z-[60]" onClick={onCollapse}>
        <div className="absolute inset-0 bg-black/50" />
        <div className="absolute inset-x-0 bottom-0 rounded-t-2xl bg-background border-t border-border max-h-[80vh] flex flex-col"
          onClick={e => e.stopPropagation()}>
          <div className="flex items-center px-4 pt-3 pb-2 shrink-0">
            <span className="flex-1 text-base font-semibold">{f.title}</span>
            <button className="px-1 text-lg leading-none text-muted-foreground" aria-label={t('desk_search_collapse_tools_tooltip')}
              onClick={onCollapse}>✕</button>
          </div>
          <div className="flex-1 overflow-y-auto px-4 pb-3 space-y-1.5">
            {f.unavailable
              ? <UnavailableFacet title={f.title} reason={f.unavailable} />
              : sheetRefused
                ? <UnavailableFacet title={f.title} reason={refusalReason(f.title)} />
                : f.body}
          </div>
          {/* ⚠️ NO "show N results" WHEN THE SHEET COLLECTED NOTHING (designer, 2026-08-18).
              That button's promise is "apply what I picked", and a refused sheet has nothing
              to apply — offering it makes the refusal look like a step that did something.
              ✕ and the backdrop already dismiss, so nothing is trapped. */}
          {resultsCount != null && !sheetRefused && !f.unavailable && (
            <div className="p-3 border-t border-border shrink-0">
              <button className="w-full rounded-full bg-primary text-primary-foreground text-sm font-semibold py-2.5 shadow-lg"
                onClick={onCollapse}>
                {tLog('desk_search_show_results', { n: resultsCount })}
              </button>
            </div>
          )}
        </div>
      </div>
    )
  }

  // ── Mobile: full-screen Filter page (2026-07-18 user ruling, corrected same day) ──
  // The ⚙ page is the NORMAL scrollable panel we already had — every facet's content
  // inline (accordion groups), full-screen with a ✕/title/clear header and the sticky
  // Show-N button. Bottom sheets are reserved for the INDIVIDUAL facet pills in the
  // command strip (next pass), never for this page.
  if (PANEL_IS_MOBILE) {
    // ── THE POOL, PINNED (design 2c) ───────────────────────────────────────────────
    // Below the title bar, ABOVE the scroll area — `shrink-0` outside the `overflow-y-auto`
    // is what makes it stay put. Until now the pool box scrolled away with the Category
    // accordion, so on a phone "which inventory am I in" — the first question this panel
    // asks, and the one that changes what every other answer means — could be off-screen
    // while the agent edited price and rooms. Same element as the desktop rail's, taken
    // from the facet registry rather than rebuilt (see the category entry's `poolBox`).
    const poolBox = facets.find(f => f && f.id === 'category')?.poolBox
    return (
      <div className="fixed inset-0 z-50 bg-background flex flex-col">
        <div className="flex items-center px-2 py-2.5 border-b border-border shrink-0">
          <button className="px-2 text-lg leading-none text-muted-foreground" aria-label={t('desk_search_collapse_tools_tooltip')}
            onClick={onCollapse}>✕</button>
          <span className="flex-1 text-center text-sm font-semibold">{t('desk_search_tools_bare')}</span>
          <button className={`px-2 text-xs ${active ? 'text-primary' : 'text-muted-foreground/40'}`}
            disabled={!active} onClick={resetAll}>{t('desk_search_tools_clear')}</button>
        </div>
        {/* ⚠️ PINNED WITH THE POOL, and both of these were PASSED TO THIS COMPONENT AND
            NEVER RENDERED until 2026-08-18 — `readout` and `poolSwitch`/`onUndoPoolSwitch`
            arrive on the mobile instance exactly as they do on the desktop one (see both
            <AdvancedSearchPanel> call sites) and this branch dropped them on the floor. A
            prop that is threaded but not consumed is the quietest kind of missing feature:
            nothing errors, nothing is empty, the phone simply answers less than the laptop.
            · CarryOverBanner(sw) is the pool switch STATED. Design 2b §2 makes saying it
              mandatory — every filter is kept across a switch, and Rule 1 is that the count
              must never move under the agent's feet unexplained. It is pinned rather than
              scrolled for the same reason poolBox is: the switch can be made from a scrolled
              position, so a banner living at the top of the scroll area would explain the
              change off-screen.
            · ThreeAnswerReadout is matched / maybe / ruled-out — the three-answer rule's own
              display. On a phone the facets live behind a page, so this is the only place the
              shelf tier is ever named; without it "maybe" folds silently into "no", which is
              the one collapse that rule forbids.
            Both self-null (no switch, nothing narrowed yet → nothing drawn), so neither costs
            a pixel of a phone's height until it has something to say — which is also why the
            wrapper below tests all three rather than poolBox alone. */}
        {(poolBox || poolSwitch) && (
          <div className="px-4 pt-2 shrink-0 border-b border-border">
            {poolBox}
            <CarryOverBanner sw={poolSwitch} onUndo={onUndoPoolSwitch} />
            <CarryOverBanner client={clientFill?.applied} onUndo={clientFill?.onUndoFill} />
          </div>
        )}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {/* ⚠️ THE READOUT SCROLLS; THE BANNER DOES NOT — and the split is measured, not felt.
              Both were pinned when this shipped an hour earlier, and the harness said what that
              costs: in a 620px panel the pinned block went pool box 207 + banner 77 + readout 74
              = 390px, leaving 124px of scroll for 1,314px of facets. 63% chrome.
              The banner earns its pin: it explains a change the agent can trigger from a
              scrolled position (the pool box is pinned), so at the top of the scroll it would
              narrate off-screen — and it self-nulls, so it costs nothing until there is a switch
              to explain. The readout earns nothing by pinning: the panel OPENS at the top of
              this scroll area, so it is on screen exactly when it is read, and it goes away only
              once the agent is deep in the facets — at which point they are editing, not
              reading a total. Pinned it was 74px of permanent chrome on a phone in the COMMON
              case, since it draws whenever a filter has narrowed anything.
              Open for the designer to overrule; the numbers above are what to overrule. */}
          <ThreeAnswerReadout r={readout} />
          {dealChange && <CarryOverBanner deal={dealChange} onUndo={undoDealChange} />}
          {facetList.map(f => (f.unavailable
            ? <UnavailableFacet key={f.id} title={f.title} reason={f.unavailable} />
            : (
              <FacetGroup key={f.id} id={f.id} title={f.title} defaultOpen={f.defaultOpen !== false} badge={f.badge}>
                {f.body}
              </FacetGroup>
            )))}
          {/* The third dropped one. Ghost chips name the questions this identity does not
              answer — "the agent must never wonder where a filter went" (design 4b Q7). That
              worry is WORSE on a phone: a desktop agent sees the whole rail at once and can
              tell a short list from a broken one, while here the facets are behind a page, so
              a removed question just looks like it was never there. Same position as the
              desktop return: after the facets, since it describes their absence. */}
        </div>
        {resultsCount != null && (
          <div className="p-3 border-t border-border shrink-0">
            <button className="w-full rounded-full bg-primary text-primary-foreground text-sm font-semibold py-2.5 shadow-lg"
              onClick={onCollapse}>
              {tLog('desk_search_show_results', { n: resultsCount })}
            </button>
          </div>
        )}
      </div>
    )
  }

  // ── Desktop: the 2026-07-17 accordion sidebar, unchanged in behavior ──
  //
  // ── THE PANEL SCROLLS ON ITS OWN (owner, 2026-08-19) ─────────────────────────────────
  // *"We also need to make side search bar scrollable. It became too large and we need to be
  // able to scroll solely a filter bar."* It had grown to ~1,300px of facets against a
  // ~900px viewport, so reaching მახასიათებლები meant scrolling the RESULTS — the agent had
  // to move the thing they were filtering in order to reach the filter.
  //
  // ⚠️ ONE REGION, NOT THREE — the owner ruled the same day, on seeing it (2026-08-19):
  // *"we should make full sidebar scrollable, not just bottom part and top part static."*
  // The first cut of this (earlier the same day) made the split the mobile page makes:
  // chrome and identity PINNED (`shrink-0`), the facet run scrolling (`flex-1 min-h-0
  // overflow-y-auto`). The argument for pinning identity was real — it is the answer that
  // changes what every other answer MEANS, which is why design 2c pins the pool box above
  // the phone's scroll area — and the comment here even named the cost and predicted this
  // complaint, prescribing §8b item 1 (collapse identity to a breadcrumb) as the fix
  // "not un-pinning it". The owner chose un-pinning. Kept on the record because the
  // breadcrumb collapse is still worth building; it is now an improvement to a scrolling
  // panel, not a precondition for one.
  //
  // So: the CARD is the scroller and everything rides in it — panel header, identity, the
  // boundary line, every facet. Nothing is `shrink-0`, and there is no inner scroll region;
  // a second scrollbar inside the first is the exact shape being removed.
  //
  // ⚠️ The bound is still `max-h-[calc(100vh-5rem)]` and it still has to leave room for the
  // sticky app header (StickyAside's `maxTop()` = header height + 8px), or the panel's first
  // rows sit under it. The old `flex flex-col` + `min-h-0` pair is gone with the inner
  // region — with one scroller the max-height is the whole mechanism.
  //
  // ⚠️ HOW THIS MEETS StickyAside. That component measures `inner.offsetHeight` and pins the
  // panel top or bottom depending on scroll direction, because the panel used to be TALLER
  // than the viewport. Bounding the height here means it no longer can be: `minTop(h)`
  // collapses to `maxTop()`, the `fixedBottom` branch stops being reachable, and the panel
  // simply pins under the header. That is a simplification of StickyAside's job, not a
  // conflict with it — but it is why the two must be read together, and why the max-height
  // must leave room for the header (`maxTop()` = header height + 8px).
  //
  // Width: 15rem → 16rem, and 16rem → 18rem at xl (owner: *"preserve width and also make it
  // little wider if design allows"*). The border stays — removing it was offered as a
  // fallback *"if needed"*, and it is not needed once the panel stops running off the page.
  return (
    // B1196: THIS is the element that scrolls — a max-h card with its own overflow, measured at
    // scrollHeight 3416 vs clientHeight 818. The page does not scroll, which is why three
    // earlier fixes written against window.scrollY moved nothing at all.
    <div ref={panelScrollRef}
      // F-047 (owner, 2026-09-06): "when I am standing on the filter bar and scroll down it will not any more,
      // because inside the panel it is already at the bottom — I have to point outside and scroll the full page."
      // `overscroll-contain` (added with the one-scroller panel, 2026-08-19) swallowed the wheel at the panel's
      // ends; the browser's default hands the remaining scroll to the page, which is what an agent expects.
      className={`${card} space-y-3 max-h-[calc(100vh-5rem)] overflow-y-auto`}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-semibold text-muted-foreground"><IconText>{t('desk_search_tools_panel_header')}</IconText></p>
        <div className="flex items-center gap-2 shrink-0">
          {/* გასუფთავება moved to the §8b boundary rule below — one clear control, and it
              belongs on the line that says where narrowing starts, not in the chrome. */}
          {/* Collapse to the thin rail (see the aside's collapsed branch). */}
          <button title={t('desk_search_collapse_tools_tooltip')} aria-label={t('desk_search_collapse_tools_tooltip')}
            className="text-muted-foreground hover:text-foreground text-sm leading-none"
            onClick={onCollapse}>◂</button>
        </div>
      </div>
      {dealChange && <CarryOverBanner deal={dealChange} onUndo={undoDealChange} />}
      {/* eBay-style facet groups (2026-07-17): each group collapses independently (state
          persists per group), enums are checkboxes (OR within a facet), ranges keep the
          histogram sliders. A closed group with active picks shows a count badge — the
          constraint-chip row above the feed stays the single truthful full display. */}
      {/* ── §8b (design turn 8): IDENTITY IS A CARD, NOT A SECTION ────────────────────────
          The panel drew identity and filters as one visual class — იყიდება / ქირავდება sat at
          the same indent and weight as წყარო's checkboxes, and by მახასიათებლები the deal type
          had scrolled out of view entirely. Nothing said which answer changes what the others
          MEAN. Turn 4 set the order; turn 8 sets the ranking, with the same components.
          The border is deliberately BRIGHTER than the panel's own, and this must stay the only
          element in the panel with this treatment — that uniqueness is what makes its edge
          legible without reading it.
          ⚠️ IT IS NOT STICKY, AND `sticky top-0 z-10` MUST NOT COME BACK (owner, 2026-08-13:
          "we really dont need to make this stick here while scrolling"). It was sticky to
          guarantee the identity answer could never scroll away — but the panel is ALREADY
          pinned by StickyAside, so a second pin inside the first had nothing to gain and one
          thing to lose: the card held the panel's top while the facets scrolled UNDER it, so
          the row beneath it rendered half-covered. The §8b item-1 breadcrumb collapse is the
          answer to "identity scrolled away", not a second sticky.
          ⚠️ NOT YET BUILT from §8b item 1: the collapse-to-breadcrumb (ancestor crumbs +
          deepest crumb + count sentence + შეცვლა). The crumbs live inside the category
          facet's own closure and it has to publish them before this card can render them;
          until then the card carries the full tree, which is correct, just taller. */}
      {identityFacet && (
        <div
          style={{ border: '1px solid hsl(var(--foreground) / 0.35)', borderRadius: '9px',
                   background: 'hsl(var(--muted) / 0.4)', padding: '10px' }}>
          <p style={{ fontSize: '10px', lineHeight: '14px', textTransform: 'uppercase',
                      letterSpacing: '.1em', color: 'hsl(var(--muted-foreground))', marginBottom: '6px' }}>
            {t('desk_search_identity_eyebrow')}
          </p>
          {identityFacet.body}
          <CarryOverBanner sw={poolSwitch} onUndo={onUndoPoolSwitch} />
          <CarryOverBanner client={clientFill?.applied} onUndo={clientFill?.onUndoFill} />
          {/* Directly under the identity block, per design 2a §4 — the readout answers
              "what did that just do to the corpus", so it belongs beside the question that
              did it, not at the bottom of a scroll. */}
          <ThreeAnswerReadout r={readout} />
        </div>
      )}
      {/* ── §8b item 2: THE BOUNDARY IS WRITTEN, NOT IMPLIED ───────────────────────────────
          One line of text does what sixteen identical headings could not: it names where
          identity stops and narrowing begins. გასუფთავება sits at its far right because this
          is the line that owns everything below it. */}
      {narrowFacets.length > 0 && (
        <div className="flex items-center gap-2">
          <span style={{ fontSize: '10px', lineHeight: '14px', textTransform: 'uppercase',
                         letterSpacing: '.1em', color: 'hsl(var(--muted-foreground) / 0.8)' }}>
            {t('desk_search_narrow_label')}
          </span>
          <span className="flex-1" style={{ height: '1px', background: 'hsl(var(--border) / 0.8)' }} />
          {active && (
            <button className="text-[10px] underline decoration-dotted shrink-0"
              style={{ color: 'hsl(var(--muted-foreground) / 0.8)' }}
              onClick={resetAll}>{t('desk_search_tools_clear')}</button>
          )}
        </div>
      )}
      {/* The facet run. NOT its own scroll region any more (owner 2026-08-19, see the top of
          this return) — the card scrolls, and a scroller nested in a scroller is what made
          the panel read as a static top over a moving bottom. The `-mr-2 pr-2` gutter trick
          went with it: the scrollbar now belongs to the card, outside this group's box. */}
      <div className="space-y-3">
        {narrowFacets.map(f => (<div key={f.id}>
          {f.unavailable
            ? <UnavailableFacet title={f.title} reason={f.unavailable} />
            : (
              <FacetGroup id={f.id} title={f.title} defaultOpen={f.defaultOpen !== false} badge={f.badge}>
                {f.body}
              </FacetGroup>
            )}
        </div>))}
        {/* The questions this identity does not answer — stated, never silently dropped.
            Collapsed to one line by default: it is context, not a control, and it must not
            compete with the facets that DO apply. Nothing here is clickable, and that is the
            point — a ghost chip explains an absence, it does not offer to fill it.
            Inside the scroller with the facets, because it describes THEIR absence — pinning
            it would put a footnote above the thing it annotates. */}
      </div>
    </div>
  )
}

// ── CARRY-OVER BANNER (design 2b §2) ─────────────────────────────────────────────────
// Appears for one event only: the pool just changed and your filters came with you.
// It exists because BOTH silent alternatives are wrong — resetting the filters turns
// "2-room in Vake, mine or a lead" back into two searches, and keeping them without a
// word breaks the rule that a number must mean what it says the moment the count moves.
//
// The "before" figure is the last settled total of the pool you left; the "after" is
// whatever the new pool reports, which arrives a beat later and renders as … until it
// does. A number that has not arrived is never drawn as 0 (brief §4).
// ⚠️ ONE component, TWO triggers (design 4b: "identical component to the pool-switch
// banner; there is ONE carry-over mechanism, not two"). `sw` is a pool switch, `deal` is a
// Sale⇄Rent change. They differ only in which lines they have to draw: a pool switch can
// quote before/after totals because both pools answer the same question, and a deal change
// cannot — a sale total and a rent total are not two readings of one number.
function CarryOverBanner({ sw, deal, client, onUndo }) {
  // B842. The client fill now replaces the WHOLE search, so it owes the agent the same two
  // things the pool switch owes: say what happened, and offer the way back. Its own copy —
  // reusing the deal-change headline would announce a deal change that did not occur.
  if (client) {
    return (
      <div className="mb-2 rounded-lg border border-border border-l-[3px] border-l-muted-foreground bg-muted/30 px-2.5 py-2">
        <div className="text-[12px] leading-[17px] text-foreground">
          {tLog('desk_client_fill_applied', { name: client.name || t('desk_suggest_unnamed_client') })}
        </div>
        <div className="mt-0.5 text-[11px] leading-4 text-muted-foreground">{t('desk_client_fill_replaced')}</div>
        {onUndo && (
          <button type="button" onClick={onUndo}
            className="mt-1 text-[11px] leading-4 text-muted-foreground underline decoration-dotted hover:text-foreground">
            {t('desk_client_fill_undo')}
          </button>
        )}
      </div>
    )
  }
  if (deal) {
    return (
      <div className="mb-2 rounded-lg border border-border border-l-[3px] border-l-muted-foreground bg-muted/30 px-2.5 py-2">
        <div className="text-[12px] leading-[17px] text-foreground">{t('desk_deal_changed_kept')}</div>
        <div className="mt-0.5 text-[11px] leading-4 text-muted-foreground">{deal.reason}</div>
        {onUndo && (
          <button type="button" onClick={onUndo}
            className="mt-1 text-[11px] leading-4 text-muted-foreground underline decoration-dotted hover:text-foreground">
            {t('desk_deal_undo')}
          </button>
        )}
      </div>
    )
  }
  if (!sw) return null
  const name = p => t(p === 'leads' ? 'desk_type_leads' : 'desk_facet_ourwork')
  return (
    <div className="mb-2 rounded-lg border border-border border-l-[3px] border-l-muted-foreground bg-muted/30 px-2.5 py-2">
      <div className="text-[12px] leading-[17px] text-foreground">{t('desk_pool_kept')}</div>
      <div className="mt-0.5 text-[11px] leading-4 text-muted-foreground">
        <span className="tabular-nums">{sw.fromTotal ?? '…'}</span> {t('desk_pool_in')} {name(sw.from)}
        {' → '}
        <span className="tabular-nums text-foreground">{sw.toTotal ?? '…'}</span> {t('desk_pool_in')} {name(sw.to)}
      </div>
      {/* What could NOT come along, named. Silence here is the failure mode: the Portal
          filter would otherwise disappear from the query while its chip stayed on screen,
          and the agent would read a wider result set as new supply. One line, the same
          Undo underneath — the undo restores the filter with the pool. */}
      {sw.droppedSources?.length > 0 && (
        <div className="mt-0.5 text-[11px] leading-4 text-muted-foreground">
          {tLog('desk_pool_dropped_sources', { n: sw.droppedSources.length })}
        </div>
      )}
      {onUndo && (
        <button type="button" onClick={onUndo}
          className="mt-1 text-[11px] leading-4 text-muted-foreground underline decoration-dotted hover:text-foreground">
          {t('desk_pool_undo')}
        </button>
      )}
    </div>
  )
}

// ── THE THREE-ANSWER READOUT (design 2a §4) ──────────────────────────────────────────
// The component that must never collapse to two values. Every search here has three
// outcomes, not two: it matches, it CANNOT BE JUDGED because the data is missing, or it
// is ruled out. Folding the middle one into "no" is the single thing this system is built
// not to do — and it has already happened once in production (a subtype filter reported
// "no entries match" while hiding 14 properties whose data could neither confirm nor rule
// them out, 0360).
//
// Both numbers already existed; they were just never shown together. `after` and `unknown`
// drive the reduction line and the amber shelf line ABOVE THE RESULTS, which an agent only
// reads once the search has run. Here they sit beside the filters that caused them.
//
// ⚠️ "Ruled out" is DERIVED (before − matches − may-match), and the two inputs carry a
// documented approximation: the local-only filters correct loaded rows only, so the figure
// tightens as pages load. It is clamped at zero — a negative would mean the corrections
// disagree, and a negative count on screen is worse than a conservative one.
function ThreeAnswerReadout({ r }) {
  if (!r || !(r.before > 0)) return null
  const matches = Math.max(0, r.after ?? 0)
  const maybe = Math.max(0, r.unknown ?? 0)
  const ruledOut = Math.max(0, r.before - matches - maybe)
  // Nothing has been narrowed yet → the readout would say "everything matches", which is
  // true and useless. It earns its space only once a filter has actually divided the corpus.
  if (matches >= r.before) return null
  const line = (key, label, n, dot, muted) => (
    <div key={key} className="flex items-center gap-2">
      {dot}
      <span className={`flex-1 min-w-0 truncate text-[11px] leading-4 ${muted ? 'text-muted-foreground/60' : 'text-muted-foreground'}`}>{label}</span>
      <span className={`shrink-0 text-[11px] leading-4 tabular-nums ${muted ? 'text-muted-foreground/60' : 'text-foreground font-medium'}`}>{n}</span>
    </div>
  )
  return (
    <div className="mb-2 rounded-lg border border-border bg-muted/20 px-2.5 py-2 space-y-1">
      {line('m', t('desk_readout_matches'), matches,
        <span className="h-[7px] w-[7px] shrink-0 rounded-full bg-foreground" />, false)}
      {line('u', t('desk_readout_maybe'), maybe,
        <span className="h-[7px] w-[7px] shrink-0 rounded-full border-[1.5px] border-dashed border-muted-foreground" />, false)}
      {line('x', t('desk_readout_ruled_out'), ruledOut,
        <span className="h-[7px] w-[7px] shrink-0 rounded-full bg-muted-foreground/30" />, true)}
    </div>
  )
}


// ── A CONTROL THAT DOES NOT APPLY IN THIS POOL (design 2b §3) ────────────────────────
// The §4 "inapplicable" pattern: the control keeps its place in the panel and states the
// reason, instead of vanishing. It is deliberately NOT a ghost chip — a ghost chip lives
// in one collapsed line at the bottom and answers "what does this IDENTITY not ask"; this
// row stays in facet order and answers "why is the question I used yesterday not here".
// The two absences have different causes and must not be folded into one presentation.
//
// Not a <button>, no handler, no disabled input: there is nothing to press, and a disabled
// control invites the click that then does nothing. Dashed border carries "exists, not in
// play" — the same visual grammar the ghost chips use, at row scale.
function UnavailableFacet({ title, reason }) {
  return (
    <div className="rounded-lg border border-dashed border-border/70 px-2 py-1.5 flex items-center justify-between gap-2">
      <span className="text-xs text-muted-foreground/70 truncate">{title}</span>
      <span className="text-[11px] leading-4 text-muted-foreground/60 shrink-0">{reason}</span>
    </div>
  )
}

// ── 6a's dashed door: "Have a client on file?" ────────────────────────────────────────
// Filling the axes BY HAND from a profile the system already holds is retyping, and
// retyping is how a derived answer and a remembered one drift apart. This is the door the
// mock drew and nobody built: pick the client, the axes fill, and the card above then says
// whose they are.
//
// ⚠️ It STATES ITS OWN POOL. The list is the clients the Desk has loaded (100 recent, 500
// with all-history), because no API searches clients by name — the suggest endpoint returns
// proposal ids and the party lookup takes a phone or an email. An agent whose older client
// is not in the list must be told that is why, or they will read the absence as "this
// client has no profile", which is a different and wrong conclusion.
function FillFromClient({ options, note, onPick }) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)}
        className="w-full mb-2 rounded-lg border border-dashed border-border/70 px-2 py-1.5 text-left text-[11px] leading-4 text-muted-foreground hover:text-foreground hover:border-border">
        {t('desk_who_have_client')} <span className="underline decoration-dotted">{t('desk_who_fill_from_client')}</span>
      </button>
    )
  }
  const needle = q.trim().toLowerCase()
  // Unnamed clients are real (an intake with a phone and no name), and they are searchable
  // only by not typing — so they stay in the list until a query excludes them.
  const hits = options.filter(o => !needle || (o.name || '').toLowerCase().includes(needle)).slice(0, 8)
  return (
    <div className="mb-2 rounded-lg border border-border p-2 space-y-1.5">
      <div className="flex items-center gap-2">
        <input autoFocus value={q} onChange={e => setQ(e.target.value)}
          placeholder={t('desk_who_client_search_placeholder')}
          className="flex-1 min-w-0 rounded-md border border-input bg-background px-2 py-1 text-xs" />
        <button type="button" className="text-xs text-muted-foreground hover:text-foreground"
          onClick={() => { setOpen(false); setQ('') }}>✕</button>
      </div>
      {hits.length > 0 ? (
        <div className="flex flex-col">
          {hits.map(o => (
            <button key={o.reqId} type="button" onClick={() => { onPick(o); setOpen(false); setQ('') }}
              className="text-left text-xs rounded px-1.5 py-1 hover:bg-accent truncate">
              {o.name || t('desk_suggest_unnamed_client')}
            </button>
          ))}
        </div>
      ) : <p className="text-[11px] leading-4 text-muted-foreground px-1">{t('desk_who_client_none')}</p>}
      {/* The bound, stated. Not a disclaimer — the difference between "she has no profile"
          and "she is older than what this list holds" changes what the agent does next. */}
      <p className="text-[11px] leading-4 text-muted-foreground/70 px-1">{t('desk_who_client_scope_note')}</p>
      {note && <p className="text-[11px] leading-4 text-amber-600 dark:text-amber-400 px-1">
        {t(note === 'empty' ? 'desk_who_client_empty' : 'desk_who_client_error')}
      </p>}
    </div>
  )
}

// ── 6b: WHOSE ANSWERS THESE ARE, AND WHAT NOBODY ASKED ────────────────────────────────
// The card that sits above the axis rows when the lenses were DERIVED from a client (the
// `restrictionsFrom` marker — deskSearchState.js). It exists for one sentence:
//
//   silence is UNKNOWN, not "no restriction".
//
// That is the honesty requirement the whole restrictions rework is judged on. A client with
// a cat and no recorded nationality produces `pol_cat:hide` and nothing on nationality — and
// a panel that shows only the cat lens invites the reading "nationality is fine here", which
// is not what anybody measured. So the silent axes are drawn, named, and labelled silent.
//
// ⚠️ The axes come from `whoRows` — the SAME rows the facet below draws, from the same
// projection query. This card must never build its own axis list: two lists of "the nine
// axes" would disagree the first time the vocabulary moves, and the disagreement would be
// invisible (both look plausible).
//
// ⚠️ NO PROSE SENTENCE YET, deliberately. 6b asks for "A Georgian family — two adults, one
// child aged 6 — with a cat", generated by the existing owner-reviewed Georgian generator.
// The engineering review measured that the generator cannot do it: `ruleSentence()` produces
// an OWNER'S RULE from {polarity, predicate}, and this is a household description — no
// polarity, enumerated members, ages. Different grammar, not a different input. Writing a
// second Georgian grammar is a real piece of work that needs the owner's eyes on its output
// (that generator shipped ungrammatical Georgian once while reporting `complete: true`), so
// the card states the facts plainly and the sentence is sized separately. Everything the
// honesty rule needs is here without it.
function ClientLensCard({ name, derived, rows, selected, onAnswer, onOpenClient }) {
  // `standard` and `other` are not person axes (one describes the AD's posture, the other is
  // the never-vanish bucket), so they are excluded from the ratio for the same reason the
  // facet's own footer excludes them.
  const axisRows = (rows || []).filter(r => r.key !== 'standard' && r.key !== 'other')
  const known = axisRows.filter(r => r.codes.some(c => derived.has(`${c.code}:${selected.get(c.code)?.mode}`)))
  // Silent = nobody answered it AT ALL — neither the client nor the agent by hand. An axis
  // the agent set themselves is answered; it is simply not the client's answer, and saying
  // "nobody asked" about it would be false.
  const silent = axisRows.filter(r => !r.codes.some(c => selected.has(c.code)))
  return (
    <div className="mb-2 rounded-lg border border-border bg-muted/20 p-2 space-y-2">
      <div>
        <div className="text-[12px] leading-[17px] text-foreground">
          {name ? tLog('desk_lens_from_named', { name }) : t('desk_lens_from_unnamed')}
        </div>
        <div className="text-[11px] leading-4 text-muted-foreground">
          {tLog('desk_lens_counts', { known: known.length, silent: silent.length })}
        </div>
      </div>

      {known.length > 0 && (
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground/70 mb-1">
            {t('desk_lens_known')}
          </div>
          <div className="flex flex-wrap gap-1">
            {/* Same rule as the axis rows: no glyph on the ordinary "the client has this"
                answer, which is the only one authorable — it would mark every chip
                identically. A legacy `restricted` entry keeps its ✗, where the mark is the
                only thing that distinguishes it. */}
            {known.flatMap(r => r.codes.filter(c => selected.has(c.code)).map(c => (
              <span key={c.code} className="rounded-full border border-border bg-background px-2 py-0.5 text-[11px] leading-4">
                {selected.get(c.code).mode === 'hide' ? c.label : `${restrictionGlyph(selected.get(c.code).mode)} ${c.label}`}
              </span>
            )))}
          </div>
        </div>
      )}

      {/* The load-bearing half. Dashed, because a dashed mark is this panel's existing
          vocabulary for "exists, not in play" (ghost chips, the unavailable facet row) —
          and because these must not look like filters that are switched off. */}
      {silent.length > 0 && (
        <div className="rounded-lg border border-dashed border-border/70 p-2">
          <div className="text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground/70 mb-1">
            {t('desk_lens_nobody_asked')}
          </div>
          <div className="flex flex-wrap gap-1 mb-1">
            {silent.map(r => (
              <span key={r.key} className="rounded-full border border-dashed border-border px-2 py-0.5 text-[11px] leading-4 text-muted-foreground/70">
                {r.label}
              </span>
            ))}
          </div>
          <p className="text-[11px] leading-4 text-muted-foreground">{t('desk_lens_unknown_not_unrestricted')}</p>
        </div>
      )}

      {/* The ratio in WORDS. "4 of 9" alone reads as progress; "the other five narrow
          nothing" is the part an agent has to know before trusting the result count. */}
      <p className="text-[11px] leading-4 text-muted-foreground">
        {tLog('desk_lens_ratio', { on: known.length, all: axisRows.length, off: axisRows.length - known.length })}
      </p>

      <div className="flex flex-wrap items-center gap-3">
        {silent.length > 0 && (
          <button type="button" onClick={() => onAnswer && onAnswer(silent[0].key)}
            className="text-[11px] leading-4 underline decoration-dotted text-muted-foreground hover:text-foreground">
            {t('desk_lens_answer_now')}
          </button>
        )}
        {onOpenClient && (
          <button type="button" onClick={onOpenClient}
            className="text-[11px] leading-4 underline decoration-dotted text-muted-foreground hover:text-foreground">
            {name ? tLog('desk_lens_complete_named', { name }) : t('desk_lens_complete_unnamed')}
          </button>
        )}
      </div>
    </div>
  )
}

// Meetings feed (MEETING_* maps, MeetingCard, MeetingsFeed) — moved to
// ./desk/MeetingsFeed.jsx (plan §3 decomposition, verbatim; status maps re-imported).

// ─── Main tab ─────────────────────────────────────────────────────────────────
// The lens a home section is read through ("mine", an office, the whole agency, the shared pool) - small and muted
// beside the heading, so a manager can tell "my book" from "the office" without opening anything (audit 2026-10-21).
function ScopeTag({ text }) {
  if (!text) return null
  return <span className="ml-2 align-middle text-[11px] font-normal text-muted-foreground">· {text}</span>
}

export default function IntakeTab({ agents = [], allAgents = [], session = null, role = null, offices = [], config = {}, currency = 'USD', onCurrency = null, fxRate = null, deskHomeSignal = 0, notifSignal = 0, addSignal = null, onNotifBadge = () => {}, shelfSignal = null, onShelfBadges = () => {} }) {
  // Display currency for every price on the Desk — the top-bar toggle's pick plus the shared
  // NBG rate (2026-07-20). Before this, the toggle only FILTERED (one-currency-at-a-time), so
  // a feed mixing "999 GEL" and "380 USD" could not be scanned or compared at a glance.
  const money = useMemo(() => ({ display: currency, rate: fxRate }), [currency, fxRate])
  const [rawText, setRawText] = useState('')
  const channel = 'scheduler'   // no picker — origin is unknown at paste time; not worth asking for yet
  const [parsing, setParsing] = useState(false)
  const [parseErr, setParseErr] = useState(null)
  const [envelope, setEnvelope] = useState(null)
  const [intakeId, setIntakeId] = useState(null)
  const [anchors, setAnchors] = useState(null)
  const [usage, setUsage] = useState(null)
  const [aiHint, setAiHint] = useState(null)          // 'client' | 'undecided' | null
  const [activeModal, setActiveModal] = useState(null) // 'client' | 'property' | 'meeting' | 'candidate' | null
  const [amendTargetId, setAmendTargetId] = useState(null)   // human overrode "new" → amend this requirement instead
  const [committing, setCommitting] = useState(false)
  const [commitMsg, setCommitMsg] = useState(null)
  const [history, setHistory] = useState([])
  const [searchPolygons, setSearchPolygons] = useState({})   // requirement_id -> GeoJSON Polygon, for the client-card area preview
  const [histLoading, setHistLoading] = useState(false)
  // ── 🔴 "IT SAYS NOTHING FOUND WHEN THERE ARE LISTINGS" (owner, 2026-08-20) ───────────────
  // TRUE while the listings fetch for the CURRENT question is in flight. It exists because the
  // zero-results block had no way to tell "the server answered, and the answer is none" from
  // "the server has not answered yet", and rendered the first sentence for both.
  // `histLoading` does NOT cover this: it belongs to `loadHistory`, and the skeleton it drives
  // is additionally gated on `history.length === 0` — i.e. the FIRST load only. Apply a filter
  // over a feed that already has rows and none of those guards fire: `visibleGroups` drops to
  // 0 the moment the snapshot is invalidated, and "ამ ფილტრებით არაფერი მოიძებნა" is on screen
  // for the entire request. On the owner's admin/all-offices scope that is ~3 s per page, which
  // is exactly long enough to read it, believe it, and change the filter.
  // 📌 The principle is already written three lines above the zero block, for the identity
  // reach-back: *"shimmer, NOT the zero state … rendering '0 results' while it runs tells the
  // operator a lie they act on (they abandon)"*. The reach-back got it; the ordinary filtered
  // search never did. This extends the same rule to the path every agent uses constantly.
  const [listingsFetching, setListingsFetching] = useState(false)
  // F-045 (owner, 2026-09-06: "page moves up a little then comes down when results show up"). The height
  // the feed grid had when the search LEFT is held as a min-height on whatever replaces it (the skeleton,
  // then the fresh grid) until the fetch ends — so the document never gets shorter mid-search and the
  // scroll position never clamps. Measured at the moment fetching flips on, which is before the DOM has
  // dropped the previous rows; released when fetching flips off (a genuinely shorter answer then settles
  // once, after the results, not before them).
  const feedGridRef = useRef(null)
  const [holdHeight, setHoldHeight] = useState(0)
  // Measured 2026-09-06 with a mutation observer on the live Desk: the grid's own hold engaged (7,903 px)
  // and the document STILL fell 15,890 → 8,332 px, because the possibles shelf, the pager and the
  // load-more line around the grid empty at the same moment. So the hold goes on the feed's CONTAINER
  // (the grid's parent) as well — imperatively, because that element is React's and carries no style
  // of its own; the hold is measured at fetch start and cleared at fetch end, nothing else touches it.
  const holdElRef = useRef(null)
  // The hold's PHASE (trace, 2026-09-06): a hold taken at the commit was released 4 ms later, because the fetch
  // it was protecting had not started yet and the locally re-filtered page already counted as "fully drawn".
  // 'commit' → the fetch that follows must run ('fetching') before the release may consider the page drawn.
  // A commit that never fetches (a local-only change) is let go after a short grace, so nothing stays held.
  const holdPhaseRef = useRef(null)   // null | 'commit' | 'fetching'
  // Case 2 of the observer (2026-09-06): a district switch re-filters the LOADED rows locally within ~200 ms
  // of the commit (11,682 → 6,087 px), while the fetch — and a hold taken at fetch start — comes ~800 ms
  // later on the debounced filters. So the hold is measured from the height the feed had BEFORE the commit
  // (remembered every render while no hold is on) and is taken AT the commit, not at the fetch.
  const lastFeedHeightRef = useRef({ grid: 0, feed: 0 })
  // ⚠️ The remembering is done INSIDE the commit effect below (one effect), not here. A separate
  // "measure every render" effect declared earlier ran FIRST in the commit's layout phase and remembered
  // the already-emptied grid (observer, 2026-09-06: the hold captured 7,867 px on a 11,818 px page).
  const holdFeedHeight = (why) => {
    const g = feedGridRef.current, p = g && g.parentElement
    // ⚠️ The phase advances to 'fetching' ONLY when the release effect observes listingsFetching === true
    // (trace 2026-09-06: flipping it here let the effect run in a render where fetching still read false and
    // release the hold at fetch start, before any fresh row existed). Taking a hold never sets 'fetching'.
    if (holdElRef.current) { perfTrace('hold:skip-already-held', { why }); return }   // keep the earlier, taller measure
    holdPhaseRef.current = 'commit'
    const last = lastFeedHeightRef.current
    perfTrace('hold:take', { why, grid: last.grid, feed: last.feed, gridNow: g ? g.offsetHeight : null, feedNow: p ? p.offsetHeight : null })
    setHoldHeight(last.grid || (g ? g.offsetHeight : 0))
    if (p) { holdElRef.current = p; p.style.minHeight = (last.feed || p.offsetHeight) + 'px' }
  }
  // Released BELOW, beside the render budget (see releaseFeedHold): measured 2026-09-06 with the observer,
  // releasing on "fetching ended" still collapsed the page 16,782 → 8,329 px, because the fresh grid mounts
  // 8 cards first and grows to 30 over idle ticks — the hold has to outlive the fetch until the page is
  // fully drawn.
  const releaseFeedHold = () => {
    perfTrace('hold:release', { doc: typeof document !== 'undefined' ? document.documentElement.scrollHeight : null, phase: holdPhaseRef.current })
    holdPhaseRef.current = null
    setHoldHeight(0)
    if (holdElRef.current) { holdElRef.current.style.minHeight = ''; holdElRef.current = null }
  }
  // F-021 (2026-09-05): a search that FAILED (timeout, 5xx) is not an empty result. The empty
  // state used to render for both, and the owner read a timed-out street search as "nothing
  // found" while the DB held 41 clusters. The message is kept for the error card; the retry
  // nonce is a dependency of the fetch effect, so pressing Retry re-asks the same question.
  const [listingsError, setListingsError] = useState(null)
  const [listingsRetry, setListingsRetry] = useState(0)
  // 2026-09-24 cache refs for the page-0 fetch effect. retrySeenRef: the listingsRetry value the
  // effect last saw — a CHANGE means a human pressed Retry, which always goes to the network.
  // page0KeyRef / page0FetchedAtRef / page0TtlRef: which page-0 cache entry is on screen, WHEN
  // its answer was fetched and the TTL the server gave it — the chain-completion write re-stores
  // the complete page under that key WITH THE ORIGINAL TIME, or completing the chain would keep
  // extending the freshness of a page 0 nobody re-asked.
  const retrySeenRef = useRef(0)
  const page0KeyRef = useRef(null)
  const page0FetchedAtRef = useRef(0)
  const page0TtlRef = useRef(null)
  const page0MetaRef = useRef(null)   // page 0's own search_meta, re-stored with the complete page
  // Hover-warm state (see warmFeedPage). Declared up here, above the fetch effect that JOINS an
  // in-flight warm, so no reader sits above its declaration.
  const hoverWarmBusyRef = useRef(false)
  const hoverWarmTimerRef = useRef(null)
  const hoverWarmInflightRef = useRef(new Map())
  const [districts, setDistricts] = useState([])
  const [geoLocations, setGeoLocations] = useState([])   // locations dictionary rows (search compiler v7 gazetteer)
  const [features, setFeatures] = useState([])
  // restriction_expansion (0217): parent_subject -> { child: tier }. {} until the vocabulary
  // fetch resolves, and {} against a pre-0217 server — both mean direct-stance-only matching,
  // which is the correct degradation, never an error state.
  const [restrictionExpansion, setRestrictionExpansion] = useState({})
  // 0759 — which feature groups a kind can answer. [] until the vocabulary fetch resolves and
  // [] against an older server; capabilityFilter reads empty as "everything applies", so both
  // degrade to the pre-0759 panel rather than to a blank one.
  const [kindFeatureCaps, setKindFeatureCaps] = useState([])
  // 0788-0790 — the condition ladder as DATA (stages, rungs, own buckets). null until the
  // vocabulary fetch lands and null against an older server; conditionLaddersFrom reads null as
  // "use the hard-coded CONDITION_LADDERS", which is the pre-0788 panel exactly.
  const [conditionLadder, setConditionLadder] = useState(null)
  // engine v26: the compiler reads the SAME registry ladder the panel renders (0788-0790) — until it lands,
  // conditionLaddersFrom(null) is the constant, so the box behaves exactly as before.
  const ladderCodes = useMemo(() => conditionLaddersFrom(conditionLadder).flatMap(([, codes]) => codes), [conditionLadder])
  // UI language for geography labels (district/city dropdowns + chips). Read per render —
  // the App-level ქარ/ENG toggle re-renders this whole tab, so no local subscription needed.
  const lang = getLanguage()
  const [viewings, setViewings] = useState([])
  // Has the meetings list been FETCHED yet? `viewings` starts as [] and stays [] while the
  // lazy load is in flight, so an empty array cannot tell "still loading" from "there are
  // none" — and the feed was blaming the agent's filters for both (fixed 2026-08-15).
  const [viewingsSettled, setViewingsSettled] = useState(false)
  // the meetings feed failed to load (getMeetingsFeed throws only when both halves failed): the list says so and offers
  // a retry instead of "no meetings yet" — the load runs once, so a failure used to stay empty until a page reload
  const [viewingsErr, setViewingsErr] = useState(null)
  const loadViewings = () => {
    setViewingsErr(null); setViewingsSettled(false)
    return getMeetingsFeed()
      .then(vs => { setViewings(vs); setViewingsErr(null) })
      .catch(e => { setViewings([]); setViewingsErr(e?.message || 'error') })
      .finally(() => setViewingsSettled(true))
  }
  // Meetings status filter — LIFTED out of MeetingsFeed (2026-07-22) so the sidebar's
  // Category→Meeting subtree and the feed's pills drive one state (two surfaces, one state).
  const [meetingStatusFilter, setMeetingStatusFilter] = useState('active')
  // Leads active↔archive — LIFTED from LeadsFeed (owner ruling 2026-07-24: "these can go in
  // დეტალური ძიება — any filter eventually should be there"). Same contract as
  // meetingStatusFilter: the Category tree in the panel is the surface, this is the state.
  const [leadArchive, setLeadArchive] = useState(false)
  // The ownership LENS over Leads (SEARCH_GRAMMAR §3b) — a DIFFERENT axis from leadArchive.
  // archive = lifecycle (what the lead is doing); mine = the agent↔lead claim relationship
  // (whose it is). Kept as separate state precisely so the two can never be collapsed into
  // one selector, which is the model the owner rejected.
  // ⚠️ Its only control was removed 2026-08-02 (the redundant `All leads | Working` pills —
  // see LeadsFeed). The state stays because the URL and the saved searches can still carry
  // it, and a value that reaches the RPC must not become unreachable state: an agent who
  // restores such a search would otherwise sit inside a filtered feed with nothing on
  // screen able to explain or clear it. `false` is the default, so nobody lands in the lens
  // by accident; the reset below is the escape hatch until the evidence-backed lens returns
  // to the panel (DOMAIN_VOCABULARY §5 R-1).
  const [leadWorked, setLeadWorked] = useState(false)
  const [actingReqId, setActingReqId] = useState(null)   // requirement currently being closed/reopened
  const [expandedGroups, setExpandedGroups] = useState(() => new Set())   // thread (🧵) expand — both views
  // Any click on a saved card opens it for editing — the object's CURRENT state, not the
  // AI-draft review flow above. One "currently editing" slot per object type; only one modal
  // is ever open at a time (clicking a card while the paste box has its own pending draft modal
  // open never happens together, so no conflict).
  const [propertyDraft, setPropertyDraft] = useState(null)   // pending extract_listing proposal being reviewed
  const [editingCandidate, setEditingCandidate] = useState(null)
  const [editingViewing, setEditingViewing] = useState(null)
  const [viewingPartyId, setViewingPartyId] = useState(null)   // person modal
  const [addReqParty, setAddReqParty] = useState(null)         // B48: "+ Add request" — {id,name,phone,email}
  const [failedEntry, setFailedEntry] = useState(null)         // a failed save attempt being retried (2026-08-01)
  const addReqPidRef = useRef(null)                            // survives onSaved→onClose to reopen the person view
  // ── THE CARD STACK (2026-08-04) ────────────────────────────────────────────────────────
  // A property card and a client card open each other, in both directions and without a
  // bound: a client's matches are properties, a property's matches are clients. While each
  // lived in its own single slot, "open" could only ever mean REPLACE, so drilling in threw
  // away where you came from — and worse, pushing without closing changed a card UNDERNEATH
  // an opaque modal, so the click read as broken (the bug at the onLeaveToSearch note above).
  //
  // So the two slots became ONE ORDERED STACK. Entries render bottom-to-top in array order;
  // since both modals are z-50 body portals, DOM order alone decides who is on top, and
  // every entry stays MOUNTED — a half-typed edit or a scroll position on a lower card is
  // still there when you come back to it.
  //
  //   { kind: 'listing', id, isOwn, seed?, view? }   — isOwn: agent's own posting vs a scraped ad
  //   { kind: 'client',  reqId }
  //
  // `view` (2026-08-05) is which tab of the deal editor that card has open — only ever set
  // on the TOP entry, reported up by the modal itself, and the reason it lives on the entry
  // rather than in its own state: popping must reveal the lower card's OWN view, and a
  // separate slot would either follow the wrong card or go stale under it.
  //
  // ⚠️ Two verbs, and the difference is load-bearing. A click from a FEED card, a search
  // result, a notification or a deep link is a fresh navigation — it RESETS the stack
  // (openListingCard/openClientCard), or a morning of clicking around would bury the agent
  // twenty modals deep with no way out but twenty ✕. A click from INSIDE a card is a
  // drill-down and PUSHES (pushListingCard/pushClientCard). Only these six functions may
  // touch cardStack; nothing else should reach for the setter.
  const [cardStack, setCardStackRaw] = useState([])
  // review 2026-09-23: every entry gets a monotonic `uid` when it enters the stack, and the
  // render keys on it. Keying on the array INDEX meant the depth-8 trim (which drops the
  // oldest) shifted every index and remounted every card, losing scroll and half-typed edits —
  // the exact thing keeping entries mounted exists to protect. Entries that already carry a
  // `__cardUid` keep it (setTopCardView spreads `top`), so an in-place update never remounts.
  // All six stack functions (and the URL restore) go through this wrapper, so none can skip it.
  const cardUidRef = useRef(0)
  const setCardStack = useCallback(next => setCardStackRaw(s => {
    const arr = typeof next === 'function' ? next(s) : next
    // Unchanged-array bail-outs (setTopCardView) must stay bail-outs: return the SAME array.
    if (arr.every(e => e && e.__cardUid != null)) return arr
    return arr.map(e => (e && e.__cardUid != null ? e : { ...e, __cardUid: ++cardUidRef.current }))
  }), [])
  const topCard = cardStack.length ? cardStack[cardStack.length - 1] : null
  const openListingCard = useCallback(e => setCardStack([{ kind: 'listing', ...e }]), [])
  const openClientCard = useCallback(reqId => setCardStack([{ kind: 'client', reqId }]), [])
  // The loop is unbounded by nature (a client's matches are properties, whose matches are
  // clients, …) and every entry stays MOUNTED with its own fetches, so the depth is capped.
  // 8 is far past real use — agents drill two or three — which is the point: this is a
  // runaway valve for a mis-click loop, not a behavior anyone should meet. What it costs when
  // it does fire is stated plainly: the OLDEST card is dropped, so at depth 8 the last ✕
  // lands on the feed instead of on where the trail started. Never trim the top — that would
  // discard the card the agent is looking at.
  const pushCard = useCallback(entry => setCardStack(s => [...s, entry].slice(-8)), [])
  const pushListingCard = useCallback(e => pushCard({ kind: 'listing', ...e }), [pushCard])
  const pushClientCard = useCallback(reqId => pushCard({ kind: 'client', reqId }), [pushCard])
  const popCard = useCallback(() => setCardStack(s => s.slice(0, -1)), [])
  // Leaving the Desk's card world entirely (the leads-search escape hatch): the whole stack
  // goes, because the point is to get OUT of the way of the feed behind it.
  const closeCards = useCallback(() => setCardStack([]), [])
  // The top card's open deal-editor tab, reported up by the modal so the URL can carry it
  // (2026-08-05 — refreshing the repost editor is a normal move, see deskUrl.js). Replaces
  // the LAST entry in place: same length, same key, so nothing remounts and no lower card
  // is touched. Bails when the value is unchanged, which is most calls — the modal reports
  // on every editor state change, including the ones that mean "still closed".
  const setTopCardView = useCallback(v => setCardStack(s => {
    if (!s.length) return s
    const top = s[s.length - 1]
    if ((top.view || null) === (v || null)) return s
    const next = { ...top }
    if (v) next.view = v; else delete next.view
    return [...s.slice(0, -1), next]
  }), [])
  const [propertyCard, setPropertyCard] = useState(null)       // { propertyId, listingId? } — the UNIFIED property card (same workspace for scraped + manual)
  const [meetingPropertySeed, setMeetingPropertySeed] = useState(null)   // core-pick-shaped seed from the lineage modal's "Add meeting"
  // "Book next viewing" from an open meeting (owner 2026-08-15): a READY seed object built
  // by CoreMeetingDetail from the meeting on screen — property + client + reps carried
  // over, so the repeat costs the taps of a date, not a re-entry.
  const [meetingRepeatSeed, setMeetingRepeatSeed] = useState(null)
  // Client share link (MEETINGS_MODEL §5) — { propertyId, propertyLabel, seedPrice, seedCurrency }
  const [shareSeed, setShareSeed] = useState(null)
  // Bumped when a share is minted/revoked, so children holding their OWN shares copy
  // (LineageModal) know to re-pull. Not the shares themselves — a counter, because the
  // child's fetch is property-scoped and this component does not know which it needs.
  const [sharesEpoch, setSharesEpoch] = useState(0)

  // Deep-link / URL restore lives in the "URL ⇄ committed search" block below (stage 2c):
  // #datadesk/listing/<id> and #datadesk?q=… are both parsed by deskUrl.parseHash — one URL
  // mechanism (plan §2.6); the old App-side deepLink prop is gone.

  // Recent-entries view/filter/sort — same controls & persistence pattern as AgentsTab
  // (ViewToggle/SingleSelect/MultiSelect, localStorage-backed) so this reads like the rest
  // of the app, not a bespoke widget.
  // Static eval, same as filtersOpen: a phone doesn't become a desktop mid-session.
  const isMobile = typeof window !== 'undefined' && !window.matchMedia('(min-width: 768px)').matches
  const [storedEntriesView, setEntriesView] = useViewPreference('desk_entries', 'card')
  // ⚠️ Mobile is STACK-ONLY (owner ruling 2026-07-25 mobile review): under 768px the left-thumb
  // 'card' form is not offered — its text column measures ~184px at a 400px viewport, where the
  // address collapses to three characters and the fixed 144×112 thumb leaves a dead gutter.
  //
  // Enforced by WIDTH, DERIVED ON EVERY RENDER — deliberately NOT the two mount effects this
  // replaces (`if (isMobile && view === 'card') setView('gallery')` + `if (view === 'list')
  // setView('card')`). Both were `[]`-dep'd and ran in that order, so 'list' → 'card' landed
  // AFTER the mobile guard had already passed, stranding a phone in the desktop form with no
  // mobile control to leave it (the card/gallery toggle is desktop-only, `hidden md:contents`).
  // The unrecognized-value fallback is equally load-bearing: `useViewPreference` persisted the
  // map pill's updater FUNCTION as its own source text for months (fixed at the hook), so live
  // preferences hold junk strings that match no view name — those must heal, not fall through
  // to a default branch. Any value outside the three real views lands on the right form for the
  // device, which also preserves the old 'list' → 'card' coercion (the ☰ list view was removed
  // 2026-07-16; the single-column 'card' view IS the list view now).
  // Derivation does not write back: a desktop session's 'card' preference survives a phone visit.
  const entriesView = ['card', 'gallery', 'map'].includes(storedEntriesView)
    ? (isMobile && storedEntriesView === 'card' ? 'gallery' : storedEntriesView)
    : (isMobile ? 'gallery' : 'card')
  // Agent-INTENT wrapper for the view toggle (desk.* telemetry, 0181): answers "do agents
  // scan by photo (recognition) or by data (comparison)" + the mobile/desktop split from
  // the same rows. ONLY the UI toggles route through this — the two mount-coercion effects
  // above stay on raw setEntriesView, because a system correction is not an agent choice
  // and logging it would poison exactly the question being asked.
  const changeEntriesView = v => {
    setEntriesView(v)
    trackDeskEvent('view_changed', { view: typeof v === 'function' ? v(entriesView) : v, device: isMobile ? 'mobile' : 'desktop' })
  }
  // Map-only: user-controlled, not automatic — off by default each time you enter map view,
  // toggled back off by the same button (2026-07-09 request: "optional... enlarge and close
  // again", not a permanent side-effect of switching to map view).
  const [mapExpanded, setMapExpanded] = useState(false)
  // Leaving map view must drop the expanded state (2026-07-18): the enlarged map hides the
  // surrounding page chrome (sidebars/add-tile, gated by `entriesView==='map' && mapExpanded`
  // below). Switching to gallery while still expanded left that collapsed layout lingering,
  // and a later return to map would reopen expanded unexpectedly. Reset closes both.
  useEffect(() => { if (entriesView !== 'map') setMapExpanded(false) }, [entriesView])
  // Right sidebar (Agent ranking) now the only thing there (2026-07-09 — the Activity/
  // Notifications/Meetings widget was removed; its notifications are already covered by the
  // Add-tile feed). Pinned to match the LEFT search-tools panel's actual height (open panel
  // vs collapsed rail are very different heights) the same way AddEntryTile matches its
  // neighboring card — CSS alone can't do this (see that fix's comment), so measure live.
  const rightAsideRef = useRef(null)
  const [rightAsideHeight, setRightAsideHeight] = useState(null)
  // the rail's worker panel reports its state up, so the phone's "next up" strip can show it without a second poll
  const [workerSummary, setWorkerSummary] = useState(null)
  // ⚠️ MATCHES ONLY A REAL TOOLS PANEL (Desk home audit 2026-10-21). On home there is no tools <aside>, so the first
  // sibling was the main column and this measured ITS first child - a 195 px nudge banner became the height of the
  // whole rail (worker + drafts + ranking), and the ranking list collapsed to 0 px with 19 rows inside it. Now: a
  // tools <aside> -> match its visible height (never below RAIL_MIN_PX, the worker + a usable ranking); anything else
  // (home) -> no imposed height, the rail takes its natural height. The observer follows the current target and
  // re-attaches on child changes and on crossing the md breakpoint.
  useEffect(() => {
    const RAIL_MIN_PX = 560
    const el = rightAsideRef.current
    if (!el || !el.parentElement) return
    const parent = el.parentElement
    let ro = null, target = null
    const attach = () => {
      const sibling = parent.firstElementChild
      if (!sibling || sibling === el || sibling.tagName !== 'ASIDE') {
        if (ro) ro.disconnect()
        ro = null; target = null
        setRightAsideHeight(null)
        return
      }
      // StickyAside (the open-panel case) makes its OWN outer <aside> collapse to 0 height on
      // purpose — its content goes position:absolute/fixed to implement the sticky-follow
      // behavior, which takes it out of flow. The real visible height lives on its inner
      // wrapper div (first child) instead; the collapsed-rail case has no such wrapper, so
      // fall back to the sibling itself there.
      const next = sibling.firstElementChild || sibling
      const measured = () => Math.max(RAIL_MIN_PX, next.getBoundingClientRect().height)
      if (next !== target) {
        if (ro) ro.disconnect()
        ro = new ResizeObserver(() => setRightAsideHeight(measured()))
        ro.observe(next)
        target = next
      }
      setRightAsideHeight(measured())
    }
    attach()
    const mo = new MutationObserver(attach)
    mo.observe(parent, { childList: true })
    const mq = window.matchMedia('(min-width: 768px)')
    mq.addEventListener?.('change', attach)
    return () => { mo.disconnect(); if (ro) ro.disconnect(); mq.removeEventListener?.('change', attach) }
  }, [entriesView, mapExpanded])
  const [entrySearch,       setEntrySearch]       = useState('')
  const [entryTypeFilter,   setEntryTypeFilter]   = useState([])   // subset of 'client'|'meeting'|'property'|'listing'
  // Leaving the Leads pool clears the lens whose control was removed 2026-08-02. Without
  // this, switching to Our work and back would silently reland the agent in a filtered leads
  // feed with nothing on screen able to clear it.
  // ⚠️ MUST live BELOW `entryTypeFilter`, not beside `leadWorked` where it was first written.
  // A hook's dep array is evaluated during render, so referencing a `const` declared 100
  // lines further down threw `Cannot access 'entryTypeFilter' before initialization` and the
  // whole Desk fell into its error boundary. `vite build`, 348 tests and the no-undef sweep
  // ALL passed — the identifier is defined, just not yet. Only opening the page found it,
  // which is the same lesson the propertyTotalForTree comment records a few hundred lines
  // down. Keep effects below every value they read.
  useEffect(() => { if (entryTypeFilter[0] !== 'Leads' && leadWorked) setLeadWorked(false) }, [entryTypeFilter])   // eslint-disable-line react-hooks/exhaustive-deps
  // Admin/manager only (agents already see only their own entries) — names, not ids, so it
  // matches by the SAME key AgentRankingPanel already uses for "whose listing is this"
  // (is_reshare ? author_name : scraped_by_name); proposals resolve created_by_agent_id → name.
  // Agent ids, NOT display names (2026-07-28): 18 live parties share the name "irakli",
  // and the filter now pushes down to the server as a party set keyed by agent id.
  // ⚠️ NO LONGER ITS OWN STATE (2026-08-09) — it is `advFilters.agentIds`, derived just below
  // the advFilters declaration. It lived here as a private useState from 2026-07-28, which is
  // precisely what kept it out of the URL, the chips, the active-filter gate and both clear
  // buttons; see the derivation for the whole reasoning.
  // B576 (2026-08-16): 'active' | 'archived' — server-side since 0600. Was
  // 'all' | 'active' | 'closed' | 'archived', filtered client-side over loaded rows.
  // ⚠️ The DEFAULT moved from 'all' to 'active', which is a real behaviour change and the
  // right one: 'all' no longer exists, and an agent opening the Desk should land on what
  // they are working, not on that plus everything they have finished. Leads have defaulted
  // to their active view since they got this axis; the two pools now agree.
  const [entryStatusFilter, setEntryStatusFilter] = useState('active')
  // Corpus-wide { active, archived } for the two pills. Fetched, never derived — the Desk
  // holds one page and these describe the whole book.
  const [workCounts, setWorkCounts] = useState(null)
  // 'activity' default (owner sort overhaul 2026-07-23): the server's activity ranking
  // (newest REAL change wins) is the smart default; 'newest' (added-date) is one option
  // among the rest in the sort dropdown.
  const [entrySort,         setEntrySort]         = useState('activity')
  // Best Match is the LEADS default (owner 2026-09-16) — but only until the agent picks a sort
  // themselves: an explicit "Added to senTavus" on Leads must stay what they chose.
  const sortPickedRef = useRef(false)
  const pickSort = v => { sortPickedRef.current = true; setEntrySort(v) }
  // v13 server-side kind coverage counts (search_meta.kind_counts, page-0 only).
  // Kept across kind switches and load-more pages (the ae3b610 stability rule —
  // counts are a property of the query, not the page); overwritten only when a
  // fresh page-0 response carries non-null counts. Feeds the Properties subtree.
  const [serverKindCounts, setServerKindCounts] = useState(null)
  // 0229 (B205): Deal facet numbers, same channel + same page-0/own-filter discipline as
  // kind_counts. Kept/overwritten by the same rule; null until a committed page-0 reports.
  const [serverIntentCounts, setServerIntentCounts] = useState(null)
  const [serverSourceCounts, setServerSourceCounts] = useState(null)   // F-086: the book view's source facet numbers (search_meta.source_counts, agent scopes since the RPC computes them)
  // 0687: the price/area/land HISTOGRAMS, same channel and the same page-0 + own-filter
  // discipline as the two counts above. Before this the three sliders were drawn from the loaded
  // page (LISTING_PAGE = 100), so the bars AND the axis AND the preset chips were a sample of a
  // corpus in the tens of thousands -- measured 2026-08-21, that sample put the building-area
  // axis at 2000m2 where the whole search says 200m2.
  // KEPT, never cleared, when a response carries none: a facet withholds its own histogram while
  // its own bound is set (the server cannot compute a price-blind one at all -- `pruned` pre-cuts
  // the price range), and dropping back to the loaded-page tally at that moment is exactly when
  // the axis would jump under the agent's cursor mid-drag.
  const [rangeFacets, setRangeFacets] = useState(null)
  // MERGE per facet, never replace the object. The server WITHHOLDS a facet while that facet's
  // own bound is set (it cannot compute a price-blind price histogram at all -- `pruned` pre-cuts
  // the price range), so a whole-object replace silently dropped the bounded facet back onto the
  // loaded-page tally -- exactly the biased sample this change exists to remove, and exactly
  // while the agent is dragging that slider. Caught in the live Desk: with priceMin=500 the price
  // slider reported serverBars=null and its bars summed to 615, the loaded row count, while area
  // and land kept theirs. `{...prev, ...next}` lets a present key win and an absent key survive.
  const mergeRangeFacets = next => setRangeFacets(prev => mergeFacetMaps(prev, next))
  // B273 move 4 (0363/0364/0369): district numbers from the SAME computation that produces the
  // rows, so the facet equals the click by construction. Same channel and same stability rule as
  // its two siblings above. null = this query hasn't reported yet → fall back to 0056 rather
  // than render a district tree with no numbers, which reorders it alphabetically (see
  // geoDistrictCounts).
  const [serverDistrictCounts, setServerDistrictCounts] = useState(null)
  // 0537/0538/0539: streets, from the same `counted` CTE and the same channel as the three
  // above, so the facet's number equals what its checkbox delivers by construction.
  // ⚠️ Its VALUE is an object, not an integer — { n, label } — because `street` lives in core
  // and the browser cannot read it, so the count has to carry its own name or the facet is a
  // list of UUIDs. ⚠️ The RPC emits it ONLY under a selected district (2,256 streets in
  // Tbilisi alone), which is also why the facet below renders only then.
  const [serverStreetCounts, setServerStreetCounts] = useState(null)
  // B914: the metro facet's counts for the CURRENT filtered search.
  const [serverMetroCounts, setServerMetroCounts] = useState(null)
  // ⚠️ AND THE CORPUS SNAPSHOT, SEPARATELY (B914 follow-up). Clearing a filter does NOT
  // re-answer this facet: the unfiltered browse takes the cheap recent-feed read, which
  // carries no `search_meta` at all, and the counts prewarm is ref-deduped per dcKey so it
  // does not fire a second time. Holding one state for both meant removing a filter left
  // the SCOPED numbers on screen with no filter to explain them — owner-reported: "when
  // filter removed it dont go back to show full numbers".
  // Two states, chosen by the SAME condition the prewarm guards on, so the two can never
  // disagree about which view is being described.
  const [corpusMetroCounts, setCorpusMetroCounts] = useState(null)
  // 0540 / turn-9 Q5: { total, with_street } for the selected district, same channel and the
  // same null discipline as the counts above — null means "not computed for this query", and
  // the facet then says nothing rather than guessing.
  const [serverStreetCoverage, setServerStreetCoverage] = useState(null)
  // ⚠️ DECLARED HERE, hundreds of lines above where it is filled, because the CHIP builder
  // reads it during render and sits above the counts. Declaring it beside its effect threw
  // `Cannot access 'streetLabels' before initialization` the first time a street was ticked
  // — the whole Desk fell to its error boundary, and `vite build` reported nothing wrong.
  // The house note about TDZ errors surviving a green build is not hypothetical.
  const [streetLabels, setStreetLabels] = useState({})
  // 0729: complex id → name, filled at click time (same reason as streetLabels above: a
  // UUID must never reach the chip bar). URL-restored filters backfill via getComplexesByIds.
  const [complexLabels, setComplexLabels] = useState({})
  // Complex header card (owner 2026-09-10): the full row for the ONE committed complex, drawn
  // above the feed. Keyed by id and kept as a small map so switching between two developments
  // and back does not refetch. Labels above stay the chip's source; this is the card's.
  const [complexRows, setComplexRows] = useState({})
  // 0688: the metro catalogue, held HERE as well as in the facet, because the filter CHIP
  // needs a station's name too — and a chip that only learns its label once the agent opens
  // the facet would leave a UUID-less, nameless filter in the bar after a shared-link
  // restore. Declared beside streetLabels for the same TDZ reason that one is.
  const [metroStations, setMetroStations] = useState(_metroStationsCache)
  useEffect(() => {
    if (_metroStationsCache) { setMetroStations(_metroStationsCache); return }
    let gone = false
    getMetroStations().then(rows => { _metroStationsCache = rows; if (!gone) setMetroStations(rows) }).catch(() => {})
    return () => { gone = true }
  }, [])
  // 0567 catalogue: the district's street LIST, fetched on its own (53ms) so the facet
  // stops waiting on a ~2,000ms corpus scan to learn street NAMES. Declared here beside
  // streetLabels for the same reason that one is — a read from below its declaration is
  // how this file reached its error boundary twice in one day.
  const [streetCatalog, setStreetCatalog] = useState([])
  // 0372: the raion's OWN remainder, un-expanded — the "parent under itself" row's number, and
  // what its checkbox delivers when ticked (`district_own_codes`). Kept SEPARATE from the
  // expanded map above on purpose: they answer different questions and the two were conflated
  // for half a day, which is how that row came to print the whole raion as its remainder.
  const [serverDistrictOwnCounts, setServerDistrictOwnCounts] = useState(null)
  // core 3836: distinct clusters per city (search_meta.city_counts, keyed by district.city) — the city dropdown's number
  const [serverCityCounts, setServerCityCounts] = useState(null)

  // ── 🔴 PROPERTIES HAD THE SAME STALE-FACET DEFECT, ON SIX FACETS (audit, 2026-09-02) ──────
  // The response handler read `if (searchMeta?.kind_counts) setServerKindCounts(...)` for every
  // facet except metro. The server NULLS a facet while its own filter is set, so `if (x)` meant
  // the previous map stayed in state indefinitely — a count from a different filter set, exactly
  // the Leads defect measured at Korter 2,037-versus-144. Metro was the ONE line written as
  // `?? null`, which is why the desk-filter-combination audit classified Properties metro as
  // correct (B) and its siblings as history-dependent (D). The fix was applied to one facet and
  // never generalised.
  //
  // 🔑 Same rule as Leads, same module: a snapshot is reusable only while the question it answers
  // is unchanged — the full canonical state MINUS that facet's own keys. Ticking a district still
  // keeps district numbers on screen (anti-flicker); ticking a SOURCE now drops them, because the
  // district universe genuinely changed.
  // ⚠️ Null is the correct degradation. A blank facet is honest; the alternative on screen was a
  // confident wrong number, and this repo has ruled that way three times.
  const rangeSigRef = useRef({})
  const facetSigRef = useRef({})
  // F-067 GENERALISED (owner report 2026-09-10: "counts still disappear when I select a source" — on the BOOK scopes).
  // The Leads fix of 2026-09-06 was applied to one facet family and never generalised: here the signature that
  // decides whether a facet's last unconstrained numbers may be kept carried `periodSinceISO(entryPeriod)` — the
  // INSTANT now−30d, recomputed on every call — so no two answers ever asked the same question, the withheld tally
  // (the server nulls a facet while its own bound is set) found nothing to keep, and the row went blank on the
  // tick and stayed blank on a reload. The period LABEL is the question ('30d' a minute later is the same '30d'),
  // and the resolve is durable now, exactly as on Leads: a fresh value is written to the desk cache under its
  // signature and a resolve with no memory seeds from it, so a reload with the source already ticked still shows.
  // 2026-10-22: whose answer the durable lfc: entry is (user + scope) — set where listingsScope is declared.
  const facetViewerRef = useRef('')
  const applyServerFacet = (family, fresh, setter, sig) => {
    const r = resolveFacetDurable(family, fresh, facetSigRef.current[family], sig, readDeskCache, writeDeskCache, { viewer: facetViewerRef.current })
    facetSigRef.current[family] = { value: r.value, sig }
    setter(r.value)
  }
  // Tbilisi selected by default since virtually every current listing is there (see
  // splitCityFromAddress) — 'all' shows every city. Only affects listing_readonly/
  // scraped_listing_readonly rows; client/meeting entries have no comparable single-city field
  // so they're never filtered out by this.
  // Default 'all' (2026-07-17, user ruling with the Desk home): no pre-applied city chip —
  // the home page isn't city-scoped, and the old Tbilisi default just sat there as a
  // mystery filter chip on every fresh load.
  const [entryCityFilter,   setEntryCityFilter]   = useState('all')
  // Bounds the fetch window by TIME, not row count — a bare limit silently truncates a busy day
  // before a quiet week even shows up (see /api/core/proposals `since` param). 'all' still hits
  // the server's row cap (100 proposals per fetch), just without a date floor.
  const [entryPeriod,       setEntryPeriod]       = useState('30d') // '1d' | '7d' | '30d' | '90d' | 'all'
  const [advFilters,        setAdvFilters]        = useState({ ...ADV_DEFAULTS })   // advanced-search panel (the old paste box's spot)
  // 0729: backfill complex chip labels after a URL restore. ⚠️ MUST sit BELOW the advFilters
  // declaration — the dependency array reads advFilters at render time, and the first cut of
  // this effect sat 45 lines above it beside complexLabels: a TDZ ReferenceError that took the
  // ENTIRE Desk down ("Cannot access before initialization"), invisible to vite build, caught
  // only by loading the deployed page. The class FILL_FORM_HANDOFF warns about, paid again.
  useEffect(() => {
    const missing = (advFilters.complexIds || []).filter(id => !complexLabels[id])
    if (!missing.length) return
    let gone = false
    getComplexesByIds(missing).then(rows => {
      if (gone || !rows.length) return
      setComplexLabels(m => { const next = { ...m }; for (const r of rows) if (!next[r.id]) next[r.id] = r.name; return next })
    }).catch(() => {})
    return () => { gone = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advFilters.complexIds])
  // The header card's row — fetched only when EXACTLY one complex is committed (a family has
  // no single subject). Same endpoint as the label backfill; the ?ids= path now returns the
  // header's columns. Same placement rule as the effect above: BELOW advFilters, or TDZ.
  const headerComplexId = (advFilters.complexIds || []).length === 1 ? advFilters.complexIds[0] : null
  useEffect(() => {
    if (!headerComplexId || complexRows[headerComplexId]) return
    let gone = false
    getComplexesByIds([headerComplexId]).then(rows => {
      if (gone || !rows?.length) return
      setComplexRows(m => ({ ...m, [rows[0].id]: rows[0] }))
      // While here, fill the chip label too — one fetch, both consumers.
      setComplexLabels(m => (m[rows[0].id] ? m : { ...m, [rows[0].id]: (getLanguage() === 'ka' ? (rows[0].name_ka || rows[0].name) : rows[0].name) }))
    }).catch(() => {})
    return () => { gone = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerComplexId])
  // The header's photo strip rides on the same row: `images` is the developer's OWN gallery
  // (complex_image, 1033), served by the ?ids= path. A first cut drew the linked flats' cover
  // photos through getComplexProperties + getPropertyThumbs; the owner corrected it the same
  // day ("not listings images but complexes own image gallery"). The members route stays —
  // the complexes browse object will want it — but nothing here calls it.
  // B935, the street half: same backfill, same reason, same placement rule (BELOW the
  // advFilters declaration — the TDZ that took the Desk down lives one lesson up). The
  // facet-counts label source is structurally absent while a street filter is on, so a
  // URL-restored street chip has no other way to a name.
  useEffect(() => {
    const missing = (advFilters.streetIds || []).filter(id => !streetLabels[id])
    if (!missing.length) return
    let gone = false
    getStreetsByIds(missing).then(rows => {
      if (gone || !rows.length) return
      setStreetLabels(m => { const next = { ...m }; for (const r of rows) if (!next[r.id]) next[r.id] = (r.name_ka || r.name_en); return next })
    }).catch(() => {})
    return () => { gone = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advFilters.streetIds])
  // ⚠️ KEYED ON `advFilters`, NOT `advFiltersDebounced` — that is the entire point. The
  // search path debounces 300ms and then the leads feed debounces another 350ms before a
  // ~2,000ms scan; this query is 53ms and answers a different, cheaper question, so making
  // it wait behind the expensive one would give back exactly what it was built to save.
  // The join is a string, not the arrays, so a re-render that mints new array identities
  // with the same codes does not refetch — the drift that made Desk open fire three
  // identical searches.
  const streetCatalogKey = [...(advFilters.districtCodes || []), ...(advFilters.districtOwnCodes || [])].sort().join(',')
  // null = IN FLIGHT (2026-08-16 audit): during the fetch the facet used to render its
  // loaded-and-empty copy — "No street is recorded for this district yet" — over Vake's 87
  // recorded streets, for as long as the request took. An answer that has not arrived must
  // render as nothing, never as an asserted "none" (the same rule the corpus total follows).
  // [] stays the two REAL empties: no district picked, and a district with no rows.
  // false = the lookup FAILED (2026-10-23 audit, Desk §6): the facet keeps the search's own counts
  // and does not say the district is empty. Only the latest lookup may write: a cancelled older one
  // used to settle as [] over the newer one still in flight (loadStreetCatalog, lib/deskAnswerState.js).
  useEffect(() => loadStreetCatalog(streetCatalogKey, getStreetCatalog, setStreetCatalog,
    e => console.warn('[street-catalog]', e?.message || e)), [streetCatalogKey])
  // The top-bar currency is a DISPLAY preference. If a price search is active, translate its
  // bounds at the same NBG rate used by the cards; merely relabelling 1000 USD as 1000 GEL
  // changes the question and swaps the result set. The filter's own currency buttons below do
  // intentionally relabel without conversion — that is the explicit search-unit control.
  useEffect(() => {
    setAdvFilters(f => {
      if (f.currency === currency) return f
      // ⚠️ per-m² bounds are money too (filters audit 2026-10-27 F04): left unconverted, USD 1,200/m² became GEL 1,200/m²
      const hasBound = [f.priceMin, f.priceMax, f.ppmMin, f.ppmMax].some(v => (v ?? '') !== '')
      if (!hasBound) return { ...f, currency }
      const rate = Number(fxRate)
      if (!(rate > 0)) return f // preserve the search rather than guess an exchange rate
      const factor = f.currency === 'USD' && currency === 'GEL' ? rate
        : f.currency === 'GEL' && currency === 'USD' ? 1 / rate
          : null
      if (factor == null) return { ...f, currency }
      const convert = value => {
        if ((value ?? '') === '') return ''
        const amount = Number(value)
        return Number.isFinite(amount) ? String(Number((amount * factor).toFixed(2))) : value
      }
      return { ...f, currency, priceMin: convert(f.priceMin), priceMax: convert(f.priceMax), ppmMin: convert(f.ppmMin), ppmMax: convert(f.ppmMax) }
    })
  }, [currency])
  // ── THE AGENT FILTER IS AN ORDINARY FILTER NOW (2026-08-09) ────────────────────────────
  // Owner report: "when admin filters by agent name it dont show agents filtered in filters."
  // It shipped 2026-07-28 as a private useState deliberately kept out of advFilters, so that
  // it would "reach the server without entering committed state / URL / saved-search
  // semantics". Every one of those omissions turned out to be a defect rather than a saving:
  // no chip named it, `advFiltersActive` could not see it, and — the damaging one —
  // `setEntryAgentFilter` had exactly ONE caller in this file (the dropdown), so neither clear
  // button reset it. An admin could clear their whole search and keep a server predicate
  // narrowing the feed, the total, the category tree and the district facet, with nothing on
  // screen saying why. That reads as missing data.
  //
  // Making it `advFilters.agentIds` is what lets every one of those fall out of a rule that
  // already exists instead of four more special cases: COMMITTED_FILTER_KEYS carries it into
  // the URL and saved searches, deskUrl serializes `agent=`, advFiltersActive counts it,
  // clearAllSearch's `setAdvFilters({...ADV_DEFAULTS})` clears it, and constraintChips gives
  // it a removable chip.
  //
  // The shim keeps the old name and the old setter contract (value OR updater), because
  // `entryAgentFilter` is read at ten sites — the counts-honesty guards among them — and
  // rewriting those is churn that would bury the actual change.
  // ⚠️ The ROLE GATE does not live here. It is applied once, in serverAdvFilters, where
  // committed state becomes a server predicate — see that note for why a URL that carries an
  // agent scope must still open for someone who cannot set one.
  // ⚠️ useMemo, not a bare `advFilters.agentIds || []`. This value is a DEPENDENCY of the
  // feedPipeline memo and of the page-reset effect, and the `|| []` fallback mints a fresh
  // array on every render whenever the key is absent (a committed state restored from a URL
  // that carried no `agent=`). That would re-run the whole feed pipeline every render — the
  // dep is `advFilters.agentIds` itself, so an absent key memoizes to ONE stable [].
  // F-091 (owner ruling 2026-09-09: "take off agents filter from leads"). Leads has no multi-agent membership
  // — only worked-by and claimed-by, one party each — so the RPC has no p_party_ids there and a committed
  // agentIds narrowed NOTHING while the control stayed lit and the counts-honesty guards below marked every
  // number provisional. On the Leads feed the filter is therefore EMPTY by definition: the control is
  // withheld (deskAgentFilter, below) and a value carried in from a saved search or from switching feeds
  // has no effect and lights nothing. The committed key is untouched, so leaving Leads restores it.
  const onLeadsFeed = entryTypeFilter[0] === 'Leads'
  const entryAgentFilter = useMemo(() => (onLeadsFeed ? [] : advFilters.agentIds || []), [advFilters.agentIds, onLeadsFeed])
  const setEntryAgentFilter = useCallback(v => setAdvFilters(f => ({
    ...f, agentIds: typeof v === 'function' ? v(f.agentIds || []) : v,
  })), [])
  const [entryPage,         setEntryPage]         = useState(0)     // page switcher over visibleGroups — resets on any filter/period change
  // ⚠️ A RESTORED PAGE HAS TO SURVIVE THE RESET DIRECTLY BELOW, and that is the whole
  // difficulty of putting the page in the URL. Applying a hash sets the filters, the filters
  // change, and the reset effect fires and zeroes the page the hash just asked for. So the
  // reset does not fight the restore — it PERFORMS it: this ref carries the wanted page, the
  // reset consumes it once instead of writing 0, and the URL writer clears it if the reset
  // never fired (a hash with a page but no filter change). Both are guaranteed to run after
  // the apply because effects run in declaration order and both are declared above it.
  const pageRestoreRef = useRef(null)
  const ENTRY_PAGE_SIZE = 30
  // Declared HERE, beside the page size, because the pager's estimatedTotalPages (~12696) reads it
  // ~350 lines above the prefetch effect that used to declare it. A component-scope const is in its
  // TDZ until its line runs — the Desk went to its error boundary on exactly that (2026-09-05).
  const AUTO_LOAD_MAX = 2000
  const [listingEntries,    setListingEntries]    = useState([])   // agent-authored/resold listings — read-only rows, not proposals
  // Instant fallback for the Properties kind subtree while no committed page-0 has
  // reported authoritative counts yet — a tally over loaded rows (spec: paint from
  // loaded rows instantly, swap to kind_counts when the commit response lands).
  const loadedKindCounts = useMemo(() => {
    const m = {}
    for (const l of listingEntries) if (l.kind) m[l.kind] = (m[l.kind] || 0) + 1
    return m
  }, [listingEntries])
  const [agentProperties,   setAgentProperties]   = useState([])   // the agent's represented properties (inventory) — for same_as cluster keys + no-listing property cards
  const [listingTotal,      setListingTotal]      = useState(0)
  const [listingTotalLanded, setListingTotalLanded] = useState(false)   // F-089: has the page-0 answer for THIS question landed? (a landed 0 is an answer)    // full count matching scope+period (server-side), not just what's fetched
  const [listingLoadingMore,setListingLoadingMore] = useState(false)
  // 100, not the server's 500 ceiling (2026-07-15 egress diet): every page request fans out
  // server-side into property_view/extraRows/thumbnail/badge batches, so page size is the
  // multiplier on both first-paint latency and Supabase egress. Filters run server-side
  // anyway (0048), so a big page buys nothing but cost.
  const LISTING_PAGE = 100
  // ── THE FIRST UNFILTERED PAGE ASKS FOR THE FIRST SCREEN (2026-09-24) ─────────────────────
  // Measured that day: the unfiltered My-properties open asked for 100 listings (~250 server
  // rows) and showed 30; server assembly is ~237 ms for 0–199 rows against ~2,009 ms for 400–599.
  // So page 0 asks for 40 (one screen of 30 groups plus slack), and the chain / idle warm bring
  // the rest in LISTING_PAGE chunks exactly as before — the near-end rule already fired a second
  // page immediately on most books, because 100 listings cluster into fewer than 90 groups.
  // What depended on the 100, checked before changing it:
  //   * localFilterMode needs `rawCount >= total`. A book of ≤100 now reaches it one chunk later
  //     (page 0 + the tail the chain fetches at once) instead of on page 0 — and the chain-
  //     completion write stores the COMPLETE page under the page-0 key, so from the second visit
  //     on the whole book paints (and filters locally) straight from cache.
  //   * facet counts come from search_meta / the dc2·fc·tc caches; `loadedKindCounts` is only the
  //     pre-arrival fallback and the agent-filter tally runs on the FILTERED path (FILTERED_PAGE),
  //     which this does not touch.
  // FILTERED pages stay FILTERED_PAGE (90, B884): there a smaller page would add a second full
  // search per click, which is the opposite of what this change is for.
  const LISTING_FIRST_PAGE = 40
  // A FILTERED first page is 30 clusters, not 100 — measured live 2026-08-12, four districts,
  // order balanced (30-first on two, 100-first on the other two, so warm buffers cancel):
  //
  //   limit=30   2,780 ms   190 KB   ~42 cards     limit=100   3,593 ms   382 KB   ~162 cards
  //
  // −813 ms (−23%) and HALF the bytes. ⚠️ Read the two numbers together before concluding
  // anything: payload halved but time fell only a quarter, so roughly **2.5 s is fixed cost**
  // that page size cannot touch (the RPC itself measured 1.1–1.3 s). Shrinking the page is
  // worth doing and is NOT the fix for the 2.5 s floor; do not let this number retire that
  // work.
  //
  // ⚠️ FILTERED ONLY, and that is the whole design. `localFilterMode` engages when the entire
  // feed fits in ONE page (total ≤ LISTING_PAGE, see its declaration), which is what makes
  // filtering an agent's own book instant with no round trip. Cutting the unfiltered page to
  // 30 would take that away from every book of 31–100 — a regression bought with a saving
  // that path does not even need, since it is served from the materialised layer and cached.
  // So the snapshot keeps 100 and only the searched path pays less.
  // ── B884, 2026-08-20 — 30 → 90, and the number is measured, not guessed ──────────────────
  // Owner: *"filtered by price and the page kept updating for minutes, adding new findings."*
  // The chain prefetches 3 display pages ahead (`nearEnd`, ENTRY_PAGE_SIZE 30), so at 30 rows a
  // request that meant THREE sequential round trips, each a full search. On the leads pool that
  // is ~3 s apiece and the agent watches cards trickle in.
  // Measured warm, in-function, leads pool, price 50k–100k USD — the RPC's own cost by p_limit:
  //     30 → 1,351 ms · 90 → 1,650 ms · 300 → 2,708 ms · 1,000 → 6,045 ms   (~5 ms per row)
  // So three 30-row pages ≈ 4,050 ms of server time and three round trips; ONE 90-row page is
  // 1,650 ms and one paint. ~2.5× less work for the same rows.
  // ⚠️ A BIGGER PAGE IS NOT FREE, which is why this is 90 and not "just fetch them all" — the
  // owner's literal ask. The cost is linear in rows: 1,000 is 6 s before anything paints, and
  // this filter matches 5,549 clusters. 90 is the point where the chain's 3-page lookahead is
  // satisfied by a single request; past that we would be buying rows nobody has scrolled to.
  // 📌 It does NOT change what an agent sees per screen — ENTRY_PAGE_SIZE (30) still owns the
  // display page. This is purely how much is fetched per round trip.
  const FILTERED_PAGE = 90
  // Multiple near-simultaneous fetches (e.g. the load effect re-firing as myAgentId/filters
  // settle, racing a load-more append) can land the same row twice — dedupe by listing_id
  // whenever entries are set so a race never surfaces as a duplicate card.
  const dedupeListings = arr => [...new Map(arr.map(l => [l.listing_id, l])).values()]
  // Identity-stable wholesale sets (Phase 2 step 1, handoff §5.1): a revalidate/cache paint
  // replaces the array with fresh JSON objects even when nothing changed, which cascades new
  // group objects → cold summary cache → full card re-render. reconcileRows keeps the OLD row
  // object (and, when nothing changed at all, the old array) wherever the new row is
  // JSON-identical. Append/patch/prune setters already reuse prev objects — only the
  // wholesale sets need this.
  const setListingEntriesReconciled = next => setListingEntries(prev => reconcileRows(prev, next, l => l.listing_id))
  const setHistoryReconciled = next => setHistory(prev => reconcileRows(prev, next, r => r.id))
  // offset for "load more" must track rowsReturned (the paginated-clause count the server
  // reports), NOT listings.length or the deduped array length. The server mixes an unbounded
  // "extraRows" context set (scraped sources behind reshares) into `listings` alongside the
  // real paginated page — advancing by listings.length overshoots the server's true cursor,
  // and once overshot every later page comes back empty forever (stuck at "N-1 of N", 2026-07-08).
  const [listingRawCount, setListingRawCount] = useState(0)
  // 'properties' (default feed, 0072 cluster pagination) or 'rows' (advanced-search path,
  // unchanged) — decides whether the "Load more" label says listings or properties, since
  // listingRawCount/listingTotal now count DIFFERENT things depending on which path served them.
  const [listingCountUnit, setListingCountUnit] = useState('rows')
  // Cooperations for every property currently in listingEntries — ONE batched fetch keyed by
  // property_id (the canonical access key, 0046), fanned out to card chips via coopsByProperty.
  // deskCoop drives the Desk-level CooperationModal (edit an existing row / open a new one
  // against a card's listing contact).
  const [coopsByProperty,   setCoopsByProperty]   = useState({})
  // B868 (a): owner-stated cooperation offers (the ad's own "ვთანამშრომლობ 50%") — same
  // batched-per-page contract as coopsByProperty; rides the SAME seen-guard effect below.
  const [coopOffersByProperty, setCoopOffersByProperty] = useState({})
  // ⚠️ Defect chips by property id (B1021, 2026-08-27) — read from the PHOTOGRAPHS, so the
  // one card-face fact here that is not the advert's own claim about itself. Same batched
  // per-page contract as coopsByProperty, its own seen-guard, and its own call: a failure
  // here must never suppress the cooperation chips, and vice versa.
  //
  // 📌 An empty map entry means FETCHED AND NONE, which is deliberately not the same as a
  // missing entry (never asked). Nothing on the card draws a "no defects" state from either
  // — absence of a claim about a wall is not a claim about the flat, which is exactly why
  // the promotion registry refuses to write `present=false` for these codes (0745).
  const [defectsByProperty, setDefectsByProperty] = useState({})
  // ⚠️ The OTHER half of the same ruling, and the one that answers his sentence literally:
  // *"if there are all fields empty from source and idmom exctracts data it takes over
  // oemty felds and we see it carly that these exact fields are created by idmon"*. Not
  // "what an agent said" — Idmon and myhome agree about a balcony on thousands of flats
  // and that is not news — but *what the advert never said and an agent found*. The server
  // owns that rule (it is a relation between rows, not a filter); this map just holds the
  // answer. Measured at ~2 chips per property, worst 5, so the card face can hold it.
  const [agentFactsByProperty, setAgentFactsByProperty] = useState({})
  // 0894: the card-face distinctiveness note ({ [property_id]: [row per intent] }). Same
  // visible-page contract as the chips above; its own seen-guard so one failure never blanks
  // another. Fetched-empty is stored as [] — "computed, nothing unusual" is a fact.
  const [distinctByProperty, setDistinctByProperty] = useState({})
  const distinctSeenRef = useRef(new Set())
  // Developer flats ride the feed as ordinary apartment cards (core 1663). What one listing row cannot say —
  // floor/size/price ranges over the stack and "N left of M" — comes from development_card, fetched for the
  // properties on screen like the distinctiveness note. Non-developer properties simply get no entry.
  const [devCardsByProperty, setDevCardsByProperty] = useState({})
  const devCardSeenRef = useRef(new Set())
  // Seller / contact line per card (core 1681) — same rails as devCardsByProperty.
  const [sellersByProperty, setSellersByProperty] = useState({})
  const sellerSeenRef = useRef(new Set())
  // 🚫 restriction summaries by LISTING id (action-row fill-state spec 2026-07-23):
  // { subjects: [{code, stance, src}] } | null (fetched, none). Same accumulate-across-
  // pages + seen-ref contract as coopsByProperty above.
  const [restrByListing,    setRestrByListing]    = useState({})
  const restrSeenRef = useRef(new Set())
  // Payment-term facts by LISTING id (B177) — deposit amount/currency + prepay booleans,
  // sliced from the SAME terms-batch responses as restrByListing (one fetch, two maps).
  // {fields} | null (fetched, none recorded). Feeds the deposit/no-last-month filters.
  const [termsByListing,    setTermsByListing]    = useState({})
  // Positive stances (allowed/preferred) by listing id — the OTHER half of the same
  // terms-batch response. restrByListing deliberately keeps only restricting stances (it is
  // the 🚫 chip's fuel and must stay that), so the 'only allowed' filter mode needs its own
  // map rather than a widened one that would change what the chip renders.
  const [allowByListing,    setAllowByListing]    = useState({})
  // B237 — "asked the owner, and there are none" by listing id. A SEPARATE map for the same
  // reason allowByListing is one: restrByListing is the 🚫 chip's fuel and must stay only
  // restricting stances, and its `null` entry is the fetched-empty marker the overlay
  // FILTERS read. Widening either would change what the chip renders or what the filter
  // classifies. { cleared: true, hidden?: true } | null (fetched, nobody has answered).
  // `hidden` = someone answered but this viewer hasn't earned the value (hidden ≠ absent) —
  // the card must not show that as "unasked", which is the confusion B237 removes.
  const [clearByListing,    setClearByListing]    = useState({})
  // The viewer's own recorded deals (B177 commission sort): fetched ONCE when the sort is
  // first selected — participant-filtered server-side, then privacy-projected, so rows
  // carry only numbers this viewer is entitled to see (their own; all of them for mgmt).
  const [myCoopRows,        setMyCoopRows]        = useState(null)
  const [deskCoop,          setDeskCoop]          = useState(null) // { propertyId, propertyLabel?, contactPartyId?, contactName?, cooperationId? }
  // Card-face 🏠＋ chip (2026-07-17): rental-restrictions / sale-conditions editor opened
  // straight from the feed, same pattern as deskCoop. Holds the card's listing row.
  const [deskTerms,         setDeskTerms]         = useState(null)
  // Card-face ↗ → "repost to portals" (B244): opens the same DealEditor on its share tab.
  // Holds the card's WHOLE listing row (same shape as deskTerms), not just an id — the
  // editor's coop/terms tabs seed from it, and with an id alone they opened empty
  // (owner-reported 2026-07-31: "თანამშრომლობა from the repost modal doesn't open").
  const [deskRepost,        setDeskRepost]        = useState(null) // listing row | null
  // ── SHARING A PROPERTY FROM THE CARD (owner 2026-09-09) ────────────────────────────────
  // ⚠️ ONLY OFFERED ON THE AGENT'S OWN BOOK, never in the leads feed, and that is the owner's
  // rule rather than a UI preference: "if that property is in leads and accessible for
  // everyone just by a link they dont need to do share like they do to client… but agent has
  // property in their working category from leads they can just share link to it and everyone
  // sees it."
  //
  // The read side already works this way. A LEAD nobody captured has no owning agent, so
  // resolvePropertyContacts withholds nothing and every agent sees the owner — a link is the
  // whole act. A property an agent CAPTURED or added by hand withholds its owner from
  // everyone else (the 2026-07-28 uniform rank rule), and there sharing is what opens it.
  // Offering "share with a colleague" on a lead would grant something the colleague already
  // has, which is the lock-with-nothing-behind-it in its other form: a door onto a room that
  // was never locked.
  const [propShare, setPropShare] = useState(null)   // the listing row whose property is being shared
  const [propShareBusy, setPropShareBusy] = useState(false)
  const [propShareErr, setPropShareErr] = useState(null)
  const [propShareReps, setPropShareReps] = useState(null)
  const openPropShare = useCallback(row => {
    setPropShare(row); setPropShareErr(null); setPropShareReps(null)
    if (row?.property_id) {
      getPropertyRepresentatives(row.property_id)
        .then(d => setPropShareReps(d.representatives || []))
        .catch(() => setPropShareReps([]))   // unknown → exclude nobody, never block the share
    }
  }, [])
  // Which tab that editor is actually on — reported by DealEditor, because the door that
  // opened it stops being the truth as soon as the agent switches tabs. Only read by the
  // URL block (2026-08-05); null while the editor is closed.
  const [deskDealTab,       setDeskDealTab]       = useState(null)

  // myAgentRecord (not just the id) so useCandidates can stamp the candidate byline here
  // too. This tab is the OTHER door onto CandidateModal — leaving it out would have made
  // "who added it" depend on which door was used, which is worse than not having it.
  const myAgentRecord = allAgents.find(a => a.auth_user_id && a.auth_user_id === session?.user?.id) || null
  const { candidates, create: createCandidate, update: updateCandidate } = useCandidates(offices, myAgentRecord)

  // Admin/general_manager see every roster agent's listings (badged with whose); manager/
  // agent_hr/agent see only their own — see the AskUserQuestion decision thread, 2026-07-03,
  // and the 2026-07-09 role split (general_manager = full access, manager = own-only).
  const myAgentId = myAgentRecord?.id || null
  // ♥ Favorites (0191): rows keyed by property_id. null = API not answered yet (or the
  // endpoint isn't deployed) → every ♥ stays hidden, feature-detect by first GET.
  // ⚠️ Declared AFTER myAgentId — the effect reads it (TDZ crash when this sat higher).
  // Identity: agent roster id, else the AUTH USER id — an admin has no roster row
  // (admin-acts-without-becoming-an-agent) and must still get per-account favorites
  // (three-views rule: the admin view is not an afterthought). The column is a plain uuid.
  // 🔴 ONE FAILED CALL USED TO COST THE WHOLE SESSION (owner-reported 2026-09-10: "favourite
  // button missing", then "now it's there" on the next load). Both shelves load once per
  // identity and swallowed their rejection, and `favSet`/`archSet` are null until they land —
  // so a single blip removed the ♡ from every card AND every modal until a full reload, with
  // nothing on screen to say why. The control did not look broken, it looked ABSENT.
  //
  // ⚠️ RETRY, DO NOT INVENT. The null-means-hide rule stays exactly as it was: rendering a heart
  // whose real state we do not know would let an agent "add" a favourite they already hold. So a
  // failure is retried rather than defaulted — three attempts with a widening gap, then give up
  // and keep today's behaviour. Recovering from a blip and guessing at state are different fixes,
  // and only one of them is safe.
  const loadShelfWithRetry = (fetcher, apply, gone) => {
    let attempt = 0
    const go = () => fetcher()
      .then(r => { if (!gone.v) apply(r) })
      .catch(() => {
        if (gone.v || ++attempt >= 3) return
        setTimeout(go, attempt * 1500)
      })
    go()
  }
  const favIdentity = myAgentId || session?.user?.id || null
  const [favRows, setFavRows] = useState(null)
  useEffect(() => {
    if (!favIdentity) return
    const gone = { v: false }
    loadShelfWithRetry(() => getFavorites(favIdentity),
      r => setFavRows(new Map((r.favorites || []).map(f => [f.property_id, f]))), gone)
    return () => { gone.v = true }
  }, [favIdentity])
  const favSet = useMemo(() => favRows && new Set(favRows.keys()), [favRows])
  // Favourites used to render in Map insertion order while the sort pill sat above it — the
  // same do-nothing control the owner caught on Leads (2026-07-31). These rows are already in
  // memory, so the sorts the saved snapshot can answer are honest and free; FEED_SORTS.Favorites
  // is the list, and this must stay in step with it — an option offered there and unhandled
  // here silently falls through to insertion order, which is the bug wearing a different hat.
  // Price is GEL-normalized through the SAME admin fx rates the feed comparator uses, so a
  // mixed USD/GEL shelf sorts honestly rather than comparing 1,900 against 5,000.
  // Unknowns SINK in every direction — a card with no price is never presented as the cheapest.
  const sortedFavRows = useMemo(() => {
    const rows = [...(favRows?.values() || [])]
    const num = (v) => { const n = v == null || v === '' ? null : +v; return Number.isFinite(n) && n > 0 ? n : null }
    const gel = f => { const p = num(f.snapshot?.price); return p == null ? null : toGel(p, f.snapshot?.currency, config?.fx_to_gel) }
    const area = f => num(f.snapshot?.area_sqm)
    const key = entrySort === 'cheapest' || entrySort === 'priciest' ? gel
      : entrySort === 'largest' ? area
      : entrySort === 'ppm2' ? (f => { const p = gel(f), a = area(f); return p != null && a ? p / a : null })
      : null
    if (!key) return rows   // 'activity' = most recently favourited, the order they arrive in
    const desc = entrySort === 'priciest' || entrySort === 'largest'
    return rows.sort((a, b) => {
      const ka = key(a), kb = key(b)
      if (ka == null && kb == null) return 0
      if (ka == null) return 1
      if (kb == null) return -1
      return desc ? kb - ka : ka - kb
    })
  }, [favRows, entrySort, config?.fx_to_gel])
  // 🗄 Archive (0375) — the SAME identity, the same optimistic pattern and the same
  // "null means the endpoint hasn't answered, so render no button" rule as ♥ above,
  // because it is the same KIND of thing: my own shelf, reversible, invisible to
  // everyone else. It is NOT the leads Archive and NOT a market report — see 0375.
  const [archRows, setArchRows] = useState(null)
  useEffect(() => {
    if (!favIdentity) return
    const gone = { v: false }
    loadShelfWithRetry(() => getArchived(favIdentity),
      r => setArchRows(new Map((r.archived || []).map(a => [a.property_id, a]))), gone)
    return () => { gone.v = true }
  }, [favIdentity])
  const archSet = useMemo(() => archRows && new Set(archRows.keys()), [archRows])
  // Reveal hidden rows IN PLACE (owner 2026-08-09) — see the note at the archSet filter in
  // activeGroups. Deliberately NOT persisted: hiding is the standing preference, revealing is a
  // momentary "what am I not being shown?", so it should lapse rather than quietly become the
  // new normal and leave an agent wondering why their shelf stopped working.
  const [showHidden, setShowHidden] = useState(false)
  const toggleArchive = l => {
    if (!archRows || !favIdentity || !l?.property_id) return
    const pid = l.property_id
    if (archRows.has(pid)) {
      setArchRows(prev => { const m = new Map(prev); m.delete(pid); return m })
      removeArchived(favIdentity, pid).catch(() => setArchRows(prev => new Map(prev).set(pid, { property_id: pid })))
    } else {
      const snapshot = {
        address: l.address || null, price: l.price ?? null, currency: l.currency || null,
        intent: l.intent || null, rooms: l.rooms ?? null, area_sqm: l.area_sqm ?? null,
        thumb: (l.images || [])[0] || null,
      }
      setArchRows(prev => new Map(prev).set(pid, { property_id: pid, listing_id: l.listing_id || null, snapshot, created_at: new Date().toISOString() }))
      addArchived({ agentId: favIdentity, propertyId: pid, listingId: l.listing_id, snapshot })
        .catch(() => setArchRows(prev => { const m = new Map(prev); m.delete(pid); return m }))
    }
  }
  const toggleFavorite = l => {
    if (!favRows || !favIdentity || !l?.property_id) return
    const pid = l.property_id
    if (favRows.has(pid)) {
      setFavRows(prev => { const m = new Map(prev); m.delete(pid); return m })
      removeFavorite(favIdentity, pid).catch(() => setFavRows(prev => new Map(prev).set(pid, { property_id: pid })))
    } else {
      const snapshot = {
        address: l.address || null, price: l.price ?? null, currency: l.currency || null,
        intent: l.intent || null, rooms: l.rooms ?? null, area_sqm: l.area_sqm ?? null,
        thumb: (l.images || [])[0] || null,
      }
      const row = { property_id: pid, listing_id: l.listing_id || null, snapshot, created_at: new Date().toISOString() }
      setFavRows(prev => new Map(prev).set(pid, row))
      addFavorite({ agentId: favIdentity, propertyId: pid, listingId: l.listing_id, snapshot })
        .catch(() => setFavRows(prev => { const m = new Map(prev); m.delete(pid); return m }))
    }
  }

  // ── Desk home (pre-search front page, 2026-07-17) ──────────────────────────
  // The default no-search/no-filter state is a cockpit, not the raw feed: upcoming
  // meetings → recent properties/clients → recently-viewed rail.
  // Design rulings (external-AI review, user-approved): action items over inventory;
  // recently-viewed is a LOCAL memory aid (localStorage), deliberately NOT the
  // listing.viewed evidence stream — that log is anti-poaching (skips own opens at
  // write time) and must not be polluted with self-views to serve a UI convenience.
  // NO "ongoing deals" section yet (user ruling): cooperation rows are not what "a
  // deal" means; the server arm (participant_agent_id) stays dormant until defined.
  // NB: this block must sit BELOW the myAgentId declaration above — it read it
  // through a temporal dead zone when it lived higher up, which crashed the whole
  // Desk tab on mount (the 2026-07-17 dark-canvas outage).
  // ⚠️ EXPLICIT MODE (2026-07-22) — home/browse is STATE, never derived from filter emptiness.
  // Until now home was `no type && no status && empty box && no adv && no image` (plus a
  // `showFullFeed` escape hatch bolted on, because "no filters" and "let me browse everything"
  // were the same state under that model). Three things were wrong with it and all three are
  // gone now — do NOT reintroduce a derived gate:
  //   1. It STROBED. One keystroke left home, backspace brought it back; the box was a mode
  //      toggle you could flap per character. That is the bug this replaces (user-reported).
  //   2. It was an unenforced contract. EVERY filter ever added had to remember to appear in
  //      that && chain or home stayed up while the feed was silently filtered underneath.
  //      `entriesView` is the scar: it had to be DELIBERATELY excluded (a leftover 'map'
  //      preference would otherwise have replaced home with a map).
  //   3. It needed `showFullFeed` to express "browse, unfiltered" at all.
  // Rule now: TYPING NEVER CHANGES THE MODE. Only actions do — commitSearch (Enter/suggestion/
  // recent), image search, a street chip, picking a category, and home's own Show-all CTAs all
  // call enterBrowse(); the header logo (deskHomeSignal → goDeskHome) is the way back.
  // Consequence, and it is intended: clearing the box no longer returns you home.
  // deskMode is GONE (DESK_MODE_REWRITE_PLAN Phase 1, §2.1): home is not a place you
  // navigate to, it is what the Desk renders when there is no COMMITTED search.
  // committedBrowse is the one non-filter bit of committed state — "the agent explicitly
  // entered the feed" (Show-all / Browse CTAs, image search, street chip, any real commit)
  // — without it, browse-everything (zero filters) would be indistinguishable from home
  // ("empty box in browse IS browse-everything", shipped 2026-07-22).
  // ⚠️ SAFETY (§2.2): isHome reads ONLY state written by commit acts. advFilters qualifies
  // since the 2a split — token compilation writes the DERIVED draft overlay
  // (draftAdvFilters below), never advFilters. Do not add a per-keystroke-written field
  // to makeCommitted's inputs; that is the original bug re-entering through a side door.
  const [committedBrowse, setCommittedBrowse] = useState(false)
  // §3b (2026-08-02): Leads is a STAGE of Property, not an Object. It still travels through
  // `entryTypeFilter` INTERNALLY — that is what every special-feed branch reads, and rewiring
  // those is step B — but it must SERIALIZE as `stage=leads`, so a shared link, a saved search
  // and the client→leads hatch (`requirementToCommitted`) all speak the same URL. Without this
  // mapping the parser's legacy `type=Leads → stage=leads` rule has no counterpart on the way
  // out, and the restore below sets an empty category: the Leads feed silently stops opening.
  // ── WHOSE LENSES ARE THESE (the `restrictionsFrom` marker) ──────────────────────────
  // Provenance, not a filter: it is written by the client→leads hatch (through the URL)
  // and read by the panel; nothing here narrows a result. It has to be its own state
  // because `advFilters` cannot carry it — `restrictions` holds `code:mode` strings and a
  // string cannot say who chose it.
  // ⚠️ It is NOT pruned here. `makeCommitted` prunes it against the canonical restrictions
  // every time this memo runs, so an agent who edits or clears a derived lens drops it
  // from the marker by the ordinary act of editing — one rule, in the state layer, rather
  // than a second copy of that rule wired into every restriction writer on this screen.
  const [restrictionsFrom, setRestrictionsFrom] = useState(null)
  const committedSearch = useMemo(() => makeCommitted({
    browse: committedBrowse,
    type: entryTypeFilter[0] === 'Leads' ? '' : (entryTypeFilter[0] || ''),
    stage: entryTypeFilter[0] === 'Leads' ? 'leads' : null,
    // F-087: the pool is a pile axis. Leads reads its archive flag, every other view the status pill; 'active' is the
    // default and is not written, so an unchanged URL still means the active pile.
    pool: (entryTypeFilter[0] === 'Leads' ? leadArchive : entryStatusFilter === 'archived') ? 'archived' : null,
    city: entryCityFilter === 'all' ? '' : entryCityFilter,
    filters: advFilters,
    restrictionsFrom,
  }), [committedBrowse, entryTypeFilter, entryCityFilter, advFilters, restrictionsFrom, leadArchive, entryStatusFilter])
  const isHome = isEmptyCommitted(committedSearch)
  // ── 6b: the client behind the derived lenses ────────────────────────────────────────
  // The marker carries an ID, never a name — provenance must not cache a label that can go
  // stale in a URL an agent pastes tomorrow. So the name is fetched, and until it lands (or
  // if the requirement is gone, or this agent may not see it) the card says "this client"
  // rather than guessing. A missing name costs a word; a wrong one costs trust.
  const [lensClientName, setLensClientName] = useState(null)
  const lensClientId = committedSearch.restrictionsFrom?.clientId || null
  useEffect(() => {
    setLensClientName(null)
    if (!lensClientId) return
    let live = true
    getRequirement(lensClientId)
      // `person.name` is the READ shape (the client modal's header reads the same field);
      // display_name is the COMMAND shape and only appears on the write side.
      .then(r => { if (live) setLensClientName(r?.person?.name || null) })
      .catch(() => {})   // no name is a rendered state, not an error to report
    return () => { live = false }
  }, [lensClientId])
  // ── "FILL THIS FROM A CLIENT" (design 6a's dashed row, built 2026-08-06) ────────────
  // The door was missing. Lenses could only be derived by coming FROM a client card
  // (the leads escape hatch), so an agent already standing in the panel — the normal
  // direction — had no way to say "this search is for Nino" and had to retype her
  // profile by hand, which is how a derived answer and a remembered one drift apart.
  //
  // ⚠️ THE POOL IS THE LOADED FEED, AND THE CONTROL SAYS SO. There is no name search for
  // clients on the API: `searchSuggest`'s client arm returns PROPOSAL ids (and only three
  // of them), while everything here needs a REQUIREMENT id, and `findPartyCandidates`
  // takes a phone or an email, never a name. So this searches the entries the Desk has
  // already loaded — 100 recent, or 500 with all-history on. That is a real bound and it
  // is written on the control rather than left to be discovered by an agent whose older
  // client silently is not there. Widening it is one field on the server's suggest arm
  // (return `result.requirement_id` beside the name); it is a server deploy, so it waits
  // to be asked for rather than riding along with a panel change.
  const clientFillOptions = useMemo(() => {
    const seen = new Map()
    for (const p of history) {
      const rid = p?.result?.requirement_id
      if (!rid || seen.has(rid)) continue
      seen.set(rid, { reqId: rid, name: p?.proposed?.person?.display_name || null })
    }
    return [...seen.values()]
  }, [history])
  const [clientFillNote, setClientFillNote] = useState(null)
  // ── B842: IT FILLS THE WHOLE SEARCH NOW (owner ruling, 2026-08-19) ───────────────────
  // Reported as *"this functon dont work"*. Reproduced in the owner's browser and it did fire —
  // picking Mariam wrote `restrict=pol_arabs:hide,pol_foreigners:hide` and drew her lens card.
  // It filled ONLY the tenant restrictions. She is `rent · max $500`; the search stayed on
  // SALE with no budget. So the control worked exactly as built, and what it was built to do
  // was a fraction of what its name promises — "fill from a client", on a panel whose visible
  // questions are budget, geography and size.
  // Owner's ruling, asked and answered: fill EVERYTHING, and offer an Undo.
  //
  // ⚠️ ONE derivation, not a second one. `requirementToCommitted` is the same function the
  // client→leads escape hatch uses (MatchSuggestions), so a client opened from her card and a
  // client picked from this door produce the identical search. Writing a second mapping here
  // is how those two would drift, and the household half is already inside it — that is why
  // the old hand-rolled `household_codes` read is gone rather than kept alongside.
  // ⚠️ REPLACE, not merge, now that it carries price/deal/geo. The old comment argued for
  // merging, and it was right while the payload was only restrictions — a tick the agent added
  // by hand and a lens derived from a client are one editable state. It stops being right once
  // the fill sets a DEAL TYPE: a half-typed sale search silently keeping its intent while
  // acquiring a rent client's budget is a search that belongs to nobody. The banner names the
  // replacement and the Undo restores the exact previous filters, which is the same contract
  // the pool switch already honours.
  const [clientFillApplied, setClientFillApplied] = useState(null)   // { name, prevFilters, prevFrom }
  const fillFromClient = useCallback(async ({ reqId, name }) => {
    setClientFillNote(null)
    let r
    try { r = await getRequirement(reqId) } catch { setClientFillNote('error'); return }
    const criteria = r?.criteria || null
    // `intent` lives on the requirement, not the criteria — it is what turns the search from
    // sale to rent, and it is the single most visible thing the old version dropped.
    const intent = r?.requirement?.intent ?? r?.intent ?? null
    const committed = requirementToCommitted(criteria, intent, reqId, r?.requirement?.property_category ?? null)
    const next = committedToAdvFilters(committed, ADV_DEFAULTS)
    // Nothing recorded at all is a real and common state and must SAY so, rather than read as
    // a broken button. Measured 2026-08-19: 51 of 114 requirement_criteria rows carry no
    // household codes — under the old restrictions-only fill those clicks produced one faint
    // amber line and nothing else, which is very likely the report itself.
    if (isEmptyCommitted(committed)) { setClientFillNote('empty'); return }
    setClientFillApplied({ name: name || null, prevFilters: advFilters, prevFrom: restrictionsFrom })
    setAdvFilters(next)
    setRestrictionsFrom(committed.restrictionsFrom || null)
    setLensClientName(name || null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advFilters, restrictionsFrom])
  // The way back. Restores the EXACT filter object that was on screen, not a recomputed one —
  // a rebuilt "before" is a second derivation and would drift from what the agent actually had.
  const undoClientFill = useCallback(() => {
    setClientFillApplied(prev => {
      if (!prev) return null
      setAdvFilters(prev.prevFilters)
      setRestrictionsFrom(prev.prevFrom || null)
      setLensClientName(null)
      return null
    })
  }, [])
  const clientFillProp = useMemo(
    () => ({ options: clientFillOptions, note: clientFillNote, onPick: fillFromClient,
             applied: clientFillApplied, onUndoFill: undoClientFill }),
    [clientFillOptions, clientFillNote, fillFromClient, clientFillApplied, undoClientFill])
  // One object, built once, for both panel call sites. `derived` is a Set of `code:mode`
  // entries — the same form the marker stores, so the card compares like with like.
  const clientLens = useMemo(() => {
    const m = committedSearch.restrictionsFrom
    if (!m) return null
    return {
      name: lensClientName,
      derived: new Set(m.derived),
      onOpenClient: () => openClientCard(m.clientId),
    }
  }, [committedSearch.restrictionsFrom, lensClientName, openClientCard])
  // §6 CommandStrip (design handoff 2026-07-23, notifications ruling 2026-07-24): the strip
  // is the collapsed cockpit AND the sole notifications entry — the bell renders in BOTH
  // desk states (that's why it's the canonical owner), the recents pill in browse only.
  // Add moved OUT of the strip (owner 2026-07-24: same placement as Agents/Candidates) —
  // a primary "+ დამატება" button at the toolbar row's right end, with a 3-kind menu.
  const [addMenuOpen, setAddMenuOpen] = useState(false)
  // ── Notifications (X-model consolidation 2026-07-24; three zones 2026-08-06) ──
  // ONE entry (strip bell, badged), ONE destination (NotificationsPanel overlay).
  //
  // ⚠️ THE PANEL-WIDE "SEEN AT" CLOCK IS GONE (2026-08-06). It lived in
  // localStorage as `desk_notif_seen` and OPENING THE BELL MARKED EVERYTHING SEEN
  // — so the badge cleared by glancing, and a colleague's unanswered request could
  // be silenced without being answered. State is per row and server-held now
  // (0447, `readState` below). Do not reintroduce a timestamp here; the stale key
  // is left unread in localStorage rather than migrated, because it says nothing
  // about which rows were actually handled.
  const [notifOpen, setNotifOpen] = useState(false)
  const openNotifPanel = () => setNotifOpen(true)
  // Decided access requests, lifted from the old add-tile feed (0158 step 4 semantics kept:
  // granted/rejected/revoked, last 14d, fail-silent for unverified viewers). Fetched at Desk
  // level because the BADGE needs the count before the panel ever mounts.
  const [accessDecided, setAccessDecided] = useState([])
  // Requests waiting on ME (scope='against-me', MEETINGS_MODEL.md §4.4). Until
  // 2026-07-24 the Desk only ever fetched 'mine', so the bell showed decisions on
  // your own asks and NEVER that a colleague was blocked waiting on you — the
  // inbound UI existed only inside the match panel's AccessRequestsNotice, which
  // an agent may never open. These are actionable, so they badge unconditionally
  // (not "since last seen": an unanswered request stays urgent until answered).
  const [accessPending, setAccessPending] = useState([])
  // What clients did with links I sent. The share page tells them "your agent has
  // been notified" — this is what makes that true, so `meeting_requested` badges
  // like any other thing waiting on you. Opens are ambient and never badge.
  const [shareActivity, setShareActivity] = useState([])
  // AI health alerts (ADMIN ONLY) — the server raises kind='ai_alert' monitor findings
  // only when the AI is genuinely broken (bad key / funds-quota out / failure streak),
  // never for routine one-off errors. Agents never see these. 2026-07-29.
  const [aiAlerts, setAiAlerts] = useState([])
  // Background-worker failures (ADMIN ONLY) — dedup crons + worker_alert (0164),
  // both from /api/health/deep. Added 2026-08-05 after two nightly dedup-audit
  // failures and a 60h portal starvation sat recorded-but-unseen for days: they
  // were only ever a badge inside the Dedup tab / had no client reader at all.
  const [systemAlerts, setSystemAlerts] = useState([])
  // Meeting follow-up nudges (0427) — fetched at Desk level because the bell badge
  // needs the critical+high count before either the banner or the panel mounts.
  // No read-state anywhere: resolving the meeting IS dismissing the nudge.
  const [meetingNudges, setMeetingNudges] = useState(null)
  // Deposit figures rewritten after they were first recorded (owner ruling B, 2026-08-06).
  // Separate state, not folded into meetingNudges: a nudge is work the viewer can finish,
  // and this is a fact they may need to challenge. Nothing here is resolved by tapping it.
  const [binderChanges, setBinderChanges] = useState(null)
  // WhatsApp clients waiting 30+ min on this agent's own line (core 4226, owner 2026-11-04 "yes build E"). Class A:
  // the client is the person waiting. Writing back in that chat is the only exit - the row then leaves on its own.
  const [clientWaiting, setClientWaiting] = useState([])
  // The medium tier marks the meeting CARD (owner ruling 2026-08-06) — the cockpit's
  // rail draws MeetingCard directly, so it needs its own lookup; the გარიგებები feed
  // builds the same index from the `nudges` prop.
  const homeNudgeIx = useMemo(() => meetingNudgeIndex(meetingNudges), [meetingNudges])
  const loadAccess = useCallback(() => {
    getAccessRequests('mine')
      .then(d => setAccessDecided((d.requests || []).filter(r =>
        r.decided_at && ['granted', 'rejected', 'revoked'].includes(r.status)
        && (Date.now() - new Date(r.decided_at)) < 14 * 86400e3)))
      .catch(() => {})
    getAccessRequests('against-me')
      .then(d => setAccessPending((d.requests || []).filter(r => r.status === 'pending')))
      .catch(() => {})   // admin / unbridged viewers simply have no inbox
    getShareActivity().then(setShareActivity).catch(() => {})
    // ⚠️ a failed read is remembered as { failed: true } when there is nothing older to keep (Desk home audit
    // 2026-10-21): the banner then says the reminders could not be loaded, instead of vanishing - an absent banner
    // reads as "nothing is overdue". A failed refresh keeps the last list it had.
    getMeetingNudges().then(setMeetingNudges).catch(() => setMeetingNudges(prev => (prev && !prev.failed) ? prev : { failed: true }))
    // Deposit figures rewritten after first save (owner ruling B, 2026-08-06). Fail-silent
    // like its neighbours: a bell that throws is worse than a bell missing one stream.
    // ⚠️ Quiet HERE, loud on the SERVER — that route's event query deliberately has no
    // .catch, so a broken lookup 500s rather than reporting "nothing was altered". This
    // side degrades a panel; that side would state a falsehood about money.
    getBinderChanges().then(setBinderChanges).catch(() => {})
    // a failed read keeps the last list (a vanished row would read as "the client was answered")
    getClientWaiting().then(d => setClientWaiting(Array.isArray(d?.rows) ? d.rows : [])).catch(() => {})
    if (role === 'admin') getAiAlerts().then(setAiAlerts).catch(() => {})
    if (role === 'admin') getSystemAlerts().then(setSystemAlerts).catch(() => {})
    // ⚠️ owner 2026-10-19: "it must catch balance error and notify manager and admin" - a manager sees the system alerts
    // too, but ONLY the 'portal_balance' ones (a portal account ran out of money; share_plans.js raises it)
    else if (role === 'manager' || role === 'general_manager')
      getSystemAlerts().then(a => setSystemAlerts((a || []).filter(x => x.worker === 'portal_balance'))).catch(() => {})
  }, [role])
  // ⏳ The BELL waits for the FEED (2026-08-09, measured on the live Desk).
  // This block issues seven requests — two access-request scopes, share activity,
  // nudges, binder changes and two admin-only alert streams — and not one of them is
  // what the agent came to look at. They were firing in the same instant as the feed:
  // entering the Desk made 48 concurrent core requests, many completing within a
  // millisecond of each other at ~2,493 ms, which is the signature of QUEUING, not of
  // individually slow endpoints. Every one of these delayed the rows on screen.
  //
  // So the initial load is deferred to the first idle moment. Nothing is dropped and
  // nothing is conditional — the bell fills in a beat later, which is the correct
  // priority for a count nobody is waiting on. The 2 s timeout is the floor, not the
  // target: a Desk that never goes idle (a busy feed) still gets its bell.
  //
  // ⚠️ Only the MOUNT path is deferred. Every explicit `loadAccess()` elsewhere — after
  // granting access, after a decision — must stay immediate: that one IS the thing the
  // agent is waiting on, and delaying it would read as the click not working.
  useEffect(() => {
    let cancelled = false
    const run = () => { if (!cancelled) loadAccess() }
    const ric = typeof window !== 'undefined' && window.requestIdleCallback
    const handle = ric ? window.requestIdleCallback(run, { timeout: 2000 })
                       : setTimeout(run, 300)   // Safari has no rIC; a short beat still yields the feed
    return () => {
      cancelled = true
      if (ric) window.cancelIdleCallback(handle); else clearTimeout(handle)
    }
  }, [myAgentId, loadAccess])
  // A nudge row opens THE MEETING — the fix lives on its status/reason chips, so
  // the nudge deliberately opens no door of its own. The loaded feed is the fast
  // path; a fresh fetch covers a nudge about a meeting outside the loaded window
  // (same pattern as the pasted-meeting-link resolver above).
  const openNudgeMeeting = useCallback(async n => {
    setNotifOpen(false)
    // ⚠️ TWO KEYS, and the second one is why clicks died (owner 2026-08-06: "those cant
    // even be opened"). getMeetingsFeed DROPS the core twin of a backfilled meeting and
    // keeps the LEGACY row (see its dedupe note), so matching only core_meeting_id finds
    // nothing for every one of the 178 backfilled rows — and the handler then did nothing
    // at all, which reads as a broken button rather than a missing row.
    const hit = list => (list || []).find(v =>
      (v.__core && v.core_meeting_id === n.id)
      || (n.legacy_viewing_id && !v.__core && String(v.id) === String(n.legacy_viewing_id)))
    let row = hit(viewings)
    if (!row) {
      const fresh = await getMeetingsFeed().catch(() => [])
      if (fresh.length) setViewings(fresh)
      row = hit(fresh)
    }
    // Still nothing → SAY SO. A silent no-op is the one outcome a nudge must never have:
    // the agent taps, nothing moves, and the banner becomes noise they learn to ignore.
    if (!row) { setCommitMsg(t('desk_nudge_open_failed')); return }
    // A missing-reason nudge must land ON the question (owner-caught live
    // 2026-08-06: "მიზეზი რისი? open modal cant even know what im asked").
    // __askReason arms the modal's reason panel for the CURRENT status, so the
    // agent arrives looking at the chips the nudge was about.
    // A core row can carry the question; a LEGACY row opens the old ViewingForm, which has
    // no reason chips — it must not be handed core-only flags.
    setEditingViewing(!row.__core ? row
      : n.why === 'missing_reason' ? { ...row, __askReason: true }
      // Same panel, DIFFERENT question. A missing-next-action nudge says "set a
      // follow-up date"; the panel it opens is titled "why?" and the date chips
      // sit below the reason list. Landing on a question nobody asked is the
      // defect this feature already shipped twice — so the flag travels too.
      : n.why === 'missing_next_action' ? { ...row, __askReason: true, __askNextAction: true }
      : n.why === 'meeting_not_updated' ? { ...row, __askOutcome: true }
      // An expired binder asks about MONEY, so it lands on the money. Without this it
      // fell through to a modal that asked nothing — the same defect as the two above,
      // reaching a third surface, which is why the fallback is now the suspicious branch
      // rather than the safe one: a `why` with no arm is a nudge with no question.
      : n.why === 'binder_expired' ? { ...row, __askBinder: true }
      : row)
  }, [viewings])
  // Optimistic removal on decide, then reload — the row must leave the feed the
  // instant it is answered, or the agent answers it twice.
  const onAccessDecided = useCallback(id => {
    setAccessPending(rows => rows.filter(r => r.id !== id))
    loadAccess()
  }, [loadAccess])
  // Which listings the viewer is actually WORKING — the salience set that decides
  // whether a price move is a row (zone B) or one of 157 in a counter (zone C).
  // Measured 2026-08-06: only 74 of 2,234 price moves in 7 days land on one.
  //
  // Three sources, all keyed both ways — /api/core/price-changes now carries
  // `property_id` alongside `listing_id` (2026-08-06), which is what lets a
  // MEETING and a COOPERATION count: both are keyed on the property, so before
  // that only shares could ever match.
  //
  // ⚠️ It still under-detects rather than over-, deliberately, and should stay
  // that way: a missed match demotes a row to a counter, while a false match puts
  // 2,234 rows back into the panel and undoes the whole round.
  const workedListingIds = useMemo(() => {
    const s = new Set()
    for (const a of shareActivity) { if (a.listing_id) s.add(a.listing_id); if (a.property_id) s.add(a.property_id) }
    // ⚠️ `property_snapshot.core_property_id`, NOT a top-level field. I wrote
    // `v.core_property_id` first and it is undefined on every row — meetings then
    // contributed NOTHING to salience, silently and with no error anywhere. The
    // authoritative path is the one the cockpit rail already uses (see the
    // homeThumbByProp lookup further down this file).
    for (const v of viewings) {
      const pid = v.property_snapshot?.core_property_id
      if (pid) s.add(pid)
    }
    return s
  }, [shareActivity, viewings])
  // ── Per-row read state (0447) ──────────────────────────────────────────────
  // Server-held, because a dismissal that does not follow the agent to their phone
  // is not a dismissal. Optimistic locally, then reconciled — the row must leave
  // the feed on the click, not on the round trip.
  const [readState, setReadState] = useState(NO_READ_STATE)
  const loadReadState = useCallback(() => {
    getNotificationRead().then(setReadState).catch(() => {})   // fail-silent until the endpoint deploys
  }, [])
  useEffect(() => { loadReadState() }, [loadReadState])
  const markNotifRead = useCallback(k => {
    setReadState(s => (s.read.has(k) ? s : { read: new Set(s.read).add(k), dismissed: s.dismissed }))
    setNotificationRead(k, 'read').catch(() => {})
  }, [])
  const dismissNotif = useCallback(k => {
    setReadState(s => ({ read: s.read, dismissed: new Set(s.dismissed).add(k) }))
    // A 409 here means the UI offered a dismiss control on a class-A row. Reload
    // rather than swallow it: the row must come BACK, or an obligation has just
    // been hidden by a bug.
    setNotificationRead(k, 'dismissed').catch(() => loadReadState())
  }, [loadReadState])
  const undoDismissNotif = useCallback(k => {
    setReadState(s => { const d = new Set(s.dismissed); d.delete(k); return { read: s.read, dismissed: d } })
    undoNotificationRead(k).catch(() => loadReadState())
  }, [loadReadState])
  // The badge counts ZONE A ONLY — obligations to a PERSON (design round 1). It is
  // therefore drivable to zero by doing work, by construction rather than by
  // discipline. Access DECISIONS dropped out of this count deliberately: "your
  // request was granted" means the wait is over, so nobody is owed anything.
  //
  // ── The badge: unresolved ZONE A only ──────────────────────────────────────
  // Two kinds of A row, and the difference decides what clears them:
  //
  //   • Rows that carry their OWN resolution — a pending draft, an access request
  //     waiting on me, a meeting owing an outcome. These badge UNCONDITIONALLY and
  //     marking them read changes nothing: the work is the only exit. Doing it makes
  //     the underlying row leave the stream, and the badge falls on its own.
  //   • Signal-only rows — a client asked to view, the AI is broken, a cron died.
  //     Nothing downstream ever reports these as "handled", so an explicit
  //     per-row read IS the resolution. It costs a deliberate act on that row,
  //     which is the whole difference from the old panel-wide clock: opening the
  //     bell no longer clears anything.
  //
  // ⚠️ `notifSeenAt` is GONE from this count. Do not reintroduce a time gate here —
  // a badge that decays with a glance is the defect the 2026-08-06 round removed.
  const stripNotifCount = useMemo(() => {
    const unread = k => !readState.read.has(k)
    return history.filter(h => h.status === 'pending' || h.status === 'failed' || h.status === 'rejected').length
      + accessPending.length
      + ((meetingNudges?.critical?.length || 0) + (meetingNudges?.high?.length || 0))
      // 0690 — a visit cancelled under the agent because the flat went to another client.
      // Badges until read: the client is waiting to hear it.
      + (meetingNudges?.gone || []).filter(n => unread(`gone:${n.id}:${n.why}`)).length
      // A client asked to see a flat. ⚠️ THIS ROW MOVED OUT OF THE SIGNAL-ONLY
      // CATEGORY (B362, owner ruling 2026-08-07): *"Reading something is evidence
      // that eyes moved across pixels. Booking a meeting is evidence that work
      // happened. Those are different events."* So it badges UNCONDITIONALLY,
      // like an access ask or a nudge — the work is the only exit, and doing it
      // makes the server drop the row, at which point the badge falls on its own.
      //
      // ⚠️ FEATURE-GATED ON THE SERVER, and the gate is the whole safety of it.
      // `resolves_on_meeting` is sent only by a server that removes the row once
      // a meeting exists for the property CLUSTER. Against an older server the
      // field is absent and the old read-clears-it rule stands — without that,
      // this half alone would make the badge PERMANENT, since nothing else would
      // ever clear it. Per row, not per payload: getShareActivity() returns the
      // array and discards the envelope.
      + shareActivity.filter(a => a.kind === 'meeting_requested'
          && (a.resolves_on_meeting || unread(`meeting:${a.id}`))).length
      + aiAlerts.filter(a => unread(`alert:ai:${a.finding_key}`)).length
      + systemAlerts.filter(a => unread(`alert:sys:${a.key}`)).length
      // A meeting starting soon (0451). Signal-only: nothing downstream reports that an
      // agent has seen it, and it must not badge unconditionally like the nudge tiers or
      // it could not be cleared until the appointment itself arrived. It also clears
      // ITSELF once the meeting passes and leaves the window.
      + (meetingNudges?.upcoming || []).filter(n => unread(`upcoming:${n.id}:${n.why}`)).length
      // A deposit figure rewritten after it was recorded (B341). Signal-only, exactly as
      // its author argued when it shipped: "nobody can resolve the fact that an amount was
      // changed; the notification's whole job is to be READ". That is this category. It
      // arrived badging on the panel-wide notifSeenAt clock, which cleared it by GLANCING;
      // a per-row read serves the same intent and cannot be satisfied by opening the bell.
      + (binderChanges?.changes || []).filter(c => unread(`binder:${c.event_id}`)).length
      // A WhatsApp client waiting on my line (core 4226). Badges UNCONDITIONALLY, like a nudge: reading it answers
      // nobody - the reply in that chat is the only exit, and the server drops the row once it is written.
      + clientWaiting.length
  }, [history, accessPending, shareActivity, aiAlerts, systemAlerts, meetingNudges, binderChanges, clientWaiting, readState])
  // desk.mode_changed (0181 telemetry): THE instrument Phase 2 needs to ever be decidable
  // (DESK_MODE_REWRITE_PLAN.md §4.2 — rapid home↔browse oscillation would be the evidence
  // that the layout swap itself is the friction). Fired on real isHome FLIPS (ref-guarded,
  // so StrictMode re-runs and no-op enterBrowse() calls from an already-browsing surface
  // never duplicate rows) — same transitions the old deskMode setters logged, plus any
  // derived flip (a commit-act write that leaves/enters home without touching the flag),
  // which under this model IS a mode change and belongs in the log.
  const prevIsHomeRef = useRef(isHome)
  useEffect(() => {
    if (prevIsHomeRef.current === isHome) return
    prevIsHomeRef.current = isHome
    trackDeskEvent('mode_changed', { to: isHome ? 'home' : 'browse', device: isMobile ? 'mobile' : 'desktop' })
  }, [isHome, isMobile])  // eslint-disable-line react-hooks/exhaustive-deps
  const enterBrowse = () => setCommittedBrowse(true)
  // "⌂ Back to home" from ANY feed view (2026-07-17): going home also RESETS the filters —
  // the browse flag alone would leave a hidden filter set armed for the next browse entry
  // (and, now that isHome derives from them, would keep the Desk in browse anyway).
  // Currency is kept (it tracks the global top-bar toggle, not a filter).
  // committedQ/dismissedSigs clear themselves via their own empty-box effects.
  const goDeskHome = () => {
    // ⚠️ entryCityFilter MUST reset here (2026-07-26): it is one of makeCommitted's inputs,
    // so a city left set keeps isHome false and the logo click landed back in browse.
    // `'active'`, the neutral status since B576 — `'all'` here left the logo click landing on a
    // feed of both piles with neither pill lit. Same correction as resetAll/clearAllSearch.
    setEntryTypeFilter([]); setEntryStatusFilter('active'); setEntrySearch(''); setEntryCityFilter('all')
    setAdvFilters(f => ({ ...ADV_DEFAULTS, currency: f.currency }))
    setImageSearch(null); setStreetSel(null)
    // ⚠️ the sort resets too (Desk home audit 2026-10-21): home's "Recently added properties" takes the first groups of
    // the feed, and the feed is fetched in entrySort order - a cheapest/oldest sort left from browsing filled the
    // "recently added" section with the cheapest/oldest flats. Like the other filters above, sort is session state.
    setEntrySort('activity')
    // entriesView is left alone (2026-07-17): home no longer depends on it, and resetting
    // it here was exactly what made the browse view "forget" the agent's choice.
    setCommittedBrowse(false)
  }
  // Header-logo home clicks (App.jsx, 2026-07-18 — replaced the strip's ⌂ button).
  // Firing on a stale nonzero signal at mount is harmless: a fresh mount is already home.
  useEffect(() => { if (deskHomeSignal) goDeskHome() }, [deskHomeSignal])  // eslint-disable-line react-hooks/exhaustive-deps
  // Header bell / + (App.jsx): same signal-counter contract as deskHomeSignal — App
  // switches the tab, the signal tells the mounted Desk what to open. ⚠️ Seeded with the
  // CURRENT value, not 0 (owner report 2026-07-24: "notifications open by default"):
  // App keeps the counter across Desk unmounts, so a fresh mount sees a stale >0 signal —
  // only a CHANGE after mount is a real press.
  const notifSigRef = useRef(notifSignal)
  useEffect(() => {
    if (notifSignal === notifSigRef.current) return
    notifSigRef.current = notifSignal
    openNotifPanel()
  }, [notifSignal])  // eslint-disable-line react-hooks/exhaustive-deps
  const addSigRef = useRef(addSignal)
  useEffect(() => {
    if (addSignal === addSigRef.current) return
    addSigRef.current = addSignal
    if (addSignal?.kind) openModal(addSignal.kind)
  }, [addSignal])  // eslint-disable-line react-hooks/exhaustive-deps
  // Badge reported UP so the header bell is truthful from any tab (freezes at last known
  // value while the Desk is unmounted — it re-syncs on remount).
  useEffect(() => { onNotifBadge(stripNotifCount) }, [stripNotifCount])  // eslint-disable-line react-hooks/exhaustive-deps
  // ── MY SHELVES: ♥ / 🗄 now live in the app header (owner 2026-08-09) ────────────────
  // Same two mechanisms as the bell above, for the same reason: the header must stay truthful
  // while another tab is open, and a press must land on the Desk and open the shelf.
  // ⚠️ `favRows`/`archRows` are null until their fetch answers. Report 0 for null rather than
  // guessing — the header renders 🗄 only above zero, and a guessed count would flash a shelf
  // button that then vanishes.
  useEffect(() => {
    onShelfBadges({ fav: favRows?.size ?? 0, hidden: archRows?.size ?? 0 })
  }, [favRows, archRows])  // eslint-disable-line react-hooks/exhaustive-deps
  // Same stale-counter guard the bell documents directly above: App keeps the signal across
  // Desk unmounts, so only a CHANGE after mount is a real press.
  const shelfSigRef = useRef(shelfSignal)
  useEffect(() => {
    if (shelfSignal === shelfSigRef.current) return
    shelfSigRef.current = shelfSignal
    if (!shelfSignal?.shelf) return
    // Exactly what the removed panel button did — `category.set` was `setEntryTypeFilter`, and
    // the telemetry keeps firing so the 2026-07-24 rail-vs-facet question can still be answered
    // for this control; only `surface` changes, which is the whole point of the move.
    setEntryTypeFilter([shelfSignal.shelf])
    trackDeskEvent('category_switched', { to: shelfSignal.shelf, count: null, surface: 'header' })
  }, [shelfSignal])  // eslint-disable-line react-hooks/exhaustive-deps
  const [recentViewed, setRecentViewed] = useState([])      // [{ id, kind, listingId, reqId, title, at }]
  const recentViewedKey = `desk_recent_viewed:${myAgentId || 'admin'}`
  useEffect(() => {
    try { setRecentViewed(JSON.parse(localStorage.getItem(recentViewedKey)) || []) } catch { setRecentViewed([]) }
  }, [recentViewedKey])
  const recordRecentView = (entry) => setRecentViewed(prev => {
    const next = [{ ...entry, at: Date.now() }, ...prev.filter(e => e.id !== entry.id)].slice(0, 10)
    try { localStorage.setItem(recentViewedKey, JSON.stringify(next)) } catch { /* quota — memory-aid only */ }
    return next
  })
  // Fill-link creator identity: the auth-uid fallback that used to live here
  // (fillCreatorId = myAgentId || session.user.id) is RETIRED (0156 /
  // IDENTITY_AND_REPRESENTATION.md inv.1) — it stored an auth uid in an
  // agents.id-namespaced column. The server now resolves the verified viewer
  // itself: agents land in created_by_agent_id, admins in created_by_party_id
  // (their staff party), and the "my links" list works for both without a
  // client-supplied id. The 2026-07-16 empty-list bug stays fixed — server-side.
  // ── POOL SCOPE — the agent's choice, not their job title (2026-08-02) ──────────────
  // Was: `(role === 'admin' || role === 'general_manager') ? 'all' : 'mine'` — a constant
  // derived from role, with no user-facing control anywhere. The pool selector needs to
  // WRITE this, so it becomes state; the role keeps gating `'all'` exactly as before.
  //
  // ⚠️ That gate is a PRODUCT choice, not a security boundary, and must never be described
  // as one. The server reads `scope` straight off the query string with no role check
  // (`server.js:6792`) — only `scope=mine` needs an agent_id. Any authenticated agent can
  // already fetch the whole company inventory by editing a URL. Traced and recorded in
  // SEARCH_INFRASTRUCTURE_AUDIT §9d, which also verified that the list response carries no
  // owner/client contact details at any scope. We clamp here because the design shows
  // "Everyone's work" to managers only — not because the clamp protects anything.
  //
  // `null` = never chosen, so the role default applies and today's behaviour is unchanged
  // byte for byte until something calls setScopePref. Deliberately NOT persisted: a scope
  // silently restored from a previous session is a posture the agent did not take today.
  // B1294/B1297 — the office scope a MANAGER is bounded by.
  // ⚠️ DECLARED HERE, above `canSeeAllScope`, for the reason this file records twice
  // already: a const cannot be read before its own declaration, and doing so threw
  // "Cannot access 'agentCounts' before initialization" and blanked the whole tab behind
  // the error boundary while build AND tests stayed green. Declare above the first consumer. Admin/GM get `company` and need none of
  // this; an agent gets `self` and never sees the facet. Fetched once per viewer, not per
  // period: a tenure does not change while the Desk is open.
  //
  // ⚠️ The agent list is the SERVER's, never derived from `agents.office_id` here. That
  // column says where somebody works TODAY, so deriving from it drops the agent who left
  // during the tenure — whose unfinished work is precisely the manager's to chase — and
  // rewrites itself whenever anyone transfers. See getOfficeScope's header.
  const [officeScope, setOfficeScope] = useState(null)
  useEffect(() => {
    if (role !== 'manager' && role !== 'agent_hr') return
    let dead = false
    getOfficeScope().then(v => { if (!dead) setOfficeScope(v) })
    return () => { dead = true }
  }, [role])
  // B1297 — owner 2026-09-12: *"managers should also see our work instead of my work, and
  // see their agents' work."* A manager is entitled to 'all' now, because 'all' no longer
  // means the agency for them: the SERVER caps the party set to their office and tenure
  // (agent-listings, scope=all). Without that cap this line would be a disclosure bug; with
  // it, 'all' is simply "our office".
  //
  // ⚠️ Gated on actually HAVING an office. A manager who covers none resolves to 'self'
  // server-side and would get an empty feed under 'all' — so they keep 'mine', which is the
  // fail-closed direction and the same condition the agent facet uses.
  const officeScoped = officeScope?.scope === 'offices' && !!(officeScope.agent_ids || []).length
  const canSeeAllScope = role === 'admin' || role === 'general_manager' || (role === 'manager' && officeScoped)
  // The office a manager's 'all' actually covers. Several offices is possible (the resolver
  // returns a list), so it joins rather than assuming one. Null for admin/GM — their 'all' is
  // the agency and "everyone's" is the honest word.
  const officeScopeLabel = officeScoped
    ? (officeScope.offices || []).map(o => o.name).filter(Boolean).join(' · ') || null
    : null
  const [scopePref, setScopePref] = useState(null)
  // 🔴 SOME ACCOUNTS CANNOT BE SCOPED TO THEMSELVES, and "Mine" lied to them (owner caught
  // it live, 2026-08-02: "I am in admin, mine shows what?").
  //
  // `myAgentId` resolves by finding a ROSTER row whose login matches the session. An admin
  // acts without becoming an agent and has no roster row, so it is null — and the listings
  // effect blanks the feed outright when scope is 'mine' with no agent id. The result was
  // an inventory that read as EMPTY rather than as unscopable: zero properties, not because
  // the account has none, but because the question could not be asked.
  //
  // Worse in the other direction: the Client and Meeting feeds only filter by agent when the
  // role is literally 'agent', so for an admin they stayed UNFILTERED. "Mine · yours · 85"
  // was in fact everyone's clients and meetings. One label, wrong in both directions at once.
  //
  // Forcing 'all' fixes both halves together — the properties feed stops needing an agent id
  // it will never have, and the label follows the scope (see the segment), so it reads
  // "Our work · everyone's", which is exactly what those two feeds were already returning.
  //
  // ⚠️ Only for accounts ALREADY entitled to 'all'. A roster-less viewer who is not an
  // admin (getMyRole falls back to 'agent' for an unseeded user) must not be widened into
  // the whole company by a bug fix — they keep today's behaviour.
  const canScopeToSelf = !!myAgentId
  const listingsScope = (!canScopeToSelf && canSeeAllScope)
    ? 'all'                                         // cannot ask "mine" → do not pretend to
    : (scopePref === 'all' && !canSeeAllScope)
      ? 'mine'                                      // clamp: never honour an ungated 'all'
      : (scopePref || (canSeeAllScope ? 'all' : 'mine'))
  facetViewerRef.current = `${session?.user?.id || myAgentId || ''}:${listingsScope}`   // 2026-10-22, lfc: viewer
  // Pool / Meeting / Business / Leads / Favorites each REPLACE the normal entries pipeline
  // with their own exclusive feed — the standard grid/map/pagination and empty-states all
  // sit out while one is active.
  //
  // ⚠️ DECLARED HERE, not next to its render-side consumers, because the entries FETCH
  // (below) is the consumer that matters most and it sits between the two. Until
  // 2026-07-27 this const lived ~1100 lines further down, so the fetch could not name it:
  // the prefetch chain, the overlay fetch and three render gates all bailed on a special
  // feed while the initial search fired anyway. On the Leads view that meant every search
  // ran TWICE — once as the leads RPC that renders, and once as a full scope=all listing
  // search whose result nothing ever reads. Measured on a real 5,936-lead feed with
  // "ქირავდება ბინა თბილისი": the ghost request took 9.3s and was the slowest thing on the
  // page by 3x. Keep this declaration above the fetch; a consumer that cannot see its own
  // gate is not gated.
  // ⚠️ MOVED DOWN to just below the identity tokens (2026-07-28, B209) — it now depends on
  // them. Still ABOVE the entries fetch effect, which is the constraint B204 established.
  // Agent NAMES on cooperation/meeting chips are admin-grade info (2026-07-11): an agent
  // reads their own cards (the name is noise), management compares agents. MUST match the
  // server's management gate (core_visibility MGMT_ROLES: admin/manager/general_manager) —
  // found 2026-07-20: 'manager' was missing here while the server granted managers full
  // numbers, so managers saw amounts with no names; deliberately NOT tied to
  // listingsScope above (whose-feed is a separate product choice).
  const privileged = role === 'admin' || role === 'general_manager' || role === 'manager'
  // Meeting status counts for the sidebar's Category→Meeting subtree — SAME scoping as
  // MeetingsFeed (visibility before counting, so the numbers reflect what this user can
  // see). Duplicated math is deliberate over lifting the feed's memo: the feed needs it
  // only when mounted, the subtree needs it whenever the sidebar shows the Meeting branch.
  //
  // ⚠️ AND DUPLICATED MATH DRIFTS — it had, in two ways, and a facet's number must equal
  // what its click delivers (fixed 2026-08-15). The feed counts a GUEST's meetings as
  // theirs (`agent_ids` is the involved set, not everyone attending) and filters the period
  // on the APPOINTMENT date; this copy did neither, so the tree said "active 3" and the
  // click produced 5. Any further change to either rule has to land in both places, or the
  // next reader gets the same silent disagreement.
  const meetingCounts = useMemo(() => {
    let rows = viewings || []
    if (!privileged && myAgentId) rows = rows.filter(v =>
      (v.agent_ids || []).includes(myAgentId)
      || (v.attendees || []).some(a => a.agent_id === myAgentId))
    const since = periodSinceISO(entryPeriod)
    if (since) rows = rows.filter(v => (meetingSchedAt(v) || v.created_at || '') >= since)
    return {
      all: rows.length,
      active: rows.filter(v => MEETING_ACTIVE.includes(v.status)).length,
      finished: rows.filter(v => MEETING_FINISHED.includes(v.status)).length,
      ...Object.fromEntries([...MEETING_ACTIVE, ...MEETING_FINISHED].map(s => [s, rows.filter(v => v.status === s).length])),
    }
  }, [viewings, privileged, myAgentId, entryPeriod])

  // Ownership rule (2026-07-12): register the contact viewer ONCE — every
  // getPropertyContacts call site (card chips, meeting seeds, modals) then sends the
  // agent id so the server can withhold another agent's scraped-origin owner and hand
  // back that agent's contact instead. Admin/GM → null → unrestricted.
  useEffect(() => { setContactViewerAgentId(privileged ? null : myAgentId) }, [privileged, myAgentId])

  // Recommendation privacy viewer (Phase 1, D14 per-agent): registered for EVERY
  // role — masking is per-agent with no admin bypass in Phase 1, so the server
  // needs to know who is looking even for admins (an unregistered viewer fails
  // closed to the fully-masked representative view). Also the actor for
  // recommendation.presented/.opened/.action_taken events.
  useEffect(() => { setCoreViewer(myAgentId) }, [myAgentId])

  useEffect(() => {
    getDistricts().then(setDistricts).catch(console.error)
    getFeatures().then(setFeatures).catch(console.error)
    // Rides the same cached /features response — see getRestrictionExpansion.
    getRestrictionExpansion().then(setRestrictionExpansion).catch(() => {})
    getKindFeatureCapability().then(setKindFeatureCaps).catch(() => {})   // 0759
    getConditionLadder().then(setConditionLadder).catch(() => {})   // 0788-0790
    // Location gazetteer for the search compiler (engine v7) — one fetch per Desk
    // load; a failure just means no near-location chips, never a broken search box.
    getLocations().then(setGeoLocations).catch(() => {})
  }, [])

  // Meetings feed (type = "Meeting"): the full viewings list is lazy — load it the first time
  // the agent opens the meetings view. getMeetingsFeed() is unscoped by status (the feed's own
  // Active/Finished tabs do that client-side); per-agent visibility is enforced inside the feed.
  useEffect(() => {
    if (entryTypeFilter[0] === 'Meeting' && viewings.length === 0) {
      loadViewings()
    }
  }, [entryTypeFilter, viewings.length])

  // Meetings on card faces (2026-07-11): the listing feed needs the viewings list to pin a
  // 📅 chip on each card. One full load when the feed first has cards (viewings volume is
  // office-scale, and the Meeting filter above wants the same list anyway); the ref stops a
  // refire loop when there are simply no viewings yet.
  const viewingsLoadedRef = useRef(false)
  useEffect(() => {
    if (!listingEntries.length || viewingsLoadedRef.current) return
    viewingsLoadedRef.current = true
    // through loadViewings: its settled/error state is what the home agenda reads (audit 2026-10-21 - this path
    // swallowed a rejection, so a failed read rendered as "no meetings")
    if (viewings.length === 0) loadViewings()
  }, [listingEntries.length])  // eslint-disable-line react-hooks/exhaustive-deps
  // Same join keys the lineage modal uses (ad URL / snapshot core_listing_id) — a card
  // looks itself up by its own url/id plus its platform siblings'.
  const meetingsIndex = useMemo(() => {
    if (!viewings.length) return null
    const byUrl = {}, byCore = {}, byProperty = {}
    for (const raw of viewings) {
      const s = raw.property_snapshot || {}
      // ⚠️ CORE MEETINGS ARE PROPERTY-ANCHORED and carry neither an ad URL nor a
      // core_listing_id — a meeting is about showing a PROPERTY, which outlives
      // any one ad. Before this they hit the `continue` below and pinned no chip
      // at all, so every meeting created after the move to core was invisible on
      // listing cards (the Meetings tab was right; the cards were not).
      if (s.core_property_id) {
        const v = raw.agent_names ? raw
          : { ...raw, agent_names: (raw.agent_ids || []).map(id => allAgents.find(a => a.id === id)?.name).filter(Boolean) }
        ;(byProperty[s.core_property_id] ??= []).push(v)
      }
      if (!s.source_url && !s.core_listing_id) continue
      // getMeetingsFeed() rows carry agent_ids, not names — resolve once here so the chip's
      // privileged label has them (getViewingsBySourceUrls rows already arrive named).
      const v = raw.agent_names ? raw
        : { ...raw, agent_names: (raw.agent_ids || []).map(id => allAgents.find(a => a.id === id)?.name).filter(Boolean) }
      if (s.source_url) (byUrl[s.source_url] ??= []).push(v)
      if (s.core_listing_id) (byCore[s.core_listing_id] ??= []).push(v)
    }
    return { byUrl, byCore, byProperty }
  }, [viewings, allAgents])

  // ── The client↔meeting link for the Recent-clients cards (owner 2026-08-01) ──
  // "The client was added to the meeting that was closed — shouldn't that be registered
  // here?" It is registered, just never shown: the meeting knows its client, the client
  // card knew nothing. Built from the SAME already-loaded feed as meetingsIndex above, so
  // no extra request and the same viewer scoping.
  //
  // Requirement first, party as the fallback — a party may hold several demands and a
  // viewing belongs to the one it was booked for; the fallback catches meetings attached
  // to a client who had no requirement yet. Legacy scheduler rows carry neither key and
  // simply contribute nothing.
  const clientMeetings = useMemo(() => {
    if (!viewings.length) return null
    const byRequirement = {}, byParty = {}
    for (const m of viewings) {
      if (m.requirement_id) (byRequirement[m.requirement_id] ??= []).push(m)
      if (m.client_party_id) (byParty[m.client_party_id] ??= []).push(m)
    }
    return { byRequirement, byParty }
  }, [viewings])

  // The proposals-feed refetch trigger lives BELOW (after committedQ / advFiltersDebounced are
  // in scope) so it can widen to all-history on an active search/filter — see `histAllTime`.
  // loadHistory is a hoisted function declaration, so a later effect can call it fine.
  //
  // ── LANDED search text (owner ruling 2026-08-05: typing must feel like typing) ──
  // The box text the FEED is filtered by, as opposed to entrySearch (what the agent is
  // typing right now). §5.3 already kept the SERVER commit-gated; this extends the same
  // gate to the client side, reversing 2026-07-17's "live residual keeps filtering
  // INSTANTLY over loaded rows — kept deliberately". What that deliberate choice actually
  // cost, measured on the real feed: every keystroke re-ran feedPipeline over every loaded
  // group and re-rendered the whole card list, and a phone/id token fired the ~2.9s
  // identity reach-back on PARTIAL numbers, digit by digit. Typing lagged behind the
  // keyboard, which is a worse disorientation than the feed holding still.
  // Written ONLY by commit acts (commitSearch — Enter / suggestion / hint / recents) and
  // the clear-on-empty effect below; everything render-expensive derives from THIS, while
  // entrySearch keeps driving the cheap live surfaces: highlight mirror, hints, the
  // suggest dropdown, searchPreview. The dropdown IS the live feedback now ("pick
  // options"); the feed answers on Enter.
  // ⚠️ Declared HERE, above its first consumer (the entryPage reset directly below) —
  // sixth instance of this file's declare-above-first-consumer rule.
  const [landedSearch, setLandedSearch] = useState('')
  // Page reset keys on the LANDED text: with the feed no longer moving under a keystroke,
  // resetting the page per keystroke would be pure disruption.
  useEffect(() => {
    // A URL restore is landing: this very run is the one that would have wiped it.
    if (pageRestoreRef.current != null) {
      const want = pageRestoreRef.current
      pageRestoreRef.current = null
      setEntryPage(want)
      return
    }
    setEntryPage(0)
  }, [entryPeriod, entryTypeFilter, entryAgentFilter, entryStatusFilter, landedSearch, entryCityFilter, entrySort, advFilters])

  // Advanced-search filters execute SERVER-SIDE (search_agent_listings RPC, migration 0048) —
  // client-side filtering only ever saw loaded pages, so past a few pages a matching listing on
  // an unfetched page silently never appeared. The panel state debounces 300ms into a refetch;
  // the client-side classify below still runs over what arrives (returned rows all pass; it
  // also catches the additive origin-ad extras the endpoint doesn't filter).
  const [advFiltersDebounced, setAdvFiltersDebounced] = useState(advFilters)
  useEffect(() => {
    const t = setTimeout(() => setAdvFiltersDebounced(advFilters), 300)
    return () => clearTimeout(t)
  }, [advFilters])
  // VALUE signature of the debounced filters — fetch effects key on THIS, not the object.
  // Effects that write advFilters with value-identical state (e.g. the currency sync on
  // mount) mint a new object identity, and each identity change re-fired the main load
  // effect: Desk open sent 3 identical agent-listings requests back-to-back (~12s each,
  // the most expensive endpoint we have — caught live 2026-07-15 during the egress diet).
  // '' whenever no filter is ACTIVE — currency (always set) rides along in the normalized
  // object, so without this gate the mount-time currency settle still produced a distinct
  // sig (and a duplicate fetch) despite zero active filters.
  // City filter → SERVER predicate (2026-07-20): a selected city expands to its district codes
  // and rides the EXISTING server district_codes filter (0100), so the feed is answered
  // server-side (complete, only-matching clusters) instead of client-filtering a partial page and
  // letting the prefetch chain crawl to find the city's rows — the same class of bug as the phone
  // reach-back. Explicit district checkboxes are more specific → they win; city is the fallback.
  // The client-side city filter below (visibleResult) stays: it still scopes clients/meetings/
  // drafts, which the listing RPC doesn't touch. cityDistrictCodes passes EVERY level's code, so
  // a property coded at any tier matches exactly (never demoted to the unknown tier by hierarchy).
  const cityDistrictCodes = useMemo(
    () => entryCityFilter === 'all' ? [] : districts.filter(d => districtInCity(d, entryCityFilter, districts)).map(d => d.code),
    [entryCityFilter, districts])
  const districtTree = useMemo(() => districtTreeIndex(districts), [districts])
  const effAdvActive = advFiltersActive(advFiltersDebounced) || cityDistrictCodes.length > 0
  const effAdvFilters = useMemo(() => {
    if (!(advFiltersActive(advFiltersDebounced) || cityDistrictCodes.length > 0)) return null
    const n0 = normalizeAdvFilters(advFiltersDebounced)
    // ⚠️ HIGHER HIERARCHY WINS (owner ruling 2026-08-01, SEARCH_GRAMMAR §5b): where the chosen
    // identity/intent/object makes a filter meaningless, the filter is IGNORED — not applied.
    // Without this, land + "rooms 2" hit `kind_attribute_capability`'s land/rooms=false, the
    // RPC's range check returned 'na', `classified` mapped 'na' → MISMATCH, and EVERY land
    // property was excluded — while the panel had correctly hidden the Rooms control, so the
    // cause was invisible. Land is the higher statement; rooms is simply not asked of it.
    // Applied HERE because effAdvFilters is the single upstream of both the server query and
    // the chip row, so the query, the chips and the panel cannot disagree.
    // ⚠️ IGNORING IS NOT DELETING: `advFilters` (state) keeps the value, so returning to a
    // compatible identity restores it. A navigation click must never destroy what was typed.
    const n = applicableFilters(n0, entryTypeFilter[0] || 'Property', ADV_DEFAULTS, SERVER_ONLY_ADV_KEYS)
    // 2026-10-22: the city rides as includes only where it makes no 0886 carve-out. Injecting every city
    // code beside "✕ Saburtalo" turned each Saburtalo child into a carve-out (the ✕ removed only rows
    // coded TBS_SABURTALO), and an own-bucket-only ✓ was widened to the whole city. See districtFilter.js.
    return cityScopedDistrictFilters(n, cityDistrictCodes, districtTree)
  }, [advFiltersDebounced, cityDistrictCodes, entryTypeFilter, districtTree])
  // SERVER-facing filter view (B177, 2026-07-27): the overlay-fact filters
  // (LOCAL_ONLY_ADV_KEYS — restriction excludes, deposit ceiling, no-last-month) never
  // reach the RPC, so they are stripped BEFORE the fetch layer sees anything. Two
  // consequences, both deliberate: (1) toggling one never refetches (the server response
  // could not differ — same argument as the currency neutralization below); (2) when ONLY
  // local filters are active this is null, so the feed keeps its unfiltered browse
  // snapshot and the pipeline's classify does all the work client-side.
  // ONE definition of "may this viewer filter by agent", used by BOTH the panel facet and
  // the server predicate above — so the control can never render for someone whose
  // selection would then be dropped server-side.
  // Agent facet counts (0232). Cached per period, never per keystroke — the RPC is 206 ms
  // warm but 3.6 s cold, so it must never sit in front of a render. Admin/GM only.
  //
  // ⚠️ DECLARED HERE, above deskAgentFilter, not down beside districtCounts where it
  // naturally belongs: deskAgentFilter reads it, and a const cannot be read before its
  // own declaration. Putting it with its sibling threw "Cannot access 'agentCounts'
  // before initialization" and blanked the whole tab behind the error boundary — build
  // and tests were both green, only the runtime smoke caught it. Fifth instance of this
  // class in this file; the rule is simply: declare above the first consumer.
  const [agentCounts, setAgentCounts] = useState({})
  useEffect(() => {
    if (!(role === 'admin' || role === 'general_manager' || role === 'manager')) return
    const acKey = `ac:${entryPeriod}`
    const cached = readDeskCache(acKey)
    if (cached) setAgentCounts(cached)
    getAgentCounts({ since: periodSinceISO(entryPeriod) })
      .then(m => { setAgentCounts(m); writeDeskCache(acKey, m) })
      .catch(() => { if (!cached) setAgentCounts({}) })
  }, [role, entryPeriod])
  // B1294 — owner 2026-09-12: *"they should have a filter of agents like admin has, but capped
  // to their office agents … and only agents that were there while they were manager."* So a
  // manager gets the SAME control, over a SMALLER list; the capability is identical and only the
  // population differs, which is why this is one facet with two populations rather than a second
  // manager-shaped control.
  //
  // ⚠️ The cap must be a REAL LIST, not an empty one. `officeScope` is null until the fetch
  // lands and `agent_ids` is empty for a manager who covers no office — both would render a
  // facet with nothing in it, which reads as "you have no agents" rather than "not loaded" or
  // "you manage nobody". The facet is withheld until the list exists, and its absence says
  // nothing false.
  const scopedAgentIds = officeScope?.scope === 'offices' ? (officeScope.agent_ids || []) : null
  const canFilterByAgent = !onLeadsFeed && (   // F-091: never offered on Leads
    role === 'admin' || role === 'general_manager' ||
    (role === 'manager' && !!scopedAgentIds?.length))
  // Two populations, one control. `allAgents` (not the role/office-filtered `agents` prop) stays
  // the base for company-wide viewers for the reason its own call site records — a resigned
  // agent's past work must still be findable.
  const agentFacetAgents = useMemo(() => {
    if (!scopedAgentIds?.length) return allAgents
    const keep = new Set(scopedAgentIds)
    return allAgents.filter(a => keep.has(a.id))
  }, [allAgents, scopedAgentIds])
  const deskAgentFilter = canFilterByAgent
    // 2026-10-22: the counts are period-only (getAgentCounts) — withheld while any other filter narrows the search.
    ? { agents: agentFacetAgents, selected: entryAgentFilter, onChange: setEntryAgentFilter, counts: agentCountsDescribeSearch(committedSearch) ? agentCounts : null }
    : null
  // F-045: the hold is taken the moment a COMMITTED input changes (the local re-filter of loaded rows runs on
  // this render; the fetch follows the debounce). A commit that changes nothing on screen still holds for the
  // fetch's duration and is released once the answer is drawn — harmless.
  const commitSigRef = useRef(null)
  useLayoutEffect(() => {
    const sig = JSON.stringify([advFilters, landedSearch, entryStatusFilter, entryPeriod, listingsScope])
    if (commitSigRef.current !== null && commitSigRef.current !== sig) {
      // a commit: hold the height REMEMBERED before this render (the DOM has already emptied)
      if (!SPECIAL_FEED) holdFeedHeight('commit')
    } else if (!holdElRef.current) {
      // no commit: remember what the feed measures now, for the next commit
      const g = feedGridRef.current, p = g && g.parentElement
      if (g && p && g.offsetHeight > 0) lastFeedHeightRef.current = { grid: g.offsetHeight, feed: p.offsetHeight }
    }
    commitSigRef.current = sig
  })   // every render: the branch decides whether this render is a commit or a measurement
  const serverAdvFilters = useMemo(() => {
    let base = null
    if (effAdvActive) {
      const rest = { ...effAdvFilters }
      for (const k of LOCAL_ONLY_ADV_KEYS) delete rest[k]
      // ── THE ROLE GATE, and the reason it is HERE and nowhere else (2026-08-09) ──────────
      // Admin/GM only, the same gate the control renders behind: an agent's own feed is
      // already scoped to them, so a self-filter would be a no-op costing an RPC round trip.
      // It sits at this edge — the single point where committed state becomes a server
      // predicate — rather than in the URL parser, because the two failures are not
      // symmetric. Refusing to PARSE `agent=` would make a link an admin shared with a
      // colleague fail as a whole; stripping it HERE means that link opens as the same search
      // minus a scope the viewer was never entitled to set. Parse permissively, apply
      // strictly. (No data is at stake either way — a non-admin's feed is server-scoped to
      // `mine` regardless — so the gate is about honest UI, not access control.)
      // 🔴 F-107 (2026-09-23): the gate is `canFilterByAgent` — the SAME predicate the control renders
      // behind — and it clears BOTH halves. It used to be a second, narrower copy (admin/GM only) that
      // cleared only ✓: a manager saw the agent facet (B1294), picked an agent, and the pick was
      // stripped here before the server could cap it to their office (B1297); and the ✕ half
      // (agentIdsNot) was never cleared for anyone.
      if (!canFilterByAgent) { rest.agentIds = []; rest.agentIdsNot = [] }
      if (advFiltersActive(rest) || (rest.districtCodes || []).length) base = rest
    }
    if (!base) return null
    // Normalized base so advSig's priceMin/priceMax reads stay meaningful instead of
    // comparing undefined. ⚠️ The old `!base && !agentIds.length` escape hatch is gone with
    // the separate state: agentIds is an ordinary key now, so advFiltersActive above answers
    // "is the agent filter the only thing set" by itself.
    return { ...ADV_DEFAULTS, ...base }
  }, [effAdvActive, effAdvFilters, canFilterByAgent])
  // ⚠️ currency is NEUTRALIZED in the signature when no price bound is set (B122 Phase 3):
  // it's a display toggle then — the RPC only reads it next to a price predicate — but it
  // still sat in the JSON, so flipping $/₾ with, say, a rooms filter active re-fetched the
  // whole filtered feed for a response that could not differ. With a price bound set the
  // flip changes the QUESTION (price ≤ N in WHICH currency) and the refetch is correct.
  // Built from serverAdvFilters (not effAdvFilters) so the local-only keys are already out.
  const advSig = useMemo(() => {
    if (!serverAdvFilters) return ''
    // a per-m² bound is a price bound for this purpose too (filters audit 2026-10-27 F04: with only ppm set, a currency
    // flip changed the question but not this signature, so the old currency's results stayed on screen)
    const hasPriceBound = [serverAdvFilters.priceMin, serverAdvFilters.priceMax, serverAdvFilters.ppmMin, serverAdvFilters.ppmMax].some(v => (v ?? '') !== '')
    return JSON.stringify(hasPriceBound ? serverAdvFilters : { ...serverAdvFilters, currency: null })
  }, [serverAdvFilters])
  // Commission sort fuel (B177), fetched once on first use of the sort. Owner ruling
  // 2026-07-27: the sort is not "my commission" — it is THE commission, and who that means
  // falls out of who is looking. Two fetch shapes, one sort:
  //   agent      → their own participations (listMyCooperations, participant-filtered).
  //   management → every deal on the loaded properties (listCooperations by property_id,
  //                already chunked at 80 ids). An admin has no participation of their own,
  //                so the participant filter would return nothing and the lens would look
  //                broken for exactly the person most likely to want a company-wide ranking.
  // Either way the server applies the SAME visibility projection before we see a number —
  // management sees all of them because it is entitled to, not because this asked louder.
  // proposed/negotiating/agreed only: an ended deal's number must not keep ranking its
  // property. Errors resolve to [] so the sort degrades to the default tail instead of
  // never settling (a null would refire the effect on every render).
  // ⚠️ Guarded by a fetch SIGNATURE, not by "have we fetched once" (caught while writing
  // this): the management route asks about the properties currently loaded, and the feed
  // grows as pages arrive — a once-only guard would rank page 1's deals and leave every
  // later page permanently blank, which reads as "those have no commission".
  // The signature is the loaded-row COUNT, not the id list: ids would rebuild a 20KB key on
  // every render for a value that only needs to say "the set moved". Same-length-different-
  // rows is possible in principle (a filter swap) — it costs a stale map until the next
  // page lands, never a wrong number, because unknown sinks rather than ranking.
  const coopSortSig = entrySort !== 'commission' ? ''
    : privileged ? `p:${listingEntries.length}` : `a:${myAgentId || ''}`
  const coopSortSigRef = useRef('')
  useEffect(() => {
    if (!coopSortSig || coopSortSigRef.current === coopSortSig) return
    coopSortSigRef.current = coopSortSig
    const propIds = privileged
      ? [...new Set(listingEntries.map(l => l.property_id).filter(Boolean))]
      : null
    // DEV attribution probe (perf-attribute-dont-assume): this fetch shares an endpoint
    // with the visible-page coop overlay, so "a /cooperations call happened" is NOT
    // evidence it ran — that misreading cost a wrong verification once already. Compiled
    // out of production.
    if (import.meta.env.DEV) {
      window.__coopSort = { sig: coopSortSig, privileged, myAgentId, propIds: propIds?.length ?? null, rows: null }
    }
    const p = privileged
      ? (propIds.length ? listCooperations({ propertyId: propIds }) : Promise.resolve([]))
      : (myAgentId ? listMyCooperations(myAgentId, { status: 'proposed,negotiating,agreed', limit: 500 }) : null)
    if (!p) return
    p.then(rows => {
      // The by-property route has no status filter threaded through the chunked wrapper —
      // narrow here so both routes rank the same set of LIVE deals rather than whatever
      // each door happened to return.
      const live = (rows || []).filter(c => !privileged || ['proposed', 'negotiating', 'agreed'].includes(c.status))
      if (import.meta.env.DEV && window.__coopSort) {
        window.__coopSort.rows = rows?.length ?? 0
        window.__coopSort.live = live.length
      }
      setMyCoopRows(live)
    }).catch(e => {
      console.error('[coop-sort]', e.message)
      coopSortSigRef.current = ''   // let a later render retry; a stuck sig would never recover
      setMyCoopRows([])
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coopSortSig])
  // property_id → { amount, currency, cmp } for the viewer's best visible deal.
  // bestVisibleDealValue works on PROJECTED rows (others' numbers arrive nulled), so this
  // map can never rank on a number the viewer isn't entitled to see. cmp is GEL-normalized
  // with the SAME admin fx rates the price sort uses (toGel) — one conversion rule per feed.
  // Entries carry `kind` ('commission' | 'price') alongside the comparable amount — the
  // sort ranks kind before value so a deal SIZE never outranks a real FEE (coopMath.js).
  // Two cooperations on one property are reduced the same way: better kind wins, then the
  // larger amount, so a property with a fee on one deal is never represented by another
  // deal's bare price.
  const myCommissionByProperty = useMemo(() => {
    const m = new Map()
    const rank = v => (v.kind === 'commission' ? 0 : 1)
    for (const c of myCoopRows || []) {
      if (!c.property_id) continue
      const best = bestVisibleDealValue(c)
      if (!best) continue
      const cmp = toGel(best.amount, best.currency, config?.fx_to_gel)
      const prev = m.get(c.property_id)
      if (!prev || rank(best) < rank(prev) || (rank(best) === rank(prev) && (cmp ?? -Infinity) > (prev.cmp ?? -Infinity))) {
        m.set(c.property_id, { ...best, cmp })
      }
    }
    if (import.meta.env.DEV && window.__coopSort) {
      window.__coopSort.mapSize = m.size
      window.__coopSort.kinds = [...m.values()].reduce((a, v) => ({ ...a, [v.kind]: (a[v.kind] || 0) + 1 }), {})
      // Do the map keys actually intersect the ids the SORT looks up? Two populated sets
      // that never meet is the failure mode a size-only probe cannot see.
      const feedIds = new Set(listingEntries.map(l => l.property_id).filter(Boolean))
      window.__coopSort.hits = [...m.keys()].filter(k => feedIds.has(k)).length
      window.__coopSort.sampleMapKey = [...m.keys()][0] || null
      window.__coopSort.sampleFeedId = [...feedIds][0] || null
    }
    return m
  }, [myCoopRows, config?.fx_to_gel, listingEntries])
  // Hover-prefetch for the Leads category node (perceived-speed remainder, 2026-07-24):
  // warms LeadsFeed's page-0 SWR snapshot for EXACTLY the query it will run on click
  // (same landedSearch/effAdvFilters/sort/archive inputs as the <LeadsFeed> render below).
  // Fire-and-forget + deduped inside prefetchLeads; the feed still revalidates on mount.
  // Leads count for the category tree (owner 2026-07-24: "leads category is missing count
  // numbers"). Every other row counts loaded rows; Leads has its own feed, so the number
  // comes from that feed's page-0 snapshot — ONE source, so the rail and the feed cannot
  // disagree. The tick just forces a re-read after an async warm lands.
  // ⚠️ Deliberately NOT a count query on every filter change: that would double the Desk's
  // per-keystroke DB load for a category the agent may not be looking at (see the feed
  // saturation incident). It warms once on mount and on the hover that already prefetches,
  // and shows nothing rather than a number from a different filter set.
  const [leadCountTick, setLeadCountTick] = useState(0)
  // ⚠️ Counts are per SUB-STATE, keyed by `archive` — active and archive are two different
  // server queries, so one number cannot stand for both (owner 2026-07-25: "numbers after
  // active is not showing... needs deeper fixing rather than surface level"). What was
  // broken was the SHAPE, not a missing lookup: one `leadCount` was computed for whichever
  // sub-state happened to be selected and printed on the PARENT row, while both child rows
  // were hard-coded to `null`. So the parent's number silently changed meaning when the
  // toggle moved, and the row the agent actually reads — აქტიური — never had one at all.
  // Now: parent = ACTIVE (what clicking it shows, stable regardless of the toggle), each
  // child = its own query's total. Still ONE source per number (the feed's own page-0
  // snapshot), so a rail number can never disagree with the feed it labels.
  // ⚠️ MEMOIZED, and it must stay that way. periodSinceISO derives its value from Date.now()
  // at MILLISECOND precision, so calling it per render yields a different string every time.
  // Passed inline to <LeadsFeed> that made `since` a new value on every render, which changed
  // the feed's effect dep, which refetched, which re-rendered — an endless reload loop on the
  // Leads page (shipped and owner-caught within the hour, 2026-07-31). It also silently broke
  // the cache: every call minted a fresh key, so no lookup could ever hit.
  // The property pipeline never had this problem because it calls periodSinceISO INSIDE the
  // fetch and keys its effects on `entryPeriod`. Anything that passes the derived value
  // across a render boundary has to hold it stable.
  const entrySince = useMemo(() => periodSinceISO(entryPeriod), [entryPeriod])
  // ⚠️ `since` must be in here as well as on the <LeadsFeed> below. These helpers read the
  // feed's OWN page-0 snapshot by cache key, so a signature missing `since` would look up a
  // different query's total and print it beside the feed — the "a count from a different
  // filter set is a lie with digits in it" rule this file already states twice.
  const leadCountArgsFor = archive => ({
    // landedSearch (2026-08-05): must equal <LeadsFeed query=…> exactly — these read the
    // feed's snapshot BY CACHE KEY, and a key built from the live text while the feed runs
    // the landed text is a lookup into a query nobody ran.
    query: landedSearch, advFilters: serverAdvFilters,
    // ⚠️ LEADS_SERVER_SORT_OF, the table <LeadsFeed sort=…> uses: with SERVER_SORT_OF, Commission (Leads-only) keyed
    // no sort here, so the rail count, facets and histograms looked up a query nobody ran (Desk search audit 2026-10-29).
    sort: LEADS_SERVER_SORT_OF[entrySort] || null, archive,
    since: entrySince,
    // ⚠️ Same rule as `since` directly above: the lens changes the answer, so it belongs in
    // the signature. Without it the rail would print the All-leads total beside a feed
    // showing only the ones this agent is working.
    mine: leadWorked,
  })
  const leadCountArgs = leadCountArgsFor(leadArchive)
  const leadHoverPrefetch = () => prefetchLeads(leadCountArgs).then(() => setLeadCountTick(t => t + 1))
  // F-022 (2026-09-05): the leads COUNT for the current filters used to fire on mount, in
  // PARALLEL with the agent's own search. Measured on the owner's Desk: one load fired five
  // searches in six seconds (three feed warms, the filtered search, this count), and under a
  // two-slot admission control the search the agent asked for hit the 8 s net while its own
  // count took a slot. Sequenced: the count fires once, AFTER the first feed page has landed
  // (listingsFetching goes true -> false). The pill shows "…" a moment longer; the search wins.
  // ⚠️ Verified on the real Desk after the first cut (search_exec_log 20:46:06): the count STILL
  // fired in the same second as the search, because listingsFetching starts FALSE on mount and
  // the effect fired before the first fetch had even begun. The right signal is the transition
  // true -> false: the first page has been asked for AND has landed.
  const leadCountPrimedRef = useRef(false)
  const feedWasFetchingRef = useRef(false)
  useEffect(() => {
    if (listingsFetching) { feedWasFetchingRef.current = true; return }
    if (!feedWasFetchingRef.current || leadCountPrimedRef.current) return
    leadCountPrimedRef.current = true
    prefetchLeads(leadCountArgsFor(false)).then(() => setLeadCountTick(t => t + 1))
  }, [listingsFetching])   // eslint-disable-line react-hooks/exhaustive-deps
  // The OTHER sub-state's total costs a WHOLE EXTRA cluster search. It used to fire on a
  // 1.2s timer after every query settle while in Leads — the reasoning being that 1.2s is a
  // real typing pause, not per-keystroke. True, and still too often: it doubled the arrival
  // rate of the single most expensive query in the app, permanently, for a number in a
  // sidebar row the agent is usually not reading.
  //
  // B18 (2026-07-29) measured what that costs when agents overlap: 22 concurrent leads
  // searches → 21 failed with statement timeouts. Server-side admission control now keeps
  // those alive, but the cheapest request is the one never sent. Warmed on HOVER instead
  // (leadSub passes this to whichever sub-row is not current), matching how the Leads
  // category row itself already behaves.
  const leadPrefetchOther = () =>
    prefetchLeads(leadCountArgsFor(!leadArchive)).then(() => setLeadCountTick(t => t + 1))
  // B1201. The hatch offers the POOL ("nobody has called these"), so it must not be counted
  // through the "leads I work" lens the rail uses. Same cache, different signature.
  const hatchLeadCount = useMemo(() => leadsCachedTotal({ ...leadCountArgsFor(false), mine: false }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [landedSearch, serverAdvFilters, entrySort, entrySince, leadCountTick])
  // review 2026-09-23: `leadWorked` is in the cache key (mine:), so it must be a dep too —
  // without it toggling "Working" kept printing the previous lens's number until page 0 landed.
  const leadCountActive = useMemo(() => leadsCachedTotal(leadCountArgsFor(false)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [landedSearch, serverAdvFilters, entrySort, entryPeriod, leadWorked, leadCountTick])
  // Client-lead count (0462). Reported UP by the feed after it fetches, exactly like
  // the Leads row's own rule (line ~2884): a category row may show only its OWN feed's
  // total for the SAME query, never a second count that could disagree with what
  // clicking the row shows. null until that feed has run — the tree renders null as no
  // number, which is the honest face of "not arrived yet".
  // ⚠️ `capped` rides along because the RPC counts through a 300 ceiling. A saturated
  // count must render "300+", never "300" — a number that is not a total may not be
  // drawn as one (the 2026-08-05 `92 → 162 → 3141` rule).
  const [clientLeadCount, setClientLeadCount] = useState(null)
  const [clientLeadCapped, setClientLeadCapped] = useState(false)
  // ── The panel's filters, translated into the demand vocabulary ─────────────
  // The shared filter language is the whole reason client leads live in this pool —
  // one query, both sides of the market. But the panel speaks SUPPLY, so three of
  // the mappings are lossy and each is capped rather than guessed:
  const clientLeadFilters = useMemo(() => {
    const f = serverAdvFilters
    // Multi-select in, single value out. With two intents or kinds chosen there is no
    // honest single answer, so the filter is DROPPED rather than silently narrowed to
    // whichever happened to be first — an unasked-for narrowing is the failure that
    // hides leads without saying so.
    const one = arr => (Array.isArray(arr) && arr.length === 1 ? arr[0] : null)
    // roomsSet is a SET ({1,3}); the RPC takes a range. min..max of the set is the
    // tightest honest envelope — it over-includes (2 slips in) rather than dropping a
    // client who asked for exactly what we hold. Wrong in the safe direction.
    const rooms = (f?.roomsSet || []).map(Number).filter(Number.isFinite)
    // ⚠️ PRICE IS USD-ONLY, deliberately. The panel's bounds are in `f.currency`, the
    // RPC's parameters are USD (`price_*_usd`). Passing a GEL number as USD would
    // filter at ~2.7× the intended budget and look entirely plausible on screen. Until
    // an fx conversion is wired here, a non-USD bound is IGNORED, not converted by
    // guess — the feed then shows more than asked, which an agent can see, instead of
    // fewer, which they cannot.
    // DF04 (Desk filters audit 2026-10-28): the conversion IS wired now — the Desk's own live
    // fxRate (GEL per USD), the same one the currency switch converts with. No rate → still ignored,
    // and the feed's notice names the budget as not applied.
    const cur = f?.currency || 'USD', rate = Number(fxRate)
    const toUsd = cur === 'USD' ? 1 : cur === 'GEL' && rate > 0 ? 1 / rate : null
    const numOr = v => (v === '' || v == null || !isFinite(+v) ? null : +v)
    const usdOr = v => (toUsd == null || numOr(v) == null ? null : Math.round(numOr(v) * toUsd))
    return {
      // the client's word, not the listing's: a requirement stores 'buy', never 'sale' (CLIENT_INTENT_OF)
      intent: one(f?.intents) ? (CLIENT_INTENT_OF[one(f.intents)] || one(f.intents)) : null,
      kind: one(f?.kinds),
      districtCodes: (f?.districtCodes || []).length ? f.districtCodes : null,
      priceMinUsd: usdOr(f?.priceMin),
      priceMaxUsd: usdOr(f?.priceMax),
      roomsMin: rooms.length ? Math.min(...rooms) : null,
      // 5 is the "5+" pill — open-ended, so it sets no upper bound (DF04, 2026-10-28; it used to cap at 5)
      roomsMax: rooms.length && !rooms.includes(5) ? Math.max(...rooms) : null,
    }
  }, [serverAdvFilters, fxRate])
  const leadCountArchive = useMemo(() => leadsCachedTotal(leadCountArgsFor(true)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [landedSearch, serverAdvFilters, entrySort, entryPeriod, leadWorked, leadCountTick])  // review 2026-09-23: +leadWorked (key reads mine:)
  // 0229 (B205): facet numbers for the LEADS view, off the same snapshot as the totals
  // above — same key, same tick, null until this exact query has been fetched.
  const leadSearchMeta = useMemo(() => leadsCachedSearchMeta(leadCountArgsFor(leadArchive)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [landedSearch, serverAdvFilters, entrySort, entryPeriod, leadArchive, leadWorked, leadCountTick])  // review 2026-09-23: +leadWorked
  // ── WHICH ROWS THE SEARCH PANEL DOES ITS ARITHMETIC ON ───────────────────────────────
  // 🚨 The panel derives the price/area histograms, the axis ceilings and the quick-select
  // buckets from the LOADED rows (nothing is hardcoded — lib/rangePresets.js). It was always
  // handed `listingEntries`, which is the OUR-WORK feed; "Leads has its own feed" (see
  // leadsCachedTotal). So on the Leads pool it received an EMPTY array and every derived number
  // silently fell back: `niceCeiling` needs 5 values and returned its hardcoded 1,000,000, the
  // histogram drew two bars, and the chips read `< $200K · $200K–$500K · $500K+` on EVERY
  // search — owner-reported on იყიდება · ბინა · თბილისი, where the honest cuts are ~85K/130K.
  // Read off the SAME page-0 snapshot the feed renders, by the SAME cache key as the total and
  // the facet counts, so the histogram under a search can only ever belong to that search.
  const leadPanelRows = useMemo(() => leadsCachedRows(leadCountArgsFor(leadArchive)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [landedSearch, serverAdvFilters, entrySort, entryPeriod, leadArchive, leadWorked, leadCountTick])  // review 2026-09-23: +leadWorked
  // Object leads only. ClientLeads rows are REQUIREMENTS, not listings — they carry no price or
  // area to distribute, so feeding them here would be a category error, and that pool's own
  // capability gate already removes these facets.
  const panelListings = entryTypeFilter[0] === 'Leads' ? (leadPanelRows || []) : listingEntries
  // Sticky per facet (owner 2026-07-28: "why don't source show counts along platform
  // names?" — they vanished the moment a portal was ticked). The server nulls a facet's
  // counts while its OWN filter is set (the narrowed corpus can't answer "what would each
  // option give me") — but the PREVIOUS response's numbers are exactly the standard
  // excluding-own-selection counts, so keep showing them: overwrite per facet only when a
  // fresh non-null arrives. This is the ae3b610 rule serverKindCounts already follows on
  // the main view — same accepted staleness window (a non-own filter change refreshes on
  // the next response; until it lands the old numbers stand), not a new convention.
  // ── 🚨 THE STICKY SLOT MUST BELONG TO ONE QUESTION (owner-reported, 2026-08-20) ─────────
  // Owner's screenshot, Leads pool, აქტიური selected: parent `ობიექტები` **30,479** — the
  // ACTIVE answer, correct — above intent children reading იყიდება 4,219 · ქირავდება 5,093 ·
  // გირავდება 93. Those are the ARCHIVE numbers. Measured against production the same day,
  // `p_lead_statuses := array['dead']`: total 9,409, sale 4,283, rent 5,098, **girao 93** —
  // the last one exact, the other two off only by the corpus moving between the screenshot
  // and the measurement. The ACTIVE answer is sale 19,537 · rent 11,122 · girao 155, which is
  // nothing like what was on screen. Two questions in one tree, for the third time.
  //
  // The cache underneath is NOT at fault: `leadsCachedSearchMeta` keys on the full signature
  // (archive included) and returns null for a question it has not fetched — deliberately, so
  // "a count from a different filter set is a lie with digits in it". The leak is HERE, in
  // the `?? prev.X` fallback below. `leadFacetCountsRef` was ONE slot shared by BOTH archive
  // states, so the moment the current question's meta was null — which is the normal state
  // right after toggling აქტიური ⇄ არქივი, and the permanent state if that query never lands
  // — the anti-flicker rule served the OTHER question's numbers instead of none.
  // `leadPrefetchOther` makes that easy to reach: hovering the არქივი row fetches the archive
  // answer, so `prev` is primed with it while the agent never leaves აქტიური.
  //
  // Keying the slot preserves the anti-flicker behaviour the comment above earns — a non-own
  // filter change still shows the previous numbers while the next response is in flight —
  // and removes only the case where "previous" meant a DIFFERENT question. When the question
  // changes, the fallback is `null`, which `row()` renders as NO NUMBER: a blank child under a
  // correct parent, never a confident wrong one. Same remedy as the two 2026-08-19 fixes on
  // this tree (`propertyTotal` following the children's chooser, and `?? kindCountsForTree`
  // becoming `?? null`) — both replaced a cross-question fallback rather than adding a guard.
  // ⚠️ THE KEY IS NOT THE WHOLE SIGNATURE, AND THAT IS THE POINT. Keying on all of
  // `leadCountArgs` would break the very rule this stickiness exists to serve: the server
  // nulls a facet's counts while its OWN filter is set, so ticking a source or a district
  // MUST keep showing the previous response's numbers — and every one of those ticks changes
  // `advFilters`. A full-signature key would blank the numbers on exactly the interaction the
  // 2026-07-28 owner report was about ("why don't source show counts along platform names?").
  // So the key carries only the axes that make it a DIFFERENT QUESTION rather than a narrower
  // one: which lifecycle bucket (`archive`), whose leads (`mine`), and over what period
  // (`since`). Filters, text and sort deliberately stay out — a narrower answer to the same
  // question is what the staleness window is for; the other bucket's answer never was.
  // ✅ REPLACED 2026-09-02 by the desk-filter-combination audit. The reasoning in the block above
  // is CORRECT about why a self-facet must keep its previous numbers, and this preserves it. What
  // was wrong is the KEY: `leadCountKey` carried only archive/mine/since and deliberately left
  // every filter out, so "previous" survived changes that had nothing to do with the facet being
  // reused. Measured on production and backed by `search_exec_log` (probe L03):
  //
  //   rent                        result 8,470 · Korter facet 2,037
  //   rent + Korter + Vake        result    80 · Korter facet STILL 2,037
  //   clean reload to rent + Vake result   470 · Korter facet 144
  //   rent + Vake, then Korter    identical URL and result 80 · Korter 144
  //
  // Same final state, same RPC answer, two different sidebars. Six facets behaved this way, and
  // Properties did NOT — it clears its scoped metro state, which is why the same audit classified
  // Properties metro correct and Leads metro defective. One response, two clients, two readings.
  //
  // 🔑 THE RULE, now in `facetOwnership.js` where it is testable: each facet's snapshot is keyed
  // by the full canonical state MINUS THAT FACET'S OWN KEYS. Ticking a source leaves the source
  // signature unchanged (anti-flicker kept); ticking a district changes it (stale number gone).
  // ⚠️ The archive/mine/since axes survive into every signature — the 2026-08-20 owner report
  // about archive numbers under an active parent is real and must not come back.
  const leadFacetSig = useMemo(() => {
    const canon = canonicalFilters(advFilters)
    // F-067 (2026-09-06): the since axis is the PERIOD LABEL, not the instant. entrySince is now-30d computed at load,
    // so with the instant in the key no two page loads ever asked the same question and the durable seed never matched
    // (measured: nine lfc: keys, none reusable across a refresh). '30d' a minute later is the same question.
    const axes = { archive: leadArchive, mine: leadWorked, since: entryPeriod || '' }
    return (family) => facetSignature(canon, axes, family)
  }, [advFilters, leadArchive, leadWorked, entryPeriod])

  const leadFacetCountsRef = useRef({})
  const leadFacetCounts = useMemo(() => {
    const prev = leadFacetCountsRef.current
    // F-067 (owner, 2026-09-06): a refresh with a facet's own bound set must still show that facet's numbers — the
    // last unconstrained answer is durable (desk cache, keyed by the signature that excludes the facet's own keys).
    // 2026-10-22: keyed by the viewer and the Leads pool, and trusted for 15 minutes only (facetOwnership.js).
    const viewer = `${session?.user?.id || myAgentId || ''}:leads`
    const pick = (family, fresh) => resolveFacetDurable(family, fresh, prev[family], leadFacetSig(family), readDeskCache, writeDeskCache, { viewer })
    const held = {
      kinds:          pick('kinds',          leadSearchMeta?.kind_counts),
      intents:        pick('intents',        leadSearchMeta?.intent_counts),
      sources:        pick('sources',        leadSearchMeta?.source_counts),
      districts:      pick('districts',      leadSearchMeta?.district_counts),
      districtsOwn:   pick('districtsOwn',   leadSearchMeta?.district_own_counts),
      // ⚠️ streets owns streetIds but NOT districtCodes — street counts are computed WITHIN the
      // chosen district, so a district change is a different universe and the snapshot must go.
      streets:        pick('streets',        leadSearchMeta?.street_counts),
      streetCoverage: pick('streetCoverage', leadSearchMeta?.street_coverage),
      metros:         pick('metros',         leadSearchMeta?.metro_counts),
    }
    // Ranges are keyed PER FACET, not as one map: the server withholds one histogram at a time,
    // so a whole-object fallback never fires and the old merge kept a price histogram across a
    // district change.
    const ranges = resolveRanges(leadSearchMeta?.range_facets, prev.ranges, leadFacetSig)
    leadFacetCountsRef.current = { ...held, ranges }
    return {
      kinds: held.kinds.value,
      intents: held.intents.value,
      sources: held.sources.value,
      districts: held.districts.value,
      districtsOwn: held.districtsOwn.value,
      streets: held.streets.value,
      streetCoverage: held.streetCoverage.value,
      metros: held.metros.value,
      ranges: Object.fromEntries(Object.entries(ranges)
        .filter(([, r]) => r.value != null).map(([k, r]) => [k, r.value])),
    }
  }, [leadSearchMeta, leadFacetSig])
  // 0687 (leads): the materialised leads pager carries the facet COUNTS but has no price or area
  // in it at all, so on the bare pool the panel's histograms fell back to the loaded page -- bars
  // summing to 103 rows against 30,319 clusters. One background request per query key fills them;
  // it no-ops when a committed filter already answered on the feed's own response. See
  // warmLeadSearchMeta for why the hot path is deliberately left alone.
  useEffect(() => {
    if (entryTypeFilter[0] !== 'Leads') return
    // 🚨 THE WARM WAITS FOR PAGE 0 TO LAND (crashes #15 and #16, 2026-09-02/03).
    // The delay below was the right idea with the wrong instrument. A 1,200 ms timer is a GUESS
    // about when page 0 finishes, and on a cold shell -- a fresh dev server, a hard reload, a
    // slow connection -- page 0 is still in flight at 1,200 ms, so the warm launched straight
    // into it: exactly the race the comment says it exists to avoid. Measured in
    // search_exec_log during crash #15, two warms overlapped at 19.6 s and 18.5 s.
    //
    // `leadSearchMeta` is null until THIS EXACT QUERY has been fetched (its own declaration says
    // so), which makes it the real signal rather than a proxy for it. Gating on it means the
    // warm cannot race the request the agent is actually waiting on, on any connection speed --
    // a fact beats a timer.
    //
    // ⚠️ It also means a page 0 that never lands never warms. That is the correct failure:
    // the warm is a background nicety for the histograms, and buying a full corpus scan on
    // behalf of a view that failed to load is the opposite of what these two crashes asked for.
    //
    // 📌 This is the CLIENT half. The server half (single-flight) collapses identical
    // in-flight warms from many tabs or reloads into one execution. Neither replaces the other:
    // this stops one client racing itself, single-flight stops many clients multiplying.
    if (!leadSearchMeta) return
    let live = true
    // A short delay still, so the warm lands after the page-0 render settles rather than
    // immediately behind its response. Much smaller than 1,200 ms because it is no longer
    // carrying the job of guessing when page 0 is done.
    const timer = setTimeout(() => {
      warmLeadSearchMeta(leadCountArgsFor(leadArchive))
        .then(got => { if (got && live) setLeadCountTick(t => t + 1) })
    }, 300)
    return () => { live = false; clearTimeout(timer) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entryTypeFilter, leadArchive, leadWorked, landedSearch, serverAdvFilters, entrySort, entryPeriod, leadSearchMeta])
  const [serverUnknown, setServerUnknown] = useState(null)          // RPC's "hidden for missing data" count
  // The reduction denominator, PER QUESTION (2026-10-22): baselineKey(view axes, city) → the total with
  // no user filter. One global "last unfiltered total" compared a Tbilisi search with an all-cities,
  // other-period number and was never reset ("listings −49%").
  const [listingBaselines, setListingBaselines] = useState({})
  const noteBaseline = (k, total) => { if (k && Number.isFinite(total) && total > 0) setListingBaselines(b => (b[k] === total ? b : { ...b, [k]: total })) }
  // The filters the feed's landed server-filtered answer was ASKED with (null = an unfiltered landing).
  // With the rows' server_vetted stamp this is what lets feedPipeline trust the server's verdict.
  const [landedServerFilters, setLandedServerFilters] = useState(null)
  // The unknown-verdict tier under active filters (0055) — data too coarse/missing to confirm
  // or deny (e.g. property coded TBS_SAMGORI under a ვარკეთილი filter). Rendered as "possible
  // matches" BELOW exact matches — surfaced, never mixed in, never silently hidden.
  const [listingPossibles, setListingPossibles] = useState([])
  // ── The THIRD verdict tier: near the selected street, but not on it (Q4, 0541/0543) ────
  // A property 340m away on Mindeli genuinely FAILS the street check — its address names a
  // different street — so it is a `mismatch` and belongs in neither list above. That is
  // correct, and it is also why "what else is around here" had no answer at all until now.
  // ⚠️ SEPARATE FROM `listingPossibles` ON PURPOSE. Merging them would put the ~700m
  // proximity answer back into the pile the street id was built to pull it out of — the bug
  // that returned Kavtaradze for Politkovskaya. Three verdicts, three lists.
  // ⚠️ DECLARED HERE, beside the state it belongs with, and NOT next to its render site.
  // Both production crashes on this file (2026-08-12) were reads of a binding declared
  // further down — `vite build` and 758 unit tests pass on those; only rendering catches it.
  const [listingNearby, setListingNearby] = useState([])
  // cluster_key → { label, m }: the street the row is nearest to and its rounded distance
  // FROM THAT STREET'S CENTRE. `street.geo` is a single ST_Point on every row, so the copy
  // must keep saying "from the street's centre" — drop that phrase and this shelf quietly
  // becomes the old radius bug wearing a heading.
  const [streetNearbyMeta, setStreetNearbyMeta] = useState(null)
  // ── Shelf paging (task #11, 2026-07-29) ────────────────────────────────────────────────
  // The shelf has its OWN cursor. It is a second result set with its own ordering (v17's
  // shelf rank: owners who have answered before, first), not a continuation of the matched
  // page, so it cannot ride `offset`.
  //
  // ⚠️ CLUSTER units, not rows — the same unit `serverUnknown` counts in. Advancing by
  // `possibleListings.length` would skip whole pages the moment one cluster carries two
  // listings, and it would do so silently.
  //
  // Why this became urgent: the shelf used to hold ~60 under a restriction lens and the
  // fixed cap was invisible. The 0249 date lens shelves 14,349 clusters on `posted` alone
  // (87% of the corpus has no posting date), so "showing 60 of 14,349" with no way forward
  // is the header telling the truth about a screen that cannot act on it.
  const [possiblesOffset, setPossiblesOffset] = useState(0)
  const [possiblesLoading, setPossiblesLoading] = useState(false)
  // The shelf's FIRST page failed (2026-10-23 audit, Desk §5). Before, the failure went to the console
  // only, after the one pending arm had been consumed, so a counted unknown tier had no cards and no
  // way to ask again. The arm is put back and the shelf offers a retry at offset 0.
  const [possiblesFailed, setPossiblesFailed] = useState(false)
  // A page that returns nothing means the cursor is spent even when the count says more
  // (the same drift `chainExhaustedRef` guards on the main chain). Without it the button
  // re-fires the same empty offset forever.
  const possiblesDoneRef = useRef(false)
  const possiblesBusyRef = useRef(false)   // state flag can't close the double-click window
  // ── THE SHELF IS FETCHED WHEN IT CAN BE SEEN (2026-09-24) ───────────────────────────────
  // pendingShelfRef: the {filters, gen} of the snapshot whose shelf has not been asked for yet —
  // armed by the page-0 landing (armShelf), consumed once. shelfWanted: its place on the page
  // (the sentinel under the matched grid) came within reach of the viewport, or the amber
  // "show anyway" line was pressed. The shelf request re-runs the whole search on the DB, so
  // asking only when someone can see the answer is what makes a filtered click ONE search.
  const pendingShelfRef = useRef(null)
  const [shelfWanted, setShelfWanted] = useState(false)
  const [shelfArmTick, setShelfArmTick] = useState(0)   // re-runs the consume effect when a new shelf is armed
  // Local-filter mode (2026-07-11): when the COMPLETE unfiltered set is already loaded
  // (rawCount ≥ total, and entries weren't replaced by a server-filtered fetch), a filter
  // change skips the server round-trip entirely — the client classify in visibleResult is
  // the filter. This does NOT reintroduce the pre-0048 bug ("client filtering missed rows
  // on unfetched pages"): local mode is gated on completeness, partial snapshots still go
  // through the RPC. The flag also flips advDisplay/possibles to their client-side sources.
  // Since the 2026-07-15 egress diet removed chain-to-complete, completeness only happens
  // when the whole feed fits in one page (total ≤ LISTING_PAGE) — bigger feeds always
  // filter server-side. That's intentional; don't re-add a prefetch chain to widen it.
  const [localFilterMode, setLocalFilterMode] = useState(false)
  // Mirror-skip (blueprint §6, verified plan): the q the last LANDED response was text-
  // filtered by, or null. While the live residual equals it, the client's raw `.includes()`
  // text mirror is SKIPPED for listing-backed groups — the server already vetted them with
  // the NORMALIZED check (0106/0108), and the raw mirror would kill cross-alphabet passes
  // (q=vake matching a Georgian-only address is exactly the case this ships for). Cleared
  // at the start of every fetch effect run, so the in-flight window (previous, possibly
  // unfiltered entries still rendered while the filtered fetch runs) keeps the mirror ON —
  // equivalent to per-row provenance, cheaper. Stored as the STRING (not the blueprint's
  // boolean) so typing PAST the committed query re-engages local narrowing naturally.
  const [landedTextQ, setLandedTextQ] = useState(null)
  // Which scope/agent/period the in-memory snapshot belongs to, or null if entries were
  // replaced by a server-filtered fetch. Local mode requires an exact match with the CURRENT
  // key — otherwise widening the period under an active filter would "locally filter" a
  // complete-looking snapshot that only covers the old, narrower period.
  const snapshotKeyRef = useRef(null)
  // Request identity for the listing fetch (SEARCH_ARCHITECTURE §17, "supersede,
  // mechanically"). AbortController alone is NOT enough: it cancels an in-flight request,
  // but a response already on the wire when abort() fires still resolves, and its .then
  // still runs — so search A could repaint the screen after search B had replaced it. Every
  // handler compares its own run id against this ref and returns if it has been superseded.
  // Cheap belt to the abort's braces; the failure it prevents is the hardest kind to
  // reproduce, because it only shows up when two searches race and the slower one wins.
  const searchRunRef = useRef(0)
  // The offset cursor is only trustworthy after the CURRENT load effect's own fetch has
  // landed. A cached paint also sets rawCount, but chaining prefetches from that cursor
  // races the revalidate: append (counts grow) → revalidate replaces with chunk 1 (counts
  // drop) → chain re-grows — the feed's "shown N / M" visibly flip-flopped (2026-07-11).
  const [cursorFresh, setCursorFresh] = useState(false)
  // ⚠️ B884 (owner, 2026-08-20: *"filtered by price and the page kept updating for minutes,
  // adding new findings"*). The STATE above cannot gate the prefetch chain, and this ref is
  // why. Both effects run in the SAME commit and the load effect is declared first, so when it
  // calls `setCursorFresh(false)` the chain effect further down still reads the previous
  // render's `true` — React has not re-rendered yet. Measured on the leads pool, one price
  // filter, no navigation: page 0 aborted at +0.0s, and a load-more for offset 30 STARTED at
  // +1.2s — before the replacement page 0 had even launched (+2.0s). Its 3,514 ms of server
  // work was then discarded by the `gen !== loadGenRef.current` guard, which is the safety net
  // working exactly as designed and hiding the cost.
  // The mirror is written SYNCHRONOUSLY at both `setCursorFresh` sites and is what the chain
  // reads. Same idiom as `loadGenRef` / `chainExhaustedRef` / `loadMoreBusyRef` right beside
  // it — every other cross-effect signal in this file is already a ref, for this same reason.
  const cursorFreshRef = useRef(false)
  // The offset the chain has already ASKED for in this generation. `listingRawCount` is state
  // too, so a second chain tick could fire before `setListingRawCount` landed and re-request
  // the offset still in flight — measured as offset 60 fetched twice (2,877 ms then 79 ms) in
  // the same trace. Reset per generation by the load effect, alongside `loadGenRef`.
  const chainOffsetRef = useRef(-1)
  // Every listing_id seen in THIS generation's fetches (chunk 1 + chained load-mores).
  // When the chain completes, rows NOT in this set are purged: they came from the merged
  // cached tail but no longer exist on the server (delisted / aged out of the period) —
  // without the purge, dead cards would accumulate in the cache forever (the exact
  // "silently accumulating dead cards" trap the delta-endpoint spec warned about).
  const fetchedIdsRef = useRef(new Set())

  // ── Search-box → filter compiler (src/lib/searchCompiler.js) ────────────────
  // Typed query compiles into filter tokens ("2 room vake under 200k" → rooms/district/
  // price chips); recognized tokens write the SAME advFilters the panel edits (one
  // constraint system, two doors — and they execute server-side like everything else).
  // What doesn't compile (residual) stays a plain text search. Chips are removable:
  // a dismissal is remembered per token signature until the box is cleared, and logged —
  // a removed chip is the compiler's most valuable failure signal (a wrong guess).
  // searchCompiler v39 (owner 2026-10-31): a typed price above every rent in the market is a sale price. The ceiling is the
  // market's own — price-sense's rent 99.5th percentile (USD, every kind but commercial) — and the typed amount is turned
  // into USD with the admin-set rates (config.fx_to_gel). ⚠️ No number of our own (owner: "it must be changed by data"):
  // until price-sense answers, isSalePrice is null and the box infers nothing.
  const [rentCeilingUsd, setRentCeilingUsd] = useState(null)
  useEffect(() => {
    let live = true
    getPriceSense().then(d => {
      const hi = Number((d?.ranges || []).find(r => r.deal === 'rent' && r.kind === '_all')?.hi)
      if (live && hi > 0) setRentCeilingUsd(hi)
    }).catch(e => console.warn('[desk] price sense unavailable — typed prices set no deal:', e?.message))
    return () => { live = false }
  }, [])
  const isSalePrice = useMemo(() => rentCeilingUsd == null ? null
    : (amount, currency) => toGel(amount, currency || advFilters.currency || 'USD', config?.fx_to_gel) / toGel(1, 'USD', config?.fx_to_gel) > rentCeilingUsd,
    [rentCeilingUsd, advFilters.currency, config?.fx_to_gel])
  const compiledQuery = useMemo(() => compileQuery(entrySearch, { districts, locations: geoLocations, features, conditionCodes: ladderCodes, isSalePrice }), [entrySearch, districts, geoLocations, features, ladderCodes, isSalePrice])
  const [dismissedSigs, setDismissedSigs] = useState(() => new Set())
  // The text commitSearch LANDED after folding it itself (review 2026-09-23). Whatever
  // tokens it still contains were left there on purpose — dismissed chips, and suggestion
  // clicks' ignoreSigs noise — so the landed-text fold effect below must not re-fold them.
  // Null when the vocabularies were not loaded at commit time: then nothing was folded yet
  // and the effect is exactly what must finish the job once they arrive.
  const commitLandedRef = useRef(null)
  const [searchDebug, setSearchDebug] = useState(false)   // double-click the 🔍 icon to toggle
  const activeTokens = useMemo(() => compiledQuery.tokens.filter(t => !dismissedSigs.has(t.sig)), [compiledQuery, dismissedSigs])
  // LANDED twins of the three live values above (see the landedSearch block for the ruling).
  // Same compiler, same dismissal filter — the ONLY difference is which text they read, so
  // the landed feed can never disagree with what Enter would have produced. Dismissing a
  // chip re-filters landedTokens without a recompile, so chip removal still acts instantly.
  const landedCompiled = useMemo(() => compileQuery(landedSearch, { districts, locations: geoLocations, features, conditionCodes: ladderCodes, isSalePrice }), [landedSearch, districts, geoLocations, features, ladderCodes, isSalePrice])
  const landedTokens = useMemo(() => landedCompiled.tokens.filter(t => !dismissedSigs.has(t.sig)), [landedCompiled, dismissedSigs])
  // Clearing the box clears the landed search without requiring Enter — deleting your query
  // and watching a filtered feed stay filtered would read as broken (same shape as
  // committedQ's own auto-clear below).
  useEffect(() => { if (!entrySearch.trim() && landedSearch) setLandedSearch('') }, [entrySearch, landedSearch])
  // District completion hints for the LAST partial word — hidden once that word already
  // compiled to a district chip (no point suggesting what's already recognized).
  const searchHints = useMemo(() => {
    const recognized = new Set(activeTokens.filter(t => t.type === 'district').map(t => t.value?.code))
    return suggestDistricts(entrySearch, { districts }).filter(h => !recognized.has(h.code))
  }, [entrySearch, districts, activeTokens])
  // A hint accept is a COMMIT (§5.3: "suggestion click" commit source) — computed on the
  // new string directly, since compiledQuery won't have re-run until the next render.
  const acceptHint = h => {
    const ns = entrySearch.replace(/[\p{L}\p{N}.]+$/u, h.insert + ' ')
    setEntrySearch(ns)
    commitSearch(ns, 'suggestion')
    closeSuggest()   // a hint accept IS a commit → the suggest surface goes away, both form factors
  }
  // listing_id tokens (engine v5): a pasted myhome/ss/korter link or bare platform id.
  // These filter by IDENTITY (source_listing_id / source_url), not via advFilters — the
  // empty patch means dismissing the chip changes no panel field, so visibleResult must
  // depend on listingIdKey explicitly (the advFilters dep won't fire for it).
  // ⚠️ landedTokens, NOT activeTokens — all three identity derivations here. On live
  // tokens, the reach-back effect keyed on these fired a ~2.9s DB-wide lookup on PARTIAL
  // phone numbers, once per digit from the first keystroke that compiled. An identity
  // answer to half a phone number is wrong twice over: it costs a server round-trip per
  // keystroke, and whatever it finds is for a number the agent hasn't finished typing.
  const listingIdQueries = useMemo(() => landedTokens.filter(t => t.type === 'listing_id').map(t => t.value.id), [landedTokens])
  const listingIdKey = listingIdQueries.join(',')
  // phone tokens (engine v8): identity search like listing_id — resolved DB-wide through
  // /listings/search's phone-suffix match (party phones, international-tolerant), merged in
  // by the same reach-back effect. Feed rows carry NO phone (C1 click-to-reveal), so the
  // loaded-row filter matches via the reach-back's found-id set + raw intake text.
  const phoneQueries = useMemo(() => landedTokens.filter(t => t.type === 'phone').map(t => t.value.digits), [landedTokens])
  const phoneKey = phoneQueries.join(',')
  // B650 (owner 2026-08-12): a cadastral code is a parcel's legal NAME, so it is an identity
  // lookup and rides the SAME reach-back as a pasted listing id or a phone — DB-wide, expanded
  // into the whole lineage family. The server owns the prefix rule; nothing here needs to know it.
  const cadastralQueries = useMemo(() => landedTokens.filter(t => t.type === 'cadastral').map(t => t.value.code), [landedTokens])
  const cadastralKey = cadastralQueries.join(',')
  // near_location tokens (engine v7): "delisi metro" / "m2 kazbegi". Distance filter in
  // visibleResult (same empty-patch contract as listing_id) — strict when the row has a
  // precise pin, weak district fallback for pinless rows ("go b", 2026-07-16).
  // ⚠️ Desk filters audit 2026-10-28 DF02/DF03: the local mirror judges ONLY the committed point,
  // at the committed radius. It used to AND every landed near token at its kind's default
  // radius, so (a) widening the radius panel to 1 km still cut cards at the metro's 300 m and
  // (b) "მეტრო დელისი მეტრო ისანი" was paged/counted by the server for Delisi alone and then
  // intersected with Isani here. The second landmark is not applied anywhere now, and the
  // unapplied-words notice names it instead of pretending it narrowed anything.
  const committedNear = advFilters.near
  const nearLocationQueries = useMemo(() => {
    const tok = committedNear && landedTokens.find(t => t.type === 'near_location' && t.value.id === committedNear.id)
    return tok ? [{ ...tok.value, radiusM: committedNear.radiusM }] : []
  }, [landedTokens, committedNear])
  const nearLocationKey = nearLocationQueries.map(q => `${q.id}@${q.radiusM}`).join(',')
  // ⚠️ DF10 (Desk filters audit 2026-10-28): the "not used as a filter" notice listed ALL landed text,
  // but the committed near word and identity tokens (listing id / phone / cadastral) keep their text
  // on purpose AND filter — "მეტრო დელისი" was labelled unused beside its own radius chip. Only the
  // rest is unapplied (a second landmark included, see DF03 above).
  const unappliedWords = useMemo(() => {
    const used = landedTokens.filter(tk => tk.start != null && (
      ['listing_id', 'phone', 'cadastral'].includes(tk.type)
      || (tk.type === 'near_location' && nearLocationQueries.some(q => q.id === tk.value?.id))))
    return boxTextAfterFold(landedSearch, used.map(tk => ({ ...tk, patch: { used: 1 } }))).trim()
  }, [landedSearch, landedTokens, nearLocationQueries])
  // ── B209 (owner report 2026-07-28: a real lead id found nothing in Leads) ───────────
  // An IDENTITY search ("where is listing 32924180", "whose phone is this") is not a browse
  // question and is not answered by any category's own feed: it is resolved DB-wide by the
  // reach-back effect below (/listings/search → lineage-family expansion) and merged into
  // the ENTRIES pipeline. The special feeds REPLACE that pipeline, and their fetches carry
  // no id parameter — so the id reached the server as nothing at all, and because no
  // predicate was sent the feed rendered EVERYTHING. Not merely a miss: an unfiltered page
  // reads as an answer, so the agent concludes the listing was never captured.
  // Fix at the single point that means "this category replaces the entries pipeline":
  // during an identity search it does not. Every SPECIAL_FEED gate below (the fetch, the
  // overlay, the zero/busy/fault states, the grid) then does the right thing for free,
  // including the honest "searching…" and "not captured" states the property surface
  // already has — which is the fail-CLOSED behaviour rule 2.3 asks for.
  // Image search is deliberately absent: runImageSearch already snaps the category out of
  // every special feed before its results land, so it cannot reach this state.
  const identitySearchActive = listingIdQueries.length > 0 || phoneQueries.length > 0 || cadastralQueries.length > 0
  // 'ClientLeads' (0462/0463, owner ruling 2026-08-06): unclaimed client DEMAND, a
  // second type inside the Leads pool. It is a SPECIAL category for the same reason
  // Business is — a requirement has no address, photo or coordinates, so it cannot
  // ride the property card pipeline; it owns its rows and renders its own feed.
  const SPECIAL_CATEGORIES = ['Meeting', 'Business', 'Leads', 'ClientLeads', 'Favorites', 'Archived']
  const SPECIAL_FEED = !identitySearchActive && entryTypeFilter.length > 0 && SPECIAL_CATEGORIES.includes(entryTypeFilter[0])
  // What THIS category's feed can honour (FEED_SORTS / FEED_PERIOD). An identity search
  // hands the pipeline back to the property feed, so the capability is the full set again.
  const feedCat = identitySearchActive ? null : entryTypeFilter[0]
  const sortAllow = feedCat && feedCat in FEED_SORTS ? FEED_SORTS[feedCat] : undefined
  const showSort = sortAllow !== null
  const showPeriod = !feedCat || FEED_PERIOD[feedCat] !== false
  // Withholding the option is only half the fix. The other half: arriving on a feed while
  // ALREADY sorted by something it cannot execute must not leave that state running
  // invisibly, with the pill falling back to the first option's label while the feed obeys
  // something else. Fall back to the default the moment the current sort is unsupported.
  useEffect(() => {
    if (sortAllow && !sortAllow.has(entrySort)) setEntrySort('activity')
  }, [sortAllow, entrySort])
  // Arriving on Leads on the untouched default switches it to Best Match; leaving Leads is handled by the
  // effect above (no other feed offers best_match, so it falls back to 'activity').
  useEffect(() => {
    if (feedCat === 'Leads' && entrySort === 'activity' && !sortPickedRef.current) setEntrySort('best_match')
  }, [feedCat, entrySort])
  // …and the CATEGORY has to move with it. Handing the entries pipeline back the query is
  // only half the fix: it then classifies against a category none of the results can be in
  // ('Leads' is not a listing category — it is a feed), so the honest answer was replaced
  // by a confident "Showing 0". Verified live: listing 32924180 exists in core and still
  // rendered 0 until this snapped the category to All.
  // This is the SAME rule runImageSearch already applies for the same reason — an identity
  // result is listing-shaped and no special feed can render it. Effect (not a render-time
  // mutation) so it fires once per identity search rather than every keystroke.
  useEffect(() => {
    if (!identitySearchActive) return
    setEntryTypeFilter(cur => (cur.length && SPECIAL_CATEGORIES.includes(cur[0])) ? [] : cur)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identitySearchActive])
  // COMMIT-TO-SEARCH (blueprint §5.3, user product decision 2026-07-17): typing NEVER fires
  // the server text search. The SERVER q updates only on COMMIT: Enter, or a
  // completion-hint accept (the step-8 suggest dropdown adds more commit sources later).
  // ⚠️ SUPERSEDED IN PART, owner ruling 2026-08-05: the second half of the original rule —
  // "the live residual keeps filtering INSTANTLY over loaded rows, kept deliberately" — is
  // REVERSED. The feed (visibleResult, identity reach-back, chips) now reads the LANDED
  // search (see the landedSearch block), so typing touches nothing render-expensive; live
  // feedback lives in the suggest dropdown + searchPreview instead. Do not restore live
  // narrowing from this comment's earlier form or from a stale doc — the owner's words:
  // "writing in the search box must feel like normal text writing".
  // 3-char rule became a commit-time validation: a shorter residual commits as '' (local
  // filtering still narrows; the server just isn't asked). committedQ auto-clears when the
  // live residual empties (box cleared / everything compiled into chips) — otherwise a
  // stale server text filter would keep constraining a visibly empty box.
  const [committedQ, setCommittedQ] = useState('')
  // Search telemetry (step 6, blueprint §13) — mutable scratch the 2.5s query-log effect
  // reads at fire time. Refs, not state: none of this should cause renders, and the log
  // effect deliberately reads whatever is CURRENT when it fires, not a stale closure.
  const searchTelemetryRef = useRef({
    rpcMs: null,          // last filtered /agent-listings round-trip, ms
    visibleCount: 0,      // visibleGroups.length after the latest re-filter
    commit: null,         // { source: 'enter'|'suggestion', q } — last commit action
    lastZero: null,       // { raw, at } — last zero-result render (feeds refined_from/abandoned)
  })
  // ── 🔗 A pasted client link is a DESTINATION, not a query ───────────────────
  // "I sent the client a link, they replied 'I like this one' — which one was
  // it?" Until now there was no way back: every reader of property_share is
  // property-scoped, so the sent-links list only appears once you have already
  // found the property, which is the thing you cannot do.
  //
  // ⚠️ DELIBERATELY NOT A RECOGNIZER in searchCompiler.js, unlike the myhome/ss
  // /korter URLs that recognizeListingRef claims. Those compile to a `listing_id`
  // token that reaches the feed as an identity SEARCH — rows, filters, a URL you
  // can share. A share token is not a description of anything: it names exactly
  // one property, it matches no text, and there is nothing to filter on. Making
  // it a token would put a bearer credential into the search state, the URL bar,
  // the recent-searches list and the search_query_log — four places it must not
  // be. Same shape as the URL deep link and the image search, which also leave
  // the box without committing.
  //
  // Resolution is server-side (see resolvePropertyShareLink): the browser cannot
  // read property_share at all, by design (0280).
  const shareStateLabel = st => ({
    revoked: t('share_revoked'), expired: t('share_expired'),
    off_market: t('desk_status_off_market'), representation_ended: t('share_representation_ended'),
  })[st] || st
  const openSharedLink = src => {
    // The token leaves the box immediately — it is not a query, and leaving it
    // there would re-commit it on the next interaction and feed it to the
    // highlight mirror as unmatched text.
    setEntrySearch('')
    setCommitMsg(null)
    resolvePropertyShareLink(src).then(r => {
      if (!r || r.found === false) { setCommitMsg(t('desk_share_lookup_none')); return }
      // A dead link still resolves, on purpose — those are the ones agents chase
      // hardest. Say which kind of dead it is, then open the property regardless.
      if (r.state && r.state !== 'active') {
        setCommitMsg(t('desk_share_lookup_inactive', { state: shareStateLabel(r.state) }))
      }
      // Prefer the card; fall back to the property workspace when the property
      // carries no listing at all. Mirrors the __isProperty branch in the card
      // click handler — one modal at a time, never a broken lineage modal.
      if (r.listing_id) openListingCard({ id: r.listing_id, isOwn: !!r.is_own_listing })
      else if (r.property_id) setPropertyCard({ propertyId: r.property_id, listingId: null })
      else setCommitMsg(t('desk_share_lookup_none'))
    }).catch(() => setCommitMsg(t('desk_share_lookup_failed')))
  }

  const commitSearch = (text, source = 'enter', opts = {}) => {
    const src = text != null ? text : entrySearch
    // Before the compiler sees it — see openSharedLink. Returns without touching
    // committedQ/advFilters/enterBrowse, so no URL write and no telemetry row.
    if (looksLikeShareLink(src)) { openSharedLink(src); return }
    const c = compileQuery(src, { districts, locations: geoLocations, features, conditionCodes: ladderCodes, isSalePrice })
    // ignoreSigs (street/client suggestion clicks): tokens the caller just pre-dismissed
    // are treated as plain text — when EVERY token is dismissed the whole phrase commits
    // (a street click must search "ილია ჭავჭავაძის გამზირი", not the gutted residual
    // "ილია გამზირი" left after the district recognizer claimed the surname).
    const toks = opts.ignoreSigs ? c.tokens.filter(tk => !opts.ignoreSigs.has(tk.sig)) : c.tokens
    // ⚠️ REVERTED B1202, 2026-09-04, within the hour: this fallback is LOAD-BEARING and my
    // reason for removing it was factually wrong. The owner asked, rightly, that resolved chips
    // should not also be searched as words — but a STREET SUGGESTION CLICK PRODUCES NO CHIP.
    // Read the caller (`suggestion` + geoNoise): it compiles the phrase, marks the DISTRICT /
    // near_location / restriction tokens as NOISE and pre-dismisses them — because
    // "ილია ჭავჭავაძის გამზირი" compiles a Chavchavadze DISTRICT chip, which is the wrong
    // district (the avenue is in Vake) and kills every listing — and then relies on the TEXT to
    // do the searching. So `ignoreSigs` means "tokens dismissed as noise", NOT "the phrase
    // became a predicate". With every token dismissed, `toks` is empty, and dropping the `src`
    // fallback left nq = '' — an empty search. Street search stopped filtering entirely.
    // The owner's rule still stands and is still worth implementing; it has to be done where a
    // chip genuinely exists (the panel's street picker writes streetIds), not here.
    // The rule lives in src/lib/searchCommit.js now, with the regression that shipped broken as
    // one of its cases. Behaviour here is IDENTICAL to before: no caller passes hasPredicate
    // yet, so this is still raw-phrase-when-everything-was-dismissed. What changed is that the
    // rule is importable, named, and covered — the reason the earlier break could not be caught
    // was that it lived in a component only a browser could reach.
    // F-102 (2026-09-10): a bare street name on Enter takes the street CHIP when the suggestion the agent
    // could already see holds exactly one street for this text. Measured first: 66 of 601 logged residuals
    // were streets the endpoint already resolves in context — agents press Enter, they do not click.
    // Narrow by design (see streetToAcceptOnEnter); anything ambiguous keeps today's text path.
    const autoStreet = streetToAcceptOnEnter({ source, residual: c.residual, liveTokens: toks.length, suggest: suggestResp, typed: src, callerHasPredicate: opts.hasPredicate === true })
    if (autoStreet) { acceptStreet(autoStreet, 0); return }
    const nq = committedTextFor({
      raw: src, residual: c.residual, liveTokens: toks.length,
      // ⚠️ The door for the owner's rule: a caller that has ALREADY applied a real predicate
      // (acceptStreet's exact-identity path sets streetIds) may drop the text entirely. It is
      // false here because those callers return before reaching commitSearch at all.
      callerHasPredicate: opts.hasPredicate === true,
    })
    searchTelemetryRef.current.commit = { source, q: nq }
    setCommittedQ(nq.length >= 3 ? nq : '')
    // FOLD the draft token overlay into the COMMITTED filters (§2.3): a commit is the one
    // act that turns what the agent was expressing into what they asked for. Same merge
    // the live draft uses (applyTokenOverlay — arrays union, panel scalars win), over the
    // tokens of THIS committed text, minus dismissed chips (a dismissed token stays text).
    // near is token-owned (no panel field writes it): each commit sets it from the first
    // near token, or clears it — so committed near always tracks the committed box, and
    // its chip below (committed-near) keeps it visible/removable after the box changes.
    const foldTokens = toks.filter(tk => !dismissedSigs.has(tk.sig))
    const nearTok = foldTokens.find(tk => tk.type === 'near_location')
    const nearVal = nearTok ? { id: nearTok.value.id, lat: nearTok.value.lat, lng: nearTok.value.lng, radiusM: NEAR_SEARCH_RADIUS_M[nearTok.value.kind] || 1000, label: nearTok.label } : null
    // ⚠️ A COMMIT CLEARS near ONLY IF THE TEXT THAT CARRIED IT STOPPED CARRYING IT (B762b).
    // The rule above — "each commit sets near from its token, or clears it" — is right for a
    // near that came from WORDS: the words are its representation, so deleting them must
    // delete the filter. It is wrong for a near that never had words. acceptPlace commits a
    // point from the dropdown and clears the box on purpose, so the agent's very next commit
    // ("2 ოთახი", Enter) found no near token and nulled a filter nobody touched — silently,
    // with its chip still on screen. Same for a near restored from a shared link or a reload.
    //
    // `landedTokens` is the PREVIOUS committed text (this render's state, not the text being
    // committed now), which answers the question exactly: a near token there and none here
    // means the agent deleted the words → clear. No near token there means the point was
    // never spelled out → it is panel-owned, and its chip ✕ is how it goes away.
    //
    // ⚠️ Deliberately DERIVED, not stored. A `picked` flag on the near object cannot survive
    // `canonicalFilters`, which reduces near to its stable identity fields on purpose — and
    // adding it there would make ORIGIN part of search identity, so a picked and a typed
    // Delisi would stop being the same search to `sameCommitted`. Origin is not identity.
    const landedHadNear = landedTokens.some(tk => tk.type === 'near_location')
    // (2026-10-22: a commit is a fold — the typed scalar replaces the committed one. A recent-search replay starts from
    // ADV_DEFAULTS: it re-runs THAT search, it does not AND it onto whatever is on screen now.)
    setAdvFilters(f => {
      const base = opts.reset ? { ...ADV_DEFAULTS, q: f.q } : f   // q: the committedQ sync owns it (it does not re-fire for the same text)
      return { ...applyTokenOverlay(base, foldTokens, { fold: true }), near: nearTok ? nearVal : (opts.reset || landedHadNear ? null : f.near) }
    })
    // A folded restriction subject leaves the BOX (owner-reported 2026-07-29, second half).
    // acceptRestriction already did this for suggestion CLICKS and recorded why: the chip is
    // the single representation of a subject, so leaving its word in the text makes it exist
    // twice, and the two cannot be edited as one. Enter (and any other commit) skipped that
    // step, which is how the reported state was reachable — type ინდოელები, press Enter,
    // then backspace: the text shrinks to "ინდოე", stops matching the stemmer, and runs as a
    // FREE-TEXT search beside the committed chip that is still filtering. Two filters, one of
    // which the agent is trying to delete.
    // ⚠️ EVERY FOLDED TOKEN, not just restrictions (owner ruling 2026-08-18: "text must be
    // gone from search, only search criterium"). The restriction-only rule above was right
    // about WHY and too narrow about WHERE: the reason a folded subject must leave the box
    // — the chip is the single representation of the constraint, so leaving its word in the
    // text makes it exist twice and the two cannot be edited as one — is not a property of
    // restrictions. It is a property of FOLDING. The owner reported the general case from
    // the general symptom: "when I write in it becomes search criteria… so I assume it
    // filters by criterium AND also text."
    //   The paragraph this replaces claimed districts and places were "the opposite case by
    // design — there the text IS the representation". That has not been true since the
    // landing ruling: `applyParsedHash` restores a search by setting the box to
    // `target.filters.q`, the RESIDUAL, so a refresh ALREADY empties the claimed words out
    // of the box and renders them as chips. Commit and restore now agree; before this they
    // put the same search on screen two different ways.
    //
    // ⚠️ ONLY tokens that actually fold — `patch` non-empty. listing_id / phone / cadastral
    // patch NOTHING (identity searches, engine v5/v8/v23): their filter IS the text, read
    // back out of `landedSearch` by listingIdQueries/phoneQueries/cadastralQueries. Strip
    // those spans and the id search stops existing. near_location keeps its words for the
    // same reason its chip is token-owned; commitSearch's own `nearVal` fold is what
    // survives once they change.
    // Dismissed tokens are already absent from foldTokens: a dismissed chip stays as text.
    // (2026-10-22: the words the compiler understood but did not apply go too — "ვაკე თბილისი" left "თბილისი", and the
    // next Enter widened the search to all of Tbilisi; "ბათუმის ცენტრი" left "ცენტრი" = central Tbilisi. boxTextAfterFold.)
    const foldSpans = foldTokens.filter(tk => tk.start != null && Object.keys(tk.patch || {}).length)
    let landedText = src
    if (foldSpans.length) {
      landedText = boxTextAfterFold(src, foldSpans, c.dropped)
      setEntrySearch(landedText)
    }
    // LAND the text the feed will filter by (owner ruling 2026-08-05 — see landedSearch).
    // The post-fold box text, not `src`: a folded restriction's constraint lives in
    // advFilters now, and landing the pre-fold text would re-express it as a token too —
    // harmless today (applyTokenOverlay unions idempotently) but it would break the
    // "landed == what the box shows" invariant that makes this state explainable.
    commitLandedRef.current = (districts || []).length && (features || []).length ? landedText : null
    setLandedSearch(landedText)
    // A commit is THE act that leaves home (see the committedSearch block). Enter on an
    // empty box is not a search — it must not kick the agent off the cockpit.
    if (src.trim()) enterBrowse()
    // Recents replay the RAW box text (not the residual) so re-running one re-compiles
    // the same chips. recordRecentSearch is declared below but commitSearch only ever
    // runs from event handlers, never during render — no TDZ risk.
    recordRecentSearch(src)
  }
  const liveResidual = (compiledQuery.tokens.length ? compiledQuery.residual : entrySearch).trim()
  useEffect(() => { if (!liveResidual && committedQ) setCommittedQ('') }, [liveResidual, committedQ])
  // §13 'abandoned': box cleared within 10s of a zero-result render — best-effort signal
  // that the agent gave up rather than reformulating (a reformulation logs refined_from
  // instead). Follow-up row, event-tagged; the log table is append-only and shape-loose.
  useEffect(() => {
    if (entrySearch.trim()) return
    const z = searchTelemetryRef.current.lastZero
    if (z && Date.now() - z.at < 10000) {
      logSearchQuery({ rawText: z.raw, compiled: [], residual: '', dismissed: [], context: { source: 'desk', event: 'abandoned' } })
      searchTelemetryRef.current.lastZero = null
    }
  }, [entrySearch])
  // Proposals feed (clients / meetings / drafts) — period-bounded by default, but an ACTIVE
  // search or filter widens it to ALL history (Option B, 2026-07-18). The frontend already
  // matches loaded clients by name (meta.title = person.display_name) and criteria (the
  // extract_requirement classify branch); the only gap was temporal — a client older than the
  // window wasn't loaded. Widening on search closes it, so a client typed by name or narrowed
  // by a criteria filter is found regardless of age — the same all-history reach a committed
  // listing search already has server-side. Payload is tiny (~200 rows) so the extra fetch is
  // practically free; stale-while-revalidate keeps it smooth. Boolean dep (not the raw filter
  // JSON) so it refetches only when search-active flips, not per keystroke. committedQ flips it
  // immediately on Enter; advFiltersDebounced covers panel filters. Caveat: the server caps the
  // page at 100, so all-history reaches the 100 most-recent entries — ample at current client
  // volume; true pagination is Phase 2. myAgentId/role ride along (resolve async, ARE the scope).
  const histAllTime = advFiltersActive(advFiltersDebounced) || !!committedQ
  useEffect(() => { loadHistory({ allowFresh: true }) }, [entryPeriod, myAgentId, role, histAllTime])   // eslint-disable-line react-hooks/exhaustive-deps
  // Sync the box's SERVER-side text search into advFilters: COMMITTED text → q. This is a
  // commit-act write (committedQ only changes in commitSearch / its empty-box clear), so it
  // may touch committed state. near NO LONGER syncs here (2a split): it used to follow the
  // LIVE near token per keystroke — a per-keystroke write into committed state, exactly what
  // §2.2 forbids now that isHome derives from advFilters. commitSearch owns near at fold
  // time; the live token still narrows loaded rows client-side via nearLocationQueries.
  // Multiple near tokens: the server gets the FIRST (one distance predicate per query) and so does
  // the client mirror (DF03, 2026-10-28); the rest are reported as unapplied words.
  useEffect(() => {
    const nq = committedQ
    setAdvFilters(f => (f.q === nq ? f : { ...f, q: nq }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [committedQ])

  // ── URL ⇄ committed search (stage 2c — DESK_MODE_REWRITE_PLAN §1.3/§2.6) ──────────────
  // IntakeTab OWNS the '#datadesk…' hash while mounted (it only mounts on the Desk tab;
  // App.jsx's tab sync is guarded to never touch a same-tab payload). Two directions, one
  // vocabulary (deskUrl.js — all encoding lives there, none here):
  //   WRITE: committedSearch → hash, via history.pushState when the user begins a NEW
  //          search context (isNewContext, §1.3) and replaceState while refining. Never
  //          per keystroke — committedSearch only changes on commit acts (§2.2), and the
  //          canonical-equality guard below skips even identity churn.
  //   READ:  mount + hashchange (back/forward, pasted links) → parseHash → apply into
  //          the live state. kind 'listing' opens the lineage modal; 'search' restores
  //          filters/type/city/q; 'home' is goDeskHome without a new history entry.
  // ⚠ PING-PONG GUARD: an applied parse updates committedSearch, which would fire the
  // write effect straight back. Both directions bail on canonical equality
  // (sameCommitted), and urlApplyRef additionally holds the write effect off while an
  // apply's setState batch is still landing — once committedSearch moves off the apply's
  // baseline, whatever the apply actually produced is canonicalized with a silent
  // replaceState (never a push: the history entry for that URL already exists).
  const committedSearchRef = useRef(committedSearch)
  committedSearchRef.current = committedSearch
  const urlApplyRef = useRef(null)      // { baseline } — apply in flight, write must wait
  const urlPrevRef = useRef(null)       // last committed state the URL reflects (push-vs-replace input)
  // ── THE OPEN CARDS, in the same hash (2026-08-05) ─────────────────────────────────────
  // Third direction on the same two effects: what is OPEN, not just what is searched. The
  // encoding lives in deskUrl.js (`cards` + `view`); here it is only ever the derived
  // string, so state and URL cannot describe different trails.
  //
  // The card-face deal editor (deskRepost/deskCoop/deskTerms — 🤝 / 🏠＋ / ↗ on a feed card,
  // with no card modal behind it) is deliberately folded into the SAME representation: it
  // is one listing plus one tab, which is exactly what a listing card with a `view` is. So
  // it serializes as one, and a refresh reopens that editor inside the property card rather
  // than floating over the feed. Same editor, same tab, same listing — the only difference
  // is that closing it lands on the property instead of on the feed, which is the better of
  // the two anyway. The alternative (restoring the bare editor) needs the exact feed ROW it
  // was opened from, and that row lives in one of three different feeds and may not survive
  // the reload at all.
  const deskDealListingId = deskRepost?.listing_id || deskTerms?.listing_id || deskCoop?.listingId || null
  const urlCards = cardStack.length
    ? cardStack
    : (deskDealListingId ? [{ kind: 'listing', id: deskDealListingId, isOwn: false }] : [])
  const urlCardView = cardStack.length
    ? (cardStack[cardStack.length - 1].view || null)
    : (deskDealListingId ? (deskDealTab || (deskRepost ? 'share' : deskCoop ? 'coop' : 'terms')) : null)
  const urlCardsStr = serializeCards(urlCards)
  const urlCardsRef = useRef(urlCardsStr)
  urlCardsRef.current = urlCardsStr
  // Cards an apply is still landing (setState is async, and the mount apply runs one
  // effect BEFORE the writer). Without this the writer computes a url from the empty
  // stack and replaceState's the pasted/refreshed trail away before it can be restored —
  // which is exactly how the deep-link hash used to vanish from the bar on arrival.
  const cardsApplyRef = useRef(null)
  // Plain function, captured once by the mount effect below — safe because it touches only
  // React setters (stable), functional updates, and refs; nothing here closes over per-render
  // state. Do not add a dependency on live state without switching to a ref.
  const applyParsedHash = parsed => {
    if (!parsed) return                 // not a #datadesk hash — not ours
    if (parsed.kind === 'share') {
      // 🔗 The client page's logo sent us here with a share token. Strip it from
      // the URL BEFORE resolving: it is a bearer credential, and leaving it in the
      // bar puts it in browser history, in any screenshot of the Desk, and in the
      // next pushState entry. Resolution needs the value, not the URL.
      const tok = parsed.shareToken
      try { history.replaceState(null, '', location.pathname + location.search + '#datadesk') } catch { /* non-blocking */ }
      openSharedLink(tok)
      return
    }
    // CARDS FIRST, and unconditionally — a hash can carry a trail over a search, over the
    // home feed, or with nothing behind it at all, and the search branches below return
    // early in two of those three cases. This is a fresh navigation (mount, paste, back),
    // so the trail RESETS rather than pushing, same rule as openListingCard.
    // isOwn:false on a restored listing is only the initial hint; the modal reflects real
    // ownership from its own payload and access stays server-enforced on load.
    const cards = parsed.cards || []
    const cardsStr = serializeCards(cards)
    if (cardsStr !== urlCardsRef.current) {
      cardsApplyRef.current = cardsStr
      setCardStack(cards)
      // A restored trail replaces whatever the card-face editor had open — it is the same
      // listing and tab, now rendered inside the card (see the urlCards note above).
      if (cards.length) { setDeskCoop(null); setDeskTerms(null); setDeskRepost(null) }
    } else {
      cardsApplyRef.current = null       // nothing to wait for; never leave a stale baseline
    }
    // The page the hash asks for (owner 2026-09-02: "make page numbers go in url"). Set BOTH:
    // the state, for the case where no filter changes and the reset never runs, and the ref, so
    // the reset performs the restore rather than zeroing it when it does.
    // ⚠️ Not clamped here. This code cannot know how many pages exist yet — the feed clamps with
    // `clampedPage` once it does, so a shared link to a page that has since shrunk away lands on
    // the last real page instead of an empty feed.
    // ⚠️ The ORDER rides with the page (audit 2026-10-29 D3): a page number only means something under the sort it
    // was taken in. A link without `sort` is the default order (every pre-D3 link); a sort this Desk no longer knows
    // falls back to the default AND drops the page — page 3 of an order we cannot reproduce is not the page asked for.
    // Only a SEARCH hash states an order: home ('#datadesk', also what App's tab sync writes) and the bare listing
    // link say nothing about the feed's sort, and must not reset the one the agent picked.
    const sortKnown = parsed.kind !== 'search' || parsed.sort === DEFAULT_SORT
      || Object.hasOwn(LEADS_SERVER_SORT_OF, parsed.sort ?? '') || CLIENT_ONLY_SORTS.has(parsed.sort)
    // ⚠️ Leads' own default order is Best Match, and a restore must land it in THIS batch. Left to the arrival swap
    // effect (feedCat === 'Leads' && 'activity' → 'best_match'), the swap is a second query change one render later,
    // after the page latch below has been spent — LeadsFeed's query effect and the reset effect then both zero the
    // restored page, so every Leads link without `sort=` opened on page 1 (audit 2026-10-29 appendix, measured in a
    // mounted IntakeTab). Same condition as that effect, so the two can never disagree about the order.
    // The codec already reads an ABSENT Leads sort as Best Match (defaultSortFor), so a Leads 'activity' here was
    // written on purpose (audit 2026-10-29 FD-1): it counts as picked, or the arrival swap would turn it into Best
    // Match one render later while keeping its page.
    const urlSortNow = sortKnown ? parsed.sort : defaultSortFor(parsed.committed?.stage)
    if (parsed.kind === 'search' && parsed.committed?.stage === 'leads' && urlSortNow === 'activity') sortPickedRef.current = true
    if (parsed.kind === 'search') setEntrySort(urlSortNow)
    if (typeof parsed.page === 'number') {
      const page = sortKnown ? parsed.page : 0
      pageRestoreRef.current = page
      setEntryPage(page)
    }
    // The externally-shared object link ('#datadesk/listing/<id>'): the card above IS the
    // whole payload — it states nothing about the search, so the current one is left alone
    // and the writer canonicalizes the bar into the '?cards=' form on the next tick.
    if (parsed.kind === 'listing') return
    const target = parsed.kind === 'home' ? makeCommitted({}) : parsed.committed
    if (sameCommitted(target, committedSearchRef.current)) return   // already there — no-op (guard)
    // ⚠️ `page` rides on the latch (2026-09-02). Without it the guard below compares only the
    // committed search, so after ANY hash restore the writer returned forever on page changes —
    // the feed moved, the bar never did. The latch has to describe everything the hash carries,
    // or it holds the writer hostage for the parts it forgot.
    urlApplyRef.current = { baseline: committedSearchRef.current, page: sortKnown ? (parsed.page ?? 0) : 0, sort: parsed.kind !== 'search' ? null : urlSortNow }
    if (parsed.kind === 'home') { goDeskHome(); return }
    // ── A RESTORE COMPILES, EXACTLY LIKE A TYPED COMMIT (2026-08-27) ─────────────────
    // The line below used to take `q` as finished text. That is true for a URL the Desk
    // WROTE — its `q` is the post-fold residual, and the constraints ride as structured
    // params (district=/bedrooms=/features=…). It is false for any OTHER url carrying
    // `?q=`: a pasted link, a hand-built one, the '#datadesk?q=' door the docs describe,
    // or an old bookmark from before a token became compilable. Those arrived as pure
    // text, so nothing compiled them — and the live capture showed the cost exactly:
    // #datadesk?q=2 საძინებელი საბურთალო ცხოველები აივანი produced SIX requests, every one
    // of them "scope=all&status=active&limit=100&offset=100…600" against the whole corpus,
    // while the box showed the query. Visible state said filtered; executable state was not.
    //
    // The fix is not a second parser — it is the SAME compileQuery + applyTokenOverlay a
    // typed Enter runs (see commitSearch), applied here too. Running it unconditionally is
    // safe BECAUSE it is idempotent: a Desk-written `q` is already the residual, so it
    // compiles to zero tokens and every value below is unchanged. One canonical compile,
    // three entry points (Enter, URL restore, refresh).
    // ⚠️ AND IT MUST WAIT FOR THE VOCABULARY. Measured 2026-08-27, one layer below the bug
    // above: with the compile wired in, "2 საძინებელი საბურთალო ცხოველები აივანი" restored as
    // bedrooms_min=2 ALONE. The bedroom recognizer is pure arithmetic; district, restriction
    // and feature recognizers all read fetched vocabularies (districts / features /
    // locations), and applyParsedHash runs at MOUNT, before any of them land — so they
    // matched nothing and failed silently, which is the same class of defect as the one being
    // fixed. The raw text is parked here and folded by the effect below the moment the
    // vocabulary arrives.
    const vocabReady = (districts || []).length > 0 && (features || []).length > 0
    const rawQ = target.filters.q || ''
    const rc = (rawQ && vocabReady) ? compileQuery(rawQ, { districts, locations: geoLocations, features, conditionCodes: ladderCodes, isSalePrice }) : { tokens: [], residual: rawQ }
    const rTokens = rc.tokens || []
    // (2026-10-22: strip only the FOLDING spans, as the fold effect below and commitSearch do — `rc.residual` also
    // swallowed identity spans, so a restored id / phone / cadastral search came back as an empty box and no search)
    const q = boxTextAfterFold(rawQ, rTokens, rc.dropped).trim()
    const rNear = rTokens.find(tk => tk.type === 'near_location')
    setAdvFilters(f => {
      const base = committedToAdvFilters(target, { ...ADV_DEFAULTS, currency: target.filters.currency || f.currency })
      if (!rTokens.length) return base
      const folded = applyTokenOverlay(base, rTokens, { fold: true })
      // near is token-owned on this path too — a restored URL whose text names a place must
      // land the same point a typed commit would, and must not inherit the previous search's.
      return { ...folded, near: rNear
        ? { id: rNear.value.id, lat: rNear.value.lat, lng: rNear.value.lng,
            radiusM: NEAR_SEARCH_RADIUS_M[rNear.value.kind] || 1000, label: rNear.label }
        : base.near }
    })
    setCommittedQ(q)
    setEntrySearch(q)   // seed the visible box (a restored search must not show an empty box over filtered results)
    // ⚠️ AND LAND IT (2026-08-05, found merging the landedSearch work into the card-URL work).
    // Since landedSearch shipped, the feed filters by the LANDED text and only commitSearch
    // writes it — so restoring a search from the hash filled the box and left the feed
    // unfiltered: the URL said ვაკე, the box said ვაკე, and every property in the city was
    // still on screen. A restore IS a commit act; it just arrives through the URL instead of
    // through Enter. `q` is already the post-fold residual (it round-tripped through
    // committedSearch), so this keeps the "landed == what the box shows" invariant that
    // block relies on — the line above sets the box to the same value.
    setLandedSearch(q)
    // §3b: a stated `stage=leads` (or a legacy `type=Leads`, which the parser maps to it)
    // opens the Leads inventory; the Object axis carries everything else.
    // ⚠️ Audit 2026-10-29 D1: `type=Property` IS the Properties aggregate. committedSearch writes only member zero
    // of ['Property', 'Listings'], and no control on this screen selects Property alone (the selector, the category
    // tree and home's "show all" all set the pair) — so every `type=Property` link, old or new, was written by the
    // pair. Restoring it as ['Property'] dropped every listing row after a refresh or a saved-search recall while
    // the selector still read "Properties".
    setEntryTypeFilter(target.stage === 'leads' ? ['Leads']
      : target.type === 'Property' ? ['Property', 'Listings']
      : (target.type ? [target.type] : []))
    // F-087: a URL restore is a commit act for the pool too — both axes are set so a leads link and a book link each
    // land on the pile they name, and a link without `pool` lands on the active pile (the default, never written).
    if (target.stage === 'leads') setLeadArchive(target.pool === 'archived')
    else setEntryStatusFilter(target.pool === 'archived' ? 'archived' : 'active')
    setEntryCityFilter(target.city || 'all')
    // Provenance travels WITH the search it describes, and is replaced wholesale on every
    // restore — including with `null`. Carrying the previous marker across a restore would
    // attach one client's name to another search's lenses, which is worse than not knowing.
    setRestrictionsFrom(target.restrictionsFrom || null)
    setCommittedBrowse(true)
  }
  // ── THE LANDED TEXT ALWAYS FOLDS (2026-08-27) ─────────────────────────────────────
  // A '?q=' that reaches the Desk as raw text must compile to the same filters typing it
  // would. The first two attempts tied that to WHEN the restore happened, and measurement
  // killed both: applyParsedHash runs at mount with districts=0/features=0 (the vocabularies
  // are fetched later), and instrumenting the order showed the deferred effect firing at
  // t=149549 with nothing parked while the restore itself ran at t=165768 with the vocabulary
  // still empty — the two never meet, and no latch between them can be made reliable.
  //
  // So this does not depend on order at all. The rule is a STATE invariant: whenever landed
  // text still contains compilable tokens AND the vocabulary is present, fold them. Whichever
  // arrives second triggers it, and it is idempotent — after folding, the landed text is the
  // residual, which compiles to zero tokens, so it never fires twice. A typed commit already
  // folds before setting landedSearch, so this is a no-op there (its residual has no tokens).
  // That is what makes typed / pasted / refreshed / restored converge on one executable state.
  useEffect(() => {
    if (!landedSearch) return
    if (!(districts || []).length || !(features || []).length) return
    // Already folded by commitSearch, which deliberately left dismissed / noise tokens as
    // text (a client or street suggestion click must not come back as a district chip).
    if (commitLandedRef.current === landedSearch) return
    const cc = compileQuery(landedSearch, { districts, locations: geoLocations, features, conditionCodes: ladderCodes, isSalePrice })
    // A dismissed chip stays text here too, exactly as in commitSearch.
    const toks = (cc.tokens || []).filter(tk => !dismissedSigs.has(tk.sig))
    if (!toks.length) return                        // already canonical — nothing to fold
    // 🔴 IDENTITY TEXT IS THE SEARCH, AND THIS EFFECT USED TO DELETE IT.
    // commitSearch folds only tokens whose `patch` is non-empty, precisely because listing_id /
    // phone / cadastral patch NOTHING — their filter IS the text, read back out of landedSearch
    // by listingIdQueries/phoneQueries/cadastralQueries. This effect applied the rule to ALL
    // tokens, so a bare id compiled to one token, took cc.residual (empty — the id consumed the
    // whole string) and set landedSearch to '', wiping the very search it was restoring. Typing
    // an id and pressing Enter emptied the box and landed on browse. Same distinction as
    // commitSearch, and it has to be made in BOTH places or they disagree.
    const foldable = toks.filter(tk => Object.keys(tk.patch || {}).length)
    if (!foldable.length) return                    // identity-only text: leave it exactly alone
    const nearTok = toks.find(tk => tk.type === 'near_location')
    setAdvFilters(f => {
      const folded = applyTokenOverlay(f, toks, { fold: true })
      return nearTok
        ? { ...folded, near: { id: nearTok.value.id, lat: nearTok.value.lat, lng: nearTok.value.lng,
              radiusM: NEAR_SEARCH_RADIUS_M[nearTok.value.kind] || 1000, label: nearTok.label } }
        : folded
    })
    // Strip only the FOLDING spans, exactly as commitSearch does — never cc.residual, which
    // also swallows identity spans. 'ვაკე 25883389' must land as '25883389' with a district chip.
    const residual = boxTextAfterFold(landedSearch, foldable, cc.dropped)
    // Same 3-character floor as commitSearch: a 1–2 character residual is not a text search.
    setCommittedQ(residual.length >= 3 ? residual : '')
    setEntrySearch(residual)
    setLandedSearch(residual)
    setCommittedBrowse(true)
  }, [landedSearch, districts, features, geoLocations, dismissedSigs, ladderCodes])

  useEffect(() => {
    applyParsedHash(parseHash(window.location.hash))
    const onHash = () => applyParsedHash(parseHash(window.location.hash))
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  useEffect(() => {
    // Mid tab-switch (mount race): this effect can run BEFORE App's tab→hash sync writes
    // '#datadesk'. Another tab's hash is App's property — writing here would replace that
    // tab's history entry. Skip; once App lands '#datadesk' this state is home ≡ canonical.
    const curTab = window.location.hash.slice(1).split(/[/?]/)[0]
    // A card apply is still landing — the stack has not caught up with the hash yet, and
    // writing now would erase the very trail being restored (see cardsApplyRef).
    if (cardsApplyRef.current != null) {
      cardsApplyRef.current = null
    }
    // If the reset effect never fired (a hash carrying a page but no filter change) the restore
    // ref is still set and would misfire on the NEXT genuine filter change, restoring an old
    // page instead of resetting to 1. Clearing it here is safe because this effect runs after
    // the reset on every render where both run.
    // ⚠️ …except the MOUNT commit, where the hash restore effect runs BETWEEN the two: it arms the latch after the reset
    // has already run, and an unconditional clear here disarmed it before the next render's reset could use it — a
    // refreshed or newly opened `page=` link landed on page 1 (audit 2026-10-29 appendix, measured in a mounted
    // IntakeTab). Clear only once the state has caught up with the latch: that is the "reset never fired" case this
    // clear exists for; until then the restore is still landing.
    if (pageRestoreRef.current === entryPage) pageRestoreRef.current = null
    // Home has no ordered feed, and a home hash is never read for its sort (see applyParsedHash) — so it writes none.
    const urlSort = isHome ? DEFAULT_SORT : entrySort
    const url = withPage(withSort(withCards(serializeSearch(committedSearch), urlCards, urlCardView), urlSort, defaultSortFor(committedSearch.stage)), entryPage)
    const pending = urlApplyRef.current
    if (pending) {
      // Still landing ONLY while both halves still match what was applied. A page change with
      // the same search is the agent moving on, not the apply still in flight.
      if (sameCommitted(pending.baseline, committedSearch) && (pending.page ?? 0) === (entryPage || 0)
          && (pending.sort == null || pending.sort === urlSort)) return
      urlApplyRef.current = null
      urlPrevRef.current = committedSearch
      if (window.location.hash !== url) window.history.replaceState(null, '', url)
      return
    }
    const parsedCur = parseHash(window.location.hash)
    // ⚠️ ALL THREE, or the guard lies — and it is now three because the page joined the hash
    // (2026-09-02). Committed-equality alone would skip the write for every card open/close that
    // happens without touching the search; adding cards but not the page skipped it for every
    // PAGE change, which is exactly what happened: clicking page 3 moved the feed and left the
    // bar saying page 1. Found by clicking, not by any test — the state was right, the URL was
    // not, and only the address bar shows that.
    if (parsedCur && parsedCur.kind !== 'listing' && sameCommitted(parsedCur.committed ?? {}, committedSearch)
        && serializeCards(parsedCur.cards) === urlCardsStr
        && (parsedCur.page || 0) === (entryPage || 0)
        && (parsedCur.sort || DEFAULT_SORT) === urlSort
        && ((parsedCur.cards || []).slice(-1)[0]?.view || null) === (urlCardView || null)) {
      return
    }
    // Opening or closing a card REPLACES, never pushes: a card is not a new search context,
    // and pushing would make ✕ and Back fight over which one closes it. Back still leaves
    // the whole trail rather than popping one card — that half is B310, not shipped here.
    const push = urlPrevRef.current != null && isNewContext(urlPrevRef.current, committedSearch)
    window.history[push ? 'pushState' : 'replaceState'](null, '', url)
    urlPrevRef.current = committedSearch
  }, [committedSearch, urlCardsStr, urlCardView, entryPage, entrySort])
  // ── Suggest dropdown (step 8, blueprint §11): query FORMULATION, not results ──
  // One dropdown under the box while typing: chips preview + places are pure client-side
  // (zero network); clients/business/complexes come from ONE debounced (250ms) call to
  // /api/core/search-suggest — the debounce is fine here, it hits tiny limit-3 arms,
  // never the clusters RPC. The response is CACHED (suggestResp.forQ) so the zero-result
  // backstop re-renders it without a second call. Clicking a suggestion COMMITS (§5.3).
  const [suggestResp, setSuggestResp] = useState(null)   // { forQ, clients, businesses, complexes } — arms null on per-arm failure
  const [searchFocused, setSearchFocused] = useState(false)
  // Client-search cap tripwire (BACKLOG B36 / SEARCH_ARCHITECTURE §0): once live proposals
  // pass the nightly warn threshold (nightly_dedup.sh, >400 of the 500 /proposals cap),
  // an all-history client search silently drops the oldest clients. The Admin→Dedup badge
  // shows it too, but agents live in this search feed — surface the same signal on the
  // zero-state, where a dropped client actually manifests as "0 results". Empty → nothing.
  const [capTripwire, setCapTripwire] = useState(false)
  useEffect(() => {
    let gone = false
    getDedupHealthAlerts()
      .then(a => { if (!gone) setCapTripwire(a.some(l => /proposals feed at/i.test(l))) })
      .catch(() => {})
    return () => { gone = true }
  }, [])
  // ── Mobile full-screen search overlay (eBay model, 2026-07-18 user ruling) ──
  // On mobile, focusing the strip's search box opens a takeover screen instead of the
  // anchored dropdown: input on top, then completion hints + the same suggest arms, then
  // recent searches. Commit (Enter / suggestion tap) closes it back to the results.
  const [searchOverlayOpen, setSearchOverlayOpen] = useState(false)
  // 🚪 THE ONE WAY TO CLOSE THE SUGGEST SURFACE. There are TWO of them — the mobile
  // takeover (`searchOverlayOpen`) and the desktop anchored dropdown (`searchFocused`) —
  // and a handler that clears one but not the other closes the box on exactly one of the
  // two form factors. That is what shipped: five commit handlers cleared only the overlay,
  // so on DESKTOP clicking 📍 metro / 🏢 complex / 👤 client / 🏪 business / a restriction
  // row, pressing Tab on a district hint, or pasting an image all ran their action with
  // the dropdown still covering the results (owner-reported 2026-08-16: "when user clicks
  // option in dropdown it must close … metro option click dont work, street click closed").
  // The metro row DID commit its filter every time; the panel that proves it was hidden
  // behind the list, which is indistinguishable from a dead row.
  // ⚠️ The dropdown wrapper carries `onMouseDown={e => e.preventDefault()}` so the input
  // never blurs — deliberately, since blur-on-mousedown unmounts the row before its click
  // lands. That is exactly why `onBlur` cannot be the closer and every handler must call
  // this one. Adding a sixth commit path? Call this, not a setter.
  // Declared here (after both states) and referenced by callbacks defined ABOVE it — safe,
  // they run on user events, long after init. `no-use-before-define` will count those as
  // new hits; they are the same shape as the ~30 baseline ones, not a TDZ (B710).
  const closeSuggest = () => { setSearchOverlayOpen(false); setSearchFocused(false) }
  // Recents now SYNC per agent (migration 0211): localStorage is the instant paint and the
  // offline/admin fallback, the server list is the truth once it lands. ⚠️ Do not route
  // this through search_query_log — that stream stays capture-first and un-deduped for
  // vocabulary review (see the 0211 header); this is only the 10 committed queries.
  const [recentSearches, setRecentSearches] = useState([])
  const recentSearchKey = `desk_recent_searches:${myAgentId || 'admin'}`
  useEffect(() => {
    try { setRecentSearches(JSON.parse(localStorage.getItem(recentSearchKey)) || []) } catch { setRecentSearches([]) }
  }, [recentSearchKey])
  // Server list overwrites the local paint (last-write-wins across devices). An empty
  // server list does NOT clear a non-empty local one: an agent whose row hasn't been
  // written yet — or an admin with no agent id — keeps what this browser remembers.
  useEffect(() => {
    let alive = true
    if (!myAgentId) return
    getRecentSearches(myAgentId).then(qs => {
      if (!alive || !qs.length) return
      setRecentSearches(qs)
      try { localStorage.setItem(recentSearchKey, JSON.stringify(qs)) } catch { /* quota — memory-aid only */ }
    })
      // review 2026-09-23: a failed fetch keeps this browser's list (the fallback above) —
      // it must not surface as an unhandled rejection.
      .catch(e => console.warn('[recent-searches]', e?.message))
    return () => { alive = false }
  }, [myAgentId, recentSearchKey])
  // ── Saved searches (0231) — recents' persistent sibling ────────────────────
  // Identity is the CANONICAL serializeSearch() hash, never raw text or a second
  // serialization (search-bar-keyword-projection rule: one model, one projection). So
  // "is this search already saved" is string equality, applying one is just setting the
  // hash (the existing restore path does the rest), and a search saved from panel
  // checkboxes — which raw text cannot express — round-trips exactly.
  const [savedSearches, setSavedSearches] = useState([])
  // Ownership uses the FAVORITES identity rule (favIdentity): agents by roster id, admins
  // by auth uid — a personal bookmark must work for the admin testing it, and recents'
  // "admins get nothing" precedent is the wrong one here (found live: the owner IS an
  // admin with no agents row, and the save button was silently disabled).
  useEffect(() => {
    let alive = true
    if (!favIdentity) return
    getSavedSearches(favIdentity).then(list => { if (alive) setSavedSearches(list) })
    return () => { alive = false }
  }, [favIdentity])
  const currentSearchParams = useMemo(() => serializeSearch(committedSearch), [committedSearch])
  const savedMatch = savedSearches.find(s => s.params === currentSearchParams) || null
  // ⚠️ A BARE CATEGORY IS NOT A SEARCH (owner 2026-07-28: "I cleared each pill myself,
  // nothing is filtering, and it still offers to clear the search"). Picking ლიდები /
  // Property in the scope dropdown serializes as '#datadesk?type=Leads' — non-empty, so
  // the first cut showed the bar on an untouched category feed and, worse, kept showing it
  // after every chip had been removed one by one. Compare against the type-only
  // serialization rather than a hardcoded list, so it stays right for any category and any
  // future param-order change; '#datadesk?browse=1' is the same idea for the all-types feed
  // (browse-with-nothing-set, which serializeSearch emits only when nothing else is set).
  // ⚠️ `stage` MUST be in here too (owner, 2026-08-02: the bar blinked in and out when
  // flipping pools). This comparison reproduces the "nothing but a category is chosen"
  // URL and hides the bar when the real one matches it. When §3b moved Leads off the
  // Object axis, a bare Leads selection stopped serializing as `type=Leads` and started
  // serializing as `stage=leads` — which this line did not reproduce, so the strings
  // stopped matching and the ruling above silently stopped applying to the one category
  // it was written for. Picking a pool is navigation, not a search; it must not offer to
  // clear or save anything.
  const bareTypeParams = useMemo(
    () => serializeSearch({ type: committedSearch.type, stage: committedSearch.stage }),
    [committedSearch.type, committedSearch.stage])
  // Property + Leads only for now (owner 2026-07-28) — a UI gate, the schema is
  // category-agnostic. '' = the default all-types feed, which is the property surface.
  const savedSearchEligible = !isHome
    && ![bareTypeParams, '#datadesk', '#datadesk?browse=1'].includes(currentSearchParams)
    && ['', 'Property', 'Listings', 'Leads'].includes(entryTypeFilter[0] || '')
  const saveCurrentSearch = () => {
    if (!favIdentity || !savedSearchEligible || savedMatch) return
    // Label = human words from the DISPLAY vocabularies each part already has (the first
    // cut reused constraintChips, but panel checkboxes — the exact filters a raw q cannot
    // express — produce no chips, so an SS.ge leads search labeled itself with raw params).
    // Presentation only — regenerable, never identity; the agent can rename it anyway.
    const f = committedSearch.filters
    const srcLabel = Object.fromEntries(SOURCE_FACET_OPTIONS)
    const dName = code => { const d = districts.find(x => x.code === code); return d ? (d.name_ka || d.name_en || code) : code }
    const parts = [
      f.q,
      committedSearch.type ? t(ENTRY_TYPE_FILTER_LABEL_KEYS[committedSearch.type] || committedSearch.type) : null,
      ...(f.intents || []).map(v => intentLabel(v)),
      ...(f.kinds || []).map(v => t(PROPERTY_CATEGORY_KEYS[v] || v)),
      ...(f.districtCodes || []).map(dName),
      ...(f.sources || []).map(v => srcLabel[v] || v),
      (f.priceMin !== '' && f.priceMin != null) || (f.priceMax !== '' && f.priceMax != null)
        ? `${f.priceMin || 0}–${f.priceMax || '∞'} ${f.currency || ''}`.trim() : null,
      (f.roomsSet || []).length ? `${[...f.roomsSet].join('/')} ${t('desk_search_rooms_short')}` : null,
    ]
    const label = parts.filter(Boolean).filter((v, i, a) => a.indexOf(v) === i).join(' · ').slice(0, 300)
      || currentSearchParams.replace(/^#datadesk\??/, '')
    const optimistic = { id: `tmp-${Date.now()}`, params: currentSearchParams, label, category: entryTypeFilter[0] || '' }
    setSavedSearches(prev => [optimistic, ...prev])
    saveSearchServer(favIdentity, { params: currentSearchParams, label, category: optimistic.category })
      .then(row => setSavedSearches(prev => row
        ? prev.map(s => (s.id === optimistic.id ? row : s))
        : prev.filter(s => s.id !== optimistic.id)))   // server refused → withdraw, never fake persistence
  }
  const renameSavedSearch = (sSaved) => {
    // window.prompt has precedent (ListingsTab lock reason) and works on mobile; an
    // inline editor inside a blur-closed dropdown fights the dropdown's own focus rules.
    const next = window.prompt(t('desk_search_rename'), sSaved.label || '')
    if (next == null) return                       // cancelled — not "clear the label"
    const label = next.trim().slice(0, 300)
    if (!label || label === sSaved.label) return
    setSavedSearches(prev => prev.map(x => x.id === sSaved.id ? { ...x, label } : x))
    if (!String(sSaved.id).startsWith('tmp-')) renameSavedSearchServer(favIdentity, sSaved.id, label)
  }
  const deleteSavedSearch = (id) => {
    setSavedSearches(prev => prev.filter(s => s.id !== id))
    if (!String(id).startsWith('tmp-')) deleteSavedSearchServer(favIdentity, id)
  }
  const applySavedSearch = (s) => {
    // The hash IS the apply mechanism (deskUrl READ path): hashchange → parseHash →
    // applyParsedHash restores filters/type/city/q, with history handled for free.
    window.location.hash = s.params
  }

  const recordRecentSearch = (q) => {
    const v = (q || '').trim()
    if (v.length < 3) return
    recordRecentSearchServer(myAgentId, v)   // fire-and-forget; local state is authoritative for this paint
    setRecentSearches(prev => {
      const next = [v, ...prev.filter(x => x !== v)].slice(0, 10)
      try { localStorage.setItem(recentSearchKey, JSON.stringify(next)) } catch { /* quota — memory-aid only */ }
      return next
    })
  }
  // 📷 Image search (2026-07-17, eBay model): upload a photo → server pHash-matches it
  // against the capture layer's hashed images → the hits ride the SAME reach-back
  // mechanism as pasted listing ids (idSearchEntries: lineage-family expansion, merge
  // into the feed, identity filter). State is a removable chip, cleared like any filter.
  const [imageSearch, setImageSearch] = useState(null)   // { name, results: [{id, distance, ...}] } | 'loading' | null
  const imageFileRef = useRef(null)
  // 🛣️ Street proximity (2026-07-18): a picked street from the suggest dropdown becomes a
  // removable chip that filters loaded listings by DISTANCE to the street's gazetteer point
  // (property has no street_id — linkage is geo, like near_location). Its geo comes from the
  // precise resolver (resolveStreetQuery → server core_street.js). Separate state + chip that
  // coexists with the box, exactly like imageSearch above; cleared by × or "clear filters".
  const [streetSel, setStreetSel] = useState(null)   // { name_ka, district, lat, lng } | null
  const runImageSearch = async (file) => {
    if (!file) return
    // Results are a feed behind the suggest surface — leaving it open hides the answer.
    // (Ctrl+V pastes into a FOCUSED box, so on desktop the dropdown is always up here.)
    closeSuggest()
    setImageSearch('loading')
    logSearchQuery({ rawText: '', compiled: [], residual: '', dismissed: [], context: { source: 'desk', event: 'image_search' } })
    try {
      const results = await searchListingsByImage(file, listingsScope === 'all' ? undefined : myAgentId)
      setImageSearch({ name: file.name, results })
      enterBrowse()   // results are a feed — home has nowhere to render them
      // Image hits are LISTINGS — Pool/Business/Client feeds can't render them; snap
      // back to the All view so the results are actually visible.
      // ⚠️ DERIVED from SPECIAL_CATEGORIES (2026-08-16), not restated. This was a hand-written
      // list and it had drifted behind that one: every SPECIAL category REPLACES the entries
      // pipeline, so every one of them swallows an image result, and the list was missing
      // ♥ Favorites, 🗄 Archived and ClientLeads. Run an image search from any of those three
      // and SPECIAL_FEED stayed true — the 📷 chip reported "N matches" over that category's
      // own feed, and the results were never rendered at all.
      // The comment at `identitySearchActive` already ASSERTED this could not happen ("image
      // search already snaps the category out of every special feed"), which is exactly why
      // nobody checked: a second list of "which categories replace the pipeline" is a second
      // definition, and it went stale silently because nothing compared it to the first.
      // 'Client' is not a special feed but stays for the original reason — it renders people,
      // not listings, so an image hit has nowhere to land there either.
      setEntryTypeFilter(t => (t.length && (SPECIAL_CATEGORIES.includes(t[0]) || t[0] === 'Client')) ? [] : t)
    } catch (e) {
      console.error('[image-search]', e.message)
      setImageSearch({ name: file.name, results: [], error: true })
    }
  }
  // ⌨️ Ctrl+V an image straight into the box (owner-reported 2026-08-04): "open the link
  // I sent the client, copy the photo, paste it in search" found nothing — because the
  // ONLY entry point was the 📷 file picker, and an image pasted onto a text input is
  // silently discarded. Retrieval was never at fault: measured against three live shares,
  // a share's own proxied photo matches its listing at hamming 0 through the same
  // search_listings_by_phash call this button already makes. So the fix is a door, not a score.
  // ⚠️ preventDefault ONLY once an image is actually in hand. Copying an image from a web
  // page puts text/html on the clipboard beside it, and swallowing every paste would break
  // pasting a listing id or a share link — the two things this box is used for most.
  const onSearchPaste = e => {
    const dt = e.clipboardData
    if (!dt) return
    const item = [...(dt.items || [])].find(i => i.kind === 'file' && (i.type || '').startsWith('image/'))
    // getAsFile must run synchronously inside the event — the clipboard item is dead after.
    const file = (item && item.getAsFile()) || [...(dt.files || [])].find(f => (f.type || '').startsWith('image/'))
    if (!file) return
    e.preventDefault()
    runImageSearch(file)
  }
  useEffect(() => {
    const q = entrySearch.trim()
    if (q.length < 3) { setSuggestResp(null); return }
    // ⚠️ NEVER send a pasted share link to the suggest endpoint. It puts `q` in a
    // URL query string, so the token — a bearer credential for the client page —
    // would land in nginx's access log and every proxy in front of it. That is
    // precisely what resolvePropertyShareLink uses a POST body to avoid, and it
    // would be undone here, silently, on the keystroke before the agent hits
    // Enter. There is also nothing to suggest: the token matches no text.
    if (looksLikeShareLink(q)) { setSuggestResp(null); return }
    // ⚠️ Desk filters audit 2026-10-28 A4: an answer belongs to text + locality. A slow answer for older
    // text, or for the previous city/district, used to land over the current one — and Enter's
    // single-street auto-accept (which checks only forQ) could take the wrong locality's street.
    // Late answers are dropped and a locality change empties the old rows at once.
    const ctx = JSON.stringify([entryCityFilter, advFilters?.districtCodes || []])
    setSuggestResp(prev => (prev && prev.forCtx !== ctx ? null : prev))
    let live = true
    const t = setTimeout(() => {
      // B940: the active locality travels with the query so the server ranks street rows by
      // the agent's context, not by whichever same-named row the index returns first.
      searchSuggest(q, role === 'agent' ? myAgentId : null, {
        city: entryCityFilter !== 'all' ? entryCityFilter : '',
        districtCodes: advFilters?.districtCodes || [],
      })
        .then(r => { if (live) setSuggestResp({ forQ: q, forCtx: ctx, ...r }) })
        .catch(() => {})   // all-arms failure → dropdown degrades to its client-side groups
    }, 250)
    return () => { live = false; clearTimeout(t) }
    // B940: context is in the deps — picking a district with the dropdown open must re-rank
    // it, not leave the pre-pick order cached under the same forQ.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entrySearch, role, myAgentId, entryCityFilter, advFilters?.districtCodes])
  // 👤 Restriction subjects (engine v18) — offered while typing, WITH their intent, because
  // the subject alone does not say which one you mean (owner 2026-07-28: "ინდოელები must
  // have two values — are they accepted or not accepted").
  // ⚠️ Deliberately NOT suppressed once the word compiles. The first cut hid the hint as
  // soon as a restriction token existed, which killed it in the one case it matters: a
  // stem match means "ინდოელ" compiles at the 6th keystroke, silently taking the DEFAULT
  // intent — precisely the ambiguity being complained about. The hint therefore stays and
  // MARKS the intent currently in force, so the default is visible and one click changes it.
  const restrictionSuggests = useMemo(
    () => suggestRestrictions(entrySearch, { features }), [entrySearch, features])
  // Keyboard cursor over the suggestion dropdown (owner 2026-07-28: "user just down arrows
  // to the correct one and presses enter instead of moving mouse to correct button").
  // -1 = nothing highlighted, so Enter still means "commit what I typed" — arrowing is
  // opt-in and never steals the plain Enter an agent has used since this box existed.
  const [suggestIdx, setSuggestIdx] = useState(-1)
  useEffect(() => { setSuggestIdx(-1) }, [entrySearch])
  const restrictionActiveMode = useMemo(() => {
    const m = new Map()
    for (const tk of activeTokens) if (tk.type === 'restriction') m.set(tk.value.code, tk.value.mode)
    return m
  }, [activeTokens])
  // 📍 Places — in-memory gazetteer match over the residual (the part not already chips).
  // Same name/alias set the near-recognizer indexes; substring, first 4, zero network.
  const placeSuggests = useMemo(() => {
    // ⚠️ Falls back to the FULL text when the residual is empty (2026-08-16, owner-reported
    // as "no metros are shown in search now"). Matching only the residual meant the place row
    // vanished at the exact moment the match SUCCEEDED: type "delisi", the near-recognizer
    // claims it as a 📍 chip, the residual empties, and the station drops out of the list —
    // while the server arms (streets/complexes/clients), which match the RAW query, kept
    // showing. So the dropdown offered three streets named Delisi and hid the metro station
    // behind a chip. The row is what lets an agent say "I meant the STATION, not the street",
    // which is the one thing the chip cannot express.
    // Residual first so a partially-typed query still narrows as it did; full text only as
    // the fallback, so this never adds rows to a query that still has unclaimed words.
    const q = (liveResidual || entrySearch).trim().toLowerCase()
    if (q.length < 3) return []
    const out = []
    for (const loc of geoLocations) {
      // ⚠️ complexes are EXCLUDED from the places arm (owner, 2026-08-26). This arm's rows
      // commit a 300 m NEAR filter from the scheduler locations dictionary — for a metro or a
      // park that IS the question, for a development it is the wrong mechanism twice over:
      // core is the canonical complex identity (0726) and membership, not proximity, is the
      // question (distance settles nothing — COMPLEX_MATCHING.md). The dropdown was offering
      // the same brand through both doors, Georgian names on one and Latin on the other,
      // which read as a naming mess and was actually a mechanism mess.
      if (loc.type === 'complex') continue
      const names = [loc.name, loc.name_ka, ...(loc.meta?.aliases || [])].filter(Boolean)
      if (names.some(n => String(n).toLowerCase().includes(q))) {
        // lat/lng ride along (2026-08-16): the row now COMMITS ITSELF as a near filter
        // (acceptPlace), so the point it stands for has to travel with it. `name` too —
        // the chip label must match the compiler's, which is built from `loc.name`.
        out.push({ id: loc.id, display: loc.name_ka || loc.name, name: loc.name, type: loc.type, lat: loc.lat, lng: loc.lng })
        if (out.length >= 4) break
      }
    }
    return out
    // entrySearch joins the deps with the fallback above — without it the row would be
    // computed from a stale box the moment the residual empties, which is the only case
    // the fallback exists for.
  }, [liveResidual, entrySearch, geoLocations])
  // A subject hint REPLACES only the partial word being typed, unlike place/client hints
  // which replace the whole box: a subject is one term inside a longer ask ("2 ოთახი ვაკე
  // ცხოველ…"), so swallowing the rest of the query would be a regression, not a shortcut.
  // The inserted text is the compiler's own phrase (restrictionPhrase), so what commits is
  // exactly what was clicked — no second path that writes the filter directly.
  const acceptRestriction = (s, mode, position) => {
    closeSuggest()
    const m = entrySearch.match(/[\p{L}\p{N}]+$/u)
    const head = m ? entrySearch.slice(0, entrySearch.length - m[0].length) : entrySearch
    // COMMIT the compiler's own phrase — the filter is still written by exactly one path
    // (text → compile → fold), never by a direct setFilters call.
    const commitText = `${head}${restrictionPhrase(s.display, mode)} `
    logSearchQuery({ rawText: entrySearch.trim(), compiled: [], residual: '', dismissed: [],
      context: { source: 'desk', event: 'suggest_click', suggestion_type: `restriction_${mode}`, position } })
    commitSearch(commitText, 'suggestion')
    // …but the box keeps only the REST of the query (owner-reported 2026-07-29).
    // commitSearch FOLDS the token into committed advFilters, so the subject now exists as
    // a chip. Leaving its word in the box made it exist TWICE, and the two could not be
    // edited as one: backspacing shrank the text while the committed chip stood, so the
    // leftover ("ინდ") ran as a FREE-TEXT search next to a live restriction filter — two
    // filters, one of which the agent was trying to delete, ending in "no results for
    // ინდ" with the chip still applied. Some prefixes also re-matched through the
    // stemmer, minting a second chip in front of the word instead of deleting it.
    // The chip is now the single representation; its ✕ is how you remove it. This is the
    // opposite of the place/district suggestions, which REPLACE the box with a canonical
    // phrase — there the text is the representation, and there is no committed twin.
    setEntrySearch(head)
  }
  // Suggestion click = commit (§11 selection behavior). Places/complexes REPLACE the box
  // with the canonical phrase (Zillow model — the near/district recognizers then compile
  // it into the right chip); clients/business also switch the category. Every click logs
  // a §13 follow-up row with type+position — the per-type click-through rate is what
  // decides which arms deserve investment.
  const acceptSuggestion = (type, position, phrase, extra = {}) => {
    // 📌 A probe lived on this line from 2026-08-14 to 2026-08-16 (B713) and it earned its
    // keep, so what it proved is written down instead: the row DOES dispatch through here,
    // and the observation is what turned "the metro row is dead" into "the metro row commits
    // the wrong filter" — which is a different bug with a different fix (see acceptPlace,
    // B762b). The lesson worth keeping is that four prior fixes to the street path were all
    // inert because reachability was inferred from `grep`; one console.log settled it in one
    // click. Reach for that earlier next time, and delete it once it has answered.
    // ⚠️ The paired `[acceptStreet]` probe is still live and still unanswered — it stays
    // until B705/B711 close. Do not remove it as a matching pair with this one.
    closeSuggest()   // any suggestion tap commits → the suggest surface goes away
    logSearchQuery({ rawText: entrySearch.trim(), compiled: [], residual: '', dismissed: [], context: { source: 'desk', event: 'suggest_click', suggestion_type: type, position } })
    if (extra.category) setEntryTypeFilter([extra.category])
    if (extra.category || extra.geoNoise) {
      // A client/business suggestion puts a PERSON/BUSINESS NAME in the box — geo tokens
      // the compiler finds inside it are noise that FILTERS OUT the very record we're
      // navigating to (found live 2026-07-17: "Giorgi Beridze" compiled a "Giorgi
      // Chqondideli District" chip that hid client Giorgi behind a district filter).
      // STREET clicks hit the same trap ("ილია ჭავჭავაძის გამზირი" re-compiled the
      // Chavchavadze DISTRICT chip — wrong district, avenue is in Vake — and killed
      // every listing), so they pass geoNoise. Pre-dismiss; the dismissal is logged
      // like any chip removal, so the compiler review query still sees the false positive.
      const c2 = compileQuery(phrase, { districts, locations: geoLocations, features, conditionCodes: ladderCodes, isSalePrice })
      // engine v18: restriction subjects join the noise set for the same reason — a
      // person/business/street NAME that happens to contain a subject word ("ოჯახი",
      // "ქალი") would filter out the very record we are navigating to.
      const noisy = new Set(c2.tokens.filter(tk => tk.type === 'district' || tk.type === 'near_location' || tk.type === 'restriction').map(tk => tk.sig))
      if (noisy.size) setDismissedSigs(s => new Set([...s, ...noisy]))
      setEntrySearch(phrase + ' ')
      commitSearch(phrase, 'suggestion', { ignoreSigs: noisy })
      return
    }
    setEntrySearch(phrase + ' ')
    commitSearch(phrase, 'suggestion')
  }
  // Street suggestion click → resolve the street's geo (precise resolver) and make a
  // PROXIMITY chip, instead of dumping the name as a text search. The clicked row and the
  // resolver read the same gazetteer, so we match the exact clicked name_ka for its point;
  // clearing the box means the chip is the sole filter (no residual re-compiling a wrong
  // district — the trap the geoNoise path guarded). Any resolver miss (no geo / latin-only /
  // network) falls back to the original text-search suggestion behavior, so nothing regresses.
  // 📍 A PLACE ROW COMMITS ITS OWN POINT (B762b, 2026-08-16) — the same rule as acceptStreet
  // one function below, arrived at the same way and for the same reason.
  //
  // What it replaces: this row used to call `acceptSuggestion('place', …)`, which wrote the
  // location's DISPLAY NAME back into the box and let `compileQuery` decide what it meant.
  // District beats place in v10's arbitration, so clicking `Delisi · metro` produced
  // `district=TBS_DELISI` — a code carried by ZERO active properties (B761) — and the Desk
  // answered 0 ლიდები / 0 ობიექტები with 1,191 shelved. Measured live, not inferred.
  // The row whose entire purpose is to say "I meant the STATION, not the district" was
  // saying it by typing the one word the compiler already reads as the district.
  //
  // A picked row is not a query. It carries `id`, `type` and the point itself, so it commits
  // `near` straight into advFilters — exactly what a typed near token folds to in
  // commitSearch, and read by the SAME `near_lat/near_lng/near_radius_m` server params. The
  // radius is NEAR_SEARCH_RADIUS_M's WALK tier (metro 300m ≈ 4 minutes), never the BUS tier
  // that card context uses — do not harmonize those two maps, the difference is the ruling.
  // The chip label comes from the compiler's own NEAR_TYPE_EMOJI so a clicked place and a
  // typed one are indistinguishable afterwards; the committed-near chip already renders it
  // with `clear: { near: null }`, so removal needs nothing new.
  //
  // ⚠️ THE BOX IS CLEARED, like acceptStreet's. Leaving "Delisi" in it would re-compile the
  // district chip on the next commit and re-create the empty-set filter this exists to
  // avoid — the geoNoise trap, reached from the other side.
  // ⚠️ A location with no point falls back to the old text path. A place without coordinates
  // cannot be a proximity filter, and inventing one would be worse than the name search.
  const acceptPlace = (p, position) => {
    if (p?.lat == null || p?.lng == null) return acceptSuggestion('place', position, p.display, { geoNoise: true })
    closeSuggest()
    logSearchQuery({ rawText: entrySearch.trim(), compiled: [], residual: '', dismissed: [], context: { source: 'desk', event: 'suggest_click', suggestion_type: 'place', position } })
    setAdvFilters(f => ({
      ...f,
      near: { id: p.id, lat: p.lat, lng: p.lng,
        radiusM: NEAR_SEARCH_RADIUS_M[p.type] || 1000,
        label: `${NEAR_TYPE_EMOJI[p.type] || '📍'} ${p.name || p.display}` },
    }))
    setEntrySearch('')
    enterBrowse()
  }
  // 0729: a complex click commits the ID as a structured filter — NEVER the name as text.
  // The name used to be pasted into the box, where no recognizer claims it (28 complex names
  // contain "Lisi", 10 "Isani", 7 "Vake" — all district keywords, so a text recognizer was
  // measured and rejected), and it fell through as residual free text: an 11s full-corpus
  // scan returning MORE than the unfiltered corpus (3,812 vs 3,805 — Georgian adverts write
  // ჰუალინგი, the row says "Hualing"). The clicked row already knows its id; ask p_complex_ids
  // the exact question, like the street path above learned in B705.
  const acceptComplex = (c, position) => {
    closeSuggest()
    logSearchQuery({ rawText: entrySearch.trim(), compiled: [], residual: '', dismissed: [], context: { source: 'desk', event: 'suggest_click', suggestion_type: 'complex', position } })
    // The chip repeats what the agent read on the row they clicked — interface language
    // first — not the storage-primary Latin name.
    setComplexLabels(m => (m[c.id] ? m : { ...m, [c.id]: (getLanguage() === 'ka' ? (c.name_ka || c.name) : c.name) }))
    setAdvFilters(f => ({ ...f, complexIds: [...new Set([...(f.complexIds || []), c.id])] }))
    setEntrySearch('')
    enterBrowse()
  }
  // The family row ("all Archi · 34"): one developer's every project, in one click. Grouped
  // by the developer COLUMN, never by name-prefix — Anagi's "Lisi Green Tower" must not ride
  // along with Lisi Development's family. p_complex_ids already takes a list.
  const acceptComplexFamily = async (developer, position) => {
    closeSuggest()
    logSearchQuery({ rawText: entrySearch.trim(), compiled: [], residual: '', dismissed: [], context: { source: 'desk', event: 'suggest_click', suggestion_type: 'complex_family', position } })
    try {
      const rows = await getComplexesByDeveloper(developer)
      if (!rows.length) return
      setComplexLabels(m => { const next = { ...m }; for (const r of rows) if (!next[r.id]) next[r.id] = r.name; return next })
      setAdvFilters(f => ({ ...f, complexIds: [...new Set([...(f.complexIds || []), ...rows.map(r => r.id)])] }))
      setEntrySearch('')
      enterBrowse()
    } catch (e) { console.error('[suggest] complex family:', e.message) }
  }
  const acceptStreet = async (s, position) => {
    // 📌 A probe sat on this line 2026-08-14 → 08-17 (B705/B711/B713). Removed with B705 still
    // open, because it had stopped being able to answer anything: its one "silent on a real
    // click" reading was later shown to be a MISSED CLICK, not a missed handler — a synthetic
    // click that lands outside the row logs nothing and looks exactly like a dead path. Its
    // paired probe in acceptSuggestion did fire and named the dispatch, which is how the row
    // descriptors were confirmed to reach these handlers at all.
    // What survives is the lesson, not the line: four fixes here were inert because
    // reachability was inferred from `grep`, and one console.log settled it in a click. Reach
    // for that first — and make the click land before trusting the silence.
    closeSuggest()
    logSearchQuery({ rawText: entrySearch.trim(), compiled: [], residual: '', dismissed: [], context: { source: 'desk', event: 'suggest_click', suggestion_type: 'street', position } })
    // 🚨 THE CLICKED ROW ALREADY KNOWS ITS STREET. The dropdown builds each street row as
    // `key: st${s.id}` — the suggest response carries the id, and it is the id of the row
    // the agent actually looked at and chose. The previous version threw it away and asked
    // the RESOLVER to find the street again from its NAME, which is both a wasted round
    // trip and a chance to answer with a different street than the one that was clicked:
    // `ნუცუბიძის` alone resolves to three rows, and the resolver dedups on coordinates.
    // Ask the same question the sidebar facet asks (p_street_ids), with the id we were
    // handed. Exact identity, no name matching anywhere in the path.
    if (s.id) {
      setStreetLabels(m => (m[s.id] ? m : { ...m, [s.id]: s.name }))
      setAdvFilters(f => ({ ...f, streetIds: [...new Set([...(f.streetIds || []), s.id])] }))
      setEntrySearch('')
      enterBrowse()
      return
    }
    try {
      const r = await resolveStreetQuery(s.name)
      const hit = (r?.candidates || []).find(c => c.name_ka === s.name) || r?.best
      // 🚨 B705 — THE STREET ID, NOT A CIRCLE ROUND ITS CENTRE (owner: "ნუციბიძის ქუჩა და
      // პლატო სხვადასხვაა"). This path used to attach the resolved POINT and filter by
      // proximity, and the "is it actually on this street" test was `streetCoreToken` —
      // the street's most-identifying token, substring-matched against the address. For
      // `შ. ნუცუბიძის ქ.` that token is `ნუცუბიძის`, which is also inside
      // `ნუცუბიძის პლატო` and `ნუცუბიძის ფერდობი`, so plateau listings were reported as
      // exactly ON the street. The links were never wrong — all 751 property_street rows
      // across both Nutsubidze street rows name the street properly and none name the
      // plateau — the CLIENT was guessing next to a system of record.
      // The resolver already selects `id` (core_street.js: select=id,name_ka,…), so the
      // box can ask the identical question the sidebar facet asks: p_street_ids. Exact
      // match, no token, no radius, and one implementation of "on this street" instead of
      // two that disagree.
      // ⚠️ Additive, and deliberately so: picking a second street from the box widens the
      // selection exactly as ticking a second checkbox does. Same filter, same semantics,
      // whichever surface the agent reached it from.
      if (hit && hit.id) {
        // The label travels with the id — `street` lives in core and the chip would
        // otherwise render a UUID until the next search response carried its name.
        setStreetLabels(m => (m[hit.id] ? m : { ...m, [hit.id]: hit.name_ka }))
        setAdvFilters(f => ({ ...f, streetIds: [...new Set([...(f.streetIds || []), hit.id])] }))
        setEntrySearch('')
        enterBrowse()
        return
      }
      // Resolver returned a point but no id (older rows, latin-only queries): the old
      // proximity chip still answers, imprecisely, rather than nothing at all.
      if (hit && hit.lat != null && hit.lng != null) {
        setStreetSel({ name_ka: hit.name_ka, district: hit.district || s.district || null, lat: hit.lat, lng: hit.lng })
        setEntrySearch('')
        // This path CLEARS the box and filters via the chip alone, so it never reaches
        // commitSearch — it must leave home on its own or the chip would filter a cockpit
        // the agent can't see the results in. (Under the old derived gate streetSel wasn't
        // in the eligibility set at all, so a street picked from home silently did nothing.)
        enterBrowse()
        return
      }
    } catch { /* fall through to the original text-search behavior */ }
    acceptSuggestion('street', position, s.name.replace(/\s*#\S*$/, '').trim(), { geoNoise: true })
  }
  // ── The draft/committed split (§2.3, 2a) ──────────────────────────────────
  // Token compilation NO LONGER writes advFilters. The old prevTokensRef diff effect
  // (apply added tokens' patches / revert removed ones', per keystroke) is replaced by a
  // PURE derivation: draftAdvFilters = committed advFilters ⊎ live token overlay
  // (applyTokenOverlay — arrays union, unset scalars take the token value). The diff
  // effect's whole removal bookkeeping (a vanished token had to un-patch exactly its own
  // fields or an invisible filter survived) is unnecessary by construction — a token that
  // leaves activeTokens (deleted text / dismissed chip) simply stops being merged.
  //   draftAdvFilters — CLIENT-side live narrowing + display only: visibleResult classify,
  //     constraint chips, mobile facet pills. Never serialized, never read by isHome.
  //   advFilters      — COMMITTED: every server fetch (advFiltersDebounced → effAdvFilters),
  //     the panel, isHome. Written only by commit acts (panel edit, chip removal,
  //     commitSearch's fold, goDeskHome/clear-all, currency + committedQ syncs).
  // ⚠️ HIGHER HIERARCHY WINS applies HERE TOO (C1, 2026-08-01). `applicableFilters` was added
  // at `effAdvFilters` (server query) and at the chip row, but NOT on this edge — and this is
  // the one that feeds `feedPipeline`/`visibleResult`, i.e. the rendered feed. Measured against
  // the real pipeline: with `kind=land` + a held `rooms=2`, every land row (rooms is null on
  // 891/891 live land rows) classified 'unknown' and was pushed to the possible-matches shelf,
  // so the confirmed feed was EMPTY while the panel had correctly hidden the Rooms control.
  // Applied at the DEFINITION, not at the feedPipeline call, so a fourth consumer cannot
  // repeat the bug. `advFilters` (state) is untouched — ignoring is still not deleting.
  // ⚠️ landedTokens since 2026-08-05 (owner ruling — see landedSearch): the overlay that
  // reaches the FEED is the committed text's tokens, so typing no longer re-cuts the cards.
  // The name "draft" survives because this is still draft RELATIVE TO advFilters state —
  // the fold only happens at commit; a dismissed chip still un-merges instantly.
  const draftAdvFilters = useMemo(
    () => applicableFilters(applyTokenOverlay(advFilters, landedTokens), entryTypeFilter[0] || 'Property', ADV_DEFAULTS, SERVER_ONLY_ADV_KEYS),
    [advFilters, landedTokens, entryTypeFilter])
  // B05 (2026-10-23 audit): bounds that are set but are not numbers. core.js refuses to search with
  // them; the feed area names them (see the alert above ResultCount).
  const invalidBounds = useMemo(() => invalidNumericBounds(draftAdvFilters), [draftAdvFilters])
  // The mobile location pill's count: ✓, ✕ and both own-bucket arrays (2026-10-22; it counted ✓ only).
  const districtPickCount = DISTRICT_KEYS.reduce((s, k) => s + (draftAdvFilters[k] || []).length, 0)
  // What the chosen identity can be ASKED — the same `interviewCapability` the panel derives
  // for itself (see its `cap`), computed here because the mobile pill row lives outside the
  // panel and must not invent a second answer to "does this identity have rooms?".
  // ⚠️ Declared beside draftAdvFilters, far above the pill row that reads it: this file's
  // declare-above-the-first-consumer rule, whose ledger of TDZ crashes is in the comments at
  // `agentCounts` and `propertyTotalForTree`. Build and tests cannot see that class.
  const pillCap = useMemo(() => {
    const p = deriveIdentityPath(draftAdvFilters)
    return interviewCapability({
      kind: p.kind, subtype: p.subtype,
      intents: draftAdvFilters.intents || [],
      objectType: entryTypeFilter[0] || 'Property',
    })
  }, [draftAdvFilters, entryTypeFilter])
  useEffect(() => { if (!entrySearch.trim()) setDismissedSigs(new Set()) }, [entrySearch])

  // ── Constraint chips — the single display of the WHOLE active filter state ──
  // Derived from advFilters/entryCityFilter themselves, not from the compile: a chip renders
  // whether the constraint came from typed text or the Filters drawer, so there is exactly one
  // place that shows what's filtering right now (the UI never lies about state). Removing a
  // chip clears exactly that field; if the field was set by a still-present search token, the
  // token is also dismissed (and logged) so it can't re-apply on the next keystroke.
  // Default OPEN since the 2026-07-06 restructure — the search tools are a persistent left
  // sidebar. Its own ◂ arrow collapses it to a thin rail (never fully gone); the rail's ▸
  // reopens it. No strip pill anymore (removed 2026-07-06).
  // Detailed-search panel: open by default on desktop (persistent sidebar), CLOSED on
  // mobile (2026-07-18 user ruling — eBay model: a small pill opens it, the panel's own
  // "show results" button closes it back to the feed).
  const [filtersOpen, setFiltersOpen] = useState(() =>
    typeof window === 'undefined' || window.matchMedia('(min-width: 768px)').matches)
  // Which facet's bottom sheet a strip pill has open (mobile pill slider, 2026-07-18) —
  // an AdvancedSearchPanel sheetOnly instance renders it; null = none.
  const [facetSheet, setFacetSheet] = useState(null)
  // Language-appropriate DISPLAY label for a city option/chip. The VALUE stays the canonical
  // Georgian-preferred label (entryCityFilter, cityTotals keys, cityKey comparisons against
  // Georgian address prefixes all key off it) — only the visible text follows the ქარ/ENG
  // toggle. Resolves through the city-level district rows (MUN_/TWN_), same lookup
  // districtInCity uses.
  const cityDisplayLabel = c => {
    if (lang === 'ka') return c
    const k = cityKey(c)
    if (k === cityKey('თბილისი')) return 'Tbilisi'
    const row = districts.find(r => /^(MUN_|TWN_)/.test(r.code) && [r.name_ka, r.name_en].some(x => x && cityKey(x) === k))
    return row?.name_en || c
  }
  const districtDisplayLabel = d => (lang === 'ka' ? (d?.name_ka || d?.name_en) : (d?.name_en || d?.name_ka))
  const chipModel = useMemo(() => {
    // DRAFT merge (2a): chips are "the single display of the WHOLE active filter state",
    // and live token patches now exist only in the draft overlay — chip from it, so a
    // typed "ვაკე" still shows its district chip before Enter commits it.
    // ⚠️ …and a filter the chosen identity IGNORES must not render a chip either (owner
    // ruling 2026-08-01): a chip asserts an ACTIVE constraint, so showing "Rooms 2" beside a
    // Land search would claim a narrowing that is deliberately not being applied. Same
    // `applicableFilters` the query uses — one rule, so chips and results cannot disagree.
    // ⚠️ B347 (owner ruling 2026-08-06): *"Selected filters must survive context changes, but
    // impossible filters must become visible, inactive, and removable."* The field half shipped
    // that day — a stranded value is stripped from the query and kept in state — but it was
    // stripped from THIS row too, so the agent got a different result set with no statement of
    // why, and the only way back was to guess which control still held the value.
    //
    // So chip from the RAW model and PARTITION at the end. The stranded chip is then built by
    // the same code, from the same vocabulary, as its active twin — a second labeller for the
    // disabled row is exactly how two descriptions of one filter drift apart (the age-preset
    // note below records that failure in miniature).
    const norm = normalizeAdvFilters(draftAdvFilters)   // a vacuous 0-min must not render a chip either
    const objectType = entryTypeFilter[0] || 'Property'
    const f = norm
    const chips = []
    const rangeChip = (key, name, lo, hi, fmtV = v => v, editKey = key) => {
      if ((lo ?? '') === '' && (hi ?? '') === '') return
      lo = lo ?? ''; hi = hi ?? ''
      const label = (lo !== '' && hi !== '' ? (lo === hi ? `${name} ${fmtV(lo)}` : `${name} ${fmtV(lo)}–${fmtV(hi)}`)
        : lo !== '' ? `${name} ≥ ${fmtV(lo)}` : `${name} ≤ ${fmtV(hi)}`).trim()
      // editKey = the STABLE identifier the editor registry keys off (owner ruling 2026-07-24:
      // a chip describes a constraint, it does not know how to edit itself). Range chips are
      // named for their facet, so the chip key IS the edit key — no second vocabulary.
      chips.push({ key, label, editKey, clear: { [`${key}Min`]: '', [`${key}Max`]: '' } })
    }
    // Checkbox facets (2026-07-17): one chip PER selected value — removing a chip unchecks
    // exactly that box (removeChip's arrayKey branch), never the whole facet.
    for (const v of (f.intents || [])) chips.push({ key: `intent:${v}`, label: LISTING_INTENT_OPTIONS.find(o => o.value === v)?.label || v, arrayKey: 'intents', value: v, editKey: 'intents', clear: {} })
    for (const v of (f.kinds || [])) chips.push({ key: `kind:${v}`, label: PROPERTY_CATEGORY_KEYS[v] ? t(PROPERTY_CATEGORY_KEYS[v]) : v, arrayKey: 'kinds', value: v, editKey: 'kinds', clear: {} })
    // B265: subtype chips — one per selected token, removable individually. editKey points
    // at the Type facet, where the chips render under their kind.
    for (const v of (f.subtypes || [])) chips.push({ key: `subtype:${v}`, label: t('desk_subtype_' + v), arrayKey: 'subtypes', value: v, editKey: 'kinds', clear: {} })
    chips.push(...sellerChips(f, t))   // 1685 Seller / Contact / Agent represents — one chip per tick
    // B1213 (2026-09-06): the ✕ chips — one per excluded value of every facet the spec gives an exclude half,
    // labelled by the same rule as the facet's ✓ chip and removable one at a time (arrayKey → removeChip).
    {
      const labelOf = {
        sources: v => (SOURCE_FACET_OPTIONS.find(([c]) => c === v) || [])[1] || v,
        districtCodes: v => { const d = districts.find(x => x.code === v); return `📍 ${districtDisplayLabel(d) || v}` },   // the ✓ chip's own rule
        // B1216 flow 8 (2026-09-05): the own bucket's ✕ must say OWN, as its ✓ twin does — "✕ Saburtalo" and
        // "✕ Saburtalo (own)" are different searches and read identically otherwise
        districtOwnCodes: v => { const d = districts.find(x => x.code === v); return `📍 ${tLog('desk_chip_district_own', { name: districtDisplayLabel(d) || v })}` },
        agentIds: v => `👤 ${allAgents.find(x => x.id === v)?.name || t('desk_ranking_unknown')}`,
        ageKinds: v => t('desk_age_kind_' + v),
        footprint: v => t('desk_footprint_' + v),
        subtypes: v => t('desk_subtype_' + v),
      }
      for (const [incKey, excKey] of excludePairs()) {
        if (isFlagFacet(incKey)) {   // a flag's ✕ half is a boolean: one chip, cleared by its own key
          if (f[excKey] === true) chips.push({ key: excKey, label: `✕ ${incKey === 'topFloor' ? '⬆ ' + t('desk_top_floor') : incKey === 'firstFloor' ? '⬇ ' + t('desk_first_floor') : t(excKey)}`, editKey: incKey, clear: { [excKey]: false } })
          continue
        }
        for (const v of (f[excKey] || [])) chips.push({ key: `${excKey}:${v}`, label: `✕ ${(labelOf[incKey] || (x => x))(v)}`, arrayKey: excKey, value: v, clear: {} })
      }
    }
    if (entryCityFilter !== 'all') chips.push({ key: 'city', label: `🏙 ${cityDisplayLabel(entryCityFilter)}`, clear: null })
    // The admin's agent scope (2026-08-09). One chip per selected agent, removable
    // individually — `arrayKey` routes it through removeChip's existing array branch, so this
    // needed no new removal path. It sits beside the city chip because both answer "which
    // pile", not "which property".
    // ⚠️ Never render the raw id. `pending` (same rule as districts) holds the chip back from
    // the search bar while the roster is still loading, and an id the loaded roster cannot
    // name falls back to the roster's own unknown label rather than printing a uuid on a
    // Georgian screen. The chip still COUNTS either way — hiding an active filter is the
    // worse lie, and this filter's entire bug history is having been invisible.
    for (const id of (f.agentIds || [])) {
      const a = allAgents.find(x => x.id === id)
      chips.push({ key: `agent:${id}`, label: `👤 ${a?.name || t('desk_ranking_unknown')}`,
        pending: !a && allAgents.length === 0, arrayKey: 'agentIds', value: id, clear: {} })
    }
    // Streets (0537/0538). Same shape as the district chip below and for the same reason —
    // unticking in the facet and removing here must be one action. The label comes from the
    // sticky map because the counts that carried it are gone as soon as the street is
    // selected; `pending` holds the chip out of the bar until a name exists, so a UUID never
    // reaches a Georgian screen, while the filter itself still counts as active.
    for (const id of (f.streetIds || [])) {
      const label = streetLabels[id]
      chips.push({ key: `street:${id}`, label: `🛣 ${label || t('desk_facet_street')}`,
        pending: !label, arrayKey: 'streetIds', value: id, editKey: 'streetIds', clear: {} })
    }
    // 0729: same rule as the street chip — one chip per selected development, label from the
    // sticky map (filled at click; backfilled from getComplexesByIds after a URL restore), and
    // `pending` keeps a UUID off a Georgian screen for the first render.
    for (const id of (f.complexIds || [])) {
      const label = complexLabels[id]
      chips.push({ key: `complex:${id}`, label: `🏢 ${label || t('desk_fieldlabel_complexes')}`,
        pending: !label, arrayKey: 'complexIds', value: id, editKey: 'complexIds', clear: {} })
    }
    // Metro (0688). Same rule as the street chip above: unticking in the facet and removing
    // here are one action, and a UUID must never reach the bar — the label comes from the
    // station catalogue the facet already loaded, so `pending` covers the first render.
    for (const id of (f.metroIds || [])) {
      const s = (metroStations || []).find(x => x.id === id)
      const label = s && (getLanguage() === 'ka' ? (s.name_ka || s.name_en) : (s.name_en || s.name_ka))
      chips.push({ key: `metro:${id}`, label: `Ⓜ ${label || t('desk_facet_metro')}`,
        pending: !label, arrayKey: 'metroIds', value: id, editKey: 'metroIds', clear: {} })
    }
    // The OWN bucket had no chip at all (found 2026-08-12) — an active filter with no visible
    // trace, which is the failure the agent-filter chip note above was written about. It also
    // could not simply reuse the district chip: "Vake" and "Vake, sub-districts excluded" are
    // DIFFERENT searches and two identical pills would be a lie about which one is running.
    // Same removal path as its sibling (`arrayKey`), so no new code handles the ✕.
    for (const code of (f.districtOwnCodes || [])) {
      const d = districts.find(x => x.code === code)
      chips.push({ key: `district_own:${code}`,
        label: `📍 ${tLog('desk_chip_district_own', { name: districtDisplayLabel(d) || code })}`,
        pending: !d && districts.length === 0, arrayKey: 'districtOwnCodes', value: code,
        editKey: 'districtCodes', clear: {} })
    }
    for (const code of (f.districtCodes || [])) {
      const d = districts.find(x => x.code === code)
      // ⚠️ `pending` = the districts dictionary hasn't landed yet, so the only label we could
      // print is the raw code. Chips still count (rail badge, mobile pills), but the search
      // bar skips them until the name resolves — a bar keyword is READ, and "TBS_SABURTALO"
      // flashing in the field breaks the vocabulary rule that no normalized token reaches the
      // screen (caught 2026-07-24 right after the keywords went in). Once the dictionary IS
      // loaded, an unknown code still shows: hiding an active filter is the worse lie.
      chips.push({ key: `district:${code}`, label: `📍 ${districtDisplayLabel(d) || code}`, pending: !d && districts.length === 0, arrayKey: 'districtCodes', value: code, editKey: 'districtCodes', clear: {} })
    }
    const money = v => `${Number(v).toLocaleString('en-US')} ${f.currency}`
    rangeChip('price', '', f.priceMin, f.priceMax, money)
    // core 3872: price per m² — its chip opens the Price facet, where its boxes are
    rangeChip('ppm', '', f.ppmMin, f.ppmMax, v => `${Number(v).toLocaleString('en-US')} ${f.currency}/მ²`, 'price')
    rangeChip('area', 'm²', f.areaMin, f.areaMax)
    rangeChip('landArea', t('desk_range_land_area'), f.landAreaMin, f.landAreaMax)
    for (const n of [...(f.roomsSet || [])].sort((a, b) => a - b))
      chips.push({ key: `rooms:${n}`, label: `${t('desk_range_rooms')} ${n === 5 ? '5+' : n}`, arrayKey: 'roomsSet', value: n, editKey: 'roomsSet', clear: {} })
    for (const n of [...(f.bedroomsSet || [])].sort((a, b) => a - b))
      chips.push({ key: `bedrooms:${n}`, label: `${t('desk_range_bedrooms')} ${n === 5 ? '5+' : n}`, arrayKey: 'bedroomsSet', value: n, editKey: 'bedroomsSet', clear: {} })
    rangeChip('bathrooms', t('desk_range_bathrooms'), f.bathroomsMin, f.bathroomsMax)
    rangeChip('balconyCount', t('desk_range_balcony_count'), f.balconyCountMin, f.balconyCountMax)   // 0757
    rangeChip('floor', t('desk_range_floor'), f.floorMin, f.floorMax)
    rangeChip('floorsTotal', t('desk_range_total_floors'), f.floorsTotalMin, f.floorsTotalMax)
    if (f.topFloor) chips.push({ key: 'topFloor', label: `⬆ ${t('desk_top_floor')}`, editKey: 'topFloor', clear: { topFloor: false } })
    if (f.firstFloor) chips.push({ key: 'firstFloor', label: `⬇ ${t('desk_first_floor')}`, editKey: 'firstFloor', clear: { firstFloor: false } })
    // Overlay-fact filters (B177): one chip per excluded restriction subject (labelled from
    // the feature vocab like the client form's household chips; raw code only when the vocab
    // hasn't landed) + the two payment excludes. All exclude-known-bad — see deskFeed.js.
    // One chip per restriction entry, labelled by MODE so the bar says what the filter
    // actually does — a bare 🚫 name could not distinguish "hide these" from "only these".
    // arrayKey/value carry the full "code:mode" string, so removeChip drops exactly this
    // entry and leaves any other subject's filter alone.
    // ⚠️ The glyph comes from restrictionGlyph (searchCompiler) and is NOT re-derived here.
    // It used to be `mode === 'hide' ? not_allowed : allowed`, written when `hide` meant
    // "exclude these". The 2026-07-28 ruling redefined `hide` as "find the ones who
    // ACCEPT", which silently inverted this label: picking the ✓ chip drew ✗ while the
    // compiler's own token drew ✓ for the same filter (owner-reported 2026-07-29).
    for (const { code, mode } of parseRestrictions(f.restrictions)) {
      const feat = features.find(x => x.code === code)
      const nm = feat?.name_ka || feat?.name_en || code
      chips.push({
        key: `restr:${code}:${mode}`,
        label: `${restrictionGlyph(mode)} ${nm}`,
        arrayKey: 'restrictions', value: `${code}:${mode}`, editKey: 'restrictions', clear: {},
      })
    }
    // Feature chips (B37) — one per checked amenity, labelled from the same vocabulary rows
    // the facet renders. ✚ ("with") and NOT ✓: the tick belongs to the restriction family
    // directly above, where it means an owner's recorded stance about a person, and the two
    // chip kinds share this row. The glyph matches the compiler's own feature token, so a
    // typed "ლიფტი" and a clicked box produce the identical chip.
    for (const code of (f.featureCodes || [])) {
      const feat = features.find(x => x.code === code)
      // The apart-hotel context speaks with the tree's own word, so the chip and the row that
      // wrote it say the same thing (and it never falls back to the raw code).
      const ctxNode = Object.keys(APARTMENT_CONTEXT).find(n => APARTMENT_CONTEXT[n] === code)
      chips.push({
        key: `feature:${code}`,
        label: `✚ ${ctxNode ? t('desk_context_' + ctxNode) : (feat?.name_ka || feat?.name_en || code)}`,
        arrayKey: 'featureCodes', value: code, editKey: 'featureCodes', clear: {},
      })
    }
    // 0756 — the same features asked with a POLARITY. Three glyphs, because a chip reading
    // "✚ ლიფტი" for "does NOT have a lift" would state the opposite of the filter, and the chip
    // row is where an agent checks what they actually asked for.
    //   ✚ has it   ✖ does not have it   ? not declared
    // ⚠️ The value is the FULL "code:state" entry, so removing the chip removes exactly this
    // question and not the code under some other polarity.
    for (const entry of (f.featureStates || [])) {
      const [code, want] = String(entry).split(':')
      const feat = features.find(x => x.code === code)
      const glyph = want === 'no' ? '✖' : want === 'unknown' ? '?' : '✚'
      chips.push({
        key: `featureState:${entry}`,
        label: `${glyph} ${feat?.name_ka || feat?.name_en || code}`,
        title: t('desk_feature_state_' + (want || 'unset')),
        arrayKey: 'featureStates', value: entry, editKey: 'featureStates', clear: {},
      })
    }
    // State chips (0433) — one per ticked rung, from the same vocabulary rows the facet
    // renders. ◈ rather than ✚ deliberately: ✚ means "and also has", and these compose the
    // other way. Two state chips are an OR, and a reader who saw them wearing the amenity
    // glyph would read the row as a conjunction the search never asked for.
    for (const code of (f.conditionCodes || [])) {
      const feat = features.find(x => x.code === code)
      chips.push({
        key: `condition:${code}`,
        label: `◈ ${feat?.name_ka || feat?.name_en || code}`,
        arrayKey: 'conditionCodes', value: code, editKey: 'conditionCodes', clear: {},
      })
    }
    if ((f.advanceMaxMonths ?? '') !== '') chips.push({ key: 'advanceMax', label: `${t('desk_filter_advance_chip')} ≤ ${f.advanceMaxMonths} ${t('desk_filter_advance_month_unit')}`, editKey: 'advanceMaxMonths', clear: { advanceMaxMonths: '' } })
    if ((f.minLeaseStay ?? '') !== '') chips.push({ key: 'minLeaseStay', label: `${t('desk_filter_minlease_chip')} ${f.minLeaseStay} ${t('desk_filter_advance_month_unit')}`, editKey: 'minLeaseStay', clear: { minLeaseStay: '' } })
    if ((f.moveInBy ?? '') !== '') chips.push({ key: 'moveInBy', label: `${t('desk_filter_movein_chip')} ${f.moveInBy}${f.moveInHard ? ' !' : ''}`, editKey: 'moveInBy', clear: { moveInBy: '', moveInHard: false } })
    // 0249. ONE chip for the date range, because the range is one constraint — and it names
    // its DIMENSION, since "2026-07-01 → 2026-07-20" alone would not say whether it means
    // when we saw it or when the owner posted it. Clearing it drops the bounds and leaves
    // dateField at its default; a dimension with no range selects nothing (canonicalFilters
    // strips it), so there is no orphan state to clean up.
    if ((f.dateFrom ?? '') !== '' || (f.dateTo ?? '') !== '') {
      chips.push({
        key: 'dateRange',
        label: `${t('desk_filter_date_' + (f.dateField || 'added'))}: ${f.dateFrom || '…'} → ${f.dateTo || '…'}`,
        editKey: 'dateFrom', clear: { dateFrom: '', dateTo: '' },
      })
    }
    // 2026-10-22: four filters that narrowed the feed with no chip (audit). B1188's dateRanges get
    // one chip per dimension, cleared to the remaining dimensions (the mapArea rule); ✓ sources one
    // per portal like their ✕ twins; addedHow one; the condition ladder's struck rungs one each.
    for (const [dim, r] of Object.entries(f.dateRanges || {})) {
      if (!r || (!r.from && !r.to)) continue
      const rest = { ...f.dateRanges }; delete rest[dim]
      chips.push({ key: `dateRanges:${dim}`, label: `${t('desk_filter_date_' + dim)}: ${r.from || '…'} → ${r.to || '…'}`,
        editKey: 'dateRanges', clear: { dateRanges: rest } })
    }
    for (const v of (f.sources || [])) chips.push({ key: `source:${v}`, label: sourceFacetLabel(v, listingsScope), arrayKey: 'sources', value: v, editKey: 'sources', clear: {} })
    if (f.addedHow) chips.push({ key: 'addedHow', label: `${t('desk_facet_added_how')}: ${t((ADDED_HOW_OPTIONS.find(([c]) => c === f.addedHow) || [])[1] || f.addedHow)}`, editKey: 'addedHow', clear: { addedHow: '' } })
    for (const code of (f.conditionExcludeCodes || [])) {
      const feat = features.find(x => x.code === code)
      chips.push({ key: `conditionNot:${code}`, label: `✕ ◈ ${feat?.name_ka || feat?.name_en || code}`,
        arrayKey: 'conditionExcludeCodes', value: code, editKey: 'conditionCodes', clear: {} })
    }
    // B215: '≤ rung' for a real threshold; the 0 stop is the minimum so '≤' would be noise.
    // 0431: rides ON the footprint chip rather than as its own — it qualifies those states
    // and says nothing alone, the same way moveInHard rides the move-in chip. Clearing the
    // activity rows clears it too, so the readout can never claim a modifier with nothing
    // to modify.
    if (f.keepMine && (f.footprint || []).length) chips.push({
      key: 'keepMine', label: t('desk_footprint_keep_mine_chip'),
      editKey: 'footprint', clear: { keepMine: false },
    })
    if ((f.exposureMax ?? '') !== '') chips.push({
      key: 'exposureMax',
      label: f.exposureMax === '0' ? t('desk_exposure_0') : `≤ ${t('desk_exposure_' + f.exposureMax)}`,
      editKey: 'exposureMax', clear: { exposureMax: '' },
    })
    // B213: the spread band chip reads as the band itself ("2–3"), no operator prefix.
    if (f.spread) chips.push({
      key: 'spread',
      label: `${t('desk_spread_chip')}: ${t('desk_spread_' + f.spread)}`,
      editKey: 'spread', clear: { spread: '' },
    })
    // 0303 — the spread replacements. Same band-chip form.
    if (f.reposts) chips.push({
      key: 'reposts', label: `${t('desk_reposts_chip')}: ${t('desk_reposts_' + f.reposts)}`,
      editKey: 'reposts', clear: { reposts: '' },
    })
    if (f.ownerPlatforms) chips.push({
      key: 'ownerPlatforms', label: `${t('desk_owner_platforms_chip')}: ${t('desk_owner_platforms_' + f.ownerPlatforms)}`,
      editKey: 'ownerPlatforms', clear: { ownerPlatforms: '' },
    })
    // ── Advert quality (v55) ─────────────────────────────────────────────────────────
    // Same rule as the hunter lenses above: an active filter with no chip is the failure
    // this row exists to prevent. All three narrow the feed, so all three get a chip.
    if (f.photos) chips.push({
      key: 'photos', label: `${t('desk_photos_chip')}: ${t('desk_photos_' + f.photos)}`,
      editKey: 'photos', clear: { photos: '' },
    })
    if (f.noDescription) chips.push({
      key: 'noDescription', label: t('desk_no_description'),
      editKey: 'noDescription', clear: { noDescription: false },
    })
    if (f.missingFacts) chips.push({
      key: 'missingFacts', label: t('desk_missing_facts'),
      editKey: 'missingFacts', clear: { missingFacts: false },
    })
    if ((f.claimsLt ?? '') !== '') chips.push({ key: 'claimsLt', label: `${t('desk_filter_claims_chip')} < ${Math.max(1, Number(f.claimsLt) || 0)}`, editKey: 'claimsLt', clear: { claimsLt: '' } })
    if ((f.claimsGt ?? '') !== '') chips.push({ key: 'claimsGt', label: `${t('desk_filter_claims_chip')} > ${f.claimsGt}`, editKey: 'claimsGt', clear: { claimsGt: '' } })
    if (SEEN_MODES.includes(f.seen)) chips.push({ key: 'seen', label: t('desk_filter_seen_' + f.seen), editKey: 'seen', clear: { seen: '' } })
    // ── 0294 hunter lenses ────────────────────────────────────────────────────────────
    // Every one gets a chip: a filter that is active but invisible in this row is the
    // failure the row exists to prevent.
    if (f.sellerType) chips.push({
      key: 'sellerType', label: `${t('desk_seller_chip')}: ${t('desk_seller_' + f.sellerType)}`,
      editKey: 'sellerType', clear: { sellerType: '' },
    })
    // One chip for the whole age question, like dateRange above — the bounds and the kind
    // are one thought, and clearing it must drop the kind too or an invisible modifier
    // survives on a range that no longer exists.
    if ((f.ageMinDays ?? '') !== '' || (f.ageMaxDays ?? '') !== '') {
      const kinds = (f.ageKinds || []).map(k => t('desk_age_kind_' + k)).join(', ')
      // When the range IS one of the presets, the chip says what the control said. A chip
      // reading "…–7" next to a box ticked "This week" describes the same filter in a second
      // vocabulary, and the reader has to work out that they are the same thing.
      const preset = AGE_PRESETS.find(p =>
        String(f.ageMinDays ?? '') === p.lo && String(f.ageMaxDays ?? '') === p.hi)
      const span = preset ? t('desk_age_preset_' + preset.key)
        : `${f.ageMinDays || '…'}–${f.ageMaxDays || '…'} ${t('desk_age_unit')}`
      chips.push({
        key: 'ageRange',
        label: `${t('desk_age_chip')}: ${span}${kinds ? ` (${kinds})` : ''}`,
        editKey: 'ageMinDays', clear: { ageMinDays: '', ageMaxDays: '', ageKinds: [], ageKindsNot: [] },
      })
    }
    if ((f.viewsMin ?? '') !== '') chips.push({
      key: 'viewsMin', label: `${t('desk_range_views')} ≥ ${f.viewsMin}`,
      editKey: 'viewsMin', clear: { viewsMin: '' },
    })
    if (f.promoted) chips.push({
      key: 'promoted', label: t('desk_promoted_chip_' + f.promoted),
      editKey: 'promoted', clear: { promoted: '' },
    })
    // Footprint is a set, so the chip names what is INCLUDED. All three selected is the
    // same answer as none selected, but it is still a committed state the agent chose —
    // chip it rather than second-guessing them.
    if ((f.footprint || []).length) chips.push({
      key: 'footprint',
      label: `${t('desk_footprint_chip')}: ${f.footprint.map(v => t('desk_footprint_' + v)).join(', ')}`,
      editKey: 'footprint', clear: { footprint: [] },
    })
    if (f.ownerEngaged) chips.push({
      key: 'ownerEngaged', label: t('desk_engaged_chip_' + f.ownerEngaged),
      editKey: 'ownerEngaged', clear: { ownerEngaged: '' },
    })
    // listing_id tokens patch NO advFilters field (identity filter, engine v5) — chip them
    // straight from the token so the row still shows every active constraint. Removal goes
    // through the token's dismissal sig, same as deleting the URL text from the box.
    // ⚠️ landedTokens (2026-08-05): a chip states what is FILTERING, and since the landing
    // ruling that is the committed text's tokens — a chip minted from a live token would
    // claim a filter that is not yet applied ("the UI never lies about state", both ways).
    for (const t of landedTokens) if (t.type === 'listing_id') {
      chips.push({ key: `listingid:${t.value.id}`, label: `🔗 ID ${t.value.id}`, sig: t.sig, clear: {} })
    }
    // near_location tokens (engine v7) — same token-only chip contract as listing_id.
    for (const t of landedTokens) if (t.type === 'near_location') {
      chips.push({ key: `near:${t.value.id}`, label: t.label, sig: t.sig, clear: {} })
    }
    // COMMITTED near (2a): near now lives in advFilters only via commitSearch's fold, so
    // once the box changes (its token gone) the committed distance filter would otherwise
    // be invisible — exactly the "invisible filter" class the old diff effect existed to
    // kill. Render it whenever no landed token already chips the same place; removing it is
    // a plain committed-field clear.
    if (advFilters.near && !landedTokens.some(t => t.type === 'near_location' && t.value.id === advFilters.near.id)) {
      // ⚠️ NAME THE PLACE, not just its radius (B762b). The URL carries near as
      // `lat,lng,radiusM,id` and CANNOT carry the label — a comma-split field cannot hold a
      // name that may contain a comma — so after a reload or a shared link this chip used to
      // read `📍 ~300m`, which states the distance and hides the thing it is measured from.
      // The id IS in the URL, so the label is derivable: look it up in the same gazetteer
      // the compiler and the dropdown read, and fall back to the radius only when the point
      // is not in it. Derive, never serialize a second copy.
      const nearLoc = advFilters.near.id ? geoLocations.find(l => l.id === advFilters.near.id) : null
      const nearLabel = advFilters.near.label
        || (nearLoc ? `${NEAR_TYPE_EMOJI[nearLoc.type] || '📍'} ${nearLoc.name_ka || nearLoc.name}` : null)
        || `📍 ~${advFilters.near.radiusM}m`
      chips.push({ key: `nearCommitted:${advFilters.near.id}`, label: nearLabel, clear: { near: null } })
    }
    // ⬠ THE DRAWN AREA NEEDS A CHIP, and it is not decoration. The shape is visible on the map
    // and NOWHERE ELSE: an agent draws it, switches to the list or the grid, and the results are
    // narrowed by a filter they can neither see nor remove. That is the same defect the agent
    // filter shipped with — wired to 2 of ~18 sites, "narrowed the feed while no chip named it
    // and neither clear button reset it" — and it is worse here, because the map is a VIEW an
    // agent leaves rather than a panel that stays open beside the results.
    // ⚠️ Clear-all already reaches it (ADV_DEFAULTS spreads registryDefaults, so mapPolygon has a
    // default of ''), which is exactly what the registry is for. Only the chip was hand-work.
    // ⚠️ The corner count is the honest label. A polygon has no name and no place it belongs to,
    // so "დახატული არეალი" alone would be indistinguishable between two different shapes; the
    // count at least tells an agent which drawing this is.
    if (advFilters.mapPolygon) ringsFromParam(advFilters.mapPolygon).forEach((ring, index, rings) => {
      chips.push({
        key: `mapArea:${index}`,
        label: `⬠ ${t('desk_facet_map_area')} ${index + 1} · ${ring.length}`,
        // A chip removes exactly the area it names. The remaining rings are re-encoded into the
        // same scalar so URL/search transport stays backwards-compatible with one-ring links.
        clear: { mapPolygon: ringsToParam(rings.filter((_, i) => i !== index)) },
      })
    })
    // phone tokens (engine v8) — identity chip, resolved via the reach-back below.
    for (const t of landedTokens) if (t.type === 'phone') {
      chips.push({ key: `phone:${t.value.digits}`, label: t.label, sig: t.sig, clear: {} })
    }
    // ── PROJECTION SPLIT (§1 keyword redesign Phase 2, 2026-07-24) ─────────────
    // Which of these constraints does a LIVE TOKEN contribute? Those are already visible in
    // the field as highlighted typed text — projecting them into the bar as keywords too
    // would render the same filter twice ("ვაკე ვაკე"). Same three shapes removeChip
    // dispatches on, so the two stay in step: sig chips ARE tokens; array chips ask whether
    // a token's patch carries that exact value; range/flag chips ask whether a token patched
    // any of the fields the chip clears.
    // A live token may suppress a bar keyword ONLY if that token is itself LANDED
    // (sig equality — sigs are content-derived, so same constraint ⇒ same sig). The two
    // texts are equal right after a commit, so the common case suppresses exactly as
    // before. The case this exists for, caught in the 2026-08-05 browser verification:
    // landed "2 rooms", then type "3 room…" WITHOUT Enter — the live token patches the
    // same field with a DIFFERENT value, and field-level matching hid the landed chip
    // while the highlighted text read "3 room". The filter said 2, every visible surface
    // said 3 — precisely the invisible-filter state this row exists to prevent.
    const liveLandedSigs = new Set(activeTokens.filter(tk => landedTokens.some(lt => lt.sig === tk.sig)).map(tk => tk.sig))
    for (const c of chips) {
      c.fromToken = c.sig ? liveLandedSigs.has(c.sig)
        : c.arrayKey ? activeTokens.some(tk => liveLandedSigs.has(tk.sig) && (tk.patch[c.arrayKey] || []).includes(c.value))
        : c.clear ? activeTokens.some(tk => liveLandedSigs.has(tk.sig) && Object.keys(tk.patch).some(k => k in c.clear))
        : false   // city / committed-near: no token behind them, always projectable
    }
    // ── B347 partition ───────────────────────────────────────────────────────────────────
    // A chip is STRANDED when the chosen identity makes its field meaningless. Same dispatch
    // shape as `fromToken` directly above (arrayKey → clear-keys → editKey), and the coverage
    // is exact rather than lucky: every field named in CAPABILITY_FILTER_KEYS is reachable
    // through some chip's `arrayKey` or its `clear` map, which is what lets one predicate
    // stand in for a per-facet list. A capability added there needs nothing here.
    //
    // ⚠️ The ACTIVE half must come out identical to what `applicableFilters` produced before,
    // or this "surface-only" change silently moves the QUERY: `constraintChips` feeds the rail
    // badge, the mobile pill count and the in-field keywords, and a stranded chip counted there
    // would claim a narrowing that is deliberately not being applied — the precise lie both
    // this row and the 2026-08-01 ruling exist to prevent.
    const dead = inapplicableFilterKeys(norm, objectType, SERVER_ONLY_ADV_KEYS)
    const isStranded = c =>
      (c.arrayKey && dead.has(c.arrayKey)) ||
      (c.clear && Object.keys(c.clear).some(k => dead.has(k))) ||
      (c.editKey && dead.has(c.editKey))
    const active = [], stranded = []
    for (const c of chips) (isStranded(c) ? stranded : active).push(c)
    return { active, stranded }
    // streetLabels: a street chip is `pending` until its name arrives with the counts, so
    // without this dep the chip would stay nameless for the rest of the session — the same
    // shape as `districts` and `allAgents` beside it.
    // geoLocations: the committed-near chip derives its name from the gazetteer by id
    // (B762b) — without it the chip would keep rendering `📍 ~300m` until some other input
    // changed, which is the stale-label half of the bug it was added to fix.
    // complexLabels + metroStations (B935, 2026-08-26): the SAME rule as streetLabels one
    // line up — a chip is `pending` until its label exists, and `readoutChips` FILTERS
    // pending chips out of the bar. Without these deps the memo never re-runs when a label
    // arrives late (URL restore: filters commit first, names arrive after), so the chip
    // stayed invisible forever while the feed stayed narrowed — a filter with no face.
    // The click path never showed it because a click writes the label BEFORE the filter.
  }, [draftAdvFilters, advFilters, entryCityFilter, districts, features, lang, activeTokens, landedTokens, entryTypeFilter, allAgents, streetLabels, complexLabels, metroStations, geoLocations, listingsScope])
  // Named so every existing consumer keeps meaning "the ACTIVE filter state" unchanged.
  const constraintChips = chipModel.active
  const strandedChips = chipModel.stranded
  // The bar's keywords = the filter model MINUS what the typed text already shows. This is a
  // one-way PROJECTION off advFilters (which the FacetRail mutates directly, via the panel's
  // setFilters) — the two-way binding of handoff invariant 10a falls out of that: rail writes
  // the model, the bar re-renders. ⚠️ Never build the reverse (filters → generated text): a
  // second writer into entrySearch would fight the compiler and the two copies would diverge.
  const searchKeywords = useMemo(() => constraintChips.filter(c => !c.fromToken && !c.pending), [constraintChips])
  // The row UNDER the pill takes the same list, minus nothing but the unlabelable ones.
  // ⚠️ `fromToken` chips are kept here and dropped from the in-field keywords, and that
  // difference is deliberate: inside the field a typed word is already tinted in place, so
  // repeating it there would print "ვაკე ვაკე". This row is not the field — it is the
  // complete answer to "what is my search doing", and a typed filter is part of that answer.
  // `pending` still goes (a district whose dictionary has not landed can only be printed as
  // a raw code, and no normalized token may reach the screen).
  const readoutChips = useMemo(() => constraintChips.filter(c => !c.pending), [constraintChips])
  // Same `pending` rule as the active row: a district whose dictionary has not landed can only
  // be printed as a raw code, and no normalized token may reach the screen — being stranded
  // does not license one.
  const readoutStranded = useMemo(() => strandedChips.filter(c => !c.pending), [strandedChips])
  // ⚠️ DF04 (Desk filters audit 2026-10-28): Client Leads asks its RPC seven things (intent, kind,
  // districts, USD budget, rooms range) but shows the whole panel. Every other active chip — and a
  // multi-kind/multi-deal pick, a budget with no exchange rate, the typed text — narrowed nothing
  // there while looking applied. They are named in the feed instead of silently widening it.
  const clientLeadIgnored = useMemo(() => {
    if (entryTypeFilter[0] !== 'ClientLeads') return []
    const f = serverAdvFilters || {}
    const OK = new Set(['intents', 'kinds', 'districtCodes', 'priceMin', 'priceMax', 'roomsSet'])
    const out = readoutChips.filter(c => {
      const ks = [c.arrayKey, ...Object.keys(c.clear || {})].filter(Boolean)
      if (!ks.length) return false
      if (ks.some(k => !OK.has(k))) return true
      if (ks.includes('kinds') && (f.kinds || []).length > 1) return true
      if (ks.includes('intents') && (f.intents || []).length > 1) return true
      if (ks.some(k => k === 'priceMin' || k === 'priceMax')) return clientLeadFilters.priceMinUsd == null && clientLeadFilters.priceMaxUsd == null
      return false
    }).map(c => c.label)
    if (landedSearch.trim()) out.push(landedSearch.trim())
    return out
  }, [entryTypeFilter, serverAdvFilters, readoutChips, clientLeadFilters, landedSearch])
  // How many of them fit right now. A CALLBACK ref, not useRef + useEffect([]): the same
  // searchPill unmounts and remounts when the Desk switches between home and the feed, and a
  // one-shot effect would keep observing the detached node and freeze the count.
  const [kwCapacity, setKwCapacity] = useState(KW_INLINE_MAX)
  // Designer guardrail (2026-07-24): collapse-to-count only ever triggers at 2+. A LONE
  // keyword always renders (truncated if it must) — "+1" costs a word and says nothing that
  // the word itself wouldn't say better. At 2+ the count is the honest summary.
  const kwShown = searchKeywords.length === 1 ? 1 : kwCapacity
  const kwObserverRef = useRef(null)
  const searchPillRef = useCallback(el => {
    if (kwObserverRef.current) { kwObserverRef.current.disconnect(); kwObserverRef.current = null }
    if (!el || typeof ResizeObserver === 'undefined') return
    setKwCapacity(kwCapacityFor(el.getBoundingClientRect().width))
    const ro = new ResizeObserver(([e]) => setKwCapacity(kwCapacityFor(e.contentRect.width)))
    ro.observe(el)
    kwObserverRef.current = ro
  }, [])
  // Chip labels carry a leading glyph as a chip affordance; as inline WORDS in the field they
  // read as text, so drop just those four known prefixes (never a generic strip — price
  // labels legitimately start with ≥/≤).
  const kwText = s => String(s || '').replace(/^(📍|🏙|🔗|⬆)\s*/, '')
  // Which keyword has its editor open (chip key), plus the registry's context. The registry
  // deliberately does NOT carry option lists: intents/kinds live where they are already defined,
  // and a second copy here is how two vocabularies drift apart.
  const [editingKw, setEditingKw] = useState(null)
  const [kwDistrictQuery, setKwDistrictQuery] = useState('')
  useEffect(() => { setEditingKw(null); setKwDistrictQuery('') }, [entryTypeFilter])
  const kwOptionsFor = field => field === 'intents' ? LISTING_INTENT_OPTIONS
    : field === 'kinds' ? Object.entries(PROPERTY_CATEGORY_KEYS).map(([value, key]) => ({ value, label: t(key) }))
    : []
  const kwDistrictOptions = useMemo(() => districts
    .filter(d => !/^(MUN_|TWN_)/.test(d.code))            // cities are their own facet, not districts
    .map(d => ({ code: d.code, label: districtDisplayLabel(d) || d.code })), [districts, lang])
  // ONE full clear for every "start over" surface (the readout's clear-filters link and
  // the dropdown's committed-state გასუფთავება — design §1's committed bar).
  // ⚠️ The agent filter needs NO line here (2026-08-09) — and that is the point of having
  // moved it. It was the one server predicate this function could not reset, because it lived
  // outside advFilters and `setEntryAgentFilter` had a single caller (the dropdown); an admin
  // could press გასუფთავება and keep a filter narrowing the feed, the total, the category tree
  // and the district facet with nothing naming it. Now that it is `advFilters.agentIds`, the
  // `setAdvFilters({ ...ADV_DEFAULTS })` below clears it like every other filter. Do not add a
  // special case back — a filter that needs its own line in this function is a filter that is
  // going to be forgotten from the next surface too.
  // `'active'` for the same reason resetAll and goDeskHome use it: it is the neutral status
  // since B576, and `'all'` made "clear all search" WIDEN the feed to both piles.
  const clearAllSearch = () => { setEntryTypeFilter([]); setEntryStatusFilter('active'); setEntrySearch(''); setLandedSearch(''); setEntryCityFilter('all'); setAdvFilters({ ...ADV_DEFAULTS }); setImageSearch(null); setStreetSel(null) }
  // ONE act, both halves (owner, 2026-08-05: "make it remove each search keyword by one
  // backspace"): when a recognized keyword leaves the TEXT, its constraint must leave the
  // MODEL in the same gesture — the split is exactly what produced "I deleted the word and
  // the filter still exists". Dismissing the sig silences the token in BOTH compiles (live
  // and landed — sigs are content-derived, so the twins share one); un-patching advFilters
  // clears the committed FOLD, which commitSearch merges forever and no text edit can reach.
  // Scalars clear only while they still equal the token's own contribution — a panel edit
  // that overrode the token is the agent's later, more deliberate act, and it wins (same
  // precedence removeChip's array branch already implements).
  function removeTokenConstraint(tok) {
    setDismissedSigs(s => new Set(s).add(tok.sig))
    const patch = tok.patch || {}
    if (!Object.keys(patch).length && tok.type !== 'near_location') return   // identity tokens: text + sig are the whole state
    setAdvFilters(f => {
      const n = { ...f }
      for (const [k, v] of Object.entries(patch)) {
        if (Array.isArray(v)) n[k] = (f[k] || []).filter(x => !v.includes(x))
        else if (f[k] === v) n[k] = (k in ADV_DEFAULTS) ? ADV_DEFAULTS[k] : ''
      }
      if (tok.type === 'near_location' && f.near?.id === tok.value?.id) n.near = null
      return n
    })
  }
  function removeChip(chip) {
    if (chip.key === 'city') { setEntryCityFilter('all'); return }
    if (chip.sig) { setDismissedSigs(s => new Set(s).add(chip.sig)); return }
    // Array-facet chips (districts + 2026-07-17 checkbox facets) remove ONE value — and
    // dismiss only tokens that contributed THAT value, so deleting a manually-picked entry
    // never kills an unrelated typed token.
    if (chip.arrayKey) {
      // landedTokens: the token to dismiss is the one FILTERING (landed). Sigs are
      // content-derived, so the same dismissal also suppresses the live twin if the text
      // still holds the word.
      const hit = landedTokens.filter(t => (t.patch[chip.arrayKey] || []).includes(chip.value))
      if (hit.length) setDismissedSigs(s => { const n = new Set(s); for (const t of hit) n.add(t.sig); return n })
      setAdvFilters(f => {
        const next = (f[chip.arrayKey] || []).filter(c => c !== chip.value)
        // B265: kinds writer #3 — removing a kind chip prunes its now-orphaned subtypes,
        // same rule as the facet and the category pill.
        if (chip.arrayKey === 'kinds') return { ...f, kinds: next, subtypes: pruneSubtypes(next, f.subtypes) }
        // 1685: "acting for" only means something under Agent — removing the Agent chip drops it too, exactly as
        // un-ticking Agent in the Contact facet does (a sub-filter must not outlive its parent)
        if (chip.arrayKey === 'contactRoles' && !next.includes('agent')) return { ...f, contactRoles: next, agentRepresents: [] }
        return { ...f, [chip.arrayKey]: next }
      })
      return
    }
    const keys = new Set(Object.keys(chip.clear))
    const hitTokens = landedTokens.filter(t => Object.keys(t.patch).some(k => keys.has(k)))
    if (hitTokens.length) setDismissedSigs(s => { const n = new Set(s); for (const t of hitTokens) n.add(t.sig); return n })
    setAdvFilters(f => ({ ...f, ...chip.clear }))
  }
  // Query log (search_query_log, 0050): fires 2.5s after the query settles, and again on
  // a dismissal. Append-only fire-and-forget — repeated rows are frequency signal, not noise.
  useEffect(() => {
    const text = entrySearch.trim()
    if (!text) return
    // ⚠️ A pasted share link is NEVER logged. search_query_log is an append-only
    // corpus that exists to teach the vocabulary (DESK_SEARCH_COMPILER.md), and a
    // 32-hex bearer token teaches it nothing while sitting in a table forever.
    // This fires 2.5s after the box settles, so an agent who pastes a link and
    // pauses before pressing Enter would write the credential without ever
    // running a search. Same reason the suggest call is gated above.
    if (looksLikeShareLink(text)) return
    const t = setTimeout(() => {
      // §13 instrumentation (step 6) — everything below reads the telemetry ref AT FIRE
      // TIME (2.5s after settle), NOT this closure's render-time values: the filtered RPC
      // lands after typing settles, so closure state (landedTextQ, listingTotal…) would be
      // systematically one response stale. The ref is mirrored every render (effect below).
      const tel = searchTelemetryRef.current
      const resultCount = tel.visibleCount
      // mode: identity tokens (listing id / phone) resolve through the DB-wide reach-back;
      // a landed server-text-filtered response = 'server'; otherwise the local mirror
      // answered ('local' — includes typing that never committed, itself a signal).
      const mode = tel.reachback ? 'reachback' : (tel.landedTextQ != null || tel.filtersOn) ? 'server' : 'local'
      // commit_source names how THIS text got committed, or null = never committed (the
      // local filter answered without a DB search — §13 wants that distinguished).
      const commitSource = tel.commit && tel.commit.q === tel.committedQ && tel.committedQ ? tel.commit.source : null
      // refined_from: a query within 30s of a ZERO-result one is a reformulation — the
      // pair names the exact transliteration/abbreviation miss (feeds map v2 directly).
      const refinedFrom = tel.lastZero && tel.lastZero.raw !== text && (Date.now() - tel.lastZero.at) < 30000
        ? tel.lastZero.raw : undefined
      logSearchQuery({
        rawText: text,
        compiled: compiledQuery.tokens,          // each token carries `matched` — replayable observations
        residual: compiledQuery.residual,
        dismissed: [...dismissedSigs],
        // result_total column (0119): server cluster total when a server search ran,
        // else the local visible count — the indexable "did it return anything" signal.
        resultTotal: tel.serverTotal != null ? tel.serverTotal : resultCount,
        context: {
          source: 'desk',
          vocab_version: compiledQuery.vocabVersion,
          vocab_hash: compiledQuery.vocabHash,        // runtime identity — catches un-bumped edits
          engine_version: compiledQuery.engineVersion,
          failures: compiledQuery.failures,           // unclaimed fragments, mechanically tagged
          result_count: resultCount,
          server_total: tel.serverTotal,
          zero_result: resultCount === 0,
          latency_ms: { compile: compiledQuery.ms ?? null, rpc_roundtrip: tel.rpcMs },
          mode,
          commit_source: commitSource,
          ...(refinedFrom !== undefined ? { refined_from: refinedFrom } : {}),
          ...(tel.suggestShown ? { suggest_shown: tel.suggestShown } : {}),
        },
      })
    }, 2500)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entrySearch, dismissedSigs])

  // Widening the period restarts from offset 0 — the OLD in-memory page doesn't necessarily
  // belong to the new window (e.g. narrowing from 'all' to '7d' would otherwise keep stale rows
  // outside the new bound around until the next "Load more").
  useEffect(() => {
    setListingTotalLanded(false)   // F-089: a new question — nothing has landed for it yet
    if (listingsScope === 'mine' && !myAgentId) { setListingEntries([]); setListingTotal(0); setListingPossibles([]); return }
    // A special feed OWNS the surface — its own component fetches and renders the rows, and
    // nothing here is displayed while one is active. Running this search anyway cost a full
    // duplicate round trip per keystroke-committed query (9.3s on a real leads search), which
    // is most of why the Leads page felt slow and then failed: the request the user was
    // waiting on was competing with one whose answer gets thrown away. Every other consumer
    // of the entries pipeline already bailed on SPECIAL_FEED; this one simply could not see
    // the flag, because it was declared below.
    // ⚠️ R1 (Desk filters audit 2026-10-28): both early returns below make no request, so they used to
    // leave searchRunRef unchanged — a shared hover request joined by the PREVIOUS run (which this
    // effect's abort cannot cancel) still matched the run id and could land its rows over the
    // special feed or the local answer. Any new question retires the old run before returning.
    if (SPECIAL_FEED) { searchRunRef.current++; return }
    const filters = serverAdvFilters   // advanced filters + selected city (folded into district_codes); local-only keys already stripped (B177)
    // Local-filter fast path: the whole unfiltered set is in memory → no server refetch,
    // the client classify filters it instantly. Entries are left untouched (they ARE the
    // snapshot); possibles/unknown switch to their client-derived sources via localFilterMode.
    // ⚠️ entrySort is PART OF THE SNAPSHOT IDENTITY (2026-07-31). This key does double duty:
    // it names the desk cache entry AND asserts "the rows in hand are the complete answer to
    // this question" for the local-filter fast path. Now that the unfiltered feed pushes its
    // sort to the server, two sorts are two different answers — without this, switching sort
    // would repaint the cached rows of the previous one and call it sorted.
    // ⚠️ …and `entryStatusFilter` for EXACTLY the same reason (2026-08-18). It is sent on the
    // request below (`status:`) and honoured server-side — the unfiltered feed has applied it
    // since B576, and the committed-search path since v41 gained `p_status` — so active and
    // archived are two different answers to two different questions. Missing from this key,
    // they shared one desk-cache entry and one `snapshotKeyRef` claim: the archived feed could
    // be painted from the active feed's cached rows and total, and the local-filter fast path
    // would then assert those rows were "the complete answer to this question" and skip the
    // refetch that would have corrected it. Same class as the sort note directly above.
    const snapKey = `${listingsScope}:${myAgentId || ''}:${entryPeriod}:${entrySort}:${entryStatusFilter}`
    // A COMMITTED text search (filters.q) always goes to the server, even over a complete
    // snapshot: the local raw mirror can't do the normalized Georgian↔Latin match
    // (0106/0108) — answering "vake" locally over Georgian-only addresses would return
    // zero rows the server would have found, precisely the miss this redesign kills.
    // !filters.agentIds: the agent filter is SERVER-side now, so a local pass would show
    // the whole agency's rows under one agent's name. Same reasoning as filters.q above.
    // 🔴 ...AND NOT WHEN A SERVER-ONLY FILTER IS ACTIVE. Owner, of the condition ladder:
    // "filters nothing at all." This branch answers from the LOADED ROWS and makes NO REQUEST,
    // and the loaded rows carry no property_feature data - so conditionCodes, featureStates,
    // conditionExcludeCodes, subtypes, addedHow and the hunter lenses were all silently ignored
    // whenever a complete snapshot happened to be in hand. The panel ticked, the chip lit, the
    // URL updated, and the feed never moved.
    // 📌 The guard already excluded filters.q and filters.agentIds for exactly this reason - "the
    // local pass cannot do it" - and that list was hand-written, so every server-only filter
    // added since was missing from it. SERVER_ONLY_ADV_KEYS is the ONE definition of that
    // question and was being used only to suppress COUNTS, never to decide whether to ask.
    // ⚠️ Reproduced before fixing: condition alone produced 4 requests, none carrying it, and the
    // headline stayed 17,740. The SAME filter paired with features=balcony went through and
    // returned 410 - because that combination missed the snapshot and took the server path. A
    // filter that works or not depending on what else you ticked is the worst shape of all.
    if (filters && !filters.q && !filters.agentIds?.length && localPassCanAnswer(filters)   // F-108: not merely 'no server-only filter'
        && snapshotKeyRef.current === snapKey && listingRawCount >= listingTotal && listingTotal > 0) {
      setLocalFilterMode(true)
      setLandedServerFilters(null)   // 2026-10-22: the unfiltered snapshot is on screen — the client classify IS the filter
      setListingPossibles([]); setServerUnknown(null)
      // No request is made on this path, so nothing is in flight. Clearing it here matters:
      // a fetch could have been running when the deps changed into this branch, and a flag
      // left true would suppress the zero state forever on a feed that is genuinely empty.
      setListingsFetching(false)
      searchRunRef.current++   // R1 — see the SPECIAL_FEED return above
      return
    }
    setLocalFilterMode(false)
    setLandedTextQ(null)    // mirror-skip OFF through the in-flight window (§6)
    setCursorFresh(false)   // fetch in flight — the prefetch chain must wait for its cursor
    cursorFreshRef.current = false   // …and B884: the ref is what the chain actually reads, because
                                     // the state above is invisible to it until the next render
    holdFeedHeight('fetch-start')   // F-045: hold the previous height of the grid AND its container
    setListingsFetching(true)   // …and the zero-state must say "searching", not "nothing found"
    setListingsError(null)      // F-021: a new question clears the last failure
    chainOffsetRef.current = -1      // new generation → no offset has been asked for yet
    loadGenRef.current++    // invalidate any in-flight load-more (it belongs to the OLD snapshot)
    chainAbortRef.current?.abort()   // …and CANCEL it: see chainAbortRef — the gen bump only dropped its rows
    possiblesAbortRef.current?.abort()
    chainExhaustedRef.current = false   // new snapshot → the cursor may have rows again
    // Stale-while-revalidate: unfiltered feeds paint instantly from the last cached response,
    // then the fetch below ALWAYS runs and overwrites (cache is a first-paint accelerator,
    // never a source of truth — saved listings are immutable, new rows arrive via revalidate).
    // FILTERED fetches are cached too since 2026-08-05 (owner: "can we use the browser's
    // memory to speed up later searches?") — and the reason they weren't is GONE, not merely
    // outvoted: the old note said "keys would churn per keystroke for near-zero hit rate",
    // and since the landing ruling a search only exists at COMMIT, so its key is minted once
    // per Enter and an agent's repeated searches (the same district, the same budget, day
    // after day) hit it. Keyed by the full question — snapshot + filter signature + sort —
    // because a cached answer to a DIFFERENT question is the one thing worse than no cache.
    // advSig already neutralizes display-only currency; chunk 1 only (~100 rows), same size
    // class the al: key already stores, and writeDeskCache's evict-retry owns the quota.
    // Derived from snapKey, never rebuilt from the same parts: the write site below composes
    // the same key, and two hand-built copies of one key are a drift bug waiting to happen.
    // ⚠️ No sort suffix: snapKey already carries entrySort (it is part of snapshot identity,
    // see its declaration). And no srvSort here — that const is declared BELOW this line;
    // reading it would be this file's sixth TDZ instance.
    // B1214 (2026-09-05): the key is THE canonical query key — pool, scope, viewer, sort, period,
    // page and the registry's own wire encoding of the filters — and the cache is the layered
    // memory → IndexedDB store (src/db/queryCache.js), read asynchronously so a paint never blocks
    // on it. `snapKey`/`advSig` above stay for the snapshot-identity refs; they no longer key a cache.
    const cacheKey = queryKey({ scope: listingsScope, viewer: myAgentId || '', pool: listingsScope === 'leads' ? '' : (entryStatusFilter || ''), period: entryPeriod, sort: entrySort, filters: filters || null, offset: 0, limit: filters ? FILTERED_PAGE : LISTING_FIRST_PAGE })
    let cached = null   // set when the cache answers; read by the fresh handler (hadComplete) and the failure path
    // 2026-10-22: the reduction "before" this answer may supply — only when no USER filter rode it
    // (unfiltered, or the city's codes alone). This fetch's own axes (scope/agent/period/status), plus the city.
    const userFiltered = advFiltersActive(advFiltersDebounced) || districtFilterActive(advFiltersDebounced)
    const baseKey = userFiltered ? null
      : baselineKey(`${listingsScope}:${myAgentId || ''}:${entryPeriod}:${entryStatusFilter}`, entryCityFilter)
    const paintCached = (hit) => {
      cached = hit
      perfTrace('paint:cached', { rows: (hit.listings || []).length, source: hit.source })
      perfMark('desk:first-cards', { firstCardsSource: hit.source, cachedAgeMs: Date.now() - (hit.fetched_at || Date.now()) })
      // ⚠️ snapshotKeyRef marks "the COMPLETE UNFILTERED corpus is in memory" — the
      // local-filter fast path and the tc: total write both trust it. A filtered cached
      // paint must never claim it: chunk 1 of a search is neither complete nor unfiltered.
      if (!filters) snapshotKeyRef.current = snapKey
      setListingEntriesReconciled(dedupeListings(cached.listings)); setListingRawCount(cached.rowsReturned)
      setListingTotal(cached.total); setListingCountUnit(cached.countUnit); setServerUnknown(cached.unknownExcluded)
      setListingTotalLanded(true)   // F-089: an answer to this question (cached) — zero included
      setListingPossibles(filters ? (cached.possibleListings || []) : [])
      noteBaseline(baseKey, cached.total)   // 2026-10-22: only an answer with no user filter is a "before"
      setLandedServerFilters(filters || null)
      // The mirror-skip must arm WITH the cached rows, exactly as it does when the live
      // response lands: these rows were server-matched (normalized, cross-alphabet), and
      // re-filtering them through the raw local mirror would drop the Georgian rows a
      // Latin query matched — the precise miss landedTextQ exists to prevent.
      if (filters && filters.q) setLandedTextQ(filters.q)
    }
    let freshLanded = false
    // Abort-on-cleanup (2026-07-15): a superseded initial fetch (dep change mid-flight — these
    // run ~10s — or StrictMode's dev double-mount, which fired a duplicate request on every cold
    // open) is CANCELLED, not left to resolve late and overwrite the newer snapshot's state.
    const ac = new AbortController()
    // §17 supersede guard: claim a run id BEFORE the request leaves. Anything that comes
    // back holding a stale id is a superseded answer and must not reach the screen.
    const runId = ++searchRunRef.current
    // Stale first (rule 2): whatever this exact view showed last time paints now, unless the
    // network has already answered or the question has moved on. A stale paint may never land
    // AFTER the fresh one — that is the one ordering this file must keep.
    // ── FRESH ANSWERS ARE NOT RE-ASKED (owner 2026-09-24, lib/deskFreshness.js) ──────────────
    // The cache is read FIRST now, and what it holds decides whether the network is asked at all:
    //   fresh      (younger than its server-sent TTL, 300 s fallback, and not older than the
    //               agent's own last save) → paint it AND land it as the answer; no request.
    //   revalidate (TTL … 15 min, or written before an own save) → paint, refetch on idle.
    //   expired / miss → paint whatever there is, refetch now.
    // The IndexedDB read costs a few ms before the request leaves on a miss; the fresh hit saves
    // the whole 2–17 s search. `listingsRetry` (the error panel's retry) always goes to the
    // network — a retry is a human saying "ask again", never something a cache may answer.
    // ⚠️ A fresh land needs `searchMeta` IN the entry (`!== undefined`): the facet counts ride it,
    // and landing an entry written before this change would blank them. Such entries revalidate.
    const forceNet = retrySeenRef.current !== listingsRetry
    retrySeenRef.current = listingsRetry
    // The chain-completion write re-stores the COMPLETE page under this key — only for the
    // UNFILTERED snapshot it was computed for (snapKey), never under a filtered key.
    page0KeyRef.current = { key: cacheKey, snapKey, filtered: !!filters }
    // v13 server sort: page selection under an explicit sort is whole-DB truth. Only the
    // filtered path supports it (the unfiltered feed RPC has no sort param); 'oldest'
    // stays client-side (no server equivalent — reverse-activity pagination not built).
    // ⚠️ NOT `filters ? … : null` any more (2026-07-31). That guard was written when the
    // unfiltered feed RPC genuinely had no sort param; since the server's `filtered` gate
    // counts a non-default sort, the search RPC answers and sorts corpus-wide. Leaving the
    // guard meant the fix was live on the server and unreachable from the client: on a clean
    // filter panel "Lowest price" still only reordered the loaded page — the exact symptom,
    // just better hidden behind the client comparator.
    // COST, ACCEPTED KNOWINGLY: a non-default sort on an otherwise-unfiltered feed now routes
    // through the corpus-scan RPC instead of the cheap recent-feed read. It is opt-in (the
    // default `activity` sends nothing and keeps the cheap path) and it is the only way page
    // SELECTION can be whole-DB truth rather than "sorted among whatever loaded".
    const srvSort = SERVER_SORT_OF[entrySort] || null
    // unknownLimit: 0 under a filter (2026-08-10) — the possible-matches shelf used to ride
    // bundled in THIS response (up to 60 clusters, ~500KB on a busy filter), which is why the
    // matched cards could not paint until the shelf was ALSO fetched and shaped server-side.
    // The shelf is now fetched separately, after the matched cards land — see
    // loadInitialPossibles below, called from this fetch's own .then. Omitted (not `0`) on
    // the unfiltered path: nothing there ever HAD a shelf, so there is nothing to skip.
    // ONE landing for both sources of an answer — the network, and a cache entry young enough
    // to BE the answer. Two copies of this body would be the drift this file keeps paying for.
    const landAnswer = (resp, fromCache, rpcT0) => {
        const { listings, total, rowsReturned, countUnit, unknownExcluded, searchMeta } = resp
        const possibleListings = resp.possibleListings || []
        // Superseded by a newer commit while this was in flight — drop it whole. Returning
        // here rather than merging is deliberate: a late answer is not extra information,
        // it is an answer to a question the agent has already stopped asking.
        if (searchRunRef.current !== runId) return
        freshLanded = true
        page0FetchedAtRef.current = fromCache ? (resp.fetched_at || Date.now()) : Date.now()
        page0TtlRef.current = resp.cacheTtlMs ?? null
        page0MetaRef.current = resp.searchMeta ?? null
        // F-045: the render budget is reset HERE, in the same update as the fresh rows — the pageSig effect
        // resets it one render later, and in that one render the release effect below read the OLD page's
        // 30 as "fully drawn" and dropped the hold onto an 8-card page (observer, 2026-09-06: 16,782 → 8,071 px).
        setRenderBudget(FIRST_PAINT_GROUPS)
        perfMark('desk:fresh-cards', { freshMs: Date.now() - rpcT0, rows: listings.length, ...(fromCache ? { freshSource: 'cache' } : {}) })
        perfMark('desk:first-cards', { firstCardsSource: fromCache ? (resp.source || 'cache') : 'network' })
        // ⚠️ Cleared only AFTER the supersede guard, never before it. A late answer to an
        // abandoned question must not report "done" on behalf of the request that replaced
        // it — that would blink the zero state on mid-search, which is the very lie this flag
        // exists to remove.
        setListingsFetching(false)
        // ⚠️ BUILT HERE, not beside the state it feeds. It closes over `listingsScope` and
        // `entryPeriod`, which are declared ~900 lines BELOW that state — an arrow body would have
        // run late enough to be safe, but only by accident, and this file has been taken to its
        // error boundary once by exactly that kind of ordering. Built where every binding is live,
        // from the filters that ASKED (`filters` = `serverAdvFilters` for this run), so a slow
        // answer is judged against its own question rather than whatever is on screen now.
        const sigOf = (family) => facetSignature(
          canonicalFilters(filters),
          // review 2026-09-23: the REAL status this request asked (it rides `status:` above),
          // mirroring the leads path's `archive: leadArchive` — hard-coded false let the
          // archived answer and the active one share a signature.
          { archive: entryStatusFilter === 'archived', mine: listingsScope === 'mine', since: entryPeriod || '' },   // F-067 generalised (2026-09-10): the period LABEL, never the instant
          family)
        // Every facet now goes through the ownership rule: the canonical committed state minus
        // that facet's own keys. See facetOwnership.js.
        applyServerFacet('kinds',          searchMeta?.kind_counts,         setServerKindCounts,        sigOf('kinds'))
        applyServerFacet('intents',        searchMeta?.intent_counts,       setServerIntentCounts,      sigOf('intents'))
        applyServerFacet('sources',        searchMeta?.source_counts,       setServerSourceCounts,      sigOf('sources'))   // F-086
        applyServerFacet('districts',      searchMeta?.district_counts,     setServerDistrictCounts,    sigOf('districts'))
        applyServerFacet('districtsOwn',   searchMeta?.district_own_counts, setServerDistrictOwnCounts, sigOf('districtsOwn'))
        applyServerFacet('cities',         searchMeta?.city_counts,         setServerCityCounts,        sigOf('cities'))
        applyServerFacet('streets',        searchMeta?.street_counts,       setServerStreetCounts,      sigOf('streets'))
        applyServerFacet('metros',         searchMeta?.metro_counts,        setServerMetroCounts,       sigOf('metros'))
        applyServerFacet('streetCoverage', searchMeta?.street_coverage,     setServerStreetCoverage,    sigOf('streetCoverage'))
        // ⚠️ Histograms merge PER FACET and are dropped when their own question changes — the
        // old `mergeRangeFacets` kept a price histogram across a district change.
        setRangeFacets(prev => {
          const held = resolveRanges(searchMeta?.range_facets, rangeSigRef.current, sigOf)
          rangeSigRef.current = held
          const out = {}
          for (const [k, r] of Object.entries(held)) if (r.value != null) out[k] = r.value
          return Object.keys(out).length ? out : null
        })
        if (filters && !fromCache) searchTelemetryRef.current.rpcMs = Date.now() - rpcT0
        snapshotKeyRef.current = filters ? null : snapKey
        setCursorFresh(true)
        cursorFreshRef.current = true   // B884 — arms the chain, and only now that page 0 has LANDED
        fetchedIdsRef.current = new Set(listings.map(l => l.listing_id))
        // If the cache held a COMPLETE snapshot for this key, merge the fresh chunk over it
        // (fresh rows win by listing_id) instead of replacing — replacing shrank the feed to
        // chunk 1 and the "shown N / M" counter visibly re-grew on every refresh (2026-07-11).
        // The offset cursor stays at rowsReturned, so the chain still re-fetches the whole
        // tail underneath; stale merged rows get overwritten by id, and rows that no longer
        // exist server-side are purged when the chain completes (see fetchedIdsRef).
        const hadComplete = !filters && cached && cached.total > 0 && cached.rowsReturned >= cached.total
        setListingEntriesReconciled(hadComplete ? dedupeListings([...cached.listings, ...listings]) : dedupeListings(listings))
        setListingRawCount(rowsReturned)
        setListingTotal(total); setListingCountUnit(countUnit); setServerUnknown(unknownExcluded)
        setListingTotalLanded(true)   // F-089: the fresh answer — zero included
        setListingPossibles(filters ? possibleListings : [])
        // Q4: a new snapshot invalidates the nearby group too. It is REFILLED by
        // loadInitialPossibles below (the only request that asks for it), so clearing here is
        // not a flicker — it is the gap between two different questions, and leaving the old
        // street's neighbours on screen under a new one would be a wrong answer, not a stale
        // one. Cleared on the unfiltered path for the same reason it can never be filled
        // there: with no street selected there is no centre to measure from.
        setListingNearby([]); setStreetNearbyMeta(null)
        // A new search is a new shelf: reset its cursor with the page-0 answer it belongs
        // to. Carrying an offset across searches would open the next shelf at page 4 of a
        // question nobody asked.
        setPossiblesOffset(0); possiblesDoneRef.current = false; possiblesBusyRef.current = false
        // The lock is dropped here, so its spinner and failure go with it: an older run that returns
        // later no longer owns the lock and clears nothing (ownRun/releaseRun, 2026-10-23 audit).
        setPossiblesLoading(false); setPossiblesFailed(false)
        // …and then go fetch that page-0 answer (2026-08-10): `possibleListings` above is
        // always `[]` now (this request asked for unknownLimit: 0), so the shelf that used to
        // arrive bundled here arrives a beat later instead, off the critical path. `runId`
        // already proved this is the current search by the time we're this far into the
        // .then; `loadGenRef.current` read fresh here is the matching generation for it.
        // ── ONE SEARCH PER FILTERED CLICK (2026-09-24) ───────────────────────────────────
        // The shelf request RE-RUNS the whole search server-side to define "cannot be judged"
        // against it, so firing it here made every filtered click two full searches. It is now
        // ARMED here and fetched only when its place on the page comes near the viewport (the
        // sentinel observer, `shelfWanted`) or the amber "show anyway" line is pressed — i.e. only
        // when somebody can see it. A short result still triggers it at once, because then the
        // sentinel IS on screen; a long result never pays for a shelf nobody scrolled to.
        if (filters) armShelf(filters, loadGenRef.current)
        else pendingShelfRef.current = null
        setLandedTextQ(filters && filters.q ? filters.q : null)   // this response WAS text-filtered (normalized, server-side) → mirror-skip arms
        // Baseline only updates from answers with no USER filter — it's the "before" of the reduction
        // signal, filed per view + city (2026-10-22: the old single global value compared a filtered
        // Tbilisi search with an all-cities total from another period; no baseline now means no %).
        noteBaseline(baseKey, total)
        setLandedServerFilters(filters || null)   // 2026-10-22: the question these rows answer (server_vetted)
        // Never downgrade a complete cached snapshot to chunk 1 — the completion effect
        // below rewrites the cache with the full purged set once the chain finishes.
        // Filtered responses also persist the shelf — a cached search repainting without
        // its possible-matches would silently drop the "cannot be judged" middle answer,
        // the one thing the three-answer rule forbids folding into "no".
        // `searchMeta` and the server's TTL ride the entry (2026-09-24): without the meta a
        // fresh-skip could not restore the facet counts, and without the TTL it could not know
        // how long the server said this answer may be reused. `null` meta is stored as null —
        // "the server sent none" — which is different from absent (an old entry: revalidate).
        if (cacheKey && !hadComplete && !fromCache) writeQuery(cacheKey, { listings, total, rowsReturned, countUnit, unknownExcluded, searchMeta: searchMeta ?? null, cacheTtlMs: resp.cacheTtlMs ?? null, ...(filters ? { possibleListings } : {}) }).catch(() => {})
        // rule 4: the next page is warmed into the cache while the agent reads this one
        if (rowsReturned > 0 && rowsReturned < total) warmNextPage(rowsReturned)
    }
    const onFetchFail = e => {
        if (e.name === 'AbortError') return   // superseded by a newer effect run — not a failure
        // Same guard on the failure path: a superseded request that fails for its OWN reasons
        // (timeout, 5xx) must not blank the feed the newer search has since filled.
        if (searchRunRef.current !== runId) return
        // Same rule on the failure path, and it must be cleared here too — a search that
        // FAILED is finished, and leaving the flag true would show "searching…" forever over
        // a feed that will never fill. (AbortError returned above without clearing, correctly:
        // a superseded request is not finished, its replacement is still running.)
        setListingsFetching(false)
        console.error('[agent-listings]', e.message)
        // F-021: record the failure so the blank area renders as an ERROR with a retry, not as
        // "nothing matches these filters". feedEmptyKind() is the rule; it has a test.
        setListingsError(e.message || 'error')
        // Only blank the feed if nothing cached is on screen — a fetch hiccup shouldn't
        // wipe a perfectly good stale view the agent is already reading.
        if (!cached) { setListingEntries([]); setListingTotal(0) }
    }
    const goNetwork = () => {
      if (searchRunRef.current !== runId || ac.signal.aborted) return
      const rpcT0 = Date.now()   // §13 latency_ms.rpc_roundtrip — measured per filtered fetch
      perfMark('desk:feed-request')
      // FIRST page: LISTING_FIRST_PAGE unfiltered (see its declaration — the first screen, not the
      // snapshot), FILTERED_PAGE under a filter. ⚠️ The SAME expression is in `cacheKey` above; a
      // limit sent but not keyed is the half-existing-key bug.
      const ask = () => getAgentListings({ scope: listingsScope, agentId: myAgentId, since: periodSinceISO(entryPeriod), limit: filters ? FILTERED_PAGE : LISTING_FIRST_PAGE, offset: 0, filters, sort: srvSort, status: entryStatusFilter, rangeFacets: true, signal: ac.signal, unknownLimit: filters ? 0 : undefined })
      // A hover warm for THIS key already in flight is joined, not duplicated (see warmFeedPage);
      // it resolves null when it skipped or failed, and then we ask for ourselves.
      const warming = hoverWarmInflightRef.current.get(cacheKey)
      ;(warming ? warming.then(r => r || ask()) : ask())
        .then(r => landAnswer(r, false, rpcT0))
        .catch(onFetchFail)
    }
    let cancelIdle = null
    readQuery(cacheKey).catch(() => null).then(hit => {
      if (searchRunRef.current !== runId || ac.signal.aborted) return
      if (hit && !freshLanded) paintCached(hit)
      const tier = hit ? freshnessTier({ fetchedAt: hit.fetched_at, ttlMs: hit.cacheTtlMs, dirtyAt: deskDirtyAt() }) : 'none'
      perfMark('desk:cache-tier', { cacheTier: tier })
      if (!forceNet && tier === 'fresh' && hit.searchMeta !== undefined) { landAnswer(hit, true, Date.now()); return }
      if (!forceNet && tier === 'revalidate') cancelIdle = whenIdle(goNetwork)   // painted; refresh behind it
      else goNetwork()                                                          // expired / miss / retry: now
    })
    return () => { ac.abort(); cancelIdle?.() }
    // entrySort in deps: an explicit sort changes which clusters page 0 holds (server sort,
    // v13) — a refetch is correct. The old tail of this note ("the unfiltered path ignores
    // it and pays only a cache-instant revalidate") stopped being true on 2026-07-31: the
    // unfiltered path now sends the sort too, and entrySort is part of snapKey, so the two
    // sorts are two cache entries rather than one repainted under the wrong label.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // SPECIAL_FEED is a real dependency, not just a guard: leaving Leads/Pool/Meeting for the
    // normal feed must re-run this fetch, or the grid would come back empty.
    // ⚠️ entryStatusFilter is one too (added 2026-08-18). It has ridden on the request as
    // `status:` since B576 and is applied server-side, but it was in neither this array nor
    // `snapKey` — so switching აქტიური ⇄ არქივი changed the word on the pill and asked the
    // server nothing. The rows, the total and every count derived from it stayed the previous
    // status's answer, which is indistinguishable from a filter that does not work. The
    // half-existing-key trap this file records four times over, at its fifth site: state,
    // transport, cache key, AND the dependency array that decides whether the question is
    // re-asked at all.
  }, [listingsScope, myAgentId, entryPeriod, advSig, entrySort, entryStatusFilter, SPECIAL_FEED, listingsRetry])
  // The agent's property inventory — the source of same_as cluster keys (so two DB rows for one
  // flat collapse into one property card) and of properties that have NO listing yet (shown as
  // standalone property cards). Cheap, changes rarely; refetched on agent change + after saves.
  useEffect(() => {
    if (!myAgentId) { setAgentProperties([]); return }
    // ⚠️ GUARDED (Desk home audit 2026-10-21): with no abort, a slower answer for the PREVIOUS agent could land after
    // this agent's and repaint their inventory; and with no cache for the new agent the old one stayed on screen.
    let gone = false
    // 2026-09-24 (owner: "cache desk pages heavily"): the entry carries its fetch time, and a
    // fresh one (15 min, never older than the agent's own last save) is not re-asked. Older
    // entries were a bare array — still painted, then refetched.
    const raw = readDeskCache(`ap:${myAgentId}`)
    const cached = Array.isArray(raw) ? raw : (raw && Array.isArray(raw.props) ? raw.props : null)
    setAgentProperties(cached || [])
    if (raw && !Array.isArray(raw) && isFreshWithin(raw.at, DESK_COUNTS_FRESH_MS)) return
    const forAgent = myAgentId
    getAgentProperties(forAgent)
      .then(props => { writeDeskCache(`ap:${forAgent}`, { at: Date.now(), props }); if (!gone) setAgentProperties(props) })
      .catch(e => { console.error('[agent-properties]', e.message); if (!gone && !cached) setAgentProperties([]) })
    return () => { gone = true }
  }, [myAgentId, entryPeriod])
  // (2026-07-20) The org-wide same_as cluster fetch (getPropertyClusters → orgPropClusters) that
  // used to collapse cross-agent stacks in the ADMIN Desk (the 2026-07-08 "two un-stacked cards"
  // fix) is GONE: /api/core/agent-listings now emits an authoritative per-row cluster_key
  // (property_cluster_map, 0133) on every loaded row, and the reach-back stamps its own family key
  // (below), so nothing needs the org-wide map anymore. One fewer admin round-trip + egress source.

  // B273 move 4c (2026-08-02): 0056 IS GONE FROM THE CLIENT. Every engine that answers "how many
  // properties are in this district" now calls `district_keys_v1` — the search (0366), the leads
  // page (0369) and the Properties page (0371) — so there is no longer a second counter to read.
  //
  // What survives is this state, now holding the LAST HONEST ANSWER instead of a wrong one.
  // First paint is the reason it must exist at all: `search_meta.district_counts` is null until
  // page 0 answers, and a numberless tree is not neutral — `byCount` ties every district at 0,
  // the sort falls through to alphabetical, and BOTH Saburtalo and Vake drop off the visible
  // top-8 behind "See all (79)" (tried and reverted 2026-08-02).
  //
  // So the honest counts are cached per scope+period and repainted from cache on arrival. A
  // returning session opens on a slightly STALE TRUE number rather than a fresh FALSE one, which
  // is strictly better than what 0056 gave: it was wrong even when current, by 1.6–2.6× with 44
  // districts greyed as empty while holding leads.
  // ⚠️ Cached counts are EXPANDED (0370 semantics), like the live ones they stand in for — the
  // `usingHonestCounts` flag below must stay true for them, or districtReach re-sums a parent
  // that already contains its children.
  const [districtCounts, setDistrictCounts] = useState({})
  // ⚠️ `entryStatusFilter` IS PART OF THIS KEY (2026-08-18), and do not confuse this with the
  // status-pill counts below, which are deliberately status-BLIND and must stay that way: the
  // pills describe the whole book, so their numbers must not move when you click between them.
  // These three caches are the opposite kind of number — dc2 (district tree), tc (corpus total)
  // and fc (kind/intent facets) all describe THE CURRENT VIEW, and the server computes every
  // one of them from the `counted` CTE, i.e. AFTER the status predicate. Measured: 309 active
  // vs 2 archived on one agent's book. Sharing a key meant the archived answer seeded the
  // active tree and vice versa — invisible until now only because the status pill did not
  // refetch at all; fixing that is what made this reachable. The map pins cache (`pins:`) has
  // carried status in its key since it shipped, for exactly this reason.
  const dcKey = `dc2:${listingsScope}:${myAgentId || ''}:${entryPeriod}:${entryTypeFilter[0] || 'all'}:${entryStatusFilter}`
  useEffect(() => {
    const cached = readDeskCache(dcKey)
    setDistrictCounts(cached || {})
  }, [dcKey])
  // ── The CORPUS TOTAL gets its own tiny cache (owner 2026-08-05: "92 → 162 → 3141 —
  // why doesn't it load instantly"). The al: snapshot cache DOES carry `total` and should
  // have seeded it instantly — but that cache stores whole feed pages, thousands of rows
  // against localStorage's ~5MB, so on exactly the big feeds where the climb is worst the
  // write fails on quota, silently, and the total is never there to seed. A bare integer
  // cannot fail that way. Same stale-true-beats-fresh-false contract as dc2 above; the
  // pulse marks it provisional until the live fetch or the prewarm overwrites it.
  const [cachedListingTotal, setCachedListingTotal] = useState(null)
  useEffect(() => {
    const t = readDeskCache(`tc:${dcKey}`)
    setCachedListingTotal(Number.isFinite(t) && t > 0 ? t : null)
  }, [dcKey])
  useEffect(() => {
    // snapshotKeyRef is non-null exactly when the last landed fetch was the UNFILTERED
    // browse — a filtered total written here would seed the next visit with the size of
    // whatever search happened to be active when this ran.
    // review 2026-09-23: …and the snapshot must be THIS view's. A dcKey change re-ran this with
    // the PREVIOUS view's listingTotal (the new fetch had not landed) and wrote the old total
    // under the new key. snapshotKeyRef holds the snapKey the total was computed for, so the
    // write is allowed only when it equals the snapKey of the view on screen now (same
    // composition as the fetch site's `snapKey`). `listingsFetching` is a dep so the write
    // still happens when the new answer lands carrying an unchanged number.
    const curSnapKey = `${listingsScope}:${myAgentId || ''}:${entryPeriod}:${entrySort}:${entryStatusFilter}`
    if (listingTotal > 0 && snapshotKeyRef.current && snapshotKeyRef.current === curSnapKey && !listingsFetching) {
      setCachedListingTotal(listingTotal)
      writeDeskCache(`tc:${dcKey}`, listingTotal)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingTotal, dcKey, listingsFetching])
  // Same trick for the CATEGORY numbers (owner 2026-08-02: "numbers grow slowly"). Without a
  // cached answer the tree tallies the rows loaded so far, so every visit re-climbs from a
  // partial page to the truth. Seeding from the last true answer for THIS query makes a return
  // visit instant, and the live response overwrites it a moment later.
  // ⚠️ Seeded, never merged: a cached map for a DIFFERENT query would be a wrong number wearing
  // a settled look. The key carries scope, agent, period and view for exactly that reason.
  // review 2026-09-23: the SERVER question behind these maps — dcKey minus the view-type part,
  // which changes the key without changing (or refetching) the answer. When the question moves
  // and no cached map exists, the previous view's kinds/intents/sources are CLEARED rather than
  // kept ("seeded, never merged" above); a type-only change keeps them, since nothing refetches
  // and they still answer the same question.
  const fcQuestion = `${listingsScope}:${myAgentId || ''}:${entryPeriod}:${entryStatusFilter}`
  const fcSeedQuestionRef = useRef(fcQuestion)
  useEffect(() => {
    const c = readDeskCache(`fc:${dcKey}`)
    const questionMoved = fcSeedQuestionRef.current !== fcQuestion
    fcSeedQuestionRef.current = fcQuestion
    if (c?.kinds) setServerKindCounts(c.kinds); else if (questionMoved) setServerKindCounts(null)
    if (c?.intents) setServerIntentCounts(c.intents); else if (questionMoved) setServerIntentCounts(null)
    if (c?.sources) setServerSourceCounts(c.sources); else if (questionMoved) setServerSourceCounts(null)   // F-086
    setRangeFacets(c?.ranges ?? null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dcKey])
  // review 2026-09-23: the fc: key carries NO filter signature, so only an UNFILTERED answer may
  // be written under it (the prewarm's own `!serverAdvFilters && !committedQ` — the condition
  // that makes the key honest). And the run where the question itself just moved is skipped:
  // its closure still holds the PREVIOUS view's maps (the seed above lands a render later).
  const fcWriteQuestionRef = useRef(fcQuestion)
  useEffect(() => {
    if (fcWriteQuestionRef.current !== fcQuestion) { fcWriteQuestionRef.current = fcQuestion; return }
    if (serverAdvFilters || committedQ) return
    if (serverKindCounts || serverIntentCounts || serverSourceCounts || rangeFacets) {
      writeDeskCache(`fc:${dcKey}`, { kinds: serverKindCounts || null, intents: serverIntentCounts || null, sources: serverSourceCounts || null, ranges: rangeFacets || null })   // F-086: sources ride the same seed
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dcKey, serverKindCounts, serverIntentCounts, serverSourceCounts, rangeFacets])
  // ── COUNTS PREWARM (owner 2026-08-05: "loads too slow, appears minutes later") ─────────
  // The honest numbers — district tree, kind/intent facets, and through them the pool
  // totals — are emitted ONLY by the search RPC (`search_meta`, v34's counted CTE), and the
  // unfiltered browse deliberately takes the cheap recent-feed read, which carries none. So
  // on a plain open every number climbed from loaded rows while the load-more chain walked
  // the corpus: ~3k rows in ~200-row hops was MINUTES of visibly wrong counts, with the true
  // figure arriving last. The dc2/fc caches only mask it for a query seen before.
  // The fix is ONE extra request: a non-default sort alone trips the server's `filtered`
  // gate (the 2026-07-31 sort fix — see SORT_OPTS in server.js), so `sort:'added', limit:1`
  // routes through v34 and returns the corpus-wide meta with a one-row page. ~2s instead of
  // minutes, no server change, and every consumer below (tree, facets, pulse-stop, the
  // cache write-throughs) is already wired to these four setters.
  // B18 discipline (the cheapest request is the one never sent): fires once per dcKey
  // (ref-deduped), never on a special feed (Leads answers from its own feed's meta), never
  // while a filter or committed text is active (the REAL filtered fetch supplies meta for
  // its own scope — and this call answering late would overwrite filtered numbers with
  // whole-corpus ones, so activation also ABORTS an in-flight warm via cleanup), and
  // 1.2s after the deps settle so it never races page-0 for the connection.
  const metaWarmRef = useRef(new Set())
  useEffect(() => {
    if (SPECIAL_FEED) return
    if (serverAdvFilters || committedQ) return
    if (listingsScope === 'mine' && !myAgentId) return
    if (metaWarmRef.current.has(dcKey)) return
    // ── COUNTS ARE FRESH FOR 15 MINUTES (owner 2026-09-24, DESK_COUNTS_FRESH_MS) ────────────
    // This warm is a FULL search (limit:1 + a non-default sort), 2–7 s on the "all" scope, and it
    // re-ran on every mount. Its answer is now kept under `pw:<dcKey>` with the time it was
    // fetched, and replayed through the SAME apply below instead of asked again while it is
    // younger than 15 minutes and older than no own save (deskFreshness). Replaying — not merely
    // skipping — matters: the warm also feeds metro/street/street-coverage facets that dc2/fc/tc
    // do not hold, so a bare skip would leave those without numbers.
    // ⚠️ `pw:` is NOT a protected prefix (deskCache.js): it can be a few tens of KB with the
    // street map, and an evicted entry only costs the warm we used to run anyway.
    const applyWarm = ({ total, searchMeta }) => {
          // The corpus total rides the same response for free — feed it to the tree's
          // fallback (tc:), NEVER to listingTotal itself: that state is the pagination
          // chain's cursor arithmetic and the reduction baseline, and a limit-1 response
          // asserting it would tangle both.
          if (Number.isFinite(total) && total > 0) {
            setCachedListingTotal(total)
            writeDeskCache(`tc:${dcKey}`, total)
          }
          if (!searchMeta) return
          // ⚠️ THIS PREWARM WRITES THE SAME STATE AS THE FILTERED PATH, so it must record the
          // same signature — otherwise the ref would say one question and the state hold another,
          // and the next filtered response would reuse a corpus map believing it matched. The
          // effect is guarded on `!serverAdvFilters && !committedQ`, so its question is by
          // definition the EMPTY filter state; signing it with `null` says exactly that.
          const corpusSig = (family) => facetSignature(
            canonicalFilters(null),
            // review 2026-09-23: real status (this warm sends `status: entryStatusFilter`).
            { archive: entryStatusFilter === 'archived', mine: listingsScope === 'mine', since: entryPeriod || '' },   // F-067 generalised (2026-09-10): the period LABEL, never the instant
            family)
          applyServerFacet('kinds',        searchMeta.kind_counts,         setServerKindCounts,        corpusSig('kinds'))
          applyServerFacet('intents',      searchMeta.intent_counts,       setServerIntentCounts,      corpusSig('intents'))
          applyServerFacet('sources',      searchMeta.source_counts,       setServerSourceCounts,      corpusSig('sources'))   // F-086
          applyServerFacet('districts',    searchMeta.district_counts,     setServerDistrictCounts,    corpusSig('districts'))
          applyServerFacet('districtsOwn', searchMeta.district_own_counts, setServerDistrictOwnCounts, corpusSig('districtsOwn'))
          applyServerFacet('cities',       searchMeta.city_counts,         setServerCityCounts,        corpusSig('cities'))
          applyServerFacet('streets',      searchMeta.street_counts,       setServerStreetCounts,      corpusSig('streets'))
          setRangeFacets(() => {
            const held = resolveRanges(searchMeta.range_facets, rangeSigRef.current, corpusSig)
            rangeSigRef.current = held
            const out = {}
            for (const [k, r] of Object.entries(held)) if (r.value != null) out[k] = r.value
            return Object.keys(out).length ? out : null
          })
          // This effect is guarded on `!serverAdvFilters && !committedQ`, so whatever it
          // returns IS the corpus-wide answer. It must never be written to the scoped state.
          if (searchMeta.metro_counts) setCorpusMetroCounts(searchMeta.metro_counts)
        if (searchMeta?.street_coverage) setServerStreetCoverage(searchMeta.street_coverage)
    }
    const warmed = readDeskCache(`pw:${dcKey}`)
    if (warmed && isFreshWithin(warmed.at, DESK_COUNTS_FRESH_MS)) {
      metaWarmRef.current.add(dcKey)
      applyWarm(warmed)
      return
    }
    const ac = new AbortController()
    const t = setTimeout(() => {
      metaWarmRef.current.add(dcKey)
      // ⚠️ `status` RIDES THIS REQUEST (2026-08-18). Keying dc2/tc/fc on the status was only
      // half — this warm is what FILLS them, and without the word it asked a third question
      // neither view is showing: no status at all, i.e. BOTH piles. Measured on one agent's
      // book: 311 both / 309 active / 2 archived, so the tree's Properties row and the whole
      // district facet were seeded 311 while the feed delivered 309. Two archived rows makes
      // that a rounding error here and a visibly wrong number for anyone with a real archive —
      // and it is the same defect the district-count work (B273) exists to forbid: a facet
      // number that is not the number its own click delivers.
      getAgentListings({ scope: listingsScope, agentId: myAgentId, since: periodSinceISO(entryPeriod), limit: 1, sort: 'added', status: entryStatusFilter, rangeFacets: true, signal: ac.signal })
        .then(({ total, searchMeta }) => {
          // Kept with its fetch time so the next mount within DESK_COUNTS_FRESH_MS replays it
          // instead of re-running the search (see applyWarm's note above).
          writeDeskCache(`pw:${dcKey}`, { at: Date.now(), total, searchMeta: searchMeta || null })
          applyWarm({ total, searchMeta })
        })
        // A failed warm is only a slower paint — the climb it replaced is still running
        // underneath. But UN-mark the key, or this session never retries what one dropped
        // request cost it.
        .catch(() => metaWarmRef.current.delete(dcKey))
    }, 1200)
    return () => { clearTimeout(t); ac.abort() }
    // entryStatusFilter: it is now part of the request AND of dcKey, so it belongs here too —
    // the ref-dedupe keys on dcKey, so the two would otherwise disagree about what "already
    // warmed" means.
  }, [dcKey, SPECIAL_FEED, serverAdvFilters, committedQ, listingsScope, myAgentId, entryPeriod, entryStatusFilter])
  // ── WHOLE-CORPUS MAP PINS (B307, owner 2026-08-05: "is there a way to optimise the
  // map view?") ────────────────────────────────────────────────────────────────────────
  // The map used to draw only the loaded pages — a "128" bubble over a 3,001-object
  // corpus, and panning past the load-more chain's reach showed nothing. One request now
  // brings every coordinate ([listing_id, lat, lng] triples, one per property, ~204 KB),
  // SWR-cached per scope+agent+period so a repeat map open paints instantly.
  // ⚠️ UNFILTERED BROWSE ONLY. Under a filter or a committed search the corpus pins would
  // contradict the filter — 3,368 dots behind a "rooms=2" answer says the filter did
  // nothing. There the map keeps drawing the (server-filtered) loaded rows, as before.
  // Declared HERE, below every dep (SPECIAL_FEED/serverAdvFilters/committedQ) — seventh
  // entry in this file's declare-above-the-consumer ledger, in the other direction.
  // B576 (2026-08-16): the two numbers on the status pills, corpus-wide.
  // ⚠️ Deliberately NOT keyed on `entryStatusFilter` — both numbers describe the whole book,
  // so they must not change when you click between them. A count that moved with the
  // selection would be describing the current view, which is the thing it exists to escape.
  // Depends on scope/agent/period only, so switching status costs no request at all.
  // Failure leaves it null and the pills render without numbers (never 0).
  useEffect(() => {
    if (listingsScope === 'mine' && !myAgentId) { setWorkCounts(null); return }
    let dead = false
    // 2026-09-24 (owner: "cache desk pages heavily"): orientation numbers, fresh for 15 min and
    // never older than the agent's own last save. A cached answer paints at once; only a stale
    // one is re-asked. A failed request never overwrites a good cached answer with "no numbers".
    const scKey = `sc:${listingsScope}:${myAgentId || ''}:${entryPeriod}`
    const cachedSc = readDeskCache(scKey)
    if (cachedSc?.counts) setWorkCounts(cachedSc.counts)
    if (cachedSc?.counts && isFreshWithin(cachedSc.at, DESK_COUNTS_FRESH_MS)) return () => { dead = true }
    getAgentListingStatusCounts({ scope: listingsScope, agentId: myAgentId, since: periodSinceISO(entryPeriod) })
      .then(c => {
        if (dead) return
        if (c) { setWorkCounts(c); writeDeskCache(scKey, { at: Date.now(), counts: c }) }
        else if (!cachedSc?.counts) setWorkCounts(null)
      })
      .catch(() => { if (!dead && !cachedSc?.counts) setWorkCounts(null) })
    return () => { dead = true }
  }, [listingsScope, myAgentId, entryPeriod])

  const [corpusPins, setCorpusPins] = useState(null)
  // The full matching pin set could not be fetched for THIS question, so the map is drawing the loaded
  // rows only (2026-10-23 audit, map section). EntriesMapView says so in words; before, the failure
  // went to the console and the partial map looked like the whole answer.
  const [corpusPinsFailed, setCorpusPinsFailed] = useState(false)
  useEffect(() => {
    setCorpusPinsFailed(false)
    // B719 (owner, 2026-08-15: "fix it so filtered map loads all matching pins"). This used to
    // read `|| serverAdvFilters || committedQ` and NULL the overlay under any search. That half
    // was right — anonymous corpus dots behind a filtered answer say the filter did nothing —
    // and it left nothing in their place, so the map fell back to `groups`, i.e. the rows the
    // feed had fetched: PAGE_SIZE 30 × 3 prefetched pages = 90 pins under a counter reading 273.
    // The cluster badge was the prefetch depth.
    // Now the filters go WITH the request and the server answers with a pin per matching
    // cluster, so the dots are the answer rather than the background. SPECIAL_FEED still bails:
    // those feeds are not a search over this corpus at all.
    if (entriesView !== 'map' || SPECIAL_FEED) { setCorpusPins(null); return }
    if (listingsScope === 'mine' && !myAgentId) { setCorpusPins(null); return }
    const filtered = !!(serverAdvFilters || committedQ)
    // ⚠️ The cache key must carry the filter, or the first filtered map poisons the unfiltered
    // one — and worse, one search's pins are served for another's. `writeDeskCache`'s quota
    // retry evicts on size, never on wrongness.
    // Keys sorted so the signature is stable across renders — an object's insertion order is
    // not a contract, and an unstable key would refetch on every render while never hitting.
    const sig = filtered
      ? JSON.stringify(Object.entries({ ...(serverAdvFilters || {}), __q: committedQ || '' }).sort(([a], [b]) => a < b ? -1 : 1))
      : ''
    // ⚠️ `entryStatusFilter` rides in the SAME key, for the same reason and independently of
    // the filter signature (B576/0600): the pins are status-scoped server-side, so without it
    // the archive map would be served the active map's cached pins — a wrong answer that looks
    // instant and never corrects. Two axes, one key; neither is redundant.
    const pinsKey = `pins:${listingsScope}:${myAgentId || ''}:${entryPeriod}:${entryStatusFilter}:${sig}`
    const cached = readDeskCache(pinsKey)
    // 🔴 2026-09-17 (owner: "when someone filters listings, map view doesn't show filtered
    // results"). This read `if (cached?.pins) setCorpusPins(...)` and otherwise LEFT THE OLD
    // PINS IN PLACE. So on a filter change the previous question's pins — usually the whole
    // unfiltered book — stayed on the map until the new answer arrived, and FOREVER if it never
    // did: the `.catch` below is silent by design, and the server's filtered branch was
    // answering 502 (its search RPC was pinned at v56 while the filter registry had grown 14
    // params v56 does not take). A filtered map drawing every listing is the exact lie B719
    // set out to remove. The pins on screen must always belong to THIS key: the cached answer
    // for it, or nothing — and nothing falls back to the feed's own (server-filtered) rows.
    setCorpusPins(cached?.pins || null)
    // 2026-09-24: a pins answer younger than its TTL (server-sent, 300 s fallback) and than the
    // agent's last own save is not re-asked — reopening the map is then free. Entries written
    // before this change carry no `at` and simply refetch.
    if (cached?.pins && freshnessTier({ fetchedAt: cached.at, ttlMs: cached.ttlMs, dirtyAt: deskDirtyAt() }) === 'fresh') return
    let dead = false
    getAgentListingPins({ scope: listingsScope, agentId: myAgentId, since: periodSinceISO(entryPeriod), status: entryStatusFilter,
      // `serverAdvFilters` is the SAME object this component hands getAgentListings as
      // `filters` (see the feed fetch above), so the map and the feed ask one question.
      // No lead_status here: this effect serves the mine/all scopes, where the pool is not
      // a lead stage — the leads map is LeadsFeed's own surface.
      // 🔴 `status` is honoured on the UNFILTERED pins (`agent_listing_pins_v1` takes it) but
      // the filtered branch runs the search RPC, which has no work-status parameter — the same
      // gap recorded at `clientOnlyCut`. Sent on both so the client is right the moment the
      // server is.
      ...(filtered ? { filters: serverAdvFilters || {} } : {}) })
      .then(r => {
        if (dead || !Array.isArray(r?.pins)) return
        setCorpusPins(r.pins)
        writeDeskCache(pinsKey, { pins: r.pins, at: Date.now(), ttlMs: r.__deskCacheTtlMs ?? null })
      })
      .catch(e => {
        if (dead) return
        // An overlay: a failed fetch leaves the loaded-row map, never an error state, but the map now
        // says it is partial. A cached answer for this same key still on screen is the full set (older),
        // so only a failure with nothing cached is reported.
        console.warn('[desk map] pins request failed — drawing the loaded rows instead', e?.status || e?.message || e)
        if (!cached?.pins) setCorpusPinsFailed(true)
      })
    return () => { dead = true }
  }, [entriesView, SPECIAL_FEED, serverAdvFilters, committedQ, listingsScope, myAgentId, entryPeriod, entryStatusFilter])
  // ⚠️ The write-through lives beside `honestDistrictCounts` (search for B273 move 4c below),
  // not here: that value is declared ~1,800 lines down, and reading it from this effect would be
  // a TDZ crash on mount — the same class of failure the filledFacts note in LineageModal
  // records ("modal doesn't open", 2026-07-21).

  // The AGENT RANKING panel became self-contained (v2, 2026-07-12): its own
  // Daily/Weekly/Monthly window, fed by the 0087 activity_ranking RPC (full-window
  // counts) instead of tallying the loaded feed page — the old scope='all' side
  // fetch and the closuresByParty state that lived here are gone with it.

  // Real offset pagination, not a bigger business-logic cap — an agency with thousands of
  // listings must be able to reach all of them (bounded by the chosen period), not silently lose
  // the tail past whatever number felt "big enough" at the time. Appends to the existing set so
  // it folds straight into the same filter/sort/page-switcher pipeline everything else uses.
  // Coop (🤝) + view (👁) chips are property-scoped and shown on the collapsed card face. Their
  // fetch is DEFERRED to the VISIBLE page (Phase 2d, 2026-07-15) and lives after SPECIAL_FEED
  // below (where "on screen" is known) — the old effects here keyed on `listingEntries` and so
  // refetched EVERY loaded property id on each page append (~200 ids re-pulled per "load more").
  // Only the state + seen-guards are declared here; the fetch + reloadDeskCoops are downstream.
  const coopSeenRef = useRef(new Set())
  // 🔗 client links by property (owner 2026-08-03). Same visible-page batch + seen-guard
  // contract as the cooperation chips above, and for the same reason: the chip has to answer
  // "did I already send this, at what price, did they look" on every card, and one request
  // per card is 80 requests.
  // ⚠️ {} entry = fetched-and-none, which is what draws the DASHED ＋ door. A MISSING entry
  // means "not asked yet" and draws nothing. The two must stay distinct: collapsing them
  // makes an unfetched card advertise "no links sent" for a property that has them, which is
  // the same unknown-shown-as-none bug the cooperation effect's own comment records.
  const [sharesByProperty, setSharesByProperty] = useState({})
  const shareSeenRef = useRef(new Set())
  const reshareSeenRef = useRef(new Map())   // property_id -> when its reshare status was last asked
  const defectSeenRef = useRef(new Set())
  const [viewsByProperty, setViewsByProperty] = useState({})
  const viewSeenRef = useRef(new Set())

  // Consecutive load-more failures — the auto-prefetch effect retriggers whenever
  // listingLoadingMore flips back to false, so WITHOUT this gate a failing fetch (server
  // down, missing dev credentials) retries in an infinite storm (caught live 2026-07-11;
  // same class as the 2026-07-01 street-matcher concurrency bug). 3 strikes stops the
  // auto-chain; the manual "Load more" button still works and a success resets the count.
  const loadMoreFailsRef = useRef(0)
  // Generation stamp: bumped whenever the main load effect starts a new fetch (scope/period/
  // filter change). A load-more that resolves under a DIFFERENT generation belongs to the old
  // snapshot — applying it would append old-window rows and corrupt the offset cursor.
  const loadGenRef = useRef(0)
  // Synchronous in-flight guard (2026-07-15): listingLoadingMore is STATE, so two prefetch-effect
  // runs landing in the same commit window both read it as false and fired the same offset twice
  // (observed live at the chain's tail — concurrent duplicate offset=800 requests). A ref flips
  // synchronously at call entry, closing that window; the state flag stays for the UI spinner.
  const loadMoreBusyRef = useRef(false)
  // Cursor exhausted (2026-07-15): a chunk returning 0 rows means the server has nothing more at
  // this cursor even if rawCount < listingTotal (total can drift from what paging actually yields
  // — rows written/removed between the count and the fetch). Without this the prefetch effect
  // re-fires the same empty offset until the numbers happen to converge. Reset alongside
  // loadGenRef in the main load effect — a new snapshot gets a fresh chain.
  const chainExhaustedRef = useRef(false)
  // ABORT the superseded chain, don't merely discard it (2026-08-10). The `gen` check below
  // has always dropped a stale load-more's ROWS, but the request itself ran to completion:
  // 4.4s of RPC work and one of the browser's six connection slots, spent answering a
  // question the agent has already changed. Measured on a filtered leads open — every filter
  // change fired an offset=60 page for the OLD filters that STARTED FIRST (the chain effect
  // reacts before the filter debounce commits) and therefore landed ahead of page 0 of the
  // new one. Same reasoning as the main fetch's own AbortController two hundred lines up.
  // ⚠️ An abort must not count toward loadMoreFailsRef — three of those disable the auto-chain
  // for the session, and a superseded request is not a failure.
  const chainAbortRef = useRef(null)
  // B1214 step 6 (2026-09-05): ONE key rule for every page of a search. The page-0 fetch, this
  // load-more, and the idle warm below all key by queryKey() with the page's offset, so a page the
  // warm already fetched is a cache hit here (no request, no wait), and a page this fetched is a hit
  // for a later restore. `pageKeyFor` is the one place the parameters are assembled.
  const pageKeyFor = (offset) => queryKey({ scope: listingsScope, viewer: myAgentId || '', pool: listingsScope === 'leads' ? '' : (entryStatusFilter || ''), period: entryPeriod, sort: entrySort, filters: serverAdvFilters || null, offset, limit: serverAdvFilters ? FILTERED_PAGE : LISTING_PAGE })
  const fetchPage = (offset, signal, prefetch = false) => getAgentListings({   // F-085: `prefetch` marks the speculative next-page warm on the wire
        scope: listingsScope, agentId: myAgentId, since: periodSinceISO(entryPeriod), prefetch,
        // Same rule as page 0 — a filtered chain keeps paging in 30s. Offset paging tolerates
        // a varying page size (the offset is the running raw count, not page × size), but the
        // two must agree on the QUESTION, which is what the note below is about.
        limit: serverAdvFilters ? FILTERED_PAGE : LISTING_PAGE, offset, signal,
        // Same reason the sort and filters are pinned here: offset pagination is only
        // coherent while every page asks the SAME question. A page 2 fetched without the
        // status would append archived rows under an active page 1.
        status: entryStatusFilter,
        // SAME sort AND SAME filter view as the initial fetch (serverAdvFilters, B177) —
        // offset pagination is only coherent when every page of one snapshot asks the
        // same question; a local-only key here would desync the chain from page 0.
        // ⚠️ Which is why `sort` is now top-level here too: while it rode inside `filters`,
        // an UNFILTERED sorted feed sent it on neither page — so page 2 arrived ordered by
        // something else entirely and appended under page 1 as if it belonged.
        filters: serverAdvFilters, sort: SERVER_SORT_OF[entrySort] || null,
      })
  // Warm the page after `offset` into the cache when the browser is idle (rule 4). Never touches
  // state, never runs twice for one key (dedupeFetch), skipped when the page is already cached or
  // the snapshot has moved on. One page ahead only — the owner's rule against speculative traffic.
  const warmNextPage = (offset) => {
    const gen = loadGenRef.current
    const run = async () => {
      if (gen !== loadGenRef.current) return
      const key = pageKeyFor(offset)
      // 2026-09-24: "already cached" means cached within the owner's 15-minute bound and after the
      // last own save — not merely present (entries lived 6 h before, so a morning's page 2 was
      // served under an afternoon's page 0). Same test as loadMoreListings' read below.
      const have = await readQuery(key, { maxAgeMs: DESK_STALE_MAX_MS }).catch(() => null)
      if (have && have.fetched_at >= deskDirtyAt()) return
      try {
        const page = await dedupeFetch(key, () => fetchPage(offset, undefined, true))   // F-085: a warm is a prefetch
        if (page && Array.isArray(page.listings)) await writeQuery(key, { listings: page.listings, rowsReturned: page.rowsReturned, countUnit: page.countUnit, warmed: true })
      } catch { /* a warm that fails costs nothing */ }
    }
    if (typeof globalThis.requestIdleCallback === 'function') globalThis.requestIdleCallback(() => { run() }, { timeout: 4000 })
    else setTimeout(run, 800)
  }
  // ── HOVER WARM: the page a click is ABOUT to ask for (2026-09-24, owner: "cache desk pages
  // heavily") ─────────────────────────────────────────────────────────────────────────────────
  // Hovering a status segment (active/archived) or a sort option warms page 0 of exactly the
  // request that click will make, so the click paints from cache. Rules, each load-bearing:
  //   * the key is built from the SAME parts as the fetch effect's `cacheKey` (queryKey, the same
  //     limit expression) — a warm under any other key warms nothing, silently;
  //   * debounced (a cursor passing over the row is not a hover) and ONE AT A TIME — a second
  //     hover while one warm is in flight is dropped, never queued;
  //   * skipped when that page is already fresh (deskFreshness);
  //   * never from typing: the search box commits on Enter (owner ruling), and nothing here
  //     listens to it.
  // The in-flight warm is published in hoverWarmInflightRef so a click that lands mid-warm JOINS
  // it (see goNetwork) instead of sending the same search twice. It carries no AbortSignal, which
  // is what makes joining it safe — an aborted shared promise would strand the feed.
  function warmFeedPageNow({ status = entryStatusFilter, sort = entrySort } = {}) {
    if (SPECIAL_FEED || hoverWarmBusyRef.current) return
    if (listingsScope === 'mine' && !myAgentId) return
    const filters = serverAdvFilters
    const key = queryKey({ scope: listingsScope, viewer: myAgentId || '', pool: listingsScope === 'leads' ? '' : (status || ''), period: entryPeriod, sort, filters: filters || null, offset: 0, limit: filters ? FILTERED_PAGE : LISTING_FIRST_PAGE })
    hoverWarmBusyRef.current = true
    const run = async () => {
      const hit = await readQuery(key).catch(() => null)
      if (hit && hit.searchMeta !== undefined && freshnessTier({ fetchedAt: hit.fetched_at, ttlMs: hit.cacheTtlMs, dirtyAt: deskDirtyAt() }) === 'fresh') return null
      const r = await getAgentListings({ scope: listingsScope, agentId: myAgentId, since: periodSinceISO(entryPeriod), limit: filters ? FILTERED_PAGE : LISTING_FIRST_PAGE, offset: 0, filters, sort: SERVER_SORT_OF[sort] || null, status, rangeFacets: true, prefetch: true, unknownLimit: filters ? 0 : undefined })
      await writeQuery(key, { listings: r.listings, total: r.total, rowsReturned: r.rowsReturned, countUnit: r.countUnit, unknownExcluded: r.unknownExcluded, searchMeta: r.searchMeta ?? null, cacheTtlMs: r.cacheTtlMs ?? null, ...(filters ? { possibleListings: r.possibleListings || [] } : {}) })
      return r
    }
    const p = run().catch(() => null).finally(() => { hoverWarmBusyRef.current = false; if (hoverWarmInflightRef.current.get(key) === p) hoverWarmInflightRef.current.delete(key) })
    hoverWarmInflightRef.current.set(key, p)
  }
  function warmFeedPage(over) {
    clearTimeout(hoverWarmTimerRef.current)
    hoverWarmTimerRef.current = setTimeout(() => warmFeedPageNow(over), 250)
  }
  async function loadMoreListings() {
    if (loadMoreBusyRef.current) return
    loadMoreBusyRef.current = true
    const gen = loadGenRef.current
    const ac = new AbortController()
    chainAbortRef.current = ac
    setListingLoadingMore(true)
    try {
      const offset = listingRawCount
      const key = pageKeyFor(offset)
      // A later page is reused inside the owner's 15-minute bound and only if written after the
      // agent's last own save (2026-09-24, deskFreshness) — it was 6 h, which could append a
      // morning's page 2 under a page 0 fetched a minute ago.
      const got = await readQuery(key, { maxAgeMs: DESK_STALE_MAX_MS }).catch(() => null)
      const hit = got && got.fetched_at >= deskDirtyAt() ? got : null
      if (gen !== loadGenRef.current) return
      if (hit) perfMark('desk:page-from-cache', { lastPageSource: hit.source, lastPageOffset: offset })
      const { listings, rowsReturned, countUnit } = hit || await dedupeFetch(key, () => fetchPage(offset, ac.signal))
      if (gen !== loadGenRef.current) return   // snapshot changed mid-flight — discard
      if (!hit) { writeQuery(key, { listings, rowsReturned, countUnit }).catch(() => {}); perfMark('desk:page-from-network', { lastPageSource: 'network', lastPageOffset: offset }) }
      if (rowsReturned > 0) warmNextPage(offset + rowsReturned)
      if (rowsReturned === 0) chainExhaustedRef.current = true
      for (const l of listings) fetchedIdsRef.current.add(l.listing_id)
      setListingEntries(prev => dedupeListings([...prev, ...listings]))
      setListingRawCount(prev => prev + rowsReturned)
      setListingCountUnit(countUnit)
      loadMoreFailsRef.current = 0
    } catch (e) {
      if (e.name === 'AbortError') return   // superseded on purpose — not a strike
      loadMoreFailsRef.current++; console.error('[agent-listings load-more]', e.message)
      // ⚠️ B884: release the duplicate-offset guard on FAILURE. A failed page leaves
      // `listingRawCount` where it was, so without this the guard would read the retry as the
      // duplicate it is designed to block and the chain would stop dead at the first network
      // blip — silently converting `loadMoreFailsRef`'s three allowed attempts into one. The
      // guard exists to stop the SAME page being fetched twice concurrently, never to stop a
      // page being fetched again after it failed.
      chainOffsetRef.current = -1
    }
    finally {
      if (chainAbortRef.current === ac) chainAbortRef.current = null
      loadMoreBusyRef.current = false; setListingLoadingMore(false)
    }
  }

  // Next page of the POSSIBLE-MATCHES shelf (task #11). Deliberately its own fetch rather
  // than a wider `limit` on the main one: the two sets have different orderings and
  // different sizes, and the matched page must not re-render because the agent wanted more
  // shelf. Only `possibleListings` from the response is consumed — everything else in it is
  // page 0 again, and merging it would reset the main chain's cursor.
  // Same abort discipline as chainAbortRef above — a shelf page for the old question is
  // the most expensive request on the desk (it re-computes the matched set to define the
  // shelf against) and the least worth finishing once the filters have moved.
  const possiblesAbortRef = useRef(null)
  // The shelf's FIRST page (2026-08-10) — what used to arrive bundled in the main filtered
  // fetch, before that fetch started asking for `unknownLimit: 0` (see its own comment).
  // Called once per snapshot, from that fetch's own .then, never from a click — so unlike
  // loadMorePossibles it takes the filters/generation as ARGUMENTS rather than reading current
  // state: by the time this resolves, the agent may have already typed a newer search, and
  // `serverAdvFilters`/`possiblesOffset` would then name that one, not the response this page
  // belongs to. `possiblesOffset` lands at SHELF_PAGE on success so loadMorePossibles' own
  // `next = possiblesOffset + SHELF_PAGE` correctly opens at the SECOND page from here.
  const shelfObserverRef = useRef(null)   // the sentinel's IntersectionObserver (see shelfSentinelRef)
  const shelfNodeRef = useRef(null)       // …and the sentinel element itself
  // Arm (not fetch) the shelf for a freshly landed page 0. See pendingShelfRef's declaration.
  function armShelf(filtersForShelf, gen) {
    pendingShelfRef.current = { filters: filtersForShelf, gen }
    setShelfWanted(false)          // a new question: its shelf must earn its own visibility
    setShelfArmTick(n => n + 1)
    // ⚠️ An IntersectionObserver reports CHANGES, plus one initial entry per observe(). A sentinel
    // that simply stays on screen across two searches would never report again, and the second
    // search's shelf would never load. Re-observing forces that initial entry for the new question.
    const io = shelfObserverRef.current, node = shelfNodeRef.current
    if (io && node) { io.unobserve(node); io.observe(node) }
  }
  // The sentinel: a zero-height marker where the shelf renders. Callback ref so it (re)attaches
  // whenever the element mounts; 800px of margin so the request leaves a beat before the agent
  // actually reaches the shelf. No IntersectionObserver (old browser) → want it at once, which is
  // exactly the previous behaviour.
  const shelfSentinelRef = useCallback(node => {
    shelfObserverRef.current?.disconnect(); shelfObserverRef.current = null
    shelfNodeRef.current = node
    if (!node) return
    if (typeof IntersectionObserver !== 'function') { setShelfWanted(true); return }
    const io = new IntersectionObserver(entries => {
      if (entries.some(e => e.isIntersecting)) setShelfWanted(true)
    }, { rootMargin: '800px 0px' })
    io.observe(node)
    shelfObserverRef.current = io
  }, [])
  useEffect(() => {
    const p = pendingShelfRef.current
    if (!shelfWanted || !p) return
    if (p.gen !== loadGenRef.current) { pendingShelfRef.current = null; return }   // armed for an abandoned question
    pendingShelfRef.current = null
    loadInitialPossibles(p.filters, p.gen)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shelfWanted, shelfArmTick])
  // A failed first page put its arm back (see the catch below); the retry spends it at offset 0.
  // Not loadMorePossibles: its next offset would skip the page that never arrived.
  function retryInitialPossibles() {
    const p = pendingShelfRef.current
    if (!p || p.gen !== loadGenRef.current) return
    pendingShelfRef.current = null
    loadInitialPossibles(p.filters, p.gen)
  }
  async function loadInitialPossibles(filtersForShelf, gen) {
    if (!filtersForShelf || possiblesBusyRef.current) return
    // The lock and the spinner belong to THIS run (2026-10-23 audit, Desk §5): see releaseRun below.
    const run = ownRun(possiblesBusyRef)
    const ac = new AbortController()
    possiblesAbortRef.current = ac
    setPossiblesLoading(true); setPossiblesFailed(false)
    try {
      // 2026-09-24: the shelf's first page is CACHED like any page — same freshness rule
      // (lib/deskFreshness.js). A fresh answer for this exact question is used without asking,
      // which removes the second full search from a repeated filtered click entirely.
      // ⚠️ Keyed on exactly what the request below sends and nothing else: queryKey over the same
      // filters + sort, the shelf page, and a `|shelf` suffix so it can never collide with page 0.
      // Owner 2026-09-24: the shelf follows the active/archived choice exactly like the main search
      // (it used to send no `status`), so the pool is in both the request and the key.
      const shelfPool = listingsScope === 'leads' ? '' : (entryStatusFilter || '')
      const shelfKey = queryKey({ scope: listingsScope, viewer: myAgentId || '', pool: shelfPool, period: entryPeriod, sort: entrySort, filters: filtersForShelf, offset: 0, limit: 1, unknownOffset: 0 }) + `|shelf:${SHELF_PAGE}`
      const hit = await readQuery(shelfKey).catch(() => null)
      if (gen !== loadGenRef.current) return
      const fresh = hit && freshnessTier({ fetchedAt: hit.fetched_at, ttlMs: hit.cacheTtlMs, dirtyAt: deskDirtyAt() }) === 'fresh'
      const answer = fresh
        ? { possibleListings: hit.listings, nearbyListings: hit.nearbyListings || [], searchMeta: { street_nearby: hit.streetNearby || null } }
        : await getAgentListings({
            scope: listingsScope, agentId: myAgentId, since: periodSinceISO(entryPeriod),
            limit: 1, offset: 0, unknownOffset: 0, unknownLimit: SHELF_PAGE, signal: ac.signal,
            status: entryStatusFilter,
            filters: { ...filtersForShelf, sort: SERVER_SORT_OF[entrySort] || null },
          })
      const { possibleListings, nearbyListings, searchMeta } = answer
      if (!fresh) writeQuery(shelfKey, { listings: possibleListings, nearbyListings: nearbyListings || [], streetNearby: searchMeta?.street_nearby || null, cacheTtlMs: answer.cacheTtlMs ?? null }).catch(() => {})
      if (gen !== loadGenRef.current) return   // a newer snapshot started before this landed
      setListingPossibles(possibleListings)
      // Q4: the THIRD verdict tier rides this request (see core.js — it is asked for only
      // here, never on the critical path and never on a later shelf page). Both are set
      // unconditionally, including to empty: leaving the previous search's nearby rows on
      // screen under a new street is the one thing worse than not showing them at all.
      setListingNearby(nearbyListings || [])
      setStreetNearbyMeta(searchMeta?.street_nearby || null)
      setPossiblesOffset(SHELF_PAGE)
      if (!possibleListings.length) possiblesDoneRef.current = true
    } catch (e) {
      if (e.name !== 'AbortError') {
        console.error('[agent-listings shelf-initial]', e.message)
        // Still this question: re-arm it and say so on the shelf, which offers the retry.
        if (gen === loadGenRef.current) { pendingShelfRef.current = { filters: filtersForShelf, gen }; setPossiblesFailed(true) }
      }
    }
    finally {
      if (possiblesAbortRef.current === ac) possiblesAbortRef.current = null
      // An older run returning late (an uncancellable cache read) used to clear the NEWER run's lock
      // and spinner here. Only the owner releases them now.
      if (releaseRun(possiblesBusyRef, run)) setPossiblesLoading(false)
    }
  }
  async function loadMorePossibles() {
    if (possiblesBusyRef.current || possiblesDoneRef.current || !serverAdvFilters) return
    const run = ownRun(possiblesBusyRef)
    const gen = loadGenRef.current
    const ac = new AbortController()
    possiblesAbortRef.current = ac
    const next = possiblesOffset + SHELF_PAGE
    setPossiblesLoading(true)
    try {
      const { possibleListings } = await getAgentListings({
        scope: listingsScope, agentId: myAgentId, since: periodSinceISO(entryPeriod),
        // limit: 1 — we want the shelf, not another matched page. The RPC still computes
        // the matched set (it is what the shelf is defined against), but shipping 200 rows
        // we will discard is pure transfer cost on a 3s query.
        limit: 1, offset: 0, unknownOffset: next, signal: ac.signal,
        status: entryStatusFilter,   // owner 2026-09-24: the shelf follows active/archived like page 0
        // SAME filter view and sort as page 0, for the same reason offset pagination needs
        // it on the main chain: a shelf paged under a different question is not the same shelf.
        filters: { ...serverAdvFilters, sort: SERVER_SORT_OF[entrySort] || null },
      })
      if (gen !== loadGenRef.current) return   // snapshot changed mid-flight — discard
      if (!possibleListings.length) { possiblesDoneRef.current = true; return }
      setPossiblesOffset(next)
      // Dedupe by listing id: the shelf's ordering is stable but not guaranteed unique
      // across pages if evidence lands between two fetches, and a duplicated card reads as
      // a bug in the data rather than a race in the paging.
      setListingPossibles(prev => {
        const seen = new Set(prev.map(l => l.listing_id))
        return [...prev, ...possibleListings.filter(l => !seen.has(l.listing_id))]
      })
    } catch (e) { if (e.name !== 'AbortError') console.error('[agent-listings shelf-page]', e.message) }
    finally {
      if (possiblesAbortRef.current === ac) possiblesAbortRef.current = null
      if (releaseRun(possiblesBusyRef, run)) setPossiblesLoading(false)   // same ownership rule as the first page
    }
  }

  // The match-count enrichment loadHistory parked because the visible feed could not show it
  // (see its comment). Held as a thunk, not as ids: it closes over the exact proposals list
  // and cache key that fetch belongs to, and running it against a later one would rewrite the
  // wrong cache entry. Cleared on every loadHistory, so a stale feed's pills never land.
  const matchCountsPendingRef = useRef(null)
  useEffect(() => {
    if (SPECIAL_FEED || !matchCountsPendingRef.current) return
    const run = matchCountsPendingRef.current
    matchCountsPendingRef.current = null
    run()
  }, [SPECIAL_FEED])

  // Generation guard (review 2026-09-23): the period fetch (100 rows) and the all-history
  // fetch (500, slow) race whenever a search is cleared, and the one that lands LAST used to
  // win — the period feed then showed all-time rows. Only the newest call may write.
  const histGenRef = useRef(0)
  async function loadHistory(opts) {
    // allowFresh (2026-09-24): ONLY the mount / period / scope effect passes it. Every post-save call
    // (onSaved → loadHistory()) must reach the server — the owner's rule that an agent's own save
    // refreshes what it changed at once. `opts` may be whatever an onSaved hands over, so it is read
    // defensively rather than destructured (a null would throw).
    const allowFresh = !!(opts && opts.allowFresh === true)
    const gen = ++histGenRef.current
    setHistLoading(true)
    // 100 (the server's own cap), not a small page size — the filter/search/sort/page controls
    // need enough rows in hand to do something; `since` (from entryPeriod) is what actually
    // bounds "recent" now, the row count is just a safety ceiling under it.
    try {
      const companyWide = role === 'admin' || role === 'general_manager'
      const proposalsScope = companyWide ? undefined : myAgentId
      // A scoped viewer whose agent id has not resolved yet would otherwise read and write
      // `hist:all:…` — the SAME key an admin's company-wide feed is cached under, so the next
      // agent on a shared computer was painted the admin's client list. No key, no cache.
      const cacheable = companyWide || !!proposalsScope
      // Stale-while-revalidate, same pattern as the agent-listings fetch above: paint the last
      // cached response instantly (the home's Recent-clients column visibly lagged the property
      // cards on every open, 2026-07-17), then the fetch always overwrites it.
      // Option B (2026-07-18): an active search/filter widens the feed to all history — see the
      // `histAllTime` trigger above. Cache key includes the all-time-ness so the period feed and
      // the all-time feed never collide in localStorage.
      const since = histAllTime ? null : periodSinceISO(entryPeriod)
      const cacheKey = `hist:${proposalsScope || 'all'}:${histAllTime ? 'all' : entryPeriod}`
      const cached = cacheable ? readDeskCache(cacheKey) : null
      if (cached) { setHistoryReconciled(cached.proposals); setSearchPolygons(cached.searchPolygons || {}) }
      // Fresh (younger than the owner's 5 min and than the last own save — deskFreshness) → the
      // cached feed IS the answer; no two proposals requests. The match-count pills were merged
      // into the cached copy when it was written, so nothing is lost. Entries from before this
      // change carry no `__at` and refetch.
      if (allowFresh && cached && isFreshWithin(cached.__at, DESK_FRESH_DEFAULT_MS)) return
      // 500 when searching all-history (server cap raised to match), 100 for the default feed —
      // keeps the default lean while an active search reaches the whole corpus (Option 2, 2026-07-18).
      // Two windows, not one — clients get their own (mergeProposalFeeds' header has the measurement).
      // The client fetch fails OPEN to the mixed feed: it can only add rows inside the same scope.
      const feedLimit = histAllTime ? 500 : 100
      const [mixedFeed, clientFeed] = await Promise.all([
        getRecentProposals(feedLimit, since, proposalsScope),
        getRecentProposals(feedLimit, since, proposalsScope, 'client').catch(() => null),
      ])
      const { proposals, searchPolygons: polys } = mergeProposalFeeds(mixedFeed, clientFeed)
      if (gen !== histGenRef.current) return          // a newer loadHistory owns the feed now
      setHistoryReconciled(proposals); setSearchPolygons(polys)
      const histAt = Date.now()   // the fetch time — the pills rewrite below keeps it, never re-stamps
      if (cacheable) writeDeskCache(cacheKey, { proposals, searchPolygons: polys, __at: histAt })
      // Deferred match-count pills (2026-07-17): the proposals fetch skips them (counts=0 —
      // they were its main latency), so merge them in once the cards are already on screen.
      // Merge by requirement_id into CURRENT state, not the closure's `proposals` — a save/
      // refresh may have landed in between. Cache rewrite uses the closure copy deliberately
      // (it matches what this fetch cached above).
      // ⚠️ NOT on a special feed (2026-08-10). `match_count` renders on ONE surface — the
      // CLIENT card's action strip (HistoryCards §objType === 'client') — and a special feed
      // (Leads/Meeting/Business/…) renders its own component, so not one of these counts can
      // reach a pixel there. It is not free: under an active filter `histAllTime` widens this
      // fetch to 500 proposals, and getProposalMatchCounts chunks ids at 80, so the invisible
      // pills fan out into ~7 requests. Measured on a cold admin open of a filtered leads
      // search: 17.9s + 12.8s + 5.1s, all of it contending with the agent-listings page the
      // agent is actually waiting on. Deferred rather than dropped — leaving a category the
      // pills DO show on must still get them, so the enrichment is parked and the effect
      // below runs it the moment the feed can display it.
      const reqIds = [...new Set(proposals.map(p => p.result?.requirement_id).filter(Boolean))]
      const enrich = () => getProposalMatchCounts(reqIds).then(counts => {
        if (!Object.keys(counts).length) return
        const merge = list => list.map(p => {
          const rid = p.result?.requirement_id
          return rid && rid in counts ? { ...p, match_count: counts[rid] } : p
        })
        if (gen !== histGenRef.current) return
        setHistory(merge)
        if (cacheable) writeDeskCache(cacheKey, { proposals: merge(proposals), searchPolygons: polys, __at: histAt })
      }).catch(() => { /* pills are optional — the feed already rendered */ })
      matchCountsPendingRef.current = null
      if (!reqIds.length) { /* nothing to count */ }
      else if (SPECIAL_FEED) matchCountsPendingRef.current = enrich
      else enrich()
    } catch (e) { console.error(e) }
    finally { if (gen === histGenRef.current) setHistLoading(false) }
  }

  // Refetch-on-focus (mobile, 2026-07-18 eBay redesign): the ↻ button is desktop-only
  // now (see the command strip's `hidden md:contents` row), so on mobile the feed
  // refreshes when the app/tab returns to the foreground — the honest answer to "did new
  // entries arrive while I was away?" without a manual button. This mirrors exactly what
  // ↻ did (loadHistory — the Recent-Entries proposals feed); the property-card feed keeps
  // its own filter/scope revalidation, and reaching into its cursor/generation machinery
  // from a focus handler is the flip-flop trap the feed comments warn about, so it's left
  // alone. Latest-closure ref so a fresh entryPeriod/role is always read; 30s throttle so
  // rapid app-switches don't hammer the endpoint; visibilitychange fires only on real
  // transitions, so this never runs on first mount.
  const loadHistoryRef = useRef(loadHistory)
  loadHistoryRef.current = loadHistory
  // A meeting booked from a surface that has no refresh prop (the match panels) announces
  // itself with a window event; the feed and the 📅 chips refresh as they do for the Desk's
  // own door, so nobody re-books from a stale chip (review 2026-09-23).
  useEffect(() => {
    const onCreated = () => { loadHistoryRef.current?.(); getMeetingsFeed().then(setViewings).catch(() => {}) }
    window.addEventListener('sntv:meeting-created', onCreated)
    return () => window.removeEventListener('sntv:meeting-created', onCreated)
  }, [])
  useEffect(() => {
    if (!isMobile) return
    let lastAt = 0
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return
      const now = Date.now()
      if (now - lastAt < 30000) return
      lastAt = now
      loadHistoryRef.current()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [isMobile])

  function resetIntake() {
    setEnvelope(null); setIntakeId(null); setAnchors(null); setUsage(null)
    setAiHint(null); setActiveModal(null); setRawText(''); setAmendTargetId(null)
  }

  async function handleParse() {
    if (!rawText.trim()) return
    setParsing(true); setParseErr(null); setCommitMsg(null)
    setEnvelope(null); setUsage(null); setAmendTargetId(null); setPropertyDraft(null)
    try {
      // ONE classifier first. buildEnvelopePrompt (server) now returns `about` — the sender's
      // STANCE: demand (looking to buy/rent) | supply (selling/renting out) | meeting | ambiguous
      // — plus a confidence. We route on that instead of running both parsers and guessing with a
      // tie-break (the old approach mis-read a buyer's desired AREA as a listing address, 2026-07-03).
      // Only a confident, unambiguous stance auto-opens a modal; otherwise the human picks.
      const { intake_id, envelope: env, anchors: a, usage: u } = await parseIntakeEnvelope({ raw_text: rawText, channel })
      setIntakeId(intake_id); setAnchors(a); setEnvelope(env); setUsage(u)

      const about = env.about || 'ambiguous'
      const confident = (env.confidence ?? 0) >= CONFIDENCE_THRESHOLD
      const hasClient = extractRequirementCmd(env) || amendRequirementCmd(env)
      const hasMeeting = !!env.meeting

      // Confident SUPPLY → parse the listing now (the demand parser deliberately returned no
      // commands for a seller) and open the property draft to review.
      if (confident && about === 'supply') {
        const pr = await parseListingText({ raw_text: rawText, channel, agent_id: myAgentId }).catch(() => null)
        if (pr?.draft) {
          setAiHint('property')
          setPropertyDraft({ id: pr.proposal_id, proposed: { input: pr.draft }, missing_fields: pr.missing_fields, intake: { raw_text: rawText } })
          setActiveModal('property'); loadHistory(); return
        }
        // listing parse failed → fall through to the human picker below
      } else if (confident && about === 'demand' && hasClient) {
        setAiHint('client'); setActiveModal('client'); return
      }
      // THE AI PRE-FILLS A MEETING, IT NEVER BOOKS ONE (owner 2026-09-23: "fill it", superseding
      // the 2026-07-24 "AI shouldn't intake meetings"). The server already extracts the date,
      // time, client and a property hint and the Desk used to throw them away, so the agent
      // re-typed what the model had read. A confident meeting paste now opens the SAME add-meeting
      // door a human uses, with the property search pre-typed from the hint; the picked row then
      // carries the date / time / notes / client into the form (envelope.meeting). Nothing is
      // saved until the agent presses Create — property, client and both representatives are
      // still matched to core records by a human, never inferred.
      if (confident && about === 'meeting' && hasMeeting) {
        setAiHint('meeting'); openModal('meeting'); return
      }

      // Confidently nothing actionable (greeting / follow-up chit-chat) — NOT ambiguity, so don't
      // alarm-glow; show the calm noop note instead.
      if (confident && about !== 'ambiguous' && about !== 'supply' && env.decision === 'noop' && !hasMeeting && !hasClient) {
        setAiHint('noop'); setActiveModal(null); return
      }

      // Ambiguous or low confidence → let the human pick. The Desk buttons glow; whichever they
      // choose opens pre-filled — client from this envelope, meeting from meetingSeed, property
      // parsed lazily on click (see openModal) so an unpicked draft never litters the pending list.
      setAiHint('undecided'); setActiveModal(null)
    } catch (e) { setParseErr(e.message) }
    finally { setParsing(false) }
  }

  // meetingSeed only PRE-FILLS ViewingForm's contact/date/notes fields — property and agent
  // stay free-text hints the human matches by hand (see buildEnvelopePrompt: "never invent an
  // id"). Recomputed from the envelope so it's available whether the modal auto-opened or the
  // human clicked "Add meeting" manually after an unrelated (e.g. client) auto-open.

  // effectiveMode/effectiveTarget: the AI's own amend pick, OR a human clicking a "possible
  // match" row in Recent Entries to override an AI "new" call — either way, never automatic.
  const aiAmendCmd = envelope ? amendRequirementCmd(envelope) : null
  const aiCreateCmd = envelope ? extractRequirementCmd(envelope) : null
  const effectiveMode = amendTargetId ? 'amend' : (aiAmendCmd ? 'amend' : (aiCreateCmd ? 'create' : null))
  const effectiveTargetId = amendTargetId || aiAmendCmd?.target_requirement_id || null

  async function handleAcceptClient(payload, wasEdited, clarifyLog) {
    setCommitting(true); setCommitMsg(null)
    try {
      let finalCmd
      if (effectiveMode === 'amend') {
        const base = aiAmendCmd && !amendTargetId
          ? aiAmendCmd
          : { type: 'amend_requirement', target_requirement_id: effectiveTargetId, person_party_id: null, ops: [] }
        finalCmd = { ...base, type: 'amend_requirement', target_requirement_id: effectiveTargetId, ops: payload.ops }
      } else {
        finalCmd = viewToCmd(payload, aiCreateCmd)
      }
      finalCmd.human_edited = wasEdited   // the confirmation signal — set by hand-editing, not a checkbox
      if (clarifyLog?.length) finalCmd.clarification_answers = clarifyLog
      const finalEnvelope = { ...envelope, commands: [finalCmd] }
      const resp = await commitIntakeEnvelope({ raw_text: rawText, channel, envelope: finalEnvelope, anchors, intake_id: intakeId, agent_id: myAgentId })
      // The HTTP call succeeding does NOT mean the gateway executed — a rejected proposal
      // returns 200 with executed:false per command (bug found 2026-07-02: an amend with bad
      // op fields froze at 'accepted' while the UI said Saved). Check every command's outcome.
      const failed = (resp.results || []).filter(r => !r.executed)
      if (failed.length) {
        const why = failed.map(f => f.result?.message || f.result?.error || f.error?.message || 'gateway refused').join('; ')
        setCommitMsg(tLog('desk_misc_not_saved', { reason: why }))
        loadHistory()   // the frozen proposal is visible in Recent entries; input kept for a retry
      } else {
        setCommitMsg(t('desk_toast_client_saved'))
        resetIntake()
        loadHistory()
      }
    } catch (e) { setCommitMsg(tLog('desk_misc_error_prefix', { message: e.message })) }
    finally { setCommitting(false) }
  }

  // handleMeetingSaved + the AI meeting modal were REMOVED 2026-07-24 (owner:
  // "AI shouldn't intake meetings"). Nothing here creates a meeting from a paste
  // any more; meetings are built from core records via MeetingModal's three seeds
  // (core/MEETINGS_MODEL.md §3). saveMeetingProposal is likewise gone — historical
  // schedule_meeting proposals still RENDER in Recent Entries, we just never write
  // another one.

  function handleRejectClient() {
    setActiveModal(null)
    setCommitMsg(t('desk_misc_rejected_not_saved'))
  }

  async function openModal(id) {
    // 'candidate' is not a Desk object and has no modal here: it lives in the Candidates
    // tab, which owns the create path. The request goes to App (the only component that
    // can change tabs) as an event, so this menu and the phone's "+" reach the same one
    // destination — see the listener in App.jsx.
    if (id === 'candidate') { window.dispatchEvent(new Event('sentavus:add-candidate')); return }
    // Disambiguation pick: the human resolved an ambiguous paste by choosing "property". The
    // listing parser wasn't run up front (so an unpicked draft never litters the pending list),
    // so run it lazily NOW to pre-fill the modal from what the AI can extract. Client/meeting
    // already have their data (envelope / meetingSeed), so they need no extra call.
    if (id === 'property' && aiHint === 'undecided' && rawText.trim() && !propertyDraft) {
      setParsing(true)
      try {
        // Race against a timeout: this await used to gate setActiveModal below, so a hung
        // fetch made the Add-property click a SILENT no-op (2026-07-13 UI audit). Losing the
        // pre-fill is fine; losing the modal is not.
        const pr = await Promise.race([
          parseListingText({ raw_text: rawText, channel, agent_id: myAgentId }),
          new Promise((_, rej) => setTimeout(() => rej(new Error('AI pre-fill timed out — opening a blank form')), 8000)),
        ])
        if (pr?.draft) {
          setPropertyDraft({ id: pr.proposal_id, proposed: { input: pr.draft }, missing_fields: pr.missing_fields, intake: { raw_text: rawText } })
          loadHistory()
        }
      } catch (e) { setParseErr(e.message) }
      finally { setParsing(false) }
    }
    setAiHint(null)
    setActiveModal(id)
    if (id === 'meeting' && viewings.length === 0) {
      getMeetingsFeed().then(setViewings).catch(() => setViewings([]))
    }
  }

  // A human clicked a "possible match" glow in Recent Entries — switch the pending draft
  // (must have come from an extract_requirement "new" call) into an amend of that entry.
  function handleGlowClick(requirementId) {
    if (!requirementId || !aiCreateCmd) return
    setAmendTargetId(requirementId)
    setActiveModal('client')
  }

  // The ONE click handler for every card in Recent Entries. Priority: if a paste is pending
  // review and THIS card is the AI's match, clicking it continues that draft-review flow (same
  // as clicking the glowing Desk button) — reviewing what's about to change beats jumping
  // straight to a free-form edit. Otherwise it opens the object's own edit modal on its
  // CURRENT saved state. No separate "expand" vs "edit" click target — one card, one action.
  // §13 engagement (B38 #4, 2026-07-28) — the POSITIVE counterpart to 'abandoned'.
  // 'abandoned' already records giving up after a zero; nothing recorded the opposite, so
  // the search log could show that v10 stopped zeroing queries without showing whether the
  // results were worth opening. A search that returns rows nobody clicks is not fixed.
  // `after_zero` is the v10-impact signal: this session saw a zero-result render recently
  // and the agent has since found something to act on. Five minutes, because reformulating
  // takes longer than the 10s 'abandoned' window and this is a coarse rate, not a timer.
  // Only fires with a live query — opening a card while browsing is not post-search
  // engagement, and counting it would inflate exactly the number this exists to measure.
  const logSearchEngagement = (event, extra = {}) => {
    const q = entrySearch.trim()
    if (!q) return
    const z = searchTelemetryRef.current.lastZero
    logSearchQuery({
      rawText: q, compiled: [], residual: '', dismissed: [],
      context: {
        source: 'desk', event,
        after_zero: !!(z && Date.now() - z.at < 300000),
        zeroed_query: z?.raw ?? null,
        ...extra,
      },
    })
  }
  async function handleCardClick(group) {
    const head = group.items[0]
    const reqId = head.result?.requirement_id
    logSearchEngagement('result_open', { object: group.items[0]?.__isProperty ? 'property' : 'listing' })
    // Desk home "recently viewed" (local memory aid — see the state block's rationale).
    try {
      const meta = compactSummary(group, districts, features)
      const listingId = head.__isProperty ? head.listingId : head.listing?.listing_id
      if (listingId || reqId) recordRecentView({
        id: group.key, kind: meta.objType, listingId: listingId || null, reqId: reqId || null,
        title: meta.title || meta.city || t('desk_meetingcard_property_fallback'),
        // Facts line (2026-07-17, user-directed): the same subtitle the card shows (deal ·
        // price · rooms · area · floor…), so the home's Recently-viewed rows say more than
        // a bare street name. Stored at click time — render must not depend on the listing
        // still being in the loaded feed.
        sub: meta.subtitle || null,
      })
    } catch { /* never block the open */ }
    const glow = reqId ? candidateGlow[reqId] : null
    if (glow === 'blue' && aiCreateCmd) { handleGlowClick(reqId); return }
    if (glow === 'amber') { setActiveModal('client'); return }

    const objType = objectTypeOf(head)
    // Read-only scraped-listing rows are facts, not something this Desk owns. Keyed off KIND
    // (both read-only listing kinds → the read-only lineage modal), handled before the
    // objType branches below. Both kinds open the SAME lineage modal — a plain capture just
    // renders with no origin section ("captured standalone"). The EDITABLE property is the
    // separate 'Property' feed (PropertyFeed), never a scraped-listing card click. Card-click
    // never triggers an external navigation on its
    // own; the source-platform link only fires if the agent clicks it inside the modal.
    // A property-cluster HEAD — opens the ONE detail modal (lineage: gallery, facts, every
    // posting, contacts, cooperations, meeting seed) with its ✎ Edit button leading to the
    // editable workspace (2026-07-09; was straight-to-editor). Must be checked BEFORE the
    // read-only listing branch below, because the head borrows a listing kind for its card
    // design (so kindOf would otherwise route it to the read-only modal). A head with no
    // listing id (shouldn't happen — every property IS an origin listing) keeps the old
    // direct-to-editor fallback rather than a broken modal.
    if (head.__isProperty) {
      if (head.listingId) openListingCard({ id: head.listingId, isOwn: false, seed: head.listing || null })
      else setPropertyCard({ propertyId: head.propertyId, listingId: null })
      return
    }
    const k = kindOf(head)
    if (k === 'listing_readonly' || k === 'scraped_listing_readonly') {
      // Both kinds open the SAME read-only lineage modal — a plain capture just renders
      // with no origin section ("captured standalone"). A scraped-listing card click stays
      // read-only by design; the editable property is reached via a Property-cluster head
      // (__isProperty) instead, so a plain listing card stays read-only.
      openListingCard({ id: head.listing?.listing_id, isOwn: k === 'listing_readonly', seed: head.listing || null })
      return
    }
    if (objType === 'client' && reqId) { openClientCard(reqId); return }
    // A client card with NO requirement_id is a failed save attempt — nothing was ever
    // created (this looked like an admin-permission bug: clicking did NOTHING, silently,
    // for everyone — 2026-07-16, fixed then by printing the stored error).
    // Since 2026-08-01 it OPENS instead (owner: "reopen to fix and save"): the modal
    // resolves the phone first, so reopening can never mint a second person for a number
    // that got registered in the meantime. See FailedEntryModal's header.
    if (objType === 'client') {
      setFailedEntry(head)
      return
    }
    // Property: a pending draft opens its review modal (the old drafts-panel behaviour, now
    // reachable from the ledger row or the activity log). An EXECUTED one reopens the same
    // modal in its saved-workspace state (add photos / new observations) — safe because
    // re-Record upserts on the same proposal_id and the button disables until dirty.
    if (objType === 'property') {
      if (head.status === 'pending' || (head.status === 'executed' && head.result?.listing_id)) {
        setPropertyDraft(head); setActiveModal('property')
      }
      return
    }
    if (objType === 'candidate') {
      const id = head.proposed?.candidate_id
      const c = id && candidates.find(x => x.id === id)
      setCommitMsg(c ? null : t('desk_misc_candidate_not_found'))
      if (c) setEditingCandidate(c)
      return
    }
    if (objType === 'meeting') {
      const viewingId = head.proposed?.viewing_id
      if (!viewingId) { setCommitMsg(t('desk_misc_no_linked_viewing')); return }
      let list = viewings
      if (!list.length) { list = await getMeetingsFeed().catch(() => []); setViewings(list) }
      const v = list.find(x => x.id === viewingId)
      setCommitMsg(v ? null : t('desk_misc_viewing_not_found'))
      if (v) setEditingViewing(v)
    }
  }

  // Close / Reopen act on the CLIENT DEMAND (the requirement), once, at the thread head —
  // not a per-event "undo". Both are soft + append-only (close_requirement / reopen_requirement,
  // migration 0039): the record is never deleted, only its lifecycle flips, so a mistaken close
  // is fully recoverable by Reopen. To correct a wrong *change*, the agent pastes a correction and
  // the AI amends again — that's what the append-only model is for. (Replaces the old "Undo",
  // which could close the whole client when clicked on a single amend — a footgun.)
  async function actOnRequirement(requirementId, type, { confirm, ok } = {}) {
    if (!requirementId) return
    if (confirm && !window.confirm(confirm)) return
    setActingReqId(requirementId); setCommitMsg(null)
    try {
      const cmd = { type, target_requirement_id: requirementId, reason: 'data_desk_manual' }
      const resp = await commitIntakeEnvelope({
        raw_text: `[manual action via Data Desk] ${type} ${requirementId}`,
        channel, envelope: { decision: 'noop', confidence: 1, commands: [cmd] }, anchors: null, intake_id: null,
        agent_id: myAgentId,
      })
      if ((resp.results || []).some(r => !r.executed)) {
        setCommitMsg(tLog('desk_misc_failed', { reason: resp.results[0]?.result?.message || 'gateway refused' }))
      } else {
        setCommitMsg(ok)
      }
      loadHistory()
    } catch (e) { setCommitMsg(tLog('desk_misc_error_prefix', { message: e.message })) }
    finally { setActingReqId(null) }
  }
  const handleCloseReq  = reqId => actOnRequirement(reqId, 'close_requirement',
    { confirm: t('desk_client_close_confirm'), ok: t('desk_client_closed_toast') })
  const handleReopenReq = reqId => actOnRequirement(reqId, 'reopen_requirement',
    { ok: t('desk_client_reopened_toast') })

  // Candidate glow for Recent Entries: confident amend target → amber; a possible-but-unsure
  // match the AI listed in candidates_considered → blue hint. Only meaningful while a draft
  // with candidates is pending review.
  const candidateGlow = useMemo(() => {
    const map = {}
    if (!envelope) return map
    if (aiAmendCmd?.target_requirement_id) map[aiAmendCmd.target_requirement_id] = 'amber'
    for (const c of envelope.candidates_considered || []) {
      if (!map[c.requirement_id] && (c.similarity == null || c.similarity >= 0.4)) map[c.requirement_id] = 'blue'
    }
    return map
  }, [envelope, aiAmendCmd])

  // Recent-entries filter/sort — operates on GROUPS (one client/meeting/property/listing = one
  // row), not raw events, so "type" and "active/closed" mean what an agent expects them to mean.
  // Agent-authored/resold listings (read-only, not a proposal) are folded into the same groups
  // list here so they share one filter/sort/render pipeline instead of a bolted-on second list.
  // Cascade reshares of the SAME source ad into one group instead of one card each: a reshare's
  // origin_listing_id (server-computed from the derived_from edge, migration 0041) is the group's
  // root key; a plain capture/origin with no reshares roots its own single-item group. Sorted
  // newest-first within the bucket so the freshest reshare is the head card (the "top of the
  // stack"); older reshares and the origin ad itself only appear once the thread is expanded
  // (reusing groupHistory/HistoryGroup's existing thread UI — no new expand mechanism needed).
  const listingGroups = useMemo(() => {
    const buckets = new Map()
    for (const l of listingEntries) {
      const rootKey = l.origin_listing_id || l.listing_id
      if (!buckets.has(rootKey)) buckets.set(rootKey, [])
      buckets.get(rootKey).push({
        id: `listing:${l.listing_id}`, proposal_kind: l.is_reshare ? 'listing_readonly' : 'scraped_listing_readonly',
        status: l.status, created_at: l.listed_at || null, listing: l, proposed: {}, result: {},
      })
    }
    return [...buckets.entries()].map(([rootKey, items]) => {
      const sorted = items.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
      // Q1: the same face function as the unified view -- the display member leads the cascade,
      // carrying the ruled address, so the Listings pill and the All view can never disagree.
      const face = faceForCluster(sorted.map(i => i.listing), sorted.map(i => i.listing).find(l => l?.address_state)?.address_state || null)
      if (face.member) {
        const idx = sorted.findIndex(i => i.listing === face.member)
        const [hit] = idx >= 0 ? sorted.splice(idx, 1) : [null]
        if (hit) sorted.unshift({ ...hit, listing: face.listing })
      }
      return { key: `listing:${rootKey}`, items: sorted }
    })
  }, [listingEntries])
  // property_id → same_as cluster key (from the inventory endpoint). Lets two DB rows for one
  // physical flat (independently scraped, joined by a same_as verdict) collapse into ONE card.
  // Empty until the server supplies cluster_key → propertyGroups then falls back to property_id.
  const agentPropCluster = useMemo(
    // property_id → identity cluster key. Own inventory first, per-row SERVER key second (0133):
    // every loaded feed row carries an authoritative cluster_key (property_cluster_map — the same
    // key v3/v12 paginated by), so it's the source of truth wherever a row is loaded. Same
    // definition (least-id of the confirmed same_as component) as the inventory keys, so they agree.
    () => ({
      ...Object.fromEntries((agentProperties || []).filter(p => p.cluster_key).map(p => [p.id, p.cluster_key])),
      ...Object.fromEntries((listingEntries || []).filter(l => l.cluster_key && l.property_id).map(l => [l.property_id, l.cluster_key])),
    }),
    [agentProperties, listingEntries])
  // Property cards (2026-07-08): ONE editable card per property the agent has. A "property" is an
  // ORIGIN listing — a scrape or a manual entry (no origin_listing_id). A RESHARE is NOT a
  // property (it's that property's listing, and lives under Listings/All), so reshares are
  // excluded here — otherwise every repost would spawn its own property card. Grouped by the
  // same_as identity cluster (0063): two DB rows for one physical flat (independently scraped,
  // joined by a same_as verdict) STACK into one entry — head + the duplicate row(s) as members —
  // so dedup shows as a cluster, not two cards. A normal property is a single card. NO listings
  // are ever nested here. The card borrows the origin listing's design (thumb/address/price/facts)
  // but is __isProperty → filters under the Property pill + opens the editable modal.
  const propertyGroups = useMemo(() => {
    const origins = listingEntries.filter(l => !l.origin_listing_id)   // scrapes + manual, never reshares
    const buckets = new Map()   // same_as cluster key → origin listings
    for (const l of origins) {
      const ck = agentPropCluster[l.property_id] || l.property_id || l.listing_id
      if (!buckets.has(ck)) buckets.set(ck, [])
      buckets.get(ck).push(l)
    }
    const toCard = (l, ck) => ({
      id: `property:${ck}:${l.property_id || l.listing_id}`, __isProperty: true,
      propertyId: l.property_id, listingId: l.listing_id,
      __source: l.source === 'manual' ? 'manual' : 'scrape',
      proposal_kind: 'scraped_listing_readonly',   // borrow the listing-card DESIGN
      status: l.status, created_at: l.listed_at || null,
      listing: l, proposed: {}, result: {},
    })
    return [...buckets.entries()].map(([ck, ls]) => {
      // oldest first; a same_as-merged flat shows its extra property rows stacked under the head
      const sorted = ls.slice().sort((a, b) => new Date(a.listed_at || 0) - new Date(b.listed_at || 0))
      return { key: `property:${ck}`, items: sorted.map(l => toCard(l, ck)) }
    })
  }, [listingEntries, agentPropCluster])
  // Unified clusters for the ALL view ONLY (distinct from propertyGroups above, which stays
  // standalone property cards for the Property pill): one cluster per physical property = the
  // EDITABLE property card as HEAD + every one of its listings (the scrape AND every reshare) as
  // read-only MEMBERS. Two listings are the same property if they share a RESHARE THREAD
  // (origin_listing_id — a reshare gets its OWN property_id, so property_id/same_as alone misses
  // the myhome/ss/korter reposts) OR their properties are one same_as identity cluster. The head
  // is anchored to the ORIGIN (oldest listing = first scrape/manual entry) so its source pill,
  // address, and first-seen date are the property's own, never a reshare's.
  const unifiedGroups = useMemo(() => {
    const parent = {}
    const find = x => { if (!(x in parent)) parent[x] = x; let r = x; while (parent[r] !== r) r = parent[r]; while (parent[x] !== r) { const n = parent[x]; parent[x] = r; x = n } return r }
    const union = (a, b) => { const ra = find(a), rb = find(b); if (ra !== rb) parent[ra] = rb }
    const clusterRep = {}
    for (const l of listingEntries) {
      find(l.listing_id)
      union(l.listing_id, `thread:${l.origin_listing_id || l.listing_id}`)
      if (l.property_id) {
        const ck = agentPropCluster[l.property_id] || l.property_id
        if (clusterRep[ck] != null) union(l.listing_id, clusterRep[ck]); else clusterRep[ck] = l.listing_id
      }
    }
    const buckets = new Map()
    for (const l of listingEntries) {
      const root = find(l.listing_id)
      if (!buckets.has(root)) buckets.set(root, [])
      buckets.get(root).push(l)
    }
    const listingItem = l => ({
      id: `listing:${l.listing_id}`, proposal_kind: l.is_reshare ? 'listing_readonly' : 'scraped_listing_readonly',
      status: l.status, created_at: l.listed_at || null, listing: l, proposed: {}, result: {},
    })
    // Under an active advanced-search filter (0073), a cluster loads with EVERY posting, not
    // just the one(s) that matched — but the card face should show the version that actually
    // matched (2026-07-10, agent-directed: "show looked up version on face value and keep all
    // group clustered"), not always the oldest/origin, which may be the one that DIDN'T match.
    // Falls back to the origin/oldest exactly as before once no filter is active or nothing in
    // the cluster carries matched_filter (defensive — server always marks it under a filter).
    // ⚠️ B259 (owner ruling 2026-07-31) REVERSED the 2026-07-10 agent-directed behaviour that
    // used to live here: under a filter, pickRep swapped the card face to whichever member
    // MATCHED, so the headline price moved depending on what you had typed.
    //   "That violates another expectation… users lose trust because the primary price
    //    appears to change depending on filters."
    // The face is now always the cluster's own stable representative (origin/oldest), and the
    // matching evidence is disclosed on its own line instead — "display the cluster truth
    // first, explain the matching evidence second". The agent's original need (which version
    // matched?) is still answered, just without making the price a function of the query.
    // Do not restore the swap from the older comment; this is the newer ruling.
    // Deterministic listing_id tiebreak so a card's face never reshuffles between two same-dated
    // postings after an unrelated edit (stable-representative invariant, 2026-07-20).
    const byOldest = (a, b) => (new Date(a.listed_at || 0) - new Date(b.listed_at || 0)) || String(a.listing_id).localeCompare(String(b.listing_id))
    // byNewest went with the swap it existed for — the members list below sorts inline.
    // B1210 / F-032 (2026-09-05): THE FACE IS THE MEMBER THAT NAMED THE STREET, when a street
    // chip is on. This is the second face-selection path (deskFeed's promotion serves the
    // Listings pill; the unified All view picks its representative HERE), which is why the
    // 2026-08-27 price-face ruling and the first B1210 cut never reached the owner's card: the
    // oldest member of the cluster was an agent's ss.ge reshare whose own text said Bendeliani
    // while the ORIGIN (the owner's myhome ad) and the street link both said Chavchavadze.
    // Preference order: members marked matched whose OWN (not inherited) address contains the
    // link's recorded witness (server: matched_street.matched_name) -> then the oldest, as before.
    // Containment of the link's own evidence, never a matcher (core/street-name-match.mjs is the one).
    // ⚠️ NOT gated on matched_filter: the ORIGIN reaches the page as a context row (matched_ids
    // holds authored ids only), so the flag is false on exactly the member that names the street.
    // Q1 (owner ruling 2026-09-05): the face member AND the address come from ONE function,
    // src/lib/displayAddress.js (canonical > the active member that named the matched street >
    // best active source). This was the second of four places that used to decide it (B1210).
    return [...buckets.entries()].map(([root, ls]) => {
      const face = faceForCluster(ls, ls.find(l => l.address_state)?.address_state || null)
      const rep = face.listing
      // Card-face photos: the head is anchored to the ORIGIN, but thumbnails ride individual
      // postings — an origin whose own thumbnail lookup came back empty showed a bare map tile
      // while a sibling posting in the SAME group carried real photos (2026-07-11). Borrow the
      // newest member's images; same property, so they're exactly as true for the head.
      // Borrow only when there is actually something to borrow (B122 Phase 3, 2026-07-27):
      // the old `|| []` fallback spread `{...rep, images: []}` for every imageless head —
      // a FRESH array every rebuild, which made every unified group fail identity
      // reconciliation on every run (measured: 81/81 itemFail, listingDiff:["images"] —
      // feed rows carry no inline images, thumbs are a separate overlay). No images to
      // borrow → pass `rep` unchanged, exactly what plain listing items do.
      const borrowedImages = rep.images?.length ? null
        : ls.slice().sort((a, b) => new Date(b.listed_at || 0) - new Date(a.listed_at || 0)).find(l => l.images?.length)?.images || null
      const head = {
        id: `unified:${root}`, __isProperty: true, propertyId: rep.property_id, listingId: rep.listing_id,
        __source: rep.source === 'manual' ? 'manual' : 'scrape',
        proposal_kind: 'scraped_listing_readonly',
        status: rep.status, created_at: rep.listed_at || null,
        listing: borrowedImages ? { ...rep, images: borrowedImages } : rep, proposed: {}, result: {},
      }
      // Excluded by listing_id, NOT by reference (2026-08-16). The head already IS `rep`, and a
      // member that repeats it renders the same ad twice — the ×2 platform chip listing one
      // #id/date/price on both rows that idSearchPropGroups shipped for a while. `l !== rep` was
      // correct only while TWO things held at once: rep is an element of ls, and listingEntries
      // never carries two objects for one listing_id (dedupeListings — which exists because
      // racing fetches genuinely can land the same row twice). The second is an invariant
      // maintained three setters away; if it ever lapses, reference-inequality keeps the twin and
      // the double comes back in the MAIN feed. Identity is the id, so compare the id.
      // Reference is kept as the fallback for a row with no listing_id: undefined !== undefined
      // is false, so an id-blind compare would drop every such member instead of just rep.
      const members = ls.filter(l => l.listing_id ? l.listing_id !== rep.listing_id : l !== rep).sort((a, b) => new Date(b.listed_at || 0) - new Date(a.listed_at || 0)).map(listingItem)
      return { key: `unified:${root}`, items: [head, ...members] }
    })
  }, [listingEntries, agentPropCluster, advFiltersDebounced])
  // ID-search reach-back (engine v5): the loaded entries only cover the current period page —
  // a pasted link to an older ad would silently miss. When a listing_id token is active,
  // resolve it DB-wide through the existing /listings/search (which owns the same per-agent
  // visibility rule as agent-listings scope=mine), then pull each hit's card shape via the
  // lineage endpoint. Merged as extra groups, deduped at merge time against whatever the
  // normal fetch already loaded.
  const [idSearchEntries, setIdSearchEntries] = useState([])
  // In-flight flag for that reach-back. Without it the feed rendered the zero state
  // ("0 results for X" / the dashed not-captured card) during the ~2.9 s the reach-back
  // takes, which is indistinguishable from a genuine miss — measured on a real session
  // (search_query_log, id 36253748): the operator abandoned the search TWICE before it
  // resolved. A pending answer is not a negative answer; say "searching", never "none".
  const [idSearchBusy, setIdSearchBusy] = useState(false)
  // Honest failure state (2026-07-26). Two distinct things the feed must never render as
  // "0 results", because an operator ACTS on that verdict — they conclude the listing was
  // never captured and go re-capture it, or tell a client we don't have it:
  //   { kind: 'failed'  }              — the search call itself died. We could not look.
  //   { kind: 'partial', failed, total} — some families didn't load. The list is incomplete.
  // `idSearchRetry` is a nonce in the effect deps so the retry button can re-run it.
  const [idSearchFault, setIdSearchFault] = useState(null)
  const [idSearchRetry, setIdSearchRetry] = useState(0)
  const imageMatchIds = useMemo(() => new Set((imageSearch && imageSearch !== 'loading' ? imageSearch.results : []).map(r => r.id)), [imageSearch])
  const imageSearchKey = imageSearch && imageSearch !== 'loading' ? [...imageMatchIds].join(',') : ''
  // image results replace the page context, same as any filter change — but only on a CHANGE of results.
  // ⚠️ Not on mount: every effect runs once at mount, and this one is declared after the hash restore, so it zeroed the
  // page a shared/refreshed link had just restored — every `page=` link opened on page 1, in every category
  // (audit 2026-10-29 appendix, measured in a mounted IntakeTab).
  const imageSearchKeyRef = useRef(imageSearchKey)
  useEffect(() => {
    if (imageSearchKeyRef.current === imageSearchKey) return
    imageSearchKeyRef.current = imageSearchKey
    setEntryPage(0)
  }, [imageSearchKey])
  useEffect(() => {
    if (!listingIdQueries.length && !phoneQueries.length && !cadastralQueries.length && !imageMatchIds.size) { setIdSearchEntries([]); setIdSearchBusy(false); setIdSearchFault(null); return }
    if (listingsScope === 'mine' && !myAgentId) { setIdSearchEntries([]); setIdSearchBusy(false); setIdSearchFault(null); return }
    let alive = true
    setIdSearchBusy(true)
    setIdSearchFault(null)
    ;(async () => {
      let asked = 0, lost = 0          // families requested vs families that failed to load
      try {
        const found = []
        const seen = new Set()
        const push = (l) => { if (l?.listing_id && !seen.has(l.listing_id)) { seen.add(l.listing_id); found.push(l) } }
        // Lineage sibling → feed-row shape (its own id becomes listing_id; a sibling that carries
        // a derived_from parent is a reshare). No thumbnails ride the lineage payload, so images
        // start empty — the group's image fallback borrows the head/origin's (same property).
        const sibToRow = s => ({
          listing_id: s.id, property_id: s.property_id || null,
          source: s.source, source_url: s.source_url || null,
          price: s.price, currency: s.currency, status: s.status, intent: s.intent,
          listed_at: s.listed_at, is_reshare: !!s.derived_from_id,
          origin_listing_id: s.derived_from_id || null, address: s.posted_address || null,
          kind: s.kind ?? null, rooms: s.rooms ?? null, bedrooms: s.bedrooms ?? null,
          area_sqm: s.area_sqm ?? null, floor: s.floor ?? null, floors_total: s.floors_total ?? null,
          author_name: s.author_name || null, images: [],
        })
        // Expand ONE hit into its WHOLE physical-property family — the lineage BFS closes over
        // derived_from AND confirmed same_as (0063), so the card renders COMPLETE at once: every
        // posting, not just the hit + its origin. This is the "2 then 19" completion the feed
        // crawl used to backfill, now delivered in a single reach-back (2026-07-20).
        const familyRows = (lin) => {
          const { listing, origin, hops, siblings } = lin || {}
          if (!listing) return []
          // Family cluster key = least property_id across the family. The lineage BFS family IS the
          // confirmed same_as component (+ derived_from siblings, which share their origin's
          // property), so this equals the server's property_cluster_map key — stamping it lets
          // idSearchPropGroups collapse the whole family into ONE card without the (now-removed)
          // org-wide map, and dedupe against loaded feed rows that carry the same server key.
          const famProps = [listing.property_id, origin?.property_id, ...(siblings || []).map(s => s.property_id)].filter(Boolean)
          const famKey = famProps.length ? famProps.slice().sort()[0] : (listing.property_id || listing.listing_id)
          const stamp = l => (l.cluster_key ? l : { ...l, cluster_key: famKey })
          const out = [stamp(hops > 0 && origin ? { ...listing, is_reshare: true, origin_listing_id: origin.listing_id } : listing)]
          if (origin) out.push(stamp(origin))
          for (const s of (siblings || [])) out.push(stamp(sibToRow(s)))
          return out
        }
        // ONE request for every hit's family (2026-07-26). This loop used to await
        // /listing/:id/lineage per id — ~0.46 s each, so 20 hits was 10.5 s measured ON the
        // box, before a browser adds 20 round trips of its own. Worse, it spent 21 requests of
        // the caller's rate-limit budget on a single keystroke, and because the data layer
        // throws on any non-ok response, ONE 429 anywhere in that fan-out collapsed the whole
        // result set and rendered as "0 results".
        //
        // The batch endpoint reuses the very same server function the single-id route calls
        // (verified byte-identical, 20/20 payloads, admin AND restricted-viewer paths), so the
        // representation/masking boundary still has exactly one implementation.
        //
        // Order is preserved deliberately: rows are merged in the original id order, so `push`
        // dedups exactly as the serial version did. Per-id failures come back in `errors` and
        // are logged rather than silently dropped — a partial answer that looks complete is the
        // failure mode this whole line of work started from.
        const expandAll = async (lids) => {
          const todo = lids.filter(l => l && !seen.has(l))
          if (!todo.length) return
          asked += todo.length
          const { results, errors } = await getListingsLineageBatch(todo)
          for (const [lid, msg] of Object.entries(errors || {})) console.error('[id-search:lineage]', lid, msg)
          // Count what we asked for and didn't get — including ids the server answered
          // for neither results nor errors. Silence is a loss too.
          for (const lid of todo) if (!results?.[lid]) lost++
          for (const lid of todo) for (const l of familyRows(results?.[lid])) push(l)
        }
        // 📷 image matches: already core listing ids — straight to family expansion.
        await expandAll([...imageMatchIds])
        // phone queries ride the same server search — /listings/search treats a ≥9-digit query
        // as a phone and suffix-matches it against party phones (engine v8). Hits in the SAME
        // family are deduped by `seen`/`covered` (one expand covers them all), so the cap counts
        // distinct FAMILIES, not raw hits — a phone owning many postings is not truncated at 5.
        // widen:'identity' (B260, owner-reported): a phone/pasted-id is an IDENTITY question —
        // "what do we know about this number?" — not a stock question. Without it the server's
        // ownership filter dropped every market listing and the same screen that NAMED the
        // person (PhonePersonHeader) said their properties don't exist. agentId still rides
        // along as the VIEWER: contact masking and coop-money gating are unchanged — only the
        // which-listings filter widens. Rendering stays behind the lineage masking boundary.
        for (const q of [...listingIdQueries, ...phoneQueries, ...cadastralQueries]) {
          const results = await searchCoreListings(q, { agentId: listingsScope === 'all' ? undefined : myAgentId, widen: 'identity' })
          await expandAll((results || []).slice(0, 20).map(r => r.id))
        }
        if (alive) {
          setIdSearchEntries(found)
          // A hit we asked for and could not load is NOT a hit that doesn't exist.
          setIdSearchFault(lost > 0 ? { kind: 'partial', failed: lost, total: asked } : null)
        }
      } catch (e) {
        console.error('[id-search]', e.message)
        // The search call itself failed. Showing an empty list here would assert
        // "nothing matched", which we do not know. Say we couldn't look.
        if (alive) { setIdSearchEntries([]); setIdSearchFault({ kind: 'failed' }) }
      }
      finally { if (alive) setIdSearchBusy(false) }
    })()
    return () => { alive = false; setIdSearchBusy(false) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingIdKey, phoneKey, cadastralKey, imageSearchKey, listingsScope, myAgentId, idSearchRetry])
  const idSearchGroups = useMemo(() => {
    const have = new Set(listingEntries.map(l => l.listing_id))
    return idSearchEntries.filter(l => l.listing_id && !have.has(l.listing_id)).map(l => ({
      key: `idsearch:${l.listing_id}`,
      items: [{
        id: `listing:${l.listing_id}`, proposal_kind: l.is_reshare ? 'listing_readonly' : 'scraped_listing_readonly',
        status: l.status, created_at: l.listed_at || null, listing: l, proposed: {}, result: {},
      }],
    }))
  }, [idSearchEntries, listingEntries])
  // Property-shaped ID-search groups for the All/Property views: ONE cluster per physical
  // property (mirrors unifiedGroups' head+members shape), grouped by the family's shared
  // property_id. The flat idSearchGroups above stays for the Listings pill, where each
  // listing IS the unit. Whole-family dedup: if ANY member's property is already in the
  // loaded entries, the normal groups already render it (and the listing-id filter at the
  // bottom of visibleResult matches it there) — adding it again would double the card.
  const idSearchPropGroups = useMemo(() => {
    // Group by the property-IDENTITY cluster (0133), not the raw property_id — a phone/id hit
    // whose family spans several same_as-linked properties must render as ONE card, not one per
    // property. Skip a family already visible in the loaded feed (now itself identity-clustered)
    // so it never double-renders (2026-07-20).
    const ckOf = l => l.cluster_key || agentPropCluster[l.property_id] || l.property_id || l.listing_id
    const haveCluster = new Set(listingEntries.map(ckOf).filter(Boolean))
    const haveListing = new Set(listingEntries.map(l => l.listing_id))
    const byOldest = (a, b) => (new Date(a.listed_at || 0) - new Date(b.listed_at || 0)) || String(a.listing_id).localeCompare(String(b.listing_id))
    const byNewest = (a, b) => (new Date(b.listed_at || 0) - new Date(a.listed_at || 0)) || String(a.listing_id).localeCompare(String(b.listing_id))
    const buckets = new Map()
    for (const l of idSearchEntries) {
      if (!l.listing_id || haveListing.has(l.listing_id)) continue
      const key = ckOf(l)
      if (haveCluster.has(key)) continue
      if (!buckets.has(key)) buckets.set(key, [])
      buckets.get(key).push(l)
    }
    return [...buckets.entries()].map(([key, ls]) => {
      // head anchors to the ORIGIN (a non-reshare if present, else oldest) — same rule as
      // unifiedGroups, with a deterministic listing_id tiebreak so the face never reshuffles.
      const sorted = ls.slice().sort(byOldest)
      const rep = sorted.find(l => !l.is_reshare) || sorted[0]
      const head = {
        id: `unified:idsearch:${key}`, __isProperty: true, propertyId: rep.property_id, listingId: rep.listing_id,
        __source: rep.source === 'manual' ? 'manual' : 'scrape',
        proposal_kind: 'scraped_listing_readonly',
        status: rep.status, created_at: rep.listed_at || null,
        listing: rep, proposed: {}, result: {},
      }
      // ⚠️ EXCLUDE `rep` — the head already IS that listing. Without this filter the
      // representative renders twice (once as the head, once as a member), so a family of N
      // postings became N+1 items: the stack read "დუბლიკატები · 2" for a single ad and the
      // platform chip aggregated it as "MyHome ×2" listing the SAME #id/date/price twice.
      // Owner-reported 2026-08-16 as "why is the same ad saved twice?" — nothing was saved
      // twice; one listing row was drawn twice. unifiedGroups (the loaded-feed twin of this
      // builder) has always filtered rep out; this reach-back copy did not.
      // Excluded by listing_id, matching unifiedGroups exactly — see the longer note there for
      // why reference-inequality is not enough. Entries with no listing_id are already skipped
      // when the buckets are filled above, so the fallback branch is unreachable here; it is
      // kept so the two builders stay literally the same expression rather than one being
      // subtly cleverer than the other.
      const members = ls.filter(l => l.listing_id ? l.listing_id !== rep.listing_id : l !== rep).sort(byNewest).map(l => ({
        id: `listing:${l.listing_id}`, proposal_kind: l.is_reshare ? 'listing_readonly' : 'scraped_listing_readonly',
        status: l.status, created_at: l.listed_at || null, listing: l, proposed: {}, result: {},
      }))
      return { key: `idsearch:prop:${key}`, items: [head, ...members] }
    })
  }, [idSearchEntries, listingEntries, agentPropCluster])
  // Group identity across runs (Phase 2 step 1): the builders above mint new group/item
  // wrappers every run even for unchanged content. reconcileGroups swaps content-identical
  // groups back to the PREVIOUS run's objects, so the summary cache and (step 2) memoized
  // cards see "unchanged" as reference equality. Ref-held map, updated in place per run.
  const groupIdentityRef = useRef(new Map())
  const allGroups = useMemo(() => {
    // Three different shapes depending on the selected pill (a group's head objType decides
    // its pill downstream, so the SAME array can't serve both Property-standalone and the
    // All-unified cluster — both would have objType 'property' heads and double up):
    //  - Listings pill: listing reshare-clusters only, untouched.
    //  - Property pill: STANDALONE editable property cards (propertyGroups) — no listings nested.
    //  - Everything else (All / Client / Meeting / unset): UNIFIED clusters (unifiedGroups) —
    //    property head + its listings nested as members, so a flat shows as ONE cluster, not
    //    scattered cards. History still supplies clients/meetings/drafts either way.
    if (entryTypeFilter[0] === 'Listings') {
      return reconcileGroups(groupIdentityRef.current, [...groupHistory(history), ...listingGroups, ...idSearchGroups])
    }
    // An EXECUTED extract_listing draft is dropped: its property already appears as a property
    // card, so keeping the draft too would double it; pending drafts stay (properties-to-review).
    const base = groupHistory(history).filter(g => !(kindOf(g.items[0]) === 'extract_listing' && g.items[0].status === 'executed'))
    // ID-search reach-back joins the Property view too (it used to be silently dropped here —
    // an admin filtering by Property and pasting an ad id got nothing), and in the unified view
    // it contributes property-shaped clusters, never loose per-listing cards.
    // Property pill now uses the SAME unified clusters as All (2026-07-17 user ruling: opening
    // the Properties category must not re-render the same properties as different-looking
    // cards — it's a filter, not a different card system). The old standalone propertyGroups
    // branch swapped every card's shape/order on category entry; the type filter below already
    // narrows unified clusters to property heads. propertyGroups stays built for now (cheap,
    // and the editable-standalone design may return behind a dedicated surface).
    return reconcileGroups(groupIdentityRef.current, [...base, ...unifiedGroups, ...idSearchPropGroups])
  }, [history, propertyGroups, unifiedGroups, listingGroups, idSearchGroups, idSearchPropGroups, entryTypeFilter])
  // "Possible matches" groups — same card machinery as listingGroups, separate list so they
  // never enter the main filter/sort pipeline (the client classify would re-drop them: they
  // are, by definition, verdict='unknown').
  // Grouped by CLUSTER — one card per property identity, the unit `serverUnknown` counts
  // in and `unknown_offset` advances in. Shared with LeadsFeed (PossibleMatches.jsx), which
  // is where the unit rule and the bug that taught it are written down.
  const possibleGroups = useMemo(() => possibleClusterGroups(listingPossibles), [listingPossibles])
  // Q4's nearby group, through the SAME cluster grouping — one card per property identity,
  // which is also the unit `street_nearby` is keyed by (the RPC groups its distance CTE by
  // cluster_key, and the server stamps the same key on every card via property_cluster_map).
  // Verified against live data before this was built: 59 nearby cards, 59 with a badge, 0
  // without — the join key is cluster_key, not listing_id and not property_id.
  const nearbyGroups = useMemo(() => possibleClusterGroups(listingNearby), [listingNearby])
  const hasGlow = Object.keys(candidateGlow).length > 0
  // Cache for the per-group summaries the pipeline reads (see groupsWithMeta) — a ref, not
  // state: it must never itself cause a render.
  const metaCacheRef = useRef({ districts: null, features: null, map: new WeakMap() })
  // The filter → tally → sort chain itself lives in ../lib/deskFeed.js (extracted VERBATIM
  // 2026-07-27, DESK_RENDER_PERF_HANDOFF §5 Phase 0 / B122) so it can be gated by
  // src/lib/deskFeed.test.mjs against committed fixtures before Phases 1–3 restructure it.
  // Nothing here changed shape: same inputs, same dep array (config is STILL absent from the
  // deps — priceOf reads config?.fx_to_gel and always has; making it a dep is a behavior
  // change, deliberately not smuggled into an extraction).
  // ⚠️ Declared HERE, above the pipeline memo, because that memo now reads
  // availByProperty during render (0220 step 4). A const referenced before its
  // declaration is a TDZ ReferenceError at render time, not a lint nit.
  const [dormancyMap, setDormancyMap] = useState(new Map())
  // 0258: property_id → { report_count, reporter_count, last_reported_at, reasons }.
  // "An agent phoned the owner and was told it is gone." Rides the SAME id-set key as
  // dormancy below — one set change, one pair of reads.
  const [marketMap, setMarketMap] = useState(new Map())
  const [authorMarkMap, setAuthorMarkMap] = useState(new Map())
  const [showDormant, setShowDormant] = useState(false)
  // ⚠️ Keyed on the property-id SET, not on `listingEntries` identity (2026-07-26 perf
  // trace: opening one modal fired this POST **5 times**, ~20kB each, because every
  // setListingEntries — cache paint, revalidate, merges — is a new array even when the
  // same properties come back. The fetch is only worth making when the SET changes.
  const dormancyKey = useMemo(
    () => [...new Set(listingEntries.map(l => l.property_id).filter(Boolean))].sort().join(','),
    [listingEntries])
  useEffect(() => {
    const pids = dormancyKey ? dormancyKey.split(',') : []
    if (!pids.length) { setDormancyMap(new Map()); setMarketMap(new Map()); setAuthorMarkMap(new Map()); return }
    let gone = false
    getDormancy(pids.slice(0, 2000)).then(m => { if (!gone) setDormancyMap(m) }).catch(() => {})
    // Independent catch on purpose: a market-report failure must not cost the feed its
    // dormancy tiering, and vice versa. Either map degrading to empty just means one
    // fewer ordering hint this render — neither decides what a CLIENT is shown.
    getMarketReportCounts(pids.slice(0, 2000)).then(m => { if (!gone) setMarketMap(m) }).catch(() => {})
    // 0374 — which of these properties' authors have been reported as agents. Same
    // independent catch and the same soft failure: no marks just means plain 📞 pills,
    // never "nobody is marked", and it decides nothing a client is shown.
    getAuthorRoleMarks(pids.slice(0, 2000)).then(m => { if (!gone) setAuthorMarkMap(m) }).catch(() => {})
    return () => { gone = true }
  }, [dormancyKey])
  // property_id → owner-stated available-from, for the move-in filter (0220 step 4).
  // Free of charge: the dormancy response already carries it (it is what drives the wake
  // moment), and dormancy is fetched for every LOADED row rather than the visible page —
  // so this filter can never judge one page while presenting the answer as the whole set.
  const availByProperty = useMemo(() => {
    const m = {}
    for (const [pid, d] of dormancyMap) if (d?.available_from) m[pid] = d.available_from
    return m
  }, [dormancyMap])
  // 2026-10-22: what the client must still judge on rows of the landed server-filtered answer. The
  // re-classify used to re-judge every key the server had already judged, so a server-passed cluster
  // could turn client unknown/mismatch — gone from the feed, still in "N match", and added again to the
  // server's own "N hidden". The draft is compared AS IT WOULD BE SENT (city codes injected, the same
  // transform as serverAdvFilters), so a city pick does not read as an unjudged district change.
  const serverVetted = useMemo(() => {
    if (localFilterMode || !landedServerFilters) return null
    const sentView = cityScopedDistrictFilters(normalizeAdvFilters(draftAdvFilters), cityDistrictCodes, districtTree)
    return serverVettedSpec({ draft: draftAdvFilters, sentView, landed: landedServerFilters, defaults: ADV_DEFAULTS })
  }, [localFilterMode, landedServerFilters, draftAdvFilters, cityDistrictCodes, districtTree])
  const visibleResult = useMemo(() => {
    // Attribution probe (B122): window.__feedPerf collects per-run timings of the two halves
    // inside this memo, so a browser measurement can say whether blocking time is
    // meta-derivation (summaryMs, with cache miss count), the pipeline (pipeMs), or React
    // render/commit (= long task total minus these).
    //
    // B611 (2026-08-09) made it reachable in PRODUCTION behind a runtime flag, because the
    // question it answers only exists at production scale. Measured in a visible tab, a
    // browse click blocks the main thread for 2,093–2,864 ms across 2–4 long tasks, and the
    // first ~1 s lands BEFORE any data arrives — but nothing could say WHICH half, since the
    // probe was compiled out of exactly the build that has the data. A dev build over seeded
    // fixtures cannot reproduce 44,386 listings.
    //
    // Off by default and costs one boolean read per memo run; `window.__PERF_PROBE = true`
    // in a console turns it on for that tab only. It records TIMINGS, never row content.
    const __dev = import.meta.env.DEV || (typeof window !== 'undefined' && window.__PERF_PROBE === true)
    const __t0 = __dev ? performance.now() : 0
    const entries = groupsWithMeta(metaCacheRef.current, allGroups, districts, features)
    const __t1 = __dev ? performance.now() : 0
    const r = feedPipeline(entries, {
      districts, allAgents, config,
      entryAgentFilter, entryStatusFilter, entryCityFilter, entryTypeFilter, entrySort,
      // The LANDED trio (2026-08-05, owner ruling — see landedSearch): the pipeline reads
      // the committed text, its compile, and its tokens, under the same arg names it has
      // always taken (deskFeed.js derives textQ = residual-or-text from exactly these).
      // This is THE line that stops typing from re-running the pipeline per keystroke.
      activeTokens: landedTokens, compiledQuery: landedCompiled, entrySearch: landedSearch, landedTextQ,
      listingIdQueries, phoneQueries, cadastralQueries, idSearchEntries,
      imageSearch, imageMatchIds,
      nearLocationQueries, streetSel,
      draftAdvFilters, candidateGlow, serverVetted,   // serverVetted: 2026-10-22, see above
      // Overlay facts (B177): maps for the exclude filters + the commission sort key.
      restrByListing, termsByListing, myCommissionByProperty, allowStances: allowByListing,
      expansion: restrictionExpansion, availByProperty,
    })
    if (__dev) {
      const __t2 = performance.now()
      ;(window.__feedPerf = window.__feedPerf || []).push({
        at: Math.round(__t2), groups: allGroups.length,
        summaryMs: +(__t1 - __t0).toFixed(1), pipeMs: +(__t2 - __t1).toFixed(1),
        misses: groupsWithMeta.lastMisses,
        // why groups missed identity reuse this run (see reconcileGroups) — distinguishes
        // real data changes (itemFail after a landing) from comparator gaps (itemFail on a
        // display-only interaction) from membership churn (noPrev/lenDiff).
        reconcile: reconcileGroups.lastStats || null,
      })
      if (window.__feedPerf.length > 300) window.__feedPerf.splice(0, 100)
    }
    return r
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  // restrByListing/termsByListing/myCommissionByProperty joined the deps 2026-07-27 (B177):
  // they are new INPUTS (overlay-fact filters + commission sort key), and the pipeline must
  // re-run when an overlay lands so a known-bad row drops the moment its fact arrives.
  // Unlike config (still deliberately absent), these did not exist when the dep array was
  // frozen — adding them is wiring, not a behavior change to smuggle.
  [allGroups, entryTypeFilter, entryAgentFilter, entryStatusFilter, landedSearch, entryCityFilter, entrySort, draftAdvFilters, candidateGlow, districts, features, allAgents, listingIdKey, nearLocationKey, phoneKey, cadastralKey, idSearchEntries, landedTextQ, dismissedSigs, imageSearch, imageSearchKey, streetSel, restrByListing, termsByListing, myCommissionByProperty, allowByListing, restrictionExpansion, serverVetted])
  const { list: visibleGroups, advStats, typeCounts = {}, filteredKindCounts = {}, filteredDistrictCounts = {}, loadedInView = 0 } = visibleResult
  useEffect(() => { perfTrace('rows', { listingEntries: listingEntries.length, visibleGroups: visibleGroups.length, fetching: listingsFetching }) }, [listingEntries.length, visibleGroups.length, listingsFetching])   // eslint-disable-line react-hooks/exhaustive-deps
  // ✓-lens presentation (owner report 2026-07-29): a card returned by a ✓ restriction
  // filter showed no ✓ anywhere on its FACE, though opening it showed the stance. The face
  // draws from restrByListing, which deliberately carries only RESTRICTING stances (it is
  // the 🚫 chip's fuel and must stay that), so the lens the agent searched by was the one
  // thing the card would not say.
  //
  // ⚠️ The ✓ chip's mode is `hide`, NOT `allow` — restrictModeOptions offers two modes and
  // `allow` is retained only so old saved searches keep their meaning. A first pass here
  // filtered on `allow`, matched nothing, and shipped as a no-op. The test for "is this the
  // ✓ lens" is the GLYPH, shared with the chip's own label, so the two cannot disagree.
  //
  // This derives NO evidence (0244: evidence lives once, in cluster_restriction_evidence_v1;
  // nobody re-implements the rule). The chip is a restatement of SELECTION — the server
  // returned this cluster for this lens, whatever the policy version currently says that
  // means — so it stays true if a ruling changes what qualifies. Solid when this row itself
  // records a positive stance for the subject (`allowed`/`preferred`, or `only`, which is
  // the ad renting exclusively to them); dashed = the honest "unconfirmed on this row" the
  // border already means, because the qualifying stance may sit on a sibling ad in the
  // cluster or on a parent subject (owner wrote უცხოელები, agent searched ინდოელები).
  // Prepended: the face scrolls its chips but the lens must be the first one read.
  const restrDisplayByListing = useMemo(() => {
    const wanted = parseRestrictions(draftAdvFilters.restrictions)
      .filter(r => restrictionGlyph(r.mode) === '✓').map(r => r.code)
    if (!wanted.length) return restrByListing
    const out = { ...restrByListing }
    for (const l of listingEntries) {
      const lid = l.listing_id
      if (!lid) continue
      const pos = new Set((allowByListing?.[lid]?.subjects || []).map(s => s.code))
      const base = out[lid]?.subjects || []
      for (const b of base) if (b.stance === 'only') pos.add(b.code)
      const add = wanted.map(code => ({ code, stance: 'allowed', src: pos.has(code) ? 'agent' : 'scrape' }))
      out[lid] = { subjects: [...add, ...base.filter(b => !wanted.includes(b.code))] }
    }
    return out
  }, [restrByListing, allowByListing, draftAdvFilters.restrictions, listingEntries])
  // ⚠️ ONE DENOMINATOR PER SURFACE (owner-caught 2026-07-31: "I as admin cant see more then
  // 119 objects and flat is 664"). The Properties ROW was a tally over LOADED rows while its
  // own kind CHILDREN read whole-corpus kind_counts — so a child rendered bigger than its
  // parent and the tree read as "admin is capped at 119". Both numbers were individually
  // true; the tree's SHAPE asserts they share a universe, and they did not.
  //
  // Verified against the live RPC before wiring (search_agent_listing_clusters_v28,
  // 2026-07-31): total_clusters == Σ kind_counts EXACTLY (Δ=0) in every scenario tested —
  // leads scope, agent scope, with and without a 30-day window. So listingTotal is the true
  // parent of these children, not an approximation of it.
  //
  // Under the agent filter both sides switch together to the filtered tally (the same rule
  // kindCountsForTree below already follows), so parent and children stay in ONE universe in
  // both modes. null until page 0 reports: a row with no number beats a number from another
  // query — leadsCachedTotal's own rule, "a count from a different filter set is a lie with
  // digits in it". Client/Meeting rows are bounded by the same scope+period as this number and
  // are complete in practice — measured 2026-07-31: 83 non-superseded proposals against the
  // /api/core/proposals cap of 100 (default) / 500 (all-history search), so the cap does not
  // bind. ⚠️ It WOULD bind silently if the corpus grew past it; that is exactly what the
  // nightly tripwire at 400 (scripts/nightly_dedup.sh) exists to catch before it does.
  //
  // 🚨 MUST STAY AT COMPONENT SCOPE. Shipped 2026-07-31 declared INSIDE the
  // restrDisplayByListing useMemo above while being read by two <FacetTree> call sites down
  // here — a free identifier, so `vite build` and 348 tests all passed and the Desk threw
  // ReferenceError at render on ?type=Property. Build+tests cannot see this class; only a
  // smoke render can (memory: extraction-runtime-smoke-gate).
  // ── Which cuts the SERVER's counts cannot see (2026-08-09, B625) ───────────────────
  // The corpus counts are computed server-side from scope + period + the committed search. A
  // filter applied only in the browser is invisible to them, so while one is on the facets must
  // read from the filtered tally or they advertise rows the click cannot deliver — B273's rule,
  // and precisely what the owner hit: status=closed emptied the feed while the tree still read
  // 3,102. The agent filter already had this treatment; `entryStatusFilter` has exactly the same
  // property and was simply never added.
  // ⚠️ The tally is a LOADED-rows count, so this can read LOW where the server count read HIGH.
  // That trade is deliberate and is the one the agent filter already made ("ბინა 3852 beside
  // shown 7"): a number smaller than the truth is a bad estimate, a number that promises rows the
  // click cannot produce is a false one.
  // ⚠️ `'all'` BECAME `'active'` HERE ON PURPOSE (B576, 2026-08-16). This test used to read
  // `!== 'all'`, and retiring the `all` state would have frozen it at TRUE forever — pinning
  // the whole tree to the loaded-rows tally on every view, which is the "reads LOW" degradation
  // the comment above accepts only as the price of honesty. It is not the price of anything if
  // the server can see the cut.
  // Measured against live 2026-08-17 rather than assumed: `agent_listing_cluster_page_v6`
  // applies the status predicate in `clusters_f`, and `district_counts` is derived from
  // `clustered_f` — i.e. AFTER it. So on the unfiltered feed the server's counts already
  // honour status and it is not a client-only cut at all.
  // 🔴 …that paragraph said `search_agent_listing_clusters_v40` — the COMMITTED-SEARCH path —
  // "has no work-status parameter … there the status is dropped server-side entirely", and
  // called B576's follow-up "a search RPC that takes the same word". THAT RPC SHIPPED: v41
  // takes `p_status`, and the route forwards it on the filtered path. Measured against live
  // 2026-08-18 rather than inferred, one request per line:
  //     unfiltered  active 308 · archived 2      filtered (rent+apartment)  active 267 · archived 2
  // So the status is no longer a client-only cut on either path.
  // ⚠️ AND THE STATUS TERM IS NOW GONE ENTIRELY (2026-08-18). It was kept one commit longer on
  // the grounds that the retired `all`/`closed` words were still reachable through the vestigial
  // `status` facet — that facet is gone (it carries the real two-button axis now), and every
  // writer of this state was audited: the two-button row, and three clears that all write
  // `active`. The URL carries no status at all. So the only reachable values are `active` and
  // `archived`, the server honours both on both paths, and the prewarm and dcKey now send and
  // key on it. There is nothing left for a client-side tally to see that the server cannot.
  // 📌 I described this to the owner as "dead code, unreachable" and that was WRONG in a way
  // worth recording: `archived !== active`, so the branch fired every time an agent opened the
  // archive — it was not dead, it was a live degradation pinning that view's whole facet tree to
  // a loaded-rows tally for a cut the server had learned to make. A branch you have argued is
  // unreachable is the one to measure, not to delete on the strength of the argument.
  // 2026-10-22: the agent pick IS sent (serverAdvFilters.agentIds → p_party_ids, since 2026-07-28), so the
  // server's total/kind/district counts already honour it. Only a pick the wire does not carry (role gate, or scope
  // not 'all') is client-only; counting loaded rows for a cut the server made drew page-sized numbers on the tree.
  const clientOnlyCut = agentCutClientOnly(entryAgentFilter, serverAdvFilters, listingsScope)
  const propertyTotalForTree = clientOnlyCut
    ? ((typeCounts.Property || 0) + (typeCounts.Listings || 0))
    // Live total → the tc: seed (last true answer for THIS dcKey, or the prewarm's) → null.
    // The seed is what turns "92 → 162 → 3141 over seconds" into the right number on the
    // first paint; null now renders as no-number-yet instead of a partial sum (see allN).
    // 2026-10-22: the seed is UNFILTERED — while a filtered search is in flight it stands in for nothing.
    : treeTotalFor({ landed: listingTotalLanded, total: listingTotal, cachedTotal: cachedListingTotal, filtered: !!serverAdvFilters })   // F-089
  // Kind counts for the Properties subtree: whole-corpus server counts normally, the
  // filtered tally while the agent filter is active (see the visibleResult comment).
  const kindCountsForTree = clientOnlyCut ? filteredKindCounts : (serverKindCounts || loadedKindCounts)
  // 0229 (B205): per-option numbers for the Deal/Type/Source facets. ONE chooser so every
  // facet answers from the view that owns the surface: the Leads view reads its own feed's
  // page-0 snapshot (the entries pipeline is gated off there — B204), everything else reads
  // the entries pipeline's search_meta. Type deliberately shares kindCountsForTree with the
  // category tree — one source, so the rail and the facet can never disagree. null = this
  // query hasn't reported yet → the facet renders without numbers rather than with stale ones.
  const facetCounts = entryTypeFilter[0] === 'Leads'
    ? leadFacetCounts
    : { kinds: kindCountsForTree, intents: serverIntentCounts, sources: serverSourceCounts }   // F-086: the book view's source facet finally has numbers
  // 0687: the HISTOGRAMS follow the same chooser, for the same reason. The Leads pool reads its
  // own feed's snapshot; everything else reads the entries pipeline's. Without this the Leads
  // panel would draw the PROPERTIES corpus under leads rows -- two universes in one panel, the
  // shape the parent/children count bug above was reported as.
  const panelRangeFacets = entryTypeFilter[0] === 'Leads' ? leadFacetCounts.ranges : rangeFacets
  // ── 🚨 THE PARENT MUST FOLLOW THE CHILDREN'S CHOOSER (owner-reported, 2026-08-19) ─────
  // Owner's screenshot, Leads pool: `ობიექტები` **1474** above children summing to **3436**
  // (საცხოვრებელი 3347 + კომერციული 42 + მიწა 47). A parent smaller than its own children,
  // and no way for an agent to tell which number to believe.
  //
  // The chooser directly above already sends the CHILDREN to `leadFacetCounts` on the Leads
  // pool — but `propertyTotal` went on reading `propertyTotalForTree`, which is `listingTotal`
  // from the entries pipeline, i.e. OUR WORK. Two universes in one tree. The row's own comment
  // states the invariant it was breaking: *"this row must share a universe with the kind
  // children beneath it"*.
  // ⚠️ The Δ=0 guarantee that comment relies on (`total_clusters == Σ kind_counts`, re-verified
  // against v41 on 2026-08-19: 30,628 == 30,628) only holds WITHIN ONE CALL. It says nothing
  // about a total from one query beside counts from another — which is exactly what shipped.
  // So the parent now reads the SAME page-0 snapshot as the children, keyed on the same
  // archive state (`leadSearchMeta` uses `leadCountArgsFor(leadArchive)`), which makes
  // parent == Σ children by construction rather than by coincidence.
  // ⚠️ During a load the two differ in KIND, not in value: `leadFacetCounts` keeps the previous
  // snapshot's counts to avoid flicker while `leadsCachedTotal` returns null. Null renders as
  // NO NUMBER (row()'s rule), so the worst transient state is a blank parent over stale
  // children — never two numbers that contradict each other.
  // 🚨 …AND THE CHILDREN LEAKED THE OTHER WAY, through a `??`. The call sites read
  // `facetCounts?.kinds ?? kindCountsForTree`, and that fallback fires in exactly one case:
  // the Leads pool, where `facetCounts` IS `leadFacetCounts` and its `.kinds` is null until
  // that query's page-0 snapshot lands. (Off Leads the chooser already returns
  // `kindCountsForTree`, so the `??` is a no-op there — it could only ever substitute the
  // wrong pool.) Measured live 2026-08-19: parent `ობიექტები` 10,132 — the leads answer,
  // correct — above children summing to 309, which is our-work-sized. Two pools, one tree.
  // The fallback is now `null`, which the tree already renders as NO NUMBER ("this query
  // hasn't reported yet → the facet renders without numbers rather than with stale ones").
  // Fixing the parent alone was not enough; both ends had to stop borrowing.
  const treePropertyTotal = entryTypeFilter[0] === 'Leads'
    ? (leadArchive ? leadCountArchive : leadCountActive)
    : propertyTotalForTree
  // TRUE while the numbers on screen are a tally over loaded rows rather than the corpus answer
  // — i.e. exactly while they climb. Drives the pulse in the category rows (see row()).
  // The agent-filter branch is excluded on purpose: those counts are a filtered tally by design
  // and do not resolve into a server answer, so pulsing them would promise an arrival that
  // never comes.
  const countsProvisional = !clientOnlyCut   // 2026-10-22: a server-sent agent pick resolves into a server answer
    && !(entryTypeFilter[0] === 'Leads' ? leadFacetCounts.kinds : serverKindCounts)
  // §13 telemetry: result_count at log-fire time + the zero-result marker that feeds
  // refined_from ("zero → respelled → hit" names the exact normalization miss) and the
  // abandoned signal. Ref writes only — never a render. No dep array on the mirror: the
  // log timeout must see CURRENT values whenever it fires, and this is cheaper than
  // enumerating every input it reads.
  useEffect(() => {
    const tel = searchTelemetryRef.current
    tel.visibleCount = visibleGroups.length
    tel.serverTotal = listingTotal
    tel.landedTextQ = landedTextQ
    tel.committedQ = committedQ
    tel.reachback = !!(listingIdQueries.length || phoneQueries.length || cadastralQueries.length)
    tel.filtersOn = advFiltersActive(advFiltersDebounced)
    tel.suggestShown = (placeSuggests.length || suggestResp) ? {
      places: placeSuggests.length,
      clients: suggestResp?.clients?.length ?? null,      // null = arm failed/absent, 0 = ran and found nothing (§11: don't conflate)
      businesses: suggestResp?.businesses?.length ?? null,
      complexes: suggestResp?.complexes?.length ?? null,
      streets: suggestResp?.streets?.length ?? null,
    } : null
    // ⚠️ A share link must never enter the telemetry ref. Guarding HERE rather
    // than at the two readers is deliberate: lastZero.raw is logged by BOTH the
    // 'abandoned' effect and `refined_from`, and openSharedLink clears the box
    // the instant it resolves — which is exactly the "box cleared after a
    // zero-result render" shape the abandoned logger is watching for. Guard the
    // writer once and neither reader can ever see a credential.
    // landedSearch (2026-08-05): a zero-result render can only be caused by a LANDED
    // query now — recording the live text would blame whatever half-typed word happened
    // to be in the box when a committed search came up empty.
    if (landedSearch.trim() && visibleGroups.length === 0 && !looksLikeShareLink(landedSearch)) {
      tel.lastZero = { raw: landedSearch.trim(), at: Date.now() }
    }
  })
  // Reduction/unknown display: server numbers when the RPC did the filtering (they cover the
  // FULL authored set), client-side classify only as fallback (covers just the loaded rows —
  // fine while the server hasn't answered yet, wrong to prefer once it has).
  // In local-filter mode the server never answered (no fetch happened) — listingTotal still
  // holds the UNFILTERED total, so the server branch would show "N of N (no reduction)".
  // The client classify covers the complete set in that mode, so its stats are exact.
  // Listing-shaped stats stay hidden on non-listing categories (2026-07-22: since the type
  // filter moved to the pipeline tail for the category rail, advStats sees listings even
  // under a Client/Meeting category — a "listings −95%" line over a client list is noise).
  const LISTING_CATEGORIES_SHOWN = entryTypeFilter.length === 0 || entryTypeFilter.some(ty => ty === 'Property' || ty === 'Listings')
  // ── CARRY-OVER BANNER (design 2b §2 — the answer to Q1) ────────────────────────────
  // When the pool switches, EVERY filter is kept and the change is STATED. The two
  // alternatives were both worse: resetting makes "2-room in Vake, mine or a lead" two
  // searches again, which is the question the restructure exists to make askable; keeping
  // them silently breaks Rule 1 the moment the count moves under the agent's feet.
  //
  // So: keep, and say what happened. "47 in leads → 6 in our work" is the sentence.
  const poolNow = entryTypeFilter[0] === 'Leads' ? 'leads' : 'work'
  // ⚠️ Audit 2026-10-29 D5: the leads total is the PILE's — archived when the archive is selected, exactly as the
  // category tree chooses it (`propertyTotal` above). Always reading the active count made the carry-over banner
  // quote 47 over a 7-lead archive. A count that has not landed stays null (the banner renders …).
  const poolTotal = poolNow === 'leads' ? ((leadArchive ? leadCountArchive : leadCountActive) ?? null) : listingTotal
  // NOTE: `sheetResultsCount` used to live here. It is declared below the activeGroups memo
  // now — it has to be able to see the 🗄 shelf, which is applied there. See it for why.
  const poolPrevRef = useRef({ pool: poolNow, total: null })
  // ⚠️ Audit 2026-10-29 D4: the EXACT category the switch left, for Undo. The pool pair (work/leads) cannot say it —
  // Undo turned Properties → Leads into "All categories". Written by an effect declared AFTER DETECT, so on the
  // commit that flips the pool DETECT still reads the category being left.
  const poolPrevCatsRef = useRef(entryTypeFilter)
  const [poolSwitch, setPoolSwitch] = useState(null)
  // 🚨 TWO effects, and the split is load-bearing — a single one was WRONG (caught in
  // self-review before this shipped, 2026-08-02).
  //
  // The first version tracked the total and detected the switch in one effect keyed on
  // `[poolNow]`. That effect can only run when `poolNow` CHANGED, so its "remember the
  // current total" branch — guarded on `prev.pool === poolNow` — was unreachable after
  // mount. The ref therefore froze at the value it held before the first fetch landed
  // (`listingTotal` starts at 0), and every banner would have opened "0 in Our work → …".
  //
  // This is verbatim the failure this repo logged twice already: an effect whose dep array
  // omits the value it reads. Build and tests cannot see it; only the number on screen can.
  //
  // Order matters. React runs effects in declaration order, so on the commit where the pool
  // flips, DETECT reads the ref while it still holds the pool we are LEAVING, and TRACK
  // overwrites it immediately after.
  useEffect(() => {   // DETECT — must be declared first
    const prev = poolPrevRef.current
    if (prev.pool === poolNow) return
    // ⚠️ ONE filter cannot survive the switch, and it must be DROPPED AND NAMED rather than
    // carried (design: the deal-change rule — "keep every answer that can survive, drop only
    // those that cannot, and name what was dropped"). Portal is leads-only in the data, not
    // just in the panel: `core.js` deliberately does not send `sources` on the main scopes,
    // because that RPC family has no `p_sources` (B128). So a carried Portal filter used to
    // sit in `advFilters` counting as an active constraint — the panel claimed filtering,
    // the chip row showed myhome.ge, and the results ignored both. A filter that is on
    // screen and not in the query is the exact failure this surface's fail-closed rule
    // exists to prevent.
    // Undo restores it, because undo restores the pool too and there it applies again.
    // B921: NOTHING IS DROPPED ANY MORE. Portal used to be discarded here because the
    // browse scopes ignored it, so carrying it would have meant a chip claiming a filter
    // the query never applied. B915 removed that asymmetry, so the filter now survives the
    // switch and keeps meaning — which is the owner's actual request ("these settings must
    // be applied on both").
    // 🚨 THE CONTROL THAT NEARLY FOOLED ME, recorded because it is reusable: I first
    // "proved" the server still ignored portal by passing `sources=nonexistent.zz` and
    // seeing the same answer as `ss.ge`. But `search_filters.js` WHITELISTS the value, so a
    // bogus one parses to null — that tested "no filter", not "a filter matching nothing".
    // A filter is only shown to bite by varying the value between two WHITELISTED ones with
    // different expected counts.
    // The variable stays (the banner and undo still read it) and is now always null; the
    // dropped-filter branch of the banner is consequently dead but harmless, and removing
    // it is a copy change for whoever next edits that string.
    const droppedSources = null
    // A switch with no filters carried nothing — there is nothing to reassure anyone about.
    // Checked AFTER the drop decision: a Portal-only search still has something to say
    // ("your Portal filter did not come with you"), and returning early would eat it.
    if (!advFiltersActive(advFiltersDebounced) && !droppedSources) { setPoolSwitch(null); return }
    setPoolSwitch({ from: prev.pool, fromTotal: prev.total, to: poolNow, droppedSources, fromCats: poolPrevCatsRef.current })
  }, [poolNow])   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { poolPrevCatsRef.current = entryTypeFilter }, [entryTypeFilter])   // after DETECT — see poolPrevCatsRef
  useEffect(() => {   // TRACK — declared second, so DETECT has already read the old value
    // `> 0` because 0 is indistinguishable from "the fetch has not landed", and quoting a
    // false 0 is worse than quoting nothing: the banner renders … for a missing number,
    // which is the §4 rule (a number that has not arrived is not the number zero).
    if (poolTotal > 0) poolPrevRef.current = { pool: poolNow, total: poolTotal }
  }, [poolNow, poolTotal])
  // Dismisses on the next filter interaction: the banner describes ONE event, and a stale
  // "→ 6" beside a set of filters that have since changed is exactly the lying number this
  // panel keeps being bitten by. Not a timer — an agent reading slowly is not the problem.
  useEffect(() => { setPoolSwitch(null) }, [advSig])   // eslint-disable-line react-hooks/exhaustive-deps
  // Undo puts back BOTH halves of the switch — the pool, and any filter the switch had to
  // drop. Restoring the pool without the filter would leave the agent in Leads holding a
  // search they never edited and no longer have.
  const undoPoolSwitch = () => {
    if (poolSwitch?.droppedSources) setAdvFilters(f => ({ ...f, sources: poolSwitch.droppedSources }))
    setEntryTypeFilter(poolSwitch?.fromCats ?? (poolSwitch?.from === 'leads' ? ['Leads'] : []))
    setPoolSwitch(null)
  }
  // 2026-10-22 (deskCounts.js serverReadout): SERVER mode compares like with like. `before` is the same
  // scope/agent/period/status AND city with the user's filters removed (a city pick is not a cut among
  // listings) — the per-question baseline, or for all cities the tc: seed of this view; none → no % and
  // no three-answer readout. `after` is the server total alone and `unknown` the server's shelf alone:
  // the old local correction subtracted a loaded-page cut from a corpus count and added page-scoped
  // unknowns to the server's own (LOCAL_ONLY_ADV_KEYS has been empty since B177, so it corrected nothing
  // real), and the server-vetted re-classify no longer produces those unknowns.
  const readoutBaseline = listingBaselines[baselineKey(`${listingsScope}:${myAgentId || ''}:${entryPeriod}:${entryStatusFilter}`, entryCityFilter)]
    ?? (entryCityFilter === 'all' ? cachedListingTotal : null)
  const advDisplay = !LISTING_CATEGORIES_SHOWN ? null
    : !localFilterMode && serverAdvFilters && (advFiltersActive(advFiltersDebounced) || districtFilterActive(advFiltersDebounced))
    ? (listingTotalLanded ? serverReadout({ baseline: readoutBaseline, total: listingTotal, serverUnknown }) : null)
    : (advStats && advStats.listingsBefore > 0
        ? { before: advStats.listingsBefore, after: advStats.listingsAfter, unknown: advStats.unknownExcluded }
        : null)
  // "Possible matches" shelf: server tier (0055) PLUS whatever the client classify shelved.
  // ⚠️ These are added, not chosen between (fixed 2026-07-27 while wiring the move-in
  // filter). The old `possibleGroups.length ? … : localFilterMode ? … : []` meant a
  // local-only filter's `unknown` verdicts reached the shelf ONLY when the whole feed fit
  // in one page — in server-filter mode, which is every feed over one page, they were
  // dropped silently. Shelving instead of hiding is the entire point of choosing `unknown`,
  // so it cannot depend on how the rows happened to be fetched. Deduped by group key
  // because a server possible and a locally-shelved group can be the same property.
  // ⚠️ The `.slice(0, 60)` that used to end this line is GONE (2026-07-29). It was written
  // when the RPC capped the shelf at 60 anyway, so it looked like a restatement of a server
  // fact; once the shelf became pageable it silently swallowed every page after the first —
  // the button would load rows the render then threw away, with no error and no clue.
  // The bound is now the server's own page count, which is what the header reports.
  const shownPossibleGroups = (() => {
    const local = advStats?.unknownGroups || []
    if (!local.length) return possibleGroups
    const seen = new Set(possibleGroups.map(g => g.key))
    return [...possibleGroups, ...local.filter(g => !seen.has(g.key))]
  })()

  // Page switcher — paginates the already-filtered/ranked list client-side. Deliberately NOT
  // ── Part C2 (2026-07-14): dormancy tier. Fetched AFTER the feed (separate
  // endpoint, deliberately zero work added to agent-listings); failure = untiered
  // feed, never an error. Cluster-level 60d activity: reveals + viewings + fresh
  // captures + availability wake. Desk-only (user decision) — matching/pool
  // never read this.
  const possiblesRef = useRef(null)   // §3 amber line's "show anyway" scroll target
  // ZeroState (b) "too-broad" — design ruling 2026-07-24 Q1: a BANNER above the grid (the
  // grid stays scrollable; the full-panel form only applies to an empty grid, which a >300
  // result set never is). Trigger: in browse, zero facets, server total > ~300. Dismissal
  // ("show all anyway") is per committed context — a new commit re-earns the banner.
  const [tooBroadDismissed, setTooBroadDismissed] = useState(false)
  useEffect(() => { setTooBroadDismissed(false) }, [committedSearch])
  const groupPropertyId = g => g.items?.[0]?.propertyId || g.items?.[0]?.listing?.property_id || null
  // Partition BEFORE pagination: active groups paginate as before; dormant sink to a
  // collapsed section below (browsable + renewable, never hidden entirely, never deleted).
  const { activeGroups, dormantGroups, unshelvedGroups } = useMemo(() => {
    // 🗄 archived (0375) leaves MY feed and nobody else's. Filtered here rather than
    // server-side on purpose: the shelf is a client-held Set exactly like ♥, so the feed
    // query stays identical for every agent and the pager keeps paging the same corpus.
    // ⚠️ Applied BEFORE the dormancy early-return, or an empty dormancy map (a fresh page,
    // or a failed side-read) would silently un-hide everything.
    // 🗄 REVEALS IN PLACE now (owner 2026-08-09). Hiding is a SUPPRESSION — its whole purpose is
    // to change what this feed shows — so the feed is where it must be able to admit it. Before,
    // the only way to see what your own feed was withholding was to leave the feed for a
    // separate destination, which is why the shelf button had to be rendered permanently even at
    // zero ("the only way back from an accidental archive"). With the reveal here, that
    // permanent control is unnecessary and the header shows 🗄 only when a shelf exists.
    const src = (archSet?.size && !showHidden) ? visibleGroups.filter(g => !archSet.has(groupPropertyId(g))) : visibleGroups
    // `unshelvedGroups` = the shelf applied, the dormancy partition NOT (2026-08-16). Published
    // because `src` was reaching only the GRID, and two other surfaces were reading
    // `visibleGroups` — which still holds the hidden rows — so the suppression stopped at the
    // one place anybody had looked. See the map and `sheetResultsCount` below.
    // Deliberately NOT `activeGroups`: dormant rows are collapsed on the grid rather than
    // removed, so they are still delivered and must still count. Shelf yes, dormancy no.
    if (!dormancyMap.size) return { activeGroups: src, dormantGroups: [], unshelvedGroups: src }
    const a = [], d = []
    for (const g of src) {
      const pid = groupPropertyId(g)
      ;(pid && dormancyMap.get(pid)?.dormant ? d : a).push(g)
    }
    // 0258 — reported-gone SINKS, it does not disappear. Owner ruling 2026-07-29 kept
    // 0178's policy rather than inventing a second one: these reports are UNVERIFIED,
    // so they de-prioritise and never remove. Deliberately NOT folded into
    // dormantGroups — dormant means "nobody has touched this in a while", reported
    // means "somebody was told it is gone". Collapsing them would lose which is which.
    // Same comparator direction as LeadsFeed.jsx so the two feeds rank identically.
    // Stable: Array.prototype.sort is stable in every engine we target, so groups with
    // equal counts keep the server's ranking — which the pager pages through.
    if (marketMap.size) {
      a.sort((x, y) => (marketMap.get(groupPropertyId(x))?.report_count || 0)
                     - (marketMap.get(groupPropertyId(y))?.report_count || 0))
    }
    return { activeGroups: a, dormantGroups: d, unshelvedGroups: src }
  }, [visibleGroups, dormancyMap, marketMap, archSet, showHidden])
  // How many rows THIS view is withholding — the number the inline line reports. Counted over
  // `visibleGroups` (post-filter, pre-hide) so it answers "hidden from what you are looking at
  // now", not "hidden in total": under a search for Vake, "2 hidden" must mean two Vake rows.
  // The header badge answers the total; these are different questions and both are honest.
  const hiddenHere = useMemo(() => {
    if (!archSet?.size) return 0
    let n = 0
    for (const g of visibleGroups) if (archSet.has(groupPropertyId(g))) n++
    return n
  }, [visibleGroups, archSet])
  // 🚨 THE MOBILE SHEET'S "see N results" BUTTON (owner-reported 2026-08-02: "mobile shows 0
  // when it should show results"). It used to read `visibleGroups.length` — the ENTRIES
  // pipeline — which is deliberately gated OFF on the Leads view (B204: leads render from
  // their own feed's snapshot, not from that pipeline). So on Leads the button said 0 always,
  // however many results the tree above it was reporting: the screenshot had a category tree
  // saying 654 over a button saying 0.
  // ⚠️ The two numbers come from different places and the button must follow the view that
  // owns the surface — the same chooser rule `facetCounts` and `geoDistrictCounts` already
  // use. A count taken from a pipeline that is not running is not a small inaccuracy; it is a
  // confident zero, and "no results" is the one answer that stops an agent looking.
  //
  // ⚠️ MOVED HERE FROM ABOVE `poolNow` (2026-08-16) so it can see the 🗄 shelf, and the reason
  // is the same sentence one polarity over. `visibleGroups` still holds the hidden rows, so
  // the button promised results the feed would then refuse to show: tap "Show 4 results",
  // land on an empty grid. The 2026-08-02 defect was a confident ZERO over a full feed; this
  // is a confident FOUR over an empty one, and the second is worse — a zero at least stops
  // the agent, while a number that evaporates on tap reads as the app being broken.
  //
  // `unshelvedGroups`, not `activeGroups`: dormant rows are collapsed on the grid rather than
  // removed, so they are still delivered and still counted. The ONLY thing subtracted versus
  // the old expression is the shelf.
  // ⚠️ NOT disabled at 0 — this button's onClick IS onCollapse, i.e. it is the sheet's close
  // control. Disabling it would trap the agent in the sheet with no way back to the feed.
  const sheetResultsCount = entryTypeFilter[0] === 'Leads'
    ? (leadCountActive ?? null)
    : unshelvedGroups.length
  // Identity-stable callbacks for the memo'd cards (B122 step 2). handleCardClick and
  // toggleFavorite read live state, so they go through useStableCallback (latest-ref);
  // per-group toggles come from a key-cached factory whose closures use functional setState
  // only — safe to cache forever. The modal-open setters (setDeskCoop etc.) are setState
  // functions and already stable.
  const onCardClickStable = useStableCallback(handleCardClick)
  const onToggleFavoriteStable = useStableCallback(toggleFavorite)
  const onToggleArchiveStable = useStableCallback(toggleArchive)
  // Sold / rented from the card (ShelfToggles asks "are you sure?" first). Same report as the
  // status card; the badge count moves at once, and a failure says so instead of pretending.
  const reportGone = (l, reason = 'deal_done_by_other') => {
    const pid = l?.property_id
    if (!pid) return
    recordMarketReport(pid, { reason, agentId: myAgentId })
      .then(r => reason === 'deal_done_by_other' && setMarketMap(prev => {
        const m = new Map(prev), cur = m.get(pid) || { property_id: pid }
        return m.set(pid, { ...cur, report_count: r?.report_count ?? ((cur.report_count || 0) + 1) })
      }))
      .catch(e => window.alert(`${t('desk_gone_failed')}: ${e.message}`))
  }
  const onReportGoneStable = useStableCallback(reportGone)
  // Memoized: a fresh object here would re-render every consumer of the context on every
  // parent render, which is exactly the identity trap the two contexts above exist to avoid.
  const viewerCtxValue = useMemo(() => ({ agentId: myAgentId }), [myAgentId])
  // Seeding a meeting is the strongest post-search action there is (B38 #4) — it costs the
  // agent real effort, so it says more than a card open. Wrapped HERE rather than inline at
  // the four call sites: setMeetingPropertySeed is a stable state setter, and an inline
  // arrow would hand the memo'd cards a new prop identity every render (B122's whole point).
  const onAddMeetingStable = useStableCallback(seed => {
    logSearchEngagement('meeting_seed')
    setMeetingPropertySeed(seed)
  })
  const onCreateContractStable = useStableCallback(propertyId => {
    if (!propertyId) return
    window.location.hash = `contract/start?property=${encodeURIComponent(propertyId)}`
  })
  const toggleFnsRef = useRef(new Map())
  const toggleForKey = key => {
    let f = toggleFnsRef.current.get(key)
    if (!f) {
      f = () => setExpandedGroups(s => { const next = new Set(s); next.has(key) ? next.delete(key) : next.add(key); return next })
      toggleFnsRef.current.set(key, f)
    }
    return f
  }
  // One renderer for both the active page and the dormant section — identical props,
  // so a dormant card behaves exactly like an active one (open/reveal/meeting all work).
  // `viewOverride` lets the Desk home render its sections in a FIXED card layout regardless
  // of the persisted browse view (2026-07-17 user report: switching to gallery/map inside a
  // category leaked into the home page's look — home and feed views are now independent).
  const renderEntryGroup = (group, viewOverride) => {
    const view = viewOverride || entriesView
    // "Available from" on the card face (2026-07-21). Free: the dormancy fetch above already
    // returns available_from per property (core_match.js builds it for the wake calculation)
    // and already runs for the whole feed — no extra request, no agent-listings change.
    const availableFrom = dormancyMap.get(groupPropertyId(group))?.available_from || null
    // 0258: the count that both SINKS this group (activeGroups sort) and BADGES it. Same
    // source for both so a card can never move down the page without saying why.
    const reportedGone = marketMap.get(groupPropertyId(group))?.report_count || 0
    // 0374 — this property's author-is-an-agent tally, if any. Counts only; the phone
    // itself never rides the batch response.
    const authorMark = authorMarkMap.get(groupPropertyId(group)) || null
    return view === 'list' ? (
      <HistoryListRow key={group.key} group={group} availableFrom={availableFrom} reportedGone={reportedGone}
        districts={districts} features={features}
        glow={candidateGlow[group.items[0].result?.requirement_id]}
        onCardClick={onCardClickStable}
        expanded={expandedGroups.has(group.key)}
        shade={expandedShade.get(group.key) || 0}
        onToggle={toggleForKey(group.key)} />
    ) : (
      <HistoryGroup key={group.key} group={group} availableFrom={availableFrom} reportedGone={reportedGone}
        districts={districts} features={features}
        glow={candidateGlow[group.items[0].result?.requirement_id]}
        glowClickable={!!aiCreateCmd}
        onCardClick={onCardClickStable}
        expanded={expandedGroups.has(group.key)}
        shade={expandedShade.get(group.key) || 0}
        onToggle={toggleForKey(group.key)} searchPolygons={searchPolygons}
        gallerySize={view === 'gallery' ? 'large' : view === 'home' ? 'home' : 'thumb'}
        coopsByProperty={coopsByProperty} defectsByProperty={defectsByProperty} agentFactsByProperty={agentFactsByProperty} distinctByProperty={distinctByProperty} devCardsByProperty={devCardsByProperty} sellersByProperty={sellersByProperty} coopOffersByProperty={coopOffersByProperty} sharesByProperty={sharesByProperty} restrByListing={restrDisplayByListing} termsByListing={termsByListing} clearByListing={clearByListing} viewsByProperty={viewsByProperty} favorites={favSet} onToggleFavorite={onToggleFavoriteStable} archived={archSet} onToggleArchive={onToggleArchiveStable} onReportGone={onReportGoneStable} authorMark={authorMark} onOpenCooperation={setDeskCoop} onAddMeeting={onAddMeetingStable} onCreateContract={onCreateContractStable} onShare={setShareSeed} onRepost={setDeskRepost} onShareOthers={openPropShare} onOpenTerms={setDeskTerms}
        meetingsIndex={meetingsIndex} clientMeetings={clientMeetings} onOpenMeeting={setEditingViewing} privileged={privileged} />
    )
  }

  // server-side offset pagination: visibleGroups merges two separately-fetched, separately-
  // ranked sources (client/meeting/candidate proposals + agent listings) into one relevance-
  // sorted list, so "page 2" only means something after that merge/sort has already happened.
  const totalPages = Math.max(1, Math.ceil(activeGroups.length / ENTRY_PAGE_SIZE))
  const clampedPage = Math.min(entryPage, totalPages - 1)
  // A page click starts the new page at the TOP (owner 2026-10-26: "it should move page to the top"). Before, the cards
  // swapped in place and the agent stayed down at the pager. The Desk may scroll inside a panel rather than the window,
  // so the nearest scrolling ancestor of the feed is reset; only the pager calls this, never a filter or a reload.
  const scrollFeedToTop = () => {
    for (let el = feedGridRef.current?.parentElement; el; el = el.parentElement) {
      const oy = getComputedStyle(el).overflowY
      if ((oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight) { el.scrollTo({ top: 0 }); return }
    }
    window.scrollTo({ top: 0 })
  }
  // B653 (owner 2026-08-12: "we should precallculate all pages and show how many page ae there").
  // ⚠️ THE PAGE COUNT CANNOT BE PRECOMPUTED FROM THE SERVER TOTAL, and the reason is the comment
  // directly above: this pager walks a MERGED, client-ranked list — agent listings plus client /
  // meeting / candidate proposals — so `listingTotal` (listings only, pre-merge, pre-client-cut)
  // is not the size of the thing being paginated. Printing pages from it would promise pages that
  // do not exist, which is the "a facet's number must equal what the click delivers" rule broken
  // in the direction that wastes the most time.
  // So the count stays honest instead: it describes the pages that EXIST NOW, and says out loud
  // when more are still arriving rather than silently growing under the agent — which is the
  // actual complaint ("გვერდი 1 / 4" quietly becoming 1 / 9).
  const morePagesPending = listingRawCount < listingTotal
  // Owner 2026-09-05: "build pager with start end pages". The END page is drawn before the rows
  // exist: the pages that exist now plus what the server still holds, at the page size. It is an
  // UPPER BOUND — unloaded listing rows may merge into existing groups, so the real count can end
  // lower and the strip shrinks as rows arrive — and it is drawn muted/dashed for exactly that
  // reason (FeedPager `loadedPages`). Clicking such a page sets entryPage past the loaded pages;
  // clampedPage then sits on the last loaded page, the near-end prefetch below keeps loading
  // until the target is reachable, and the feed lands on it — past AUTO_LOAD_MAX too, because a
  // click is an explicit ask (owner 2026-09-05: "show last pages too"; the unfiltered book is
  // ~3,800 listings and the cap hid its end). Not offered once the chain has given up (3 failed
  // loads): a page that cannot load is a promise the click would break.
  const estimatedTotalPages = (morePagesPending && loadMoreFailsRef.current < 3)
    ? Math.max(totalPages, Math.ceil((activeGroups.length + Math.max(0, listingTotal - listingRawCount)) / ENTRY_PAGE_SIZE))
    : totalPages
  // ⚠️ THE URL CARRIES `entryPage`, NOT `clampedPage` — clampedPage is declared here, three
  // thousand lines below the writer, so the writer cannot see it (this file's declare-above-
  // first-consumer rule). That is fine for the feed, which clamps as it renders, and NOT fine
  // for the bar: it would advertise "page 21" over a four-page result forever. So the state is
  // corrected back once the count is FINAL.
  // ⚠️ Only when `morePagesPending` is false, and that condition is the whole point. During
  // loading totalPages is 1, so clamping eagerly would snap a restored "page 7" to page 1
  // before its results had arrived — destroying the very restore the URL exists to perform.
  useEffect(() => {
    // ⚠️ AND NOT WHILE A SPECIAL FEED IS ON SCREEN. Leads/ClientLeads render their own list
    // and their own page count; `totalPages` here describes the PROPERTY feed, which is empty
    // then — so this clamped every leads page straight back to 1. Caught by clicking page 3 and
    // watching it snap to 1, which no build or unit test would have shown.
    if (SPECIAL_FEED) return
    if (morePagesPending) return
    // ⚠️ NOR BEFORE THE FIRST ANSWER HAS LANDED (2026-10-29). On a fresh load listingTotal and listingRawCount are
    // both 0, so morePagesPending is false and totalPages is 1 — a refreshed `type=Property&page=3` link snapped to
    // page 1 before a single row arrived. A landed answer (zero included) is what makes totalPages final.
    if (!listingTotalLanded) return
    if (entryPage > totalPages - 1) setEntryPage(Math.max(0, totalPages - 1))
  }, [entryPage, totalPages, morePagesPending, listingTotalLanded])

  const pageGroups = useMemo(() => activeGroups.slice(clampedPage * ENTRY_PAGE_SIZE, (clampedPage + 1) * ENTRY_PAGE_SIZE),
    [activeGroups, clampedPage])

  // ── B611: paint the first cards, then fill the rest in on idle ──────────────
  // Measured on production in a VISIBLE tab (the distinction matters — a hidden tab
  // reports no long tasks at all, which is how this was misdiagnosed as a network
  // problem once already): switching to the objects feed blocked the main thread for
  // 2,093–2,864 ms across 2–4 long tasks. The attribution probe below settles where
  // it goes — of 3,266 ms blocked, the data pipeline was 4.4 ms and meta-derivation
  // 16.8 ms. **21 ms of 3,266 is data. The rest is React rendering the cards**, at
  // roughly 88 ms per group, and it scales exactly with how many are on screen:
  // 2 cards → 0 ms blocked, 30 groups → 2,645 ms.
  //
  // ⚠️ `content-visibility: auto` was TRIED AND REJECTED (7% — inside the noise).
  // It lets the browser skip layout and paint, and the cost here is neither: it is
  // JS. Don't reach for it again on this surface.
  //
  // So the page still holds 30 groups; it just stops building all of them in the
  // click's own frame. Nothing is dropped and no data changes — only the ORDER in
  // which the same cards reach the DOM, and the tail lands within a few hundred ms.
  //
  // ⚠️ Deliberately budgeted at the RENDER only. `pageGroups` still feeds the batch
  // prefetches below (terms, cooperations, shares, views) at full width, so the tail
  // cards arrive already carrying their data instead of each minting a fetch — which
  // would trade a render stall for a request storm.
  const FIRST_PAINT_GROUPS = 8
  const IDLE_CHUNK = 8
  const [renderBudget, setRenderBudget] = useState(FIRST_PAINT_GROUPS)
  // Reset on a genuinely different page of results. Keyed on a VALUE signature, not
  // on `activeGroups` identity: that array is rebuilt by an upstream memo on ordinary
  // re-renders, and resetting per identity would re-collapse the feed mid-scroll.
  const pageSig = `${clampedPage}:${pageGroups.length}:${pageGroups[0]?.key || ''}`
  useEffect(() => { setRenderBudget(FIRST_PAINT_GROUPS) }, [pageSig])
  // F-045: the feed's height hold (declared beside listingsFetching) is released only once the fetch has
  // ended AND the render budget has drawn the whole page — not at the fetch's end, which is before the
  // fresh rows exist on screen.
  useEffect(() => {
    if (!(holdHeight || holdElRef.current)) return
    if (listingsFetching) { if (holdPhaseRef.current !== 'fetching') perfTrace('hold:phase-fetching'); holdPhaseRef.current = 'fetching'; return }
    if (holdPhaseRef.current === 'fetching') {
      // the possible-matches shelf is fetched AFTER the fresh page and mounts below the grid (trace 2026-09-06:
      // released at 8,317 px, then +3,3k when the shelf landed) — the layout is not ready until it has
      if (renderBudget >= pageGroups.length && !possiblesLoading) { releaseFeedHold(); return }
      // CEILING: a hidden tab starves the idle callbacks that grow the render budget (observed 2026-09-06:
      // fresh rows landed, the hold stayed on for 25 s+). Nothing may hold the page past a few seconds after
      // the fetch has ended, whatever the budget or the shelf are doing.
      const h = setTimeout(() => { if (holdPhaseRef.current === 'fetching') { perfTrace('hold:ceiling'); releaseFeedHold() } }, 8000)
      return () => clearTimeout(h)
    }
    // held at a commit, no fetch yet: give the fetch its debounce to start; if none comes, let go
    const h = setTimeout(() => { if (holdPhaseRef.current === 'commit') releaseFeedHold() }, 1500)
    return () => clearTimeout(h)
  }, [listingsFetching, renderBudget, pageGroups.length, holdHeight, possiblesLoading])   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (renderBudget >= pageGroups.length) return
    const grow = () => setRenderBudget(b => Math.min(pageGroups.length, b + IDLE_CHUNK))
    const ric = typeof window !== 'undefined' && window.requestIdleCallback
    // The timeout is the floor, not the target: a feed that never goes idle still
    // completes. Safari has no rIC, so it gets a plain tick.
    const h = ric ? window.requestIdleCallback(grow, { timeout: 250 }) : setTimeout(grow, 32)
    return () => { if (ric) window.cancelIdleCallback(h); else clearTimeout(h) }
  }, [renderBudget, pageGroups.length])
  const renderedGroups = useMemo(
    () => (renderBudget >= pageGroups.length ? pageGroups : pageGroups.slice(0, renderBudget)),
    [pageGroups, renderBudget])

  // Two alternating ring shades for EXPANDED groups only, cycling in on-screen order — so two
  // expanded groups sitting near each other (even with a collapsed one between them) are always
  // visually distinguishable, instead of every expanded group getting the identical amber ring.
  const expandedShade = useMemo(() => {
    const map = new Map()
    let i = 0
    for (const g of visibleGroups) if (expandedGroups.has(g.key)) map.set(g.key, i++ % 2)
    return map
  }, [visibleGroups, expandedGroups])
  // landedSearch (2026-08-05): "filters active" is a statement about the FEED, and typing
  // no longer filters it — an uncommitted word claiming filters-active would show clear-
  // filters affordances for a filter that does not exist yet.
  // ⚠️ `entryStatusFilter !== 'active'`, not `!== 'all'` (B576, 2026-08-16): `active` is the
  // DEFAULT now and a default is not a filter. Left as `!== 'all'` this term would be true on
  // every render, so the Desk would claim filters-active before the agent touched anything —
  // permanent clear-filters affordances over an untouched view, which is the same lie the
  // landedSearch note above exists to prevent.
  const entryFiltersActive = entryTypeFilter.length > 0 || entryStatusFilter !== 'active' || landedSearch.trim() !== '' || entryCityFilter !== 'all' || advFiltersActive(advFilters) || !!imageSearch || !!streetSel
  // SPECIAL_FEED moved UP to just below listingsScope (2026-07-27) — it used to be declared
  // here, hundreds of lines BELOW the entries fetch that most needs it, so that fetch was the
  // one consumer with no gate. See its new home for the bug that caused.

  // ── Desk home derived data (see the committedSearch block above for the design rationale) ──
  // Home is now DERIVED: `isHome = isEmptyCommitted(committedSearch)` (Phase 1 §2.1) — safe,
  // unlike the pre-2026-07-22 gate, because every committedSearch input is written only by
  // commit acts (§2.2; typing writes the draft overlay, never advFilters). The only extra
  // gate is SPECIAL_FEED: Pool/Meeting/Business/Leads REPLACE the entries pipeline with their
  // own exclusive feed, so home can't co-render with one. (Reaching a special feed sets the
  // category, which makes committedSearch non-empty anyway — belt-and-braces, not mechanism.)
  const deskHomeActive = !SPECIAL_FEED && isHome
  // ── S6: THE ESCAPE HATCH AT THE END OF THE LIST (design 2b §5) ─────────────────────
  // "That's all in our work — the same search matches N leads nobody has called yet."
  // The situation it answers is the one the whole pool restructure exists for: an agent
  // reaches the bottom of their own inventory and has no idea that the identical criteria
  // have supply one pool over. Before the pools were one axis this was two searches, and
  // the second one never got run.
  //
  // Shown only when the list is genuinely EXHAUSTED (`listingRawCount >= listingTotal`).
  // Offering it mid-scroll would be telling an agent to leave a list they have not read.
  // Zero results counts as exhausted, and is the case where it matters most.
  // Not on the map: a card appended after a map is appended to nothing.
  const leadsHatchOn = !SPECIAL_FEED && !deskHomeActive && entriesView !== 'map'
    && (advFiltersActive(advFiltersDebounced) || landedSearch.trim() !== '')
    && listingTotal >= 0 && listingRawCount >= listingTotal
    && !(phoneQueries.length || listingIdQueries.length || cadastralQueries.length || imageMatchIds.size)
  // The number has to be the leads total for THIS search, and `leadCountActive` is exactly
  // that (same cache key the Leads feed itself reads). It is cache-only, so while standing
  // in our work it is usually absent — warm it once, when the card actually appears, rather
  // than paying for the most expensive query in the app on every filter edit. Until it
  // lands the card renders no number at all (never 0 — brief §4).
  useEffect(() => {
    // F-022c (2026-09-05): the hatch's count fired WHILE the primary search was still running
    // (nginx 20:52:01: the leads count for the street preflighted beside the search, both hit the
    // 8 s net at :09). A count for a card that only renders once the search has answered must
    // wait for that answer.
    if (leadsHatchOn && leadCountActive == null && !listingsFetching && feedWasFetchingRef.current) leadHoverPrefetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadsHatchOn, leadCountActive, listingsFetching])   // listingsFetching: the gate above must re-evaluate when the search lands
  // ⚠️ FROZEN WHILE TYPING (2026-07-22; re-checked at the 2a split per plan §2.7. Since the
  // 2026-08-05 landing ruling this is BELT, no longer load-bearing: visibleResult/activeGroups
  // read the LANDED side now, so typing cannot re-cut them in the first place. Kept because
  // it costs one line and still guards the half-typed-but-landed edge — the render between
  // a commit act and its state landing.) activeGroups used to narrow LIVE on box text — and since
  // typing no longer leaves home, an unfrozen cockpit would silently re-cut its own property
  // and client rows under the open dropdown, and empty them entirely on a no-match query
  // (home looking broken while the agent is mid-word). The cockpit is a fixed front page, not
  // a result surface: it holds the last box-empty snapshot until the search actually commits
  // — at which point deskHomeActive is false and this memo returns null anyway.
  // Live feedback for the in-progress query lives in the dropdown instead (searchPreview).
  const homeSnapRef = useRef({ props: [], clients: [] })
  // ── Cockpit telemetry (briefing ruling 2026-07-23: "instrument first") ──
  // One desk.home_viewed impression per home ENTRY (ref-gated — deskHome recomputes on
  // every feed refresh, the event must not) with per-section item counts, plus
  // desk.home_click per section via onClickCapture on each <section> below. A week of
  // "which section did anyone touch" decides the briefing's content (designer step 1);
  // the events are the measurement, deliberately not wired to any UI.
  const homeViewedRef = useRef(false)
  const homeClick = section => () => trackDeskEvent('home_click', { section })
  const deskHome = useMemo(() => {
    if (!deskHomeActive) return null
    if (entrySearch.trim()) return homeSnapRef.current
    // activeGroups is already relevance/recency-sorted — just split by object type.
    const props = [], clients = []
    for (const g of activeGroups) {
      const meta = compactSummary(g, districts, features)
      // Owner ruling 2026-07-23: front-page priority = my properties → clients → leads,
      // with recently-added properties capped at 3 (a Recent-leads 3-card section joins
      // the main column below them — see homeLeads).
      if (meta.objType === 'client') { if (clients.length < 4) clients.push(g) }
      else if (meta.objType !== 'candidate' && meta.objType !== 'meeting') { if (props.length < 3) props.push(g) }
      if (props.length >= 3 && clients.length >= 4) break
    }
    homeSnapRef.current = { props, clients }
    return homeSnapRef.current
  }, [deskHomeActive, entrySearch, activeGroups, districts, features])
  // ⚠️ The groups the page actually DRAWS (Desk home audit 2026-10-21). Home shows 3 property and 3-4 client groups, but
  // thumbnails and every card overlay (descriptions, cooperations, offers, restrictions, views, ...) were collected over
  // the whole 30-group browse page behind it. On home they follow the home cards; in browse, the page as before.
  const hydrateGroups = useMemo(
    () => (deskHomeActive && deskHome ? [...deskHome.props, ...deskHome.clients] : pageGroups),
    [deskHomeActive, deskHome, pageGroups])
  // Recent leads on the front page (owner 2026-07-23): 3 newest lead clusters, rendered
  // through the SAME card pipeline as everything else (LeadsFeed's pseudo-group shape).
  // Fetched once per home entry — small page, thumbs patched in, no lead-metadata overlay
  // (density chips are feed detail; the cockpit is glance-and-go).
  // ⚠️ (Desk home audit 2026-10-21) The pool keeps a STATUS: a failed first read used to look exactly like "no leads"
  // (the section hides when empty, on purpose), and a failed refresh kept old cards with no word. It also holds up to
  // six clusters so the personal Hide (🗄) can be applied HERE - home leads skipped the shelf filter, so a hidden lead
  // stayed on the front page - and the next eligible lead refills the slot.
  const [homeLeadPool, setHomeLeadPool] = useState({ status: 'loading', groups: [] })
  const [homeLeadsTick, setHomeLeadsTick] = useState(0)
  useEffect(() => {
    if (!deskHomeActive) return
    let gone = false
    setHomeLeadPool(p => ({ ...p, status: p.groups.length ? 'refreshing' : 'loading' }))
    getAgentListings({ scope: 'leads', limit: 12 }).then(async r => {
      if (gone) return
      const byCluster = new Map()
      for (const l of r.listings) {
        const k = l.cluster_key || l.property_id || l.listing_id
        if (!byCluster.has(k)) byCluster.set(k, [])
        byCluster.get(k).push(l)
      }
      const top = [...byCluster.entries()].slice(0, 6)
      const lids = top.flatMap(([, ms]) => ms.map(m => m.listing_id)).filter(Boolean)
      const th = lids.length ? await getListingThumbs(lids).catch(() => ({})) : {}
      if (gone) return
      setHomeLeadPool({ status: 'ready', groups: top.map(([key, members]) => ({
        key: `homelead:${key}`,
        items: members.map(li => ({
          id: `listing:${li.listing_id}`, proposal_kind: 'scraped_listing_readonly',
          status: li.status, created_at: li.listed_at || null,
          lead_badge: 'active',   // LEAD card badge (designer board)
          listing: { ...li, images: (li.images?.length ? li.images : th[li.listing_id]) || [] },
          proposed: {}, result: {},
        })),
      })) })
    }).catch(() => { if (!gone) setHomeLeadPool(p => ({ ...p, status: 'failed' })) })
    return () => { gone = true }
  }, [deskHomeActive, homeLeadsTick])
  const homeLeads = useMemo(() => {
    const src = (archSet?.size && !showHidden) ? homeLeadPool.groups.filter(g => !archSet.has(groupPropertyId(g))) : homeLeadPool.groups
    return src.slice(0, 3)
  }, [homeLeadPool, archSet, showHidden])
  // ⚠️ HOME AGENDA, loaded on its own (audit 2026-10-21): the only automatic load waited for the INVENTORY to have
  // cards, so an agent with meetings but no listings - or a failed inventory read - was told "no meetings" without the
  // agenda ever being asked. Home now loads it on entry through loadViewings (settled/error/retry), and while home
  // stays open re-reads the agenda and nudges every 5 minutes (visible tab only) and moves the "upcoming" cutoff on.
  const [homeNow, setHomeNow] = useState(() => Date.now())
  useEffect(() => {
    if (!deskHomeActive) return
    if (!viewingsLoadedRef.current) { viewingsLoadedRef.current = true; loadViewings() }
    const id = setInterval(() => {
      if (document.visibilityState !== 'visible') return
      setHomeNow(Date.now())
      // quiet refresh: a failure keeps the list on screen (it was read successfully before)
      getMeetingsFeed().then(vs => { setViewings(vs); setViewingsErr(null) }).catch(() => {})
      getMeetingNudges().then(setMeetingNudges).catch(() => setMeetingNudges(prev => (prev && !prev.failed) ? prev : { failed: true }))
    }, 5 * 60_000)
    return () => clearInterval(id)
  }, [deskHomeActive])  // eslint-disable-line react-hooks/exhaustive-deps
  const homeMeetings = useMemo(() => {
    if (!deskHomeActive) return []
    let rows = (viewings || []).filter(v => MEETING_ACTIVE.includes(v.status))
    // Guests too (fixed 2026-08-15) — `agent_ids` is the INVOLVED set, so an agent who is
    // GOING to a meeting without a share in it never saw it on their own front page, which
    // is the one surface that exists to tell them where to be.
    if (!privileged && myAgentId) rows = rows.filter(v =>
      (v.agent_ids || []).includes(myAgentId)
      || (v.attendees || []).some(a => a.agent_id === myAgentId))
    // ⚠️ "მოახლოებული" MEANS UPCOMING, and it has to be true (owner-caught 2026-08-06:
    // a meeting seven hours past was sitting here while the red banner chased the same
    // row two inches above it). This used to keep yesterday's, with the comment "a
    // just-missed meeting still needs follow-up" — a correct instinct written BEFORE the
    // nudge banner existed. The banner is that follow-up now, so a passed meeting has
    // exactly one home and the two surfaces stop saying different things about it.
    //
    // The cut is the SAME grace the critical nudge uses (0427 config, default 2h), read
    // from the nudges payload so the two can never drift: inside the grace an agent may
    // literally be in the meeting, and it still reads as "now". Past it, the banner owns
    // it — and the moment the agent reschedules, the new time is in the future and the
    // row returns here on its own.
    const graceH = meetingNudges?.config?.missing_update_after_hours ?? 2
    const cutoff = homeNow - graceH * 3600e3
    // ⚠️ meetingSchedAt, not a private re-derivation (fixed 2026-08-15). This read the
    // embedded events ONLY, so a row whose time lives in `scheduled_at` — which is where
    // core meetings keep it — yielded null and was then DROPPED by the filter on the next
    // line. It survived only because the feed adapter happens to synthesize a `scheduled`
    // event; one shape change away from silently emptying the section.
    return rows
      .map(v => ({ v, at: meetingSchedAt(v) }))
      .filter(x => x.at && new Date(x.at).getTime() >= cutoff)
      // NO cap here (audit 2026-10-21): the memo kept 6, the heading printed 6 and "+N" said 2 while 5 were unseen.
      // This is the TRUE upcoming total; the renderer slices only the cards it draws.
      .sort((a, b) => a.at.localeCompare(b.at)).map(x => x.v)
  }, [deskHomeActive, viewings, privileged, myAgentId, meetingNudges, homeNow])
  // ── phone home top + section scope (Desk home audit 2026-10-21) ──────────────────────────────────────────────
  const phoneHomeTop = deskHomeActive && !!deskHome && isMobile
  const jumpTo = id => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  const nextMeetingText = (() => {
    if (viewingsErr) return t('desk_home_meetings_failed')
    if (!viewingsSettled) return t('desk_home_meetings_loading')
    const v = homeMeetings[0]
    if (!v) return t('desk_home_no_meetings')
    const at = meetingSchedAt(v)
    const when = at ? fmtTbilisi(at, { weekday: 'short', hour: '2-digit', minute: '2-digit', hour12: false }) : ''
    return tLog('desk_next_meeting', { when, who: v.client_name || '' }).replace(/ · $/, '')
  })()
  // Each home section has its OWN lens (they never shared one): say which, small, beside the heading.
  const officeOrAll = officeScopeLabel || t(role === 'manager' ? 'desk_scope_office' : 'desk_scope_company')
  const homeScope = {
    props: listingsScope === 'mine' ? t('desk_scope_mine') : officeOrAll,
    clients: myAgentId ? t('desk_scope_mine') : t('desk_scope_company'),
    meetings: !privileged && myAgentId ? t('desk_scope_mine') : officeOrAll,
  }
  // ── Meeting card photos, fetched by PROPERTY (2026-08-01) ───────────────────
  // The card thumb used to be scavenged from `listingEntries` — whatever the Desk
  // happened to have loaded. That made the picture a function of the viewer's scope:
  // an agent's own listings are in their ~100-row page, an ADMIN's page is the newest
  // 100 of the whole agency, so admins saw the grey map placeholder on properties
  // holding several listings and a dozen photos (verified against the live rows).
  // The property is the key the meeting actually has, so ask by it.
  //
  // Capped at 60 ids per fetch (the server caps too) and cached there for 10 min;
  // ordered soonest-meeting-first so the cockpit's six always make the cut.
  const [meetingThumbs, setMeetingThumbs] = useState({})
  // property_id → a representative listing id, so a meeting (which knows only a property)
  // can open the property modal, which is keyed by LISTING.
  const [meetingListingByProp, setMeetingListingByProp] = useState({})
  // EVERY distinct meeting property, appointment-date order (matching the feed's own sort
  // since 2026-08-15 — created_at order meant the rows the agent actually sees could miss
  // the cut). Uncapped: the cooperation fetch consumes this whole list (listCooperations
  // chunks at 80 ids itself), because past the old 60-cap the terms row — INCLUDING the
  // amber "terms missing" warning — silently vanished, and absence-of-fetch was
  // indistinguishable from absence-of-deal on the exact row built to be unmissable.
  const meetingPropIdsAll = useMemo(() => {
    const at = v => v.scheduled_at
      || (v.events || []).find(e => e.kind === 'scheduled' || e.kind === 'reschedule')?.event_at
      || v.created_at || ''
    const ordered = [
      ...homeMeetings,
      ...[...(viewings || [])].sort((a, b) => at(b).localeCompare(at(a))),
    ]
    const out = []
    const seen = new Set()
    for (const v of ordered) {
      const pid = v.property_snapshot?.core_property_id
      if (!pid || seen.has(pid)) continue
      seen.add(pid); out.push(pid)
    }
    return out
  }, [homeMeetings, viewings])
  // The THUMBS fetch keeps the 60-cap — that server endpoint caps at 60 per call, and the
  // cockpit only ever draws the first handful of photos. Only the cooperation coverage
  // above needed to stop truncating.
  const meetingPropIds = useMemo(() => meetingPropIdsAll.slice(0, 60), [meetingPropIdsAll])
  useEffect(() => {
    if (!meetingPropIds.length) return
    let gone = false
    getPropertyThumbs(meetingPropIds)
      .then(({ thumbs, listings }) => {
        if (gone) return
        setMeetingThumbs(prev => ({ ...prev, ...thumbs }))
        setMeetingListingByProp(prev => ({ ...prev, ...listings }))
      })
      // A thumb miss is cosmetic — ListingGallery still draws its map slide.
      .catch(e => console.error('[meeting-thumbs]', e.message))
    return () => { gone = true }
  }, [meetingPropIds.join(',')])   // eslint-disable-line react-hooks/exhaustive-deps
  const homeThumbByProp = useMemo(() => {
    const m = new Map()
    for (const l of listingEntries || []) if (l.property_id && !m.has(l.property_id)) m.set(l.property_id, l)
    // Property-keyed photos fill the gaps AND re-photograph a matched-but-imageless
    // entry (agent-listings ships thumbs=0; only cards on the current feed page get
    // patched, and a meeting's property is usually not one of them).
    for (const [pid, images] of Object.entries(meetingThumbs)) {
      if (!images?.length) continue
      const have = m.get(pid)
      if (!have) m.set(pid, { property_id: pid, images })
      else if (!have.images?.length) m.set(pid, { ...have, images })
    }
    return m
  }, [listingEntries, meetingThumbs])
  // Impression: once per home entry, when the cockpit's data is actually in hand.
  useEffect(() => {
    if (!deskHomeActive) { homeViewedRef.current = false; return }
    if (homeViewedRef.current || !deskHome) return
    // Cold-load guard (found in the first live trace): while the feed is still loading,
    // deskHome exists with all-zero sections — an impression stamped then reads as "agent
    // saw an empty cockpit", polluting the week's section analysis. Wait until the fetch
    // settled (or anything is actually on screen); a genuinely empty cockpit after load
    // IS worth recording.
    if (histLoading && !deskHome.props.length && !deskHome.clients.length && !homeMeetings.length && !homeLeads.length) return
    homeViewedRef.current = true
    trackDeskEvent('home_viewed', {
      meetings: homeMeetings.length, props: deskHome.props.length,
      leads: homeLeads.length, clients: deskHome.clients.length,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deskHomeActive, deskHome, histLoading])
  // Auto-prefetch the next server chunk — DEMAND-driven only (2026-07-15): fetch ahead once
  // the agent pages within 2 client pages of the end of what's loaded. The old (2026-07-11)
  // unconditional chain-to-complete — which downloaded the ENTIRE feed on every cold Desk
  // open to enable local-filter mode — is gone: it was the top Supabase egress driver (5GB
  // free-tier quota blown 141% by 2026-07-15) and the reason first load felt slow. Filters
  // run server-side (0048), so completeness buys nothing the RPC doesn't already provide;
  // local-filter mode still kicks in naturally when the dataset fits in one page.
  // AUTO_LOAD_MAX stays as the safety cap: a client-side filter (city/text over loaded rows)
  // with zero matches would otherwise make nearEnd chain through the whole dataset.
  // listingLoadingMore doubles as the concurrency guard — loadMoreListings advances the
  // offset cursor by rowsReturned, so overlapping calls would fetch the same chunk twice.
  // (AUTO_LOAD_MAX is declared beside ENTRY_PAGE_SIZE — see the note there.)
  // Free-text chain-to-complete REMOVED (prefetch demotion, 2026-07-16): the residual text
  // rides advFilters.q into the v5 RPC (0104) — the server returns only matching clusters,
  // already complete for the query, so the old "download everything so the client textQ can
  // see it" fallback (2026-07-15) is dead weight: it re-downloaded the whole feed on every
  // typed street name. The client textQ in visibleResult stays as the haystack-mirroring
  // re-filter (and the only filter for non-listing rows). ⚠️ Don't re-add a chain here for a
  // new client-only predicate — give it a server param instead (this is the second time the
  // chain was removed; it keeps growing back).
  useEffect(() => {
    // ⚠️ B884: `cursorFreshRef`, NOT the `cursorFresh` state. Both are kept in step; only the
    // ref is correct HERE, because the load effect runs earlier in this same commit and its
    // `setCursorFresh(false)` is not visible to this line until the next render. Reading the
    // state let a load-more start against a cursor whose page 0 had just been aborted.
    if (SPECIAL_FEED || listingLoadingMore || !cursorFreshRef.current || loadMoreFailsRef.current >= 3) return
    // ⚠️ not behind HOME (Desk home audit 2026-10-21): home draws 3 property groups, yet "near the end of a 30-group page"
    // chained a 100-row continuation on every home open. The chain starts when the agent actually enters browse.
    if (deskHomeActive) return
    if (chainExhaustedRef.current) return   // server already returned an empty chunk at this cursor
    // Identity reach-back queries (phone / listing-id / image) are answered DB-wide by the idSearch
    // effect (searchCoreListings + full lineage family) — paging the feed to let the client-side
    // identity filter "find" them adds nothing and used to chain the ENTIRE DB (a reach-back
    // collapses visibleGroups to ~2, which nearEnd reads as "at the end" → crawl to AUTO_LOAD_MAX).
    // The reach-back owns them; never crawl the feed for an identity query (2026-07-20).
    if (phoneQueries.length || listingIdQueries.length || cadastralQueries.length || imageMatchIds.size) return
    // TEXT gets the same rule (2026-07-22, user report: typing "White Square Varketili 4"
    // background-crawled 1830 rows). Landed-but-not-server-committed text (a short residual
    // commits as '') filters LOADED rows only, so zero local matches made nearEnd chain the
    // whole feed hunting for rows the client filter can never match — §5.3 says typing NEVER
    // fetches; the whole-DB answer lives behind Enter (committed q → server-complete RPC,
    // whose own total already stops this chain). landedSearch since 2026-08-05 — live text
    // no longer moves visibleGroups at all, so gating on it would be a no-op that reads
    // like protection.
    // ⚠️ AUTO_LOAD_MAX no longer protects here: the cap (2000) was tuned when the DB was much
    // larger than it — at 1830 listings "capped" = the entire database, on every typed query.
    if (landedSearch.trim() && visibleGroups.length === 0) return
    if (listingRawCount <= 0 || listingRawCount >= listingTotal) return
    // Owner 2026-09-05 ("we need pager to show last pages too"): the cap stops AUTOMATIC crawling, not an
    // explicit ask. A page the agent clicked past the loaded pages (entryPage beyond totalPages) keeps
    // loading until it is reachable — that click is the intent the cap exists to wait for.
    if (listingRawCount >= AUTO_LOAD_MAX && entryPage < totalPages) return
    // B884: never ask twice for the same offset. `listingRawCount` is state, so two chain ticks
    // can run before `setListingRawCount` lands and both compute the same cursor — measured as
    // offset 60 fetched twice in one filter (2,877 ms, then 79 ms). `loadMoreBusyRef` does not
    // cover it: the second tick can arrive AFTER the first request resolved and cleared the
    // busy flag but BEFORE its state update rendered.
    if (listingRawCount === chainOffsetRef.current) return
    const nearEnd = (clampedPage + 3) * ENTRY_PAGE_SIZE > visibleGroups.length
    if (nearEnd) { chainOffsetRef.current = listingRawCount; loadMoreListings() }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [SPECIAL_FEED, deskHomeActive, listingLoadingMore, cursorFresh, listingRawCount, listingTotal, clampedPage, visibleGroups.length, advFiltersDebounced, phoneQueries.length, listingIdQueries.length, cadastralQueries.length, imageMatchIds.size])
  // Lazy thumbnails (Phase 2d, 2026-07-15): agent-listings is called with thumbs=0 (its
  // page-wide thumbnail stage was the largest one left after the page-100 diet), so photo
  // strips are fetched HERE for just the ~30 groups on the current display page and patched
  // into listingEntries/listingPossibles by id — unifiedGroups re-derives card faces from
  // entries, so the newest-member fallback and MeetingCard thumbs all pick them up for free.
  // Patch-by-id is snapshot-safe: a late response whose ids belong to a replaced snapshot
  // matches nothing. ⚠️ thumbsCacheRef holds the URLS per id (with [] = known-no-photos), not
  // a mere requested-flag — entries get REPLACED wholesale (SWR revalidate always overwrites;
  // filter/scope changes refetch), and a flag-only guard would skip the re-request after the
  // replacement wiped the patched images, silently un-photographing every already-seen card.
  // Re-patching from the URL cache is synchronous and terminates: patched entries carry
  // images, so the next effect run finds nothing to patch. inflight is separate so a burst of
  // pageGroups identity changes can't double-request the same ids mid-flight; on failure the
  // ids are released so the next page turn retries.
  const thumbsCacheRef = useRef(new Map())
  const thumbsInflightRef = useRef(new Set())
  useEffect(() => {
    if (SPECIAL_FEED) return
    const cachedPatch = {}
    const want = []
    // ── 🚨 B876: THE SHELF WAS NEVER ASKED FOR (owner, 2026-08-19) ────────────────────────
    // *"why images dont load on front page cards?"* — the possible-matches shelf drew a MAP on
    // every card. Not the no-photo fallback: those three properties hold 16, 120 and 120
    // photos. `agent-listings` is called with `thumbs=0` (2026-07-15, lazy thumbnails), so NO
    // row arrives with images and this effect is the only thing that fills them — and it walked
    // `pageGroups` alone, the matched page. The shelf's ids were never collected, so
    // `listing-thumbs` was never called for them and their `images` stayed empty forever.
    // The give-away that this was an oversight rather than a decision: `applyPatch` below
    // already patches `setListingPossibles`. The write side was built for shelf rows; only the
    // read side forgot to ask.
    // ⚠️ ORDER MATTERS. `fetchPrioritized` fires a small first request for the head of `want`
    // and the rest after it lands, so the matched cards must stay FIRST — the shelf sits below
    // them on screen and must not delay the photos an agent is actually looking at.
    // `nearbyGroups` (the Q4 third tier) rides along for the same reason and by the same rule.
    const thumbGroups = [...hydrateGroups, ...shownPossibleGroups, ...nearbyGroups]
    for (const g of thumbGroups) for (const it of g.items) {
      const l = it.listing
      if (!l?.listing_id || l.images?.length) continue
      const known = thumbsCacheRef.current.get(l.listing_id)
      if (known) { if (known.length) cachedPatch[l.listing_id] = known }
      else if (!thumbsInflightRef.current.has(l.listing_id)) want.push(l.listing_id)
    }
    const applyPatch = map => {
      const patch = e => (map[e.listing_id]?.length && !(e.images?.length)) ? { ...e, images: map[e.listing_id] } : e
      setListingEntries(prev => prev.map(patch))
      setListingPossibles(prev => prev.length ? prev.map(patch) : prev)
    }
    if (Object.keys(cachedPatch).length) applyPatch(cachedPatch)
    if (!want.length) return
    for (const id of want) thumbsInflightRef.current.add(id)
    // Priority split (2026-08-10, owner: "load first image first, then the rest in the
    // background"): `want` covers the whole visible page (~30 cards), and a single
    // getListingThumbs(want) makes every one of their photos appear at the same instant —
    // whenever the SLOWEST id in the batch resolves. fetchPrioritized fires a small first
    // request for the cards actually above the fold, then the rest once that lands.
    fetchPrioritized(want, getListingThumbs, (thumbs, ids) => {
      for (const id of ids) { thumbsCacheRef.current.set(id, thumbs[id] || []); thumbsInflightRef.current.delete(id) }
      applyPatch(thumbs)
    }).catch(e => {
      for (const id of want) thumbsInflightRef.current.delete(id)
      console.error('[listing-thumbs]', e.message)
    })
  }, [hydrateGroups, shownPossibleGroups, nearbyGroups, SPECIAL_FEED])
  // Ad text for the platform-chip hover (2026-07-30 owner request: "hovering a platform pill
  // should show that platform ad's scraped comment — it's already in the modal, having it
  // instantly is a win"). Same lazy, on-screen-only shape as the thumbs effect above and for
  // the same reason: descriptions are up to 4,000 chars server-side, so they are a side-read
  // rather than a widening of agent-listings.
  // The map is keyed by listing_id and passed down to HistoryCards — the chip builder already
  // holds each posting's listing row, so it can attach the right text per PLATFORM.
  // Cached-forever within the session (`seen`), unlike thumbs which re-patch entries: this
  // map lives beside the entries rather than inside them, so a wholesale entry replacement
  // cannot wipe it and there is nothing to re-apply.
  // Property ids of the cards actually ON SCREEN — the current page plus the dormant section
  // when it's open. Coop/view chips fetch for THESE (Phase 2d, 2026-07-15), not every loaded
  // row. property_id, not listing_id — both chips are property-scoped.
  // ── Self-loading feeds report what they render (2026-08-02) ──────────────────────────
  // LeadsFeed owns its own row set — those ids can never appear in pageGroups, so no widening
  // of the memos below can reach them. Rather than give that feed a second copy of the
  // cooperation/restriction fetch (the restriction merge alone is ~30 lines of stance
  // precedence, and a second copy is exactly how these overlays drifted apart per surface in
  // the first place), the feed reports its ids UP and the ONE set of effects covers it.
  //
  // ⚠️ Stored as sorted COMMA-JOINED STRINGS, not arrays. This crosses a render boundary from
  // a child, and a fresh array identity each render would retrigger the memo → the effect →
  // setState → render: the Leads reload loop this file warns about at the <MeetingsFeed> call
  // site, but carrying fetches. Primitive equality makes an unchanged set a genuine no-op.
  //
  // ⚠️ DECLARED HERE, ABOVE the memos that read it — not beside coopPropertyIds where it
  // reads better. `visibleListingIds` names feedIds.l in its DEP ARRAY, and a dep array is
  // evaluated DURING render, so a later declaration is a temporal-dead-zone ReferenceError
  // that takes the whole Desk into its error boundary. That is the identical trap already
  // recorded above adTextByListing, and `vite build` catches neither: the name is declared
  // and in scope, just later. It was re-triggered here on 2026-08-02 and caught in-browser.
  const [feedIds, setFeedIds] = useState({ p: '', l: '' })
  const onFeedVisible = useCallback((propertyIds = [], listingIds = []) => {
    const p = [...new Set(propertyIds.filter(Boolean))].sort().join(',')
    const l = [...new Set(listingIds.filter(Boolean))].sort().join(',')
    setFeedIds(prev => (prev.p === p && prev.l === l) ? prev : { p, l })
  }, [])
  const visiblePropertyIds = useMemo(() => {
    const s = new Set()
    const add = g => { const pid = groupPropertyId(g); if (pid) s.add(pid) }
    for (const g of hydrateGroups) add(g)
    if (showDormant) for (const g of dormantGroups.slice(0, 100)) add(g)
    // Meeting cards show their deal terms on the face too (owner 2026-08-01), and a
    // meeting's property is usually NOT one of the listing cards on this page. Folding
    // the ids in here rather than adding a second fetch keeps one batching mechanism —
    // the effect below already marks fetched-empty, which is what lets a card tell
    // "no cooperation" apart from "not loaded yet" and nudge accordingly.
    // ALL meeting properties, not the thumbs' 60-capped slice (fixed 2026-08-15) — see
    // meetingPropIdsAll's comment: past the cap the terms row silently vanished.
    for (const pid of meetingPropIdsAll) s.add(pid)
    // Front-page Recent-leads, same reason (2026-08-02): those three cards are fetched by
    // their own effect and are not in pageGroups, so without this they showed the 🤝＋
    // "add cooperation" affordance on properties that already HAVE one.
    for (const g of homeLeads) add(g)
    return [...s]
  }, [hydrateGroups, showDormant, dormantGroups, meetingPropIdsAll, homeLeads])
  // Visible LISTING ids — the restrictions overlay keys by listing (listing_policy rows
  // sit on listings, not properties), so it needs its own id set beside the property one.
  // Covers every surface this component renders, for the same reason visiblePropertyIds
  // does: a card that is on screen but absent from this set silently shows no 🚫 chip,
  // which reads as "no restrictions" rather than "never asked".
  const visibleListingIds = useMemo(() => {
    const s = new Set()
    const add = g => { for (const it of (g?.items || [])) if (it.listing?.listing_id) s.add(it.listing.listing_id) }
    for (const g of hydrateGroups) add(g)
    for (const g of homeLeads) add(g)
    for (const lid of Object.values(meetingListingByProp)) if (lid) s.add(lid)
    for (const id of feedIds.l ? feedIds.l.split(',') : []) s.add(id)
    return [...s]
  }, [hydrateGroups, homeLeads, meetingListingByProp, feedIds.l])
  // Cooperations get their OWN id set = the rendered cards plus whatever a self-loading feed
  // reported. Deliberately NOT folded into visiblePropertyIds: that one also drives the 👁
  // views fetch, which LeadsFeed already performs for its own rows — merging them would fire
  // a second identical views request on the busiest surface in the Desk.
  const coopPropertyIds = useMemo(() => {
    const s = new Set(visiblePropertyIds)
    for (const id of feedIds.p ? feedIds.p.split(',') : []) s.add(id)
    return [...s]
  }, [visiblePropertyIds, feedIds.p])
  // Ad-text ids = the listings actually on screen, on EVERY surface this component renders.
  //
  // ⚠️ Origins are deliberately NOT included (owner ruling 2026-08-02: "non-source chips hover
  // shouldn't show comment"). An origin fallback was briefly shipped and reverted the same day:
  // a repost of ours has no owner comment by construction, and borrowing the source ad's words
  // onto our repost's chip attributes them to the wrong posting.
  //
  // What WAS the real bug: three surfaces never ASKED for ad text at all, so their chips
  // hovered blank whether or not the ad had a comment. That is what this memo fixes.
  // ⚠️ Deliberately its own memo rather than widening `visibleListingIds`: that set also feeds
  // the restrictions overlay, where a policy belongs to the listing being SHOWN — and that
  // endpoint is capped at 80 ids per call.
  // ⚠️ homeLeads is folded in EXPLICITLY (2026-08-02, owner-caught on a real screen). The
  // front page's Recent-leads section fetches its own rows and patches THUMBS only — those
  // three cards are not in `pageGroups`, so an ids set built from pageGroups alone left the
  // first thing an agent sees hovering blank. Measured on the home page: 11 platform chips,
  // 3 with hover text — and the 3 were exactly the Recently-added-properties cards, which
  // ride `activeGroups` and therefore were covered. That is the third surface to lose this
  // side-read the same way (Leads and Meeting were the first two), which is why ad text is
  // gathered HERE for every surface this component renders rather than beside each loader.
  const visibleAdTextIds = useMemo(() => {
    const s = new Set()
    const add = g => { for (const it of (g?.items || [])) {
      if (it.listing?.listing_id) s.add(it.listing.listing_id)
    } }
    for (const g of hydrateGroups) add(g)
    for (const g of homeLeads) add(g)
    // MEETING cards are the fourth surface (2026-08-02). MeetingsFeed builds each card's face
    // from `thumbsByProp`, whose listing ids IntakeTab already resolved into
    // meetingListingByProp — so no new plumbing, just ask for their text too. A meeting whose
    // property has no loaded listing renders a snapshot-built pseudo-listing with listing_id
    // null; that one legitimately has no ad text to fetch and drops out below.
    for (const lid of Object.values(meetingListingByProp)) if (lid) s.add(lid)
    return [...s]
  }, [hydrateGroups, homeLeads, meetingListingByProp])
  // ⚠️ MOVED HERE 2026-07-30 (was 30 lines ABOVE `visibleListingIds`). A useEffect body runs
  // after render, so the closure would have been fine — but the DEPENDENCY ARRAY is evaluated
  // DURING render, and reading a `const` before its declaration is a temporal-dead-zone
  // ReferenceError. It crashed the whole Desk tab into its error boundary.
  // ⚠️ Neither `vite build` nor the JSX import audit can catch this: the name IS declared and IS
  // in scope, just later. Anything reading a `const` from a hook dep array must sit below it.
  const [adTextByListing, setAdTextByListing] = useState({})
  const adTextSeenRef = useRef(new Set())
  useEffect(() => {
    // ⚠️ NO SPECIAL_FEED guard, unlike the thumbs effect above. SPECIAL_CATEGORIES includes
    // 'Leads', and copying that guard here is why the chips hovered blank on the busiest
    // surface in the Desk. Leads has its own loader (LeadsFeed fetches descriptions beside
    // its thumbs and provides them itself); the remaining special categories render through
    // pageGroups, so letting the effect run covers them. When there is nothing on screen
    // visibleAdTextIds is empty and this no-ops — the guard bought nothing and cost a feature.
    const want = visibleAdTextIds.filter(id => !adTextSeenRef.current.has(id))
    if (!want.length) return
    for (const id of want) adTextSeenRef.current.add(id)
    const chunks = []
    for (let i = 0; i < want.length; i += 80) chunks.push(want.slice(i, i + 80))
    Promise.all(chunks.map(c => getListingDescriptions(c))).then(batches => {
      const m = {}
      // null marks "asked, nothing there" so a card with no ad text is not re-requested
      // on every page turn — the same fetched-empty discipline the restrictions overlay uses.
      for (const id of want) m[id] = null
      for (const b of batches) for (const [id, v] of Object.entries(b)) m[id] = v
      setAdTextByListing(prev => ({ ...prev, ...m }))
    }).catch(e => {
      for (const id of want) adTextSeenRef.current.delete(id)
      console.error('[listing-descriptions]', e.message)
    })
  }, [visibleAdTextIds])
  // Cooperation chips (🤝). Accumulates across page turns (a property that scrolls off keeps its
  // chips — harmless, and avoids a refetch on page-back); coopSeenRef guards re-request. No
  // scope/agent reset needed — a cooperation belongs to a property regardless of who's looking,
  // so a stale entry is only ever unused, never wrong. reloadDeskCoops (the modal's post-save
  // refresh) force-refetches the visible ids so a just-saved coop shows immediately.
  // WHICH CARD a cooperation belongs on. `for_property_ids` is the server's answer (the
  // requested ids this row's FLAT covers — a cooperation opened against a same_as sibling is
  // the same deal, and 1,222 property rows were showing "terms not recorded" over one).
  // Falls back to the row's own id against a server that predates the field, which is exactly
  // the old behaviour — so this can be deployed in either order.
  const coopKeysFor = c => (c.for_property_ids?.length ? c.for_property_ids : [c.property_id])
  function reloadDeskCoops() {
    const ids = visiblePropertyIds
    if (!ids.length) return
    for (const pid of ids) coopSeenRef.current.add(pid)
    listCooperations({ propertyId: ids }).then(rows => {
      setCoopsByProperty(prev => {
        const m = { ...prev }
        for (const pid of ids) m[pid] = []
        for (const c of rows) for (const pid of coopKeysFor(c)) (m[pid] = m[pid] || []).push(c)
        return m
      })
    }).catch(e => { for (const pid of ids) coopSeenRef.current.delete(pid); console.error('[cooperations]', e.message) })
  }
  useEffect(() => {
    // ⚠️ NO SPECIAL_FEED guard (removed 2026-08-02) — same correction the ad-text effect
    // already carries above, for the same reason. SPECIAL_CATEGORIES includes Meeting and
    // Leads, and the guard meant a meeting card or a front-page lead whose property HAS a
    // cooperation rendered the 🤝＋ "add" affordance instead of its chip: unknown shown as
    // "none". The id sets are empty when nothing is on screen, so the guard bought nothing.
    // (LeadsFeed still needs its OWN fetch — its rows never enter visiblePropertyIds.)
    const want = coopPropertyIds.filter(pid => !coopSeenRef.current.has(pid))
    if (!want.length) return
    for (const pid of want) coopSeenRef.current.add(pid)
    listCooperations({ propertyId: want }).then(rows => {
      setCoopsByProperty(prev => {
        const m = { ...prev }
        for (const pid of want) if (!m[pid]) m[pid] = []   // mark fetched-empty so absence is deterministic
        for (const c of rows) for (const pid of coopKeysFor(c)) (m[pid] = m[pid] || []).push(c)
        return m
      })
    }).catch(e => { for (const pid of want) coopSeenRef.current.delete(pid); console.error('[cooperations]', e.message) })
    // Owner-stated offers ride the SAME want set, their own call — a failure here never
    // suppresses the cooperation chips (and vice versa). Fire-and-forget: an offer that
    // arrives late just pops the chip in on the next render.
    listCoopOffers(want).then(map => {
      setCoopOffersByProperty(prev => ({ ...prev, ...map }))
    }).catch(e => console.error('[coop-offers]', e.message))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coopPropertyIds])
  // 🔗 client-link chips. Rides the SAME id set as the cooperation chips (both are
  // property-scoped card-face facts fetched for the visible page) with its OWN seen-guard, so
  // a failure on one never suppresses the other. reloadShares re-pulls after a mint, so a
  // freshly created link turns the dashed door solid without a page turn.
  function reloadShares(ids) {
    const want = [...new Set((ids || []).filter(Boolean))]
    if (!want.length) return
    for (const pid of want) shareSeenRef.current.add(pid)
    getPropertyShares({ propertyId: want }).then(rows => {
      setSharesByProperty(prev => {
        const m = { ...prev }
        for (const pid of want) m[pid] = []   // fetched-empty, so absence is deterministic
        // review 2026-09-23: index by `for_property_ids` when the server publishes it (the
        // cluster-expanded answer, same contract as /cooperations — coopKeysFor above), so a link
        // minted against a same_as sibling shows on this flat's card. Falls back to the row's own
        // property_id against a server that predates the field; HistoryCards dedupes by share id.
        for (const r of rows) for (const pid of coopKeysFor(r)) (m[pid] = m[pid] || []).push(r)
        return m
      })
    }).catch(e => { for (const pid of want) shareSeenRef.current.delete(pid); console.error('[property-shares]', e.message) })
  }
  useEffect(() => {
    reloadShares(coopPropertyIds.filter(pid => !shareSeenRef.current.has(pid)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coopPropertyIds])
  // 🔁 reshare-pool marker (owner 2026-10-27): the same visible id set, its own seen-guard, into the shared store the card
  // body reads (desk/reshareStatus.js) - a failure here leaves every other chip alone.
  // ⚠️ Unlike the cooperation chips this state MOVES within minutes (a queued flat is posted, a repost lands), so the seen
  // guard is a fetch TIME, not a set: a flat on screen is asked again once its answer is 5 minutes old - and a 5-minute
  // tick re-runs the check while the page just sits there.
  const [reshareTick, setReshareTick] = useState(0)
  useEffect(() => { const t = setInterval(() => setReshareTick(x => x + 1), 5 * 60e3); return () => clearInterval(t) }, [])
  useEffect(() => {
    const now = Date.now()
    const want = coopPropertyIds.filter(pid => !(now - (reshareSeenRef.current.get(pid) || 0) < 5 * 60e3))
    if (!want.length) return
    for (const pid of want) reshareSeenRef.current.set(pid, now)
    getReshareStatus(want).then(rows => mergeReshareStatus(want, rows))
      .catch(e => { for (const pid of want) reshareSeenRef.current.delete(pid); console.error('[reshare-status]', e.message) })
  }, [coopPropertyIds, reshareTick])
  // ⚠️ Defect chips ride the SAME id set as the cooperation and 🔗 chips — all three are
  // property-scoped card-face facts for the visible page — with their own seen-guard, so one
  // failing leaves the other two alone. No scope/agent reset: a defect belongs to the flat
  // regardless of who is looking, so a kept entry is only ever unused, never wrong.
  useEffect(() => {
    const want = coopPropertyIds.filter(pid => !defectSeenRef.current.has(pid))
    if (!want.length) return
    for (const pid of want) defectSeenRef.current.add(pid)
    getPropertyChips(want, { group: 'defect' }).then(map => {
      setDefectsByProperty(prev => {
        const m = { ...prev }
        for (const pid of want) m[pid] = map[pid] || []   // fetched-empty, so absence is deterministic
        return m
      })
    }).catch(e => { for (const pid of want) defectSeenRef.current.delete(pid); console.error('[property-chips]', e.message) })
    // Its own call, deliberately: a failure on one group must not blank the other, and the
    // two answer different questions (what is WRONG with the flat / what did an agent add
    // that the advert never said). Fire-and-forget — a late answer just pops the chips in.
    // ⚠️ It does NOT share defectSeenRef's rollback: this map is allowed to arrive late or
    // not at all without making the defects refetch on the next render.
    getPropertyChips(want, { group: 'agent_filled' }).then(map => {
      setAgentFactsByProperty(prev => {
        const m = { ...prev }
        for (const pid of want) m[pid] = map[pid] || []
        return m
      })
    }).catch(e => console.error('[property-chips agent_filled]', e.message))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coopPropertyIds])
  // 0894: the distinctiveness note rides the SAME id set, with its own seen-guard (rolled back on
  // failure so the next render retries). The note is a projection the DB refreshes on a cron;
  // a card asks once per page and never recomputes anything.
  useEffect(() => {
    const want = coopPropertyIds.filter(pid => !distinctSeenRef.current.has(pid))
    if (!want.length) return
    for (const pid of want) distinctSeenRef.current.add(pid)
    getPropertyDistinctiveness(want).then(map => {
      setDistinctByProperty(prev => {
        const m = { ...prev }
        for (const pid of want) m[pid] = map[pid] || []
        return m
      })
    }).catch(e => { for (const pid of want) distinctSeenRef.current.delete(pid); console.error('[property-distinctiveness]', e.message) })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coopPropertyIds])
  useEffect(() => {
    const want = coopPropertyIds.filter(pid => !devCardSeenRef.current.has(pid))
    if (!want.length) return
    for (const pid of want) devCardSeenRef.current.add(pid)
    getDevelopmentCards(want).then(map => {
      if (!Object.keys(map).length) return
      setDevCardsByProperty(prev => ({ ...prev, ...map }))
    }).catch(e => { for (const pid of want) devCardSeenRef.current.delete(pid); console.error('[development-cards]', e.message) })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coopPropertyIds])
  useEffect(() => {
    const want = coopPropertyIds.filter(pid => !sellerSeenRef.current.has(pid))
    if (!want.length) return
    for (const pid of want) sellerSeenRef.current.add(pid)
    getSellersByProperty(want).then(map => {
      if (!Object.keys(map).length) return
      setSellersByProperty(prev => ({ ...prev, ...map }))
    }).catch(e => { for (const pid of want) sellerSeenRef.current.delete(pid); console.error('[sellers]', e.message) })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coopPropertyIds])
  // 🚫 restrictions chip fuel (action-row fill-state spec, 2026-07-23) — same
  // visible-page overlay pattern as cooperations above: fetch listing_policy for the
  // LISTINGS on this page via the existing terms-batch endpoint (≤80 ids per call —
  // its hard cap), keep only restricting stances (not_allowed / only), accumulate
  // across pages. Client-side per-page fetch deliberately, NOT a shapeListingRow field:
  // the payload stays trimmed and the extraRows derived-field trap can't apply (we ask
  // for exactly the rows being rendered).
  useEffect(() => {
    // ⚠️ NO SPECIAL_FEED guard (removed 2026-08-02) — see the cooperations effect above.
    // B177 widening: while an overlay-fact FILTER is active (restriction exclude / payment),
    // the classify needs the fact for EVERY loaded row, not just the visible page — the
    // visible page is itself the OUTPUT of that filtering (a chip-only overlay here would be
    // circular and would let known-bad rows sit on later pages). Same seen-ref, same 80-cap
    // batches; the cost is a few extra calls the moment the filter is first switched on.
    const overlayFilterActive = (draftAdvFilters.restrictions || []).length > 0
      || draftAdvFilters.advanceMaxMonths !== ''
    const source = overlayFilterActive
      ? [...new Set([...visibleListingIds, ...listingEntries.map(l => l.listing_id).filter(Boolean)])]
      : visibleListingIds
    const want = source.filter(id => !restrSeenRef.current.has(id))
    if (!want.length) return
    for (const id of want) restrSeenRef.current.add(id)
    const chunks = []
    for (let i = 0; i < want.length; i += 80) chunks.push(want.slice(i, i + 80))
    Promise.all(chunks.map(c => getListingTermsBatch(c))).then(batches => {
      // 0748 made the three legacy listing_terms booleans evidence for the SAME
      // restriction subjects used by listing_policy. The batch already returns both stores,
      // but the card projection used only `policies`, so a saved `pets_allowed = false`
      // affected matching/search while remaining invisible on the card. Project those terms
      // into the existing subject shape; a real policy row wins because it is the current
      // editor for these questions (ManualPropertyModal retired the duplicate boolean inputs).
      const policies = batches.flatMap(b => b.policies || [])
      const policyKeys = new Set(policies.map(p => `${p.listing_id}:${p.subject}`))
      const termSubjects = [
        ['pets_allowed', 'pol_animals'],
        ['smoking_allowed', 'pol_smoker'],
        ['children_allowed', 'pol_children'],
      ]
      for (const b of batches) for (const row of (b.terms || [])) {
        if (!row?.listing_id) continue
        for (const [field, subject] of termSubjects) {
          if (row[field] == null || policyKeys.has(`${row.listing_id}:${subject}`)) continue
          policies.push({ listing_id: row.listing_id, subject,
            stance: row[field] ? 'allowed' : 'not_allowed', source_kind: row.source_kind })
        }
      }
      const m = {}
      for (const id of want) m[id] = null   // fetched-empty marker: absence is deterministic
      for (const p of policies) {
        if (p.stance !== 'not_allowed' && p.stance !== 'only') continue
        const e = (m[p.listing_id] ||= { subjects: [] })
        const src = (p.source_kind ?? 'agent') === 'agent' ? 'agent' : 'scrape'
        const prev = e.subjects.find(s => s.code === p.subject)
        // Agent-recorded wins over scrape for the same subject (matches the modal's
        // override rule); otherwise first record of a subject wins.
        if (!prev) e.subjects.push({ code: p.subject, stance: p.stance, src })
        else if (prev.src === 'scrape' && src === 'agent') { prev.stance = p.stance; prev.src = 'agent' }
      }
      setRestrByListing(prev => ({ ...prev, ...m }))
      // Positive half — same rows, opposite stances, its own map (see the state comment).
      const ma = {}
      for (const id of want) ma[id] = null
      for (const pol of policies) {
        if (pol.stance !== 'allowed' && pol.stance !== 'preferred') continue
        const e = (ma[pol.listing_id] ||= { subjects: [] })
        const src = (pol.source_kind ?? 'agent') === 'agent' ? 'agent' : 'scrape'
        const prev = e.subjects.find(x => x.code === pol.subject)
        if (!prev) e.subjects.push({ code: pol.subject, stance: pol.stance, src })
        else if (prev.src === 'scrape' && src === 'agent') { prev.stance = pol.stance; prev.src = 'agent' }
      }
      setAllowByListing(prev => ({ ...prev, ...ma }))
      // B237 third state. Same fetched-empty marker contract as the two maps above, so
      // "nobody answered" stays deterministic rather than "not loaded yet". `hidden.clears`
      // carries the earned-knowledge stubs — someone DID answer, this viewer just hasn't
      // earned the value, which the card renders differently from unasked.
      const mc = {}
      for (const id of want) mc[id] = null
      for (const b of batches) {
        for (const c of (b.clears || [])) if (c?.listing_id) mc[c.listing_id] = { cleared: true }
        for (const h of (b.hidden?.clears || [])) if (h?.listing_id && !mc[h.listing_id]) mc[h.listing_id] = { cleared: true, hidden: true }
      }
      setClearByListing(prev => ({ ...prev, ...mc }))
      // The SAME batch already carries listing_terms rows — the payment filters (B177)
      // read them from this second map. Only the filter-relevant fields are kept; a null
      // entry is the same fetched-empty marker as above.
      const mt = {}
      for (const id of want) mt[id] = null
      for (const b of batches) for (const trow of (b.terms || [])) {
        if (!trow?.listing_id) continue
        mt[trow.listing_id] = {
          deposit_amount: trow.deposit_amount ?? null,
          deposit_currency: trow.deposit_currency ?? null,
          prepay_first_month: trow.prepay_first_month ?? null,
          prepay_last_month: trow.prepay_last_month ?? null,
          // 2026-09-12: the card renders this now, so the slice has to carry what the
          // card shows. It kept four fields while listing_terms grew three more, and a
          // „დეპოზიტი: ნახევარი თვე" listing arrived here as four nulls — the fact was
          // fetched, dropped in this object, and the card correctly said nothing.
          deposit_required: trow.deposit_required ?? null,
          deposit_basis: trow.deposit_basis ?? null,
          deposit_months: trow.deposit_months ?? null,
          min_lease_months: trow.min_lease_months ?? null,
          // Provenance for the ✦ mark — a term Idmon read out of the advert says so on the
          // card, the same way a feature does (1082).
          source_kind: trow.source_kind ?? null,
          source_agent: trow.source_agent ?? null,
          source_version: trow.source_version ?? null,
          recorded_at: trow.recorded_at ?? null,
        }
      }
      setTermsByListing(prev => ({ ...prev, ...mt }))
    }).catch(e => { for (const id of want) restrSeenRef.current.delete(id); console.error('[restrictions]', e.message) })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleListingIds, draftAdvFilters.restrictions, draftAdvFilters.advanceMaxMonths, listingEntries])
  // 👁 who-saw-my-listing chips — same visible-page scoping. Server is the gate (rep-only;
  // non-represented ids come back omitted), so asking for visible ids is always safe.
  // Failure = no chips, never a broken feed.
  useEffect(() => {
    // ⚠️ NO SPECIAL_FEED guard (removed 2026-08-02) — see the cooperations effect above.
    const want = visiblePropertyIds.filter(pid => !viewSeenRef.current.has(pid))
    if (!want.length) return
    for (const pid of want) viewSeenRef.current.add(pid)
    getListingViews(want).then(m => setViewsByProperty(prev => ({ ...prev, ...Object.fromEntries(m) })))
      .catch(e => { for (const pid of want) viewSeenRef.current.delete(pid); console.error('[listing-views]', e.message) })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visiblePropertyIds])
  // Chain completion (unfiltered): the loaded set now equals server truth for this key, so
  // (1) purge rows the merged cached tail carried that NO fetch this generation returned —
  // they no longer exist server-side; (2) cache the FULL snapshot, so the next refresh
  // paints the complete feed instead of chunk 1 re-growing before the agent's eyes.
  // Stamp excludes entries.length so the prune's own setListingEntries doesn't re-write.
  const cacheWrittenRef = useRef(null)
  useEffect(() => {
    if (SPECIAL_FEED || listingLoadingMore || !cursorFresh) return
    if (advFiltersActive(advFiltersDebounced)) return
    if (listingTotal <= 0 || listingRawCount < listingTotal) return
    const snapKey = `${listingsScope}:${myAgentId || ''}:${entryPeriod}:${entrySort}:${entryStatusFilter}`   // must match the fetch site's key exactly
    if (snapshotKeyRef.current !== snapKey) return
    const stamp = `${snapKey}:${listingRawCount}:${listingTotal}`
    if (cacheWrittenRef.current === stamp) return
    cacheWrittenRef.current = stamp
    const pruned = listingEntries.filter(l => fetchedIdsRef.current.has(l.listing_id))
    if (pruned.length !== listingEntries.length) setListingEntries(pruned)
    // 2026-09-24: into the PAGE-0 query-cache entry, not `al:`. Nothing has read `al:` since the
    // B1214 move to queryCache — this write only spent localStorage quota that the protected
    // count caches share. Stored under the key page 0 is READ from, the complete book paints on
    // the next visit and `hadComplete` in the fetch effect merges the fresh page over it, which
    // is what that branch was always written for.
    // ⚠️ `fetchedAt` is page 0's ORIGINAL fetch time, never now: the chain landing late must not
    // extend how long the cached page counts as fresh. searchMeta is page 0's own
    // (page0MetaRef), so a fresh land from this entry restores exactly what that answer carried.
    const p0 = page0KeyRef.current
    if (p0 && !p0.filtered && p0.snapKey === snapKey && page0FetchedAtRef.current) writeQuery(p0.key, {
      listings: pruned, total: listingTotal, rowsReturned: listingRawCount,
      countUnit: listingCountUnit, unknownExcluded: serverUnknown, searchMeta: page0MetaRef.current, cacheTtlMs: page0TtlRef.current,
    }, { fetchedAt: page0FetchedAtRef.current }).catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [SPECIAL_FEED, listingLoadingMore, cursorFresh, listingRawCount, listingTotal, listingEntries, advFiltersDebounced, listingsScope, myAgentId, entryPeriod, entryStatusFilter])
  // Cities on offer for the filter dropdown — UNION of the known city vocabulary (district
  // table rows that ARE cities: ბათუმი, ფოთი, ქუთაისი…) and whatever appears in loaded
  // listing addresses. Loaded-data-only meant a city with no listing on the current page
  // simply didn't exist in the dropdown, and "ოზურგეთი" + "ოზურგეთის მუნიციპალიტეტი"
  // rendered as two cities. Deduped by cityKey, shortest label wins, თბილისი pinned first.
  const availableCities = useMemo(() => {
    const byKey = new Map()   // cityKey -> shortest seen label
    const offer = label => {
      if (!label) return
      const k = cityKey(label)
      if (!k) return
      const cur = byKey.get(k)
      if (!cur || label.length < cur.length) byKey.set(k, label)
    }
    offer('თბილისი')
    // Only CITY-LEVEL rows (MUN_* municipalities, TWN_* towns) join the city vocabulary.
    // ⚠️ Don't test the `city` column alone: since the 0067 backfill (2026-07-07) Batumi/
    // Kutaisi/Rustavi NEIGHBORHOODS (BAT_/KUT_/RUS_) and REG_ regions carry real city values
    // too — a bare `city !== 'Tbilisi'` test offered every one of them ("Aqua", "აეროპორტი",
    // whole regions) as a "city" and flooded this dropdown.
    for (const d of districts) if (/^(MUN_|TWN_)/.test(d.code) && d.city && d.city !== 'Tbilisi') offer(d.name_ka || d.name_en)
    // Addresses missing the "თბილისი," prefix split to whatever comes first instead — often a
    // Tbilisi street/avenue name (e.g. "ვაჟა-ფშაველა"), not a city. The district table above
    // already carries the full known-city vocabulary, so an address-derived label only gets
    // offered as a NEW city if it matches one already known — anything else is street noise,
    // not a real unseen city, and gets dropped rather than polluting the dropdown.
    const knownCityKeys = new Set(byKey.keys())
    for (const l of listingEntries) {
      const { city } = splitCityFromAddress(l.address)
      if (city && knownCityKeys.has(cityKey(city))) offer(city)
    }
    const tbilisiKey = cityKey('თბილისი')
    return [...byKey.entries()]
      .sort(([ka, a], [kb, b]) => (ka === tbilisiKey ? -1 : kb === tbilisiKey ? 1 : a.localeCompare(b)))
      .map(([, label]) => label)
  }, [listingEntries, districts])

  // Gray-out fuel for the strip's City/District dropdowns (core migration 0056). Lives here
  // (not in the search-tools sidebar) since 2026-07-06's restructure moved both selects to the
  // command strip. Only active once counts actually loaded — an empty map would roll up to
  // reachable=0 everywhere and disable the whole dropdown before the fetch lands.
  // Under the agent filter, swap the scope-wide server counts for the filtered tally
  // (2026-07-23) — same rule as kindCountsForTree; graying stability stands otherwise.
  // ── B273 move 4 (2026-08-02): the numbers come from the SEARCH, not from a second counter ──
  // Owner ruling: "There must be exactly one definition of 'how many properties match this
  // filter.' The search and the displayed counts should both come from that same implementation."
  // `search_meta.district_counts` is emitted by the same `counted` CTE that produces the rows
  // (0364) and, since 0369, by the materialised page too — so the facet equals the click by
  // construction rather than by two implementations agreeing. Verified live: Bagebi 180 = 180,
  // the exact number that opened B273 reading 68.
  //
  // Chosen by the SAME rule as facetCounts above — the view that owns the surface answers —
  // so the district tree and the Deal/Type/Source facets can never disagree about which query
  // they describe.
  //
  // ⚠️ THESE MOVE WITH THE FILTERS, and 0056's did not. That is the point (a count that cannot
  // survive being clicked is the bug), but it is a real behaviour change for the gray-out: a
  // district greys when it has nothing UNDER THE CURRENT FILTERS, rather than nothing at all.
  // That is the honest reading of "can I click this and get results".
  //
  // ⚠️ THE 0056 FALLBACK STAYS, and must, for two distinct reasons — do not delete it:
  //   1. FIRST PAINT. Until page 0 answers, the honest counts are null, and a tree with no
  //      numbers is not neutral: `byCount` ties every district at 0 and the sort falls through
  //      to alphabetical, which pushed BOTH Saburtalo and Vake off the visible top-8 behind
  //      "See all (79)" when this was tried on 2026-08-02. A wrong number beside Saburtalo
  //      still lets an agent click Saburtalo; an alphabetical top-8 hides it.
  //   2. THE PROPERTIES BARE PAGE HAS NO HONEST COUNTS YET. 0369 taught
  //      `lead_listing_cluster_page_v3` to tally districts; its sibling
  //      `agent_listing_cluster_page_v3` was not touched, so the unfiltered Properties view
  //      still has only 0056 to read. Retiring 0056 entirely needs that second migration
  //      (B273 move 4b) — until then this line is the seam between an honest number and the
  //      old one, and it is deliberate.
  //
  // ⚠️ EXPANDED vs SUMMED — the two sources are not interchangeable and the flag below is what
  // keeps them apart. 0370's counts already include a code's children (distinct clusters, the
  // filter's own one-level membership test); 0056's are per exact code and must still be summed
  // up the tree. Feeding one to the other's reader is a silent double count — it is precisely
  // how Vake came to read 1,296 against a click of 1,292.
  const honestDistrictCounts = entryTypeFilter[0] === 'Leads' ? leadFacetCounts.districts : serverDistrictCounts
  // Same chooser, same reason: the view that owns the surface answers. Feeds the own row's
  // number and its checkbox (0372/0363).
  const honestOwnCounts = entryTypeFilter[0] === 'Leads' ? leadFacetCounts.districtsOwn : serverDistrictOwnCounts
  // Streets: the same chooser again, and there is no 0056-style fallback to fall back TO —
  // the search is the only thing that has ever counted streets, so an absent map means "not
  // asked yet" and the facet simply does not render. No number is better than a wrong one.
  const honestStreetCounts = entryTypeFilter[0] === 'Leads' ? leadFacetCounts.streets : serverStreetCounts
  useEffect(() => {
    if (!honestStreetCounts) return
    setStreetLabels(prev => {
      let changed = false
      const next = { ...prev }
      for (const [id, v] of Object.entries(honestStreetCounts)) {
        const label = v && typeof v === 'object' ? v.label : null
        if (label && next[id] !== label) { next[id] = label; changed = true }
      }
      return changed ? next : prev      // returning prev keeps the effect from re-rendering forever
    })
  }, [honestStreetCounts])
  // B273 move 4c: cache the honest answer so the NEXT first paint has a true number to sort by.
  // Keyed exactly as it is read (see dcKey above) — leads and properties count different piles
  // and must never inherit each other's numbers. That confusion is half of what made 0056 wrong:
  // it counted our authored listings and was rendered over the leads feed.
  // review 2026-09-23: dc2: is keyed WITHOUT filters, so only an unfiltered answer may be filed
  // under it (same honesty condition as the fc:/prewarm path; on Leads also the default lens and
  // active pile, which the key does not carry either). The run where dcKey itself just changed
  // is skipped — its closure still holds the previous view's map.
  const dc2WriteKeyRef = useRef(dcKey)
  useEffect(() => {
    if (dc2WriteKeyRef.current !== dcKey) { dc2WriteKeyRef.current = dcKey; return }
    if (serverAdvFilters || committedQ || landedSearch.trim()) return
    if (entryTypeFilter[0] === 'Leads' && (leadArchive || leadWorked)) return
    if (honestDistrictCounts && Object.keys(honestDistrictCounts).length) writeDeskCache(dcKey, honestDistrictCounts)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dcKey, honestDistrictCounts])
  // Cached counts carry 0370's EXPANDED semantics, exactly like the live ones they stand in for,
  // so the flag stays true for them — otherwise districtReach re-sums a parent that already
  // contains its children.
  // 2026-10-22: dc2 is the UNFILTERED answer (keyed without filters). With a city picked the server nulls
  // district_counts, and reading dc2 then drew unfiltered numbers beside a filtered search. It may stand in
  // only while the question is dc2's own (district/city keys aside — the facet's own); otherwise no number.
  const districtSeedOk = districtSeedAllowed(serverAdvFilters, {
    committedText: !!(committedQ || landedSearch.trim()),
    leadsLens: entryTypeFilter[0] === 'Leads' && !!(leadArchive || leadWorked),
  })
  const seedDistrictCounts = districtSeedOk ? districtCounts : NO_COUNTS
  // `!clientOnlyCut` (2026-10-22): a server-sent agent pick yields EXPANDED server counts, which must not be re-summed.
  const usingHonestCounts = !clientOnlyCut && !!(honestDistrictCounts || Object.keys(seedDistrictCounts).length)
  const geoDistrictCounts = clientOnlyCut ? filteredDistrictCounts : (honestDistrictCounts || seedDistrictCounts)
  const hasDistrictCounts = Object.keys(geoDistrictCounts).length > 0
  const { exactOf: distExactOf, reachableOf: distReachableOf } = useMemo(
    () => districtReach(districts, geoDistrictCounts, { expanded: usingHonestCounts }),
    [districts, geoDistrictCounts, usingHonestCounts])
  // City dropdown totals (2026-10-22, deskCounts.js cityTotalsFrom). The old Σ-over-every-code was right for
  // 0056 (one code per listing) and ~2× on EXPANDED counts, where a cluster sits at its code and every
  // ancestor ("Tbilisi (6406)" beside "3,293 match"). Expanded maps sum the city's top codes only; the
  // selected city takes the landed search total itself when no district is picked inside it.
  const selectedCityTotal = (entryTypeFilter[0] !== 'Leads' && !clientOnlyCut && entryCityFilter !== 'all' && serverAdvFilters
    && listingTotalLanded && !districtFilterActive(advFiltersDebounced)) ? listingTotal : null
  const cityTotals = useMemo(() => (hasDistrictCounts
    ? cityTotalsFrom({ cities: availableCities, districts, counts: geoDistrictCounts, expanded: usingHonestCounts,
        inCity: (d, c) => districtInCity(d, c, districts), selectedCity: entryCityFilter, selectedTotal: selectedCityTotal,
        // core 3836: the server's distinct per-city tally answers the same question as geoDistrictCounts only off Leads
        // (parked there) and off a client-only cut (whose numbers are the loaded rows, not the server's universe)
        cityCounts: entryTypeFilter[0] !== 'Leads' && !clientOnlyCut ? serverCityCounts : null, cityOf: c => cityValue(c, districts) })
    : new Map()), [availableCities, districts, geoDistrictCounts, hasDistrictCounts, usingHonestCounts, entryCityFilter, selectedCityTotal, entryTypeFilter, clientOnlyCut, serverCityCounts])

  // Dropdown order (owner ruling 2026-07-27): a city you actually have listings in outranks
  // one that merely sorts earlier — the volume IS the relevance here. Alphabetical put
  // ბაკურიანი (14) and ბათუმი (11) behind a run of empty municipalities, so reaching a city
  // you work in meant scrolling past ones you don't. Count descending, then alphabetical
  // among ties — which is every zero-count city, so the vain tail keeps its old shape.
  // ⚠️ Until the counts land, hasDistrictCounts is false and EVERY city ties at 0; sorting on
  // that would be pure alphabetical and drop Tbilisi into the middle of the list for the first
  // paint, then jump it to the top. So the countless case keeps the old Tbilisi-pinned order.
  // ⚠️ Ties break on the DISPLAY label, not the stored one. The value is always the Georgian
  // label (see cityDisplayLabel) — sorting on it under ENG produced a list ordered by the
  // Georgian alphabet while showing English words, so "Gardabani, Dusheti, Kobuleti" read as
  // unsorted to anyone not reading the hidden გ/დ/ქ behind them.
  const citiesByVolume = useMemo(() => {
    const tbilisiKey = cityKey('თბილისი')
    const byLabel = (a, b) => cityDisplayLabel(a).localeCompare(cityDisplayLabel(b))
    return [...availableCities].sort((a, b) => {
      if (!hasDistrictCounts) {
        if (cityKey(a) === tbilisiKey) return -1
        if (cityKey(b) === tbilisiKey) return 1
        return byLabel(a, b)
      }
      return (cityTotals.get(b) || 0) - (cityTotals.get(a) || 0) || byLabel(a, b)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- cityDisplayLabel is rebuilt every
    // render; `lang` + `districts` are its only real inputs and both are listed.
  }, [availableCities, cityTotals, hasDistrictCounts, lang, districts])

  // Geo bundle for AdvancedSearchPanel — hoisted (2026-07-18) because TWO instances
  // consume it now: the sidebar/⚙-page instance and the strip pills' sheetOnly instance.
  const panelGeo = {
    city: entryCityFilter,
    // District belongs to the OLD city? Clear it — otherwise a hidden filter from
    // another city silently empties the grid (e.g. Vake carried into Rustavi).
    // All four arrays (2026-10-22): a Tbilisi "✕ Bagebi" rode into Batumi as district_not.
    onCity: city => {
      const inNew = code => {
        const d = districts.find(x => x.code === code)
        return !!d && districtInCity(d, city, districts)
      }
      const patch = pruneDistrictsToCity(advFilters, inNew)
      // ⚠️ Audit 2026-10-29 D2: districts were the only thing pruned, so a Tbilisi street / metro station / nearby
      // point stayed in the request after a switch to Zugdidi. "All cities" and the same city move nothing.
      const place = city === 'all' || city === entryCityFilter ? null : pruneLocationToCity(advFilters, {
        metroInCity: cityKey(city) === cityKey('თბილისი'),
        nearInCity: near => {
          const loc = (geoLocations || []).find(l => l.id === near.id)
          return !!loc?.district && inNew(loc.district)
        },
      })
      if (patch || place) setAdvFilters(f => ({ ...f, ...patch, ...place }))
      setEntryCityFilter(city)
    },
    availableCities: citiesByVolume, cityTotals, hasDistrictCounts, districts, distExactOf, distReachableOf,
    // The gazetteer, for the near-radius facet: it needs the picked point's TYPE to name which
    // distance was the default. ⚠️ AdvancedSearchPanel is a separate component and cannot see
    // IntakeTab's state — reading `geoLocations` from inside it throws a ReferenceError that
    // takes the whole Desk to its error boundary, which is what the StreetFacetList comment
    // above records happening to `districtDisplayLabel`. Passing a reference costs nothing.
    locations: geoLocations,
    // RAW per-code own counts. `distExactOf` already rolls children up (own + children's own),
    // so it cannot answer "how many sit at the raion itself" — which is the number that makes
    // a parent's total add up in front of the agent. See the remainder row in DistrictFacetList.
    // ⚠️ The OWN map, never the expanded one (0372). `geoDistrictCounts` includes a code's
    // children since 0370, so feeding it here made the "parent under itself" row print the
    // raion's WHOLE total as its remainder — Didube reading ~1,316 where it means ~544. Nothing
    // failed; the children just looked like they added up to nothing.
    // ⚠️ NULL means "not counted for this query", and {} would mean "counted, all zero" —
    // the difference is load-bearing and used to be flattened here. The RPC stops emitting
    // district_own_counts the moment ANY district filter is active (it declines to count a
    // facet the query has already narrowed), so `|| {}` turned "unknown" into "zero" and the
    // parent-under-itself row deleted itself exactly when an agent selected that parent.
    // Reported as "when Vake is selected, the Vake below is gone".
    distOwnOf: honestOwnCounts || null,
    // 0537/0538/0539: { "<street uuid>": { n, label } }, or null when the search has not
    // been asked for it. It rides `geo` because a street IS geography — the same bundle the
    // district tree reads, so the two can never be fed from different answers.
    streetCounts: honestStreetCounts || null,
    // B914: scoped per-station counts. Leads and properties are different piles, so this
    // follows the same chooser rule the street counts use rather than a second source.
    // B914: the SCOPED tally while a filter is active, the CORPUS snapshot when none is.
    // ⚠️ The condition is `serverAdvFilters || committedQ` — byte-for-byte the one the counts
    // prewarm guards on — because that prewarm is the ONLY thing that fills the corpus
    // snapshot. Any other test here and the two disagree about which view we are in, and the
    // facet reads a snapshot that was never taken.
    // Unarrived stays null, and the facet renders NO number for null — never 0, never the
    // module-scoped catalogue figure this whole row exists to stop showing.
    metroCounts: pickFacetCounts({
      isLeads: entryTypeFilter[0] === 'Leads',
      leads: leadFacetCounts.metros,
      scoped: serverMetroCounts,
      corpus: corpusMetroCounts,
      // ⚠️ byte-for-byte the predicate the counts prewarm guards on — it is the only thing
      // that fills `corpus`, so a wider test here reads a snapshot never taken.
      filtered: !!(serverAdvFilters || committedQ),
    }),
    // Sticky id→name, accumulated from every answer that carried one. The counts vanish the
    // moment a street is selected (the RPC declines to count what the query already
    // narrowed), and without this the row an agent just ticked would lose its own name and
    // render as a UUID. Labels are stable facts; counts are answers to one question.
    streetLabels,
    streetCatalog,
    streetCoverage: (entryTypeFilter[0] === 'Leads' ? leadFacetCounts.streetCoverage : serverStreetCoverage) || null,
    cityDisplayLabel,
  }

  // Suggest rows — ONE renderer for both surfaces (2026-07-18, same rule as
  // CooperationChips): the desktop anchored dropdown and the mobile full-screen overlay
  // render this identical list; only the container differs (eBay's own desktop/mobile split).
  const hasSuggestRows = entrySearch.trim().length >= 3
    && (activeTokens.length > 0 || placeSuggests.length > 0 || restrictionSuggests.length > 0
        || suggestResp?.clients?.length > 0 || suggestResp?.businesses?.length > 0 || suggestResp?.complexes?.length > 0
        || suggestResp?.streets?.length > 0)
  // ONE flat list of rows, built as DATA (2026-07-28). Every row is the same shape and every
  // row IS exactly what picking it does — which is what makes arrowing through them possible.
  // The restriction subject used to render as one row carrying two little buttons; the owner
  // called it: "it must show two dropdown options one with tick one with x exactly what will
  // be picked, so user just down arrows to the correct one and presses enter instead of
  // moving mouse to correct button". A row that contains choices is not a choice.
  const suggestItems = useMemo(() => {
    const out = []
    // Subjects first: they are the only rows that change a FILTER rather than navigate, and
    // they are two rows because the subject alone does not say which state you mean.
    for (const s of restrictionSuggests) {
      for (const o of restrictModeOptions()) {
        out.push({
          key: `rs${s.code}:${o.value}`, glyph: o.icon, label: s.display, tag: o.label,
          active: restrictionActiveMode.get(s.code) === o.value,
          onSelect: () => acceptRestriction(s, o.value, out.length),
        })
      }
    }
    placeSuggests.forEach((p, i) => out.push({
      // The tag already NAMES the type ("metro"); the icon must not contradict it with a
      // generic pin (owner, 2026-08-27). placeIcon returns null for a type we have no glyph
      // for, and then the pin is the honest answer rather than a guess.
      key: `pl${p.id ?? i}`, icon: placeIcon(p.type) || 'map-pin', label: p.display, tag: p.type,
      onSelect: () => acceptPlace(p, i),
    }))
    ;(suggestResp?.streets || []).forEach((s, i) => out.push({
      key: `st${s.id}`, icon: 'route', label: s.name,
      // DF01 (2026-10-28): the server reads a typed house number but never filters by it — say so on
      // the row, or "ანა პოლიტკოვსკაიას 999" looks like an exact address and returns the whole street.
      tag: [s.house_number ? tLog('desk_suggest_street_whole', { n: s.house_number }) : null, s.district, s.city].filter(Boolean).join(' · ') || t('desk_suggest_street_tag'),
      onSelect: () => acceptStreet(s, i),
    }))
    // count = linked properties (server arm, exact, null on failure). Distinguishes three
    // same-named towers at a glance (2026-07-22); 0 renders too — "known complex, no
    // inventory yet" is exactly the fact that stops a pointless click.
    // Naming (owner, 2026-08-26): primary in the INTERFACE language, the other language as
    // the muted secondary — agents meet both spellings on the portals daily, so the second
    // name is the recognition anchor, not decoration. 297 of 332 complexes carry a Georgian
    // name; the rest fall back to Latin alone.
    ;(suggestResp?.complexes || []).forEach((c, i) => out.push({
      key: `cx${c.id}`, icon: 'building',
      label: getLanguage() === 'ka' ? (c.name_ka || c.name) : c.name,
      sub: (getLanguage() === 'ka' ? (c.name_ka ? c.name : null) : c.name_ka) || null,
      tag: `${c.count != null ? `${c.count} · ` : ''}${t('desk_suggest_complex_tag')}`,
      onSelect: () => acceptComplex(c, i),
    }))
    {
      // ≥2 visible rows from one developer → offer the whole family as one row.
      const devs = [...new Set((suggestResp?.complexes || []).map(c => c.developer).filter(Boolean))]
      for (const dev of devs) {
        if ((suggestResp.complexes || []).filter(c => c.developer === dev).length >= 2) out.push({
          key: `cxfam${dev}`, icon: 'building', label: tLog('desk_suggest_complex_family', { dev }),
          tag: t('desk_suggest_complex_tag'),
          onSelect: () => acceptComplexFamily(dev, -1),
        })
      }
    }
    ;(suggestResp?.clients || []).forEach((c, i) => out.push({
      key: `cl${c.id}`, icon: 'user', label: c.name || t('desk_suggest_unnamed_client'),
      tag: t('desk_suggest_client_tag'),
      onSelect: () => acceptSuggestion('client', i, c.name || entrySearch.trim(), { category: 'Client' }),
    }))
    ;(suggestResp?.businesses || []).forEach((b, i) => out.push({
      key: `bz${b.id}`, icon: 'store', label: b.name, tag: t('desk_suggest_business_tag'),
      onSelect: () => acceptSuggestion('business', i, b.name || entrySearch.trim(), { category: 'Business' }),
    }))
    return out
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the accept* handlers are
    // rebuilt every render; their inputs (entrySearch, features, suggestResp) are all listed.
  }, [restrictionSuggests, restrictionActiveMode, placeSuggests, suggestResp, entrySearch, lang])
  const suggestRows = !hasSuggestRows ? null : (<>
    {suggestItems.map((it, i) => (
      <button key={it.key} type="button"
        // onMouseDown, not onClick: the input blurs on mousedown and the dropdown unmounts
        // before a click can land. Mouse and keyboard now reach the same handler.
        onMouseDown={e => { e.preventDefault(); it.onSelect() }}
        onMouseEnter={() => setSuggestIdx(i)}
        className={`w-full text-left px-3 py-1.5 flex items-center gap-2 ${
          i === suggestIdx ? 'bg-accent' : 'hover:bg-accent'}`}>
        {/* 16, not the 13 chips use: a dropdown row is a place to READ an icon, and the
            place glyphs (metro / park / plaza) carry detail that closes up at 13. */}
        <span className="shrink-0">{it.glyph ? it.glyph : <Icon name={it.icon} size={16} />}</span>
        <span className="truncate">{it.label}{it.sub ? <span className="text-muted-foreground"> · {it.sub}</span> : null}</span>
        <span className={`text-xs ml-auto shrink-0 ${it.active ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
          {it.tag}{it.active ? ' ·' : ''}
        </span>
      </button>
    ))}
  </>)
  // Shared by the desktop input and the mobile overlay input — one navigation model, or the
  // two surfaces drift (the suggestRows rule, applied to behaviour instead of markup).
  const onSuggestKeyDown = (e, onClose) => {
    const n = hasSuggestRows ? suggestItems.length : 0
    if (e.key === 'ArrowDown' && n) { e.preventDefault(); setSuggestIdx(i => (i + 1) % n); return }
    if (e.key === 'ArrowUp' && n) { e.preventDefault(); setSuggestIdx(i => (i <= 0 ? n : i) - 1); return }
    if (e.key === 'Enter') {
      // A highlighted row wins; otherwise Enter keeps its original meaning.
      if (suggestIdx >= 0 && suggestIdx < n) { e.preventDefault(); suggestItems[suggestIdx].onSelect(); onClose?.(); return }
      commitSearch(); onClose?.(); return
    }
    if (e.key === 'Escape') { if (suggestIdx >= 0) { setSuggestIdx(-1); return } onClose?.() }
  }

  // Live query preview — ONE renderer for both surfaces, pinned at the TOP of the dropdown
  // (2026-07-22). Since typing no longer moves the page behind it (see the committedSearch block),
  // this row is the whole of the per-keystroke feedback: how many loaded rows the query is
  // down to, and which chips it compiled. It replaces the old `desk_suggest_will_filter` row
  // inside suggestRows (which showed the chips but no count, and only at ≥3 chars — a
  // 2-char query that compiled a chip said nothing at all).
  // ⚠️ The count is LOADED ROWS ONLY. The whole-DB, cross-alphabet search is Enter's job
  // (§5.3 commit-to-search) — the label must keep saying so, or this row starts reading as
  // "your search found N", which for an uncommitted query is a lie.
  //
  // ⚠️ …and when a SERVER-ONLY predicate is active there is no honest count to show at all
  // (task #8, 2026-07-29). The loaded rows were never narrowed by a restriction lens, a date
  // range, a claim count or view status — the client does not evaluate any of them — so
  // "{n} of the loaded" beside such a query is a true sentence about a set the agent did not
  // ask for, rendered where they will read it as their answer. The number is dropped and the
  // row says which way the question actually gets answered; the commit pill is the action.
  // Deliberately NOT "0 results" and not a spinner: nothing has been searched yet.
  // ⚠️ 2026-10-23 audit (Desk §4): the feed follows the LANDED text, not keystrokes, so while the box
  // differs from it neither number above describes what is typed — it showed the previous answer's
  // loaded count beside the new words. And the Enter pill compared residual text only, so a box of
  // words the compiler fully understood (empty residual) had no visible Enter. The green total showed
  // the previous `listingTotal` as soon as the text was committed. searchPreviewState decides all
  // three from the box, the landed text and whether the committed answer has arrived.
  const previewServerOnly = serverOnlyFiltersActive(draftAdvFilters)
  const preview = searchPreviewState({ boxText: entrySearch, landedText: landedSearch, committedQ, serverOnly: previewServerOnly, totalLanded: listingTotalLanded, fetching: listingsFetching })
  const searchPreview = (activeTokens.length > 0 || liveResidual.length >= 3) ? (
    <div className="px-3 py-1.5 text-xs text-muted-foreground border-b border-border/50 flex items-center gap-2">
      {preview.count && (
        <span className="shrink-0">
          {preview.count === 'whole_base_only'
            ? t('desk_search_preview_whole_base_only')
            : tLog('desk_search_preview_loaded', { n: visibleGroups.length })}
        </span>
      )}
      {activeTokens.length > 0 && (
        <span className="truncate">{preview.count ? '· ' : ''}{activeTokens.map(tk => tk.label).join(' · ')}</span>
      )}
      {preview.action === 'enter' ? (
        /* Preview-vs-commit invariant (design §1): left = instant loaded-rows count, right =
           the BRIGHT whole-base commit pill. Clickable — same act as Enter, never merges
           with the preview number. */
        <button type="button" className={pill('none', { door: true, extra: 'ml-auto shrink-0 bg-primary text-primary-foreground font-semibold hover:opacity-90' })}
          onClick={() => { commitSearch(); closeSuggest() }}>
          {t('desk_search_preview_enter')} ↵
        </button>
      ) : preview.action ? (
        /* Committed state (design §1): green check "whole base · N" replaces the commit
           pill — the query IS the server answer now — plus გასუფთავება to start over.
           Until that answer lands ('committed_pending') only the clear action shows. */
        <span className="ml-auto shrink-0 inline-flex items-center gap-2">
          {preview.action === 'committed' && (
            <span className="text-emerald-600 dark:text-emerald-400 font-medium">✓ {tLog('desk_search_committed_base', { n: listingTotal })}</span>
          )}
          <button type="button" className="underline decoration-dotted hover:text-foreground"
            onClick={clearAllSearch}>{t('desk_search_clear_filters')}</button>
        </span>
      ) : null}
    </div>
  ) : null

  // Recent searches — ONE renderer for both surfaces (mobile overlay + desktop dropdown),
  // same rule as suggestRows. Shown only while the box is idle (empty / < 3 chars), eBay/
  // Google style; the last 10 committed queries come from localStorage (recordRecentSearch).
  // A click re-runs the RAW text so it re-compiles the same chips, then closes whichever
  // surface is open.
  const showRecents = entrySearch.trim().length < 3 && recentSearches.length > 0
  // Saved searches sit ABOVE recents in the same idle dropdown — a kept search outranks a
  // remembered one. Same idle gate as recents; the ✕ deletes without applying (stop
  // propagation, or every delete would also run the search it just removed).
  const showSaved = entrySearch.trim().length < 3 && savedSearches.length > 0
  const savedRows = !showSaved ? null : (<>
    <div className="px-3 pt-2 pb-1 text-xs text-muted-foreground">{t('desk_search_saved')}</div>
    {savedSearches.map(s => (
      <div key={s.id} className="w-full flex items-center hover:bg-accent">
        <button className="flex-1 min-w-0 text-left px-3 py-2 flex items-center gap-2"
          onClick={() => { applySavedSearch(s); closeSuggest() }}>
          <span className="text-muted-foreground"><Icon name="bookmark" /></span>
          <span className="truncate">{s.label || s.params.replace(/^#datadesk\??/, '')}</span>
        </button>
        <button className="px-2 py-2 text-muted-foreground hover:text-foreground shrink-0"
          title={t('desk_search_rename')}
          onClick={e => { e.stopPropagation(); renameSavedSearch(s) }}><Icon name="pencil" size={13} /></button>
        <button className="px-3 py-2 text-muted-foreground hover:text-destructive shrink-0"
          title={t('desk_search_saved_delete')}
          onClick={e => { e.stopPropagation(); deleteSavedSearch(s.id) }}>✕</button>
      </div>
    ))}
  </>)
  const recentRows = !showRecents ? null : (<>
    <div className="px-3 pt-2 pb-1 text-xs text-muted-foreground">{t('desk_search_recent')}</div>
    {recentSearches.map(q => (
      <button key={q} className="w-full text-left px-3 py-2 hover:bg-accent flex items-center gap-2"
        onClick={() => { setEntrySearch(q); commitSearch(q, 'recent', { reset: true }); closeSuggest() }}>
        <span className="text-muted-foreground"><Icon name="clock" /></span><span className="truncate">{q}</span>
      </button>
    ))}
  </>)

  // The ONE search pill (2026-07-17): rendered in the command strip on feed views, and as
  // the home cockpit's own top row (same width as the add-tile, so Agent Ranking starts
  // beside it at the very top) — a single const so there is never a second box whose state
  // could diverge. Typing in it NO LONGER leaves home (2026-07-22, see the committedSearch block) —
  // a commit does.
  // Saved-search bar (0231, owner 2026-07-28; moved UNDER the pill itself 2026-07-28 —
  // "its more logical to be there"): after a committed search, one slim row directly
  // beneath the search bar — clear everything, and keep or drop THIS search. The
  // save/delete toggle is driven by canonical-params equality (savedMatch), so "is this
  // saved" can never disagree with what applying the row would restore.
  // ⚠️ On DESKTOP this row is absolutely positioned under the pill — it must sit directly
  // below the search box (owner ruling 2026-07-28, twice), yet it must NOT take height in
  // the command strip. In normal flow it did both times we tried: inside the pill's flex
  // item it made that item ~20px taller than every sibling, so `items-center` centred the
  // pill+bar stack and lifted the pill above the ↻ / Agent / sort pills; as a w-full strip
  // child it wrapped a whole line and split the toolbar in two. Absolute = zero height, so
  // the strip stays one aligned line; the strip's md:mb-7 (see its className) reserves the
  // space this row floats into. Mobile keeps it in flow — that layout is a vertical stack,
  // where under-the-pill is simply the next row and absolute would overlay the pill slider.
  // It mounts in the command strip only — the home cockpit's copy of the pill can never
  // show it anyway (savedSearchEligible starts with !isHome).
  const savedSearchBar = !savedSearchEligible ? null : (
    <div className="flex items-center gap-3 px-3 pt-1 text-xs text-muted-foreground whitespace-nowrap md:absolute md:left-0 md:top-full md:z-10">   {/* F-046: z-10, BELOW the sticky header (App.jsx z-20) — at z-20 the row painted over the logo once the page scrolled under the header (owner screenshot 2026-09-06) */}
      {/* ── THE FULL FILTER READOUT (design turn 5, owner ruling 2026-08-06) ──────────
          This slot used to hold the identity LINEAGE — `for sale › residential › apartment`,
          moved up here from the panel on 2026-08-02. The owner's ruling replaces it: "we can
          remove that lineage part and use it to full filter description."
          The reason it wins: the lineage answered only the identity third of the question.
          "What is narrowing my results right now" was spread over three places — the words
          highlighted inside the box (typed), this lineage (identity), and the side panel
          (everything clicked, invisible from here). One row now answers all of it.
          ⚠️ Nothing new is DEFINED here. `constraintChips` is already declared to be "the
          single display of the WHOLE active filter state", identity chips included, with its
          own removal semantics — this is a second PLACEMENT of that one list, not a second
          list. The in-field keywords keep rendering the ones that fit: per turn 5 the input
          is the query and the chips are its readout, so the overlap is the design, not drift. */}
      <FilterReadout chips={readoutChips} stranded={readoutStranded} capacity={kwCapacity} onRemove={removeChip} />
      {/* ── WORDS THAT BECAME NOTHING (2026-08-27, real-agent workflow test) ─────────────
          A client requirement was silently dropped. Typing the real brief — "2 საძინებელი
          საბურთალო ცხოველები აივანი 800 მდე სექტემბრიდან" — compiled five constraints and
          left "სექტემბრიდან" (from September) as residual: not a filter, not sent to the
          server, not filtering anything client-side either, and NOT MENTIONED. The agent
          reads 36 confident results and believes availability was part of the question. It
          was not, so those 36 include flats free in December — a wasted call each, and the
          precise "dangerous assumption" this surface exists to prevent.
          The number is deliberately not folded into the chip row: a chip means a constraint
          that IS executing, and putting a non-constraint there would be the same lie in the
          opposite direction. It says what happened and points at the panel that can express
          it (availability IS searchable — move_in_by — it simply has no recognizer). */}
      {unappliedWords && (
        <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400"
              title={t('desk_search_unapplied_hint')}>
          <Icon name="alert-triangle" size={12} />
          {tLog('desk_search_unapplied', { words: unappliedWords })}
        </span>
      )}
      {(readoutChips.length > 0 || readoutStranded.length > 0) && <span className="text-border">·</span>}
      <button type="button" className="hover:text-foreground hover:underline"
        onClick={clearAllSearch}>{t('desk_search_clear_all')}</button>
      <span className="text-border">·</span>
      {savedMatch ? (
        <button type="button" className="hover:text-destructive hover:underline flex items-center gap-1"
          onClick={() => deleteSavedSearch(savedMatch.id)}>
          <Icon name="bookmark" size={12} /> {t('desk_search_saved_delete')}
        </button>
      ) : (
        <button type="button" className="hover:text-foreground hover:underline flex items-center gap-1"
          onClick={saveCurrentSearch} disabled={!favIdentity}
          title={favIdentity ? undefined : t('desk_search_save_needs_agent')}>
          <Icon name="bookmark" size={12} /> {t('desk_search_save')}
        </button>
      )}
    </div>
  )
  // ── CALL A / A3: on a phone the field says HOW MANY, the row beneath says WHICH ─────────
  // Owner ruling 2026-08-18 emptied the box of any text that became a criterion. On desktop
  // those criteria come back as keyword pills inside the field; the phone cannot copy that at
  // ~200px, and the designer ruled it must not try: the readout row (`savedSearchBar`) sits in
  // FLOW directly under the bar on mobile, 8px away, so a keyword in the field would print the
  // same constraint twice — the duplication the 2026-07-24 keyword rework existed to remove.
  // (That is also why the same argument does NOT apply on desktop, where the readout is
  // absolutely positioned off the pill and the field is the only place the criteria appear.)
  //
  // Measured before building, 30 days of search_query_log: 2,025 committed searches, of which
  // only 348 (17%) actually empty the field — 613 keep their text because identity tokens
  // (listing id, phone, cadastral) never fold, and 993 compiled nothing at all. So this is a
  // real but minority state, which is why it gets a count and not a redesign of the bar.
  //
  // TWO conditions the designer attached, both load-bearing:
  //   · MUTED, never foreground — it is a status, not text you can edit.
  //   · it MUST carry its own ✕. A field that says "3 filters" and cannot be emptied from
  //     itself is the invisible-filter failure this whole surface keeps relapsing into.
  // Tapping the words opens the same full-screen takeover a tap on the empty field opens, so
  // the field keeps exactly one meaning: "this is where searching happens".
  // Count comes from `constraintChips` — the same list the ⚙ pill counts, so the two can never
  // disagree. ⚠️ They now print the same number ~20px apart; if that reads as noise in use, the
  // designer's instruction is to drop the count from the ⚙ pill, NOT from here.
  const mobileFilterSummary = isMobile && !entrySearch.trim() && constraintChips.length > 0
  const searchPill = (
          // relative: the anchor savedSearchBar hangs off on desktop (md:absolute top-full).
          <div className="relative grow basis-56 min-w-[200px]">
          <div ref={searchPillRef} className="relative w-full flex items-center rounded-full border border-input bg-background focus-within:ring-2 focus-within:ring-ring">
            {/* Double-click the magnifier = compiler debug view (per-recognition trace,
                residual, vocab version, timing) — observability for "why didn't X compile",
                without shipping a visible dev tool to agents. */}
            <span className="pl-3 text-muted-foreground text-xs cursor-pointer select-none shrink-0"
              title={t('desk_search_debug_tooltip')}
              onDoubleClick={() => !isMobile && setSearchDebug(v => !v)}><Icon name="search" /></span>
            {/* §1 keyword redesign (2026-07-24): the in-field ×-chips are gone. Constraints
                render as inline highlighted keywords inside the field — TYPED ones tinted in
                place by HighlightedSearchInput's mirror (Phase 1), model ones projected here
                (Phase 2). Together they are the single readable summary of what's applied
                (handoff invariant 8/10a). Overflow past KW_INLINE_MAX collapses to +N → panel.
                Desktop only: the mobile strip box is ~200px and hands off to the full-screen
                overlay on tap — mobile shows filter state on its own facet-pill row instead. */}
            {!isMobile && searchKeywords.length > 0 && (
              // ⚠️ The keyword row must YIELD, never the input: with shrink-0 here, three
              // keywords ate the whole pill and left a 16px-wide box nobody could type in
              // (measured 2026-07-24). So this row shrinks + clips (each keyword truncates)
              // while the input below keeps a min-width floor. Typing always stays possible.
              <span className="flex items-center gap-1.5 pl-2 min-w-0 overflow-hidden">
                {searchKeywords.slice(0, kwShown).map(c => (
                  // group/kw: the remove control appears on hover/focus only — a keyword is a
                  // word, not a persistent ✕-chip (design §1). Removal goes through the same
                  // removeChip the chips used, so a keyword whose value ALSO came from a typed
                  // token still dismisses that token instead of letting it re-apply.
                  // Shrinkable (min-w) so an over-long label ellipsizes rather than the row
                  // clipping a whole keyword off at its edge — but never below 3rem, where a
                  // keyword stops reading as a word at all.
                  <span key={c.key} className={`group/kw relative inline-flex items-center min-w-[3rem] max-w-[10rem] text-sm ${KEYWORD_CLASS}`}>
                    {/* Click the WORD → edit it; click the ✕ → remove it. Editing opens the
                        facet's own control from the registry, never a bespoke per-chip form
                        (owner ruling: chips describe, the registry edits). A keyword with no
                        registry entry — a listing id, a phone — stays plain text: you don't
                        "edit" an identity, you remove it and type another. */}
                    {isEditableKeyword(c.editKey) ? (
                      <button type="button" className="truncate text-left"
                        title={t('desk_kw_edit_title')}
                        onClick={() => setEditingKw(k => k === c.key ? null : c.key)}>{kwText(c.label)}</button>
                    ) : <span className="truncate">{kwText(c.label)}</span>}
                    {editingKw === c.key && (
                      <span className="absolute z-30 top-full left-0 mt-1 rounded-md border border-border bg-popover shadow-lg p-2"
                        onMouseDown={e => e.stopPropagation()}>
                        <KeywordEditor editKey={c.editKey} filters={advFilters} setFilters={setAdvFilters}
                          ctx={{ optionsFor: kwOptionsFor, districtOptions: kwDistrictOptions,
                                 districtQuery: kwDistrictQuery, setDistrictQuery: setKwDistrictQuery }} />
                      </span>
                    )}
                    <button type="button" title={t('desk_search_kw_remove')} aria-label={t('desk_search_kw_remove')}
                      className="ml-0.5 px-0.5 shrink-0 leading-none text-muted-foreground opacity-0 group-hover/kw:opacity-100 focus:opacity-100 hover:text-foreground"
                      onClick={() => removeChip(c)}>×</button>
                  </span>
                ))}
                {searchKeywords.length > kwShown && (
                  // The overflow count NAMES what it hides (tooltip) and opens the panel that
                  // owns those filters — a bare "+2" would be exactly the invisible-filter
                  // state the chips row existed to prevent.
                  <button type="button" className="text-xs text-muted-foreground hover:text-foreground shrink-0"
                    title={searchKeywords.slice(kwShown).map(c => kwText(c.label)).join(' · ')}
                    onClick={() => setFiltersOpen(true)}>+{searchKeywords.length - kwShown}</button>
                )}
              </span>
            )}
            {/* Mobile (2026-07-18, eBay model): tapping the strip box OPENS the full-screen
                overlay instead of the anchored dropdown. preventDefault on mousedown so this
                input NEVER takes focus on mobile — the first version blurred on focus
                instead, and the blur/autofocus race left the strip input holding the
                keyboard, so Enter committed without closing the overlay. */}
            {/* Inline keywords, TYPED side (§1 Phase 1, 2026-07-24): recognized filter words
                are highlighted IN the field; the ×-chips that used to sit before this input
                are gone. Removal of a typed keyword is text editing — delete the word.
                activeTokens carry {start,end} spans the mirror paints. */}
            {mobileFilterSummary && (<>
              <button type="button" className="flex-1 min-w-0 truncate px-2.5 py-1.5 text-left text-sm text-muted-foreground"
                onClick={() => setSearchOverlayOpen(true)}>
                {tLog('desk_search_field_filter_count', { n: constraintChips.length })}
              </button>
              {/* Its own control, not a second meaning bolted onto the words: tap the count to
                  search, tap the ✕ to clear. `clearAllSearch` is the same action the readout
                  row's "clear all" link runs, so one search has one way to end. */}
              <button type="button" className="px-3 shrink-0 text-sm leading-none text-muted-foreground hover:text-foreground"
                title={t('desk_search_field_clear')} aria-label={t('desk_search_field_clear')}
                onClick={clearAllSearch}>✕</button>
            </>)}
            {/* The input stays MOUNTED and merely hidden while the summary shows — it owns the
                paste handler, the token mirror and the caret logic, and unmounting it would
                drop all three on a state the agent leaves with one tap. `hidden` also takes it
                out of the tab order, so the summary is what a keyboard reaches. */}
            <HighlightedSearchInput value={entrySearch} tokens={activeTokens} aria-label={t('desk_search_label')}
              onChange={e => setEntrySearch(e.target.value)}
              onPaste={onSearchPaste}
              // Click a TYPED keyword → select its whole span, so the next keystroke replaces the
              // constraint instead of corrupting it mid-word (designer 2026-07-24: "click a
              // keyword to edit it inline"; for typed ones that IS the text, so editing is text
              // editing). Projected keywords can't do this — they aren't in the input's value —
              // which is why they get a popover instead; one verb, two mechanisms, split by origin.
              // ⚠️ Selection only fires when the caret lands INSIDE a recognized span, so clicking
              // free text still places a normal caret. Runs on click, not on every selectionchange,
              // or dragging a selection across a keyword would fight the user.
              onClick={e => {
                const at = e.target.selectionStart
                if (at == null || e.target.selectionStart !== e.target.selectionEnd) return   // a drag-select is the user's own
                const tok = activeTokens.find(tk => Number.isInteger(tk.start) && at > tk.start && at < tk.end)
                if (tok) e.target.setSelectionRange(tok.start, tok.end)
              }}
              onMouseDown={e => { if (isMobile) { e.preventDefault(); setSearchOverlayOpen(true) } }}
              onFocus={() => { if (isMobile) setSearchOverlayOpen(true); else setSearchFocused(true) }}
              onBlur={() => setSearchFocused(false)}
              onKeyDown={e => {
                if (e.key === 'Tab' && searchHints.length) { e.preventDefault(); acceptHint(searchHints[0]) }
                // ⌫ ON AN EMPTY BOX REMOVES THE LAST KEYWORD (owner-reported 2026-08-05:
                // "when you delete filter it still stays"). The reported gesture was deleting
                // the typed words and expecting the filters to go with them — reasonable,
                // because after a commit the constraint is FOLDED into advFilters and its word
                // is no longer in the text, so text editing can no longer reach it. The ✕ on
                // each keyword only appears on hover, which a keyboard never triggers.
                // This is the chip-input idiom every mail client shares, so it needs no
                // teaching, and it routes through the SAME removeChip as the ✕ — which also
                // dismisses the underlying token, so a still-typed word cannot re-apply it.
                // ⚠️ Only the last VISIBLE keyword. Past `kwShown` they collapse into "+N",
                // and deleting one from behind that count would mutate a filter nobody can
                // see — the invisible-mutation class this file warns about three times.
                // Guarded on an empty box, so it can never eat text the agent is editing.
                // ⚠️ e.target.value, NOT entrySearch (2026-08-05, second report): the state
                // in this closure is one render behind the DOM while keys are landing, and
                // on the heavy live feed a render is slower than the ~33ms key-repeat — so
                // the press right after the text emptied read stale non-empty state and did
                // nothing. The DOM value is what the agent SEES, and it is never stale.
                // ⚠️ !e.repeat: a HELD backspace must stop at the empty box, not continue
                // into the pills at repeat speed — reaching empty mid-hold would otherwise
                // strip every filter in ~100ms. One deliberate press per filter.
                if (e.key === 'Backspace' && !e.repeat && !e.target.value && !isMobile) {
                  const visible = searchKeywords.slice(0, kwShown)
                  const last = visible[visible.length - 1]
                  if (last) { e.preventDefault(); removeChip(last); return }
                }
                // A KEYWORD DELETES WHOLE, filter included (owner, 2026-08-05: "remove each
                // search keyword by one backspace, no letter delete"). Letter-deleting
                // through a recognized word was worse than slow — mid-word the compiler
                // stopped recognizing it, the landed chip popped out IN FRONT of the box
                // ("shows the same filter fully"), and the constraint outlived its own text
                // because the committed fold is unreachable from the text. One press now
                // takes the span AND its constraint (removeTokenConstraint) together.
                // Caret must TOUCH the span (inside or at its end): free text around the
                // keywords still edits letter by letter, and a caret one space past a word
                // eats that space first — normal editing, then the chip gesture.
                // ⚠️ !e.repeat here too: a HELD backspace stays plain text-editing the whole
                // way (its repeats letter-delete into the word as free text) — chip-sized
                // jumps at key-repeat speed would eat every constraint in a blink.
                if (e.key === 'Backspace' && !e.repeat && !isMobile
                    && e.target.selectionStart === e.target.selectionEnd) {
                  const at = e.target.selectionStart
                  const tok = activeTokens.find(tk => Number.isInteger(tk.start) && at > tk.start && at <= tk.end)
                  if (tok) {
                    e.preventDefault()
                    removeTokenConstraint(tok)
                    const ns = (e.target.value.slice(0, tok.start) + e.target.value.slice(tok.end))
                      .replace(/\s{2,}/g, ' ')
                    setEntrySearch(ns.trimStart())
                    return
                  }
                }
                // Arrow/Enter/Escape go through the shared dropdown navigation (2026-07-28).
                // With nothing highlighted its Enter branch is the ORIGINAL §5.3 behaviour —
                // commit-to-search, the server text search fires here, never per keystroke.
                onSuggestKeyDown(e, closeSuggest)
              }}
              placeholder={t(
                // Category-scoped placeholder (2026-07-17): SAME box, same state, same
                // compiler — only the invitation text follows the active category.
                entryTypeFilter[0] === 'Client' ? 'desk_search_placeholder_client'
                : entryTypeFilter[0] === 'Meeting' ? 'desk_search_placeholder_meeting'
                : entryTypeFilter[0] === 'Leads' ? 'desk_search_placeholder_leads'
                : entryTypeFilter[0] === 'Business' ? 'desk_search_placeholder_business'
                : 'desk_search_placeholder')}
              wrapperClassName={mobileFilterSummary ? 'hidden' : 'flex-1 min-w-[7rem]'}
              boxClassName="pl-2 pr-2 py-1.5 text-sm focus:outline-none" />
            {/* 📷 image search — pHash lookup over the capture layer's hashed images. */}
            <input ref={imageFileRef} type="file" accept="image/*" className="hidden"
              onChange={e => { runImageSearch(e.target.files?.[0]); e.target.value = '' }} />
            <button type="button" className="px-2 text-sm text-muted-foreground hover:text-foreground shrink-0"
              title={t('desk_search_by_image_tooltip')}
              onClick={() => imageFileRef.current?.click()}>
              {imageSearch === 'loading' ? <Icon name="hourglass" /> : <Icon name="camera" />}
            </button>
            <span className="h-5 w-px bg-border shrink-0" />
            {/* bg-background, NOT bg-transparent: Chromium paints the native option popup
                with the select's own background — transparent fell back to a WHITE popup
                with the dark theme's light text (unreadable, user-reported 2026-07-17).
                Explicit option colors as belt-and-braces for both themes. */}
            <select aria-label={t('desk_search_category_label')}
              className="h-9 bg-background pl-2 pr-2 text-xs rounded-r-full focus:outline-none shrink-0 max-w-[130px] [&>option]:bg-background [&>option]:text-foreground"
              value={entryTypeFilter.length === 0 ? '' : (entryTypeFilter[0] === 'Property' ? 'Properties' : entryTypeFilter[0])}
              onChange={e => {
                const v = e.target.value
                // '' = ALL (eBay default, 2026-07-17 — supersedes the 2026-07-16 properties-only
                // default); 'Properties' = the property+listing pills; the rest are single pills.
                setEntryTypeFilter(v === '' ? [] : v === 'Properties' ? ['Property', 'Listings'] : [v])
                // Picking a category IS a browse action (this select renders on home too).
                // Picking ALL is not — it's the default, and treating it as one would make the
                // "clear the category" gesture kick the agent out of the cockpit.
                if (v !== '') enterBrowse()
              }}>
              <option value="">{t('desk_search_all_types')}</option>
              <option value="Properties">{t('desk_type_properties')}</option>
              {['Client', 'Meeting', 'Business', 'Leads'].map(ty => <option key={ty} value={ty}>{t(ENTRY_TYPE_FILTER_LABEL_KEYS[ty])}</option>)}
            </select>
            {/* Suggest dropdown (§11): grouped, Zillow-style — suggestions, never results.
                onMouseDown preventDefault keeps the input's blur from killing the click.
                Chips preview + places render with zero server dependency; server arms are
                whatever the cached suggest response holds (per-arm null = arm failed,
                simply not rendered — independent arm failure, §11). */}
            {!isMobile && searchFocused && (searchPreview || hasSuggestRows || showRecents || showSaved) && (
              <div className="absolute top-full left-0 right-0 mt-1 z-40 rounded-xl border border-border bg-popover shadow-lg py-1 text-sm max-h-96 overflow-y-auto"
                onMouseDown={e => e.preventDefault()}>
                {searchPreview}
                {suggestRows}
                {savedRows}
                {recentRows}
              </div>
            )}
          </div>
          {savedSearchBar}
          </div>
  )

  // + დამატება — ONE definition, rendered next to the search pill in BOTH desk states
  // (owner 2026-08-03). It used to exist only on the browse toolbar while home carried a
  // full-width three-button AddEntryTile; that tile is gone, so home now reaches the add
  // kinds through this same button and menu — same placement, same ids, one behaviour.
  // Desktop only — mobile has the fixed header "+" (App.jsx).
  const addButton = (
    <div className="hidden md:block relative shrink-0">
      <button type="button" onClick={() => setAddMenuOpen(o => !o)}
        className="flex items-center gap-1 shrink-0 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity whitespace-nowrap">
        + {t('desk_strip_add')}
      </button>
      {addMenuOpen && (<>
        <button type="button" aria-label="close" className="fixed inset-0 z-40 cursor-default"
          onClick={() => setAddMenuOpen(false)} />
        <div ref={keepInView} className="absolute right-0 top-full mt-1 z-50 rounded-lg border border-border bg-background shadow-lg py-1 w-52">
          {DESK_BUTTONS.filter(b => !b.cap || can(role, b.cap)).map(b => (
            <button key={b.id} type="button"
              className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-accent transition-colors"
              onClick={() => { setAddMenuOpen(false); openModal(b.id) }}>
              <Icon name={b.icon} size={15} /> {t(b.labelKey)}
            </button>
          ))}
        </div>
      </>)}
    </div>
  )

  // Mobile full-screen search takeover (eBay model, 2026-07-18): the strip box hands off
  // here on focus. Same entrySearch state, same commit path — only the container differs
  // from desktop's anchored dropdown. Completion hints render as tappable rows (the old
  // dashed chips + Enter-hint + debug panel are desktop-only now — the debug panel's red
  // strikethrough lines were leaking to agents on accidental 🔍 double-taps).
  const searchOverlay = isMobile && searchOverlayOpen ? (
    <div className="fixed inset-0 z-50 bg-background flex flex-col">
      <div className="flex items-center gap-1 px-2 py-2 border-b border-border">
        <button className="px-2 py-1 text-xl text-muted-foreground" aria-label={t('desk_search_overlay_back')}
          onClick={() => setSearchOverlayOpen(false)}>←</button>
        <div className="flex-1 flex items-center rounded-full border border-input bg-background focus-within:ring-2 focus-within:ring-ring">
          {/* Inline keywords in the mobile takeover too (§1 Phase 1, 2026-07-24) — same
              transparent-input-over-mirror component as desktop. */}
          <HighlightedSearchInput autoFocus value={entrySearch} tokens={activeTokens} aria-label={t('desk_search_label')}
            onChange={e => setEntrySearch(e.target.value)}
            onKeyDown={e => onSuggestKeyDown(e, () => setSearchOverlayOpen(false))}
            onPaste={onSearchPaste}
            placeholder={t('desk_search_placeholder')}
            wrapperClassName="flex-1 min-w-0"
            boxClassName="pl-4 pr-2 py-2 text-sm focus:outline-none" />
          {entrySearch && (
            <button className="px-3 text-muted-foreground" title={t('desk_search_clear_box')}
              onClick={() => setEntrySearch('')}>✕</button>
          )}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto py-1 text-sm">
        {/* Same live preview the desktop dropdown pins on top — the overlay covers the page
            entirely here, so it is literally the only feedback the query has. */}
        {searchPreview}
        {/* Completion hints — same accept semantics as the desktop dashed chips. */}
        {searchHints.map(h => (
          <button key={h.code} className="w-full text-left px-3 py-2 hover:bg-accent flex items-center gap-2"
            onClick={() => acceptHint(h)}>
            <span><Icon name="map-pin" /></span><span className="truncate">{h.display}</span>
            <span className="text-xs text-muted-foreground ml-auto shrink-0">{h.insert}</span>
          </button>
        ))}
        {suggestRows}
        {/* Recents fill the idle screen (box empty / too short to suggest), eBay-style —
            shared renderer, same as the desktop dropdown. */}
        {savedRows}
        {recentRows}
      </div>
    </div>
  ) : null

  return (
    <ViewerCtx.Provider value={viewerCtxValue}>
    <MoneyCtx.Provider value={money}>
    <AdTextCtx.Provider value={adTextByListing}>
    <div className="space-y-6">
      {searchOverlay}
      {/* Strip facet-pill bottom sheet (mobile pill slider, 2026-07-18): a second
          AdvancedSearchPanel instance in sheetOnly mode — same registry body as the
          <Icon name="settings" /> page, so a pill can never show different controls than the full page. */}
      {isMobile && facetSheet && (
        <AdvancedSearchPanel bookScope={listingsScope} features={features} filters={advFilters} setFilters={setAdvFilters} listings={panelListings} facetCounts={facetCounts} rangeFacets={panelRangeFacets} kindFeatureCaps={kindFeatureCaps} conditionLadder={conditionLadder} poolCounts={leadSearchMeta?.pool_counts || null}
          statusFilter={entryStatusFilter} onStatusChange={setEntryStatusFilter}
          onCollapse={() => setFacetSheet(null)} entryType={entryTypeFilter[0] || ''}
          resultsCount={sheetResultsCount} readout={advDisplay} poolSwitch={poolSwitch && { ...poolSwitch, toTotal: poolTotal }} onUndoPoolSwitch={undoPoolSwitch} clientLens={clientLens} clientFill={clientFillProp} sort={entrySort} geo={panelGeo} sheetOnly={facetSheet}
          agentFilter={deskAgentFilter}
          category={{ value: entryTypeFilter, set: setEntryTypeFilter, counts: typeCounts, meeting: { value: meetingStatusFilter, set: setMeetingStatusFilter, counts: meetingCounts, settled: viewingsSettled }, warmStatus: v => warmFeedPage({ status: v }), lead: { value: leadArchive, set: setLeadArchive, prefetch: leadHoverPrefetch, prefetchOther: leadPrefetchOther, count: leadCountActive, archiveCount: leadCountArchive, worked: { value: leadWorked, set: setLeadWorked } }, clientLead: { count: clientLeadCount, capped: clientLeadCapped }, workCounts, favCount: favRows ? favRows.size : null, archCount: archRows ? archRows.size : null, kindCounts: facetCounts?.kinds ?? null, countsProvisional, propertyTotal: treePropertyTotal, workPropertyTotal: propertyTotalForTree, scope: { value: listingsScope, set: setScopePref, canAll: canSeeAllScope && canScopeToSelf, allLabel: officeScopeLabel } }} />
      )}
      {/* Top: add-buttons row, then the hero grid: activity side column + ranking.
          Restructured 2026-07-06: the buttons left their tall left column, and the
          advanced-search panel moved down to the entries section — an expandable drawer
          directly above the search bar it complements (⚙ Filters toggles it). The old
          md 3-across hero with fixed 340px/260px siblings crushed the panel to ~100px
          (the truncated-"An…" dropdowns bug). */}
      {/* Restructured 2026-07-06 (three-column Desk): the old hero grid dissolved — Add buttons
          moved into the entries grid, AgentRanking lives in the RIGHT sidebar beside the cards
          (the Activity/Notifications/Meetings widget that used to share that column was removed
          2026-07-09), search tools live in the LEFT sidebar; only the parser stays up here. */}
          {SHOW_MAIN_PARSER && <div className={card}>
            {/* No title/button anymore: paste a message and press Enter to parse. The box
                glows amber once it has content so it reads as "ready — hit Enter". Enter parses
                (preventDefault), Shift+Enter still inserts a newline for the rare hand-edit. */}
            <textarea
              className={`${input} font-mono text-sm transition-shadow ${rawText.trim() ? 'ring-2 ring-amber-400/70 border-amber-400' : ''}`}
              rows={5}
              placeholder={t('desk_paste_main_placeholder')}
              value={rawText}
              onChange={e => setRawText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); if (!parsing && rawText.trim()) handleParse() } }}
            />
            <div className="flex items-center gap-3 mt-2 min-h-[1.25rem]">
              {parsing
                ? <span className="text-xs text-primary"><IconText>{t('desk_paste_analyzing')}</IconText></span>
                : rawText.trim()
                  ? <span className="text-xs text-muted-foreground">{t('desk_paste_enter_hint')}</span>
                  : null}
              {usage && <span className="text-xs text-muted-foreground">{usage.model} · {usage.tokens_in}↑ {usage.tokens_out}↓</span>}
            </div>
            {parseErr && <p className="mt-2 text-sm text-destructive">{parseErr}</p>}
            {commitMsg && <p className="mt-2 text-sm text-muted-foreground">{commitMsg}</p>}
            {aiHint === 'undecided' && (
              <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                {t('desk_paste_unsure_type')}
              </p>
            )}
            {hasGlow && (
              <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                {t('desk_paste_matches_existing')}
              </p>
            )}
            {aiHint === 'noop' && (
              <p className="mt-2 text-xs text-muted-foreground">
                {envelope?.candidates_considered?.length
                  ? t('desk_paste_followup_existing')
                  : t('desk_paste_nothing_actionable')}
              </p>
            )}
            {aiHint === 'property' && (
              <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                {t('desk_paste_looks_like_property')}
              </p>
            )}
          </div>}

      {/* Entries — full width, sorted by relevance to the pending paste when one exists.
          The "🏠 N need review" chip moved into the notifications panel (right sidebar) —
          it was a lone stray line above the strip (2026-07-06). */}
      <div>
        {/* Command strip — the ONE row that owns primary filtering: view switcher + refresh,
            the search box (compiles into the same filter state the sidebar edits), City +
            District geography dropdowns (moved up from the old drawer, 2026-07-06 — with
            0056 counts + gray-out), the search-tools sidebar toggle, type pills, and
            status/sort/period. flex-wrap so narrow widths wrap whole controls.
            The whole strip is feed-only (2026-07-17): on the Desk home the SAME pill
            (searchPill const) renders instead as the cockpit's own top row inside the main
            column — add-tile width — so Agent Ranking starts beside it at the very top. */}
        {/* md:mb-7 reserves the strip's bottom gap for the saved-search row, which hangs
            absolutely off the pill on desktop (see savedSearchBar) and so contributes no
            height of its own — without it the row would sit on top of the results header. */}
        {!deskHomeActive && (
        <div className={`flex flex-wrap items-center gap-2 ${savedSearchEligible ? 'md:mb-7 mb-2' : 'mb-2'} desk-mode-enter`}>
          {/* ⌂ home button removed 2026-07-18 (user ruling): the app-header logo is the
              home button now (eBay model) — it fires goDeskHome via deskHomeSignal. */}
          {/* Desktop-only row A (2026-07-18 pill slider): on mobile the map toggle is a
              slider pill, ↻ is dropped outright (period changes refetch; nothing else
              earned a pill), and the agent filter rides at the slider's tail. */}
          <div className="hidden md:contents">
          {/* The clock/recents home button was removed 2026-07-24 (owner: "use the top
              Sentavus logo as home, we won't need the recent button") — the app-header logo
              already fires deskHomeSignal → goDeskHome on every size, so a second in-toolbar
              home control was redundant. */}
          {/* View switch (card/gallery/map): the normal listing pipeline AND the Leads feed
              honor it (Leads renders the same HistoryGroup cards, so card/gallery/map all
              apply — owner 2026-07-24: "make Leads similar to properties"). The OTHER special
              feeds (Pool/Meeting/Business/Favorites) have bespoke layouts and ignore it, so
              the toggle stays hidden there rather than reading as a dead control. */}
          {(!SPECIAL_FEED || entryTypeFilter[0] === 'Leads') && (
            <ViewToggle view={entriesView} setView={changeEntriesView}
              hideList hideCard={isMobile} cardIcon="☰" cardTitle={t('agents_view_list')} third={[
              { id: 'gallery', icon: '🖼️', title: t('desk_search_gallery_view_tooltip') },
              { id: 'map', icon: <Icon name="map" />, title: t('desk_search_map_view_tooltip') },
            ]} />
          )}
          {/* ↻ REMOVED 2026-08-09 (owner: "I don't think we need this refresh button when we
              already have main browser's own refresh"). It was the desktop half of a control
              mobile never had — the mobile pill row dropped it in 2026-07-18 for the same
              reason, so this only finishes that ruling.
              What the browser's own reload does NOT lose, which is why this is safe now and
              would not have been a year ago: the open card trail is in the URL (B309,
              `?cards=…`), the search is in the hash, and `loadHistory` still runs on tab focus
              (the 2026-07-18 refetch-on-focus path) — so returning to the tab already refetches
              without anyone pressing anything. Don't re-add a button whose job is already done
              twice over. */}
          {/* Agent filter MOVED into DETAILED SEARCH (owner 2026-07-28) — it is admin-only
              and was taking permanent toolbar width from controls everyone uses. It lives
              as the panel's second facet now, right after Category; `deskAgentFilter`
              carries allAgents (not the role/office-filtered `agents` prop) so a resigned
              agent's past entries stay findable. */}
          </div>
          {/* Mobile layout (2026-07-18 user ruling, 3rd pass — eBay pill slider): the
              search bar stretches a FULL line of its own; below it ONE horizontally
              scrollable row of uniform pills owns every secondary control. md:contents
              dissolves the search wrapper on desktop so the flex-wrap layout there is
              untouched. Icons are the SVG Icon set, never raw emoji (see Icon.jsx
              header — emoji ignore color and blur at chip size). */}
          <div className="w-full md:contents">
          {/* eBay-style unified search pill (2026-07-17, user-directed): input + 📷 image
              search + category scope in ONE rounded control, category default = All. */}
          {searchPill}
          </div>
          {/* Mobile pill slider — however many pills, it scrolls, never wraps (user
              ruling: "even if 10 it must be a slider"). Facet pills open their OWN
              bottom sheets (AdvancedSearchPanel sheetOnly below); ⚙ opens the full
              Filter page; sort/period are PillSheetSelects; map toggles the view. */}
          <div className="w-full overflow-x-auto md:hidden" style={{ scrollbarWidth: 'none' }}>
            <div className="flex items-center gap-1.5 w-max pb-0.5">
              {/* ⚠️ LEADS BELONG HERE (owner 2026-07-25 mobile review: "detailed search for
                  example on mobile leads lack it"). Leads took the FULL filter set on
                  2026-07-23 — same search bar, same facets, same server sort via the 0187
                  scope=leads fork — and the DESKTOP panel was opened to them in that change
                  (see the sidebar gate below, which excludes only Business). This mobile
                  list was not updated with it, so a phone got the search bar and no way to
                  refine it while a laptop showed every facet on the same category. Meeting/
                  Business stays out: that feed really does carry its own controls. */}
              {!['Meeting', 'Business'].includes(entryTypeFilter[0]) && (
                <button type="button" className={mobilePill(constraintChips.length > 0)}
                  onClick={() => setFiltersOpen(o => !o)}>
                  <Icon name="settings" size={13} /> {t('desk_search_tools_bare')}{constraintChips.length ? ` · ${constraintChips.length}` : ''}
                </button>
              )}
              {/* 0310's "both surfaces must offer the same set" is now structural, not a
                  request: deskSortOptions() IS the set, shared with the desktop dropdown. */}
              {showSort && <PillSheetSelect title={t('desk_search_sort_title')} value={entrySort} onChange={pickSort}
                icon={<Icon name="arrow-up-down" size={13} />}
                options={deskSortOptions({ allow: sortAllow, feed: feedCat })} />}
              {/* Same ruling as the ⚙ pill above — the facet sheets are the mobile equivalent
                  of the desktop panel Leads already has. */}
              {!['Meeting', 'Business'].includes(entryTypeFilter[0]) && (<>
                <button type="button" className={mobilePill(districtPickCount > 0)}
                  onClick={() => setFacetSheet('location')}>
                  <Icon name="map-pin" size={13} /> {t('desk_facet_location')}{districtPickCount ? ` · ${districtPickCount}` : ''} ▾
                </button>
                <button type="button" className={mobilePill(draftAdvFilters.priceMin !== '' || draftAdvFilters.priceMax !== '')}
                  onClick={() => setFacetSheet('price')}>
                  {t('desk_range_price_header')} ▾
                </button>
                {/* ── SLOT 4 FOLLOWS THE IDENTITY (designer ruling, 2026-08-18) ────────────
                    Slots 1–3 — ⚙, location, price — never move: those are the questions every
                    identity answers, and they are where the muscle memory actually sits. From
                    here the row asks what the chosen identity CAN answer, off the same
                    `interviewCapability` predicate the sheet's refusal reads, so the two can
                    never disagree: a land search offers plot size where a flat offers rooms.
                    That makes the refusal in the sheet unreachable by this route rather than
                    better worded — which is the point. It is still reachable from a saved
                    search or a shared link that names an identity, so the refusal stays.
                    ⚠️ A DROPPED PILL LEAVES NO TRACE — no dashed dead pill spending a 44px
                    target on a question that cannot be asked. The absence is explained in
                    full one tap away, in the ⚙ page's ghost row, which is the surface that
                    already owns "what is not being asked here". Two places saying it would be
                    the duplication Call A was decided against.
                    ⚠️ Land area has no facet of its own: the `area` facet renders the plot
                    control when capability allows it (see `showLandArea`). So the pill is
                    labelled for the plot and opens `area` — one facet, contextual door. */}
                {applies(pillCap, 'rooms') && (
                  <button type="button" className={mobilePill((draftAdvFilters.roomsSet || []).length > 0)}
                    onClick={() => setFacetSheet('rooms')}>
                    {t('desk_range_rooms')}{(draftAdvFilters.roomsSet || []).length ? ` · ${(draftAdvFilters.roomsSet || []).length}` : ''} ▾
                  </button>
                )}
                {/* ⚠️ "INSTEAD OF", NOT "AS WELL AS" — slot 4 holds exactly one pill.
                    Keying this on `applies(landArea)` alone was wrong twice, caught by
                    printing the matrix rather than by reading the code: with NO kind chosen
                    landArea is true (nothing has been excluded yet), so the plain browse row —
                    the one every agent opens on — would have grown a plot-size pill it never
                    had; and a house has both, so it would have grown one too. The designer's
                    word was "instead", and the substitute reading also keeps the row LENGTH
                    stable, which is what the muscle-memory objection was actually about.
                    Land area stays reachable for houses through the area sheet and the ⚙ page,
                    exactly as today. */}
                {!applies(pillCap, 'rooms') && applies(pillCap, 'landArea') && (
                  <button type="button" className={mobilePill(draftAdvFilters.landAreaMin !== '' || draftAdvFilters.landAreaMax !== '')}
                    onClick={() => setFacetSheet('area')}>
                    {t('desk_range_land_area')} ▾
                  </button>
                )}
                <button type="button" className={mobilePill((draftAdvFilters.kinds || []).length > 0)}
                  onClick={() => setFacetSheet('type')}>
                  {t('desk_search_type_label')}{(draftAdvFilters.kinds || []).length ? ` · ${(draftAdvFilters.kinds || []).length}` : ''} ▾
                </button>
              </>)}
              <button type="button" className={mobilePill(entriesView === 'map')}
                onClick={() => changeEntriesView(v => v === 'map' ? 'gallery' : 'map')}>
                <Icon name="map" size={13} /> {t('desk_search_map_pill')}
                {(advFilters.near || advFilters.mapPolygon) ? ` · ${(advFilters.near ? 1 : 0) + ringsFromParam(advFilters.mapPolygon).length}` : ''}
              </button>
              {showPeriod && <PillSheetSelect title={t('desk_search_period_title')} value={entryPeriod} onChange={setEntryPeriod}
                icon={<Icon name="calendar" size={13} />}
                options={[
                  { value: '1d',  label: t('desk_search_period_today'), pillLabel: '1' },
                  { value: '7d',  label: t('desk_search_period_7d'), pillLabel: '7' },
                  { value: '30d', label: t('desk_search_period_30d'), pillLabel: '30' },
                  { value: '90d', label: t('desk_search_period_90d'), pillLabel: '90' },
                  { value: 'all', label: t('desk_search_period_all_time'), pillLabel: '∞' },
                ]} />}
            </div>
          </div>
          {(readoutChips.length > 0 || readoutStranded.length > 0) && (
            <div className="w-full overflow-x-auto md:hidden" style={{ scrollbarWidth: 'none' }}>
              <div className="flex w-max items-center gap-1.5 pb-1 text-xs">
                {[...readoutChips, ...readoutStranded].map(c => (
                  <span key={`mobile:${c.key}`} className="inline-flex min-h-9 items-center gap-1 rounded-full border border-border bg-background px-2.5">
                    <span className={c.stranded ? 'line-through text-muted-foreground' : ''}>{c.label}</span>
                    <button type="button" className="flex h-7 w-7 items-center justify-center rounded-full text-base text-muted-foreground hover:bg-accent hover:text-foreground"
                      title={t('desk_search_remove_filter')} aria-label={`${t('desk_search_remove_filter')}: ${c.label}`}
                      onClick={() => removeChip(c)}>×</button>
                  </span>
                ))}
              </div>
            </div>
          )}
          {/* Desktop row C: sort + period keep their SingleSelects. Status moved into the
              search-tools sidebar (2026-07-06) — it's a refinement, not a primary lens. */}
          <div className="hidden md:flex md:items-center md:gap-2">
            {/* Sort DROPDOWN (owner overhaul 2026-07-23, replacing the §3 segments: "we
                need better sort than this" → all four asks — dropdown form, freshness,
                ₾/m², activity default). activity/price/area/updated push down to the v13
                RPC (SERVER_SORT_OF); ppm2 + fresh sort loaded rows client-side until the
                family's next coordinated bump adds them (0190 B128 note). */}
            {/* 0310: all three dates, both directions, corpus-wide via the RPC. `added`
                REPLACES the legacy `newest` control here (same intent, whole corpus instead
                of the loaded page); `newest` remains an accepted key (B206). Labels and
                grouping now live in deskSortOptions() — shared with the mobile sheet. */}
            {showSort && <SingleSelect value={entrySort} onChange={pickSort} options={deskSortOptions({ allow: sortAllow, feed: feedCat })}
              /* Leads only: warm the page for a sort while the cursor dwells on its row, so
                 the click paints from cache instead of paying the corpus scan (the server
                 single-flights + caches the same query, so a hover can never stampede). */
              onOptionHover={feedCat === 'Leads' ? (v => prefetchLeads({
                // review 2026-09-23: exactly the feed's own args (leadCountArgsFor) with only
                // the sort swapped — it used the LIVE text and omitted `mine`, so the warm
                // landed under a key the click would never read.
                ...leadCountArgsFor(leadArchive), sort: LEADS_SERVER_SORT_OF[v] || null,
              })) : (v => warmFeedPage({ sort: v }))} />}
            {/* Time period — bounds the FETCH window (server `since`), not just what's shown;
                widening it triggers a refetch (see the entryPeriod effect), it doesn't just
                re-filter whatever happened to already be in hand. */}
            {showPeriod && <SingleSelect value={entryPeriod} onChange={setEntryPeriod} compact={isMobile} options={[
              { value: '1d',  label: t('desk_search_period_today'), compactLabel: <><Icon name="calendar" size={14} /> 1</> },
              { value: '7d',  label: t('desk_search_period_7d'), compactLabel: <><Icon name="calendar" size={14} /> 7</> },
              { value: '30d', label: t('desk_search_period_30d'), compactLabel: <><Icon name="calendar" size={14} /> 30</> },
              { value: '90d', label: t('desk_search_period_90d'), compactLabel: <><Icon name="calendar" size={14} /> 90</> },
              { value: 'all', label: t('desk_search_period_all_time'), compactLabel: <><Icon name="calendar" size={14} /> ∞</> },
            ]} />}
          </div>
          {/* + დამატება — SAME placement as Agents/Candidates (owner 2026-07-24): the primary
              filled button at the toolbar row's right end. Defined once as `addButton` above
              (home renders the same one beside its search pill). */}
          {addButton}
        </div>
        )}

        {/* §6 CommandStrip — the cockpit COLLAPSED into one horizontal strip under the search
            bar (design handoff §6; "never a screen swap"). One continuous surface: home shows
            the full cockpit, browse shows THIS strip, and any pill expands the same
            AddEntryTile inline (add buttons + notifications feed) without leaving results.
            Gated exactly like the results grid: not on home, not on special feeds (they own
            their chrome), not while the map is expanded (spatial view eats every side row).
            This strip REPLACES the old first-grid-cell AddEntryTile — the cockpit's collapsed
            form is the one place add/notifications live in browse now. */}
        {/* The strip is GONE entirely (owner 2026-07-23, after the eBay bell ruling emptied
            it to one pill): recents moved INTO the toolbar as a button before the view
            switcher — a whole row for one pill was extra chrome. */}

        {/* C3: rep's in-app access-request notice — above everything, it's a live deal */}
        <AccessRequestsNotice />

        {/* Hints + compiler debug — IN FLOW, never an absolute overlay
            (the old overlay covered the type pills and top cards on narrow screens). */}
        {/* Desktop-only since 2026-07-18: on mobile all of this lives inside the
            full-screen search overlay (hints as rows; the debug panel — whose red
            strikethrough lines agents kept seeing after accidental 🔍 double-taps —
            simply doesn't exist there). */}
        {!isMobile && (searchHints.length > 0 || (searchDebug && entrySearch.trim()) || (liveResidual.length >= 3 && liveResidual !== committedQ)) && (
          <div className="space-y-1 mb-2">
            {/* Commit-to-search discoverability (§5.3): typing only narrows LOADED rows; the
                whole-DB (cross-alphabet) search waits for Enter. Shown while the residual is
                committable but uncommitted — disappears the moment it commits. */}
            {liveResidual.length >= 3 && liveResidual !== committedQ && (
              <div className="text-xs text-muted-foreground pl-2">{t('desk_search_commit_hint')}</div>
            )}
            {/* Completion hints for the last partial word — Tab or click accepts.
                Dashed border = "not applied yet", vs the solid constraint chips. */}
            {searchHints.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {searchHints.map((h, i) => (
                  <button key={h.code}
                    className={pill('none', { door: true, extra: 'border border-dashed border-muted-foreground/40 bg-background text-muted-foreground shadow-sm hover:border-primary/60 hover:text-foreground' })}
                    onClick={() => acceptHint(h)}>
                    {h.insert} → 📍 {h.display}{i === 0 ? <kbd className="ml-1 text-xs opacity-60">Tab</kbd> : null}
                  </button>
                ))}
              </div>
            )}
            {searchDebug && entrySearch.trim() && (
              <div className="rounded-lg border border-border bg-background shadow-md p-2 text-xs font-mono space-y-0.5">
                {compiledQuery.tokens.map(t => (
                  <div key={t.sig}>
                    <span className={dismissedSigs.has(t.sig) ? 'text-red-500 line-through' : 'text-green-600 dark:text-green-400'}>
                      {dismissedSigs.has(t.sig) ? <Icon name="x" /> : <Icon name="check" />}
                    </span>{' '}
                    {t.type} = {JSON.stringify(t.patch)} <span className="text-muted-foreground">← "{t.matched}"</span>
                  </div>
                ))}
                {compiledQuery.tokens.length === 0 && <div className="text-muted-foreground">{t('desk_search_no_recognitions')}</div>}
                {compiledQuery.failures?.map((f, i) => (
                  <div key={i} className="text-amber-600 dark:text-amber-400">? "{f.fragment}" — {f.reason}</div>
                ))}
                <div className="text-muted-foreground">
                  vocab v{compiledQuery.vocabVersion} #{compiledQuery.vocabHash}
                  {' · '}engine v{compiledQuery.engineVersion}{' · '}{compiledQuery.ms} ms
                </div>
              </div>
            )}
          </div>
        )}

        {/* Close/reopen results (actOnRequirement) — used to render inside the filter panel,
            which made a save message look like part of search. */}
        {commitMsg && !SHOW_MAIN_PARSER && <p className="text-xs text-muted-foreground mb-2">{commitMsg}</p>}
        {hasGlow && (
          <p className="text-xs text-amber-600 dark:text-amber-400 mb-2"><IconText>{t('desk_search_sorted_by_match_hint')}</IconText></p>
        )}

        {/* ONE line for the whole filter-state readout (2026-07-06): constraint chips on the
            left, "Showing X of Y · clear filters" pushed right — two stacked single-purpose
            rows wasted a line each. Chips: one per active constraint, whatever set it (typed
            text or the tools sidebar) — the single truthful display of the filter state; ×
            clears exactly that field (and dismisses+logs the search token that set it, so it
            can't re-apply). */}
        {/* Constraint chips MOVED INTO the search field (design skin §1, 2026-07-24) — this
            row keeps only the identity chips (image/street, which aren't panel filters) and
            the honest count readout. On mobile the in-field chips don't render (dead input),
            so chips show here as before. */}
        {/* §1 Phase 1 (2026-07-24): the mobile constraint-chip row was removed — the strip box
            now shows those constraints as inline highlighted keywords (same as desktop). Image
            and street remain their own removable chips (they aren't typed text), and the
            count/percentage summary stays. */}
        {(imageSearch || streetSel || entryFiltersActive) && (
          <div className="flex flex-wrap items-center gap-1.5 mb-2">
            {/* 📷 image-search chip — a removable identity filter, same contract as chips. */}
            {imageSearch && imageSearch !== 'loading' && (
              <span className={`${COUNTER} border border-primary/40 bg-background shadow-sm`}>
                <Icon name="camera" /> {imageSearch.error ? t('desk_search_image_failed') : tLog('desk_search_image_chip', { n: imageSearch.results.length })}
                <button className="text-muted-foreground hover:text-foreground" title={t('desk_search_remove_filter')}
                  onClick={() => setImageSearch(null)}>×</button>
              </span>
            )}
            {/* 🛣️ picked-street chip — removable geo-proximity filter, same contract as 📷. */}
            {streetSel && (
              <span className={`${COUNTER} border border-primary/40 bg-background shadow-sm`}>
                <Icon name="route" /> {streetSel.name_ka}
                <button className="text-muted-foreground hover:text-foreground" title={t('desk_search_remove_filter')}
                  onClick={() => setStreetSel(null)}>×</button>
              </span>
            )}
            {/* ♥/🗄 lens chip — same contract as 📷 and 🛣️ (owner 2026-08-09: "these filters
                aren't showing up there to remove"). A snapshot lens REPLACES the feed, and
                because SPECIAL_FEED suppresses the "showing N of M" readout there was no
                indicator anywhere near the search box that one was on — the only way out was
                `clear all searches`, which discards the agent's whole query as collateral.
                ⚠️ Deliberately only the SNAPSHOT lenses, not every category. Properties/Client/
                Meeting are the ordinary type axis; they already read their state back from the
                facet tree and keep the normal readout, so a chip there would be a second
                control for state that is already visible twice. */}
            {SNAPSHOT_LENS.has(entryTypeFilter[0]) && (
              <span className={`${COUNTER} border border-primary/40 bg-background shadow-sm`}>
                {entryTypeFilter[0] === 'Favorites'
                  ? <>♥ {t('desk_type_favorites')}</>
                  : <><Icon name="eye-off" size={11} /> {t('desk_archive_view')}</>}
                <button className="text-muted-foreground hover:text-foreground" title={t('desk_search_remove_filter')}
                  onClick={() => setEntryTypeFilter([])}>×</button>
              </span>
            )}
            {/* ⚠️ !SPECIAL_FEED: this readout is computed from visibleGroups/allGroups —
                the PROPERTY feed's pipeline. Pool/Meetings/Leads/etc render their own
                feeds with their own counts, so showing this span there reports another
                feed's numbers as if they were this one's ("ნაჩვენებია 0 / 60" over a
                lead grid that failed to load, 2026-07-27). */}
            {/* ── MOVED, 2026-08-02 (owner) ────────────────────────────────────────
                The "showing N of M · −P%" readout used to live HERE, top-right of the
                toolbar, while the headline above the grid printed a second, different
                number ("352 ობიექტი"). Two counts, two places, two corpora — the exact
                shape this panel keeps being bitten by. It now renders once, as the
                headline, next to the grid it describes. See ResultCount below.

                The `clear filters` button went with it and was NOT re-created: the toolbar
                already carries `ყველა ძებნის გასუფთავება` (clear all searches) a few pixels
                away, and two controls that clear the same state is how an agent learns to
                distrust both. One clear, one place. */}
          </div>
        )}

        {/* Amber hidden-for-missing-data line (design §3): its own tinted row, NEVER
            red/destructive — "unknown ≠ no match" is the honesty rule rendered. The button
            scrolls to the possible-matches shelf (0055), which IS the "show anyway" tier. */}
        {!SPECIAL_FEED && !deskHomeActive && advDisplay?.unknown > 0 && (
          <div className="flex items-center gap-2 mb-2 rounded-lg bg-amber-500/10 border border-amber-500/30 px-3 py-1.5 text-xs text-amber-700 dark:text-amber-300">
            <Icon name="info" size={13} className="shrink-0" />
            <span className="min-w-0">{tLog('desk_hidden_unknown_line', { n: advDisplay.unknown })}</span>
            {/* 2026-09-24: the shelf is fetched on demand now, so "show" must also be the demand —
                offered while the shelf is not yet asked for, it asks, then scrolls to where it lands. */}
            {(shownPossibleGroups.length > 0 || !shelfWanted) && (
              <button type="button"
                className={pill('none', { door: true, extra: 'ml-auto shrink-0 border border-amber-500/50 hover:bg-amber-500/15 transition-colors' })}
                onClick={() => { setShelfWanted(true); (possiblesRef.current || shelfNodeRef.current)?.scrollIntoView({ behavior: 'smooth', block: 'start' }) }}>
                {t('desk_hidden_unknown_show')}
              </button>
            )}
          </div>
        )}

        {/* 🗑 B705 — the street-proximity note was DELETED with `streetCoreToken`.
            It existed (owner request 2026-07-24) because a street search was a ~700m GEO
            filter that could silently show listings on OTHER streets, so it counted how many
            were "exactly on" the street by substring-matching the name's identifying token
            against the address. That count was the bug the owner reported: `ნუცუბიძის` also
            matches `ნუცუბიძის პლატო`, so plateau flats were reported as on the street.
            A street search is now an EXACT `p_street_ids` filter over `property_street`, so
            every row in the result IS on the street and there is nothing to disclaim. The
            honest version of this note is no note. The `desk_street_none_exact` /
            `_some_exact` / `_all_exact` i18n keys are now unused. */}

        {/* Too-broad banner (design ruling Q1): committed + zero facets + >300 results →
            invite refinement in the FIXED order rooms → district → budget. Never blocks
            the grid; "show all anyway" dismisses for this committed context.
            ⚠️ "Zero facets" means zero refinements of ANY kind — the client-side
            agent/status/city dropdowns narrow the grid without touching advFilters or
            listingTotal, so without these gates the banner fired over an agent-filtered
            7-row view claiming the unfiltered server total (owner report 2026-07-23). */}
        {/* COMMITTED searches only (owner ruling 2026-07-23): a committed broad search is
            a real "now what?" moment; firing on casual browsing was noise. committedBrowse
            alone (browse-everything) doesn't count — a committed TEXT query does. */}
        {!SPECIAL_FEED && !deskHomeActive && !tooBroadDismissed && !!committedQ
          && !advFiltersActive(advFilters)
          /* `=== 'active'` since B576 (2026-08-16) — the neutral status. As `=== 'all'` this
             term went permanently false with the retirement of that state, which would have
             silently retired the whole too-broad banner along with it.
             ⚠️ A PLAIN block comment, not the braced JSX comment form: this sits inside an
             expression, where the braced form parses as an object literal. And do not spell
             that form out literally here — its closing token would end THIS comment early,
             a parse error `vite build` accepted and only the no-undef gate caught. */
          && !entryAgentFilter.length && entryStatusFilter === 'active' && entryCityFilter === 'all'
          && listingTotal > 300 && visibleGroups.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-2 rounded-lg border border-border bg-card/60 px-3 py-1.5 text-xs">
            <span className="text-muted-foreground">{tLog('desk_too_broad_line', { n: listingTotal.toLocaleString('en-US') })}</span>
            {[
              { key: 'rooms', label: t('desk_range_rooms'), sheet: 'rooms' },
              { key: 'district', label: t('desk_facet_location'), sheet: 'location' },
              { key: 'budget', label: t('desk_range_price_header'), sheet: 'price' },
            ].map((qp, i) => (
              <button key={qp.key} type="button"
                className={pill('none', { door: true, extra: 'border border-border bg-background hover:border-primary/50 transition-colors' })}
                onClick={() => { if (isMobile) setFacetSheet(qp.sheet); else setFiltersOpen(true) }}>
                <span className="text-muted-foreground">{i + 1}.</span> {qp.label}
              </button>
            ))}
            <button type="button" className="ml-auto shrink-0 underline decoration-dotted text-muted-foreground hover:text-foreground"
              onClick={() => setTooBroadDismissed(true)}>{t('desk_too_broad_show_all')}</button>
          </div>
        )}

        {/* Phone→person header: a phone query surfaces WHO above WHAT (KNOWLEDGE_MODEL §0);
            deep-links into PersonModal (cluster-aggregated, dup card included). */}
        {/* Category chips rail REMOVED same day it shipped (2026-07-22 second user ruling):
            categories live in the Detailed-Search sidebar as its FIRST facet — the true
            eBay left-rail model — not as horizontal tab-chips stacking rows above the
            feed (the meetings feed showed where that road ends). typeCounts + the
            desk.category_switched telemetry moved there with it (AdvancedSearchPanel
            `category` prop). */}
        <PhonePersonHeader phones={phoneQueries} onOpenPerson={setViewingPartyId} />

        {/* Three-column body (2026-07-06): LEFT = search-tools sidebar (collapses to a thin
            rail via its own ◂ arrow; the rail's ▸ button reopens it), MIDDLE = the cards,
            RIGHT = Activity/Notifications/Meetings + Agent ranking. Below lg everything stacks:
            tools (or rail), cards, then side panels. */}
        <div className="flex flex-col md:flex-row gap-4 items-start">
        {/* Map view is spatial — the left search-tools panel and right activity/ranking
            column just eat into the one thing that matters there (screen width for the
            map itself). User-controlled via the ⤢/⤡ button next to the view switcher
            (not automatic on entering map view) — both collapse away entirely while
            expanded, restored the moment it's toggled back off. Nothing above (header/
            toolbar) is touched, only this row's sides. */}
        {/* Per-category sidebar scope (§12 phase one, 2026-07-17 user ruling): Meeting/Pool/
            Business feeds keep their OWN controls — no facet sidebar, not even the rail.
            The Desk home renders NO search chrome at all (second 2026-07-17 ruling — even the
            thin rail read as clutter): entry into the searchable feed is the sections' own
            ყველას ნახვა / Browse buttons. Dropping the home rail also un-truncates Agent
            Ranking: the right aside height-matches the row's FIRST child (see rightAsideRef),
            which on home is now the cards column, so ranking runs the full page. */}
        {!(entriesView === 'map' && mapExpanded) && !deskHomeActive
          && !['Business'].includes(entryTypeFilter[0])   /* Meeting rejoined 2026-07-22 (Category-only); LEADS rejoined 2026-07-23 with the FULL panel — owner: "lead category is treated like properties, same filter search bar"; every facet genuinely applies there via the 0187 leads fork of the search RPC */
          && (filtersOpen ? (
          /* Width bumped one step (owner, 2026-08-19: "preserve width and also make it little
             wider if design allows") — 15rem→16rem, and 16rem→18rem at xl, where there is room
             to spare. The panel's own scroller (see the desktop return in AdvancedSearchPanel)
             is the other half of that request. */
          <StickyAside className="w-full md:w-64 xl:w-72 shrink-0 desk-mode-enter">
            <AdvancedSearchPanel bookScope={listingsScope} features={features} filters={advFilters} setFilters={setAdvFilters} listings={panelListings} facetCounts={facetCounts} rangeFacets={panelRangeFacets} kindFeatureCaps={kindFeatureCaps} conditionLadder={conditionLadder} poolCounts={leadSearchMeta?.pool_counts || null}
              statusFilter={entryStatusFilter} onStatusChange={setEntryStatusFilter}
              onCollapse={() => setFiltersOpen(false)} entryType={entryTypeFilter[0] || ''}
              resultsCount={sheetResultsCount} readout={advDisplay} poolSwitch={poolSwitch && { ...poolSwitch, toTotal: poolTotal }} onUndoPoolSwitch={undoPoolSwitch} clientLens={clientLens} clientFill={clientFillProp} sort={entrySort}
              geo={panelGeo}
              agentFilter={deskAgentFilter}
              category={{ value: entryTypeFilter, set: setEntryTypeFilter, counts: typeCounts, meeting: { value: meetingStatusFilter, set: setMeetingStatusFilter, counts: meetingCounts, settled: viewingsSettled }, warmStatus: v => warmFeedPage({ status: v }), lead: { value: leadArchive, set: setLeadArchive, prefetch: leadHoverPrefetch, prefetchOther: leadPrefetchOther, count: leadCountActive, archiveCount: leadCountArchive, worked: { value: leadWorked, set: setLeadWorked } }, clientLead: { count: clientLeadCount, capped: clientLeadCapped }, workCounts, favCount: favRows ? favRows.size : null, archCount: archRows ? archRows.size : null, kindCounts: facetCounts?.kinds ?? null, countsProvisional, propertyTotal: treePropertyTotal, workPropertyTotal: propertyTotalForTree, scope: { value: listingsScope, set: setScopePref, canAll: canSeeAllScope && canScopeToSelf, allLabel: officeScopeLabel } }} />
          </StickyAside>
        ) : (
          // Collapsed: not gone — a thin rail whose ▸ reopens the panel. Vertical label +
          // constraint-count badge so the hidden filter state stays visible at a glance.
          // Desktop only (2026-07-18): on mobile the command strip's Detailed-search pill
          // is the opener, so the rail would be a duplicate full-width bar.
          <aside className="hidden md:block shrink-0 md:sticky md:top-2">
            <button title={t('desk_search_open_tools_tooltip')} aria-label={t('desk_search_open_tools_tooltip')}
              onClick={() => setFiltersOpen(true)}
              className="flex md:flex-col items-center justify-center gap-2 w-full md:w-7 md:h-56 rounded-xl border border-border bg-card py-2 text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors">
              <span className="text-sm leading-none">▸</span>
              <span className="text-xs font-medium md:[writing-mode:vertical-rl] md:rotate-180">{constraintChips.length ? tLog('desk_search_tools_rail_label', { n: constraintChips.length }) : t('desk_search_tools_bare')}</span>
            </button>
          </aside>
        ))}
        <div className="flex-1 min-w-0 w-full">
        {/* Skeleton grid under the FIRST load (design skin §4): shimmer instead of a blank
            column while nothing is in hand yet. Only when truly empty — once any rows are
            loaded, SWR refreshes happen behind real cards, never behind shimmer. */}
        {!SPECIAL_FEED && histLoading && !idSearchBusy && history.length === 0 && visibleGroups.length === 0 && (
          <div className="grid gap-3 items-start [grid-template-columns:repeat(auto-fill,minmax(min(340px,100%),1fr))]">
            {Array.from({ length: 6 }, (_, i) => <SkeletonCard key={i} />)}
            <SearchElapsedNote />
          </div>
        )}
        {!SPECIAL_FEED && history.length === 0 && !histLoading && !idSearchBusy && <p className="text-sm text-muted-foreground">{t('desk_empty_no_entries')}</p>}
        {/* Reach-back in flight and nothing to show yet — shimmer, NOT the zero state.
            The id/phone/image reach-back resolves DB-wide and can take seconds; rendering
            "0 results" while it runs tells the operator a lie they act on (they abandon). */}
        {!SPECIAL_FEED && idSearchBusy && visibleGroups.length === 0 && (
          <div className="grid gap-3 items-start [grid-template-columns:repeat(auto-fill,minmax(min(340px,100%),1fr))]">
            {Array.from({ length: 3 }, (_, i) => <SkeletonCard key={i} />)}
            <SearchElapsedNote />
          </div>
        )}
        {/* ── "We could not look" ≠ "there is nothing" (2026-07-26) ────────────────
            The search call itself failed. The zero state below asserts a fact about the
            corpus ("no results for X", "this id was never captured") and an operator ACTS
            on it — they go re-capture the listing, or tell a client we don't have it. We
            don't know that. So this REPLACES the zero state rather than sitting above it,
            and it offers the only useful action: look again. */}
        {!SPECIAL_FEED && !idSearchBusy && idSearchFault?.kind === 'failed' && visibleGroups.length === 0 && (
          <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-4 max-w-md space-y-2">
            <p className="text-sm font-medium text-foreground"><Icon name="alert-triangle" /> {t('desk_search_failed_title')}</p>
            <p className="text-xs text-muted-foreground">{t('desk_search_failed_hint')}</p>
            <button className={btn('outline')} onClick={() => setIdSearchRetry(n => n + 1)}>{t('desk_search_retry')}</button>
          </div>
        )}
        {/* Results ARE showing, but not all of them loaded. Same rule, milder case: the
            list below is real, it is just not the whole answer — say so instead of letting
            a short list read as a complete one. */}
        {!SPECIAL_FEED && !idSearchBusy && idSearchFault?.kind === 'partial' && (
          <div className="mb-3 rounded-xl border border-amber-500/40 bg-amber-500/5 px-3 py-2 flex items-center gap-2 flex-wrap">
            <span className="text-xs text-foreground">
              <Icon name="alert-triangle" /> {tLog('desk_search_partial', { n: idSearchFault.failed, m: idSearchFault.total })}
            </span>
            <button className="text-xs underline decoration-dotted text-muted-foreground hover:text-foreground"
              onClick={() => setIdSearchRetry(n => n + 1)}>{t('desk_search_retry')}</button>
          </div>
        )}
        {/* ── 🔴 SEARCHING IS NOT NOTHING (owner, 2026-08-20) ──────────────────────────────
            *"it says nothing found when actually there are listings"* — and the Desk was not
            wrong about the data, it was answering before the data arrived. Applying a filter
            invalidates the snapshot, so `visibleGroups` drops to 0 immediately while the
            request runs; on the owner's admin/all-offices scope that is ~3 s per page, long
            enough to read "ამ ფილტრებით არაფერი მოიძებნა", believe it, and change the filter.
            The skeleton three lines up already does this for the identity reach-back, for the
            reason written there — "rendering '0 results' while it runs tells the operator a lie
            they act on (they abandon)". The ordinary filtered search, which every agent uses
            constantly, never got the same courtesy. It does now, with the same shimmer and the
            same elapsed note, so a slow search LOOKS slow instead of looking empty.
            ⚠️ Deliberately NOT gated on `history.length === 0` like the first-load skeleton
            above: the whole point is the case where rows are already in hand and a new
            question is being asked over them. */}
        {!SPECIAL_FEED && !idSearchBusy && !idSearchFault && listingsFetching && visibleGroups.length === 0 && (
          <div className="grid gap-3 items-start [grid-template-columns:repeat(auto-fill,minmax(min(340px,100%),1fr))]" style={holdHeight ? { minHeight: holdHeight } : undefined}>
            {Array.from({ length: 3 }, (_, i) => <SkeletonCard key={i} />)}
            <SearchElapsedNote />
          </div>
        )}
        {!SPECIAL_FEED && !idSearchBusy && !idSearchFault && !listingsFetching && history.length > 0 && visibleGroups.length === 0 && (
          /* ── 🚨 ZERO RESULTS MUST NOT READ AS A HEADING OVER RESULTS ──────────────────────
             Owner, 2026-08-19: "this message must be in the middle to be shown so user knows
             it's not found and what's shown is not a real result.. now it's really misleading."
             On a filters-only search this block rendered a bare
             `<p>ამ ფილტრებით არაფერი მოიძებნა.</p>` in the parent's small muted type, hard
             left — and the possible-matches shelf then drew 29 CARDS directly beneath it. Those
             cards are the SHELF (properties the database can neither confirm nor rule out) and
             they look identical to results, so an agent reads 29 results where there are none.
             Centred and given real weight now, and when the shelf is non-empty it says outright
             that what follows is not a result set. The shelf keeps its own header; this is the
             line that stops the two being read as one list. */
          <div className="text-sm text-muted-foreground space-y-4 py-6 text-center max-w-xl mx-auto">
            {/* eBay-style zero state (2026-07-18): a clear "0 results for X" headline, then the
                honest coverage line (§11 — says what WAS searched so "not found" ≠ "doesn't
                exist"), then Related searches as chips. Replaces the flat "No entries…" line +
                inline red-button suggestions. */}
            <div className="space-y-1">
              {entrySearch.trim() ? (<>
                <p className="text-base font-semibold text-foreground">
                  {t('desk_search_zero_results')} <span>“{entrySearch.trim()}”</span>
                </p>
                {/* ID-chip zero state (2026-07-22): a recognized platform id that finds
                    nothing means the listing WAS understood but is NOT CAPTURED — say
                    exactly that, or "0 results" reads as "the search is broken" (user
                    report: bare korter id 792347, legit id, listing never captured).
                    Recognition succeeded; coverage failed. Different fact, different line. */}
                {listingIdQueries.length > 0 ? (
                  /* ZeroState (a) recognized-not-captured (design §5): the dashed capture
                     card — recognition succeeded, coverage failed, and the way forward is a
                     button, not a shrug. "Open source" only when the query WAS a URL (a bare
                     id can't honestly reconstruct one across platforms). */
                  <div className="mt-2 rounded-xl border border-dashed border-border p-4 max-w-md space-y-2">
                    <p className="text-foreground font-medium"><Icon name="file-text" /> {tLog('desk_search_id_not_captured', { id: listingIdQueries.join(', ') })}</p>
                    <div className="flex items-center gap-2 pt-1">
                      <button className={btn('primary')} onClick={() => openModal('property')}>{t('desk_zero_capture_now')}</button>
                      {/^https?:\/\//.test(entrySearch.trim()) && (
                        <a className={btn('outline')} href={entrySearch.trim()} target="_blank" rel="noreferrer">{t('desk_zero_open_source')}</a>
                      )}
                    </div>
                  </div>
                ) : (
                  <p>{t('desk_search_no_exact')}</p>
                )}
              </>) : (
                /* Same weight as the typed-query headline above. This was the only branch left
                   in small muted type — and it is exactly the branch the owner was looking at. */
                feedEmptyKind({ error: listingsError, fetching: listingsFetching, total: listingTotal, rows: listingEntries.length }) === 'error'
                  ? (
                    /* F-021: a timed-out or failed search. Never the empty-state copy: the DB may hold
                       every answer and the agent must not read a 500 as "none". */
                    <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-4 max-w-md space-y-2">
                      <p className="text-base font-semibold text-foreground">{t('desk_search_failed')}</p>
                      <p className="text-xs text-muted-foreground break-all">{listingsError}</p>
                      <button className={btn('primary')} onClick={() => setListingsRetry(n => n + 1)}>{t('desk_retry')}</button>
                    </div>
                  )
                  : <p className="text-base font-semibold text-foreground">{t('desk_empty_no_match_filters')}</p>
              )}
              {/* Only when cards really do follow. Announcing a shelf that is not there would
                  be the same class of lie pointing the other way. */}
              {shownPossibleGroups.length > 0 && (
                <p className="pt-1 text-xs leading-5 text-amber-600 dark:text-amber-400">
                  {t('desk_zero_shelf_follows')}
                </p>
              )}
            </div>
            {/* Cap-tripwire notice (B36): only when nightly_dedup.sh has flagged the proposals
                feed near its 500-row cap. On an active search, all-history client loading is
                capped, so an older client can be silently absent — say so here rather than let
                "0 results" read as "this client doesn't exist". Same signal as the Admin→Dedup
                badge, surfaced where the agent actually searches. */}
            {capTripwire && histAllTime && (
              <div className="rounded-lg border border-sky-500/40 bg-sky-500/10 p-3 text-xs text-sky-700 dark:text-sky-300">
                <Icon name="trending-up" /> {t('desk_search_cap_notice')}
              </div>
            )}
            {/* Widen the period (a feed-size control, not a search scope — the record may be
                older than the window; one click refetches with an all-time `since`) + the
                coverage line. */}
            {entrySearch.trim() && (
              <div className="space-y-2">
                {entryPeriod !== 'all' && (
                  <button className={btn('outline')} onClick={() => {
                    logSearchQuery({ rawText: entrySearch.trim(), compiled: [], residual: '', dismissed: [], context: { source: 'desk', event: 'widen_all_time' } })
                    setEntryPeriod('all')
                  }}>{t('desk_search_widen_all_time')}</button>
                )}
                <p className="text-xs">{t('desk_search_coverage')}</p>
              </div>
            )}
            {/* Related searches (eBay pattern): the CACHED suggest response as chips — a
                query-refinement escape hatch, and the duplicate-client guard of last resort
                (an agent who ignored the dropdown gets caught here before creating a dup). */}
            {entrySearch.trim() && suggestResp && suggestResp.forQ === entrySearch.trim()
              && ((suggestResp.clients?.length || 0) + (suggestResp.businesses?.length || 0) + (suggestResp.complexes?.length || 0) + (suggestResp.streets?.length || 0)) > 0 && (
              <div className="space-y-2">
                <p className="font-semibold text-foreground">{t('desk_search_related')}</p>
                <div className="flex flex-wrap gap-2">
                  {(suggestResp.clients || []).map((c, i) => (
                    <button key={`zcl${c.id}`} className={chip}
                      onClick={() => acceptSuggestion('client', i, c.name || entrySearch.trim(), { category: 'Client' })}><Icon name="user" /> {c.name || t('desk_suggest_unnamed_client')}</button>
                  ))}
                  {(suggestResp.businesses || []).map((b, i) => (
                    <button key={`zbz${b.id}`} className={chip}
                      onClick={() => acceptSuggestion('business', i, b.name || entrySearch.trim(), { category: 'Business' })}><Icon name="store" /> {b.name}</button>
                  ))}
                  {(suggestResp.complexes || []).map((c, i) => (
                    <button key={`zcx${c.id}`} className={chip}
                      onClick={() => acceptComplex(c, i)}><Icon name="building" /> {getLanguage() === 'ka' ? (c.name_ka || c.name) : c.name}{(getLanguage() === 'ka' ? (c.name_ka ? c.name : null) : c.name_ka) ? <span className="text-muted-foreground ml-1">· {getLanguage() === 'ka' ? c.name : c.name_ka}</span> : null}</button>
                  ))}
                  {/* 🚨 THE SECOND STREET RENDERER, and the one an agent actually clicks
                      (B705). The suggest list is built in TWO places — the `out` rows above
                      (which call acceptStreet) and this chip row — and only this one was
                      reachable from the dropdown. It called acceptSuggestion with
                      `geoNoise: true`, i.e. it dumped the street's NAME into a text search
                      and let the client decide what "on this street" meant. That is the
                      substring path: `ნუცუბიძის` matches `ნუცუბიძის პლატო` and
                      `ნუცუბიძის ფერდობი`, so picking შ. ნუცუბიძის ქ. returned the plateau.
                      Two renderers for one concept, drifting — the failure this file names
                      repeatedly. Both now call acceptStreet, which filters on `s.id`
                      (already present here: the key is `zst${s.id}`) through p_street_ids,
                      the same question the sidebar facet asks. One handler, one meaning. */}
                  {(suggestResp.streets || []).map((s, i) => (
                    <button key={`zst${s.id}`} className={chip}
                      onClick={() => acceptStreet(s, i)}><Icon name="route" /> {s.name}</button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* 🔴 B111's reveal nudge is REMOVED FROM THE DESK — owner, 2026-09-04, pointing at
            it on his phone: "let's remove this message completely for now, I'll add it if
            needed later." So this is a mount removed, not a feature deleted: OpenTouchNudge,
            getOpenTouches, the AttemptOutcome one-tap buttons and every attempt row still
            exist and still record outcomes wherever else they are reached. Putting the strip
            back is restoring this one line.
            ⚠️ Do NOT "clean up" the component or its endpoint as dead code on the strength of
            an unused-export sweep — they are parked, not gone, and the original ruling behind
            them (2026-07-22: "human behavior follows reminders far more reliably than good
            intentions") has not been withdrawn, only this placement.
        <OpenTouchNudge onOpen={id => openListingCard({ id, isOwn: false })} /> */}

        {/* Meeting follow-up nudges (0427) — same placement rule as B111 above: the
            reminder lives where the work happens, renders nothing when nothing is
            owed, and each row is 1–2 taps from resolved (it opens the meeting's own
            status/reason chips). Data comes from the Desk-level fetch the bell badge
            already paid for. */}
        {/* ⚠️ PHONE HOME: search + add FIRST, then a one-line "next up" (Desk home audit 2026-10-21 - on an admin phone
            the search started 937 px down, the agenda ~6 screens and the worker ~8 screens). The reminder banners below
            stay exactly as they are - critical reminders are never collapsed (owner ruling) - and the sections keep their
            order; the strip only points into them. Desktop keeps the search row inside the cockpit. */}
        {phoneHomeTop && (
          <div className="space-y-2 mb-3">
            <div className="flex items-center gap-2" onClickCapture={homeClick('quick_actions')}>
              {searchPill}
              {addButton}
            </div>
            <div className="flex gap-2 overflow-x-auto no-scrollbar text-xs" aria-label={t('desk_next_up')}>
              <button type="button" onClick={() => jumpTo('desk-home-meetings')}
                className="shrink-0 rounded-full border border-border bg-card px-3 py-1.5 text-left">
                <Icon name="calendar" size={12} className="mr-1 text-muted-foreground" />
                {nextMeetingText}
              </button>
              {workerSummary && (
                <button type="button" onClick={() => jumpTo('desk-worker')}
                  className={`shrink-0 rounded-full border px-3 py-1.5 ${workerSummary.paused || !workerSummary.pcOnline
                    ? 'border-amber-500/50 text-amber-700 dark:text-amber-400' : 'border-border text-emerald-600 dark:text-emerald-400'}`}>
                  {workerSummary.paused ? t('my_worker_now_paused') : t('my_worker_now_running')}
                  {' · '}{workerSummary.pcOnline ? t('my_worker_pc_online') : t('my_worker_pc_offline')}
                </button>
              )}
            </div>
          </div>
        )}
        <MeetingNudgesBanner data={meetingNudges} onOpenMeeting={openNudgeMeeting}
          onRetry={() => getMeetingNudges().then(setMeetingNudges).catch(() => setMeetingNudges(prev => (prev && !prev.failed) ? prev : { failed: true }))} />

        {/* Somebody filled the vacancy form and nobody has called them (owner 2026-09-09).
            Same placement rule as the meeting nudges directly above: the reminder lives where
            the work day happens, renders nothing when nothing is owed, and a row is one tap
            from the candidate's own interview section. Management only — the capability is
            evaluated HERE rather than inside the banner, so there is one gate per surface and
            it is visible at the call site. */}
        <NewApplicantsBanner enabled={can(role, 'tab.candidates')}
          onOpenCandidate={id => window.dispatchEvent(new CustomEvent('sentavus:open-candidate', { detail: { id } }))} />

        {/* The development itself, above the flats in it (owner 2026-09-10: "complex details on
            first place and then apartments below"). BELOW the two nudge banners on purpose —
            those belong to the agent's day, this one belongs to the search, and a filter must
            never hide a reminder. Draws only for exactly ONE committed complex; a developer
            family or an empty filter has no single subject. ✕ goes through removeChip, so the
            card and the chip bar share one removal definition. */}
        {headerComplexId && complexRows[headerComplexId] && (
          <ComplexHeaderCard complex={complexRows[headerComplexId]} lang={getLanguage()} t={t}
            districtName={code => { const d = districts.find(x => x.code === code); return d ? (getLanguage() === 'ka' ? (d.name_ka || d.name_en) : (d.name_en || d.name_ka)) || code : code }}
            onRemove={() => removeChip({ arrayKey: 'complexIds', value: headerComplexId })}
            onDeveloper={dev => acceptComplexFamily(dev, 0)}
            onShowOnMap={entriesView === 'map' ? null : () => changeEntriesView('map')} />
        )}


        {/* ⚠️ query = the LANDED text, never entrySearch (audit 2026-10-29 FD-2): with no structured filter LeadsFeed runs this
            prop as the search, so the live box text ran the feed per keystroke while the rail/meta/URL (leadCountArgsFor) still
            described the landed search. The feed answers on Enter, like the book feed (2026-08-05). */}
        {SPECIAL_FEED && entryTypeFilter[0] === 'Leads' && <LeadsFeed page={entryPage} onPage={setEntryPage} agentId={myAgentId} query={landedSearch}
          advFilters={serverAdvFilters} sort={LEADS_SERVER_SORT_OF[entrySort] || null}
          /* The toolbar period pill bounds THIS feed too (2026-07-31). It was rendered above
             the Leads category and wired only to the property pipeline, so it looked live and
             bounded nothing — every leads query ran all-time. */
          since={entrySince}
          archive={leadArchive} onArchiveChange={setLeadArchive} view={entriesView}
          mine={leadWorked} onMineChange={setLeadWorked}
          onSnapshot={() => setLeadCountTick(t => t + 1)}
          districts={districts} features={features} onCardClick={handleCardClick}
          /* B649 (owner 2026-08-12): "click on dot pin it wont open property modal — it shows on
             hover price and address but wont open on click." Both halves of that sentence are the
             proof: EntriesMapView binds `mouseover` unconditionally and `click` behind
             `if (onPinClick)`, so without this prop the tooltip works and no click handler is ever
             attached. LeadsFeed forwarded it correctly; this call site simply never passed it,
             while the browse feed below always did.
             ⚠️ FIFTH instance of one trap on this component — LeadsFeed's own header enumerates
             the first four (♥ favorites → the action row → clearByListing → the data behind them).
             Forwarding a prop is not the same as the prop being passed. B607 is the real fix. */
          onPinClick={id => openListingCard({ id, isOwn: false })}
          /* ⤢ map enlarger (owner 2026-08-16: "leads category map has no enlarger like our
             works map has") — SIXTH instance of the trap B649's note above names: LeadsFeed
             forwards mapExpanded/onToggleMapExpand correctly and has since 2026-08-05, but
             this call site never passed them, so EntriesMapView's `{onToggleExpand && …}`
             gate hid the button. Same parent state as the browse map below, which is what
             makes the chrome-hiding gates (`entriesView==='map' && mapExpanded`) work here
             too. Forwarding a prop is not the same as the prop being passed; B607. */
          mapExpanded={mapExpanded} onToggleMapExpand={() => setMapExpanded(v => !v)}
          favorites={favSet} onToggleFavorite={toggleFavorite}
          archived={archSet} onToggleArchive={toggleArchive}
          /* ⚠️ THE SAME PROPS THE PROPERTY FEED PASSES (2026-07-25). HistoryItem renders the
             cooperation / terms / meeting / share row ONLY when at least one of these
             handlers is truthy (HistoryCards:1002), so omitting them is not a no-op with a
             sane default — it silently opts the whole feed out of every action, exactly the
             way LeadsFeed lost the ♥ by not forwarding favorites. Leads are where an agent
             MUST be able to do this: since 0205, cooperation / agent-recorded restrictions /
             meeting are the only things that mark a lead `working`. */
          coopsByProperty={coopsByProperty} defectsByProperty={defectsByProperty} agentFactsByProperty={agentFactsByProperty} distinctByProperty={distinctByProperty} devCardsByProperty={devCardsByProperty} sellersByProperty={sellersByProperty} coopOffersByProperty={coopOffersByProperty} sharesByProperty={sharesByProperty} restrByListing={restrDisplayByListing} clearByListing={clearByListing}
          onOpenCooperation={setDeskCoop} onAddMeeting={onAddMeetingStable} onCreateContract={onCreateContractStable}
          onShare={setShareSeed} onRepost={setDeskRepost} onOpenTerms={setDeskTerms}
          /* ⚠️ And the maps above must actually be FILLED for this feed (2026-08-02). Passing
             them was necessary but not sufficient: this feed owns its rows, so their ids never
             reached the fetches that populate them and every lead card rendered "no cooperation
             / no restrictions" as a fact. onVisibleIds reports them to the one loader. */
          onVisibleIds={onFeedVisible} />}

        {/* Client leads (0462/0463): unclaimed DEMAND inside the Leads pool. Its own feed
            for the same reason Business has one — a requirement has no address, photo or
            coordinates, so the property card pipeline would render a column of empty frames
            and read as broken data rather than as a different kind of object.
            ⚠️ It takes the SHARED filter vocabulary (district / rooms / price / intent), which
            is the whole reason this belongs in the same pool: one query, both sides of the
            market. It does NOT take the portal/source facet — a client has no portal. */}
        {SPECIAL_FEED && entryTypeFilter[0] === 'ClientLeads' && <ClientLeadsFeed
          agentId={myAgentId}
          /* Owner ruling 2026-08-07: reviewing a lead is NOT claiming it. Management gets
             a look-without-taking door; `privileged` is the tier list already used for
             every other management affordance on this surface, so this cannot drift from
             them. Agents are unaffected — for them the only door is still the pick-up. */
          canReviewWithoutClaim={privileged}
          {...clientLeadFilters}
          ignoredFilters={clientLeadIgnored}
          districts={districts} features={features}
          onSnapshot={(n, capped) => { setClientLeadCount(n); setClientLeadCapped(capped) }}
          /* Picking one up moves it into that agent's own work, so the pool it just left
             must stop claiming it. The feed drops the row locally; this keeps the rail's
             number honest without a refetch that would read as a flicker. */
          onPickedUp={() => setClientLeadCount(n => (n == null ? null : Math.max(0, n - 1)))} />}

        {/* Business: exclusive feed of business-for-sale assets — they can't ride the property
            pipeline (their host property rows have no listing → no card; asset_type='business'
            is filtered out of agent-listings by design, 0075). */}
        {SPECIAL_FEED && entryTypeFilter[0] === 'Business' && <BusinessFeed query={entrySearch} />}

        {/* ♥ Favorites (0191): renders from the SNAPSHOT rows alone — zero core reads;
            the snapshot is what you favorited (may age), the lineage modal (click) shows
            live truth. Unfavoriting here is the same optimistic toggle as the card ♥. */}
        {entryTypeFilter[0] === 'Favorites' && (
          (favRows?.size ?? 0) === 0
            ? <p className="text-sm text-muted-foreground">{t('desk_fav_empty')}</p>
            : <div className="grid gap-3 items-start [grid-template-columns:repeat(auto-fill,minmax(min(340px,100%),1fr))]">
                {sortedFavRows.map(f => {
                  const s = f.snapshot || {}
                  return (
                    <div key={f.property_id} className="relative rounded-lg border border-border bg-card p-3 cursor-pointer hover:border-primary/40 transition-all"
                      onClick={() => f.listing_id && openListingCard({ id: f.listing_id, isOwn: false })}>
                      <button type="button" className="absolute z-10 top-2 right-2 rounded-full px-1.5 py-1 text-sm leading-none bg-rose-500/20 text-rose-500"
                        title={t('desk_fav_remove')}
                        onClick={e => { e.stopPropagation(); toggleFavorite({ property_id: f.property_id }) }}>♥</button>
                      <div className="flex gap-2.5">
                        {s.thumb
                          ? <img src={s.thumb} alt="" className="w-20 h-20 rounded-md object-cover shrink-0" />
                          : <div className="w-20 h-20 rounded-md bg-muted shrink-0" />}
                        <div className="min-w-0 text-sm space-y-0.5">
                          <p className="font-medium truncate">{s.address || '—'}</p>
                          {s.price != null && <p className="font-semibold">{Number(s.price).toLocaleString('en-US')} {s.currency || ''}{s.intent === 'rent' ? '/mo' : ''}</p>}
                          <p className="text-xs text-muted-foreground truncate">
                            {[s.rooms != null ? tLog('desk_entry_rooms_abbrev', { rooms: s.rooms }) : null,
                              s.area_sqm != null ? tLog('desk_entry_area_abbrev', { area: s.area_sqm }) : null,
                              f.created_at ? new Date(f.created_at).toLocaleDateString() : null].filter(Boolean).join(' · ')}
                          </p>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
        )}

        {/* 🗄 Archive (0375): the Favorites block's twin, on purpose — same snapshot-only
            render, same optimistic un-toggle. The note is load-bearing: an agent must never
            believe this took the property off anyone else's desk. */}
        {entryTypeFilter[0] === 'Archived' && (
          (archRows?.size ?? 0) === 0
            ? <p className="text-sm text-muted-foreground">{t('desk_archive_empty')}</p>
            : <>
              <p className="text-xs text-muted-foreground mb-2">{t('desk_archive_note')}</p>
              <div className="grid gap-3 items-start [grid-template-columns:repeat(auto-fill,minmax(min(340px,100%),1fr))]">
                {[...archRows.values()].map(f => {
                  const a = f.snapshot || {}
                  return (
                    <div key={f.property_id} className="relative rounded-lg border border-border bg-card p-3 cursor-pointer hover:border-primary/40 transition-all"
                      onClick={() => f.listing_id && openListingCard({ id: f.listing_id, isOwn: false })}>
                      <button type="button" className="absolute z-10 top-2 right-2 rounded-full px-1.5 py-1 leading-none bg-sky-500/20 text-sky-500"
                        title={t('desk_archive_remove')}
                        onClick={e => { e.stopPropagation(); toggleArchive({ property_id: f.property_id }) }}>
                        <Icon name="eye-off" size={14} />
                      </button>
                      <div className="flex gap-2.5">
                        {a.thumb
                          ? <img src={a.thumb} alt="" className="w-20 h-20 rounded-md object-cover shrink-0" />
                          : <div className="w-20 h-20 rounded-md bg-muted shrink-0" />}
                        <div className="min-w-0 text-sm space-y-0.5">
                          <p className="font-medium truncate">{a.address || '—'}</p>
                          {a.price != null && <p className="font-semibold">{Number(a.price).toLocaleString('en-US')} {a.currency || ''}{a.intent === 'rent' ? '/mo' : ''}</p>}
                          <p className="text-xs text-muted-foreground truncate">
                            {[a.rooms != null ? tLog('desk_entry_rooms_abbrev', { rooms: a.rooms }) : null,
                              a.area_sqm != null ? tLog('desk_entry_area_abbrev', { area: a.area_sqm }) : null,
                              f.created_at ? new Date(f.created_at).toLocaleDateString() : null].filter(Boolean).join(' · ')}
                          </p>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </>
        )}

        {/* Meetings: like Pool, an exclusive feed — one MeetingCard per viewing (client +
            property + listing), with its own Active/Finished status tabs. */}
        {entryTypeFilter[0] === 'Meeting' && (
          <MeetingsFeed viewings={viewings} agents={allAgents} myAgentId={myAgentId} role={role}
            /* entrySince, not a fresh call: this crosses a render boundary as a prop, and an
               unstable value defeats the memo it feeds on every render (same root cause as the
               Leads reload loop, without the fetch that made that one catastrophic). */
            since={entrySince} listingEntries={listingEntries} thumbsByProp={meetingThumbs} coopsByProp={coopsByProperty}
            /* So an empty screen can say WHY it is empty rather than blaming the filters. */
            loading={!viewingsSettled}
            loadError={viewingsErr} onRetry={loadViewings}
            /* A status written from the card's outcome pill: same refresh the modal triggers. */
            onChanged={() => { getMeetingsFeed().then(setViewings).catch(() => {}); getMeetingNudges().then(setMeetingNudges).catch(() => {}) }}
            statusFilter={meetingStatusFilter} onStatusChange={setMeetingStatusFilter}
            districts={districts} features={features}
            /* The medium tier lives on the card face now, not in the banner (owner
               ruling 2026-08-06) — the feed needs the rows to mark its own cards. */
            nudges={meetingNudges}
            onOpen={v => setEditingViewing(v)} />
        )}

        {/* Headline result count for the PROPERTY feed — the server-truth twin of the leads
            one (see ResultCount.jsx). ⚠️ Deliberately NOT the same number as the
            `desk_search_showing_of` span above: that one is visibleGroups/allGroups, i.e.
            how far the CLIENT-side adv filters cut the rows already fetched. This is
            `listingTotal`, the whole-corpus count for scope+period. Both are true; only this
            one answers "how many did my search find".
            ⚠️ !SPECIAL_FEED for the same reason that span carries it (2026-07-27): Leads
            and the other special feeds render their own counts, and listingTotal belongs to
            the property pipeline — printing it over a lead grid reports another feed's
            number. listingTotal starts at 0 and is only set on the first response, so the
            >0 guard keeps "0 ობიექტი" off the skeleton; the zero state owns real zeros. */}
        {/* B05 (2026-10-23 audit): a bound that is set but is not a number (a hand-edited or old
            link) is refused by core.js instead of being read as "no bound" — say which one, here. */}
        {!deskHomeActive && invalidBounds.length > 0 && (
          <p role="alert" className="mb-2 rounded-lg border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-foreground">
            {/* Named by the link's own parameter (priceMin=…), which is where these values come from. */}
            {tLog('desk_filter_invalid_number', { fields: invalidBounds.map(b => `${b.key}=“${String(b.value)}”`).join(', ') })}
          </p>
        )}
        {!SPECIAL_FEED && !deskHomeActive && visibleGroups.length > 0 && (
          <p className="mb-2 flex flex-wrap items-baseline gap-x-1.5 text-xs text-muted-foreground">
            <ResultCount unit="properties" total={listingTotalLanded ? listingTotal : null} inline matching={entryFiltersActive} />   {/* F-089: a landed 0 reads "0 matches" */}
            {/* What the CLIENT-side filters cut from the rows already fetched — a different
                question from the corpus total beside it, which is why both are here rather
                than one replacing the other. Filters can SPLIT a cascade group into
                per-listing rows, so the visible count can legitimately exceed the grouped
                total mid-settle; the denominator is clamped so the line never reads
                "Showing 524 of 379" while the two states converge.
                2026-10-22: the denominator is the chosen category's (and city's) groups BEFORE the
                advanced classify (feedPipeline loadedInView) — allGroups also held clients, meetings,
                drafts and other cities. */}
            {entryFiltersActive && (
              <span>
                {tLog('desk_search_showing_of', { n: visibleGroups.length, m: Math.max(visibleGroups.length, loadedInView) })}
                {/* How hard the panel is cutting. Under time pressure this is the number
                    that survives — the unknown count is NOT folded in, it has its own amber
                    line below, so incomplete data can never masquerade as a confident cut. */}
                {advDisplay && advDisplay.before > 0 && advDisplay.after < advDisplay.before && (
                  <span className="ml-1">
                    {tLog('desk_search_listings_reduction', { pct: Math.round((1 - advDisplay.after / advDisplay.before) * 100) })}
                  </span>
                )}
              </span>
            )}
          </p>
        )}
        {/* 🗄 THE FEED ADMITS WHAT IT IS WITHHOLDING (owner 2026-08-09) ────────────────────
            A hidden property is a suppression, not a place: its entire job is to change what
            THIS feed shows, so this is where it has to be answerable. Previously the only way
            to find out was to leave for a separate destination, which is also why the shelf
            button had to sit in the panel permanently at zero.
            Rendered only when this view actually holds hidden rows — nothing to see, nothing to
            say. `hiddenHere` counts within the current result set, so under a search it means
            "hidden among THESE", which is the question being asked at that moment. */}
        {!SPECIAL_FEED && !deskHomeActive && hiddenHere > 0 && (
          <p className="mb-2 flex items-center gap-2 text-xs text-muted-foreground">
            <Icon name="eye-off" size={12} />
            <span>{tLog('desk_hidden_here_n', { n: hiddenHere })}</span>
            <button type="button" className="text-primary hover:underline"
              onClick={() => setShowHidden(v => !v)}>
              {t(showHidden ? 'desk_hidden_hide_again' : 'desk_hidden_show')}
            </button>
          </p>
        )}
        {/* Map view is spatial, not paginated — it plots every filtered entry at once rather
            than just the current page, same as a real map's expectation that panning/zooming,
            not "next page", is how you see more.
            ⚠️ `unshelvedGroups`, NOT `visibleGroups` (2026-08-16). The 🗄 shelf was applied on
            the way to the GRID and nowhere else, so a hidden property kept its pin: the agent
            hid it, switched to map, and there it was — and clicking the pin opened the card,
            which is the shelf failing at the one thing it does. The 08-09 reveal made this
            worse rather than better, because `hiddenHere` now states a count the map does not
            honour: "2 hidden in this view" over a map still drawing both.
            Not the paginated `activeGroups` either — that has the dormancy partition applied,
            and dormant rows are collapsed on the grid rather than removed, so the map would
            have started dropping properties nobody asked it to drop. Shelf yes, dormancy no.
            Reading the same `src` means Show/Hide restores the pins too, so the map and the
            grid cannot disagree about what is hidden. */}
        {SPECIAL_FEED ? null : !deskHomeActive && entriesView === 'map' ? (
          /* 🗺️ THE MAP WRITES FILTERS NOW. Both shapes commit into `advFilters` — the same state
             the panel edits and the URL carries — so a point dropped here is the same filter as
             one picked from the search box, and it survives a refresh and a shared link.
             ⚠️ A dropped point carries NO id and NO label, on purpose: it is not a gazetteer
             place and pretending otherwise would put a name on a spot nobody named. The
             committed-near chip already falls back to `📍 ~Nm` for exactly this case (B762b). */
          <EntriesMapView groups={unshelvedGroups} districts={districts} features={features} onCardClick={handleCardClick}
            corpusPins={corpusPins} corpusPinsFailed={corpusPinsFailed} onPinClick={id => openListingCard({ id, isOwn: false })}
            expanded={mapExpanded} onToggleExpand={() => setMapExpanded(v => !v)}
            near={advFilters.near} onNear={n => setAdvFilters(f => ({ ...f, near: n }))}
            polygons={ringsFromParam(advFilters.mapPolygon)}
            onPolygon={ring => setAdvFilters(f => {
              const current = ringsFromParam(f.mapPolygon)
              return current.length >= 10 ? f : { ...f, mapPolygon: ringsToParam([...current, ring]) }
            })}
            onRemovePolygon={index => setAdvFilters(f => ({ ...f, mapPolygon: index == null ? '' : ringsToParam(ringsFromParam(f.mapPolygon).filter((_, i) => i !== index)) }))} />
        ) : deskHomeActive && deskHome ? (
        /* ── Desk home: the pre-search cockpit (2026-07-17) ─────────────────── */
        <div className="space-y-6 desk-mode-enter">
          {/* SAME pill as the feed strip (searchPill const) — cockpit top row, add-tile
              width, so Agent Ranking top-aligns with it. Typing leaves home instantly. */}
          {/* The add kinds ride HERE now (owner 2026-08-03) — the same + დამატება button and
              menu the browse toolbar carries, right of the search pill. It replaces the
              full-width Add-client / Add-object / Add-business tile that used to sit under
              this row: one door for adding, in the same place in both desk states. */}
          {!phoneHomeTop && (
            <div className="flex items-center gap-2" onClickCapture={homeClick('quick_actions')}>
              {searchPill}
              {addButton}
            </div>
          )}

          {/* Asymmetric 65/35 split (external-AI layout review 2026-07-17; re-cut by owner
              2026-07-30): main col = the feed itself — leads FIRST, then my properties. The
              thing an agent scans on arrival is inventory, not the agenda, and leads are the
              perishable half of it. The rail carries the short, dated, low-volume columns:
              meetings (urgent, but a list of ≤4 dates, not a grid) above clients, then
              utility (recently viewed). Empty sections NEVER collapse — they shrink to a slim
              CTA banner, so the layout keeps spatial memory. The leads section is the one
              exception: it hides outright when empty rather than banner-ing. */}
          <div className="grid gap-6 lg:grid-cols-3 items-start">
            <div className="lg:col-span-2 min-w-0 space-y-6">
              {/* Recent leads (owner 2026-07-23) — 3 newest lead clusters, now ABOVE
                  properties (owner 2026-07-30: leads are the perishable half of the column;
                  my own properties keep). Same card renderer; hidden entirely while empty —
                  an empty leads section is noise, not news, and properties take the top. */}
              {homeLeads.length === 0 && homeLeadPool.status === 'failed' && (
                /* a failed first read is not an empty pool (audit 2026-10-21) - the empty pool still hides */
                <div className="flex items-center justify-between rounded-lg border border-red-500/40 bg-red-500/5 px-3 py-2.5 text-xs text-red-600 dark:text-red-400" role="status">
                  <span>{t('desk_home_leads_failed')}</span>
                  <button className="hover:underline shrink-0 ml-3" onClick={() => setHomeLeadsTick(n => n + 1)}>{t('desk_home_retry')}</button>
                </div>
              )}
              {homeLeads.length > 0 && (
                <section onClickCapture={homeClick('recent_leads')}>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold"><Icon name="target" size={14} className="mr-1 text-muted-foreground" />{t('desk_home_recent_leads')}<ScopeTag text={t('desk_scope_pool')} /></h3>
                    <button className="text-xs text-primary hover:underline" onClick={() => { setEntryTypeFilter(['Leads']); enterBrowse(); leadHoverPrefetch() }}>{t('desk_home_show_all')}</button>
                  </div>
                  <div className="grid gap-3 items-start [grid-template-columns:repeat(auto-fill,minmax(min(300px,100%),1fr))]">
                    {homeLeads.map(group => renderEntryGroup(group, 'home'))}
                  </div>
                  {homeLeadPool.status === 'failed' && (
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">{t('desk_home_leads_stale')}{' '}
                      <button className="underline" onClick={() => setHomeLeadsTick(n => n + 1)}>{t('desk_home_retry')}</button></p>
                  )}
                </section>
              )}
              <section onClickCapture={homeClick('recent_props')}>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold"><Icon name="house" size={14} className="mr-1 text-muted-foreground" />{t('desk_home_recent_props')}<ScopeTag text={homeScope.props} /></h3>
                  {/* Same values the search pill's "Properties" option sets — the section's
                      Show-all lands in the Properties category, not the untyped full feed
                      (2026-07-17 user report: clients' Show-all picked its category, this
                      one didn't). */}
                  <button className="text-xs text-primary hover:underline" onClick={() => { setEntryTypeFilter(['Property', 'Listings']); enterBrowse() }}>{t('desk_home_show_all')}</button>
                </div>
                {/* 'home' = the Zillow-style front-page card (image top, price-led text) —
                    only this section uses it; the full feed keeps the eBay side-by-side card. */}
                <div className="grid gap-3 items-start [grid-template-columns:repeat(auto-fill,minmax(min(300px,100%),1fr))]">
                  {deskHome.props.map(group => renderEntryGroup(group, 'home'))}
                </div>
                {deskHome.props.length === 0 && <p className="text-sm text-muted-foreground">{t('desk_empty_no_entries')}</p>}
              </section>
            </div>
            <div className="space-y-6 min-w-0">
              <section id="desk-home-meetings" className="scroll-mt-4" onClickCapture={homeClick('meetings')}>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold"><Icon name="calendar" size={14} className="mr-1 text-muted-foreground" />{t('desk_home_meetings')}<ScopeTag text={homeScope.meetings} />
                    {/* The COUNT, not a presence dot (2026-08-15) - the TRUE upcoming total since 2026-10-21 (the
                        memo no longer caps it), so the number and the +N below add up. */}
                    {homeMeetings.length > 0 && (
                      <span className="ml-2 align-middle text-xs font-normal text-muted-foreground">{homeMeetings.length}</span>
                    )}
                  </h3>
                  {/* Home's Show-all CTAs each set a category AND enterBrowse(). The category
                      alone already flips isHome (committedSearch.type), but the browse flag is
                      what keeps the agent in the feed after they later clear the category
                      ("empty box in browse = browse everything") — so every CTA sets both. */}
                  <button className="text-xs text-primary hover:underline" onClick={() => { setEntryTypeFilter(['Meeting']); enterBrowse() }}>{t('desk_home_show_all')}</button>
                </div>
                {homeMeetings.length > 0 ? (
                  /* One column in the rail — the old auto-fill grid was sized for the wide
                     main column and would leave a single card stranded mid-row here. */
                  <div className="grid gap-3 items-start grid-cols-1">
                    {homeMeetings.slice(0, 4).map(v => (
                      <MeetingCard key={v.id} v={v} agents={allAgents}
                        thumbListing={homeThumbByProp.get(v.property_snapshot?.core_property_id)}
                        nudge={meetingNudgeOf(homeNudgeIx, v)}
              coops={coopsByProperty[v.property_snapshot?.core_property_id]} onOpen={setEditingViewing}
                        onChanged={() => { getMeetingsFeed().then(setViewings).catch(() => {}); getMeetingNudges().then(setMeetingNudges).catch(() => {}) }} />
                    ))}
                    {/* Says what it is holding back rather than letting it vanish. */}
                    {homeMeetings.length > 4 && (
                      <button type="button" className="text-xs text-primary hover:underline text-left"
                        onClick={() => { setEntryTypeFilter(['Meeting']); enterBrowse() }}>
                        + {homeMeetings.length - 4}
                      </button>
                    )}
                  </div>
                ) : viewingsErr ? (
                  /* a failed agenda read is NOT "no meetings" (audit 2026-10-21) */
                  <div className="flex items-center justify-between rounded-lg border border-red-500/40 bg-red-500/5 px-3 py-2.5 text-xs text-red-600 dark:text-red-400" role="status">
                    <span>{t('desk_home_meetings_failed')}</span>
                    <button className="hover:underline shrink-0 ml-3" onClick={() => loadViewings()}>{t('desk_home_retry')}</button>
                  </div>
                ) : !viewingsSettled ? (
                  <div className="rounded-lg border border-border/60 bg-card/50 px-3 py-2.5 text-xs text-muted-foreground">{t('desk_home_meetings_loading')}</div>
                ) : (
                  <div className="flex items-center justify-between rounded-lg border border-border/60 bg-card/50 px-3 py-2.5 text-xs text-muted-foreground">
                    <span>{t('desk_home_no_meetings')}</span>
                    <button className="text-primary hover:underline shrink-0 ml-3" onClick={enterBrowse}>{t('desk_home_browse_props')}</button>
                  </div>
                )}
              </section>
              <section onClickCapture={homeClick('recent_clients')}>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold"><Icon name="user-check" size={14} className="mr-1 text-muted-foreground" />{t('desk_home_recent_clients')}<ScopeTag text={homeScope.clients} /></h3>
                  <button className="text-xs text-primary hover:underline" onClick={() => { setEntryTypeFilter(['Client']); enterBrowse() }}>{t('desk_home_show_all')}</button>
                </div>
                {deskHome.clients.length > 0 ? (
                  <div className="grid gap-3 items-start grid-cols-1">
                    {deskHome.clients.slice(0, 3).map(group => renderEntryGroup(group, 'card'))}
                  </div>
                ) : (
                  <div className="flex items-center justify-between rounded-lg border border-border/60 bg-card/50 px-3 py-2.5 text-xs text-muted-foreground">
                    <span>{t('desk_home_no_clients')}</span>
                    <button className="text-primary hover:underline shrink-0 ml-3" onClick={() => openModal('client')}>{t('desk_home_add_client_cta')}</button>
                  </div>
                )}
              </section>
              <section onClickCapture={homeClick('recent_viewed')}>
                <h3 className="text-sm font-semibold mb-2"><Icon name="clock" size={14} className="mr-1 text-muted-foreground" />{t('desk_home_recent_viewed')}</h3>
                {recentViewed.length > 0 ? (
                  <div className="space-y-1">
                    {recentViewed.slice(0, 10).map(e => (
                      <button key={e.id} className="w-full text-left rounded-md px-2 py-1.5 text-xs hover:bg-card transition-colors"
                        onClick={() => e.listingId ? openListingCard({ id: e.listingId, isOwn: false })
                          : e.reqId ? openClientCard(e.reqId) : null}>
                        <span className="text-muted-foreground hover:text-foreground truncate block">
                          <span className="opacity-60 mr-1.5"><Icon name={e.kind === 'client' ? 'user-check' : 'house'} size={12} /></span>{e.title}
                        </span>
                        {/* Facts line stored at click time (older entries predate it → no line). */}
                        {e.sub && <span className="text-xs text-muted-foreground/70 truncate block pl-5">{e.sub}</span>}
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="rounded-lg border border-border/60 bg-card/50 px-3 py-2.5 text-xs text-muted-foreground">{t('desk_home_no_viewed')}</p>
                )}
              </section>
            </div>
          </div>
        </div>
        ) : (
        <div className={entriesView === 'list' ? 'space-y-1.5' : entriesView === 'card' ? 'grid gap-3 items-start grid-cols-1 max-w-4xl' : 'grid gap-3 items-start [grid-template-columns:repeat(auto-fill,minmax(min(340px,100%),1fr))]'} ref={feedGridRef} style={listingsFetching && holdHeight ? { minHeight: holdHeight } : undefined}>
          {/* The in-grid AddEntryTile is GONE (2026-07-24, §6 CommandStrip): in browse the
              collapsed-cockpit strip above the grid is the one home of add + notifications —
              rendering the tile here too showed the same surface twice (owner report). */}
          {/* renderedGroups, not pageGroups — see the render budget above. The page is
              still 30 groups; this only stops all 30 being built in the click's frame. */}
          {renderedGroups.map(group => renderEntryGroup(group))}
        </div>
        )}

        {/* Part C2: dormant tier — 60 days with no reveal/meeting/fresh-capture/wake.
            Collapsed below the active feed, browsable, greyed but fully functional:
            any reveal or meeting from here auto-renews by definition (it creates the
            very event the dormancy computation reads). Never deleted. */}
        {!SPECIAL_FEED && entriesView !== 'map' && !deskHomeActive && dormantGroups.length > 0 && (
          <div className="mt-6">
            <button className="w-full text-left text-sm text-muted-foreground border-t border-border pt-3 hover:text-foreground"
              onClick={() => setShowDormant(v => !v)}>
              {showDormant ? '▾' : '▸'} 💤 {tLog('desk_dormant_section', { n: dormantGroups.length })}
            </button>
            {showDormant && (
              <div className={`mt-3 opacity-70 ${entriesView === 'list' ? 'space-y-1.5' : entriesView === 'card' ? 'grid gap-3 items-start grid-cols-1 max-w-4xl' : 'grid gap-3 items-start [grid-template-columns:repeat(auto-fill,minmax(min(340px,100%),1fr))]'}`}>
                {dormantGroups.slice(0, 100).map(group => renderEntryGroup(group))}
              </div>
            )}
            {showDormant && dormantGroups.length > 100 && (
              <p className="text-xs text-muted-foreground mt-2">{tLog('desk_dormant_more', { n: dormantGroups.length - 100 })}</p>
            )}
          </div>
        )}

        {!SPECIAL_FEED && entriesView !== 'map' && !deskHomeActive && visibleGroups.length > ENTRY_PAGE_SIZE && (
          <FeedPager
            page={clampedPage} totalPages={estimatedTotalPages} loadedPages={totalPages}
            onPage={p => { setEntryPage(p); scrollFeedToTop() }} morePending={morePagesPending} showJump />
        )}

        {/* Real server pagination for the listings source specifically — an agency's authored/
            resold listings can genuinely run past a few hundred; this fetches the next batch
            (offset-based, see loadMoreListings) rather than a fixed cap silently dropping the
            tail. Folds into listingEntries → the same filter/sort/page-switcher pipeline above
            picks it up automatically, no separate render path needed. */}
        {/* Compares listingRawCount (rows-clause cursor) against listingTotal (rows-clause
            total) — both apples-to-apples. listingEntries.length can't be used here: it also
            counts the unbounded extraRows context, which would make this hide too early or
            too late relative to how much of the real paginated set remains (2026-07-08). */}
        {/* While the auto-prefetch chain is running this is a PASSIVE progress line (clicking
            a button for something already happening was noise); the button only remains for
            the one case the chain deliberately stops — the AUTO_LOAD_MAX cap on huge unfiltered
            feeds (and filtered sets, where only proximity prefetch runs) — mainly for map view,
            which has no page boundary to trigger the proximity prefetch. */}
        {/* Hidden during an identity reach-back (phone / listing-id / image): the feed chain is
            intentionally halted for those (the reach-back answers them DB-wide), so this row would
            otherwise show a "loading X/Y" that never advances — the reported "says loading but
            doesn't load" (2026-07-20). Feed pagination progress is meaningless for an identity query. */}
        {!SPECIAL_FEED && !deskHomeActive && listingRawCount < listingTotal
          && !(phoneQueries.length || listingIdQueries.length || cadastralQueries.length || imageMatchIds.size) && (
          <div className="flex justify-center mt-3">
            {/* fails≥3 → the chain gave up (see loadMoreFailsRef): show the manual button, not
                an eternal "Loading…" that isn't. The ref is settled by the time the failing
                fetch's setListingLoadingMore(false) re-renders this. */}
            {((!advFiltersActive(advFiltersDebounced) && listingRawCount < AUTO_LOAD_MAX && loadMoreFailsRef.current < 3) || listingLoadingMore) ? (
              <span className="text-xs text-muted-foreground">
                {tLog('desk_autoload_progress', { loaded: listingRawCount, total: listingTotal })}
              </span>
            ) : (
              <button className={btn('ghost')} onClick={() => {
                // §13 load_more_clicked: follow-up row, CLICKS only (the auto-chain effect
                // calls loadMoreListings too — logging those would drown the signal). Only
                // when a text query is active: the metric calibrates search page size.
                if (entrySearch.trim()) logSearchQuery({ rawText: entrySearch.trim(), compiled: [], residual: '', dismissed: [], context: { source: 'desk', event: 'load_more' } })
                loadMoreListings()
              }}>
                {listingCountUnit === 'properties'
                  ? tLog('desk_load_more_properties', { loaded: listingRawCount, total: listingTotal })
                  : tLog('desk_load_more_listings', { loaded: listingRawCount, total: listingTotal })}
              </button>
            )}
          </div>
        )}

        {/* Possible matches (0055): the unknown-verdict tier — data too coarse or missing to
            confirm OR deny the active filters (e.g. property coded at raion level under an
            ubani filter, or no price under a price filter). Exact matches always render first
            above; this shelf keeps the can't-rule-out rows visible instead of hiding them
            behind the "hidden for missing data" count. Deliberately OUTSIDE the main pipeline:
            no sort/pagination mixing, capped at 60 by the RPC. */}
        {/* Shelf sentinel (2026-09-24): when this comes within reach of the viewport the shelf's
            first page is fetched — see pendingShelfRef. Zero height, so it moves nothing. */}
        {!SPECIAL_FEED && entriesView !== 'map' && advFiltersActive(advFiltersDebounced) && (
          <div ref={shelfSentinelRef} aria-hidden="true" style={{ height: 1 }} />
        )}
        {!SPECIAL_FEED && entriesView !== 'map' && advFiltersActive(advFiltersDebounced) && (
          <PossibleMatchesShelf
            groups={shownPossibleGroups} serverGroups={possibleGroups.length}
            serverTotal={serverUnknown} view={entriesView} containerRef={possiblesRef}
            onLoadMore={loadMorePossibles} loading={possiblesLoading}
            failed={possiblesFailed} onRetry={retryInitialPossibles}
            exhausted={possiblesDoneRef.current}
            renderGroup={group => (
              entriesView === 'list' ? (
                <HistoryListRow key={group.key} group={group}
                  districts={districts} features={features}
                  onCardClick={onCardClickStable}
                  expanded={false} shade={0} onToggle={NOOP} />
              ) : (
                <HistoryGroup key={group.key} group={group}
                  districts={districts} features={features}
                  onCardClick={onCardClickStable}
                  expanded={false} shade={0} onToggle={NOOP}
                  searchPolygons={searchPolygons}
                  gallerySize={entriesView === 'gallery' ? 'large' : 'thumb'}
                  coopsByProperty={coopsByProperty} defectsByProperty={defectsByProperty} agentFactsByProperty={agentFactsByProperty} distinctByProperty={distinctByProperty} devCardsByProperty={devCardsByProperty} sellersByProperty={sellersByProperty} sharesByProperty={sharesByProperty} restrByListing={restrDisplayByListing} termsByListing={termsByListing} clearByListing={clearByListing} viewsByProperty={viewsByProperty} favorites={favSet} onToggleFavorite={onToggleFavoriteStable} archived={archSet} onToggleArchive={onToggleArchiveStable} authorMark={authorMarkMap.get(groupPropertyId(group)) || null} onOpenCooperation={setDeskCoop} onAddMeeting={onAddMeetingStable} onCreateContract={onCreateContractStable} onShare={setShareSeed} onRepost={setDeskRepost} onShareOthers={openPropShare} onOpenTerms={setDeskTerms}
                  meetingsIndex={meetingsIndex} clientMeetings={clientMeetings} onOpenMeeting={setEditingViewing} privileged={privileged} />
              )
            )} />
        )}

        {/* Near the selected street, but not on it (Q4, 0541/0543). The THIRD verdict tier:
            these rows FAIL the street check — their address names a different street — so
            they are absent from both lists above by construction, which is correct and is
            also why "what else is around here" had no answer before.
            ⚠️ ITS OWN HEADING, AND THE HEADING SAYS "FROM THE STREET'S CENTRE". `street.geo`
            is a single ST_Point per street, so this IS the ~700m centroid radius that used to
            answer "Politkovskaya" with Kavtaradze. The designer's ruling was that it may come
            back only under its own heading with the centre named. Fold this into the shelf
            above, or drop that phrase, and the old bug is silently restored.
            ⚠️ No pager: `nearby_ids` has no cursor — it is the whole nearby set or nothing —
            so `serverTotal` is the group count itself and the shelf's own button correctly
            never appears. Bounded by the 1000m server cap; the worst street measured
            corpus-wide at 400m is 334 properties, and an agent-scoped set is far smaller. */}
        {!SPECIAL_FEED && entriesView !== 'map' && nearbyGroups.length > 0 && (
          <PossibleMatchesShelf
            groups={nearbyGroups} serverGroups={nearbyGroups.length}
            serverTotal={nearbyGroups.length} view={entriesView}
            header={({ shown }) => tLog('desk_nearby_street_header', { shown, m: advFiltersDebounced.streetNearbyM || 0 })}
            renderGroup={group => {
              // group.key is `possible:${cluster_key}` — the prefix comes from the shared
              // grouping helper, and street_nearby is keyed by the bare cluster_key.
              const near = streetNearbyMeta?.[group.key.replace(/^possible:/, '')] || null
              return (
                <div key={group.key}>
                  {/* The badge is a WRAPPER, not a change to the card: HistoryGroup is shared
                      with five other feeds and a per-tier prop would reach all of them. */}
                  {near && (
                    <p className="text-xs text-muted-foreground mb-1">
                      {tLog('desk_nearby_street_badge', { street: near.label, m: near.m })}
                    </p>
                  )}
                  {entriesView === 'list' ? (
                    <HistoryListRow group={group}
                      districts={districts} features={features}
                      onCardClick={onCardClickStable}
                      expanded={false} shade={0} onToggle={NOOP} />
                  ) : (
                    <HistoryGroup group={group}
                      districts={districts} features={features}
                      onCardClick={onCardClickStable}
                      expanded={false} shade={0} onToggle={NOOP}
                      searchPolygons={searchPolygons}
                      gallerySize={entriesView === 'gallery' ? 'large' : 'thumb'}
                      coopsByProperty={coopsByProperty} defectsByProperty={defectsByProperty} agentFactsByProperty={agentFactsByProperty} distinctByProperty={distinctByProperty} devCardsByProperty={devCardsByProperty} sellersByProperty={sellersByProperty} sharesByProperty={sharesByProperty} restrByListing={restrDisplayByListing} clearByListing={clearByListing} viewsByProperty={viewsByProperty} favorites={favSet} onToggleFavorite={onToggleFavoriteStable} archived={archSet} onToggleArchive={onToggleArchiveStable} authorMark={authorMarkMap.get(groupPropertyId(group)) || null} onOpenCooperation={setDeskCoop} onAddMeeting={onAddMeetingStable} onCreateContract={onCreateContractStable} onShare={setShareSeed} onRepost={setDeskRepost} onShareOthers={openPropShare} onOpenTerms={setDeskTerms}
                      meetingsIndex={meetingsIndex} clientMeetings={clientMeetings} onOpenMeeting={setEditingViewing} privileged={privileged} />
                  )}
                </div>
              )
            }} />
        )}

        {/* ── S6: the same search, one pool over (design 2b §5) ────────────────────────
            Deliberately at the END of the results, never in the panel: it is an answer to
            "I have read all of these", which only the bottom of the list can know.
            ⚠️ It re-runs the SAME criteria with the pool switched — it must go through the
            pool control's own path (`setEntryTypeFilter(['Leads'])`), not a bespoke fetch,
            so the carry-over banner and its Undo appear exactly as they do for a click on
            the segmented control. A second route into the same state is how the two drift.
            ⚠️ DELIBERATE DEVIATION from the mock, which restates the criteria in prose
            ("apartment, for sale, Vake, under $200k"). That sentence already exists on this
            screen — the constraint chip row sits directly above the results — and a second
            rendering of the filter state would be a second definition of it, which this
            surface has been bitten by before. The card quotes the COUNT, which is the fact
            the chip row cannot carry. */}
        {leadsHatchOn && (
          <div className="mt-4 rounded-xl border border-border bg-muted/20 px-4 py-3">
            <p className="text-sm font-medium text-foreground">
              {listingTotal > 0 ? tLog('desk_hatch_thats_all', { n: listingTotal }) : t('desk_hatch_none_here')}
            </p>
            {/* No number until the leads query has answered — the card still makes sense
                without it ("nobody has called these yet" is the offer), and an invented or
                stale figure here would send an agent to a pool that does not hold it. */}
            <p className="mt-0.5 text-xs text-muted-foreground">
              {/* ⚠️ B1201: THE WHOLE LEAD POOL, not the lens. `leadCountActive` carries
                  `mine: leadWorked`, so under the "leads I work" lens it counts the agent's own
                  claims -- and there are TWO live claims in the entire system, so it reads 0.
                  This sentence says "nobody has called these yet", which is a claim about the
                  POOL, and it printed 0 beside a search the pool answers with 247.
                  Reading the mine:false key costs nothing: leadsCachedTotal only reads cache, so
                  when that query has not been run it returns null and the honest
                  desk_hatch_leads_unknown wording renders instead of a number from the wrong
                  population. */}
              {hatchLeadCount != null
                ? tLog('desk_hatch_leads_n', { n: hatchLeadCount })
                : t('desk_hatch_leads_unknown')}
            </p>
            <button type="button"
              className="mt-2 rounded-full border border-border px-3 py-1.5 text-xs font-semibold hover:bg-accent"
              onClick={() => {
                trackDeskEvent('category_switched', { to: 'Leads', count: leadCountActive, surface: 'hatch' })
                setEntryTypeFilter(['Leads'])
              }}>
              {t('desk_hatch_go')}
            </button>
          </div>
        )}
        </div>

        {/* RIGHT sidebar — same width as the left search-tools bar. The Add client/property
            buttons moved into the entries grid itself (2026-07-06, always the first slot —
            see AddEntryTile); the Activity/Notifications/Meetings widget that used to sit here
            was removed (2026-07-09 — its notifications now live in the Add-tile feed, and
            nothing replaced the Activity/Meetings tabs, a deliberate call). Agent ranking is now
            the only occupant, height-matched to the left search-tools panel (see rightAsideHeight
            above) instead of a short fixed box with empty space below it.
            Agent ranking: shown to everyone (2026-07-05); self-contained since v2
            (2026-07-12) — own period tabs + fetches, see AgentRankingPanel. */}
        {(deskHomeActive || !(entriesView === 'map' && mapExpanded)) && (
          // Height-match applies from md up only — on mobile the measured sibling is the
          // whole stacked feed column, which inflated the card while the inner list stayed
          // capped at 280px (cut rows + dead space below, found 2026-07-12).
          <aside ref={rightAsideRef} style={rightAsideHeight ? { '--aside-h': `${rightAsideHeight}px` } : undefined}
            /* Ranking rail deliberately NARROWER than the facet rail (design ruling
               2026-07-24 Q2 — proportion, not pixel-matching). */
            /* Tried and reverted 2026-08-03: dropping the rail 4rem on home ("move რეიტინგი
               lower") left an empty band beside the search row — with the add tile gone the
               rail top-aligns with the pill and reads fine. Top-aligned in both states. */
            /* md:-mr-5 (owner 2026-09-29: "move stats table to the very right edge"): the page has a 24px side
               margin; the rail alone steps 20px into it, keeping a 4px sliver so its border is not cut. */
            /* ⚠️ md:max-h + md:overflow-y-auto (owner 2026-10-27: "users can't scroll to the bottom" with the reshare details
               open): the rail is STICKY at a fixed height, so anything that grew past its bottom — the worker box's reshare
               pools, opened in place — could never be scrolled into view; the page scroll only moves what is not stuck. The
               rail now scrolls itself, never taller than the screen, and the ranking keeps a usable height below it. */
            className="w-full md:w-64 xl:w-72 shrink-0 md:sticky md:top-2 md:h-[var(--aside-h)] md:max-h-[calc(100vh-1rem)] md:overflow-y-auto space-y-2 md:flex md:flex-col md:-mr-5">
            {/* the agent's own auto worker, first thing in the rail (owner 2026-10-20: "when they login to their desk
                must see details of auto worker" / "we may place that over stats sidebar") */}
            <div id="desk-worker" className="bg-card rounded-lg scroll-mt-4"><MyWorkerSwitch onSummary={setWorkerSummary} /></div>
            {/* Rail drafts callout (designer board 2026-07-23): pending/failed drafts as a
                STANDING card above the ranking — the bell badges the same count, this makes
                it visible without opening anything. Same signal, second surface (deliberate:
                the bell is ambient, this is where the eye rests while browsing). Opens the
                notifications panel. Hidden at zero — an empty callout is noise. */}
            {stripNotifCount > 0 && (
              <button type="button" onClick={() => setNotifOpen(true)}
                className="w-full text-left rounded-lg border border-amber-500/40 bg-amber-500/5 px-3 py-2 hover:border-amber-500/70 transition-colors">
                <p className="text-sm font-semibold text-amber-600 dark:text-amber-400">{tLog('desk_rail_drafts_n', { n: stripNotifCount })}</p>
                <p className="text-xs text-muted-foreground">{t('desk_rail_drafts_sub')}</p>
              </button>
            )}
            {/* Rail telemetry (briefing ruling): Q3 — "rail survives only if agents click
                it" — is decided by exactly this event. surface says where the click came
                from (home vs browse); the rail renders on both. */}
            {/* flex-1 + min-h-0: the ranking card's h-full needs a BOUNDED parent, or the list grows
                past the rail instead of scrolling inside it (owner report 2026-09-29). */}
            <div className="md:flex-1 md:min-h-[16rem]" onClickCapture={() => trackDeskEvent('home_click', { section: 'ranking_rail', surface: deskHomeActive ? 'home' : 'browse' })}>
              {/* Scorecard v2 (owner ruling 2026-09-29): owner work + results × attendance, unbacked paid
                  reposts cost points, and a management-only floor tab. The v1 board stays one
                  config flip away (config.desk_scorecard.model = 'v1', Admin → General). */}
              {config?.desk_scorecard?.model === 'v1'
                ? <AgentRankingPanel myAgentId={myAgentId} allAgents={allAgents} viewings={viewings} config={config} />
                : <AgentScorecardPanel myAgentId={myAgentId} allAgents={allAgents} config={config}
                    canSeeFloor={role === 'admin' || role === 'general_manager' || role === 'manager'} />}
            </div>
          </aside>
        )}
        </div>
      </div>

      {/* Notifications overlay — the ONE destination (design ruling 2026-07-24); acting on
          a row closes the panel first so the opened card lands in a clean main column. */}
      {notifOpen && (
        <NotificationsPanel history={history} accessRows={accessDecided} accessPending={accessPending} shareActivity={shareActivity}
          aiAlerts={aiAlerts}
          systemAlerts={systemAlerts}
          meetingNudges={meetingNudges}
          binderChanges={binderChanges}
          clientWaiting={clientWaiting}
          onOpenMeeting={openNudgeMeeting}
          isAdminViewer={role === 'admin'}
          onAccessDecided={onAccessDecided}
          worked={workedListingIds}
          readState={readState} onMarkRead={markNotifRead}
          onDismiss={dismissNotif} onUndoDismiss={undoDismissNotif}
          agentId={listingsScope === 'all' ? null : myAgentId}
          onOpenActivity={item => { setNotifOpen(false); handleCardClick({ items: [item] }) }}
          // is_own_listing only ships on share rows (the server picks the sender's own capture
          // when there is one); price/ended rows carry none and stay isOwn:false as before.
          /* Share rows can now arrive with no listing_id (nothing active left on the
             property) but a property_id — open the workspace rather than nothing, the
             same fallback openSharedLink uses. price/ended rows carry only listing_id
             and are unaffected. */
          onOpenListing={pc => {
            setNotifOpen(false)

            if (pc.listing_id) openListingCard({ id: pc.listing_id, isOwn: !!pc.is_own_listing })
            else if (pc.property_id) setPropertyCard({ propertyId: pc.property_id, listingId: null })
          }}
          onOpenDemand={reqId => { setNotifOpen(false); openClientCard(reqId) }}
          onClose={() => setNotifOpen(false)} />
      )}

      {/* Client. AMEND (continuing an existing demand) keeps ClientReview's ops-diff view — it has
          no editable-form analog. CREATE (a fresh AI draft) and plain manual entry BOTH use the
          same editable ManualClientModal, pre-filled from the AI draft when there is one, so an
          AI-opened client looks exactly like the property modal (2026-07-03). */}
      {activeModal === 'client' && (
        effectiveMode === 'amend' ? (
          <Modal title={t('desk_amend_modal_title')} onClose={() => setActiveModal(null)}>
            <ClientReview
              mode="amend"
              ops={aiAmendCmd && !amendTargetId ? aiAmendCmd.ops : opsFromCriteria(aiCreateCmd?.criteria)}
              targetLabel={effectiveTargetId ? tLog('desk_amend_target_label', { id: effectiveTargetId.slice(0, 8) }) : null}
              decision={envelope.decision}
              confidence={envelope.confidence}
              districts={districts}
              features={features}
              onAccept={handleAcceptClient}
              onReject={handleRejectClient}
              loading={committing}
            />
          </Modal>
        ) : (
          <ManualClientModal districts={districts} features={features}
            draft={effectiveMode === 'create' ? aiCreateCmd : null}
            intakeId={intakeId} rawText={rawText}
            decision={envelope?.decision} confidence={envelope?.confidence}
            agentId={myAgentId}
            agentName={allAgents.find(a => a.id === myAgentId)?.name || session?.user?.email?.split('@')[0] || null}
            onClose={() => setActiveModal(null)}
            onSaved={() => { setCommitMsg(t('desk_toast_client_saved')); resetIntake(); loadHistory() }} />
        )
      )}

      {/* Property — observation capture through the one supply path (contract:
          core/LISTING_OBSERVATION_CONTRACT.md). Typed Save commits directly;
          opened from PropertyDrafts it reviews a pending extract_listing proposal. */}
      {activeModal === 'property' && (
        <ManualPropertyModal districts={districts} draft={propertyDraft} agentId={myAgentId}
          agentName={allAgents.find(a => a.id === myAgentId)?.name || session?.user?.email?.split('@')[0] || null}
          privileged={privileged}
          onClose={() => { setActiveModal(null); setPropertyDraft(null) }}
          onDraftCreated={loadHistory}
          onAddMeeting={onAddMeetingStable} onCreateContract={onCreateContractStable} onShare={setShareSeed}
          onSaved={() => { setCommitMsg(t('desk_toast_property_saved')); loadHistory() }} />
      )}



      {/* Candidate — reuses the real Candidates modal (its own full-screen modal) */}
      {activeModal === 'business' && (
        <AddBusinessModal listings={listingEntries} onClose={() => setActiveModal(null)} />
      )}
      {/* Add-meeting door → hands the picked row to the SAME meetingPropertySeed the card
          faces use, so there is still exactly one create path (MEETINGS_MODEL §3). */}
      {activeModal === 'meeting' && (
        <AddMeetingModal listings={listingEntries}
          agentId={myAgentId} scopeAll={listingsScope === 'all'}
          initialQuery={envelope?.meeting?.property_hint || ''}
          // The pasted message's meeting fields ride along with the picked row (2026-09-23).
          onPick={row => { setActiveModal(null); setMeetingPropertySeed(envelope?.meeting ? { ...row, ai_meeting: envelope.meeting } : row) }}
          onClose={() => setActiveModal(null)} />
      )}
      {activeModal === 'candidate' && (
        <CandidateModal candidate={null} config={config} me={myAgentRecord} offices={offices}
          // Same authority as the Candidates tab's render of this modal — see the note there.
          seeAllVacancyLinks={role === 'admin' || role === 'general_manager'}
          canSetVacancyLinkPolicy={role === 'admin'}
          onSave={async data => {
            const saved = await createCandidate(data)
            logCandidateProposal(data, saved?.id).then(loadHistory).catch(e => console.error('[candidate proposal audit]', e.message))
            return saved
          }}
          onDelete={() => {}}
          onPromote={() => {}}
          onClose={() => setActiveModal(null)} />
      )}

      {/* THE CARD STACK (see the cardStack declaration for the model). Clicking a Recent
          Entries card opens it here — the live saved state, fetched fresh, never the stale
          AI-draft shape above. Rendered bottom-to-top in array order; every entry stays
          mounted, so a lower card keeps its scroll position and any half-typed edit.
          ⚠️ Both modals are z-50 body portals, so WHO IS ON TOP IS DOM ORDER, which for
          portals is MOUNT order — not this array's index and not any z-index. The array
          only has to grow at the end (push) and shrink at the end (pop) for the two to
          agree, which is why nothing may splice into the middle of it. */}
      {cardStack.map((c, i) => c.kind === 'client' ? (
        <ClientEditModal
          key={`client:${c.reqId}:${c.__cardUid ?? i}`}   // review 2026-09-23: stable uid, not the index
          requirementId={c.reqId}
          districts={districts} features={features}
          leadSince={entrySince}
          actingReqId={actingReqId}
          agentId={myAgentId}
          agents={allAgents || agents || []}
          onClose={popCard}
          onSaved={loadHistory}
          // The person view is NOT a stack card — it is its own slot, and it cannot open a
          // third thing, so mounting last is enough to put it on top. Its own onOpenDemand
          // pushes, so person → another demand lands back here on ✕.
          onOpenPerson={partyId => setViewingPartyId(partyId)}
          // A matched property PUSHES (owner, 2026-08-04). ✕ lands back on this client with
          // the match list where it was, so the next match is one click instead of finding
          // the client again.
          onOpenListing={listingId => pushListingCard({ id: listingId, isOwn: false })}
          // Leaving for the leads feed drops the WHOLE stack — the point is to get out of
          // the way of the search behind it, not to reveal another card.
          onLeaveToSearch={closeCards}
          onClosedOrReopened={(reqId, isClosed) => {
            popCard()
            isClosed ? handleReopenReq(reqId) : handleCloseReq(reqId)
          }} />
      ) : (
        <ListingLineageModal
          key={`listing:${c.id}:${c.__cardUid ?? i}`}   // review 2026-09-23: stable uid, not the index
          listingId={c.id} isOwn={c.isOwn} seed={c.seed || null} onClose={popCard}
          meetingsIndex={meetingsIndex}
          // Shelf verbs: same sets and same handlers the card face uses (owner 2026-09-10).
          // STABLE callbacks, not the raw ones — this modal re-renders on every fetch it makes,
          // and a fresh function identity per render is the trap the contexts above avoid.
          favorites={favSet} archived={archSet}
          onToggleFavorite={onToggleFavoriteStable} onToggleArchive={onToggleArchiveStable}
          // Which deal-editor tab this card had open, restored from the URL and reported
          // back so a refresh returns to it (2026-08-05). Only the TOP card reports: the
          // URL carries one `view`, and a covered card cannot be clicked anyway.
          initialView={c.view || null}
          onViewChange={i === cardStack.length - 1 ? setTopCardView : null}
          // Legacy rows from the modal's URL-matched summary are THIN (no commission/
          // events) — swap in the full feed row by id before handing to ViewingForm,
          // or a save from the form could null fields the thin row never carried.
          onOpenMeeting={v => setEditingViewing(v.__core ? v : ((viewings || []).find(x => !x.__core && String(x.id) === String(v.id)) || v))}
          onAddMeeting={onAddMeetingStable} onCreateContract={onCreateContractStable} onShare={setShareSeed} sharesEpoch={sharesEpoch}
          // The other half of the loop, and the reason the stack exists (owner 2026-08-04):
          // a matching client PUSHES instead of replacing this card. Before the stack this
          // had to close the property first — one editingRequirementId slot meant a push
          // would have swapped a card underneath an opaque modal, so the click read as
          // broken. Now property → client → its next property → … all unwind on ✕.
          onOpenDemand={reqId => pushClientCard(reqId)}
          // Still POPS rather than pushing: this modal fetches once on open, so a property
          // edited in the editor above it would come back showing pre-edit values. Wants a
          // refresh key (the termsRefresh pattern in LineageModal) before it can stack.
          onEditProperty={({ propertyId, listingId }) => { popCard(); setPropertyCard({ propertyId, listingId }) }}
          agentId={myAgentId} agentName={allAgents.find(a => a.id === myAgentId)?.name || null} privileged={privileged}
          /* The roster, for ↗ → "share with a colleague" (0983). allAgents, not the
             office-filtered list: sharing a flat across offices is ordinary. */
          agents={allAgents}
          // T37: admin comments are ADMIN only — `privileged` includes managers, so it cannot carry this.
          isAdmin={role === 'admin'} />
      ))}

      {/* Person view — one human, all their demands + owned/listed properties. Clicking a
          demand PUSHES it onto the card stack and closes this view: the person modal is the
          hub you chose the demand FROM, and leaving it open would cover the card it just
          opened (it is not a stack card, so it cannot be interleaved). ✕ on the demand
          therefore lands on whatever was under the person view. */}
      {viewingPartyId && (
        <PersonModal partyId={viewingPartyId} districts={districts} privileged={privileged} agentId={myAgentId}
          onClose={() => setViewingPartyId(null)}
          onAddRequest={p => { addReqPidRef.current = p.id; setAddReqParty(p); setViewingPartyId(null) }}
          onOpenDemand={reqId => { setViewingPartyId(null); pushClientCard(reqId) }} />
      )}
      {/* B48: add a request to an EXISTING person — ManualClientModal in preset-party mode.
          onClose fires on both save and cancel (ManualClientModal does onSaved(); onClose()),
          so it's the single reopen point; the ref carries the party id so we land back on the
          now-refreshed person view. onSaved only toasts + reloads the ledger. */}
      {addReqParty && (
        <ManualClientModal districts={districts} features={features}
          presetParty={addReqParty} agentId={myAgentId}
          agentName={allAgents.find(a => a.id === myAgentId)?.name || session?.user?.email?.split('@')[0] || null}
          onSaved={() => { setCommitMsg(t('desk_toast_request_saved')); loadHistory() }}
          onClose={() => { setAddReqParty(null); if (addReqPidRef.current) { setViewingPartyId(addReqPidRef.current); addReqPidRef.current = null } }} />
      )}

      {/* A failed save, reopened (2026-08-01). Resolves the phone BEFORE offering a form,
          so a retry can never mint a second person for a number registered since — see
          FailedEntryModal's header for the owner's exact question and the answer. */}
      {failedEntry && (
        <FailedEntryModal entry={failedEntry} districts={districts} features={features}
          agentId={myAgentId}
          agentName={allAgents.find(a => a.id === myAgentId)?.name || session?.user?.email?.split('@')[0] || null}
          onOpenParty={pid => setViewingPartyId(pid)}
          onDone={() => { setCommitMsg(t('desk_failed_entry_done')); loadHistory() }}
          onClose={() => setFailedEntry(null)} />
      )}

      {/* The UNIFIED property card — same workspace whether the property was typed in or
          scraped (provenance is evidence, not identity; the card just shows more or less
          of it). Opened from listing cards; the pool's market modal stays read-only. */}
      {propertyCard && (
        <ManualPropertyModal districts={districts} existing={propertyCard} agentId={myAgentId}
          agentName={allAgents.find(a => a.id === myAgentId)?.name || null} privileged={privileged}
          onClose={() => setPropertyCard(null)}
          onAddMeeting={onAddMeetingStable} onCreateContract={onCreateContractStable} onShare={setShareSeed}
          onSaved={() => loadHistory()} />
      )}

      {/* Meeting opened straight from a listing card's modal — the property (and any agreed
          cooperation commission) arrives pre-picked; the agent only adds client + time. Nests
          ON TOP of the card modal (lineage/property), same as CooperationModal (2026-07-09):
          the card modal is never closed to make room for this — see the portal note below. */}
      {/* Seed 1 (MEETINGS_MODEL §3): the property card / card-face "Add meeting".
          Routes to the CORE MeetingModal, not the legacy ViewingForm — creation now
          has ONE destination, so a meeting booked from a card and one booked from a
          match row land in the same database. ViewingForm stays for EDITING the 189
          legacy scheduler viewings (and their commission/binder), which is the only
          thing it is still the right tool for.
          ⚠️ Consequence, deliberate and recorded: the core create captures no
          commission — in the chain that belongs to Offer/Deal, and that step is not
          built. New meetings therefore have no commission field until it is. */}
      {meetingPropertySeed && createPortal(
        <MeetingModal
          seed={{
            propertyId: meetingPropertySeed.property_id || null,
            propertyLabel: meetingPropertySeed.address || null,
            // Rooms · area · floor · price — see meetingPropertyFacts. Every door that
            // builds this seed already carries the fields, so all of them gain the line.
            propertyFacts: meetingPropertyFacts(meetingPropertySeed),
            // WHICH AD this booking came from (0357, owner 2026-08-01). The seed has
            // carried `id` (the listing id) all along and this modal dropped it, so the
            // deal-close door later had to ask the agent to re-pick from every posting on
            // the property. Provenance, not identity — see the migration's header.
            listingId: meetingPropertySeed.id || null,
            // Rep PREVIEW (owner 2026-07-29: own property said "no representative on
            // record"). The lineage modal already knows who represents — the viewer
            // themselves, or the rep its banner names; server still snapshots at create.
            propertyRepName: meetingPropertySeed.property_rep_name || null,
            needsPropertyAccess: !!meetingPropertySeed.needs_property_access,
            // The client side is empty by design here: a property card knows the
            // property, not who is being shown it (§3, entry point 1).
            // What the agent typed when NOTHING matched (add-meeting door, 2026-08-03) —
            // a number or a scrap of an address. It is not a property and must never be
            // dressed up as one; it rides through as an open question (notes + client
            // quick-add) so the booking can happen now and be completed later.
            unresolved: meetingPropertySeed.unresolved || null,
            // What the pasted message said about the meeting (envelope.meeting, 2026-09-23):
            // date / time / notes / client name+phone. Pre-fills only — the agent confirms.
            aiMeeting: meetingPropertySeed.ai_meeting || null,
          }}
          onClose={() => setMeetingPropertySeed(null)}
          onCreated={() => { setCommitMsg(t('desk_toast_meeting_saved')); loadHistory(); getMeetingsFeed().then(setViewings).catch(() => {}) }} />,
        document.body
      )}

      {/* "Book next viewing" (owner 2026-08-15) — the seed arrives READY from
          CoreMeetingDetail (property + client + reps of the meeting on screen). The
          modal portals itself, so no createPortal wrapper here. */}
      {/* Share this flat with a colleague — the card-face twin of the lineage modal's ↗ door,
          using the same picker so "one, many or all" behaves identically wherever it is met. */}
      {propShare && (
        <Modal title={t('share_kind_others')} onClose={() => setPropShare(null)}>
          <p className="text-xs text-muted-foreground mb-2 truncate">
            {propShare.address || propShare.listing_id}
          </p>
          {propShareErr && <p className="text-xs text-destructive mb-1">{propShareErr}</p>}
          <AgentPicker agents={allAgents} busy={propShareBusy}
            exclude={(propShareReps || []).map(r => r.agent_id).filter(Boolean)}
            note={t('desk_property_share_note')}
            onCommit={async list => {
              setPropShareBusy(true); setPropShareErr(null)
              const failed = []
              for (const a2 of list) {
                try { await sharePropertyWith(propShare.property_id, { toAgentId: a2.id }) }
                catch { failed.push(a2.name || a2.id) }
              }
              // Never stop at the first failure: with several picked, aborting leaves a
              // half-shared flat behind an error naming only the one that broke.
              if (failed.length) {
                setPropShareErr(tLog('desk_client_share_partial',
                  { ok: list.length - failed.length, bad: failed.length, names: failed.join(', ') }))
                setPropShareBusy(false)
              } else {
                setPropShareBusy(false)
                setCommitMsg(tLog('desk_property_shared_n', { n: list.length }))
                setPropShare(null)
              }
            }}
            onCancel={() => setPropShare(null)} />
        </Modal>
      )}

      {meetingRepeatSeed && (
        <MeetingModal
          seed={meetingRepeatSeed}
          onClose={() => setMeetingRepeatSeed(null)}
          onCreated={() => { setMeetingRepeatSeed(null); setCommitMsg(t('desk_toast_meeting_saved')); loadHistory(); getMeetingsFeed().then(setViewings).catch(() => {}) }} />
      )}

      {/* Card-face 🤝 / 🏠＋ chips (owner 2026-07-30): BOTH open the same combined
          DealEditor the lineage modal uses — cooperation + owner terms as two tabs,
          each half seeded from what the card knows. The 🏠＋ path previously passed a
          `saleMode` prop RentalTermsModal no longer accepts, so sale cards opened the
          rent-shaped editor — mode now flows through termsModeFor properly. */}
      {(deskCoop || deskTerms || deskRepost) && (() => {
        // deskRepost and deskTerms hold the SAME listing-row shape (item.listing), so the
        // repost door seeds coop/terms identically to the 🏠＋ door — before 2026-07-31 it
        // carried only an id and the other two tabs opened empty (owner-reported).
        const rowCtx = deskTerms || deskRepost || null
        const pid = deskCoop?.propertyId || rowCtx?.property_id || null
        const liveCoop = (coopsByProperty[pid] || []).find(c => !['ended', 'rejected'].includes(c.status)) || null
        const coopProps = deskCoop
          || (liveCoop ? { propertyId: pid, cooperationId: liveCoop.id } : null)
          || (rowCtx?.author_party_id ? { propertyId: pid, contactPartyId: rowCtx.author_party_id,
              contactName: rowCtx.author_name || null, seedIntent: coopIntentOf(rowCtx.intent),
              seedCurrency: rowCtx.currency || null, seedPrice: rowCtx.price ?? null,
              listingId: rowCtx.listing_id } : null)
        // Also feeds the repost tab (B244): it is the listing this editor was opened from —
        // via ↗, 🏠＋ or 🤝＋. Null on a property-only entry (no ad to repost) — which
        // correctly hides the tab rather than offering one with nothing to send.
        // ⚠️ deskRepost is only ever set by pressing ↗ → Repost, so when it is set that is
        // unambiguously the door the agent asked for — hence it also wins the initialTab
        // race below, even when a cooperation/terms context is loaded for this same card.
        const termsListing = deskRepost?.listing_id || deskTerms?.listing_id || deskCoop?.listingId || null
        const termsProps = termsListing ? {
          listingId: termsListing, propertyId: pid,
          // listingIntent = the AD's shape (rent/sale/girao…), which termsModeFor wants;
          // seedIntent is the coop-vocabulary fallback for older payloads.
          mode: termsModeFor(rowCtx?.intent || deskCoop?.listingIntent || deskCoop?.seedIntent || 'rent', liveCoop?.transaction_intent || null),
          rent: rowCtx?.price ?? deskCoop?.price ?? null,
          rentCurrency: rowCtx?.currency ?? deskCoop?.currency ?? null,
        } : null
        return (
          <DealEditor coop={coopProps} terms={termsProps}
            share={/* origin, when this row is one of OUR reposts — a reshare's raw rows carry
                      no images, so the payload arrived photo-less. See ShareTab. */
              termsListing ? { listingId: termsListing, originListingId: rowCtx?.origin_listing_id || null } : null}
            initialTab={deskRepost ? 'share' : deskCoop ? 'coop' : 'terms'} agentId={myAgentId}
            sourceUrl={rowCtx?.source_url || null}
            /* 0374: the row this editor was opened from, so the header can say whether its
               author has been reported as an agent — and let that be corrected in place. */
            authorListing={rowCtx}
            onCoopSaved={reloadDeskCoops}
            onTabChange={setDeskDealTab}
            onClose={() => { setDeskCoop(null); setDeskTerms(null); setDeskRepost(null); setDeskDealTab(null) }} />
        )
      })()}

      {editingCandidate && (
        <CandidateModal candidate={editingCandidate} config={config} me={myAgentRecord} offices={offices}
          onSave={data => updateCandidate(editingCandidate.id, data)}
          onDelete={() => {}}
          onPromote={() => {}}
          onClose={() => setEditingCandidate(null)} />
      )}

      {shareSeed && (
        <ShareModal propertyId={shareSeed.propertyId} propertyLabel={shareSeed.propertyLabel}
          seedPrice={shareSeed.seedPrice} seedCurrency={shareSeed.seedCurrency}
          /* A mint or a revoke happened behind this modal — re-pull THIS property so the
             card's 🔗 pill flips dashed→solid (or back) without a page turn. */
          /* The lineage modal keeps its OWN copy of the shares (it is opened from surfaces
             with no page behind them — the bell, a pasted link), so bump an epoch to make
             it re-pull too. Without this, minting a link from inside that modal leaves the
             pill directly under the agent's cursor stale while the card behind it updates. */
          onClose={() => { setShareSeed(null); reloadShares([shareSeed.propertyId]); setSharesEpoch(n => n + 1) }} />
      )}

      {/* ⚠️ Core meetings must NEVER reach ViewingForm — it writes to the scheduler
          `viewings` table by id, and a core row's id is `core:<uuid>`, which exists
          in no such table. One guard here covers all five setEditingViewing callers
          (feed, home cockpit, property modal, search, notifications). */}
      {editingViewing && (editingViewing.__core ? (
        <CoreMeetingDetail meeting={editingViewing} agents={agents} role={role}
          // The next-visit door (owner 2026-08-15). The create modal is a z-50 body
          // portal now, so it stacks over this detail modal by mount order.
          onBookNext={seed => setMeetingRepeatSeed(seed)}
          // The superseded banner's door (owner 2026-08-21): swap the modal to the CURRENT
          // visit of the same deal. Same slot, not a push — the old visit is not a place
          // to come back to for edits, which is the whole point of the lock.
          onOpenCurrent={id => {
            const cur = (viewings || []).find(x => x.__core && x.core_meeting_id === id)
            if (cur) setEditingViewing(cur)
            else getMeetingsFeed().then(vs => { setViewings(vs); const c = vs.find(x => x.__core && x.core_meeting_id === id); if (c) setEditingViewing(c) }).catch(() => {})
          }}
          // ADMIN only, not `privileged` — management reads and edits meetings,
          // but erasing a business record is a narrower power (0199). The server
          // re-checks the verified role; this only decides whether to draw it.
          isAdmin={role === 'admin'}
          // Open the property from the meeting (owner 2026-08-01). Prefer a listing already
          // loaded on the Desk; fall back to the server-resolved representative listing, so
          // this works for an admin whose feed page doesn't happen to contain the property.
          onOpenProperty={pid => {
            const id = (listingEntries || []).find(l => l.property_id === pid)?.listing_id
              || meetingListingByProp[pid] || null
            if (!id) return
            // The property STACKS on the meeting (owner 2026-08-04), reversing the
            // 2026-08-01 "one modal at a time" ruling now that its cause is known: the big
            // card rendered BEHIND the small one because CoreMeetingDetail hand-rolled its
            // overlay at z-[60] against every card modal's z-50 — it won on z-index, not on
            // mount order, so closing the meeting was the only way to see the property. That
            // overlay is now a z-50 body portal like the rest, so the later mount wins and ✕
            // lands back on the meeting instead of on the feed.
            // The stack retired the invisible-mutation hazard this used to guard against (a
            // push is a new entry, never an edit to a covered one), so only the cosmetic half
            // is left: when the meeting was opened FROM this very property, the card is right
            // below and "open the property" means REVEAL it — stacking a second identical copy
            // on top of itself would be silly. Any OTHER property pushes.
            if (topCard?.kind === 'listing' && String(topCard.id) === String(id)) { setEditingViewing(null); return }
            pushListingCard({ id, isOwn: false })
          }}
          onClose={() => setEditingViewing(null)}
          // The nudge list is state-derived, so an edit here may have just resolved one —
          // re-pull it or the banner keeps nagging about a meeting already answered.
          onChanged={() => {
            getMeetingsFeed().then(setViewings).catch(() => {})
            getMeetingNudges().then(setMeetingNudges).catch(() => {})
          }} />
      ) : (
        <ViewingForm agents={agents} allAgents={allAgents} viewing={editingViewing} allViewings={viewings}
          onClose={() => setEditingViewing(null)}
          onOpen={() => setEditingViewing(null)}
          onSaved={() => { setEditingViewing(null); loadHistory(); getMeetingsFeed().then(setViewings).catch(() => {}) }} />
      ))}
    </div>
    </AdTextCtx.Provider>
    </MoneyCtx.Provider>
    </ViewerCtx.Provider>
  )
}
